import { Fragment, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import defaultConsoleLogo from "./assets/turuk_logo_white_blue_shadow.png";

const STORAGE_SESSION = "turuk_portal_session_v2";
const STORAGE_LANG = "turuk_portal_lang_v1";
const STORAGE_COLUMNS_PREFS_PREFIX = "turuk_portal_columns_v1";
const DEFAULT_API_BASE = import.meta.env.VITE_API_BASE || "/api/v1";
const MAX_CONSOLE_LOGO_FILE_BYTES = 2 * 1024 * 1024;
const DEFAULT_CONSOLE_LOGO_CLICK_URL = "#/dashboard";
const DEFAULT_DASHBOARD_SECTION_SETTINGS = {
  total_deployment: true,
  summary_accounts: true,
  summary_profiles: true,
  summary_apps: true,
  summary_groups: true,
  support_tickets: true,
  security: true,
  security_encrypted: true,
  security_passcode: true,
  security_supervised: true,
  ownership: true,
  ownership_corporate: true,
  ownership_personal: true,
  ownership_unassigned: true,
  last_seen_overview: true,
  last_seen_breakdown: true,
  last_seen_bucket_0to1: true,
  last_seen_bucket_2to7: true,
  last_seen_bucket_8to15: true,
  last_seen_bucket_16to30: true,
  last_seen_bucket_31plus: true,
  platforms: true,
  platform_ios: true,
  platform_macos: true,
  platform_windows: true,
  platform_android: true
};
const DEFAULT_DASHBOARD_SECTION_ORDER = [
  "total_deployment",
  "support_tickets",
  "platforms",
  "security",
  "ownership",
  "last_seen_breakdown",
  "last_seen_overview"
];
const DEFAULT_MAIN_NAV_ORDER = [
  "dashboard",
  "command_center",
  "health_status",
  "accounts",
  "groups",
  "turuk_agent",
  "documents",
  "action_logs",
  "devices",
  "profiles",
  "apps",
  "settings"
];
const LazyDashboardPage = lazy(() => import("./components/DashboardPage.jsx"));
const DEVICE_DEFAULT_COLUMN_KEYS = [
  "device_name",
  "ownership",
  "compliance",
  "os",
  "os_version",
  "last_sync"
];
const DEVICE_COLUMN_KEYS = [
  "device_name",
  "imei",
  "wifi_mac",
  "meid",
  "carrier",
  "os_version",
  "ownership",
  "cert_expiry",
  "enrollment_date",
  "encrypted",
  "account_name",
  "model",
  "phone",
  "os",
  "total_storage",
  "primary_email",
  "last_sync",
  "free_storage",
  "serial",
  "compliance"
];
const PROFILE_DEFAULT_COLUMN_KEYS = ["name", "identifier", "groups", "devices", "status", "updated_at"];
const PROFILE_COLUMN_KEYS = ["name", "identifier", "groups", "devices", "status", "updated_at"];
const POLICY_DEFAULT_COLUMN_KEYS = ["name", "platform", "policy_type", "updated_at", "scope_tags", "compliance"];
const POLICY_COLUMN_KEYS = ["name", "platform", "policy_type", "updated_at", "scope_tags", "compliance"];
const APP_DEFAULT_COLUMN_KEYS = ["name", "platform", "type", "version", "assigned", "groups"];
const APP_COLUMN_KEYS = [
  "name",
  "bundle_id",
  "platform",
  "type",
  "version",
  "vpp_token",
  "assigned",
  "groups",
  "developer",
  "status",
  "updated_at"
];
const PROFILE_WORKSPACE_PAGES = [
  "profiles",
  "profilesAll",
  "profilesPlatformWindows",
  "profilesPlatformIOS",
  "profilesPlatformMacOS",
  "profilesPlatformAndroid",
  "policiesAll",
  "policiesCompliance",
  "devicesProfiles"
];
const APPS_WORKSPACE_PAGES = [
  "apps",
  "appsAll",
  "appsPlatformWindows",
  "appsPlatformIOS",
  "appsPlatformMacOS",
  "appsPlatformAndroid",
  "appsConfig",
  "appsUpload"
];
const DEVICE_WORKSPACE_PAGES = [
  "devices",
  "devicesList",
  "devicesRequest",
  "devicesMonitor",
  "devicesPlatformWindows",
  "devicesPlatformIOS",
  "devicesPlatformMacOS",
  "devicesPlatformAndroid",
  "devicesConfig",
  "devicesCompliance",
  "devicesDetailAbout",
  "devicesDetailApps",
  "devicesDetailProfiles",
  "devicesDetailLocation",
  "devicesDetailSharedIPad",
  "devicesDetailActivationLock",
  "devicesDetailMacOSUEM",
  "devicesDetailCommands",
  "devicesDetailRemoteAccess"
];
const DEVICE_DETAILS_PAGES = [
  "devicesDetailAbout",
  "devicesDetailApps",
  "devicesDetailProfiles",
  "devicesDetailLocation",
  "devicesDetailSharedIPad",
  "devicesDetailActivationLock",
  "devicesDetailMacOSUEM",
  "devicesDetailCommands",
  "devicesDetailRemoteAccess"
];
const DEVICE_DETAILS_PAGE_TO_TAB = {
  devicesDetailAbout: "about",
  devicesDetailApps: "apps",
  devicesDetailProfiles: "profiles",
  devicesDetailLocation: "location",
  devicesDetailSharedIPad: "sharedIPad",
  devicesDetailActivationLock: "activationLock",
  devicesDetailMacOSUEM: "macOSUEM",
  devicesDetailCommands: "commands",
  devicesDetailRemoteAccess: "remoteAccess"
};

let gapiIframesLoaderPromise = null;

function loadGapiIframesScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window is not available"));
  }
  if (window.gapi?.iframes?.getContext) {
    return Promise.resolve();
  }
  if (!gapiIframesLoaderPromise) {
    gapiIframesLoaderPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-gapi-iframes="1"]');
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Failed to load gapi script")), {
          once: true
        });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://apis.google.com/js/api.js";
      script.async = true;
      script.dataset.gapiIframes = "1";
      script.onload = () => {
        try {
          if (window.gapi?.iframes?.getContext) {
            resolve();
            return;
          }
          if (typeof window.gapi?.load !== "function") {
            reject(new Error("gapi.load is unavailable"));
            return;
          }
          window.gapi.load("gapi.iframes", {
            callback: () => resolve(),
            onerror: () => reject(new Error("Failed to initialize gapi.iframes")),
            timeout: 10000,
            ontimeout: () => reject(new Error("gapi.iframes load timeout"))
          });
        } catch (err) {
          reject(err);
        }
      };
      script.onerror = () => reject(new Error("Failed to load gapi script"));
      document.head.appendChild(script);
    });
  }
  return gapiIframesLoaderPromise;
}

const PROFILE_BUILDER_SECTIONS = [
  { key: "general", label: "General", icon: "⚙" },
  { key: "restrictions", label: "Restrictions", icon: "⛔" },
  { key: "domains", label: "Domains", icon: "🌐" },
  { key: "global_http_proxy", label: "Global HTTP Proxy", icon: "🌍" },
  { key: "dns_proxy", label: "DNS Proxy", icon: "🛠" },
  { key: "content_filter", label: "Content Filter", icon: "☰" },
  { key: "certificates", label: "Certificates", icon: "📜" },
  { key: "certificate_transparency", label: "Certificate Transparency", icon: "🔍" },
  { key: "passcode", label: "Passcode", icon: "🔒" },
  { key: "wifi", label: "Wi-Fi", icon: "📶" },
  { key: "vpn", label: "VPN", icon: "🛡" },
  { key: "airplay", label: "AirPlay", icon: "📺" },
  { key: "airplay_security", label: "AirPlay Security", icon: "🔐" },
  { key: "airprint", label: "AirPrint", icon: "🖨" },
  { key: "calendar", label: "Calendar", icon: "📅" },
  { key: "subscribed_calendars", label: "Subscribed Calendars", icon: "🗓" },
  { key: "contacts", label: "Contacts", icon: "👤" },
  { key: "exchange_active_sync", label: "Exchange ActiveSync", icon: "✉" },
  { key: "google_account", label: "Google Account", icon: "ⓖ" },
  { key: "ldap", label: "LDAP", icon: "@" },
  { key: "mail", label: "Mail", icon: "✉" },
  { key: "macos_server_account", label: "macOS Server Account", icon: "🖥" },
  { key: "scep", label: "SCEP", icon: "🔏" },
  { key: "cellular", label: "Cellular", icon: "📡" },
  { key: "fonts", label: "Fonts", icon: "Aa" },
  { key: "notifications", label: "Notifications", icon: "🔔" },
  { key: "tv_remote", label: "TV Remote", icon: "📲" },
  { key: "lock_screen_message", label: "Lock Screen Message", icon: "📱" },
  { key: "web_clips", label: "Web Clips", icon: "✂" },
  { key: "filevault", label: "FileVault", icon: "FV" },
  { key: "pppc", label: "Privacy Preferences", icon: "PP" },
  { key: "system_extensions", label: "System Extensions", icon: "SE" },
  { key: "kernel_extensions", label: "Kernel Extensions", icon: "KE" },
  { key: "firewall", label: "Firewall", icon: "FW" },
  { key: "login_items", label: "Login Items", icon: "LI" },
  { key: "background_tasks", label: "Background Tasks", icon: "BT" },
  { key: "macos_software_update", label: "macOS Software Update", icon: "SU" },
  { key: "local_admin", label: "Local Admin", icon: "LA" },
  { key: "scripts_packages", label: "Scripts / Packages", icon: "PKG" }
];

const PROFILE_BUILDER_SECTION_TITLE_KEYS = {
  global_http_proxy: "profileSectionTitleGlobalHTTPProxySupervised",
  dns_proxy: "profileSectionTitleDNSProxySupervised",
  content_filter: "profileSectionTitleContentFilterSupervised"
};

const PROFILE_BUILDER_SECTION_HINT_KEYS = {
  restrictions: "profileSectionHintRestrictions",
  domains: "profileSectionHintDomains",
  global_http_proxy: "profileSectionHintGlobalHTTPProxy",
  dns_proxy: "profileSectionHintDNSProxy",
  content_filter: "profileSectionHintContentFilter",
  certificates: "profileSectionHintCertificates",
  passcode: "profileSectionHintPasscode",
  vpn: "profileSectionHintVPN",
  airplay: "profileSectionHintAirPlay",
  filevault: "profileSectionHintFileVault",
  pppc: "profileSectionHintPPPC",
  system_extensions: "profileSectionHintSystemExtensions",
  firewall: "profileSectionHintFirewall",
  login_items: "profileSectionHintLoginItems",
  background_tasks: "profileSectionHintBackgroundTasks",
  macos_software_update: "profileSectionHintMacOSSoftwareUpdate",
  local_admin: "profileSectionHintLocalAdmin",
  scripts_packages: "profileSectionHintScriptsPackages"
};

const PROFILE_BUILDER_SECTION_WARNING_KEYS = {
  global_http_proxy: "profileSectionWarningGlobalHTTPProxy",
  dns_proxy: "profileSectionWarningDNSProxy"
};

const PROFILE_BUILDER_SECTION_PLATFORMS = {
  general: ["ios", "macos", "tvos", "visionos"],
  restrictions: ["ios", "macos", "tvos", "visionos"],
  domains: ["ios", "macos"],
  global_http_proxy: ["ios", "macos", "tvos"],
  dns_proxy: ["ios", "macos"],
  content_filter: ["ios", "macos"],
  certificates: ["ios", "macos", "tvos", "visionos"],
  certificate_transparency: ["ios", "macos", "tvos", "visionos"],
  passcode: ["ios", "macos", "tvos", "visionos"],
  wifi: ["ios", "macos", "tvos", "visionos"],
  vpn: ["ios", "macos", "tvos", "visionos"],
  airplay: ["ios", "macos"],
  airplay_security: ["ios", "macos"],
  airprint: ["ios", "macos"],
  calendar: ["ios", "macos"],
  subscribed_calendars: ["ios", "macos"],
  contacts: ["ios", "macos"],
  exchange_active_sync: ["ios", "macos"],
  google_account: ["ios", "macos"],
  ldap: ["ios", "macos"],
  mail: ["ios", "macos"],
  macos_server_account: ["macos"],
  scep: ["ios", "macos"],
  acme: ["ios", "macos"],
  sso_extension: ["ios", "macos"],
  cellular: ["ios"],
  fonts: ["ios", "macos"],
  notifications: ["ios", "macos"],
  home_screen_layout: ["ios"],
  app_lock: ["ios"],
  conference_room_display: ["ios"],
  tv_remote: ["ios"],
  lock_screen_message: ["ios"],
  web_clips: ["ios", "macos"],
  filevault: ["macos"],
  pppc: ["macos"],
  system_extensions: ["macos"],
  kernel_extensions: ["macos"],
  firewall: ["macos"],
  login_items: ["macos"],
  background_tasks: ["macos"],
  macos_software_update: ["macos"],
  local_admin: ["macos"],
  scripts_packages: ["macos"]
};

const PROFILE_BUILDER_SECTION_MIN_OS = {
  general: { ios: "iOS 4", macos: "macOS 10.7", tvos: "tvOS 9", visionos: "visionOS 1.1" },
  restrictions: { ios: "iOS 4", macos: "macOS 10.7", tvos: "tvOS 9", visionos: "visionOS 1.1" },
  domains: { ios: "iOS 7", macos: "macOS 10.7" },
  global_http_proxy: { ios: "iOS 6", macos: "macOS 10.9", tvos: "tvOS 10.2" },
  dns_proxy: { ios: "iOS 11", macos: "macOS 10.15" },
  content_filter: { ios: "iOS 7", macos: "macOS 10.15" },
  certificates: { ios: "iOS 4", macos: "macOS 10.7", tvos: "tvOS 9", visionos: "visionOS 1.1" },
  certificate_transparency: { ios: "iOS 12.2", macos: "macOS 10.14.4", tvos: "tvOS 12.2", visionos: "visionOS 1.1" },
  passcode: { ios: "iOS 4", macos: "macOS 10.7", tvos: "tvOS 9", visionos: "visionOS 1.1" },
  wifi: { ios: "iOS 4", macos: "macOS 10.7", tvos: "tvOS 9", visionos: "visionOS 1.1" },
  vpn: { ios: "iOS 4", macos: "macOS 10.7", tvos: "tvOS 17", visionos: "visionOS 1.1" },
  airplay: { ios: "iOS 7", macos: "macOS 10.10" },
  airplay_security: { ios: "iOS 7.1", macos: "macOS 10.10" },
  airprint: { ios: "iOS 7", macos: "macOS 10.10" },
  calendar: { ios: "iOS 4", macos: "macOS 10.8" },
  subscribed_calendars: { ios: "iOS 4", macos: "macOS 10.8" },
  contacts: { ios: "iOS 7", macos: "macOS 10.11" },
  exchange_active_sync: { ios: "iOS 4", macos: "macOS 10.7" },
  google_account: { ios: "iOS 9.3", macos: "macOS 10.11" },
  ldap: { ios: "iOS 4", macos: "macOS 10.7" },
  mail: { ios: "iOS 4", macos: "macOS 10.7" },
  macos_server_account: { macos: "macOS 10.8" },
  scep: { ios: "iOS 4", macos: "macOS 10.7" },
  acme: { ios: "iOS 16", macos: "macOS 13" },
  sso_extension: { ios: "iOS 13", macos: "macOS 10.15" },
  cellular: {
    title: "Cellular",
    titleRu: "Cellular",
    icon: "▥",
    minOS: "iOS 7",
    description: "Configure APN settings for cellular data connections.",
    descriptionRu: "Настройка APN для подключений по сотовой сети.",
    fields: [
      {
        key: "configuredApnType",
        type: "select",
        label: "Configured APN Type",
        labelRu: "Configured APN Type",
        defaultValue: "default",
        options: [
          { value: "default", label: "Default APN", labelRu: "Default APN" },
          { value: "data", label: "Data APN", labelRu: "Data APN" },
          { value: "default_and_data", label: "Default and Data APNs", labelRu: "Default and Data APNs" },
        ],
      },
      {
        key: "defaultApnName",
        type: "text",
        label: "Default APN Name",
        labelRu: "Default APN Name",
        description: "Access Point Name for the default APN configuration",
        descriptionRu: "Access Point Name for the default APN configuration",
        placeholder: "[required]",
        required: true,
        visibleWhen: { key: "configuredApnType", values: ["default", "default_and_data"] },
      },
      {
        key: "defaultApnAuthenticationType",
        type: "select",
        label: "Default APN Authentication Type",
        labelRu: "Default APN Authentication Type",
        description: "Authentication used by the default APN configuration",
        descriptionRu: "Authentication used by the default APN configuration",
        defaultValue: "PAP",
        options: ["PAP", "CHAP"],
        visibleWhen: { key: "configuredApnType", values: ["default", "default_and_data"] },
      },
      {
        key: "defaultApnUsername",
        type: "text",
        label: "Default APN User Name",
        labelRu: "Default APN User Name",
        description: "User name for authenticating the connection",
        descriptionRu: "User name for authenticating the connection",
        placeholder: "[optional]",
        visibleWhen: { key: "configuredApnType", values: ["default", "default_and_data"] },
      },
      {
        key: "defaultApnPassword",
        type: "password",
        label: "Default APN Password",
        labelRu: "Default APN Password",
        description: "Password for authenticating the connection",
        descriptionRu: "Password for authenticating the connection",
        placeholder: "[optional]",
        visibleWhen: { key: "configuredApnType", values: ["default", "default_and_data"] },
      },
      {
        key: "defaultApnSupportedIpVersions",
        type: "select",
        label: "Default APN Supported IP Versions",
        labelRu: "Default APN Supported IP Versions",
        description: "Supported Internet Protocol versions for connections",
        descriptionRu: "Supported Internet Protocol versions for connections",
        defaultValue: "",
        options: [
          { value: "", label: "-", labelRu: "-" },
          { value: "IPv4", label: "IPv4", labelRu: "IPv4" },
          { value: "IPv6", label: "IPv6", labelRu: "IPv6" },
          { value: "IPv4AndIPv6", label: "IPv4 and IPv6", labelRu: "IPv4 and IPv6" },
        ],
        visibleWhen: { key: "configuredApnType", values: ["default", "default_and_data"] },
      },
      {
        key: "dataApnName",
        type: "text",
        label: "Data APN Name",
        labelRu: "Data APN Name",
        description: "Access Point Name for the data APN configuration",
        descriptionRu: "Access Point Name for the data APN configuration",
        placeholder: "[required]",
        required: true,
        visibleWhen: { key: "configuredApnType", values: ["data", "default_and_data"] },
      },
      {
        key: "dataApnAuthenticationType",
        type: "select",
        label: "Data APN Authentication Type",
        labelRu: "Data APN Authentication Type",
        description: "Authentication used by the data APN configuration",
        descriptionRu: "Authentication used by the data APN configuration",
        defaultValue: "PAP",
        options: ["PAP", "CHAP"],
        visibleWhen: { key: "configuredApnType", values: ["data", "default_and_data"] },
      },
      {
        key: "dataApnUsername",
        type: "text",
        label: "Data APN User Name",
        labelRu: "Data APN User Name",
        description: "User name for authenticating the connection",
        descriptionRu: "User name for authenticating the connection",
        placeholder: "[optional]",
        visibleWhen: { key: "configuredApnType", values: ["data", "default_and_data"] },
      },
      {
        key: "dataApnPassword",
        type: "password",
        label: "Data APN Password",
        labelRu: "Data APN Password",
        description: "Password for authenticating the connection",
        descriptionRu: "Password for authenticating the connection",
        placeholder: "[optional]",
        visibleWhen: { key: "configuredApnType", values: ["data", "default_and_data"] },
      },
      {
        key: "dataApnSupportedIpVersions",
        type: "select",
        label: "Data APN Supported IP Versions",
        labelRu: "Data APN Supported IP Versions",
        description: "Supported Internet Protocol versions for connections",
        descriptionRu: "Supported Internet Protocol versions for connections",
        defaultValue: "",
        options: [
          { value: "", label: "-", labelRu: "-" },
          { value: "IPv4", label: "IPv4", labelRu: "IPv4" },
          { value: "IPv6", label: "IPv6", labelRu: "IPv6" },
          { value: "IPv4AndIPv6", label: "IPv4 and IPv6", labelRu: "IPv4 and IPv6" },
        ],
        visibleWhen: { key: "configuredApnType", values: ["data", "default_and_data"] },
      },
    ],
  },
  fonts: { ios: "iOS 7", macos: "macOS 10.7" },
  notifications: { ios: "iOS 9.3", macos: "macOS 10.14.4" },
  home_screen_layout: { ios: "iOS 9.3" },
  app_lock: { ios: "iOS 7" },
  conference_room_display: { ios: "iOS 7" },
  tv_remote: { ios: "iOS 11.3" },
  lock_screen_message: { ios: "iOS 9.3" },
  web_clips: { ios: "iOS 4", macos: "macOS 10.7" },
  filevault: { macos: "macOS 10.13" },
  pppc: { macos: "macOS 10.14" },
  system_extensions: { macos: "macOS 10.15" },
  kernel_extensions: { macos: "macOS 10.13.2" },
  firewall: { macos: "macOS 10.12" },
  login_items: { macos: "macOS 10.13" },
  background_tasks: { macos: "macOS 13" },
  macos_software_update: { macos: "macOS 10.11" },
  local_admin: { macos: "macOS 10.13" },
  scripts_packages: { macos: "macOS 10.13" }
};

const PROFILE_GENERAL_AUTO_REMOVE_VALUES = new Set(["Never", "OnDate", "AfterDays"]);

function createEmptyProfileCreateForm(platform = "ios") {
  return {
    name: "",
    groupIds: [],
    platform: normalizeProfilePlatform(platform),
    description: "",
    autoRemove: "Never",
    removalDate: "",
    removalDays: ""
  };
}

function createEmptyProfileViewForm(platform = "ios") {
  return {
    id: "",
    name: "",
    identifier: "",
    groupIds: [],
    platform: normalizeProfilePlatform(platform),
    description: "",
    autoRemove: "Never",
    removalDate: "",
    removalDays: ""
  };
}

function createProfileBuilderConfiguredState() {
  return PROFILE_BUILDER_SECTIONS.reduce((acc, section) => {
    if (section.key !== "general") {
      acc[section.key] = false;
    }
    return acc;
  }, {});
}

function profileBuilderSectionSupportsPlatform(sectionKey, platformValue) {
  const platform = normalizeProfilePlatform(platformValue);
  const platforms = PROFILE_BUILDER_SECTION_PLATFORMS[sectionKey];
  if (!Array.isArray(platforms) || platforms.length === 0) return platform === "ios" || platform === "macos";
  return platforms.includes(platform);
}

function profileBuilderSectionsForPlatform(platformValue) {
  return PROFILE_BUILDER_SECTIONS.filter((section) =>
    profileBuilderSectionSupportsPlatform(section.key, platformValue)
  );
}

function profileBuilderSupportedPlatformKeys() {
  return new Set(["ios", "macos"]);
}

function profileBuilderCanCreateForPlatform(platformValue) {
  return profileBuilderSupportedPlatformKeys().has(normalizeProfilePlatform(platformValue));
}

function normalizeForcedProfilePlatform(platformValue) {
  const raw = String(platformValue || "").trim().toLowerCase();
  if (!raw || raw === "all") return "";
  return normalizeProfilePlatform(raw);
}

function pruneProfileSectionPayloadsForPlatform(sectionPayloads, platformValue) {
  const out = createProfileSectionPayloadsState();
  Object.entries(sectionPayloads || {}).forEach(([sectionKey, payloads]) => {
    if (!profileBuilderSectionSupportsPlatform(sectionKey, platformValue)) return;
    out[sectionKey] = Array.isArray(payloads) ? payloads : [];
  });
  return out;
}

function pruneProfileConfiguredSectionsForPlatform(configuredSections, platformValue) {
  const out = createProfileBuilderConfiguredState();
  Object.entries(configuredSections || {}).forEach(([sectionKey, configured]) => {
    if (!profileBuilderSectionSupportsPlatform(sectionKey, platformValue)) return;
    out[sectionKey] = Boolean(configured);
  });
  return out;
}

function normalizeConsoleLogoDataURL(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeConsoleLogoClickURL(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return "";
  if (raw.startsWith("#/") || raw.startsWith("/")) return raw;
  const lower = raw.toLowerCase();
  if (lower.startsWith("https://") || lower.startsWith("http://")) return raw;
  return "";
}

function normalizePublicTenantBootstrap(value) {
  const rawTenant = value?.tenant && typeof value.tenant === "object" ? value.tenant : {};
  return {
    tenant: {
      name: String(rawTenant.name || "").trim(),
      slug: String(rawTenant.slug || "").trim(),
      primary_domain: String(rawTenant.primary_domain || "").trim(),
      plan: String(rawTenant.plan || "").trim()
    },
    logo_data_url: normalizeConsoleLogoDataURL(value?.logo_data_url)
  };
}

function normalizeAgentBranding(value) {
  const raw = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  return {
    organization_name: String(raw.organization_name || "Turuk").trim() || "Turuk",
    primary_color: String(raw.primary_color || "#0A84FF").trim() || "#0A84FF",
    logo_data_url: String(raw.logo_data_url || "").trim(),
    support_email: String(raw.support_email || "").trim(),
    support_phone: String(raw.support_phone || "").trim()
  };
}

function normalizeAgentServices(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => ({
      id: String(item?.id || "").trim(),
      title: String(item?.title || "").trim(),
      description: String(item?.description || "").trim(),
      url: String(item?.url || "").trim(),
      icon: String(item?.icon || "link").trim() || "link",
      category: String(item?.category || "service").trim() || "service",
      sort_order: Number.isFinite(Number(item?.sort_order)) ? Number(item.sort_order) : index + 1,
      is_enabled: item?.is_enabled !== false
    }))
    .filter((item) => item.title || item.url || item.description);
}

function createEmptyAgentService() {
  return {
    id: "",
    title: "",
    description: "",
    url: "",
    icon: "link",
    category: "service",
    sort_order: 0,
    is_enabled: true
  };
}

const MAX_DOCUMENT_UPLOAD_BYTES = 10 * 1024 * 1024;
const ALLOWED_DOCUMENT_UPLOAD_EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".txt", ".rtf", ".csv",
  ".png", ".jpg", ".jpeg", ".gif", ".pages", ".numbers", ".key"
];

function createEmptyDocumentForm() {
  return {
    id: "",
    title: "",
    description: "",
    documentType: "link",
    url: "",
    fileName: "",
    contentType: "",
    fileData: "",
    documentFiles: [],
    folderId: "",
    groupIds: [],
    requireAck: false,
    ackDueDate: "",
    isActive: true
  };
}

function createEmptySSOForm() {
  return {
    sso_preset: "microsoft",
    provider: "oidc",
    enabled: false,
    enforce_sso: false,
    auto_provision: false,
    allowed_domains_text: "",
    default_role: "helpdesk",
    oidc_issuer_url: "",
    oidc_client_id: "",
    oidc_client_secret: "",
    oidc_scopes: "openid email profile",
    oidc_auth_url: "",
    oidc_token_url: "",
    oidc_userinfo_url: "",
    saml_entity_id: "",
    saml_sso_url: "",
    saml_x509_cert: ""
  };
}

const SSO_PROVIDER_PRESETS = {
  microsoft: {
    provider: "oidc",
    labelRu: "Microsoft Entra ID",
    labelEn: "Microsoft Entra ID",
    issuerPlaceholder: "https://login.microsoftonline.com/<tenant-id>/v2.0",
    scopes: "openid email profile"
  },
  google: {
    provider: "oidc",
    labelRu: "Google Workspace",
    labelEn: "Google Workspace",
    issuerPlaceholder: "https://accounts.google.com",
    scopes: "openid email profile"
  },
  okta: {
    provider: "oidc",
    labelRu: "Okta",
    labelEn: "Okta",
    issuerPlaceholder: "https://company.okta.com/oauth2/default",
    scopes: "openid email profile"
  },
  custom_oidc: {
    provider: "oidc",
    labelRu: "Custom OIDC",
    labelEn: "Custom OIDC",
    issuerPlaceholder: "https://idp.example.com",
    scopes: "openid email profile"
  },
  custom_saml: {
    provider: "saml",
    labelRu: "Custom SAML",
    labelEn: "Custom SAML",
    issuerPlaceholder: "",
    scopes: "openid email profile"
  }
};

function detectSSOPreset(config) {
  const provider = String(config?.provider || "oidc").toLowerCase();
  if (provider === "saml") return "custom_saml";
  const issuer = String(config?.oidc_issuer_url || "").toLowerCase();
  if (issuer.includes("login.microsoftonline.com")) return "microsoft";
  if (issuer.includes("accounts.google.com")) return "google";
  if (issuer.includes("okta.com")) return "okta";
  return "custom_oidc";
}

const DDM_PAYLOAD_TEMPLATES = {
  software_update: {
    labelRu: "Обновления iOS/iPadOS/macOS",
    labelEn: "Software Update",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.software-update",
    display_name: "Software Update",
    payload: {
      Type: "com.apple.configuration.softwareupdate.enforcement",
      Payload: {
        TargetOSVersion: "latest",
        TargetBuildVersion: "",
        TargetLocalDateTime: "",
        MaximumUserDeferrals: 5,
        InstallASAP: true
      }
    }
  },
  software_update_settings: {
    labelRu: "Настройки обновлений ОС",
    labelEn: "Software Update Settings",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.software-update-settings",
    display_name: "Software Update Settings",
    payload: {
      Type: "com.apple.configuration.softwareupdate.settings",
      Payload: {
        AutomaticActions: true,
        BetaProgram: "",
        MaximumUserDeferrals: 5
      }
    }
  },
  passcode: {
    labelRu: "Требования к паролю",
    labelEn: "Passcode",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.passcode",
    display_name: "Passcode",
    payload: {
      Type: "com.apple.configuration.passcode.settings",
      Payload: {
        MinimumLength: 6,
        RequireAlphanumeric: true,
        MaximumFailedAttempts: 10
      }
    }
  },
  safari: {
    labelRu: "Safari",
    labelEn: "Safari",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.safari",
    display_name: "Safari",
    payload: {
      Type: "com.apple.configuration.safari.settings",
      Payload: {
        AllowJavaScript: true,
        AllowPopups: false
      }
    }
  },
  certificate_asset: {
    labelRu: "Сертификат",
    labelEn: "Certificate asset",
    declaration_type: "asset",
    identifier: "com.turuk.ddm.certificate",
    display_name: "Certificate asset",
    payload: {
      Type: "com.apple.asset.credential.certificate",
      Payload: {
        Reference: "",
        CertificatePEM: ""
      }
    }
  },
  identity_asset: {
    labelRu: "Учетные данные",
    labelEn: "Identity asset",
    declaration_type: "asset",
    identifier: "com.turuk.ddm.identity",
    display_name: "Identity asset",
    payload: {
      Type: "com.apple.asset.credential.identity",
      Payload: {
        Reference: "",
        IdentityP12: "",
        Password: ""
      }
    }
  },
  mail: {
    labelRu: "Почта",
    labelEn: "Mail",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.mail",
    display_name: "Mail",
    payload: {
      Type: "com.apple.configuration.mail",
      Payload: {
        AccountDescription: "",
        EmailAddress: "",
        HostName: "",
        IdentityAssetReference: ""
      }
    }
  },
  exchange: {
    labelRu: "Exchange",
    labelEn: "Exchange",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.exchange",
    display_name: "Exchange",
    payload: {
      Type: "com.apple.configuration.exchange",
      Payload: {
        AccountDescription: "",
        EmailAddress: "",
        HostName: "",
        UserName: "",
        IdentityAssetReference: ""
      }
    }
  },
  google: {
    labelRu: "Google аккаунт",
    labelEn: "Google account",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.google",
    display_name: "Google account",
    payload: {
      Type: "com.apple.configuration.google.account",
      Payload: {
        AccountDescription: "",
        EmailAddress: "",
        UserName: ""
      }
    }
  },
  ldap: {
    labelRu: "LDAP",
    labelEn: "LDAP",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.ldap",
    display_name: "LDAP",
    payload: {
      Type: "com.apple.configuration.ldap",
      Payload: {
        AccountDescription: "",
        HostName: "",
        UserName: "",
        UseSSL: true
      }
    }
  },
  calendar: {
    labelRu: "Календарь",
    labelEn: "Calendar",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.calendar",
    display_name: "Calendar",
    payload: {
      Type: "com.apple.configuration.calendar",
      Payload: {
        AccountDescription: "",
        HostName: "",
        UserName: "",
        UseSSL: true
      }
    }
  },
  contacts: {
    labelRu: "Контакты",
    labelEn: "Contacts",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.contacts",
    display_name: "Contacts",
    payload: {
      Type: "com.apple.configuration.contacts",
      Payload: {
        AccountDescription: "",
        HostName: "",
        UserName: "",
        UseSSL: true
      }
    }
  },
  screen_sharing: {
    labelRu: "Общий доступ к экрану",
    labelEn: "Screen Sharing",
    declaration_type: "management",
    identifier: "com.turuk.ddm.screen-sharing",
    display_name: "Screen Sharing",
    payload: {
      Type: "com.apple.management.screen-sharing",
      Payload: {
        Enabled: true
      }
    }
  },
  legacy_profile: {
    labelRu: "Профиль конфигурации",
    labelEn: "Legacy profile",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.legacy-profile",
    display_name: "Legacy profile",
    payload: {
      Type: "com.apple.configuration.legacy",
      Payload: {
        ProfileIdentifier: "",
        ProfileURL: ""
      }
    }
  },
  legacy_interactive_profile: {
    labelRu: "Интерактивный профиль",
    labelEn: "Legacy interactive profile",
    declaration_type: "configuration",
    identifier: "com.turuk.ddm.legacy-interactive-profile",
    display_name: "Legacy interactive profile",
    payload: {
      Type: "com.apple.configuration.legacy.interactive",
      Payload: {
        ProfileIdentifier: "",
        ProfileURL: "",
        UserEnrollmentRequired: true
      }
    }
  },
  management_properties: {
    labelRu: "Свойства управления",
    labelEn: "Management properties",
    declaration_type: "management",
    identifier: "com.turuk.ddm.management-properties",
    display_name: "Management properties",
    payload: {
      Type: "com.apple.management.properties",
      Payload: {
        Properties: {
          department: "",
          owner: ""
        }
      }
    }
  },
  activation: {
    labelRu: "Активация и условие",
    labelEn: "Activation + predicate",
    declaration_type: "activation",
    identifier: "com.turuk.ddm.activation",
    display_name: "Activation",
    payload: {
      Type: "com.apple.activation.simple",
      Payload: {
        Predicate: "TRUEPREDICATE",
        Declarations: []
      }
    }
  }
};

const DDM_TEMPLATE_ORDER = [
  "software_update",
  "software_update_settings",
  "passcode",
  "safari",
  "certificate_asset",
  "identity_asset",
  "mail",
  "exchange",
  "google",
  "ldap",
  "calendar",
  "contacts",
  "screen_sharing",
  "legacy_profile",
  "legacy_interactive_profile",
  "management_properties",
  "activation"
];

const DDM_STATUS_SUBSCRIPTION_OPTIONS = [
  { key: "device.identifier.serial-number", labelRu: "Серийный номер", labelEn: "Serial number" },
  { key: "device.operating-system.version", labelRu: "Версия ОС", labelEn: "OS version" },
  { key: "device.operating-system.build-version", labelRu: "Сборка ОС", labelEn: "OS build" },
  { key: "device.model.family", labelRu: "Семейство модели", labelEn: "Model family" },
  { key: "device.security.passcode-compliant", labelRu: "Соответствие паролю", labelEn: "Passcode compliance" },
  { key: "device.security.filevault.enabled", labelRu: "FileVault", labelEn: "FileVault" },
  { key: "device.software-update.pending-version", labelRu: "Доступное обновление ОС", labelEn: "Pending OS update" },
  { key: "device.software-update.install-state", labelRu: "Статус установки ОС", labelEn: "OS install state" },
  { key: "device.application.list", labelRu: "Список приложений", labelEn: "Application list" },
  { key: "device.battery.health", labelRu: "Состояние батареи", labelEn: "Battery health" }
];

function createEmptyDDMForm() {
  const template = DDM_PAYLOAD_TEMPLATES.software_update;
  return {
    id: "",
    template: "software_update",
    declaration_type: template.declaration_type,
    channel: "device",
    identifier: template.identifier,
    display_name: template.display_name,
    payload_text: JSON.stringify(template.payload, null, 2),
    fields: ddmFieldsFromPayload("software_update", template.payload),
    is_active: true,
    all_devices: true,
    group_ids: [],
    device_udids_text: ""
  };
}

function ddmAssetKindFromPayload(payload) {
  const payloadType = String(payload?.Type || "");
  if (payloadType === "com.apple.asset.credential.certificate") return "certificate";
  if (payloadType === "com.apple.asset.credential.identity") return "identity";
  return "reference";
}

function ddmAssetReferenceFromPayload(payload) {
  return String(payload?.Payload?.Reference || "").trim();
}

function ddmAssetMetadataFromForm(form, payload) {
  if (form.declaration_type !== "asset") return {};
  return {
    reference: ddmAssetReferenceFromPayload(payload),
    payload_type: payload?.Type || "",
    updated_from: "declaration_form"
  };
}

function ddmFieldsFromPayload(templateKey, payload) {
  const body = payload?.Payload || {};
  const props = body.Properties || {};
  switch (templateKey) {
    case "software_update":
      return {
        targetOSVersion: body.TargetOSVersion || "latest",
        targetBuildVersion: body.TargetBuildVersion || "",
        targetLocalDateTime: body.TargetLocalDateTime || "",
        maximumUserDeferrals: String(body.MaximumUserDeferrals ?? 5),
        installASAP: body.InstallASAP !== false
      };
    case "software_update_settings":
      return {
        automaticActions: body.AutomaticActions !== false,
        betaProgram: body.BetaProgram || "",
        maximumUserDeferrals: String(body.MaximumUserDeferrals ?? 5)
      };
    case "passcode":
      return {
        minimumLength: String(body.MinimumLength ?? 6),
        requireAlphanumeric: body.RequireAlphanumeric !== false,
        maximumFailedAttempts: String(body.MaximumFailedAttempts ?? 10)
      };
    case "safari":
      return {
        allowJavaScript: body.AllowJavaScript !== false,
        allowPopups: Boolean(body.AllowPopups),
        allowAutoFill: body.AllowAutoFill !== false
      };
    case "certificate_asset":
      return {
        reference: body.Reference || "",
        certificatePEM: body.CertificatePEM || ""
      };
    case "identity_asset":
      return {
        reference: body.Reference || "",
        identityP12: body.IdentityP12 || "",
        password: body.Password || ""
      };
    case "mail":
    case "exchange":
      return {
        accountDescription: body.AccountDescription || "",
        emailAddress: body.EmailAddress || "",
        hostName: body.HostName || "",
        userName: body.UserName || "",
        identityAssetReference: body.IdentityAssetReference || ""
      };
    case "google":
      return {
        accountDescription: body.AccountDescription || "",
        emailAddress: body.EmailAddress || "",
        userName: body.UserName || ""
      };
    case "ldap":
    case "calendar":
    case "contacts":
      return {
        accountDescription: body.AccountDescription || "",
        hostName: body.HostName || "",
        userName: body.UserName || "",
        useSSL: body.UseSSL !== false
      };
    case "screen_sharing":
      return { enabled: body.Enabled !== false };
    case "legacy_profile":
      return {
        profileIdentifier: body.ProfileIdentifier || "",
        profileURL: body.ProfileURL || ""
      };
    case "legacy_interactive_profile":
      return {
        profileIdentifier: body.ProfileIdentifier || "",
        profileURL: body.ProfileURL || "",
        userEnrollmentRequired: body.UserEnrollmentRequired !== false
      };
    case "management_properties":
      return {
        department: props.department || "",
        owner: props.owner || "",
        customPropertiesText: JSON.stringify(props, null, 2)
      };
    case "activation":
      return {
        predicateMode: body.Predicate && body.Predicate !== "TRUEPREDICATE" ? "custom" : "all",
        predicate: body.Predicate || "TRUEPREDICATE",
        osVersion: "",
        modelFamily: "",
        sharedIPad: "",
        statusItem: "",
        statusValue: "",
        managementProperty: "",
        managementPropertyValue: "",
        declarationsText: Array.isArray(body.Declarations) ? body.Declarations.join("\n") : ""
      };
    default:
      return {};
  }
}

function ddmNumber(value, fallback) {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function ddmActivationPredicate(fields) {
  const mode = String(fields?.predicateMode || "all");
  if (mode === "custom") return String(fields?.predicate || "").trim() || "TRUEPREDICATE";
  if (mode === "os") return `device.operating-system.version >= '${String(fields?.osVersion || "").trim()}'`;
  if (mode === "model") return `device.model.family == '${String(fields?.modelFamily || "").trim()}'`;
  if (mode === "shared_ipad") return `device.is-shared-ipad == ${fields?.sharedIPad === "false" ? "false" : "true"}`;
  if (mode === "status") return `${String(fields?.statusItem || "").trim()} == '${String(fields?.statusValue || "").trim()}'`;
  if (mode === "management_property") {
    return `management.properties.${String(fields?.managementProperty || "").trim()} == '${String(fields?.managementPropertyValue || "").trim()}'`;
  }
  return "TRUEPREDICATE";
}

function ddmPayloadFromForm(form) {
  if (form.template === "custom") {
    return JSON.parse(form.payload_text || "{}");
  }
  const template = DDM_PAYLOAD_TEMPLATES[form.template] || DDM_PAYLOAD_TEMPLATES.software_update;
  const fields = form.fields || {};
  const base = { Type: template.payload.Type, Payload: {} };
  switch (form.template) {
    case "software_update":
      base.Payload = {
        TargetOSVersion: String(fields.targetOSVersion || "").trim(),
        TargetBuildVersion: String(fields.targetBuildVersion || "").trim(),
        TargetLocalDateTime: String(fields.targetLocalDateTime || "").trim(),
        MaximumUserDeferrals: ddmNumber(fields.maximumUserDeferrals, 5),
        InstallASAP: Boolean(fields.installASAP)
      };
      break;
    case "software_update_settings":
      base.Payload = {
        AutomaticActions: Boolean(fields.automaticActions),
        BetaProgram: String(fields.betaProgram || "").trim(),
        MaximumUserDeferrals: ddmNumber(fields.maximumUserDeferrals, 5)
      };
      break;
    case "passcode":
      base.Payload = {
        MinimumLength: ddmNumber(fields.minimumLength, 6),
        RequireAlphanumeric: Boolean(fields.requireAlphanumeric),
        MaximumFailedAttempts: ddmNumber(fields.maximumFailedAttempts, 10)
      };
      break;
    case "safari":
      base.Payload = {
        AllowJavaScript: Boolean(fields.allowJavaScript),
        AllowPopups: Boolean(fields.allowPopups),
        AllowAutoFill: Boolean(fields.allowAutoFill)
      };
      break;
    case "certificate_asset":
      base.Payload = {
        Reference: String(fields.reference || "").trim(),
        CertificatePEM: String(fields.certificatePEM || "").trim()
      };
      break;
    case "identity_asset":
      base.Payload = {
        Reference: String(fields.reference || "").trim(),
        IdentityP12: String(fields.identityP12 || "").trim(),
        Password: String(fields.password || "")
      };
      break;
    case "mail":
    case "exchange":
      base.Payload = {
        AccountDescription: String(fields.accountDescription || "").trim(),
        EmailAddress: String(fields.emailAddress || "").trim(),
        HostName: String(fields.hostName || "").trim(),
        UserName: String(fields.userName || "").trim(),
        IdentityAssetReference: String(fields.identityAssetReference || "").trim()
      };
      break;
    case "google":
      base.Payload = {
        AccountDescription: String(fields.accountDescription || "").trim(),
        EmailAddress: String(fields.emailAddress || "").trim(),
        UserName: String(fields.userName || "").trim()
      };
      break;
    case "ldap":
    case "calendar":
    case "contacts":
      base.Payload = {
        AccountDescription: String(fields.accountDescription || "").trim(),
        HostName: String(fields.hostName || "").trim(),
        UserName: String(fields.userName || "").trim(),
        UseSSL: Boolean(fields.useSSL)
      };
      break;
    case "screen_sharing":
      base.Payload = { Enabled: Boolean(fields.enabled) };
      break;
    case "legacy_profile":
      base.Payload = {
        ProfileIdentifier: String(fields.profileIdentifier || "").trim(),
        ProfileURL: String(fields.profileURL || "").trim()
      };
      break;
    case "legacy_interactive_profile":
      base.Payload = {
        ProfileIdentifier: String(fields.profileIdentifier || "").trim(),
        ProfileURL: String(fields.profileURL || "").trim(),
        UserEnrollmentRequired: Boolean(fields.userEnrollmentRequired)
      };
      break;
    case "management_properties":
      try {
        base.Payload = { Properties: JSON.parse(fields.customPropertiesText || "{}") };
      } catch {
        base.Payload = {
          Properties: {
            department: String(fields.department || "").trim(),
            owner: String(fields.owner || "").trim()
          }
        };
      }
      break;
    case "activation":
      base.Payload = {
        Predicate: ddmActivationPredicate(fields),
        Declarations: String(fields.declarationsText || "")
          .split(/[\n,]+/)
          .map((value) => value.trim())
          .filter(Boolean)
      };
      break;
    default:
      return JSON.parse(form.payload_text || "{}");
  }
  return base;
}

function ddmTemplateForPayload(declarationType, payload) {
  const payloadType = payload?.Type || "";
  return (
    DDM_TEMPLATE_ORDER.find((key) => {
      const template = DDM_PAYLOAD_TEMPLATES[key];
      return template?.declaration_type === declarationType && template?.payload?.Type === payloadType;
    }) || "custom"
  );
}

function defaultDDMStatusSubscriptions() {
  return DDM_STATUS_SUBSCRIPTION_OPTIONS.map((item) => item.key);
}

function ddmAssignmentSummary(assignments, lang) {
  const list = Array.isArray(assignments) ? assignments : [];
  if (list.some((item) => item.all_devices)) return lang === "ru" ? "Все устройства" : "All devices";
  const groupCount = list.filter((item) => item.group_id).length;
  const deviceCount = list.filter((item) => item.device_udid).length;
  const parts = [];
  if (groupCount) parts.push(`${groupCount} ${lang === "ru" ? "групп" : "groups"}`);
  if (deviceCount) parts.push(`${deviceCount} UDID`);
  return parts.join(", ") || "-";
}

function prettyDDMProtocolValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "-";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function ddmRawText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

function ddmPolicyKindLabel(item, lang) {
  let payload = {};
  try {
    payload = typeof item?.payload === "string" ? JSON.parse(item.payload || "{}") : item?.payload || {};
  } catch {
    payload = {};
  }
  const type = String(payload?.Type || "");
  if (type.includes("softwareupdate")) return lang === "ru" ? "Обновление ОС" : "OS update";
  if (type.includes("passcode")) return lang === "ru" ? "Пароль" : "Passcode";
  if (type.includes("app")) return lang === "ru" ? "Приложение" : "Application";
  return lang === "ru" ? "Политика Apple" : "Apple policy";
}

function ddmDeclarationTypeLabel(value, lang) {
  if (lang !== "ru") return value || "-";
  const labels = {
    configuration: "Конфигурация",
    asset: "Ресурс",
    activation: "Активация",
    management: "Управление"
  };
  return labels[value] || value || "-";
}

function ddmChannelLabel(value, lang) {
  if (lang !== "ru") return value || "-";
  const labels = {
    device: "Устройство",
    user: "Пользователь"
  };
  return labels[value] || value || "-";
}

function ddmPolicyTargetSummary(item, lang) {
  let payload = {};
  try {
    payload = typeof item?.payload === "string" ? JSON.parse(item.payload || "{}") : item?.payload || {};
  } catch {
    payload = {};
  }
  const body = payload?.Payload || {};
  const type = String(payload?.Type || "");
  if (type.includes("softwareupdate")) {
    const version = body.TargetOSVersion || body.TargetBuildVersion || "latest";
    return lang === "ru" ? `ОС ${version}` : `OS ${version}`;
  }
  if (type.includes("passcode")) {
    return lang === "ru"
      ? `Минимум ${body.MinimumLength || 6} символов`
      : `Minimum ${body.MinimumLength || 6} characters`;
  }
  return item?.display_name || item?.identifier || "-";
}

function ddmPolicyApplicationStatus(item, statuses, lang) {
  if (item?.is_active === false) return lang === "ru" ? "Отключена" : "Disabled";
  const statusItems = Array.isArray(statuses) ? statuses : [];
  if (statusItems.length === 0) return lang === "ru" ? "Ожидает" : "Pending";
  const identifier = String(item?.identifier || "").trim();
  const relevant = statusItems.filter((status) =>
    ddmRawText(status?.declarations).includes(identifier) || ddmRawText(status?.raw_report).includes(identifier)
  );
  const haystack = relevant.map((status) => ddmRawText(status?.raw_report)).join("\n").toLowerCase();
  if (haystack.includes("unsupported") || haystack.includes("not supported")) {
    return lang === "ru" ? "Устройство не поддерживает" : "Unsupported";
  }
  if (haystack.includes("error") || haystack.includes("invalid")) {
    return lang === "ru" ? "Ошибка" : "Error";
  }
  if (relevant.length > 0) return lang === "ru" ? "Применено" : "Applied";
  return lang === "ru" ? "Ожидает" : "Pending";
}

function hasActiveDDMSoftwareUpdatePolicy(items = []) {
  return (Array.isArray(items) ? items : []).some((item) => {
    if (item?.is_active === false || item?.declaration_type !== "configuration") return false;
    const payload = typeof item?.payload === "string" ? (() => {
      try { return JSON.parse(item.payload || "{}"); } catch { return {}; }
    })() : item?.payload || {};
    return [
      "com.apple.configuration.softwareupdate.enforcement",
      "com.apple.configuration.softwareupdate.settings"
    ].includes(String(payload?.Type || ""));
  });
}

function ssoConfigToForm(config) {
  const base = createEmptySSOForm();
  const raw = config && typeof config === "object" && !Array.isArray(config) ? config : {};
  return {
    ...base,
    sso_preset: detectSSOPreset(raw),
    provider: String(raw.provider || base.provider).trim() || base.provider,
    enabled: Boolean(raw.enabled),
    enforce_sso: Boolean(raw.enforce_sso),
    auto_provision: Boolean(raw.auto_provision),
    allowed_domains_text: Array.isArray(raw.allowed_domains) ? raw.allowed_domains.join("\n") : "",
    default_role: String(raw.default_role || base.default_role).trim() || base.default_role,
    oidc_issuer_url: String(raw.oidc_issuer_url || "").trim(),
    oidc_client_id: String(raw.oidc_client_id || "").trim(),
    oidc_client_secret: "",
    oidc_scopes: String(raw.oidc_scopes || base.oidc_scopes).trim() || base.oidc_scopes,
    oidc_auth_url: String(raw.oidc_auth_url || "").trim(),
    oidc_token_url: String(raw.oidc_token_url || "").trim(),
    oidc_userinfo_url: String(raw.oidc_userinfo_url || "").trim(),
    saml_entity_id: String(raw.saml_entity_id || "").trim(),
    saml_sso_url: String(raw.saml_sso_url || "").trim(),
    saml_x509_cert: String(raw.saml_x509_cert || "").trim()
  };
}

function ssoFormToPayload(form) {
  const rawDomains = String(form?.allowed_domains_text || "");
  const allowedDomains = rawDomains
    .split(/[\s,;]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const preset = SSO_PROVIDER_PRESETS[String(form?.sso_preset || "").trim()] || SSO_PROVIDER_PRESETS.custom_oidc;
  const payload = {
    provider: preset.provider,
    enabled: Boolean(form?.enabled),
    enforce_sso: Boolean(form?.enforce_sso),
    auto_provision: Boolean(form?.auto_provision),
    allowed_domains: Array.from(new Set(allowedDomains)),
    default_role: String(form?.default_role || "helpdesk").trim() || "helpdesk",
    oidc_issuer_url: String(form?.oidc_issuer_url || "").trim(),
    oidc_client_id: String(form?.oidc_client_id || "").trim(),
    oidc_scopes: String(form?.oidc_scopes || "openid email profile").trim() || "openid email profile",
    oidc_auth_url: String(form?.oidc_auth_url || "").trim(),
    oidc_token_url: String(form?.oidc_token_url || "").trim(),
    oidc_userinfo_url: String(form?.oidc_userinfo_url || "").trim(),
    saml_entity_id: String(form?.saml_entity_id || "").trim(),
    saml_sso_url: String(form?.saml_sso_url || "").trim(),
    saml_x509_cert: String(form?.saml_x509_cert || "").trim()
  };
  const secret = String(form?.oidc_client_secret || "").trim();
  if (secret) payload.oidc_client_secret = secret;
  return payload;
}

function normalizeDashboardSectionSettings(value) {
  const out = { ...DEFAULT_DASHBOARD_SECTION_SETTINGS };
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return out;
  }
  Object.keys(DEFAULT_DASHBOARD_SECTION_SETTINGS).forEach((key) => {
    if (typeof value[key] === "boolean") {
      out[key] = value[key];
    }
  });
  return out;
}

function normalizeDashboardSectionOrder(value) {
  const remaining = new Set(DEFAULT_DASHBOARD_SECTION_ORDER);
  const out = [];
  if (Array.isArray(value)) {
    value.forEach((item) => {
      const key = String(item || "").trim();
      if (!remaining.has(key)) return;
      remaining.delete(key);
      out.push(key);
    });
  }
  DEFAULT_DASHBOARD_SECTION_ORDER.forEach((key) => {
    if (remaining.has(key)) {
      out.push(key);
    }
  });
  return out;
}

function normalizeMainNavOrder(value) {
  const remaining = new Set(DEFAULT_MAIN_NAV_ORDER);
  const out = [];
  if (Array.isArray(value)) {
    value.forEach((item) => {
      const key = String(item || "").trim();
      if (!remaining.has(key)) return;
      remaining.delete(key);
      out.push(key);
    });
  }
  DEFAULT_MAIN_NAV_ORDER.forEach((key) => {
    if (remaining.has(key)) {
      out.push(key);
    }
  });
  return out;
}

const RESTRICTIONS_TAB_ORDER = ["functionality", "apps", "media_content"];
const RESTRICTIONS_APPS_FIELD_KEYS = new Set([
  "allowITunes",
  "allowNews",
  "allowPodcasts",
  "allowGameCenter",
  "allowMultiplayerGaming",
  "allowAddingGameCenterFriends",
  "allowSafari",
  "safariAllowAutoFill",
  "forceSafariFraudWarning",
  "safariAllowJavaScript",
  "safariAllowPopups",
  "safariAcceptCookies",
  "restrictAppUsageMode"
]);
const RESTRICTIONS_MEDIA_FIELD_KEYS = new Set([
  "ratingRegion",
  "ratingMovies",
  "ratingTVShows",
  "ratingApps",
  "allowExplicitContent",
  "allowBookstoreErotica"
]);

const IOS_SYSTEM_APPS_CATALOG = Object.freeze([
  { bundleId: "com.apple.Preferences", name: "Settings", nameRu: "Настройки" },
  { bundleId: "com.apple.AppStore", name: "App Store", nameRu: "App Store" },
  { bundleId: "com.apple.MobileStore", name: "iTunes Store", nameRu: "iTunes Store" },
  { bundleId: "com.apple.MobileSMS", name: "Messages", nameRu: "Сообщения" },
  { bundleId: "com.apple.mobilemail", name: "Mail", nameRu: "Почта" },
  { bundleId: "com.apple.mobilephone", name: "Phone", nameRu: "Телефон" },
  { bundleId: "com.apple.mobilesafari", name: "Safari", nameRu: "Safari" },
  { bundleId: "com.apple.mobilecal", name: "Calendar", nameRu: "Календарь" },
  { bundleId: "com.apple.MobileAddressBook", name: "Contacts", nameRu: "Контакты" },
  { bundleId: "com.apple.camera", name: "Camera", nameRu: "Камера" },
  { bundleId: "com.apple.mobileslideshow", name: "Photos", nameRu: "Фото" },
  { bundleId: "com.apple.music", name: "Music", nameRu: "Музыка" },
  { bundleId: "com.apple.Music", name: "Music", nameRu: "Музыка" },
  { bundleId: "com.apple.tv", name: "TV", nameRu: "TV" },
  { bundleId: "com.apple.podcasts", name: "Podcasts", nameRu: "Подкасты" },
  { bundleId: "com.apple.news", name: "News", nameRu: "Новости" },
  { bundleId: "com.apple.stocks", name: "Stocks", nameRu: "Акции" },
  { bundleId: "com.apple.tips", name: "Tips", nameRu: "Советы" },
  { bundleId: "com.apple.videos", name: "Videos", nameRu: "Видео" },
  { bundleId: "com.apple.VoiceMemos", name: "Voice Memos", nameRu: "Диктофон" },
  { bundleId: "com.apple.MobileTimer", name: "Clock", nameRu: "Часы" },
  { bundleId: "com.apple.mobiletimer", name: "Clock", nameRu: "Часы" },
  { bundleId: "com.apple.calculator", name: "Calculator", nameRu: "Калькулятор" },
  { bundleId: "com.apple.compass", name: "Compass", nameRu: "Компас" },
  { bundleId: "com.apple.measure", name: "Measure", nameRu: "Рулетка" },
  { bundleId: "com.apple.reminders", name: "Reminders", nameRu: "Напоминания" },
  { bundleId: "com.apple.mobilenotes", name: "Notes", nameRu: "Заметки" },
  { bundleId: "com.apple.Maps", name: "Maps", nameRu: "Карты" },
  { bundleId: "com.apple.Home", name: "Home", nameRu: "Дом" },
  { bundleId: "com.apple.Health", name: "Health", nameRu: "Здоровье" },
  { bundleId: "com.apple.Passbook", name: "Wallet", nameRu: "Wallet" },
  { bundleId: "com.apple.Bridge", name: "Watch", nameRu: "Watch" },
  { bundleId: "com.apple.weather", name: "Weather", nameRu: "Погода" },
  { bundleId: "com.apple.DocumentsApp", name: "Files", nameRu: "Файлы" },
  { bundleId: "com.apple.iCloudDriveApp", name: "iCloud Drive", nameRu: "iCloud Drive" },
  { bundleId: "com.apple.Passwords", name: "Passwords", nameRu: "Пароли" },
  { bundleId: "com.apple.shortcuts", name: "Shortcuts", nameRu: "Команды" },
  { bundleId: "com.apple.Translate", name: "Translate", nameRu: "Перевод" },
  { bundleId: "com.apple.Magnifier", name: "Magnifier", nameRu: "Лупа" },
  { bundleId: "com.apple.clips", name: "Clips", nameRu: "Clips" },
  { bundleId: "com.apple.iBooks", name: "Books", nameRu: "Книги" },
  { bundleId: "com.apple.findmy", name: "Find My", nameRu: "Локатор" },
  { bundleId: "com.apple.facetime", name: "FaceTime", nameRu: "FaceTime" },
  { bundleId: "com.apple.journal", name: "Journal", nameRu: "Дневник" },
  { bundleId: "com.apple.freeform", name: "Freeform", nameRu: "Freeform" },
  { bundleId: "com.apple.Playgrounds", name: "Swift Playgrounds", nameRu: "Swift Playgrounds" },
  { bundleId: "com.apple.GenerativePlaygroundApp", name: "Playground", nameRu: "Playground" },
  { bundleId: "com.apple.rsvp", name: "Invites", nameRu: "Приглашения" },
  { bundleId: "com.apple.AppStoreConnect", name: "App Store Connect", nameRu: "App Store Connect" },
  { bundleId: "com.apple.BarcodeScanner", name: "Barcode Scanner", nameRu: "Сканер кода" },
  { bundleId: "com.apple.ios.configurator", name: "Apple Configurator", nameRu: "Apple Configurator" },
  { bundleId: "com.apple.store.Jolly", name: "Apple Store", nameRu: "Apple Store" },
  { bundleId: "com.apple.visionproapp", name: "Apple Vision Pro", nameRu: "Apple Vision Pro" },
  { bundleId: "com.apple.iMovie", name: "iMovie", nameRu: "iMovie" },
  { bundleId: "com.apple.mobilegarageband", name: "GarageBand", nameRu: "GarageBand" },
  { bundleId: "com.apple.Keynote", name: "Keynote", nameRu: "Keynote" },
  { bundleId: "com.apple.Numbers", name: "Numbers", nameRu: "Numbers" },
  { bundleId: "com.apple.Pages", name: "Pages", nameRu: "Pages" }
]);

function normalizeRestrictionsPickerResultItem(rawItem, lang) {
  const bundleID = String(rawItem?.bundle_id || rawItem?.bundleId || "").trim();
  if (!bundleID) return null;
  const localizedName =
    lang === "ru" ? String(rawItem?.nameRu || "").trim() : String(rawItem?.nameEn || "").trim();
  const baseName = String(rawItem?.name || rawItem?.title || "").trim();
  const name = localizedName || baseName || bundleID;
  return {
    ...rawItem,
    bundle_id: bundleID,
    name
  };
}

function iosSystemAppsPickerResults(query, lang) {
  const needle = String(query || "").trim().toLowerCase();
  return IOS_SYSTEM_APPS_CATALOG
    .filter((item) => {
      if (!needle) return true;
      const bundleID = String(item.bundleId || "").toLowerCase();
      const nameEn = String(item.name || "").toLowerCase();
      const nameRu = String(item.nameRu || "").toLowerCase();
      return (
        bundleID.includes(needle) ||
        nameEn.includes(needle) ||
        nameRu.includes(needle)
      );
    })
    .map((item) =>
      normalizeRestrictionsPickerResultItem(
        {
          bundle_id: item.bundleId,
          name: lang === "ru" ? item.nameRu || item.name : item.name,
          nameEn: item.name,
          nameRu: item.nameRu,
          source: "ios_system"
        },
        lang
      )
    )
    .filter(Boolean)
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), lang));
}

function mergeRestrictionsPickerResults(lang, ...groups) {
  const byBundle = new Map();
  groups.forEach((group) => {
    const items = Array.isArray(group) ? group : [];
    items.forEach((entry) => {
      const normalized = normalizeRestrictionsPickerResultItem(entry, lang);
      if (!normalized) return;
      const key = String(normalized.bundle_id || "").toLowerCase();
      if (!key || byBundle.has(key)) return;
      byBundle.set(key, normalized);
    });
  });
  return Array.from(byBundle.values());
}

function fieldAcceptsIOSBundleIdentifier(field) {
  const key = String(field?.key || "").trim().toLowerCase();
  if (!key) return false;
  return (
    key === "bundleidentifier" ||
    key.endsWith("bundleidentifier") ||
    key === "appbundleid" ||
    key === "appbundleidentifier"
  );
}

function iosSystemBundleOptionsForLanguage(lang) {
  return [...IOS_SYSTEM_APPS_CATALOG]
    .map((item) => ({
      bundleId: String(item.bundleId || "").trim(),
      name: lang === "ru" ? String(item.nameRu || item.name || "").trim() : String(item.name || "").trim()
    }))
    .filter((item) => item.bundleId && item.name)
    .sort((a, b) => a.name.localeCompare(b.name, lang));
}

function restrictionFieldTab(fieldKey) {
  if (RESTRICTIONS_APPS_FIELD_KEYS.has(fieldKey)) return "apps";
  if (RESTRICTIONS_MEDIA_FIELD_KEYS.has(fieldKey)) return "media_content";
  return "functionality";
}

function normalizeRestrictionAppEntries(rawEntries) {
  const source = Array.isArray(rawEntries)
    ? rawEntries
    : typeof rawEntries === "string"
      ? String(rawEntries)
          .split(/\r?\n|,/)
          .map((value) => value.trim())
          .filter(Boolean)
      : [];
  const byBundle = new Map();
  source.forEach((entry) => {
    if (typeof entry === "string") {
      const bundleId = entry.trim();
      if (!bundleId) return;
      if (!byBundle.has(bundleId)) {
        byBundle.set(bundleId, { bundleId, name: bundleId });
      }
      return;
    }
    if (!entry || typeof entry !== "object") return;
    const bundleId = String(entry.bundleId || entry.bundle_id || entry.identifier || "").trim();
    if (!bundleId) return;
    const name = String(entry.name || entry.title || bundleId).trim() || bundleId;
    if (!byBundle.has(bundleId)) {
      byBundle.set(bundleId, { bundleId, name });
    }
  });
  return Array.from(byBundle.values());
}

function isLikelyBundleIdentifier(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (text.includes("..")) return false;
  return /^[A-Za-z0-9][A-Za-z0-9._-]*(\.[A-Za-z0-9._-]+)+$/.test(text);
}

const NOTIFICATION_ALERT_TYPE_TO_INT = {
  None: 0,
  Banners: 1,
  Alerts: 2
};

const NOTIFICATION_GROUPING_TYPE_TO_INT = {
  Automatic: 0,
  ByApp: 1,
  Off: 2
};

const NOTIFICATION_PREVIEW_TYPE_TO_INT = {
  Always: 0,
  WhenUnlocked: 1,
  Never: 2
};

const PROFILE_BUILDER_RU_TEXT = {
  "Functionality": "Функции",
  "Apps": "Приложения",
  "Media Content": "Медиаконтент",
  "Allow use of camera": "Разрешить использование камеры",
  "Allow FaceTime": "Разрешить FaceTime",
  "Allow screenshots and screen recording": "Разрешить снимки и запись экрана",
  "Allow AirPlay, View Screen by Classroom, and Screen Sharing": "Разрешить AirPlay, просмотр экрана через Classroom и Screen Sharing",
  "Allow Classroom to perform AirPlay and View Screen without prompting": "Разрешить Classroom выполнять AirPlay и просмотр экрана без запроса",
  "Allow AirDrop": "Разрешить AirDrop",
  "Allow iMessage": "Разрешить iMessage",
  "Allow Radio": "Разрешить Radio",
  "Allow Live Voicemail": "Разрешить Live Voicemail",
  "Allow voice dialing while device is locked": "Разрешить голосовой набор при заблокированном устройстве",
  "Allow Siri": "Разрешить Siri",
  "Allow Siri while device is locked": "Разрешить Siri при заблокированном устройстве",
  "Enable Siri profanity filter": "Включить фильтр ненормативной лексики Siri",
  "Show user-generated content in Siri": "Показывать пользовательский контент в Siri",
  "Allow Siri Suggestions": "Разрешить Siri Suggestions",
  "Allow Apple Books": "Разрешить Apple Books",
  "Allow installing apps": "Разрешить установку приложений",
  "Allow installing apps using App Store": "Разрешить установку приложений через App Store",
  "Allow automatic app downloads": "Разрешить автоматическую загрузку приложений",
  "Allow installing marketplace apps": "Разрешить установку приложений из marketplace",
  "Allow installing apps from web distribution": "Разрешить установку приложений через web distribution",
  "Allow removing apps": "Разрешить удаление приложений",
  "Allow removing system apps": "Разрешить удаление системных приложений",
  "Allow app clips": "Разрешить App Clips",
  "Allow in-app purchase": "Разрешить покупки внутри приложений",
  "Require iTunes Store password for all purchases": "Требовать пароль iTunes Store для всех покупок",
  "Allow iCloud backup": "Разрешить резервное копирование в iCloud",
  "Allow iCloud documents & data": "Разрешить документы и данные iCloud",
  "Allow iCloud Keychain": "Разрешить iCloud Keychain",
  "Allow managed apps to store data in iCloud": "Разрешить управляемым приложениям хранить данные в iCloud",
  "Allow backup of enterprise books": "Разрешить резервное копирование корпоративных книг",
  "Allow notes and highlights sync for enterprise books": "Разрешить синхронизацию заметок и выделений для корпоративных книг",
  "Allow Shared Albums": "Разрешить Shared Albums",
  "Allow iCloud Photos": "Разрешить iCloud Photos",
  "Allow My Photo Stream": "Разрешить My Photo Stream",
  "Allow automatic sync while roaming": "Разрешить автоматическую синхронизацию в роуминге",
  "Allow USB drive access in Files app": "Разрешить доступ к USB-накопителям в Files",
  "Allow network drive access in Files app": "Разрешить доступ к сетевым дискам в Files",
  "Force encrypted backups": "Требовать зашифрованные резервные копии",
  "Allow apps to request to track": "Разрешить приложениям запрашивать отслеживание",
  "Allow personalized ads delivered by Apple": "Разрешить персонализированную рекламу Apple",
  "Allow Genmoji": "Разрешить Genmoji",
  "Allow Image Playground": "Разрешить Image Playground",
  "Allow Image Wand": "Разрешить Image Wand",
  "Allow Writing Tools": "Разрешить Writing Tools",
  "Allow Mail summaries": "Разрешить summaries в Mail",
  "Allow call recording": "Разрешить запись звонков",
  "Allow Notes transcription": "Разрешить transcription в Notes",
  "Allow changing default browser": "Разрешить изменение браузера по умолчанию",
  "Allow Erase All Content and Settings": "Разрешить «Стереть контент и настройки»",
  "Force preserve eSIM during Erase All Content and Settings": "Сохранять eSIM при «Стереть контент и настройки»",
  "Allow users to accept untrusted TLS certificates": "Разрешить пользователям принимать недоверенные TLS-сертификаты",
  "Allow automatic updates to certificate trust settings": "Разрешить автоматическое обновление настроек доверия сертификатов",
  "Allow trusting new enterprise app authors": "Разрешить доверять новым авторам корпоративных приложений",
  "Allow installing configuration profiles": "Разрешить установку configuration profiles",
  "Allow adding VPN configurations": "Разрешить добавление VPN-конфигураций",
  "Force automatic date and time": "Принудительно включить автоматические дату и время",
  "Allow Classroom to lock to an app and lock the device without prompting": "Разрешить Classroom закреплять приложение и блокировать устройство без запроса",
  "Automatically join Classroom classes without prompting": "Автоматически присоединяться к занятиям Classroom без запроса",
  "Require teacher permission to leave Classroom unmanaged classes": "Требовать разрешение преподавателя для выхода из неуправляемых занятий Classroom",
  "Force Wi‑Fi power on": "Принудительно включить Wi-Fi",
  "Allow modifying account settings": "Разрешить изменение настроек учетных записей",
  "Allow modifying Bluetooth settings": "Разрешить изменение настроек Bluetooth",
  "Allow modifying cellular data app settings": "Разрешить изменение настроек сотовых данных для приложений",
  "Allow modifying cellular plan settings": "Разрешить изменение настроек сотового плана",
  "Allow modifying eSIM settings": "Разрешить изменение настроек eSIM",
  "Allow transferring eSIM to another device": "Разрешить перенос eSIM на другое устройство",
  "Allow modifying device name": "Разрешить изменение имени устройства",
  "Allow modifying notification settings": "Разрешить изменение настроек уведомлений",
  "Allow modifying passcode": "Разрешить изменение код-пароля",
  "Allow modifying Touch ID fingerprints / Face ID faces": "Разрешить изменение Touch ID / Face ID",
  "Allow Screen Time": "Разрешить Screen Time",
  "Allow modifying Wallpaper": "Разрешить изменение обоев",
  "Allow modifying Personal Hotspot settings": "Разрешить изменение настроек режима модема",
  "Allow Find My Friends": "Разрешить Find My Friends",
  "Allow Find My Devices": "Разрешить Find My Devices",
  "Allow modifying Find My Friends settings": "Разрешить изменение настроек Find My Friends",
  "Allow USB accessories while device is locked": "Разрешить USB-аксессуары при заблокированном устройстве",
  "Allow pairing with non-Configurator hosts": "Разрешить сопряжение с хостами не из Configurator",
  "Allow putting into recovery mode from an unpaired device": "Разрешить перевод в recovery mode с несопряженного устройства",
  "Allow documents from managed sources in unmanaged destinations": "Разрешить документы из управляемых источников в неуправляемых назначениях",
  "Allow documents from unmanaged sources in managed destinations": "Разрешить документы из неуправляемых источников в управляемых назначениях",
  "Treat AirDrop as unmanaged destination": "Считать AirDrop неуправляемым назначением",
  "Allow Handoff": "Разрешить Handoff",
  "Allow sending diagnostic and usage data to Apple": "Разрешить отправку диагностических данных в Apple",
  "Allow modifying diagnostics settings": "Разрешить изменение настроек диагностики",
  "Allow Touch ID / Face ID to unlock device": "Разрешить разблокировку через Touch ID / Face ID",
  "Allow password AutoFill": "Разрешить AutoFill паролей",
  "Require Touch ID / Face ID authentication before AutoFill": "Требовать Touch ID / Face ID перед AutoFill",
  "Allow unlock with Apple Watch": "Разрешить разблокировку с Apple Watch",
  "Force Apple Watch wrist detection": "Требовать обнаружение запястья Apple Watch",
  "Allow pairing with Apple Watch": "Разрешить сопряжение с Apple Watch",
  "Require passcode on first AirPlay pairing": "Требовать код-пароль при первом сопряжении AirPlay",
  "Join only Wi‑Fi networks installed by a Wi‑Fi payload": "Подключаться только к Wi-Fi сетям, установленным Wi-Fi payload",
  "Allow setting up new nearby devices": "Разрешить настройку новых устройств рядом",
  "Allow proximity based password sharing requests": "Разрешить запросы обмена паролем поблизости",
  "Allow password sharing": "Разрешить обмен паролями",
  "Allow AirPrint": "Разрешить AirPrint",
  "Allow discovery of AirPrint printers using iBeacons": "Разрешить обнаружение принтеров AirPrint через iBeacons",
  "Allow storage of AirPrint credentials in Keychain": "Разрешить хранение учетных данных AirPrint в Keychain",
  "Disallow AirPrint to destinations with untrusted certificates": "Запретить AirPrint для назначений с недоверенными сертификатами",
  "Allow predictive keyboard": "Разрешить предиктивную клавиатуру",
  "Allow keyboard shortcuts": "Разрешить сочетания клавиш",
  "Allow continuous path keyboard": "Разрешить continuous path keyboard",
  "Allow auto correction": "Разрешить автокоррекцию",
  "Allow spell check": "Разрешить проверку орфографии",
  "Allow Define": "Разрешить поиск определения",
  "Allow dictation": "Разрешить диктовку",
  "Allow personalized handwriting generation": "Разрешить персонализированную генерацию рукописного текста",
  "Allow Wallet notifications in Lock screen": "Разрешить уведомления Wallet на экране блокировки",
  "Show Control Center in Lock screen": "Показывать Control Center на экране блокировки",
  "Show Notification Center in Lock screen": "Показывать Notification Center на экране блокировки",
  "Show Today view in Lock screen": "Показывать Today view на экране блокировки",
  "Defer software updates for days": "Отложить обновления ПО на дни",
  "Allow use of iTunes Store": "Разрешить использование iTunes Store",
  "Allow use of News": "Разрешить использование News",
  "Allow use of Podcasts": "Разрешить использование Podcasts",
  "Allow use of Game Center": "Разрешить использование Game Center",
  "Allow multiplayer gaming": "Разрешить многопользовательские игры",
  "Allow adding Game Center friends": "Разрешить добавление друзей в Game Center",
  "Allow use of Safari": "Разрешить использование Safari",
  "Enable AutoFill": "Разрешить AutoFill",
  "Force fraud warning": "Включить предупреждение о мошеннических сайтах",
  "Enable JavaScript": "Разрешить JavaScript",
  "Block pop-ups": "Блокировать всплывающие окна",
  "Accept cookies": "Принимать cookies",
  "Restrict App Usage": "Ограничить использование приложений",
  "Ratings region": "Регион возрастных рейтингов",
  "Movies": "Фильмы",
  "TV Shows": "ТВ-шоу",
  "Applications": "Приложения",
  "Explicit content": "Контент 18+",
  "Erotica in Apple Books": "Эротика в Apple Books",
  "Connection Name": "Название подключения",
  "Connection Type": "Тип подключения",
  "Server": "Сервер",
  "Account": "Учетная запись",
  "User Authentication": "Аутентификация пользователя",
  "Password": "Пароль",
  "Machine Authentication": "Аутентификация устройства",
  "Shared Secret": "Shared Secret",
  "Certificate": "Сертификат",
  "Proxy Setup": "Настройка прокси",
  "Proxy Server": "Прокси-сервер",
  "Proxy Port": "Порт прокси",
  "Display name": "Отображаемое имя",
  "IP address": "IP-адрес",
  "Resource path": "Путь ресурса",
  "Port": "Порт",
  "Force TLS": "Требовать TLS",
  "TLS required": "TLS обязателен",
  "Queue name": "Имя очереди",
  "Printer model": "Модель принтера",
  "Printer UUID": "UUID принтера",
  "Description": "Описание",
  "Server host": "Хост сервера",
  "Server port": "Порт сервера",
  "Username": "Имя пользователя",
  "Use SSL": "Использовать SSL",
  "Refresh interval (minutes)": "Интервал обновления, минут",
  "Email address": "Email",
  "Sync mail days": "Синхронизировать почту за дней",
  "Prevent moving messages": "Запретить перемещение сообщений",
  "Disable Mail sync": "Отключить синхронизацию Mail",
  "Disable Contacts sync": "Отключить синхронизацию Contacts",
  "Disable Calendar sync": "Отключить синхронизацию Calendar",
  "Disable Notes sync": "Отключить синхронизацию Notes",
  "Disable sharing from managed account": "Запретить общий доступ из управляемой учетной записи",
  "Enable S/MIME": "Включить S/MIME",
  "Account description": "Описание учетной записи",
  "Account display name": "Отображаемое имя учетной записи",
  "Incoming server": "Входящий сервер",
  "Incoming port": "Порт входящего сервера",
  "Incoming username": "Имя пользователя входящего сервера",
  "Incoming password": "Пароль входящего сервера",
  "Incoming authentication": "Аутентификация входящего сервера",
  "Incoming SSL": "SSL входящего сервера",
  "Outgoing server": "Исходящий сервер",
  "Outgoing port": "Порт исходящего сервера",
  "Outgoing username": "Имя пользователя исходящего сервера",
  "Outgoing password": "Пароль исходящего сервера",
  "Outgoing authentication": "Аутентификация исходящего сервера",
  "Outgoing SSL": "SSL исходящего сервера",
  "Outgoing password same as incoming": "Пароль исходящего сервера совпадает с входящим",
  "Disable account sharing": "Отключить общий доступ к учетной записи",
  "SCEP URL": "SCEP URL",
  "Name": "Название",
  "Subject": "Subject",
  "Subject alternative names": "Subject alternative names",
  "Challenge": "Challenge",
  "Key type": "Тип ключа",
  "Key size": "Размер ключа",
  "Key usage mask": "Маска использования ключа",
  "Retries": "Повторы",
  "Retry delay (seconds)": "Задержка повтора, секунд",
  "Certificate renewal interval": "Интервал обновления сертификата",
  "CA fingerprint": "Отпечаток CA",
  "Allow key export": "Разрешить экспорт ключа",
  "Allow all apps access": "Разрешить доступ всем приложениям",
  "Client identifier": "Идентификатор клиента",
  "Hardware-bound private key": "Приватный ключ, привязанный к устройству",
  "Request Apple attestation when issuing certificate": "Запросить Apple attestation при выпуске сертификата",
  "Extension identifier": "Идентификатор extension",
  "Team identifier": "Team ID",
  "Type": "Тип",
  "Screen locked behavior": "Поведение при заблокированном экране",
  "APN type": "Тип APN",
  "APN username": "Имя пользователя APN",
  "APN password": "Пароль APN",
  "Proxy server": "Прокси-сервер",
  "Proxy port": "Порт прокси",
  "Font name": "Название шрифта",
  "Font identifier": "Идентификатор шрифта",
  "Enable notifications": "Включить уведомления",
  "Alert type": "Тип уведомления",
  "Enable badges": "Включить badges",
  "Enable sounds": "Включить звуки",
  "Show in Lock Screen": "Показывать на экране блокировки",
  "Show in Notification Center": "Показывать в Notification Center",
  "Enable critical alerts": "Включить критические уведомления",
  "Show in CarPlay": "Показывать в CarPlay",
  "Notification grouping": "Группировка уведомлений",
  "Preview type": "Тип предпросмотра",
  "App bundle identifier": "Bundle ID приложения",
  "Disable touch": "Отключить касания",
  "Disable device rotation": "Отключить поворот устройства",
  "Disable volume buttons": "Отключить кнопки громкости",
  "Disable ringer switch": "Отключить переключатель звонка",
  "Disable sleep/wake button": "Отключить кнопку сна/пробуждения",
  "Disable auto-lock": "Отключить автоблокировку",
  "Enable VoiceOver": "Включить VoiceOver",
  "Enable Zoom": "Включить Zoom",
  "Lock screen footnote": "Сноска экрана блокировки",
  "Lock screen message": "Сообщение экрана блокировки",
  "Asset tag": "Asset tag",
  "Label": "Название",
  "Allow removal": "Разрешить удаление",
  "Full screen": "Во весь экран",
  "Precomposed icon": "Precomposed icon",
  "Ignore manifest scope": "Игнорировать manifest scope",
  "Target app bundle ID": "Bundle ID целевого приложения"
};

function normalizeProfileBuilderUIText(value) {
  return String(value || "")
    .replace(/\s*\(tvOS only\)/gi, "")
    .replace(/\s*\(только для supervised\)/gi, " (supervised only)")
    .replace(/\s*\(только supervised\)/gi, " (supervised only)")
    .replace(/\s+/g, " ")
    .trim();
}

function translateProfileBuilderText(value, lang) {
  const raw = String(value || "").trim();
  if (!raw || lang !== "ru") return normalizeProfileBuilderUIText(raw);
  const withoutTvOS = raw.replace(/\s*\(tvOS only\)/gi, "");
  const supervisedSuffix = /\s*\(supervised only\)\s*$/i.test(withoutTvOS);
  const deprecatedMatch = withoutTvOS.match(/\s*\((deprecated in iOS \d+)\)\s*$/i);
  let base = withoutTvOS
    .replace(/\s*\(supervised only\)\s*$/i, "")
    .replace(/\s*\(deprecated in iOS \d+\)\s*$/i, "")
    .trim();
  let out = PROFILE_BUILDER_RU_TEXT[withoutTvOS.trim()] || PROFILE_BUILDER_RU_TEXT[base] || withoutTvOS.trim();
  if (out === withoutTvOS.trim() && supervisedSuffix) {
    out = `${base} (supervised only)`;
  }
  if (out !== withoutTvOS.trim() && supervisedSuffix && !/\(supervised only\)$/i.test(out)) {
    out = `${out} (supervised only)`;
  }
  if (deprecatedMatch && !/\(deprecated in iOS \d+\)$/i.test(out)) {
    out = `${out} (${deprecatedMatch[1]})`;
  }
  return normalizeProfileBuilderUIText(out);
}

function isTvOSOnlyProfileField(field) {
  return /\(tvOS only\)/i.test(String(field?.label || field?.labelRu || ""));
}

function mapNotificationEnumToInt(value, mapping, fallbackKey) {
  const key = String(value || "").trim();
  if (Object.prototype.hasOwnProperty.call(mapping, key)) {
    return mapping[key];
  }
  return mapping[fallbackKey];
}

function mapNotificationIntToEnum(value, mapping, fallbackKey) {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  const entry = Object.entries(mapping).find(([, intValue]) => intValue === parsed);
  return entry ? entry[0] : fallbackKey;
}

function normalizeAirPlayPasswordEntries(rawEntries) {
  const source = Array.isArray(rawEntries) ? rawEntries : [];
  const out = [];
  source.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const deviceName = String(entry.deviceName || entry.DeviceName || entry.name || "").trim();
    const password = String(entry.password || entry.Password || "").trim();
    if (!deviceName) return;
    out.push({ deviceName, password });
  });
  return out;
}

function airPrintPrinterRowsFromPayload(payloadItem = {}) {
  if (Array.isArray(payloadItem?.printers) && payloadItem.printers.length > 0) {
    return payloadItem.printers;
  }
  const hasLegacyPrinter =
    payloadItem?.ipAddress ||
    payloadItem?.IPAddress ||
    payloadItem?.hostName ||
    payloadItem?.resourcePath ||
    payloadItem?.ResourcePath ||
    payloadItem?.port ||
    payloadItem?.Port ||
    payloadItem?.forceTLS ||
    payloadItem?.ForceTLS;
  if (!hasLegacyPrinter) return [];
  return [
    {
      hostName: payloadItem.ipAddress || payloadItem.IPAddress || payloadItem.hostName || "",
      forceTLS: Boolean(payloadItem.forceTLS || payloadItem.ForceTLS),
      port: payloadItem.port || payloadItem.Port || "",
      resourcePath: payloadItem.resourcePath || payloadItem.ResourcePath || ""
    }
  ];
}

function normalizeAirPrintPrinterEntries(rawEntries) {
  const source = Array.isArray(rawEntries) ? rawEntries : [];
  return source
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const hostName = String(entry.hostName || entry.ipAddress || entry.IPAddress || entry.host || "").trim();
      const resourcePath = String(entry.resourcePath || entry.ResourcePath || "").trim();
      const port = String(entry.port ?? entry.Port ?? "").trim();
      const forceTLS = Boolean(entry.forceTLS ?? entry.ForceTLS ?? entry.useTLS ?? false);
      if (!hostName && !resourcePath && !port && !forceTLS) return null;
      return { hostName, forceTLS, port, resourcePath };
    })
    .filter(Boolean);
}

function sectionArrayItems(rawValue) {
  if (Array.isArray(rawValue)) {
    return rawValue.map((item) => String(item ?? "").trim()).filter(Boolean);
  }
  return String(rawValue ?? "")
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function blankLDAPSearchSetting() {
  return {
    description: "",
    scope: "LDAPSearchSettingScopeSubtree",
    searchBase: ""
  };
}

function normalizeLDAPSearchSettings(rawValue) {
  if (Array.isArray(rawValue)) {
    return rawValue
      .map((item) => {
        if (typeof item === "string") {
          const value = item.trim();
          return value ? { ...blankLDAPSearchSetting(), description: value } : null;
        }
        if (!item || typeof item !== "object") return null;
        const description = String(item.description ?? item.LDAPSearchSettingDescription ?? "").trim();
        const searchBase = String(item.searchBase ?? item.LDAPSearchSettingSearchBase ?? "").trim();
        const scope = String(item.scope ?? item.LDAPSearchSettingScope ?? "LDAPSearchSettingScopeSubtree").trim();
        if (!description && !searchBase) return null;
        return {
          description,
          scope: scope || "LDAPSearchSettingScopeSubtree",
          searchBase
        };
      })
      .filter(Boolean);
  }

  return String(rawValue ?? "")
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => ({ ...blankLDAPSearchSetting(), description: entry }));
}

const APPLE_PROFILE_SECTION_SCHEMAS = {
  restrictions: {
    payloadType: "com.apple.applicationaccess",
    supportsMultiple: false,
    fields: [
      { key: "allowCamera", plistKey: "allowCamera", type: "boolean", label: "Allow use of camera", defaultValue: true },
      { key: "allowFaceTime", plistKey: "allowFaceTime", type: "boolean", label: "Allow FaceTime (supervised only)", defaultValue: true },
      { key: "allowScreenShot", plistKey: "allowScreenShot", type: "boolean", label: "Allow screenshots and screen recording", defaultValue: true },
      { key: "allowAirPlayIncomingRequests", plistKey: "allowAirPlayIncomingRequests", type: "boolean", label: "Allow AirPlay, View Screen by Classroom, and Screen Sharing", defaultValue: true },
      {
        key: "forceClassroomUnpromptedScreenObservation",
        plistKey: "forceClassroomUnpromptedScreenObservation",
        type: "boolean",
        label: "Allow Classroom to perform AirPlay and View Screen without prompting (supervised only)",
        defaultValue: false
      },
      { key: "allowAirDrop", plistKey: "allowAirDrop", type: "boolean", label: "Allow AirDrop (supervised only)", defaultValue: true },
      { key: "allowiMessage", plistKey: "allowiMessage", type: "boolean", label: "Allow iMessage (supervised only)", defaultValue: true },
      { key: "allowRadioService", plistKey: "allowRadioService", type: "boolean", label: "Allow Radio (supervised only)", defaultValue: true },
      { key: "allowLiveVoicemail", plistKey: "allowLiveVoicemail", type: "boolean", label: "Allow Live Voicemail (supervised only)", defaultValue: true },
      { key: "allowVoiceDialing", plistKey: "allowVoiceDialing", type: "boolean", label: "Allow voice dialing while device is locked (deprecated in iOS 17)", defaultValue: true },
      { key: "allowAssistant", plistKey: "allowAssistant", type: "boolean", label: "Allow Siri", defaultValue: true },
      { key: "allowAssistantWhileLocked", plistKey: "allowAssistantWhileLocked", type: "boolean", label: "Allow Siri while device is locked", defaultValue: true, showWhen: { key: "allowAssistant", truthy: true } },
      { key: "forceAssistantProfanityFilter", plistKey: "forceAssistantProfanityFilter", type: "boolean", label: "Enable Siri profanity filter (supervised only)", defaultValue: false, showWhen: { key: "allowAssistant", truthy: true } },
      { key: "allowAssistantUserGeneratedContent", plistKey: "allowAssistantUserGeneratedContent", type: "boolean", label: "Show user-generated content in Siri (supervised only)", defaultValue: true, showWhen: { key: "allowAssistant", truthy: true } },
      { key: "allowAssistantSuggestions", plistKey: "allowAssistantSuggestions", type: "boolean", label: "Allow Siri Suggestions", defaultValue: true, showWhen: { key: "allowAssistant", truthy: true } },
      { key: "allowBookstore", plistKey: "allowBookstore", type: "boolean", label: "Allow Apple Books (supervised only)", defaultValue: true },
      { key: "allowAppInstallation", plistKey: "allowAppInstallation", type: "boolean", label: "Allow installing apps (supervised only)", defaultValue: true },
      { key: "allowUIAppInstallation", plistKey: "allowUIAppInstallation", type: "boolean", label: "Allow installing apps using App Store (supervised only)", defaultValue: true, showWhen: { key: "allowAppInstallation", truthy: true } },
      { key: "allowAutomaticAppDownloads", plistKey: "allowAutomaticAppDownloads", type: "boolean", label: "Allow automatic app downloads (supervised only)", defaultValue: true, showWhen: { key: "allowAppInstallation", truthy: true } },
      { key: "allowMarketplaceAppInstallation", plistKey: "allowMarketplaceAppInstallation", type: "boolean", label: "Allow installing marketplace apps (supervised only)", defaultValue: true, showWhen: { key: "allowAppInstallation", truthy: true } },
      { key: "allowAppInstallationFromWeb", plistKey: "allowAppInstallationFromWeb", type: "boolean", label: "Allow installing apps from web distribution (supervised only)", defaultValue: true, showWhen: { key: "allowAppInstallation", truthy: true } },
      { key: "allowAppRemoval", plistKey: "allowAppRemoval", type: "boolean", label: "Allow removing apps (supervised only)", defaultValue: true },
      { key: "allowSystemAppRemoval", plistKey: "allowSystemAppRemoval", type: "boolean", label: "Allow removing system apps (supervised only)", defaultValue: true },
      { key: "allowAppClips", plistKey: "allowAppClips", type: "boolean", label: "Allow app clips (supervised only)", defaultValue: true },
      { key: "allowInAppPurchases", plistKey: "allowInAppPurchases", type: "boolean", label: "Allow in-app purchase", defaultValue: true },
      { key: "forceITunesStorePasswordEntry", plistKey: "forceITunesStorePasswordEntry", type: "boolean", label: "Require iTunes Store password for all purchases (deprecated in iOS 17)", defaultValue: false },
      { key: "allowCloudBackup", plistKey: "allowCloudBackup", type: "boolean", label: "Allow iCloud backup", defaultValue: true },
      { key: "allowCloudDocumentSync", plistKey: "allowCloudDocumentSync", type: "boolean", label: "Allow iCloud documents & data (supervised only)", defaultValue: true },
      { key: "allowiCloudKeychain", plistKey: "allowiCloudKeychain", type: "boolean", label: "Allow iCloud Keychain", defaultValue: true },
      { key: "allowManagedAppsCloudSync", plistKey: "allowManagedAppsCloudSync", type: "boolean", label: "Allow managed apps to store data in iCloud", defaultValue: true },
      { key: "allowEnterpriseBookBackup", plistKey: "allowEnterpriseBookBackup", type: "boolean", label: "Allow backup of enterprise books", defaultValue: true },
      { key: "allowEnterpriseBookMetadataSync", plistKey: "allowEnterpriseBookMetadataSync", type: "boolean", label: "Allow notes and highlights sync for enterprise books", defaultValue: true },
      { key: "allowSharedStream", plistKey: "allowSharedStream", type: "boolean", label: "Allow Shared Albums", defaultValue: true },
      { key: "allowCloudPhotoLibrary", plistKey: "allowCloudPhotoLibrary", type: "boolean", label: "Allow iCloud Photos", defaultValue: true },
      { key: "allowPhotoStream", plistKey: "allowPhotoStream", type: "boolean", label: "Allow My Photo Stream (deprecated in iOS 17)", defaultValue: true },
      { key: "allowRoamingData", plistKey: "allowRoamingData", type: "boolean", label: "Allow automatic sync while roaming", defaultValue: true },
      { key: "allowFilesUSBDriveAccess", plistKey: "allowFilesUSBDriveAccess", type: "boolean", label: "Allow USB drive access in Files app (supervised only)", defaultValue: true },
      { key: "allowFilesNetworkDriveAccess", plistKey: "allowFilesNetworkDriveAccess", type: "boolean", label: "Allow network drive access in Files app (supervised only)", defaultValue: true },
      { key: "forceEncryptedBackup", plistKey: "forceEncryptedBackup", type: "boolean", label: "Force encrypted backups", defaultValue: false },
      { key: "allowAppTrackingTransparency", plistKey: "allowAppTrackingTransparency", type: "boolean", label: "Allow apps to request to track", defaultValue: true },
      { key: "allowPersonalizedAdvertising", plistKey: "allowPersonalizedAdvertising", type: "boolean", label: "Allow personalized ads delivered by Apple", defaultValue: true },
      { key: "allowGenmoji", plistKey: "allowGenmoji", type: "boolean", label: "Allow Genmoji (supervised only)", defaultValue: true },
      { key: "allowImagePlayground", plistKey: "allowImagePlayground", type: "boolean", label: "Allow Image Playground (supervised only)", defaultValue: true },
      { key: "allowImageWand", plistKey: "allowImageWand", type: "boolean", label: "Allow Image Wand (supervised only)", defaultValue: true },
      { key: "allowWritingTools", plistKey: "allowWritingTools", type: "boolean", label: "Allow Writing Tools (supervised only)", defaultValue: true },
      { key: "allowMailSummary", plistKey: "allowMailSummary", type: "boolean", label: "Allow Mail summaries (supervised only)", defaultValue: true },
      { key: "allowCallRecording", plistKey: "allowCallRecording", type: "boolean", label: "Allow call recording (supervised only)", defaultValue: true },
      { key: "allowNotesTranscription", plistKey: "allowNotesTranscription", type: "boolean", label: "Allow Notes transcription (supervised only)", defaultValue: true },
      { key: "allowDefaultBrowserModification", plistKey: "allowDefaultBrowserModification", type: "boolean", label: "Allow changing default browser (supervised only)", defaultValue: true },
      { key: "allowEraseContentAndSettings", plistKey: "allowEraseContentAndSettings", type: "boolean", label: "Allow Erase All Content and Settings (supervised only)", defaultValue: true },
      { key: "forcePreserveESIMOnErase", plistKey: "forcePreserveESIMOnErase", type: "boolean", label: "Force preserve eSIM during Erase All Content and Settings", defaultValue: false },
      { key: "allowUntrustedTLSPrompt", plistKey: "allowUntrustedTLSPrompt", type: "boolean", label: "Allow users to accept untrusted TLS certificates", defaultValue: true },
      { key: "allowCertificateTrustSettings", plistKey: "allowCertificateTrustSettings", type: "boolean", label: "Allow automatic updates to certificate trust settings", defaultValue: true },
      { key: "allowEnterpriseAppTrust", plistKey: "allowEnterpriseAppTrust", type: "boolean", label: "Allow trusting new enterprise app authors", defaultValue: true },
      { key: "allowConfigurationProfileInstallation", plistKey: "allowConfigurationProfileInstallation", type: "boolean", label: "Allow installing configuration profiles (supervised only)", defaultValue: true },
      { key: "allowVPNCreation", plistKey: "allowVPNCreation", type: "boolean", label: "Allow adding VPN configurations (supervised only)", defaultValue: true },
      { key: "forceAutomaticDateAndTime", plistKey: "forceAutomaticDateAndTime", type: "boolean", label: "Force automatic date and time (supervised only)", defaultValue: false },
      { key: "allowClassroomAppLock", plistKey: "allowClassroomAppLock", type: "boolean", label: "Allow Classroom to lock to an app and lock the device without prompting (supervised only)", defaultValue: false },
      { key: "forceClassroomAutomaticallyJoinClasses", plistKey: "forceClassroomAutomaticallyJoinClasses", type: "boolean", label: "Automatically join Classroom classes without prompting (supervised only)", defaultValue: false },
      { key: "forceClassroomRequestPermissionToLeaveClasses", plistKey: "forceClassroomRequestPermissionToLeaveClasses", type: "boolean", label: "Require teacher permission to leave Classroom unmanaged classes (supervised only)", defaultValue: false },
      { key: "forceWiFiPowerOn", plistKey: "forceWiFiPowerOn", type: "boolean", label: "Force Wi‑Fi power on (supervised only)", defaultValue: false },
      { key: "allowAccountModification", plistKey: "allowAccountModification", type: "boolean", label: "Allow modifying account settings (supervised only)", defaultValue: true },
      { key: "allowBluetoothModification", plistKey: "allowBluetoothModification", type: "boolean", label: "Allow modifying Bluetooth settings (supervised only)", defaultValue: true },
      { key: "allowAppCellularDataModification", plistKey: "allowAppCellularDataModification", type: "boolean", label: "Allow modifying cellular data app settings (supervised only)", defaultValue: true },
      { key: "allowCellularPlanModification", plistKey: "allowCellularPlanModification", type: "boolean", label: "Allow modifying cellular plan settings (supervised only)", defaultValue: true },
      { key: "allowESIMModification", plistKey: "allowESIMModification", type: "boolean", label: "Allow modifying eSIM settings (supervised only)", defaultValue: true },
      { key: "allowESIMTransfer", plistKey: "allowESIMTransfer", type: "boolean", label: "Allow transferring eSIM to another device (supervised only)", defaultValue: true },
      { key: "allowDeviceNameModification", plistKey: "allowDeviceNameModification", type: "boolean", label: "Allow modifying device name (supervised only)", defaultValue: true },
      { key: "allowNotificationSettingsModification", plistKey: "allowNotificationSettingsModification", type: "boolean", label: "Allow modifying notification settings (supervised only)", defaultValue: true },
      { key: "allowPasscodeModification", plistKey: "allowPasscodeModification", type: "boolean", label: "Allow modifying passcode (supervised only)", defaultValue: true },
      { key: "allowFingerprintModification", plistKey: "allowFingerprintModification", type: "boolean", label: "Allow modifying Touch ID fingerprints / Face ID faces (supervised only)", defaultValue: true },
      { key: "allowScreenTimeModification", plistKey: "allowScreenTimeModification", type: "boolean", label: "Allow Screen Time (supervised only)", defaultValue: true },
      { key: "allowWallpaperModification", plistKey: "allowWallpaperModification", type: "boolean", label: "Allow modifying Wallpaper (supervised only)", defaultValue: true },
      { key: "allowPersonalHotspotModification", plistKey: "allowPersonalHotspotModification", type: "boolean", label: "Allow modifying Personal Hotspot settings (supervised only)", defaultValue: true },
      { key: "allowFindMyFriends", plistKey: "allowFindMyFriends", type: "boolean", label: "Allow Find My Friends (supervised only)", defaultValue: true },
      { key: "allowFindMyDevice", plistKey: "allowFindMyDevice", type: "boolean", label: "Allow Find My Devices (supervised only)", defaultValue: true },
      { key: "allowFindMyFriendsModification", plistKey: "allowFindMyFriendsModification", type: "boolean", label: "Allow modifying Find My Friends settings (supervised only)", defaultValue: true },
      { key: "allowUSBRestrictedMode", plistKey: "allowUSBRestrictedMode", type: "boolean", label: "Allow USB accessories while device is locked (supervised only)", defaultValue: false },
      { key: "allowHostPairing", plistKey: "allowHostPairing", type: "boolean", label: "Allow pairing with non-Configurator hosts (supervised only)", defaultValue: true },
      { key: "forceUnpairedDeviceRecovery", plistKey: "forceUnpairedDeviceRecovery", type: "boolean", label: "Allow putting into recovery mode from an unpaired device (supervised only)", defaultValue: false },
      { key: "allowOpenFromManagedToUnmanaged", plistKey: "allowOpenFromManagedToUnmanaged", type: "boolean", label: "Allow documents from managed sources in unmanaged destinations", defaultValue: true },
      { key: "allowOpenFromUnmanagedToManaged", plistKey: "allowOpenFromUnmanagedToManaged", type: "boolean", label: "Allow documents from unmanaged sources in managed destinations", defaultValue: true },
      { key: "forceAirDropUnmanaged", plistKey: "forceAirDropUnmanaged", type: "boolean", label: "Treat AirDrop as unmanaged destination", defaultValue: false },
      { key: "allowActivityContinuation", plistKey: "allowActivityContinuation", type: "boolean", label: "Allow Handoff", defaultValue: true },
      { key: "allowDiagnosticSubmission", plistKey: "allowDiagnosticSubmission", type: "boolean", label: "Allow sending diagnostic and usage data to Apple", defaultValue: true },
      { key: "allowDiagnosticSubmissionModification", plistKey: "allowDiagnosticSubmissionModification", type: "boolean", label: "Allow modifying diagnostics settings (supervised only)", defaultValue: true },
      { key: "allowFingerprintForUnlock", plistKey: "allowFingerprintForUnlock", type: "boolean", label: "Allow Touch ID / Face ID to unlock device", defaultValue: true },
      { key: "allowPasswordAutoFill", plistKey: "allowPasswordAutoFill", type: "boolean", label: "Allow password AutoFill (supervised only)", defaultValue: true },
      { key: "forcePasswordAutoFillAuthentication", plistKey: "forcePasswordAutoFillAuthentication", type: "boolean", label: "Require Touch ID / Face ID authentication before AutoFill (supervised only)", defaultValue: false },
      { key: "allowPairedWatch", plistKey: "allowPairedWatch", type: "boolean", label: "Allow unlock with Apple Watch", defaultValue: true },
      { key: "forceWatchWristDetection", plistKey: "forceWatchWristDetection", type: "boolean", label: "Force Apple Watch wrist detection", defaultValue: false },
      { key: "allowWatchPairing", plistKey: "allowWatchPairing", type: "boolean", label: "Allow pairing with Apple Watch (supervised only)", defaultValue: true },
      { key: "forceAirPlayOutgoingRequestsPairingPassword", plistKey: "forceAirPlayOutgoingRequestsPairingPassword", type: "boolean", label: "Require passcode on first AirPlay pairing", defaultValue: false },
      { key: "forceWiFiToAllowedNetworksOnly", plistKey: "forceWiFiToAllowedNetworksOnly", type: "boolean", label: "Join only Wi‑Fi networks installed by a Wi‑Fi payload (supervised only)", defaultValue: false },
      { key: "allowProximitySetupToNewDevice", plistKey: "allowProximitySetupToNewDevice", type: "boolean", label: "Allow setting up new nearby devices (supervised only)", defaultValue: true },
      { key: "allowProximityPasswordSharingRequests", plistKey: "allowProximityPasswordSharingRequests", type: "boolean", label: "Allow proximity based password sharing requests (supervised only)", defaultValue: true },
      { key: "allowPasswordSharing", plistKey: "allowPasswordSharing", type: "boolean", label: "Allow password sharing (supervised only)", defaultValue: true },
      { key: "allowAirPrint", plistKey: "allowAirPrint", type: "boolean", label: "Allow AirPrint (supervised only)", defaultValue: true },
      { key: "allowAirPrintiBeaconDiscovery", plistKey: "allowAirPrintiBeaconDiscovery", type: "boolean", label: "Allow discovery of AirPrint printers using iBeacons (supervised only)", defaultValue: true },
      { key: "allowAirPrintCredentialsStorage", plistKey: "allowAirPrintCredentialsStorage", type: "boolean", label: "Allow storage of AirPrint credentials in Keychain (supervised only)", defaultValue: true },
      { key: "forceAirPrintTrustedTLSRequirement", plistKey: "forceAirPrintTrustedTLSRequirement", type: "boolean", label: "Disallow AirPrint to destinations with untrusted certificates (supervised only)", defaultValue: false },
      { key: "allowPredictiveKeyboard", plistKey: "allowPredictiveKeyboard", type: "boolean", label: "Allow predictive keyboard (supervised only)", defaultValue: true },
      { key: "allowKeyboardShortcuts", plistKey: "allowKeyboardShortcuts", type: "boolean", label: "Allow keyboard shortcuts (supervised only)", defaultValue: true },
      { key: "allowContinuousPathKeyboard", plistKey: "allowContinuousPathKeyboard", type: "boolean", label: "Allow continuous path keyboard (supervised only)", defaultValue: true },
      { key: "allowAutoCorrection", plistKey: "allowAutoCorrection", type: "boolean", label: "Allow auto correction (supervised only)", defaultValue: true },
      { key: "allowSpellCheck", plistKey: "allowSpellCheck", type: "boolean", label: "Allow spell check (supervised only)", defaultValue: true },
      {
        key: "allowDefinitionLookup",
        plistKey: "allowDefinitionLookup",
        type: "boolean",
        label: "Allow Define (supervised only)",
        labelRu: "Разрешить поиск определения (supervised only)",
        hintRu: "Разрешает пользователю выделять слово и смотреть определение через системный словарь/Lookup.",
        defaultValue: true
      },
      { key: "allowDictation", plistKey: "allowDictation", type: "boolean", label: "Allow dictation (supervised only)", defaultValue: true },
      {
        key: "allowHandwriting",
        plistKey: "allowHandwriting",
        type: "boolean",
        label: "Allow personalized handwriting generation (supervised only)",
        labelRu: "Разрешить персонализированную генерацию рукописного текста (supervised only)",
        hintRu: "Разрешает системной функции генерировать рукописный текст в персональном стиле пользователя, если функция доступна на устройстве.",
        defaultValue: true
      },
      { key: "allowWalletNotificationView", plistKey: "allowWalletNotificationView", type: "boolean", label: "Allow Wallet notifications in Lock screen", defaultValue: true },
      { key: "allowLockScreenControlCenter", plistKey: "allowLockScreenControlCenter", type: "boolean", label: "Show Control Center in Lock screen", defaultValue: true },
      { key: "allowLockScreenNotificationsView", plistKey: "allowLockScreenNotificationsView", type: "boolean", label: "Show Notification Center in Lock screen", defaultValue: true },
      { key: "allowLockScreenTodayView", plistKey: "allowLockScreenTodayView", type: "boolean", label: "Show Today view in Lock screen", defaultValue: true },
      { key: "allowRemoteAppPairing", plistKey: "allowRemoteAppPairing", type: "boolean", label: "Allow pairing with Remote app (tvOS only)", defaultValue: true },
      { key: "allowIncomingAirPlayRequests", plistKey: "allowIncomingAirPlayRequests", type: "boolean", label: "Allow incoming AirPlay requests (tvOS only)", defaultValue: true },
      { key: "allowDeviceSleep", plistKey: "allowDeviceSleep", type: "boolean", label: "Allow device sleep (tvOS only)", defaultValue: true },
      {
        key: "deferSoftwareUpdatesDays",
        plistKey: "enforcedSoftwareUpdateDelay",
        type: "number",
        label: "Defer software updates for days (maximum 90 days) (supervised only)",
        labelRu: "Отложить обновления ПО на дни (максимум 90 дней) (supervised only)",
        min: 1,
        max: 90,
        defaultValue: ""
      },
      {
        key: "allowITunes",
        plistKey: "allowITunes",
        type: "boolean",
        label: "Allow use of iTunes Store (supervised only)",
        labelRu: "Разрешить использование iTunes Store (только для supervised)",
        defaultValue: true
      },
      {
        key: "allowNews",
        plistKey: "allowNews",
        type: "boolean",
        label: "Allow use of News (supervised only)",
        labelRu: "Разрешить использование News (только для supervised)",
        defaultValue: true
      },
      {
        key: "allowPodcasts",
        plistKey: "allowPodcasts",
        type: "boolean",
        label: "Allow use of Podcasts (supervised only)",
        labelRu: "Разрешить использование Podcasts (только для supervised)",
        defaultValue: true
      },
      {
        key: "allowGameCenter",
        plistKey: "allowGameCenter",
        type: "boolean",
        label: "Allow use of Game Center (supervised only)",
        labelRu: "Разрешить использование Game Center (только для supervised)",
        defaultValue: true
      },
      {
        key: "allowMultiplayerGaming",
        plistKey: "allowMultiplayerGaming",
        type: "boolean",
        label: "Allow multiplayer gaming (supervised only)",
        labelRu: "Разрешить многопользовательские игры (только для supervised)",
        defaultValue: true,
        showWhen: { key: "allowGameCenter", truthy: true }
      },
      {
        key: "allowAddingGameCenterFriends",
        plistKey: "allowAddingGameCenterFriends",
        type: "boolean",
        label: "Allow adding Game Center friends (supervised only)",
        labelRu: "Разрешить добавление друзей в Game Center (только для supervised)",
        defaultValue: true,
        showWhen: { key: "allowGameCenter", truthy: true }
      },
      {
        key: "allowSafari",
        plistKey: "allowSafari",
        type: "boolean",
        label: "Allow use of Safari (supervised only)",
        labelRu: "Разрешить использование Safari (только для supervised)",
        defaultValue: true
      },
      {
        key: "safariAllowAutoFill",
        plistKey: "safariAllowAutoFill",
        type: "boolean",
        label: "Enable AutoFill (supervised only)",
        labelRu: "Разрешить AutoFill (только для supervised)",
        defaultValue: true,
        showWhen: { key: "allowSafari", truthy: true }
      },
      {
        key: "forceSafariFraudWarning",
        plistKey: "forceSafariFraudWarning",
        type: "boolean",
        label: "Force fraud warning",
        labelRu: "Включить предупреждение о мошеннических сайтах",
        defaultValue: false,
        showWhen: { key: "allowSafari", truthy: true }
      },
      {
        key: "safariAllowJavaScript",
        plistKey: "safariAllowJavaScript",
        type: "boolean",
        label: "Enable JavaScript",
        labelRu: "Разрешить JavaScript",
        defaultValue: true,
        showWhen: { key: "allowSafari", truthy: true }
      },
      {
        key: "safariAllowPopups",
        plistKey: "safariAllowPopups",
        type: "boolean",
        label: "Block pop-ups",
        labelRu: "Блокировать всплывающие окна",
        invertBool: true,
        defaultValue: false,
        showWhen: { key: "allowSafari", truthy: true }
      },
      {
        key: "safariAcceptCookies",
        plistKey: "safariAcceptCookies",
        type: "select_number",
        label: "Accept cookies",
        labelRu: "Принимать cookies",
        defaultValue: "2",
        options: [
          { value: "0", label: "Never", labelRu: "Никогда" },
          { value: "1", label: "From current website only", labelRu: "Только с текущего веб‑сайта" },
          { value: "2", label: "Always", labelRu: "Всегда" }
        ],
        showWhen: { key: "allowSafari", truthy: true }
      },
      {
        key: "restrictAppUsageMode",
        type: "select",
        label: "Restrict App Usage (supervised only)",
        labelRu: "Ограничить использование приложений (только для supervised)",
        defaultValue: "AllowAll",
        serialize: false,
        options: [
          { value: "AllowAll", label: "Allow all apps", labelRu: "Разрешить все приложения" },
          { value: "DenySome", label: "Do not allow some apps", labelRu: "Не разрешать некоторые приложения" },
          { value: "AllowSome", label: "Only allow some apps", labelRu: "Разрешить только некоторые приложения" }
        ]
      },
      {
        key: "ratingRegion",
        plistKey: "ratingRegion",
        type: "select",
        label: "Ratings region",
        labelRu: "Регион возрастных рейтингов",
        defaultValue: "us",
        options: [
          { value: "us", label: "United States", labelRu: "США" },
          { value: "ca", label: "Canada", labelRu: "Канада" },
          { value: "gb", label: "United Kingdom", labelRu: "Великобритания" },
          { value: "de", label: "Germany", labelRu: "Германия" },
          { value: "fr", label: "France", labelRu: "Франция" },
          { value: "au", label: "Australia", labelRu: "Австралия" }
        ]
      },
      {
        key: "ratingMovies",
        plistKey: "ratingMovies",
        type: "select_number",
        label: "Movies",
        labelRu: "Фильмы",
        defaultValue: "1000",
        options: [
          { value: "0", label: "Do not allow movies", labelRu: "Не разрешать фильмы" },
          { value: "1000", label: "Allow all movies", labelRu: "Разрешить все фильмы" }
        ]
      },
      {
        key: "ratingTVShows",
        plistKey: "ratingTVShows",
        type: "select_number",
        label: "TV Shows",
        labelRu: "ТВ‑шоу",
        defaultValue: "1000",
        options: [
          { value: "0", label: "Do not allow TV shows", labelRu: "Не разрешать ТВ‑шоу" },
          { value: "1000", label: "Allow all TV shows", labelRu: "Разрешить все ТВ‑шоу" }
        ]
      },
      {
        key: "ratingApps",
        plistKey: "ratingApps",
        type: "select_number",
        label: "Apps",
        labelRu: "Приложения",
        defaultValue: "1000",
        options: [
          { value: "0", label: "Do not allow apps", labelRu: "Не разрешать приложения" },
          { value: "1000", label: "Allow all apps", labelRu: "Разрешить все приложения" }
        ]
      },
      {
        key: "allowExplicitContent",
        plistKey: "allowExplicitContent",
        type: "boolean",
        label: "Allow playback of explicit music, podcasts & iTunes U media (supervised only)",
        labelRu: "Разрешить воспроизведение контента 18+ в музыке, подкастах и iTunes U (только для supervised)",
        defaultValue: true
      },
      {
        key: "allowBookstoreErotica",
        plistKey: "allowBookstoreErotica",
        type: "boolean",
        label: "Allow explicit sexual content in Apple Books",
        labelRu: "Разрешить контент сексуального характера в Apple Books",
        defaultValue: true
      }
    ]
  },
  domains: {
    payloadType: "com.apple.domains",
    supportsMultiple: false,
    fields: [
      {
        key: "emailDomains",
        plistKey: "EmailDomains",
        type: "array_strings",
        ui: "list",
        label: "Unmarked Email Domains",
        labelRu: "Немаркированные email-домены",
        hint: "Email addresses not matching any of these domains will be marked in Mail",
        hintRu: "Email-адреса вне этих доменов будут помечаться в Mail",
        itemPlaceholder: "example.com"
      },
      {
        key: "managedDomains",
        plistKey: "SafariManagedDomains",
        type: "array_strings",
        ui: "list",
        label: "Managed Safari Web Domains",
        labelRu: "Управляемые Safari Web Domains",
        hint: "URL patterns of domains from which documents will be considered managed",
        hintRu: "URL-шаблоны доменов, из которых документы считаются управляемыми",
        itemPlaceholder: "example.com"
      },
      {
        key: "passwordAutoFillDomains",
        plistKey: "SafariPasswordAutoFillDomains",
        type: "array_strings",
        ui: "list",
        label: "Safari Password AutoFill Domains (supervised only)",
        labelRu: "Safari Password AutoFill Domains (supervised only)",
        hint: "URL patterns of websites for which passwords can be saved",
        hintRu: "URL-шаблоны сайтов, для которых можно сохранять пароли",
        itemPlaceholder: "example.com"
      }
    ]
  },
  global_http_proxy: {
    payloadType: "com.apple.proxy.http.global",
    supportsMultiple: false,
    fields: [
      {
        key: "proxyType",
        plistKey: "ProxyType",
        type: "select",
        label: "Proxy Type",
        labelRu: "Тип прокси",
        defaultValue: "Manual",
        options: ["None", "Manual", "Auto"]
      },
      {
        key: "proxyServer",
        plistKey: "ProxyServer",
        type: "text",
        label: "Proxy Server and Port",
        labelRu: "Прокси-сервер и порт",
        hint: "Host name or IP address, and port number for the proxy server",
        hintRu: "Имя хоста или IP-адрес, а также номер порта прокси-сервера",
        placeholder: "[required]",
        showWhen: { key: "proxyType", equals: "Manual" }
      },
      {
        key: "proxyServerPort",
        plistKey: "ProxyServerPort",
        type: "number",
        label: "Port",
        labelRu: "Порт",
        placeholder: "[required]",
        showWhen: { key: "proxyType", equals: "Manual" }
      },
      {
        key: "proxyUsername",
        plistKey: "ProxyUsername",
        type: "text",
        label: "User Name",
        labelRu: "Имя пользователя",
        hint: "Optional. Use only when the proxy requires authentication; iOS may repeatedly prompt if proxy auth is not accepted.",
        hintRu: "Необязательно. Используйте только если прокси требует аутентификацию; iOS может постоянно запрашивать пароль, если proxy auth не принимается.",
        placeholder: "[optional]",
        showWhen: { key: "proxyType", equals: "Manual" }
      },
      {
        key: "proxyPassword",
        plistKey: "ProxyPassword",
        type: "password",
        label: "Password",
        labelRu: "Пароль",
        hint: "Optional. For first tests, leave blank and use a proxy without authentication.",
        hintRu: "Необязательно. Для первых тестов оставьте пустым и используйте прокси без аутентификации.",
        placeholder: "[optional]",
        showWhen: { key: "proxyType", equals: "Manual" }
      },
      {
        key: "proxyPACURL",
        plistKey: "ProxyPACURL",
        type: "text",
        label: "PAC URL",
        labelRu: "PAC URL",
        placeholder: "[required]",
        showWhen: { key: "proxyType", equals: "Auto" }
      },
      {
        key: "proxyCaptiveLoginAllowed",
        plistKey: "ProxyCaptiveLoginAllowed",
        type: "boolean",
        label: "Allow bypassing proxy to access captive networks",
        labelRu: "Разрешить обход прокси для доступа к captive networks",
        defaultValue: false
      },
      {
        key: "proxyPACFallbackAllowed",
        plistKey: "ProxyPACFallbackAllowed",
        type: "boolean",
        label: "Allow direct connection if PAC is unreachable",
        labelRu: "Разрешить прямое подключение, если PAC недоступен",
        defaultValue: false,
        showWhen: { key: "proxyType", equals: "Auto" }
      }
    ]
  },
  dns_proxy: {
    payloadType: "com.apple.dnsProxy.managed",
    supportsMultiple: false,
    fields: [
      {
        key: "appBundleIdentifier",
        plistKey: "AppBundleIdentifier",
        type: "text",
        label: "App Bundle ID",
        labelRu: "App Bundle ID",
        hint: "Bundle identifier of the app containing the DNS proxy network extension",
        hintRu: "Bundle identifier приложения, содержащего DNS proxy network extension",
        placeholder: "[required]"
      },
      {
        key: "providerBundleIdentifier",
        plistKey: "ProviderBundleIdentifier",
        type: "text",
        label: "Provider Bundle ID",
        labelRu: "Provider Bundle ID",
        hint: "Bundle identifier of the DNS proxy network extension to use",
        hintRu: "Bundle identifier используемого DNS proxy network extension",
        placeholder: "[optional]"
      },
      {
        key: "providerConfiguration",
        plistKey: "ProviderConfiguration",
        type: "textarea",
        label: "Provider Configuration",
        labelRu: "Конфигурация провайдера",
        hint: "Vendor specific configuration values",
        hintRu: "Вендор-специфичные параметры конфигурации",
        placeholder: "[optional]",
        rows: 12,
        fileLoader: true
      }
    ]
  },
  content_filter: {
    payloadType: "com.apple.webcontent-filter",
    supportsMultiple: false,
    fields: [
      {
        key: "filterType",
        type: "select",
        label: "Filter Type",
        labelRu: "Тип фильтра",
        defaultValue: "BuiltIn",
        serialize: false,
        options: [
          {
            value: "BuiltIn",
            label: "Built-in: Limit Adult Content",
            labelRu: "Built-in: Ограничить контент 18+"
          },
          {
            value: "BuiltInSpecificWebsitesOnly",
            label: "Built-in: Specific Websites Only",
            labelRu: "Built-in: Только определенные веб-сайты"
          },
          { value: "Plugin", label: "Plugin", labelRu: "Plugin" }
        ]
      },
      {
        key: "permittedURLs",
        type: "array_strings",
        ui: "list",
        label: "Allowed URLs",
        labelRu: "Разрешенные URL",
        hint: "Specific URLs that will be allowed",
        hintRu: "Конкретные URL, которые будут разрешены",
        itemPlaceholder: "https://example.com",
        serialize: false,
        showWhen: { key: "filterType", in: ["BuiltIn", "BuiltInSpecificWebsitesOnly"] }
      },
      {
        key: "blacklistedURLs",
        type: "array_strings",
        ui: "list",
        label: "Unallowed URLs",
        labelRu: "Запрещенные URL",
        hint: "Additional URLs that will not be allowed",
        hintRu: "Дополнительные URL, которые будут запрещены",
        itemPlaceholder: "https://example.com",
        serialize: false,
        showWhen: { key: "filterType", equals: "BuiltIn" }
      },
      {
        key: "userDefinedName",
        plistKey: "UserDefinedName",
        type: "text",
        label: "Filter Name",
        labelRu: "Название фильтра",
        hint: "Display name of the filter in the app and on the device",
        hintRu: "Отображаемое имя фильтра в приложении и на устройстве",
        placeholder: "[required]",
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "pluginBundleID",
        plistKey: "PluginBundleID",
        type: "text",
        label: "Identifier",
        labelRu: "Идентификатор",
        hint: "Bundle identifier of the app containing filter providers",
        hintRu: "Bundle identifier приложения, содержащего filter providers",
        placeholder: "[required]",
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "serverAddress",
        plistKey: "ServerAddress",
        type: "text",
        label: "Service Address",
        labelRu: "Адрес сервиса",
        hint: "Host name or IP address or URL for the device",
        hintRu: "Host name, IP-адрес или URL для устройства",
        placeholder: "[optional]",
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "organization",
        plistKey: "Organization",
        type: "text",
        label: "Organization",
        labelRu: "Организация",
        hint: "Organization parameter for use by the filter provider",
        hintRu: "Параметр организации для filter provider",
        placeholder: "[optional]",
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "userName",
        plistKey: "UserName",
        type: "text",
        label: "User Name",
        labelRu: "Имя пользователя",
        hint: "User name for authenticating the service",
        hintRu: "Имя пользователя для аутентификации сервиса",
        placeholder: "[optional]",
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "password",
        plistKey: "Password",
        type: "password",
        label: "Password",
        labelRu: "Пароль",
        hint: "Password for authenticating the service",
        hintRu: "Пароль для аутентификации сервиса",
        placeholder: "[optional]",
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "payloadCertificateUUID",
        plistKey: "PayloadCertificateUUID",
        type: "text",
        label: "Certificate",
        labelRu: "Сертификат",
        hint: "Certificate for authenticating to the service",
        hintRu: "Сертификат для аутентификации в сервисе",
        placeholder: "Add certificates in the Certificates payload",
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "filterBrowsers",
        plistKey: "FilterBrowsers",
        type: "boolean",
        label: "Filter WebKit Traffic",
        labelRu: "Фильтровать WebKit Traffic",
        defaultValue: true,
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "filterSockets",
        plistKey: "FilterSockets",
        type: "boolean",
        label: "Filter Socket Traffic",
        labelRu: "Фильтровать Socket Traffic",
        defaultValue: true,
        showWhen: { key: "filterType", equals: "Plugin" }
      },
      {
        key: "vendorConfig",
        plistKey: "VendorConfig",
        type: "json_dict",
        label: "Custom Data (JSON)",
        labelRu: "Custom Data (JSON)",
        hint: "Custom configuration data for the filter",
        hintRu: "Пользовательские данные конфигурации для фильтра",
        rows: 8,
        showWhen: { key: "filterType", equals: "Plugin" }
      }
    ]
  },
  certificates: {
    payloadType: "com.apple.security.pkcs1",
    payloadTypes: ["com.apple.security.pkcs1", "com.apple.security.pkcs12", "com.apple.security.root"],
    supportsMultiple: true,
    fields: []
  },
  certificate_transparency: {
    payloadType: "com.apple.security.certificatetransparency",
    supportsMultiple: false,
    fields: [
      {
        key: "excludedCertificates",
        plistKey: "ExcludedCertificates",
        type: "array_strings",
        ui: "list",
        label: "Excluded Certificates",
        labelRu: "Исключенные сертификаты",
        hint: "Certificates to be excluded from Certificate Transparency enforcement. Use the SHA-256 hash of the certificate subject public key info.",
        hintRu: "Сертификаты, исключенные из проверки Certificate Transparency. Укажите SHA-256 hash subject public key info сертификата.",
        itemPlaceholder: "SHA-256 hash"
      },
      {
        key: "excludedDomains",
        plistKey: "ExcludedDomains",
        type: "array_strings",
        ui: "list",
        label: "Excluded Domains",
        labelRu: "Исключенные домены",
        hint: "Domains to be excluded from Certificate Transparency enforcement",
        hintRu: "Домены, исключенные из проверки Certificate Transparency",
        itemPlaceholder: "example.com"
      }
    ]
  },
  passcode: {
    payloadType: "com.apple.mobiledevice.passwordpolicy",
    supportsMultiple: false,
    fields: [
      {
        key: "allowSimple",
        plistKey: "allowSimple",
        type: "boolean",
        label: "Allow simple value",
        labelRu: "Разрешить простой пароль",
        hint: "Permit the use of repeating, ascending, and descending character sequences",
        hintRu: "Разрешает повторяющиеся, возрастающие и убывающие последовательности символов",
        defaultValue: true
      },
      {
        key: "requireAlphanumeric",
        plistKey: "requireAlphanumeric",
        type: "boolean",
        label: "Require alphanumeric value",
        labelRu: "Требовать буквенно-цифровой пароль",
        hint: "Requires passcode to contain at least one letter and one number",
        hintRu: "Требует минимум одну букву и одну цифру в пароле",
        defaultValue: false
      },
      {
        key: "minLength",
        plistKey: "minLength",
        type: "select_number",
        label: "Minimum passcode length",
        labelRu: "Минимальная длина пароля",
        hint: "Smallest number of passcode characters allowed",
        hintRu: "Минимально допустимое количество символов пароля",
        defaultValue: "",
        options: [
          { value: "", label: "--", labelRu: "--" },
          { value: "4", label: "4", labelRu: "4" },
          { value: "5", label: "5", labelRu: "5" },
          { value: "6", label: "6", labelRu: "6" },
          { value: "7", label: "7", labelRu: "7" },
          { value: "8", label: "8", labelRu: "8" },
          { value: "9", label: "9", labelRu: "9" },
          { value: "10", label: "10", labelRu: "10" },
          { value: "11", label: "11", labelRu: "11" },
          { value: "12", label: "12", labelRu: "12" },
          { value: "13", label: "13", labelRu: "13" },
          { value: "14", label: "14", labelRu: "14" },
          { value: "15", label: "15", labelRu: "15" },
          { value: "16", label: "16", labelRu: "16" }
        ]
      },
      {
        key: "minComplexChars",
        plistKey: "minComplexChars",
        type: "select_number",
        label: "Minimum number of complex characters",
        labelRu: "Минимальное число сложных символов",
        hint: "Smallest number of non-alphanumeric characters allowed",
        hintRu: "Минимально допустимое количество не-буквенно-цифровых символов",
        defaultValue: "",
        options: [
          { value: "", label: "--", labelRu: "--" },
          { value: "1", label: "1", labelRu: "1" },
          { value: "2", label: "2", labelRu: "2" },
          { value: "3", label: "3", labelRu: "3" },
          { value: "4", label: "4", labelRu: "4" }
        ]
      },
      {
        key: "maxPINAgeInDays",
        plistKey: "maxPINAgeInDays",
        type: "number",
        label: "Maximum passcode age (1-730 days, or none)",
        labelRu: "Максимальный возраст пароля (1-730 дней, или none)",
        hint: "Days after which passcode must be changed",
        hintRu: "Через сколько дней пароль должен быть изменен",
        min: 1,
        max: 730
      },
      {
        key: "maxInactivity",
        plistKey: "maxInactivity",
        type: "select_number",
        label: "Maximum Auto-Lock",
        labelRu: "Максимальный Auto-Lock",
        hint: "Longest auto-lock time available to the user",
        hintRu: "Максимальное время до автоблокировки, доступное пользователю",
        defaultValue: "",
        options: [
          { value: "", label: "--", labelRu: "--" },
          { value: "0", label: "Immediately", labelRu: "Сразу" },
          { value: "1", label: "1 minute", labelRu: "1 минута" },
          { value: "2", label: "2 minutes", labelRu: "2 минуты" },
          { value: "3", label: "3 minutes", labelRu: "3 минуты" },
          { value: "4", label: "4 minutes", labelRu: "4 минуты" },
          { value: "5", label: "5 minutes", labelRu: "5 минут" }
        ]
      },
      {
        key: "pinHistory",
        plistKey: "pinHistory",
        type: "number",
        label: "Passcode history (1-50 passcodes, or none)",
        labelRu: "История паролей (1-50 паролей, или none)",
        hint: "Number of unique passcodes before reuse",
        hintRu: "Количество уникальных паролей до повторного использования",
        min: 1,
        max: 50
      },
      {
        key: "maxGracePeriod",
        plistKey: "maxGracePeriod",
        type: "select_number",
        label: "Maximum grace period for device lock",
        labelRu: "Максимальный grace period для блокировки устройства",
        hint: "Longest device lock grace period available to the user",
        hintRu: "Максимальная отсрочка блокировки устройства, доступная пользователю",
        defaultValue: "",
        options: [
          { value: "", label: "--", labelRu: "--" },
          { value: "0", label: "Immediately", labelRu: "Сразу" },
          { value: "1", label: "1 minute", labelRu: "1 минута" },
          { value: "2", label: "2 minutes", labelRu: "2 минуты" },
          { value: "5", label: "5 minutes", labelRu: "5 минут" },
          { value: "15", label: "15 minutes", labelRu: "15 минут" }
        ]
      },
      {
        key: "maxFailedAttempts",
        plistKey: "maxFailedAttempts",
        type: "select_number",
        label: "Maximum number of failed attempts",
        labelRu: "Максимальное количество неудачных попыток",
        hint: "Number of passcode entry attempts allowed before all data on device will be erased",
        hintRu: "Количество попыток ввода пароля до стирания данных на устройстве",
        defaultValue: "",
        options: [
          { value: "", label: "--", labelRu: "--" },
          { value: "4", label: "4", labelRu: "4" },
          { value: "5", label: "5", labelRu: "5" },
          { value: "6", label: "6", labelRu: "6" },
          { value: "7", label: "7", labelRu: "7" },
          { value: "8", label: "8", labelRu: "8" },
          { value: "9", label: "9", labelRu: "9" },
          { value: "10", label: "10", labelRu: "10" }
        ]
      }
    ]
  },
  vpn: {
    payloadType: "com.apple.vpn.managed",
    supportsMultiple: true,
    fields: [
      {
        key: "userDefinedName",
        plistKey: "UserDefinedName",
        type: "text",
        label: "Connection Name",
        labelRu: "Название подключения",
        hint: "Display name of the connection (displayed on the device)",
        hintRu: "Отображаемое имя соединения (показывается на устройстве)",
        placeholder: "[required]"
      },
      {
        key: "vpnType",
        plistKey: "VPNType",
        type: "select",
        label: "Connection Type",
        labelRu: "Тип подключения",
        hint: "Type of connection enabled by this policy",
        hintRu: "Тип VPN-подключения",
        defaultValue: "L2TP",
        options: [
          { value: "IKEv2", label: "IKEv2", labelRu: "IKEv2" },
          { value: "IPSec", label: "IPsec", labelRu: "IPsec" },
          { value: "L2TP", label: "L2TP", labelRu: "L2TP" },
          {
            value: "PPTP",
            label: "PPTP (iOS 9 and OS X 10.11 and earlier)",
            labelRu: "PPTP (iOS 9 и OS X 10.11 и ранее)"
          },
          { value: "CiscoAnyConnect", label: "Cisco AnyConnect", labelRu: "Cisco AnyConnect" },
          {
            value: "CiscoLegacyAnyConnect",
            label: "Cisco Legacy AnyConnect (iOS 11 and earlier)",
            labelRu: "Cisco Legacy AnyConnect (iOS 11 и ранее)"
          },
          {
            value: "JuniperSSL",
            label: "Juniper SSL (iOS 11 and earlier)",
            labelRu: "Juniper SSL (iOS 11 и ранее)"
          },
          { value: "PulseSecure", label: "Pulse Secure", labelRu: "Pulse Secure" },
          { value: "F5SSL", label: "F5 SSL", labelRu: "F5 SSL" },
          { value: "SonicWALL", label: "SonicWALL Mobile Connect", labelRu: "SonicWALL Mobile Connect" },
          { value: "ArubaVIA", label: "Aruba VIA", labelRu: "Aruba VIA" },
          { value: "CheckPointMobileVPN", label: "Check Point Mobile VPN", labelRu: "Check Point Mobile VPN" },
          { value: "CustomSSL", label: "Custom SSL", labelRu: "Custom SSL" }
        ]
      },
      {
        key: "vpnSubType",
        plistKey: "VPNSubType",
        type: "text",
        label: "Provider Bundle ID",
        labelRu: "Provider Bundle ID",
        hint: "Bundle identifier of the VPN provider extension or custom SSL plug-in",
        hintRu: "Bundle identifier VPN provider extension или Custom SSL plug-in",
        placeholder: "[required]",
        showWhen: { key: "vpnType", equals: "CustomSSL" }
      },
      {
        key: "remoteAddress",
        plistKey: "RemoteAddress",
        type: "text",
        label: "Server",
        labelRu: "Сервер",
        hint: "Host name or IP address for VPN server",
        hintRu: "Host name или IP address VPN-сервера",
        placeholder: "[required]"
      },
      {
        key: "authName",
        type: "text",
        label: "Account",
        labelRu: "Аккаунт",
        hint: "User account for authenticating the connection",
        hintRu: "Учетная запись для аутентификации соединения",
        placeholder: "[set on device]"
      },
      {
        key: "userAuthenticationType",
        type: "radio",
        label: "User Authentication",
        labelRu: "Аутентификация пользователя",
        hint: "Authentication type for connection",
        hintRu: "Тип аутентификации для соединения",
        defaultValue: "Password",
        options: [
          { value: "Password", label: "Password", labelRu: "Пароль" },
          { value: "RSASecurID", label: "RSA SecurID", labelRu: "RSA SecurID" }
        ]
      },
      {
        key: "authPassword",
        type: "password",
        label: "Password",
        labelRu: "Пароль",
        showWhen: { key: "userAuthenticationType", equals: "Password" }
      },
      {
        key: "sendAllTraffic",
        type: "boolean",
        label: "Send all traffic through VPN",
        labelRu: "Отправлять весь трафик через VPN",
        defaultValue: false
      },
      {
        key: "machineAuthenticationType",
        type: "select",
        label: "Machine Authentication",
        labelRu: "Аутентификация устройства",
        hint: "Authentication type for connection",
        hintRu: "Тип аутентификации для соединения",
        defaultValue: "SharedSecret",
        options: [
          { value: "SharedSecret", label: "Shared Secret", labelRu: "Shared Secret" },
          { value: "Certificate", label: "Certificate", labelRu: "Сертификат" },
          { value: "None", label: "None", labelRu: "Нет" }
        ]
      },
      {
        key: "sharedSecret",
        type: "password",
        label: "Shared Secret",
        labelRu: "Shared Secret",
        hint: "Shared secret for the connection",
        hintRu: "Shared secret для соединения",
        placeholder: "[optional]",
        showWhen: { key: "machineAuthenticationType", equals: "SharedSecret" }
      },
      {
        key: "payloadCertificateUUID",
        type: "text",
        label: "Certificate",
        labelRu: "Сертификат",
        hint: "Certificate for authenticating to the service",
        hintRu: "Сертификат для аутентификации в сервисе",
        showWhen: { key: "machineAuthenticationType", equals: "Certificate" }
      },
      {
        key: "proxyType",
        type: "select",
        label: "Proxy Setup",
        labelRu: "Настройка прокси",
        hint: "Configures proxies to be used with this VPN connection",
        hintRu: "Настройка прокси для этого VPN-соединения",
        defaultValue: "None",
        options: [
          { value: "None", label: "None", labelRu: "Нет" },
          { value: "Manual", label: "Manual", labelRu: "Вручную" },
          { value: "Auto", label: "Auto", labelRu: "Автоматически" }
        ]
      },
      {
        key: "proxyServer",
        type: "text",
        label: "Proxy Server",
        labelRu: "Прокси-сервер",
        showWhen: { key: "proxyType", equals: "Manual" }
      },
      {
        key: "proxyServerPort",
        type: "number",
        label: "Proxy Port",
        labelRu: "Порт прокси",
        showWhen: { key: "proxyType", equals: "Manual" }
      },
      {
        key: "proxyPACURL",
        type: "text",
        label: "Proxy PAC URL",
        labelRu: "Proxy PAC URL",
        showWhen: { key: "proxyType", equals: "Auto" }
      }
    ]
  },
  airplay: {
    payloadType: "com.apple.airplay",
    supportsMultiple: true,
    fields: []
  },
  airplay_security: {
    payloadType: "com.apple.airplay.security",
    supportsMultiple: false,
    fields: [
      {
        key: "accessType",
        plistKey: "AccessType",
        type: "select",
        label: "Access",
        labelRu: "Доступ",
        hint: "Network requirement for devices that connect to Apple TV using AirPlay",
        hintRu: "Требование к сети для устройств, которые подключаются к Apple TV через AirPlay",
        defaultValue: "ANY",
        options: [
          {
            value: "ANY",
            label: "Devices on any Wi-Fi network",
            labelRu: "Устройства в любой Wi-Fi сети"
          },
          {
            value: "WIFI_ONLY",
            label: "Devices on the same Wi-Fi network",
            labelRu: "Устройства в той же Wi-Fi сети"
          }
        ]
      },
      {
        key: "securityType",
        plistKey: "SecurityType",
        type: "select",
        label: "Security",
        labelRu: "Безопасность",
        hint: "Security requirement for devices that connect to the Apple TV using AirPlay",
        hintRu: "Требование безопасности для устройств, которые подключаются к Apple TV через AirPlay",
        defaultValue: "PASSCODE_ONCE",
        options: [
          {
            value: "PASSCODE_ONCE",
            label: "First-time passcode",
            labelRu: "Код-пароль при первом подключении"
          },
          {
            value: "PASSCODE_ALWAYS",
            label: "Passcode",
            labelRu: "Код-пароль"
          },
          {
            value: "PASSWORD",
            label: "Password",
            labelRu: "Пароль"
          }
        ]
      },
      {
        key: "password",
        plistKey: "Password",
        type: "password",
        label: "Password",
        labelRu: "Пароль",
        showWhen: { key: "securityType", equals: "PASSWORD" }
      }
    ]
  },
  airprint: {
    payloadType: "com.apple.airprint",
    supportsMultiple: false,
    fields: [
      { key: "printers", type: "airprint_printers", serialize: false, label: "Printers", labelRu: "Принтеры", defaultValue: [] }
    ]
  },
  calendar: {
    payloadType: "com.apple.caldav.account",
    supportsMultiple: true,
    fields: [
      { key: "accountDescription", plistKey: "CalDAVAccountDescription", type: "text", label: "Description" },
      { key: "hostName", plistKey: "CalDAVHostName", type: "text", label: "Server host" },
      { key: "port", plistKey: "CalDAVPort", type: "number", label: "Server port", defaultValue: 443 },
      { key: "principalURL", plistKey: "CalDAVPrincipalURL", type: "text", label: "Principal URL" },
      { key: "username", plistKey: "CalDAVUsername", type: "text", label: "Username" },
      { key: "password", plistKey: "CalDAVPassword", type: "password", label: "Password" },
      { key: "useSSL", plistKey: "CalDAVUseSSL", type: "boolean", label: "Use SSL", defaultValue: true },
      { key: "refreshInterval", plistKey: "CalDAVRefreshInterval", type: "number", label: "Refresh interval (minutes)" }
    ]
  },
  subscribed_calendars: {
    payloadType: "com.apple.subscribedcalendar.account",
    supportsMultiple: true,
    fields: [
      {
        key: "accountDescription",
        plistKey: "SubCalAccountDescription",
        type: "text",
        label: "Description",
        labelRu: "Описание",
        hint: "The description of the calendar subscription",
        hintRu: "Описание подписки на календарь"
      },
      {
        key: "accountURL",
        plistKey: "SubCalAccountURL",
        type: "text",
        label: "URL",
        labelRu: "URL",
        hint: "The URL of the calendar file",
        hintRu: "URL файла календаря",
        placeholder: "[required]"
      },
      {
        key: "username",
        plistKey: "SubCalAccountUsername",
        type: "text",
        label: "User Name",
        labelRu: "Имя пользователя",
        hint: "The user name for this subscription",
        hintRu: "Имя пользователя для этой подписки",
        placeholder: "[optional]"
      },
      {
        key: "password",
        plistKey: "SubCalAccountPassword",
        type: "password",
        label: "Password",
        labelRu: "Пароль",
        hint: "The password for this subscription",
        hintRu: "Пароль для этой подписки",
        placeholder: "[optional]"
      },
      { key: "useSSL", plistKey: "SubCalAccountUseSSL", type: "boolean", label: "Use SSL", labelRu: "Использовать SSL", defaultValue: true }
    ]
  },
  contacts: {
    payloadType: "com.apple.carddav.account",
    supportsMultiple: true,
    fields: [
      { key: "accountDescription", plistKey: "CardDAVAccountDescription", type: "text", label: "Account Description", defaultValue: "My CardDAV Account" },
      { key: "hostName", plistKey: "CardDAVHostName", type: "text", label: "Account Host Name" },
      { key: "port", plistKey: "CardDAVPort", type: "number", label: "Port", defaultValue: 443 },
      { key: "principalURL", plistKey: "CardDAVPrincipalURL", type: "text", label: "Principal URL" },
      { key: "username", plistKey: "CardDAVUsername", type: "text", label: "Account User Name" },
      { key: "password", plistKey: "CardDAVPassword", type: "password", label: "Account Password" },
      { key: "useSSL", plistKey: "CardDAVUseSSL", type: "boolean", label: "Use SSL", defaultValue: true },
      { key: "communicationServiceAppBundleID", type: "text", label: "Communication Service Rules", serialize: false }
    ]
  },
  exchange_active_sync: {
    payloadType: "com.apple.eas.account",
    supportsMultiple: true,
    fields: [
      { key: "accountName", type: "text", label: "Account Name", serialize: false, defaultValue: "Exchange ActiveSync" },
      { key: "host", plistKey: "Host", type: "text", label: "Exchange ActiveSync Host" },
      { key: "ssl", plistKey: "SSL", type: "boolean", label: "Use SSL", defaultValue: true },
      { key: "userName", plistKey: "UserName", type: "text", label: "User" },
      { key: "emailAddress", plistKey: "EmailAddress", type: "text", label: "Email Address" },
      { key: "oauth", plistKey: "OAuth", type: "boolean", label: "Use OAuth for authentication", defaultValue: false },
      { key: "password", plistKey: "Password", type: "password", label: "Password" },
      { key: "overridePreviousPassword", plistKey: "OverridePreviousPassword", type: "boolean", label: "Override previous password", defaultValue: false },
      {
        key: "mailDaysToSync",
        plistKey: "MailNumberOfPastDaysToSync",
        type: "select_number",
        label: "Past Days of Mail to Sync",
        defaultValue: 7,
        options: [
          { value: 1, label: "1 day", labelRu: "1 день" },
          { value: 3, label: "3 days", labelRu: "3 дня" },
          { value: 7, label: "1 week", labelRu: "1 неделя" },
          { value: 14, label: "2 weeks", labelRu: "2 недели" },
          { value: 31, label: "1 month", labelRu: "1 месяц" },
          { value: 0, label: "No limit", labelRu: "Без ограничения" }
        ]
      },
      { key: "authenticationCredentialName", plistKey: "AuthenticationCredentialName", type: "text", label: "Authentication Credential Name" },
      { key: "authenticationCredential", type: "select", label: "Authentication Credential", serialize: false },
      { key: "preventMove", plistKey: "PreventMove", type: "boolean", label: "Prevent moving messages", defaultValue: false },
      { key: "disableMailRecentsSyncing", plistKey: "disableMailRecentsSyncing", type: "boolean", label: "Disable recent addresses sync", defaultValue: false },
      { key: "allowMailDrop", plistKey: "allowMailDrop", type: "boolean", label: "Allow Mail Drop", defaultValue: false },
      { key: "preventAppSheet", plistKey: "PreventAppSheet", type: "boolean", label: "Use only in Mail", defaultValue: false },
      { key: "smimeSigningEnabled", plistKey: "SMIMESigningEnabled", type: "boolean", label: "Enable S/MIME signing", defaultValue: false },
      { key: "smimeSigningUserOverrideable", plistKey: "SMIMESigningUserOverrideable", type: "boolean", label: "Allow user to enable or disable S/MIME signing", defaultValue: false },
      { key: "smimeEncryptByDefault", plistKey: "SMIMEEncryptByDefault", type: "boolean", label: "Enable S/MIME encryption by default", defaultValue: false },
      { key: "smimeEncryptionUserOverrideable", plistKey: "SMIMEEncryptionUserOverrideable", type: "boolean", label: "Allow user to enable or disable S/MIME encryption", defaultValue: false },
      { key: "smimeEnablePerMessageSwitch", plistKey: "SMIMEEnablePerMessageSwitch", type: "boolean", label: "Enable per-message encryption switch", defaultValue: false },
      { key: "disableMailSync", plistKey: "disableMailSync", type: "boolean", label: "Disable Mail sync", defaultValue: false },
      { key: "disableContactsSync", plistKey: "disableContactsSync", type: "boolean", label: "Disable Contacts sync", defaultValue: false },
      { key: "disableCalendarSync", plistKey: "disableCalendarSync", type: "boolean", label: "Disable Calendar sync", defaultValue: false },
      { key: "disableRemindersSync", plistKey: "disableRemindersSync", type: "boolean", label: "Disable Reminders sync", defaultValue: false },
      { key: "disableNotesSync", plistKey: "disableNotesSync", type: "boolean", label: "Disable Notes sync", defaultValue: false },
      { key: "allowModifyMail", type: "boolean", label: "Allow users to modify Mail", serialize: false, defaultValue: true },
      { key: "allowModifyContacts", type: "boolean", label: "Allow users to modify Contacts", serialize: false, defaultValue: true },
      { key: "allowModifyCalendars", type: "boolean", label: "Allow users to modify Calendars", serialize: false, defaultValue: true },
      { key: "allowModifyReminders", type: "boolean", label: "Allow users to modify Reminders", serialize: false, defaultValue: true },
      { key: "allowModifyNotes", type: "boolean", label: "Allow users to modify Notes", serialize: false, defaultValue: true },
      { key: "communicationServiceAppBundleID", type: "text", label: "Communication Service Rules", serialize: false }
    ]
  },
  google_account: {
    payloadType: "com.apple.google-oauth",
    supportsMultiple: true,
    fields: [
      { key: "accountDescription", plistKey: "AccountDescription", type: "text", label: "Account Description" },
      { key: "accountName", plistKey: "AccountName", type: "text", label: "Account Name" },
      { key: "emailAddress", plistKey: "EmailAddress", type: "text", label: "Email Address" },
      { key: "communicationServiceAppBundleID", type: "text", label: "Communication Service Rules", serialize: false }
    ]
  },
  ldap: {
    payloadType: "com.apple.ldap.account",
    supportsMultiple: true,
    fields: [
      { key: "accountDescription", plistKey: "LDAPAccountDescription", type: "text", label: "Account Description" },
      { key: "userName", plistKey: "LDAPAccountUserName", type: "text", label: "Account User Name" },
      { key: "password", plistKey: "LDAPAccountPassword", type: "password", label: "Account Password" },
      { key: "hostName", plistKey: "LDAPAccountHostName", type: "text", label: "Account Host Name" },
      { key: "useSSL", plistKey: "LDAPAccountUseSSL", type: "boolean", label: "Use SSL", defaultValue: true },
      { key: "searchSettings", plistKey: "LDAPSearchSettings", type: "custom", label: "Search Settings", serialize: false }
    ]
  },
  mail: {
    payloadType: "com.apple.mail.managed",
    supportsMultiple: true,
    fields: [
      { key: "emailAccountDescription", plistKey: "EmailAccountDescription", type: "text", label: "Account Description" },
      { key: "accountType", type: "select", label: "Account Type", serialize: false, defaultValue: "IMAP", options: ["IMAP", "POP"] },
      { key: "emailAccountName", plistKey: "EmailAccountName", type: "text", label: "User Display Name" },
      { key: "emailAddress", plistKey: "EmailAddress", type: "text", label: "Email Address" },
      { key: "incomingHost", plistKey: "IncomingMailServerHostName", type: "text", label: "Incoming server" },
      { key: "incomingPort", plistKey: "IncomingMailServerPortNumber", type: "number", label: "Incoming port" },
      { key: "incomingUsername", plistKey: "IncomingMailServerUsername", type: "text", label: "Incoming username" },
      { key: "incomingPassword", plistKey: "IncomingPassword", type: "password", label: "Incoming password" },
      {
        key: "incomingAuth",
        plistKey: "IncomingMailServerAuthentication",
        type: "select",
        label: "Incoming authentication",
        defaultValue: "EmailAuthPassword",
        options: ["EmailAuthNone", "EmailAuthPassword", "EmailAuthCRAMMD5", "EmailAuthNTLM", "EmailAuthHTTPMD5"]
      },
      { key: "incomingUseSSL", plistKey: "IncomingMailServerUseSSL", type: "boolean", label: "Incoming SSL", defaultValue: true },
      { key: "incomingImapPath", plistKey: "IncomingMailServerIMAPPathPrefix", type: "text", label: "Incoming IMAP path prefix" },
      { key: "outgoingHost", plistKey: "OutgoingMailServerHostName", type: "text", label: "Outgoing server" },
      { key: "outgoingPort", plistKey: "OutgoingMailServerPortNumber", type: "number", label: "Outgoing port" },
      { key: "outgoingUsername", plistKey: "OutgoingMailServerUsername", type: "text", label: "Outgoing username" },
      { key: "outgoingPassword", plistKey: "OutgoingPassword", type: "password", label: "Outgoing password" },
      {
        key: "outgoingAuth",
        plistKey: "OutgoingMailServerAuthentication",
        type: "select",
        label: "Outgoing authentication",
        defaultValue: "EmailAuthPassword",
        options: ["EmailAuthNone", "EmailAuthPassword", "EmailAuthCRAMMD5", "EmailAuthNTLM", "EmailAuthHTTPMD5"]
      },
      { key: "outgoingUseSSL", plistKey: "OutgoingMailServerUseSSL", type: "boolean", label: "Outgoing SSL", defaultValue: true },
      {
        key: "outgoingPasswordSameAsIncoming",
        plistKey: "OutgoingPasswordSameAsIncomingPassword",
        type: "boolean",
        label: "Outgoing password same as incoming",
        defaultValue: true
      },
      {
        key: "incomingServerAdvanced",
        plistKey: "IncomingMailServer",
        type: "json_dict",
        label: "Incoming server advanced (JSON)",
        rows: 6
      },
      {
        key: "outgoingServerAdvanced",
        plistKey: "OutgoingMailServer",
        type: "json_dict",
        label: "Outgoing server advanced (JSON)",
        rows: 6
      },
      {
        key: "smimeEnabled",
        plistKey: "SMIMEEnabled",
        type: "boolean",
        label: "Enable S/MIME",
        defaultValue: false
      },
      { key: "preventMove", plistKey: "PreventMove", type: "boolean", label: "Prevent moving messages", defaultValue: false },
      { key: "allowMailDrop", plistKey: "allowMailDrop", type: "boolean", label: "Allow Mail Drop", defaultValue: false },
      { key: "preventAppSheet", plistKey: "PreventAppSheet", type: "boolean", label: "Use only in Mail", defaultValue: false },
      { key: "disableMailRecentsSyncing", plistKey: "disableMailRecentsSyncing", type: "boolean", label: "Disable Mail recents syncing", defaultValue: false },
      { key: "smimeSigningUserOverrideable", plistKey: "SMIMESigningUserOverrideable", type: "boolean", label: "Allow user to enable or disable S/MIME signing", defaultValue: false },
      { key: "smimeEncryptionUserOverrideable", plistKey: "SMIMEEncryptionUserOverrideable", type: "boolean", label: "Allow user to enable or disable S/MIME encryption", defaultValue: false },
      { key: "smimeEnablePerMessageSwitch", plistKey: "SMIMEEnablePerMessageSwitch", type: "boolean", label: "Enable per-message encryption switch", defaultValue: false },
      {
        key: "smimeEncryptByDefault",
        plistKey: "SMIMEEncryptByDefault",
        type: "boolean",
        label: "S/MIME: encrypt by default",
        defaultValue: false,
        showWhen: { key: "smimeEnabled", truthy: true }
      },
      {
        key: "smimeSigningEnabled",
        plistKey: "SMIMESigningEnabled",
        type: "boolean",
        label: "S/MIME: sign messages",
        defaultValue: false,
        showWhen: { key: "smimeEnabled", truthy: true }
      },
      {
        key: "smimeSigningCertificateUUID",
        plistKey: "SMIMESigningCertificateUUID",
        type: "text",
        label: "S/MIME signing certificate UUID",
        showWhen: { key: "smimeEnabled", truthy: true }
      },
      {
        key: "smimeEncryptionCertificateUUID",
        plistKey: "SMIMEEncryptionCertificateUUID",
        type: "text",
        label: "S/MIME encryption certificate UUID",
        showWhen: { key: "smimeEnabled", truthy: true }
      },
      {
        key: "smimeConfiguration",
        plistKey: "SMIME",
        type: "json_dict",
        label: "S/MIME advanced (JSON)",
        rows: 8,
        showWhen: { key: "smimeEnabled", truthy: true }
      }
    ]
  },
  macos_server_account: {
    payloadType: "com.apple.macosserver.account",
    supportsMultiple: false,
    fields: [
      { key: "serverAccountDescription", plistKey: "ServerAccountDescription", type: "text", label: "Account description" },
      { key: "hostName", plistKey: "HostName", type: "text", label: "Server host" },
      { key: "port", plistKey: "Port", type: "number", label: "Server port" },
      { key: "userName", plistKey: "UserName", type: "text", label: "Username" },
      { key: "password", plistKey: "Password", type: "password", label: "Password" },
      { key: "ssl", plistKey: "SSL", type: "boolean", label: "Use SSL", defaultValue: true }
    ]
  },
  scep: {
    payloadType: "com.apple.security.scep",
    supportsMultiple: true,
    fields: [
      { key: "url", plistKey: "URL", type: "text", label: "URL", placeholder: "[required]" },
      { key: "name", plistKey: "Name", type: "text", label: "Name", placeholder: "[optional]" },
      { key: "subject", plistKey: "Subject", type: "text", label: "Subject", placeholder: "[optional] e.g. O=Company Name/CN=Foo" },
      {
        key: "subjectAdvanced",
        plistKey: "Subject",
        type: "json_array",
        label: "Subject advanced (JSON array)",
        rows: 6
      },
      { key: "subjectAltNameType", type: "select", label: "Subject Alternative Name Type", serialize: false, defaultValue: "None", options: ["None", "RFC 822 Name", "DNS Name", "Uniform Resource Identifier"] },
      { key: "subjectAltNameValue", type: "text", label: "Subject Alternative Name Value", serialize: false },
      { key: "ntPrincipalName", type: "text", label: "NT Principal Name", serialize: false, placeholder: "[optional]" },
      { key: "subjectAltName", plistKey: "SubjectAltName", type: "array_strings", label: "Subject alternative names" },
      {
        key: "subjectAltNameAdvanced",
        plistKey: "SubjectAltName",
        type: "json_dict",
        label: "SubjectAltName advanced (JSON)",
        rows: 6
      },
      { key: "challenge", plistKey: "Challenge", type: "password", label: "Challenge" },
      {
        key: "keyType",
        plistKey: "KeyType",
        type: "select",
        label: "Key type",
        defaultValue: "RSA",
        options: ["RSA", "EC"]
      },
      { key: "keysize", plistKey: "Keysize", type: "select_number", label: "Key Size", defaultValue: 1024, options: [1024, 2048, 4096] },
      { key: "keyUsage", plistKey: "KeyUsage", type: "number", label: "Key usage mask" },
      { key: "useAsDigitalSignature", type: "boolean", label: "Use as digital signature", serialize: false, defaultValue: false },
      { key: "useForKeyEncipherment", type: "boolean", label: "Use for key encipherment", serialize: false, defaultValue: false },
      { key: "retries", plistKey: "Retries", type: "number", label: "Retries", defaultValue: 3 },
      { key: "retryDelay", plistKey: "RetryDelay", type: "number", label: "Retry Delay", defaultValue: 10 },
      { key: "certificateRenewalTimeInterval", plistKey: "CertificateRenewalTimeInterval", type: "number", label: "Certificate renewal interval" },
      { key: "certificateExpirationNotificationThreshold", type: "number", label: "Expiration notification threshold (days)", labelRu: "Порог уведомления об истечении (дней)", serialize: false },
      { key: "certificateLifecycleOwner", type: "text", label: "Lifecycle owner / CA policy", labelRu: "Владелец lifecycle / CA policy", serialize: false },
      { key: "caFingerprint", plistKey: "CAFingerprint", type: "text", label: "Fingerprint" },
      { key: "caFingerprintData", plistKey: "CAFingerprint", type: "data", label: "CA fingerprint (Base64 data)" },
      { key: "keyIsExtractable", plistKey: "KeyIsExtractable", type: "boolean", label: "Allow key export", defaultValue: false },
      { key: "extendedKeyUsage", plistKey: "ExtendedKeyUsage", type: "array_strings", label: "Extended key usage OIDs" },
      {
        key: "extendedKeyUsageAdvanced",
        plistKey: "ExtendedKeyUsage",
        type: "json_array",
        label: "Extended key usage advanced (JSON array)",
        rows: 5
      },
      { key: "allowAllAppsAccess", plistKey: "AllowAllAppsAccess", type: "boolean", label: "Allow all apps access", defaultValue: false }
    ]
  },
  acme: {
    payloadType: "com.apple.security.acme",
    supportsMultiple: true,
    fields: [
      {
        key: "directoryURL",
        plistKey: "DirectoryURL",
        type: "text",
        label: "Directory URL",
        labelRu: "Directory URL",
        placeholder: "[required]"
      },
      {
        key: "clientIdentifier",
        plistKey: "ClientIdentifier",
        type: "text",
        label: "Client identifier",
        labelRu: "Client identifier",
        placeholder: "[required]"
      },
      {
        key: "hardwareBound",
        plistKey: "HardwareBound",
        type: "boolean",
        label: "Hardware-bound private key",
        labelRu: "Hardware-bound private key",
        defaultValue: true
      },
      {
        key: "keyType",
        plistKey: "KeyType",
        type: "select",
        label: "Key type",
        labelRu: "Key type",
        defaultValue: "RSA",
        options: ["RSA", "ECSECPrimeRandom"]
      },
      {
        key: "keySize",
        plistKey: "KeySize",
        type: "select_number",
        label: "Key size",
        labelRu: "Key size",
        defaultValue: "2048",
        options: ["2048", "3072", "4096"]
      },
      {
        key: "subject",
        plistKey: "Subject",
        type: "json_array",
        label: "Subject (JSON array)",
        labelRu: "Subject (JSON array)",
        rows: 6
      },
      {
        key: "subjectAltName",
        plistKey: "SubjectAltName",
        type: "json_dict",
        label: "Subject alternative name (JSON)",
        labelRu: "Subject alternative name (JSON)",
        rows: 6
      },
      {
        key: "extendedKeyUsage",
        plistKey: "ExtendedKeyUsage",
        type: "array_strings",
        ui: "list",
        label: "Extended key usage OIDs",
        labelRu: "Extended key usage OIDs",
        itemPlaceholder: "1.3.6.1.5.5.7.3.2"
      },
      {
        key: "certificateLifetimeDays",
        type: "number",
        label: "Requested certificate lifetime (days)",
        labelRu: "Запрошенный срок сертификата (дней)",
        serialize: false
      },
      {
        key: "certificateExpirationNotificationThreshold",
        type: "number",
        label: "Expiration notification threshold (days)",
        labelRu: "Порог уведомления об истечении (дней)",
        serialize: false
      },
      {
        key: "attest",
        plistKey: "Attest",
        type: "boolean",
        label: "Request Apple attestation when issuing certificate",
        labelRu: "Запросить Apple attestation при выпуске сертификата",
        defaultValue: false
      }
    ]
  },
  sso_extension: {
    payloadType: "com.apple.extensiblesso",
    supportsMultiple: true,
    fields: [
      {
        key: "extensionIdentifier",
        plistKey: "ExtensionIdentifier",
        type: "text",
        label: "Extension identifier",
        labelRu: "Extension identifier",
        placeholder: "[required]"
      },
      {
        key: "teamIdentifier",
        plistKey: "TeamIdentifier",
        type: "text",
        label: "Team identifier",
        labelRu: "Team identifier"
      },
      {
        key: "type",
        plistKey: "Type",
        type: "select",
        label: "Type",
        labelRu: "Type",
        defaultValue: "Redirect",
        options: ["Redirect", "Credential"]
      },
      {
        key: "realm",
        plistKey: "Realm",
        type: "text",
        label: "Kerberos realm",
        labelRu: "Kerberos realm",
        showWhen: { key: "type", equals: "Credential" }
      },
      {
        key: "hosts",
        plistKey: "Hosts",
        type: "array_strings",
        ui: "list",
        label: "Hosts",
        labelRu: "Hosts",
        itemPlaceholder: "server.example.com",
        showWhen: { key: "type", equals: "Credential" }
      },
      {
        key: "urls",
        plistKey: "URLs",
        type: "array_strings",
        ui: "list",
        label: "URLs",
        labelRu: "URLs",
        itemPlaceholder: "https://login.example.com",
        showWhen: { key: "type", equals: "Redirect" }
      },
      {
        key: "screenLockedBehavior",
        plistKey: "ScreenLockedBehavior",
        type: "select",
        label: "Screen locked behavior",
        labelRu: "Screen locked behavior",
        defaultValue: "DoNotHandle",
        options: ["DoNotHandle", "Cancel", "Allow"]
      },
      {
        key: "additionalExtensionData",
        plistKey: "AdditionalExtensionData",
        type: "json_dict",
        label: "Additional extension data (JSON)",
        labelRu: "Additional extension data (JSON)",
        rows: 8
      }
    ]
  },
  cellular: {
    payloadType: "com.apple.cellular",
    supportsMultiple: false,
    fields: [
      { key: "apn", plistKey: "APN", type: "text", label: "APN" },
      {
        key: "apnType",
        plistKey: "APNType",
        type: "select",
        label: "APN type",
        defaultValue: "Default",
        options: ["Default", "Attach", "Both"]
      },
      { key: "apnUsername", plistKey: "APNUsername", type: "text", label: "APN username" },
      { key: "apnPassword", plistKey: "APNPassword", type: "password", label: "APN password" },
      { key: "attachAPN", plistKey: "AttachAPN", type: "text", label: "Attach APN" },
      { key: "attachAPNUsername", plistKey: "AttachAPNUsername", type: "text", label: "Attach APN username" },
      { key: "attachAPNPassword", plistKey: "AttachAPNPassword", type: "password", label: "Attach APN password" },
      { key: "proxyServer", plistKey: "ProxyServer", type: "text", label: "Proxy server" },
      { key: "proxyPort", plistKey: "ProxyPort", type: "number", label: "Proxy port" }
    ]
  },
  fonts: {
    payloadType: "com.apple.font",
    supportsMultiple: true,
    fields: [
      { key: "fontName", plistKey: "Name", type: "text", label: "Font name" },
      { key: "fontIdentifier", plistKey: "FontIdentifier", type: "text", label: "Font identifier" },
      { key: "fontContent", plistKey: "PayloadContent", type: "data", label: "Font payload (Base64)" }
    ]
  },
  notifications: {
    payloadType: "com.apple.notificationsettings",
    supportsMultiple: true,
    fields: [
      { key: "bundleIdentifier", plistKey: "BundleIdentifier", type: "text", label: "Bundle identifier" },
      { key: "notificationsEnabled", plistKey: "NotificationsEnabled", type: "boolean", label: "Enable notifications", defaultValue: true },
      {
        key: "alertType",
        plistKey: "AlertType",
        type: "select",
        label: "Alert type",
        defaultValue: "Banners",
        options: ["None", "Banners", "Alerts"]
      },
      { key: "badgesEnabled", plistKey: "BadgesEnabled", type: "boolean", label: "Enable badges", defaultValue: true },
      { key: "soundsEnabled", plistKey: "SoundsEnabled", type: "boolean", label: "Enable sounds", defaultValue: true },
      { key: "showInLockScreen", plistKey: "ShowInLockScreen", type: "boolean", label: "Show in Lock Screen", defaultValue: true },
      { key: "showInNotificationCenter", plistKey: "ShowInNotificationCenter", type: "boolean", label: "Show in Notification Center", defaultValue: true },
      { key: "criticalAlertEnabled", plistKey: "CriticalAlertEnabled", type: "boolean", label: "Enable critical alerts", defaultValue: false },
      { key: "showInCarPlay", plistKey: "ShowInCarPlay", type: "boolean", label: "Show in CarPlay", defaultValue: false },
      {
        key: "groupingType",
        plistKey: "GroupingType",
        type: "select",
        label: "Notification grouping",
        defaultValue: "Automatic",
        options: ["Automatic", "ByApp", "Off"]
      },
      {
        key: "previewType",
        plistKey: "PreviewType",
        type: "select",
        label: "Preview type",
        defaultValue: "Always",
        options: ["Always", "WhenUnlocked", "Never"]
      }
    ]
  },
  home_screen_layout: {
    payloadType: "com.apple.homescreenlayout",
    supportsMultiple: false,
    fields: [
      {
        key: "dock",
        plistKey: "Dock",
        type: "json_array",
        label: "Dock layout (JSON array)",
        labelRu: "Dock layout (JSON array)",
        hint: "Use dictionaries with Type and BundleID / DisplayName, matching Apple Home Screen Layout payload.",
        hintRu: "Используйте словари с Type и BundleID / DisplayName по формату Apple Home Screen Layout payload.",
        rows: 8
      },
      {
        key: "pages",
        plistKey: "Pages",
        type: "json_array",
        label: "Pages layout (JSON array)",
        labelRu: "Pages layout (JSON array)",
        rows: 12,
        placeholder: `[\n  [\n    { "Type": "Application", "BundleID": "com.apple.mobilesafari" }\n  ]\n]`
      }
    ]
  },
  app_lock: {
    payloadType: "com.apple.app.lock",
    supportsMultiple: false,
    fields: [
      {
        key: "appIdentifier",
        plistKey: "AppIdentifier",
        type: "text",
        label: "App bundle identifier",
        labelRu: "Bundle identifier приложения",
        placeholder: "[required]"
      },
      {
        key: "disableTouch",
        plistKey: "DisableTouch",
        type: "boolean",
        label: "Disable touch",
        labelRu: "Отключить touch",
        defaultValue: false
      },
      {
        key: "disableDeviceRotation",
        plistKey: "DisableDeviceRotation",
        type: "boolean",
        label: "Disable device rotation",
        labelRu: "Отключить поворот устройства",
        defaultValue: false
      },
      {
        key: "disableVolumeButtons",
        plistKey: "DisableVolumeButtons",
        type: "boolean",
        label: "Disable volume buttons",
        labelRu: "Отключить кнопки громкости",
        defaultValue: false
      },
      {
        key: "disableRingerSwitch",
        plistKey: "DisableRingerSwitch",
        type: "boolean",
        label: "Disable ringer switch",
        labelRu: "Отключить ringer switch",
        defaultValue: false
      },
      {
        key: "disableSleepWakeButton",
        plistKey: "DisableSleepWakeButton",
        type: "boolean",
        label: "Disable sleep/wake button",
        labelRu: "Отключить кнопку sleep/wake",
        defaultValue: false
      },
      {
        key: "disableAutoLock",
        plistKey: "DisableAutoLock",
        type: "boolean",
        label: "Disable auto-lock",
        labelRu: "Отключить автоблокировку",
        defaultValue: false
      },
      {
        key: "enableVoiceOver",
        plistKey: "EnableVoiceOver",
        type: "boolean",
        label: "Enable VoiceOver",
        labelRu: "Включить VoiceOver",
        defaultValue: false
      },
      {
        key: "enableZoom",
        plistKey: "EnableZoom",
        type: "boolean",
        label: "Enable Zoom",
        labelRu: "Включить Zoom",
        defaultValue: false
      }
    ]
  },
  conference_room_display: {
    payloadType: "com.apple.tv.conference.room.display",
    supportsMultiple: false,
    fields: [
      { key: "displayName", plistKey: "DisplayName", type: "text", label: "Display name" },
      { key: "messageTitle", plistKey: "MessageTitle", type: "text", label: "Message title" },
      { key: "messageText", plistKey: "MessageText", type: "textarea", label: "Message text", rows: 4 },
      { key: "airPlayPassword", plistKey: "AirPlayPassword", type: "password", label: "AirPlay password" }
    ]
  },
  tv_remote: {
    payloadType: "com.apple.tv.remote",
    supportsMultiple: false,
    fields: [
      {
        key: "allowedRemotes",
        plistKey: "AllowedRemotes",
        type: "array_strings",
        ui: "list",
        label: "Allowed Remotes (tvOS only)",
        defaultValue: []
      },
      {
        key: "allowedTVs",
        plistKey: "AllowedTVs",
        type: "array_strings",
        ui: "list",
        label: "Allowed TVs (iOS only)",
        defaultValue: []
      }
    ]
  },
  lock_screen_message: {
    payloadType: "com.apple.shareddeviceconfiguration",
    supportsMultiple: false,
    fields: [
      { key: "lockScreenMessage", plistKey: "LockScreenMessage", type: "text", label: "\"If Lost, Return to...\" Message", placeholder: "[optional]" },
      { key: "assetTagInformation", plistKey: "AssetTagInformation", type: "text", label: "Asset Tag Information", placeholder: "[optional]" },
      { key: "lockScreenFootnote", plistKey: "LockScreenFootnote", type: "textarea", label: "Lock screen footnote", rows: 3 }
    ]
  },
  web_clips: {
    payloadType: "com.apple.webClip.managed",
    supportsMultiple: true,
    fields: [
      { key: "label", plistKey: "Label", type: "text", label: "Label", placeholder: "[required]" },
      { key: "url", plistKey: "URL", type: "text", label: "URL", placeholder: "[required]" },
      { key: "isRemovable", plistKey: "IsRemovable", type: "boolean", label: "Removable", defaultValue: true },
      { key: "icon", plistKey: "Icon", type: "data", label: "Icon" },
      { key: "precomposed", plistKey: "Precomposed", type: "boolean", label: "Precomposed Icon", defaultValue: false },
      { key: "fullScreen", plistKey: "FullScreen", type: "boolean", label: "Full Screen", defaultValue: false },
      { key: "ignoreManifestScope", plistKey: "IgnoreManifestScope", type: "boolean", label: "Ignore Manifest Scope", defaultValue: false },
      { key: "targetApplicationBundleIdentifier", plistKey: "TargetApplicationBundleIdentifier", type: "text", label: "Target app bundle ID" },
    ]
  },
  filevault: {
    payloadType: "com.apple.MCX.FileVault2",
    supportsMultiple: false,
    fields: [
      { key: "enable", plistKey: "Enable", type: "select", label: "FileVault", labelRu: "FileVault", defaultValue: "On", options: ["On", "Off"] },
      { key: "defer", plistKey: "Defer", type: "boolean", label: "Defer enablement until logout", labelRu: "Отложить включение до выхода пользователя", defaultValue: true },
      { key: "deferDontAskAtUserLogout", plistKey: "DeferDontAskAtUserLogout", type: "boolean", label: "Do not ask at user logout", labelRu: "Не спрашивать при выходе пользователя", defaultValue: false },
      { key: "showRecoveryKey", plistKey: "ShowRecoveryKey", type: "boolean", label: "Show personal recovery key to user", labelRu: "Показать пользователю personal recovery key", defaultValue: false },
      { key: "useRecoveryKey", plistKey: "UseRecoveryKey", type: "boolean", label: "Create personal recovery key", labelRu: "Создавать personal recovery key", defaultValue: true },
      { key: "outputPath", plistKey: "OutputPath", type: "text", label: "Recovery key output path", labelRu: "Путь вывода recovery key" },
      { key: "certificateUUID", plistKey: "CertificateUUID", type: "text", label: "Institutional recovery certificate payload UUID", labelRu: "UUID payload сертификата восстановления" },
      {
        key: "escrowLocationDescription",
        plistKey: "EscrowLocationDescription",
        type: "text",
        label: "Escrow location description",
        labelRu: "Описание места escrow recovery key"
      },
      {
        key: "forceEnableInSetupAssistant",
        plistKey: "ForceEnableInSetupAssistant",
        type: "boolean",
        label: "Force enable in Setup Assistant",
        labelRu: "Принудительно включить в Setup Assistant",
        defaultValue: false
      }
    ]
  },
  pppc: {
    payloadType: "com.apple.TCC.configuration-profile-policy",
    supportsMultiple: false,
    fields: [
      {
        key: "services",
        plistKey: "Services",
        type: "json_dict",
        label: "Privacy Preferences Policy Control services (JSON)",
        labelRu: "PPPC Services (JSON)",
        hint: "Configure service dictionaries such as Accessibility, SystemPolicyAllFiles, Camera, Microphone, AppleEvents.",
        hintRu: "Настройте словари сервисов: Accessibility, SystemPolicyAllFiles, Camera, Microphone, AppleEvents.",
        rows: 14,
        placeholder: `{\n  "Accessibility": [\n    {\n      "Identifier": "com.example.agent",\n      "IdentifierType": "bundleID",\n      "CodeRequirement": "identifier \\"com.example.agent\\"",\n      "Allowed": true\n    }\n  ]\n}`
      }
    ]
  },
  system_extensions: {
    payloadType: "com.apple.system-extension-policy",
    supportsMultiple: false,
    fields: [
      { key: "allowedTeamIdentifiers", plistKey: "AllowedTeamIdentifiers", type: "array_strings", ui: "list", label: "Allowed team identifiers", labelRu: "Разрешенные Team ID", itemPlaceholder: "ABCDE12345" },
      { key: "allowedSystemExtensions", plistKey: "AllowedSystemExtensions", type: "json_dict", label: "Allowed system extensions (JSON)", labelRu: "Allowed system extensions (JSON)", rows: 8 },
      { key: "allowedSystemExtensionTypes", plistKey: "AllowedSystemExtensionTypes", type: "json_dict", label: "Allowed system extension types (JSON)", labelRu: "Allowed system extension types (JSON)", rows: 8 },
      { key: "removableSystemExtensions", plistKey: "RemovableSystemExtensions", type: "json_dict", label: "Removable system extensions (JSON)", labelRu: "Removable system extensions (JSON)", rows: 8 }
    ]
  },
  kernel_extensions: {
    payloadType: "com.apple.syspolicy.kernel-extension-policy",
    supportsMultiple: false,
    fields: [
      { key: "allowedTeamIdentifiers", plistKey: "AllowedTeamIdentifiers", type: "array_strings", ui: "list", label: "Allowed team identifiers", labelRu: "Разрешенные Team ID", itemPlaceholder: "ABCDE12345" },
      { key: "allowedKernelExtensions", plistKey: "AllowedKernelExtensions", type: "json_dict", label: "Allowed kernel extensions (JSON)", labelRu: "Allowed kernel extensions (JSON)", rows: 8 },
      { key: "allowUserOverrides", plistKey: "AllowUserOverrides", type: "boolean", label: "Allow user overrides", labelRu: "Разрешить user overrides", defaultValue: false }
    ]
  },
  firewall: {
    payloadType: "com.apple.security.firewall",
    supportsMultiple: false,
    fields: [
      { key: "enableFirewall", plistKey: "EnableFirewall", type: "boolean", label: "Enable firewall", labelRu: "Включить firewall", defaultValue: true },
      { key: "blockAllIncoming", plistKey: "BlockAllIncoming", type: "boolean", label: "Block all incoming connections", labelRu: "Блокировать все входящие подключения", defaultValue: false },
      { key: "enableStealthMode", plistKey: "EnableStealthMode", type: "boolean", label: "Enable stealth mode", labelRu: "Включить stealth mode", defaultValue: true },
      { key: "applications", plistKey: "Applications", type: "json_array", label: "Application rules (JSON array)", labelRu: "Правила приложений (JSON array)", rows: 8 }
    ]
  },
  login_items: {
    payloadType: "com.apple.loginitems.managed",
    supportsMultiple: false,
    fields: [
      { key: "autoLaunchedApplicationDictionary-managed", plistKey: "AutoLaunchedApplicationDictionary-managed", type: "json_array", label: "Managed login items (JSON array)", labelRu: "Managed login items (JSON array)", rows: 10 },
      { key: "autoLaunchedApplicationDictionary", plistKey: "AutoLaunchedApplicationDictionary", type: "json_array", label: "User-visible login items (JSON array)", labelRu: "User-visible login items (JSON array)", rows: 10 }
    ]
  },
  background_tasks: {
    payloadType: "com.apple.servicemanagement",
    supportsMultiple: false,
    fields: [
      { key: "rules", plistKey: "Rules", type: "json_array", label: "Background task management rules (JSON array)", labelRu: "Правила background tasks (JSON array)", rows: 12 }
    ]
  },
  macos_software_update: {
    payloadType: "com.apple.SoftwareUpdate",
    supportsMultiple: false,
    fields: [
      { key: "automaticCheckEnabled", plistKey: "AutomaticCheckEnabled", type: "boolean", label: "Check for updates automatically", labelRu: "Автоматически проверять обновления", defaultValue: true },
      { key: "automaticDownload", plistKey: "AutomaticDownload", type: "boolean", label: "Download updates automatically", labelRu: "Автоматически загружать обновления", defaultValue: true },
      { key: "automaticallyInstallMacOSUpdates", plistKey: "AutomaticallyInstallMacOSUpdates", type: "boolean", label: "Install macOS updates automatically", labelRu: "Автоматически устанавливать macOS updates", defaultValue: false },
      { key: "configDataInstall", plistKey: "ConfigDataInstall", type: "boolean", label: "Install system data files", labelRu: "Устанавливать system data files", defaultValue: true },
      { key: "criticalUpdateInstall", plistKey: "CriticalUpdateInstall", type: "boolean", label: "Install critical updates", labelRu: "Устанавливать critical updates", defaultValue: true },
      { key: "restrictSoftwareUpdateRequireAdminToInstall", plistKey: "restrict-software-update-require-admin-to-install", type: "boolean", label: "Require admin to install updates", labelRu: "Требовать администратора для установки обновлений", defaultValue: false },
      { key: "enforcedSoftwareUpdateDelay", plistKey: "enforcedSoftwareUpdateDelay", type: "number", label: "Major update deferral days", labelRu: "Отсрочка major updates, дней", min: 0, max: 90 },
      { key: "enforcedSoftwareUpdateMinorOSDeferredInstallDelay", plistKey: "enforcedSoftwareUpdateMinorOSDeferredInstallDelay", type: "number", label: "Minor update deferral days", labelRu: "Отсрочка minor updates, дней", min: 0, max: 90 },
      { key: "enforcedSoftwareUpdateNonOSDeferredInstallDelay", plistKey: "enforcedSoftwareUpdateNonOSDeferredInstallDelay", type: "number", label: "Non-OS update deferral days", labelRu: "Отсрочка non-OS updates, дней", min: 0, max: 90 }
    ]
  },
  local_admin: {
    payloadType: "com.apple.localadmin",
    supportsMultiple: true,
    fields: [
      { key: "userName", plistKey: "UserName", type: "text", label: "Local admin username", labelRu: "Имя локального администратора", placeholder: "[required]" },
      { key: "fullName", plistKey: "FullName", type: "text", label: "Full name", labelRu: "Полное имя" },
      { key: "passwordHash", plistKey: "PasswordHash", type: "text", label: "Password hash / escrow reference", labelRu: "Password hash / escrow reference" },
      { key: "hidden", plistKey: "Hidden", type: "boolean", label: "Hide user", labelRu: "Скрыть пользователя", defaultValue: true },
      { key: "secureToken", plistKey: "SecureToken", type: "boolean", label: "Request Secure Token workflow", labelRu: "Запросить Secure Token workflow", defaultValue: false },
      { key: "rotatePassword", plistKey: "RotatePassword", type: "boolean", label: "Rotate password through MDM workflow", labelRu: "Ротация пароля через MDM workflow", defaultValue: false }
    ]
  },
  scripts_packages: {
    payloadType: "pro.turuk.macos.scripts-packages",
    supportsMultiple: true,
    fields: [
      { key: "name", plistKey: "Name", type: "text", label: "Name", labelRu: "Название", placeholder: "[required]" },
      {
        key: "executionEngine",
        plistKey: "ExecutionEngine",
        type: "select",
        label: "Execution engine",
        labelRu: "Механизм выполнения",
        defaultValue: "mdm_install_application",
        options: ["mdm_install_application", "turuk_macos_agent"]
      },
      { key: "kind", plistKey: "Kind", type: "select", label: "Type", labelRu: "Тип", defaultValue: "pkg", options: ["pkg", "dmg", "script"] },
      { key: "bundleIdentifier", plistKey: "BundleIdentifier", type: "text", label: "Bundle ID / package ID", labelRu: "Bundle ID / package ID" },
      { key: "version", plistKey: "Version", type: "text", label: "Version", labelRu: "Версия" },
      { key: "installURL", plistKey: "InstallURL", type: "text", label: "Install URL", labelRu: "URL установки" },
      { key: "sha256", plistKey: "SHA256", type: "text", label: "SHA-256", labelRu: "SHA-256" },
      { key: "runAs", plistKey: "RunAs", type: "select", label: "Run as", labelRu: "Запускать от", defaultValue: "root", options: ["root", "logged_in_user"] },
      { key: "timeoutSeconds", plistKey: "TimeoutSeconds", type: "number", label: "Timeout, seconds", labelRu: "Timeout, секунд", min: 30, max: 86400 },
      { key: "retryCount", plistKey: "RetryCount", type: "number", label: "Retry count", labelRu: "Количество повторов", min: 0, max: 10 },
      {
        key: "rebootBehavior",
        plistKey: "RebootBehavior",
        type: "select",
        label: "Reboot behavior",
        labelRu: "Перезагрузка",
        defaultValue: "none",
        options: ["none", "if_required", "force"]
      },
      {
        key: "installCheckScript",
        plistKey: "InstallCheckScript",
        type: "textarea",
        label: "Install check script",
        labelRu: "Скрипт проверки установки",
        rows: 6
      },
      {
        key: "preinstallScript",
        plistKey: "PreinstallScript",
        type: "textarea",
        label: "Preinstall script",
        labelRu: "Preinstall script",
        rows: 6
      },
      { key: "script", plistKey: "Script", type: "textarea", label: "Script", labelRu: "Скрипт", rows: 10, showWhen: { key: "kind", equals: "script" } },
      {
        key: "postinstallScript",
        plistKey: "PostinstallScript",
        type: "textarea",
        label: "Postinstall script",
        labelRu: "Postinstall script",
        rows: 6
      },
      { key: "arguments", plistKey: "Arguments", type: "array_strings", ui: "list", label: "Arguments", labelRu: "Аргументы" }
    ]
  }
};

function createSectionPayloadDraft(sectionKey) {
  const schema = APPLE_PROFILE_SECTION_SCHEMAS[sectionKey];
  if (!schema) return { id: randomHex(12) };
  const draft = { id: randomHex(12) };
  schema.fields.forEach((field) => {
    if (field.defaultValue !== undefined) {
      draft[field.key] = field.defaultValue;
      return;
    }
    if (field.type === "boolean") draft[field.key] = false;
    else if (field.type === "number" || field.type === "select_number") draft[field.key] = "";
    else if (field.type === "array_strings") draft[field.key] = field.ui === "list" ? [] : "";
    else if (field.type === "data") draft[field.key] = "";
    else draft[field.key] = "";
  });
  if (sectionKey === "restrictions") {
    draft.restrictedApps = normalizeRestrictionAppEntries(draft.restrictedApps);
  }
  if (sectionKey === "airplay") {
    draft.airplayPasswords = [];
    draft.allowedDestinations = [];
  }
  if (sectionKey === "airprint") {
    draft.printers = [];
  }
  return draft;
}

function isSectionFieldVisible(field, payloadItem) {
  const rules = field?.showWhen;
  if (!rules) return true;
  const conditions = Array.isArray(rules) ? rules : [rules];
  return conditions.every((condition) => {
    const value = payloadItem?.[condition?.key];
    if (Object.prototype.hasOwnProperty.call(condition || {}, "equals")) {
      return String(value ?? "") === String(condition.equals ?? "");
    }
    if (Array.isArray(condition?.in)) {
      return condition.in.map((item) => String(item)).includes(String(value ?? ""));
    }
    if (Object.prototype.hasOwnProperty.call(condition || {}, "truthy")) {
      return condition.truthy ? Boolean(value) : !Boolean(value);
    }
    return true;
  });
}

function createProfileSectionPayloadsState() {
  const out = {};
  Object.keys(APPLE_PROFILE_SECTION_SCHEMAS).forEach((sectionKey) => {
    out[sectionKey] = [];
  });
  return out;
}

function capitalizeTenantToken(value) {
  const cleaned = String(value || "").trim().toLowerCase();
  if (!cleaned) return "Active";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function tenantReadinessLabel(key, t) {
  const normalized = String(key || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
  switch (normalized) {
    case "status":
      return t("tenantsReadinessStatus");
    case "billing":
      return t("tenantsReadinessBilling");
    case "domain":
      return t("tenantsReadinessDomain");
    case "admin":
      return t("tenantsReadinessAdmin");
    case "mdm_apns":
      return t("tenantsReadinessMDMAPNS");
    case "enrollment_profile":
      return t("tenantsReadinessEnrollmentProfile");
    case "first_device":
      return t("tenantsReadinessFirstDevice");
    default:
      return t("tenantsReadinessUnknown");
  }
}

function tenantReadinessMessage(check, t) {
  const normalized = String(check?.message || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
  switch (normalized) {
    case "status_required":
      return t("tenantsReadinessReasonStatus");
    case "billing_required":
      return t("tenantsReadinessReasonBilling");
    case "domain_required":
      return t("tenantsReadinessReasonDomain");
    case "admin_required":
      return t("tenantsReadinessReasonAdmin");
    case "mdm_apns_required":
      return t("tenantsReadinessReasonMDMAPNS");
    case "enrollment_profile_required":
      return t("tenantsReadinessReasonEnrollmentProfile");
    case "first_device_required":
      return t("tenantsReadinessReasonFirstDevice");
    default:
      return String(check?.message || "").trim();
  }
}

function tenantUsageLabel(key, t) {
  const normalized = String(key || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
  switch (normalized) {
    case "devices":
      return t("tenantsUsageDevices");
    case "accounts":
      return t("tenantsUsageAccounts");
    case "admins":
      return t("tenantsUsageAdmins");
    default:
      return t("tenantsUsageUnknown");
  }
}

function tenantSaveErrorMessage(err, t) {
  const message = String(err?.message || "");
  const normalized = message.toLowerCase();
  if (normalized.includes("slug is reserved") || normalized.includes("reserved tenant domain")) {
    return t("tenantsReservedDomain");
  }
  if (err?.status === 409 || normalized.includes("already exists")) {
    if (normalized.includes("primary_domain")) {
      return t("tenantsConflictPrimaryDomain");
    }
    if (normalized.includes("slug")) {
      return t("tenantsConflictSlug");
    }
    return t("tenantsConflictGeneric");
  }
  return message || t("tenantsSaveFailed");
}

const PAGE_TO_ROUTE = {
  login: "/login",
  dashboard: "/dashboard",
  commandCenter: "/command-center",
  healthStatus: "/health-status",
  accountsList: "/accounts/list",
  accountsCreate: "/accounts/create",
  accountsConnect: "/accounts/connect",
  groups: "/groups",
  notifications: "/notifications",
  agentNotificationsCreate: "/notifications/create",
  agentNotificationsSent: "/notifications/sent",
  agentSupportTickets: "/notifications/support",
  documents: "/documents",
  actionLogs: "/action-logs",
  devicesOverview: "/devices/list",
  devicesList: "/devices/list",
  devicesRequest: "/devices/request",
  devicesMonitor: "/devices/monitor",
  devicesPlatformWindows: "/devices/platform/windows",
  devicesPlatformIOS: "/devices/platform/ios-ipados",
  devicesPlatformMacOS: "/devices/platform/macos",
  devicesPlatformAndroid: "/devices/platform/android",
  devicesConfig: "/devices/management/configuration",
  devicesCompliance: "/devices/management/compliance",
  devicesDetailAbout: "/devices/device/about",
  devicesDetailApps: "/devices/device/apps",
  devicesDetailProfiles: "/devices/device/profiles",
  devicesDetailLocation: "/devices/device/location",
  devicesDetailSharedIPad: "/devices/device/shared-ipad",
  devicesDetailActivationLock: "/devices/device/activation-lock",
  devicesDetailMacOSUEM: "/devices/device/macos-uem",
  devicesDetailCommands: "/devices/device/commands",
  devicesDetailRemoteAccess: "/devices/device/remote-access",
  profiles: "/profiles/all",
  profilesAll: "/profiles/all",
  profilesPlatformWindows: "/profiles/platform/windows",
  profilesPlatformIOS: "/profiles/platform/ios-ipados",
  profilesPlatformMacOS: "/profiles/platform/macos",
  profilesPlatformAndroid: "/profiles/platform/android",
  policiesAll: "/profiles/policies/all",
  policiesCompliance: "/profiles/policies/compliance",
  apps: "/apps/all",
  appsOverview: "/apps/all",
  appsAll: "/apps/all",
  appsPlatformWindows: "/apps/platform/windows",
  appsPlatformIOS: "/apps/platform/ios-ipados",
  appsPlatformMacOS: "/apps/platform/macos",
  appsPlatformAndroid: "/apps/platform/android",
  appsConfig: "/apps/management/configuration",
  appsUpload: "/apps/management/upload-enterprise",
  settingsConsoleBranding: "/settings/console-branding",
  settingsConsoleDashboard: "/settings/console-dashboard",
  settingsOrganization: "/settings/organization",
  settingsAgent: "/settings/agent",
  settingsConsoleUsers: "/settings/console-users",
  settingsCertificates: "/settings/certificates",
  settingsADE: "/settings/apple-business-manager",
  settingsVPP: "/settings/apple-apps-books",
  settingsDeclarative: "/settings/declarative-management",
  settingsSecurity: "/settings/security",
  settingsNotifications: "/settings/notifications",
  settingsReports: "/settings/reports",
  settingsSSO: "/settings/sso",
  settingsTenants: "/settings/organizations",
  settingsRoleSettings: "/settings/role-settings"
};

const ROUTE_TO_PAGE = {
  "/": "dashboard",
  "/login": "login",
  "/dashboard": "dashboard",
  "/command-center": "commandCenter",
  "/health-status": "healthStatus",
  "/accounts": "accountsList",
  "/accounts/list": "accountsList",
  "/accounts/create": "accountsCreate",
  "/accounts/connect": "accountsConnect",
  "/accounts/groups": "groups",
  "/groups": "groups",
  "/notifications": "agentNotificationsCreate",
  "/notifications/create": "agentNotificationsCreate",
  "/notifications/sent": "agentNotificationsSent",
  "/notifications/support": "agentSupportTickets",
  "/documents": "documents",
  "/action-logs": "actionLogs",
  "/audit-logs": "actionLogs",
  "/devices": "devicesList",
  "/devices/overview": "devicesList",
  "/devices/list": "devicesList",
  "/devices/request": "devicesRequest",
  "/devices/monitor": "devicesMonitor",
  "/devices/platform/windows": "devicesPlatformWindows",
  "/devices/platform/ios-ipados": "devicesPlatformIOS",
  "/devices/platform/macos": "devicesPlatformMacOS",
  "/devices/platform/android": "devicesPlatformAndroid",
  "/devices/platform/linux": "devicesList",
  "/devices/management/configuration": "devicesConfig",
  "/devices/management/compliance": "policiesCompliance",
  "/devices/device/about": "devicesDetailAbout",
  "/devices/device/apps": "devicesDetailApps",
  "/devices/device/profiles": "devicesDetailProfiles",
  "/devices/device/location": "devicesDetailLocation",
  "/devices/device/shared-ipad": "devicesDetailSharedIPad",
  "/devices/device/activation-lock": "devicesDetailActivationLock",
  "/devices/device/macos-uem": "devicesDetailMacOSUEM",
  "/devices/device/commands": "devicesDetailCommands",
  "/devices/device/remote-access": "devicesDetailRemoteAccess",
  "/profiles": "profilesAll",
  "/profiles/all": "profilesAll",
  "/profiles/platform/windows": "profilesPlatformWindows",
  "/profiles/platform/ios-ipados": "profilesPlatformIOS",
  "/profiles/platform/macos": "profilesPlatformMacOS",
  "/profiles/platform/android": "profilesPlatformAndroid",
  "/profiles/platform/linux": "profilesAll",
  "/profiles/policies/all": "policiesAll",
  "/profiles/policies/compliance": "policiesCompliance",
  "/apps": "appsAll",
  "/apps/overview": "appsAll",
  "/apps/all": "appsAll",
  "/apps/platform/windows": "appsPlatformWindows",
  "/apps/platform/ios-ipados": "appsPlatformIOS",
  "/apps/platform/macos": "appsPlatformMacOS",
  "/apps/platform/android": "appsPlatformAndroid",
  "/apps/management/configuration": "appsConfig",
  "/apps/management/upload-enterprise": "appsUpload",
  "/settings": "settingsConsoleBranding",
  "/settings/all": "settingsConsoleBranding",
  "/settings/console-branding": "settingsConsoleBranding",
  "/settings/console-dashboard": "settingsConsoleDashboard",
  "/settings/organization": "settingsOrganization",
  "/settings/agent": "settingsAgent",
  "/settings/console-users": "settingsConsoleUsers",
  "/settings/certificates": "settingsCertificates",
  "/settings/apple-business-manager": "settingsADE",
  "/settings/apple-apps-books": "settingsVPP",
  "/settings/declarative-management": "settingsDeclarative",
  "/settings/security": "settingsSecurity",
  "/settings/notifications": "settingsNotifications",
  "/settings/reports": "settingsReports",
  "/settings/support-access": "settingsSecurity",
  "/settings/sso": "settingsSSO",
  "/settings/organizations": "settingsTenants",
  "/settings/api-keys": "settingsConsoleBranding",
  "/settings/role-settings": "settingsRoleSettings"
};

const PAGE_PERMISSION_KEY = {
  dashboard: "dashboard",
  commandCenter: "devices.commands",
  healthStatus: "dashboard",
  accounts: "accounts.list",
  accountsList: "accounts.list",
  accountsCreate: "accounts.create",
  accountsConnect: "accounts.connect",
  groups: "groups",
  notifications: "notifications",
  agentNotificationsCreate: "notifications",
  agentNotificationsSent: "notifications",
  agentSupportTickets: "settings.agent",
  documents: "documents",
  actionLogs: "settings.security",
  accountsGroups: "groups",
  devices: "devices.list",
  devicesOverview: "devices.list",
  devicesList: "devices.list",
  devicesRequest: "devices.request",
  devicesMonitor: "devices.monitor",
  devicesPlatformWindows: "devices.platform.windows",
  devicesPlatformIOS: "devices.platform.ios",
  devicesPlatformMacOS: "devices.platform.macos",
  devicesPlatformAndroid: "devices.platform.android",
  devicesConfig: "devices.management.configuration",
  devicesCompliance: "devices.management.compliance",
  devicesDetailAbout: "devices.detail",
  devicesDetailApps: "devices.apps",
  devicesDetailProfiles: "devices.profiles",
  devicesDetailLocation: "devices.detail",
  devicesDetailSharedIPad: "devices.detail",
  devicesDetailActivationLock: "devices.detail",
  devicesDetailMacOSUEM: "devices.detail",
  devicesDetailCommands: "devices.commands",
  devicesDetailRemoteAccess: "settings.security",
  profiles: "profiles.all",
  profilesAll: "profiles.all",
  profilesPlatformWindows: "profiles.platform.windows",
  profilesPlatformIOS: "profiles.platform.ios",
  profilesPlatformMacOS: "profiles.platform.macos",
  profilesPlatformAndroid: "profiles.platform.android",
  policiesAll: "profiles.policies.all",
  policiesCompliance: "profiles.policies.compliance",
  devicesProfiles: "profiles.all",
  apps: "apps.all",
  appsOverview: "apps.all",
  appsAll: "apps.all",
  appsPlatformWindows: "apps.platform.windows",
  appsPlatformIOS: "apps.platform.ios",
  appsPlatformMacOS: "apps.platform.macos",
  appsPlatformAndroid: "apps.platform.android",
  appsConfig: "apps.management.configuration",
  appsUpload: "apps.management.upload",
  settingsConsoleBranding: "settings.console_branding",
  settingsConsoleDashboard: "settings.console_branding",
  settingsOrganization: "settings.tenants",
  settingsAgent: "settings.agent",
  settingsConsoleUsers: "settings.console_users",
  settingsCertificates: "settings.certificates",
  settingsADE: "settings.ade",
  settingsVPP: "settings.vpp",
  settingsDeclarative: "settings.declarative",
  settingsSecurity: "settings.security",
  settingsNotifications: "settings.notifications",
  settingsReports: "settings.reports",
  settingsSSO: "settings.sso",
  settingsTenants: "settings.tenants",
  settingsRoleSettings: "settings.role_settings"
};

const ROLE_PERMISSION_GROUPS = [
  {
    titleKey: "navDashboard",
    items: [{ key: "dashboard", labelKey: "roleSettingsPermissionDashboard" }]
  },
  {
    titleKey: "navAccounts",
    items: [
      { key: "accounts", labelKey: "roleSettingsPermissionAccounts" },
      { key: "accounts.list", labelKey: "roleSettingsPermissionAccountsList" },
      { key: "accounts.create", labelKey: "roleSettingsPermissionAccountsCreate" },
      { key: "accounts.connect", labelKey: "roleSettingsPermissionAccountsConnect" },
      { key: "groups", labelKey: "roleSettingsPermissionGroups" }
    ]
  },
  {
    titleKey: "navNotifications",
    items: [{ key: "notifications", labelKey: "roleSettingsPermissionNotifications" }]
  },
  {
    titleKey: "navDocuments",
    items: [{ key: "documents", labelKey: "roleSettingsPermissionDocuments" }]
  },
  {
    titleKey: "navDevices",
    items: [
      { key: "devices", labelKey: "roleSettingsPermissionDevices" },
      { key: "devices.overview", labelKey: "roleSettingsPermissionDevicesOverview" },
      { key: "devices.list", labelKey: "roleSettingsPermissionDevicesList" },
      { key: "devices.request", labelKey: "roleSettingsPermissionDevicesRequest" },
      { key: "devices.monitor", labelKey: "roleSettingsPermissionDevicesMonitor" },
      { key: "devices.platform.windows", labelKey: "roleSettingsPermissionDevicesPlatformWindows" },
      { key: "devices.platform.ios", labelKey: "roleSettingsPermissionDevicesPlatformIOS" },
      { key: "devices.platform.macos", labelKey: "roleSettingsPermissionDevicesPlatformMacOS" },
      { key: "devices.platform.android", labelKey: "roleSettingsPermissionDevicesPlatformAndroid" },
      { key: "devices.management.configuration", labelKey: "roleSettingsPermissionDevicesConfig" },
      { key: "devices.management.compliance", labelKey: "roleSettingsPermissionDevicesCompliance" },
      { key: "devices.detail", labelKey: "roleSettingsPermissionDevicesDetail" },
      { key: "devices.apps", labelKey: "roleSettingsPermissionDevicesApps" },
      { key: "devices.profiles", labelKey: "roleSettingsPermissionDevicesProfiles" },
      { key: "devices.commands", labelKey: "roleSettingsPermissionDevicesCommands" }
    ]
  },
  {
    titleKey: "navProfiles",
    items: [
      { key: "profiles", labelKey: "roleSettingsPermissionProfiles" },
      { key: "profiles.all", labelKey: "roleSettingsPermissionProfilesAll" },
      { key: "profiles.platform.windows", labelKey: "roleSettingsPermissionProfilesPlatformWindows" },
      { key: "profiles.platform.ios", labelKey: "roleSettingsPermissionProfilesPlatformIOS" },
      { key: "profiles.platform.macos", labelKey: "roleSettingsPermissionProfilesPlatformMacOS" },
      { key: "profiles.platform.android", labelKey: "roleSettingsPermissionProfilesPlatformAndroid" },
      { key: "profiles.policies.all", labelKey: "roleSettingsPermissionPoliciesAll" },
      { key: "profiles.policies.compliance", labelKey: "roleSettingsPermissionPoliciesCompliance" }
    ]
  },
  {
    titleKey: "navApps",
    items: [
      { key: "apps", labelKey: "roleSettingsPermissionApps" },
      { key: "apps.overview", labelKey: "roleSettingsPermissionAppsOverview" },
      { key: "apps.all", labelKey: "roleSettingsPermissionAppsAll" },
      { key: "apps.platform.windows", labelKey: "roleSettingsPermissionAppsPlatformWindows" },
      { key: "apps.platform.ios", labelKey: "roleSettingsPermissionAppsPlatformIOS" },
      { key: "apps.platform.macos", labelKey: "roleSettingsPermissionAppsPlatformMacOS" },
      { key: "apps.platform.android", labelKey: "roleSettingsPermissionAppsPlatformAndroid" },
      { key: "apps.management.configuration", labelKey: "roleSettingsPermissionAppsConfig" },
      { key: "apps.management.upload", labelKey: "roleSettingsPermissionAppsUpload" }
    ]
  },
  {
    titleKey: "navSettings",
    items: [
      { key: "settings", labelKey: "roleSettingsPermissionSettings" },
      { key: "settings.console_branding", labelKey: "roleSettingsPermissionSettingsConsoleBranding" },
      { key: "settings.console_users", labelKey: "roleSettingsPermissionSettingsConsoleUsers" },
      { key: "settings.certificates", labelKey: "roleSettingsPermissionSettingsCertificates" },
      { key: "settings.ade", labelKey: "roleSettingsPermissionSettingsADE" },
      { key: "settings.vpp", labelKey: "roleSettingsPermissionSettingsVPP" },
      { key: "settings.declarative", labelKey: "roleSettingsPermissionSettingsDeclarative" },
      { key: "settings.security", labelKey: "roleSettingsPermissionSettingsSecurity" },
      { key: "settings.notifications", labelKey: "roleSettingsPermissionSettingsNotifications" },
      { key: "settings.reports", labelKey: "roleSettingsPermissionSettingsReports" },
      { key: "settings.sso", labelKey: "roleSettingsPermissionSettingsSSO" },
      { key: "settings.agent", labelKey: "roleSettingsPermissionSettingsAgent" },
      { key: "settings.tenants", labelKey: "roleSettingsPermissionSettingsTenants" },
      { key: "settings.openapi", labelKey: "roleSettingsPermissionSettingsOpenAPI" },
      { key: "settings.role_settings", labelKey: "roleSettingsPermissionSettingsRoles" }
    ]
  }
];

function buildRoleTemplatePermissions(viewKeys = [], editKeys = []) {
  const out = buildEmptyRolePermissionsState();
  viewKeys.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(out, key)) return;
    out[key] = { view: true, edit: false };
  });
  editKeys.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(out, key)) return;
    out[key] = { view: true, edit: true };
  });
  return out;
}

const ROLE_SCOPE_PLATFORMS = [
  { key: "ios", label: "iOS/iPadOS" },
  { key: "macos", label: "macOS" },
  { key: "tvos", label: "tvOS" },
  { key: "visionos", label: "visionOS" },
  { key: "windows", label: "Windows" },
  { key: "android", label: "Android" }
];

const ENTERPRISE_ROLE_TEMPLATES = [
  {
    key: "read-only-support-operator",
    labelKey: "roleSettingsTemplateSupport",
    descriptionKey: "roleSettingsTemplateSupportHint",
    roleKey: "read-only-support-operator",
    displayName: "Read-only Support Operator",
    permissions: buildRoleTemplatePermissions([
      "dashboard",
      "accounts",
      "accounts.list",
      "groups",
      "devices",
      "devices.list",
      "devices.detail",
      "devices.apps",
      "devices.profiles",
      "notifications",
      "settings",
      "settings.agent"
    ])
  },
  {
    key: "helpdesk",
    labelKey: "roleSettingsTemplateHelpdesk",
    descriptionKey: "roleSettingsTemplateHelpdeskHint",
    roleKey: "helpdesk",
    displayName: "Helpdesk",
    permissions: buildRoleTemplatePermissions(
      [
        "dashboard",
        "accounts.list",
        "groups",
        "devices",
        "devices.list",
        "devices.detail",
        "devices.apps",
        "devices.profiles",
        "settings"
      ],
      ["accounts", "accounts.connect", "devices.commands", "notifications", "settings.agent"]
    )
  },
  {
    key: "security-admin",
    labelKey: "roleSettingsTemplateSecurity",
    descriptionKey: "roleSettingsTemplateSecurityHint",
    roleKey: "security-admin",
    displayName: "Security Admin",
    permissions: buildRoleTemplatePermissions(
      ["dashboard", "devices", "devices.list", "devices.detail", "settings"],
      [
        "devices.management.compliance",
        "settings.security",
        "settings.sso",
        "settings.role_settings",
        "settings.console_users"
      ]
    )
  }
];

const PAGE_ACCESS_FALLBACK_ORDER = [
  "dashboard",
  "devicesList",
  "profilesAll",
  "appsAll",
  "accountsList",
  "groups",
  "agentNotificationsCreate",
  "agentNotificationsSent",
  "agentSupportTickets",
  "documents",
  "settingsConsoleBranding",
  "settingsADE",
  "settingsVPP",
  "settingsDeclarative",
  "settingsNotifications",
  "settingsReports",
];

const I18N = {
  ru: {
    appTitle: "Turuk MDM Portal",
    loginTitle: "Вход в консоль MDM",
    loginSubtitle: "Авторизация",
    loginTenantSubtitle: "Вход в консоль организации",
    login: "Логин",
    password: "Пароль",
    signIn: "Войти",
    ssoSignIn: "Войти через SSO",
    ssoLocalLoginBlocked: "Для этой организации включен обязательный SSO. Используйте вход через SSO или break-glass администратора.",
    ssoSettingsTitle: "Вход через Microsoft, Google или Okta",
    ssoSettingsHint: "Настройка входа администраторов через внешний провайдер для этой организации.",
    ssoStatusEnabled: "Включено",
    ssoStatusDisabled: "Отключено",
    ssoProviderPreset: "Способ входа",
    ssoEnabled: "Включить SSO",
    ssoEnforce: "Требовать SSO",
    ssoAutoProvision: "Автоматически создавать администраторов",
    ssoAllowedDomains: "Разрешенные домены email",
    ssoDefaultRole: "Роль по умолчанию",
    ssoTenantIssuerURL: "Tenant / Issuer URL",
    ssoOIDCClientID: "OIDC Client ID",
    ssoOIDCClientSecret: "OIDC Client Secret",
    ssoSecretConfiguredPlaceholder: "Секрет уже сохранен; оставьте пустым, чтобы не менять",
    ssoSecretEmptyPlaceholder: "Введите client secret",
    ssoOIDCScopes: "OIDC scopes",
    ssoOIDCAuthURL: "OIDC Authorization URL",
    ssoOIDCTokenURL: "OIDC Token URL",
    ssoOIDCUserinfoURL: "OIDC UserInfo URL",
    ssoOptionalDiscoveryPlaceholder: "Необязательно, если доступен discovery",
    ssoSAMLEntityID: "SAML Entity ID",
    ssoSAMLSSOURL: "SAML SSO URL",
    ssoSAMLX509Cert: "SAML X.509 certificate",
    ssoAdvancedSettings: "Расширенные настройки",
    ssoTest: "Проверить SSO",
    ssoEnforceWarning: "Перед обязательным SSO проверьте вход через провайдера. Break-glass администратор останется доступен для аварийного локального входа.",
    ssoEnforceConfirm: "Перед включением обязательного SSO убедитесь, что вход через провайдера работает. Break-glass администратор останется доступен. Продолжить?",
    ssoSave: "Сохранить SSO",
    ssoSaveDone: "Настройки SSO сохранены",
    ssoSaveFailed: "Не удалось сохранить SSO",
    ssoBreakGlassHint: "Break-glass local admin сохраняет локальный вход даже при обязательном SSO. По умолчанию это master_admin.",
    ssoRedirectURIHint: "Redirect URI для IdP",
    reset: "Сбросить",
    signingIn: "Проверка...",
    signOut: "Выйти",
    mfaCode: "Код MFA",
    mfaCodeRequired: "Введите код MFA",
    mfaSetupTitle: "Настройка MFA",
    mfaSetupRequiredHint: "Для этой учетной записи требуется MFA. Добавьте секрет в приложение-аутентификатор и подтвердите код.",
    mfaStartSetup: "Создать секрет MFA",
    mfaSecret: "Секрет MFA",
    mfaSetupScanHint: "Отсканируйте QR код в Authenticator/1Password/Google Authenticator или добавьте секрет вручную, затем введите 6-значный код.",
    mfaSetupSecretReady: "Секрет MFA создан",
    mfaSetupFailed: "Не удалось начать настройку MFA",
    mfaEnable: "Включить MFA",
    mfaEnabled: "MFA включена",
    mfaEnableFailed: "Не удалось включить MFA",
    passkeyRequired: "Подтвердите вход Passkey",
    passkeyUnsupported: "Этот браузер не поддерживает Passkey/WebAuthn.",
    passkeyLoginFailed: "Не удалось подтвердить Passkey",
    passkeySetupTitle: "Passkey для своей учетной записи",
    passkeySetupHint: "Passkey можно использовать вместо кода MFA при входе в консоль.",
    passkeyAlreadyEnabled: "Passkey уже настроен для этой учетной записи.",
    passkeyName: "Название Passkey",
    passkeyNamePlaceholder: "MacBook Touch ID",
    passkeyRegister: "Добавить Passkey",
    passkeyRegistered: "Passkey добавлен",
    passkeyRegisterFailed: "Не удалось добавить Passkey",
    currentPasswordRequired: "Введите текущий пароль",
    reauthTitle: "Подтверждение действия",
    reauthHint: "Введите пароль еще раз для выполнения чувствительного действия.",
    reauthCancelled: "Подтверждение отменено",
    reauthPasswordRequired: "Введите пароль",
    confirm: "Подтвердить",
    refresh: "Обновить",
    navDashboard: "Панель мониторинга",
    navDevices: "Устройства",
    navProfiles: "Профили и Политики",
    navAccounts: "Аккаунты",
    navAccountsGroups: "Группы",
    navNotifications: "Turuk Companion",
    navDocuments: "Документы",
    navActionLogs: "Логи действий",
    navApps: "Приложения",
    navSettings: "Настройки",
    turukAgentTitle: "Turuk Companion",
    turukAgentNotificationsGroup: "Уведомления",
    turukAgentNotificationsSent: "Отправленные",
    turukAgentSupportTickets: "Обращения в поддержку",
    actionLogsTitle: "Логи действий",
    actionLogsSubtitle: "Журнал административных и опасных действий: команды MDM, обновление APNs, скачивание профилей регистрации и изменения организаций.",
    actionLogsRefresh: "Обновить логи",
    actionLogsRefreshDone: "Логи действий обновлены.",
    actionLogsExportCSV: "Экспорт CSV",
    actionLogsExportJSON: "Экспорт JSON",
    actionLogsExportDone: "Экспорт audit logs скачан.",
    actionLogsExportFailed: "Не удалось экспортировать audit logs",
    actionLogsFilterActor: "Администратор/API key",
    actionLogsFilterDevice: "Устройство UDID",
    actionLogsFilterAction: "Действие",
    actionLogsFilterFrom: "С даты",
    actionLogsFilterTo: "По дату",
    actionLogsApplyFilters: "Показать",
    actionLogsClearFilters: "Сбросить фильтры",
    actionLogsColTime: "Время",
    actionLogsColActor: "Администратор",
    actionLogsColAction: "Действие",
    actionLogsColResult: "Результат",
    actionLogsColIP: "IP",
    actionLogsColPath: "Endpoint",
    actionLogsColDetails: "Детали",
    actionLogsColIntegrity: "Целостность",
    actionLogsSuccess: "Успешно",
    actionLogsFailed: "Ошибка",
    actionLogsSessionExpired: "Сессия истекла",
    actionLogsNoItems: "Логов пока нет.",
    actionLogsLoadFailed: "Не удалось загрузить логи действий",
    auditActionAuthMe: "Проверка сессии",
    auditActionAccountsManage: "Создание аккаунта",
    auditActionAccountsUpdate: "Изменение аккаунта",
    auditActionAccountsStatus: "Изменение статуса аккаунта",
    auditActionAccountsDelete: "Удаление аккаунта",
    auditActionAccountsDeviceAssign: "Назначение устройства аккаунту",
    auditActionAccountsDeviceUnassign: "Открепление устройства от аккаунта",
    auditActionAccountsMobileUnbind: "Отвязка Companion-устройства",
    auditActionDevicesDelete: "Удаление устройств",
    auditActionAppleCommand: "Команда Apple MDM",
    auditActionDeviceInformation: "Запрос информации устройства",
    auditActionSecurityInfo: "Запрос статуса безопасности",
    auditActionInstalledApps: "Запрос списка приложений",
    auditActionProfileList: "Запрос списка профилей",
    auditActionDeviceLocation: "Запрос геопозиции",
    auditActionClearPasscode: "Удалить код разблокировки",
    auditActionDeviceLock: "Заблокировать устройство",
    auditActionLostModeSound: "Воспроизвести звук режима пропажи",
    auditActionLostModeEnable: "Включить режим пропажи",
    auditActionLostModeDisable: "Выключить режим пропажи",
    auditActionRestartDevice: "Перезагрузить устройство",
    auditActionShutdownDevice: "Выключить устройство",
    auditActionScheduleOSUpdate: "Запланировать обновление ОС",
    auditActionAvailableOSUpdates: "Проверить доступные обновления ОС",
    auditActionOSUpdateStatus: "Запросить статус обновления ОС",
    auditActionRemoveProfile: "Удалить профиль",
    auditActionRemoveApplication: "Удалить приложение",
    auditActionReinstallApplication: "Переустановить приложение",
    auditActionAllowActivationLock: "Разрешить блокировку активации",
    auditActionAPNSCSR: "Создание CSR MDM APNs",
    auditActionAPNSConfig: "Обновление MDM APNs",
    auditActionEnrollmentDownload: "Скачивание профиля регистрации",
    auditActionTenantDataExport: "Экспорт данных организации",
    auditActionTenantCreate: "Создание организации",
    auditActionTenantUpdate: "Изменение организации",
    auditActionCommandRetry: "Повтор команды",
    auditActionCommandCancel: "Отмена команды",
    auditActionAuditLogsExport: "Экспорт audit logs",
    auditActionSupportTicket: "Доступ к обращению поддержки",
    auditActionSupportSessionRequest: "Запрос support-доступа",
    auditActionSupportSessionApprove: "Подтверждение support-доступа",
    auditActionSupportSessionRevoke: "Отзыв support-доступа",
    auditActionSupportSessionAccess: "Support-доступ к организации",
    auditActionSSOConfig: "Изменение SSO",
    notificationsSubtitle: "Новости компании, обязательные действия и предупреждения о нарушениях для Turuk Companion.",
    notificationsCreate: "Создать уведомление",
    notificationsCategory: "Тип",
    notificationsCategoryNews: "Новости компании",
    notificationsCategoryRequired: "Обязательное действие",
    notificationsCategoryCompliance: "Нарушение требований",
    notificationsSeverity: "Важность",
    notificationsSeverityInfo: "Информация",
    notificationsSeverityWarning: "Предупреждение",
    notificationsSeverityCritical: "Критично",
    notificationsTitleField: "Заголовок",
    notificationsBody: "Сообщение",
    notificationsActionLabel: "Текст кнопки",
    notificationsActionURL: "Deep link или HTTPS-ссылка",
    notificationsTarget: "Получатели",
    notificationsTargetAll: "Все аккаунты",
    notificationsTargetAccount: "Аккаунт",
    notificationsTargetGroup: "Группа",
    notificationsExpiresAt: "Действует до",
    notificationsSend: "Опубликовать и отправить push",
    notificationsSent: "Уведомление опубликовано",
    notificationsSendFailed: "Не удалось опубликовать уведомление",
    notificationsDeleted: "Уведомление удалено",
    notificationsDeleteConfirm: "Удалить отправленное уведомление?",
    notificationsDeleteFailed: "Не удалось удалить уведомление",
    notificationsNoItems: "Уведомлений пока нет.",
    notificationsPushStats: "Push: отправлено {sent} из {targeted}",
    roleSettingsPermissionNotifications: "Turuk Companion: уведомления",
    documentsTitle: "Документы",
    documentsSubtitle: "Загрузка документов и назначение группам.",
    documentsAdd: "Добавить",
    documentsEdit: "Изменить документ",
    documentsSave: "Сохранить",
    documentsDelete: "Удалить",
    documentsDeleteConfirm: "Удалить документ?",
    documentsDeleteSelected: "Удалить выбранные",
    documentsDeleteSelectedConfirm: "Удалить выбранные документы?",
    documentsDeleteSelectedFailed: "Не удалось удалить документы",
    documentsSelectedCount: "Выбрано документов",
    documentsSearch: "Поиск документа",
    documentsNoItems: "Документов пока нет.",
    documentsColTitle: "Название",
    documentsColCreatedBy: "Добавил",
    documentsColGroups: "Группы",
    documentsColStatus: "Статус",
    documentsColUpdatedAt: "Последнее изменение",
    documentsURL: "URL",
    documentsFile: "Файл",
    documentsFileName: "Имя файла",
    documentsGroups: "Назначить группам",
    documentsActive: "Активен",
    documentsStatusActive: "Активен",
    documentsStatusInactive: "Отключен",
    documentsFileTooLarge: "Файл слишком большой. Максимум 10 MB.",
    documentsFileTypeNotAllowed: "Тип файла не разрешен",
    documentsAllowedFileTypes: "Разрешены: {types}. Максимальный размер: 10 MB.",
    documentsReadFailed: "Не удалось прочитать файл",
    documentsSaved: "Документ сохранен",
    documentsSaveFailed: "Не удалось сохранить документ",
    documentsDeleted: "Документ удален",
    roleSettingsPermissionDocuments: "Документы",
    complianceCenterTitle: "Центр соответствия требованиям",
    complianceCenterSubtitle: "Правила для групп, текущие нарушения и обязательные действия.",
    complianceCompliant: "Соответствует",
    complianceActionRequired: "Требует действий",
    complianceCritical: "Критичные нарушения",
    complianceResolved: "Исправлено",
    complianceEvaluate: "Проверить все устройства",
    complianceEvaluationDone: "Проверено аккаунтов: {count}. Создано уведомлений: {notifications}",
    complianceEvaluationFailed: "Не удалось проверить соответствие",
    complianceRuleCreate: "Новое правило",
    complianceRuleEdit: "Изменить правило",
    complianceRuleHint: "Правило применяется ко всем активным аккаунтам и устройствам выбранной группы.",
      complianceRuleName: "Название правила",
      complianceRuleGroup: "Группа",
      complianceRuleGroups: "Группы",
      complianceRuleGroupsPlaceholder: "Выберите группы",
      complianceRuleGroupsRequired: "Выберите хотя бы одну группу",
      complianceRuleType: "Требование",
    complianceRulePlatform: "Платформа",
    complianceRuleProfile: "Обязательный профиль",
    complianceRuleApp: "Обязательное приложение",
    complianceRuleMinimumOSVersion: "Минимальная версия ОС",
    complianceRuleSeverity: "Важность",
    complianceRuleDeadlineHours: "Срок выполнения, часов",
    complianceRuleActive: "Правило активно",
    complianceNotifyCompanion: "Уведомлять пользователя в компаньоне",
    complianceNotifyEmail: "Отправлять e-mail уведомление",
    complianceNotificationEmail: "E-mail для уведомлений",
	    complianceRuleNotifications: "Уведомления",
	    complianceNotifyCompanionShort: "Компаньон",
	    complianceNotifyEmailShort: "Email",
	    complianceNotifyNoneShort: "Только мониторинг",
	    complianceRuleAutomation: "Автоматизация",
	    complianceAutoRemediationProfile: "Автоматически устанавливать отсутствующий профиль",
	    complianceAutoRemediationApp: "Принудительно устанавливать отсутствующее приложение",
	    complianceAutoRemediationOS: "Принудительно устанавливать обновление ОС",
	    complianceAutoRemediationShort: "Auto-remediation",
	    complianceMonitoringOnlyShort: "Только аудит",
	    complianceRetryHours: "Повтор автоисправления, часов",
	    complianceEscalationHours: "Эскалация после срока, часов",
	    complianceEscalationEmail: "E-mail для escalation",
	    complianceRuleTarget: "Объект требования",
    complianceRuleDeadline: "Срок",
    complianceRuleProfileRequired: "Профиль должен быть установлен",
    complianceRuleAppRequired: "Приложение должно быть установлено",
    complianceRulePasscodeRequired: "Код-пароль обязателен",
	    complianceRuleMinimumOS: "Минимальная версия ОС",
	    complianceRuleAttestationRequired: "Managed Device Attestation обязателен",
	    complianceRuleAllowedSIM: "Разрешенная SIM-карта",
	    complianceRuleCustomValue: "Свое MDM-значение",
	    complianceRuleTemplate: "Создать из шаблона",
	    complianceRuleTemplatePlaceholder: "Выберите шаблон",
	    complianceRuleTemplateCustom: "Свое правило",
    complianceRuleTemplateMinimumOS: "Минимальная версия iOS",
    complianceRuleTemplatePasscode: "Passcode обязателен",
    complianceRuleTemplateProfile: "Профиль обязателен",
    complianceRuleTemplateApp: "Приложение обязательно",
    complianceRuleTemplateAttestation: "Managed Device Attestation",
	    complianceRuleTemplateSIM: "Проверка SIM-карты",
	    complianceRuleCustomField: "MDM поле",
	    complianceRuleCustomFieldHint: "Выберите из подсказок или введите свой путь к MDM-значению. Поддерживаются поля устройства, security.* и network.*.",
	    complianceRuleCustomOperator: "Оператор",
	    complianceRuleCustomValueInput: "Ожидаемое значение",
	    complianceRuleCustomOperatorEquals: "равно",
	    complianceRuleCustomOperatorNotEquals: "не равно",
	    complianceRuleCustomOperatorContains: "содержит",
	    complianceRuleCustomOperatorNotContains: "не содержит",
	    complianceRuleCustomOperatorStartsWith: "начинается с",
	    complianceRuleCustomOperatorEndsWith: "заканчивается на",
	    complianceRuleCustomOperatorGreaterThan: "больше",
	    complianceRuleCustomOperatorGreaterOrEqual: "больше или равно",
	    complianceRuleCustomOperatorLessThan: "меньше",
	    complianceRuleCustomOperatorLessOrEqual: "меньше или равно",
	    complianceRuleCustomOperatorExists: "существует",
	    complianceRuleCustomOperatorNotExists: "не существует",
	    complianceRuleSaved: "Правило соответствия сохранено",
    complianceRuleSaveFailed: "Не удалось сохранить правило",
    complianceRuleDeleteConfirm: "Удалить правило соответствия?",
    complianceRuleDeleted: "Правило соответствия удалено",
    complianceRuleDeleteFailed: "Не удалось удалить правило",
    complianceRulesTitle: "Правила соответствия",
    complianceIssuesTitle: "Нарушения и обязательные действия",
    complianceEvidenceTitle: "Доказательства нарушения",
    complianceEvidenceWhatHappened: "Что произошло",
    complianceEvidenceNextStep: "Что делать",
    complianceEvidenceShowMoreTimeline: "Показать всю хронологию",
    complianceEvidenceShowLessTimeline: "Свернуть хронологию",
    complianceEvidenceRule: "Правило",
	    complianceEvidenceRemediationHistory: "История исправлений",
    complianceEvidenceNoRemediationHistory: "Попыток исправления пока нет.",
    complianceEvidenceDeploymentType: "Тип",
    complianceEvidenceDeploymentTarget: "Объект",
    complianceEvidenceDeploymentState: "Состояние",
    complianceEvidenceDeploymentResult: "Результат",
    complianceEvidenceDeploymentQueuedAt: "Поставлено в очередь",
    complianceEvidenceDeploymentUpdatedAt: "Обновлено",
    complianceEvidenceTimeline: "Хронология",
    complianceEvidenceAdmin: "Админские доказательства",
	    complianceEvidenceOpen: "Открыть детали",
    complianceEvidenceClose: "Закрыть детали",
    complianceEvidenceNoData: "Доказательства пока не собраны.",
    complianceEvidenceDownloadReport: "Скачать отчет",
    complianceEvidenceReportDownloaded: "Compliance evidence report скачан",
    complianceEvidenceReportFailed: "Не удалось скачать compliance evidence report",
    complianceIssueStartRemediation: "Начать исправление",
    complianceIssueInstallProfile: "Установить профиль",
    complianceIssueInstallApp: "Установить приложение",
    complianceIssueRequestAttestation: "Запросить аттестацию",
    complianceIssueSendOSUpdateInstruction: "Отправить инструкцию обновления iOS",
    complianceIssueSendPasscodeInstruction: "Отправить инструкцию настройки пароля",
    complianceIssueSendSIMInstruction: "Отправить инструкцию проверки SIM",
    complianceIssueSendReminder: "Отправить напоминание",
    complianceIssueReevaluate: "Переоценить",
    complianceIssueResolve: "Закрыть вручную",
    complianceIssueResolveReason: "Причина ручного закрытия",
    complianceIssueWaive: "Исключение",
    complianceIssueWaiveReason: "Причина исключения",
    complianceIssueActionDone: "Действие выполнено",
    complianceIssueActionFailed: "Не удалось выполнить действие",
    complianceIssueFilters: "Фильтры нарушений",
    complianceIssueFilterStatus: "Статус",
    complianceIssueFilterRule: "Правило",
    complianceIssueFilterDevice: "Устройство",
    complianceIssueFilterSearch: "Поиск",
    complianceIssueFilterAllStatuses: "Все статусы",
    complianceIssueFilterOpen: "Открытые",
    complianceIssueFilterCritical: "Критичные",
    complianceIssueFilterOverdue: "Просроченные",
    complianceIssueFilterResolved: "Исправленные",
    complianceIssueFilterWaived: "Исключения",
    complianceIssueFilterAllRules: "Все правила",
    complianceIssueFilterAllDevices: "Все устройства",
    complianceIssueFilterSearchPlaceholder: "Аккаунт, устройство, причина",
    complianceIssueFilterReset: "Сбросить фильтры",
    complianceRemediationStatus: "Статус исправления",
    complianceRemediationStatusWaitingAction: "Ожидает действия",
    complianceRemediationStatusInstructionSent: "Инструкция отправлена",
    complianceRemediationStatusWaitingSync: "Ожидает синхронизации",
    complianceRemediationStatusConfirmed: "Пользователь подтвердил",
    complianceRemediationStatusResolved: "Исправлено",
    complianceRemediationStatusWaived: "Исключение",
    complianceDeviceInstruction: "Инструкция на устройстве",
    complianceInstructionProfile: "Откройте приложение Turuk Companion и установите назначенный профиль. После установки устройство отправит обновленный список профилей.",
    complianceInstructionApp: "Откройте приложение Turuk Companion и установите назначенное приложение. После установки устройство отправит обновленный список приложений.",
    complianceInstructionMinimumOS: "На устройстве откройте Настройки > Основные > Обновление ПО, установите доступное обновление iOS и вернитесь в приложение Turuk Companion для повторной проверки.",
    complianceInstructionPasscode: "На устройстве откройте Настройки > Face ID/Touch ID и код-пароль, включите код-пароль и вернитесь в приложение Turuk Companion для повторной проверки.",
    complianceInstructionAttestation: "Администратор запросит Managed Device Attestation. Держите устройство онлайн, пока оно не ответит на MDM-команду.",
    complianceInstructionSIM: "Проверьте SIM/eSIM на устройстве. Если SIM заменена ошибочно, верните разрешенную SIM и выполните повторную синхронизацию.",
    complianceIssueToggleFilters: "Фильтры",
    complianceIssueExportExcel: "Скачать Excel",
    complianceIssueFilteredCount: "Показано: {count} из {total}",
    complianceIssueExportDone: "Compliance report скачан",
    complianceIssueExportFailed: "Не удалось скачать compliance report",
    complianceIssueDetectedAt: "Обнаружено",
    complianceIssueLastAction: "Последнее действие",
    complianceIssueLastActionStarted: "Исправление начато",
    complianceIssueLastActionConfirmed: "Подтверждено пользователем",
    complianceIssueLastActionResolved: "Исправлено",
    complianceIssueLastActionWaived: "Исключение",
    complianceIssueLastActionDetected: "Последняя проверка",
    complianceIssueNextStepAlreadyStarted: "Исправление уже начато. Дождитесь синхронизации устройства или запустите переоценку.",
    complianceIssueNextStepStartRemediation: "Запустите исправление из этой карточки.",
    complianceIssueNextStepReviewDevice: "Проверьте состояние устройства и запустите переоценку.",
    complianceIssueShowMore: "Показать еще",
    complianceIssue: "Нарушение",
    complianceReason: "Точная причина",
    complianceDueAt: "Исправить до",
    complianceEvent: "Событие",
    complianceEventAt: "Время",
    complianceEventDetected: "Нарушение обнаружено",
    complianceEventReopened: "Нарушение обнаружено повторно",
    complianceEventActionStarted: "Исправление начато",
    complianceEventConfirmed: "Пользователь подтвердил выполнение",
    complianceEventResolved: "Нарушение исправлено",
    complianceEventWaived: "Исключение подтверждено",
    complianceNoRules: "Правила соответствия не созданы.",
    complianceNoIssues: "Нарушений не обнаружено.",
    complianceNoHistory: "История пока пуста.",
    complianceImmediately: "Сразу",
    hoursShort: "ч",
    inactive: "неактивно",
    accountsTitle: "Аккаунт",
    appsColumns: "Столбцы",
    appsResetColumns: "Сбросить выбор столбцов",
    appsSearchAll: "Поиск",
    appsColName: "Имя",
    appsColBundleID: "Bundle ID",
    appsColPlatform: "Платформа",
    appsColType: "Тип",
    appsColVersion: "Версия",
    appsColVPPToken: "Имя токена VPP",
    appsColAssigned: "Назначено",
    appsColGroups: "Группы",
    appsColDeveloper: "Разработчик",
    appsColStatus: "Статус",
    appsColUpdatedAt: "Последнее изменение",
    appsEditGroups: "Изменить группы",
    appsEditGroupsModalTitle: "Изменить группы приложений",
    appsSelectedCount: "Выбрано приложений",
    appsDeleteSelected: "Удалить выбранные",
    appsDeleteSelectedConfirm: "Удалить выбранные приложения?",
    appsDeleteSelectedFailed: "Не удалось удалить выбранные приложения",
    appsAssignToGroups: "Изменение групп приложений",
    appsAssignGroupsFailed: "Не удалось изменить группы приложений",
    appsUploadHint: "Добавьте корпоративное приложение, размещенное на вашем HTTPS-хостинге. Для iOS/iPadOS укажите manifest.plist, который ссылается на IPA.",
    appsUploadManifestRequired: "Укажите Manifest URL или App Store ID",
    appsEnterpriseManifestURL: "Manifest URL / URL пакета",
    appsEnterpriseManifestHint: "iOS/iPadOS: manifest.plist для enterprise IPA. macOS: URL pkg/dmg. Android/Windows: URL пакета используется как источник записи в каталоге.",
    appsEnterpriseDescriptionPlaceholder: "Описание, версия, владелец приложения или примечания для администраторов",
    appsUploadCreate: "Добавить приложение",
    appsUploadClear: "Очистить",
    roleReadOnly: "read-only",
    roleAdmin: "admin",
    roleMasterAdmin: "master admin",
    dashboardTotalDeployment: "Общее количество устройств",
    dashboardSummaryAccounts: "Аккаунты",
    dashboardSummaryProfiles: "Профили",
    dashboardSummaryApps: "Приложения",
    dashboardSummaryGroups: "Группы",
    dashboardSectionSecurity: "Безопасность",
    dashboardSectionSupportTickets: "Обращения в поддержку",
    dashboardSectionOwnership: "Владение",
    dashboardSectionLastSeenOverview: "Обзор последней активности",
    dashboardSectionLastSeenBreakdown: "Распределение по последней активности",
    dashboardSectionPlatforms: "Платформы",
    onboardingDemoSeeded: "Demo data создана",
    onboardingDemoSeedFailed: "Не удалось создать demo data",
    dashboardNoSections: "Для панели мониторинга не выбраны видимые блоки.",
    dashboardSecurityEncrypted: "Зашифровано",
    dashboardSecurityPasscode: "Passcode compliant",
    dashboardSecuritySupervised: "Supervised",
    dashboardSupportOpen: "Открыто",
    dashboardSupportWaiting: "Ожидает",
    dashboardOwnershipCorporate: "Корпоративные",
    dashboardOwnershipPersonal: "Личные",
    dashboardOwnershipUnassigned: "Без аккаунта",
    dashboardLastSeenRecent: "активны за последние 7 дней",
    dashboardLastSeenCoverage: "Доля устройств с check-in за 7 дней",
    dashboardLastSeen0to1: "0-1 дней",
    dashboardLastSeen2to7: "2-7 дней",
    dashboardLastSeen8to15: "8-15 дней",
    dashboardLastSeen16to30: "16-30 дней",
    dashboardLastSeen31Plus: "31+ дней",
    searchDevices: "Поиск по UDID / имени / серийному",
    devicesColumns: "Столбцы",
    devicesResetColumns: "Сбросить выбор столбцов",
    devicesCount: "устройства",
    devicesFilterLastSeen: "Последняя активность",
    devicesColName: "Имя устройства",
    devicesColIMEI: "IMEI",
    devicesColWiFiMac: "MAC-адрес Wi-Fi",
    devicesColMEID: "MEID",
    devicesColCarrier: "Абонентский оператор",
    devicesColOSVersion: "Версия ОС",
    devicesColOwnership: "Владение",
    devicesOwnershipPersonal: "Личное",
    devicesOwnershipCorporate: "Корпоративное",
    devicesColCertExpiry: "Дата окончания срока действия сертификата управления",
    devicesColEnrollmentDate: "Дата регистрации",
    devicesColEncrypted: "Зашифровано",
    devicesColAccountName: "Имя аккаунта",
    devicesColControlled: "Контролируется",
    devicesColModel: "Модель",
    devicesColPhone: "Номер телефона",
    devicesColOS: "ОС",
    devicesColTotalStorage: "Общий объем хранилища",
    devicesColPrimaryEmail: "Основной адрес электронной почты пользователя",
    devicesColLastSync: "Последняя синхронизация",
    devicesColFreeStorage: "Свободное место в хранилище",
    devicesColSerial: "Серийный номер",
    devicesColCompliance: "Соответствие требованиям",
    deviceDetailsTabAbout: "Об устройстве",
    deviceDetailsBatteryLevel: "Уровень батареи",
    deviceDetailsSupplementalBuild: "Supplemental build",
    deviceDetailsSupplementalOSExtra: "Supplemental OS extra",
    deviceDetailsActiveManagedUsers: "Активные управляемые пользователи",
    deviceDetailsManagedAdmins: "Управляемые локальные администраторы",
    deviceDetailsDeviceID: "Device ID",
    deviceDetailsDoNotDisturb: "Не беспокоить",
    deviceDetailsEASDeviceID: "EAS Device ID",
    deviceDetailsEID: "EID",
    deviceDetailsESIMIdentifier: "Идентификатор eSIM",
    deviceDetailsAppleSilicon: "Apple silicon",
    deviceDetailsICloudBackup: "iCloud Backup включен",
    deviceDetailsFindMy: "Find My включен",
    deviceDetailsMDMLostMode: "Managed Lost Mode включен",
    deviceDetailsMultiUser: "Shared iPad",
    deviceDetailsLastICloudBackup: "Последний iCloud Backup",
    deviceDetailsMaximumResidents: "Максимум пользователей Shared iPad",
    deviceDetailsModemFirmware: "Версия прошивки модема",
    deviceDetailsAuthGracePeriod: "Grace period онлайн-аутентификации",
    deviceDetailsQuotaSize: "Квота хранилища",
    deviceDetailsResidentUsers: "Пользователи Shared iPad",
    deviceDetailsSIP: "System Integrity Protection",
    deviceDetailsTimeZone: "Часовой пояс",
    deviceDetailsBluetoothMac: "MAC-адрес Bluetooth",
    deviceDetailsEthernetMacs: "MAC-адреса Ethernet",
    deviceDetailsConnectedWiFiSSID: "Подключенная Wi-Fi сеть",
    deviceDetailsConnectedWiFiBSSID: "Wi-Fi BSSID",
    deviceDetailsCarrierSettingsVersion: "Версия настроек оператора",
    deviceDetailsCurrentCarrier: "Текущий оператор",
    deviceDetailsCurrentMCC: "Текущий MCC",
    deviceDetailsCurrentMNC: "Текущий MNC",
    deviceDetailsDataRoaming: "Роуминг данных",
    deviceDetailsICCID: "ICCID",
    deviceDetailsAllowedICCID: "Разрешенная SIM-карта",
    deviceDetailsAllowedICCIDEdit: "Изменить разрешенный ICCID",
    deviceDetailsAllowedICCIDUseCurrent: "Сделать текущую SIM разрешенной",
    deviceDetailsAllowedICCIDSaved: "Разрешенная SIM-карта обновлена",
    deviceDetailsAllowedICCIDFailed: "Не удалось обновить разрешенную SIM-карту",
    deviceDetailsComplianceTitle: "Несоответствие требованиям",
    deviceDetailsComplianceCheckNow: "Проверить устройство сейчас",
    deviceDetailsComplianceOpenIssues: "Открытые нарушения",
    deviceDetailsComplianceNoIssues: "Открытых нарушений по устройству нет",
    deviceDetailsComplianceRule: "Правило",
    deviceDetailsComplianceStatus: "Статус",
    deviceDetailsComplianceDueAt: "Исправить до",
    deviceDetailsComplianceLastCommand: "Последняя команда проверки",
    deviceDetailsComplianceNoCommand: "Команда проверки еще не отправлялась",
    deviceDetailsSIMAuditTitle: "SIM audit",
    deviceDetailsSIMCurrentICCID: "Текущий ICCID",
    deviceDetailsSIMAllowedICCID: "Разрешенный ICCID",
    deviceDetailsSIMMatch: "Совпадает",
    deviceDetailsSIMObservedAt: "Последняя проверка SIM",
    deviceDetailsSIMModalTitle: "Разрешенная SIM-карта",
    deviceDetailsSIMModalSubtitle: "Редактирование allowlist и доказательства изменений SIM для этого устройства.",
    deviceDetailsSIMAuditOld: "Было",
    deviceDetailsSIMAuditNew: "Стало",
    deviceDetailsSIMAuditActor: "Кто изменил",
    deviceDetailsSIMAuditAt: "Когда",
    deviceDetailsSIMAuditEmpty: "Истории изменений пока нет",
    deviceDetailsSIMEvidenceTitle: "Evidence",
    deviceDetailsSIMSave: "Сохранить SIM",
    deviceDetailsNetworkTethered: "Сетевой tethering",
    deviceDetailsPersonalHotspot: "Режим модема",
    deviceDetailsIsRoaming: "Роуминг",
    deviceDetailsSIMCarrier: "Оператор SIM",
    deviceDetailsSIMMCC: "SIM MCC",
    deviceDetailsSIMMNC: "SIM MNC",
    deviceDetailsSubscriberCarrier: "Оператор абонента",
    deviceDetailsSubscriberMCC: "MCC абонента",
    deviceDetailsSubscriberMNC: "MNC абонента",
    deviceDetailsCellularDataUsage: "Трафик сотовой сети",
    deviceDetailsWiFiDataUsage: "Трафик Wi-Fi",
    deviceDetailsTabApps: "Приложения",
    deviceDetailsTabProfiles: "Профили",
    deviceDetailsTabLocation: "Локация",
    deviceDetailsTabSharedIPad: "Shared iPad",
    deviceDetailsTabMacOSUEM: "macOS UEM",
    deviceDetailsTabCommands: "Запросы",
    deviceDetailsTabRemoteAccess: "Удаленный доступ",
    deviceDetailsCommandsMenu: "Команды",
    deviceDetailsAppleCommandsSecurity: "Безопасность и восстановление",
    deviceDetailsAppleCommandsInventory: "Инвентаризация",
    deviceDetailsAppleCommandsProfilesApps: "Профили и приложения",
    deviceDetailsAppleCommandsUsers: "Учетные записи macOS",
    deviceDetailsAppleCommandsMacManagement: "Управление macOS",
    deviceDetailsAppleCommandsContent: "Контент",
    deviceDetailsAppleCommandsAdvanced: "Дополнительные команды",
    deviceDetailsAppleCommandEraseDevice: "Стереть устройство",
    deviceDetailsAppleCommandReturnToService: "Стереть и вернуть в управление",
    deviceDetailsAppleCommandRestrictions: "Запросить ограничения устройства",
    deviceDetailsAppleCommandActivationLockBypassCode: "Запросить код обхода блокировки активации",
    deviceDetailsAppleCommandClearActivationLockBypassCode: "Удалить код обхода блокировки активации",
    deviceDetailsAppleCommandRotateFileVaultKey: "Сменить ключ FileVault",
    deviceDetailsAppleCommandSetRecoveryLock: "Установить пароль Recovery Lock",
    deviceDetailsAppleCommandVerifyRecoveryLock: "Проверить пароль Recovery Lock",
    deviceDetailsAppleCommandSetFirmwarePassword: "Установить пароль прошивки",
    deviceDetailsAppleCommandVerifyFirmwarePassword: "Проверить пароль прошивки",
    deviceDetailsAppleCommandClearRestrictionsPassword: "Удалить пароль ограничений",
    deviceDetailsAppleCommandManagedApplicationList: "Запросить управляемые приложения",
    deviceDetailsAppleCommandCertificateList: "Запросить сертификаты",
    deviceDetailsAppleCommandProvisioningProfileList: "Запросить provisioning profiles",
    deviceDetailsAppleCommandContentCachingInformation: "Запросить данные Content Caching",
    deviceDetailsAppleCommandInstallProvisioningProfile: "Установить provisioning profile",
    deviceDetailsAppleCommandRemoveProvisioningProfile: "Удалить provisioning profile",
    deviceDetailsAppleCommandValidateApplications: "Проверить приложения",
    deviceDetailsAppleCommandInstallEnterpriseApplication: "Установить корпоративное приложение",
    deviceDetailsAppleCommandManagedApplicationAttributes: "Запросить атрибуты управляемых приложений",
    deviceDetailsAppleCommandManagedApplicationConfiguration: "Запросить конфигурацию управляемых приложений",
    deviceDetailsAppleCommandManagedApplicationFeedback: "Запросить обратную связь приложений",
    deviceDetailsAppleCommandRefreshCellularPlans: "Обновить сотовые планы",
    deviceDetailsAppleCommandUserList: "Запросить локальных пользователей",
    deviceDetailsAppleCommandUnlockUserAccount: "Разблокировать учетную запись",
    deviceDetailsAppleCommandLogOutUser: "Завершить сеанс пользователя",
    deviceDetailsAppleCommandDeleteUser: "Удалить пользователя",
    deviceDetailsAppleCommandAccountConfiguration: "Настроить локальные учетные записи",
    deviceDetailsAppleCommandSetAutoAdminPassword: "Изменить пароль автоматического администратора",
    deviceDetailsAppleCommandEnableRemoteDesktop: "Включить удаленный рабочий стол",
    deviceDetailsAppleCommandDisableRemoteDesktop: "Выключить удаленный рабочий стол",
    deviceDetailsAppleCommandRequestMirroring: "Запросить дублирование экрана",
    deviceDetailsAppleCommandStopMirroring: "Остановить дублирование экрана",
    deviceDetailsAppleCommandInstallMedia: "Установить медиаконтент",
    deviceDetailsAppleCommandRemoveMedia: "Удалить медиаконтент",
    deviceDetailsAppleCommandApplyRedemptionCode: "Применить код погашения",
    deviceDetailsAppleCommandManagedMediaList: "Запросить управляемый медиаконтент",
    deviceDetailsAppleCommandActiveNSExtensionsMappings: "Запросить активные сетевые расширения",
    deviceDetailsAppleCommandNSExtensionsMappings: "Запросить сопоставления сетевых расширений",
    deviceDetailsAppleCommandUserConfigured: "Подтвердить настройку пользователя",
    deviceDetailsAppleCommandDeviceConfigured: "Подтвердить настройку устройства",
    deviceDetailsAppleCommandInviteToProgram: "Отправить приглашение в программу",
    deviceDetailsAppleCommandLOMDeviceRequest: "Отправить запрос Lights-Out Management",
    deviceDetailsAppleCommandLOMSetupRequest: "Настроить Lights-Out Management",
    deviceDetailsAppleCommandDeclarativeManagement: "Синхронизировать декларативное управление",
    deviceDetailsAppleCommandSettings: "Отправить настройки MDM",
    deviceDetailsAppleCommandModalHint: "Параметры команды передаются устройству как словарь Apple MDM. Удалите необязательные поля или заполните значения перед отправкой.",
    deviceDetailsAppleCommandDestructiveWarning: "Команда может удалить данные, доступ или учетную запись. Проверьте параметры перед отправкой.",
    deviceDetailsAppleCommandParameters: "Параметры команды (JSON)",
    deviceDetailsAppleCommandSubmit: "Отправить команду",
    deviceDetailsAppleCommandInvalidJSON: "Параметры должны быть корректным JSON-объектом",
    deviceDetailsAppleCommandQueued: "Команда Apple MDM поставлена в очередь",
    deviceDetailsAppleCommandFailed: "Не удалось отправить команду Apple MDM",
    deviceDetailsCommandClearPasscode: "Удалить код разблокировки",
    deviceDetailsCommandDeviceLock: "Заблокировать экран",
    deviceDetailsCommandPlayLostModeSound: "Воспроизвести звук режима пропажи",
    deviceDetailsCommandTurnOnLostMode: "Включить режим пропажи",
    deviceDetailsCommandTurnOffLostMode: "Выключить режим пропажи",
    deviceDetailsCommandRestartDevice: "Перезагрузить устройство",
    deviceDetailsCommandShutDownDevice: "Выключить устройство",
    deviceDetailsCommandScheduleOSUpdateScan: "Запланировать сканирование обновлений ОС",
    deviceDetailsCommandAvailableOSUpdates: "Проверить доступные обновления ОС",
    deviceDetailsCommandScheduleOSUpdateDownload: "Запланировать загрузку обновления ОС",
    deviceDetailsCommandScheduleOSUpdateInstall: "Запланировать установку обновления ОС",
    deviceDetailsCommandRemoveMDMProfile: "Удалить профиль управления MDM",
    deviceDetailsRemoveMDMProfileConfirm: "Удалить профиль управления MDM с этого устройства? Устройство будет снято с управления Turuk MDM.",
    deviceDetailsRemoveMDMProfileQueued: "Удаление профиля управления MDM поставлено в очередь",
    deviceDetailsRemoveMDMProfileFailed: "Не удалось отправить удаление профиля управления MDM",
    deviceDetailsOSUpdateModalTitleDownload: "Запланировать загрузку обновления ОС",
    deviceDetailsOSUpdateModalTitleInstall: "Запланировать установку обновления ОС",
    deviceDetailsOSUpdateModalProductKey: "Product Key",
    deviceDetailsOSUpdateModalProductVersion: "Версия ОС",
    deviceDetailsOSUpdateModalSubmitDownload: "Запланировать загрузку",
    deviceDetailsOSUpdateModalSubmitInstall: "Запланировать установку",
    deviceDetailsOSUpdateModalHint: "Если Product Key и Версия ОС не указаны, устройство загрузит или установит последнее доступное обновление. Apple MDM не содержит параметров «только Wi-Fi» или точного ночного времени: условия сети, питания и доступность установки контролирует ОС.",
    deviceDetailsOSUpdateModalTargets: "Выбрано устройств",
    devicesBulkCommands: "Команды",
    devicesBulkOSUpdateDownload: "Загрузить, но не устанавливать ОС",
    devicesBulkOSUpdateDownloadInstall: "Установить загруженное обновление ОС",
    devicesBulkOSUpdateAppleOnly: "Команды обновления ОС доступны только для выбранных устройств Apple",
    deviceDetailsLostModeModalTitle: "Включить режим пропажи",
    deviceDetailsLostModeModalMessage: "Короткое сообщение",
    deviceDetailsLostModeModalPhone: "Телефон",
    deviceDetailsLostModeModalSubmit: "Включить",
    deviceDetailsLocationRequest: "Запросить местоположение",
    deviceDetailsLocationRequestQueued: "Запрос местоположения поставлен в очередь",
    deviceDetailsLocationRequestFailed: "Не удалось отправить запрос местоположения",
    deviceDetailsLocationRequiresIOS: "Команда доступна только для устройств iOS и iPadOS",
    deviceDetailsLocationRequiresSupervised: "Команда доступна только для устройств в режиме Supervised",
    deviceDetailsLocationLostModeActiveHint: "Режим пропажи активен. Местоположение будет запрошено через Apple MDM.",
    deviceDetailsLocationLostModeRequiredHint: "Для запроса через Apple MDM устройство временно перейдет в режим пропажи и затем автоматически выйдет из него.",
    deviceDetailsLocationLostModeQueued: "Временный режим пропажи поставлен в очередь; местоположение будет запрошено автоматически",
    deviceDetailsLocationLostModeModalTitle: "Включить режим пропажи и запросить местоположение",
    deviceDetailsLocationLostModeModalHint: "Apple разрешает MDM получать местоположение только у supervised-устройства в режиме пропажи. Для запроса местоположения Turuk автоматически выключит режим пропажи после получения координат.",
    deviceDetailsLocationLostModeModalSubmit: "Включить и запросить",
    deviceDetailsNoApps: "Список приложений пуст",
    deviceDetailsNoProfiles: "Профили из консоли не найдены",
    deviceDetailsNoLocation: "Данные местоположения пока не получены",
    deviceDetailsLocationUpdatedAt: "Время точки",
    deviceDetailsLocationLatitude: "Широта",
    deviceDetailsLocationLongitude: "Долгота",
    deviceDetailsLocationAltitude: "Высота",
    deviceDetailsLocationAccuracy: "Точность",
    deviceDetailsLocationSpeed: "Скорость",
    deviceDetailsLocationSource: "Источник",
    deviceDetailsLocationMap: "Карта",
    deviceDetailsLocationOpenOSM: "Открыть в OpenStreetMap",
    deviceDetailsLocationOpenGoogle: "Открыть в Google Maps",
    deviceDetailsLocationHistory: "История точек",
    deviceDetailsSharedIPadTitle: "Shared iPad / Education",
    deviceDetailsSharedIPadHint: "Рабочие процессы для общих iPad, Managed Apple Account и classroom-управления.",
    deviceDetailsSharedIPadRefreshUsers: "Обновить пользователей",
    deviceDetailsSharedIPadNoUsers: "Список пользователей пока не получен. Отправьте UserList.",
    deviceDetailsSharedIPadUserName: "Пользователь",
    deviceDetailsSharedIPadFullName: "Имя",
    deviceDetailsSharedIPadLoggedIn: "Сессия",
    deviceDetailsSharedIPadData: "Данные",
    deviceDetailsSharedIPadLogout: "Завершить сеанс",
    deviceDetailsSharedIPadDelete: "Удалить пользователя",
    deviceDetailsSharedIPadQueued: "Команда Shared iPad поставлена в очередь",
    deviceDetailsSharedIPadFailed: "Не удалось отправить команду Shared iPad",
    deviceDetailsSharedIPadSettings: "Настройки организации",
    deviceDetailsSharedIPadManagedDomain: "Managed Apple Account domain",
    deviceDetailsSharedIPadDefaultDomain: "Default domain",
    deviceDetailsSharedIPadClassroomNotes: "Classroom / Education notes",
    deviceDetailsSharedIPadSettingsSaved: "Настройки Shared iPad сохранены",
    deviceDetailsActivationLockTitle: "Блокировка активации",
    deviceDetailsActivationLockHint: "Срок действия кода обхода активации задает Apple, поэтому используйте последний код и обновляйте его перед восстановлением устройства.",
    deviceDetailsActivationLockRefresh: "Запросить код",
    deviceDetailsActivationLockClear: "Очистить код",
    deviceDetailsActivationLockNoCodes: "Код обхода пока не получен. Отправьте запрос кода блокировки активации.",
    deviceDetailsActivationLockCode: "Код обхода",
    deviceDetailsActivationLockReportedAt: "Получен",
    deviceDetailsActivationLockExpiresAt: "Истекает",
    deviceDetailsActivationLockSource: "Команда",
    deviceDetailsActivationLockRefreshQueued: "Запрос кода обхода поставлен в очередь",
    deviceDetailsActivationLockClearQueued: "Очистка кода обхода поставлена в очередь",
    deviceDetailsActivationLockFailed: "Не удалось выполнить действие блокировки активации",
    deviceDetailsCommandStateQueued: "В очереди",
    deviceDetailsCommandStateSent: "Отправлена на устройство",
    deviceDetailsCommandStateDone: "Выполнена устройством",
    deviceDetailsCommandStateDeadLetter: "Не выполнена",
    deviceDetailsCommandStateCanceled: "Отменена",
    deviceDetailsCommandStateNotNow: "Устройство отложило команду (NotNow)",
    deviceDetailsCommandStateError: "Ошибка выполнения на устройстве",
    deviceDetailsCommandLastError: "Последняя ошибка",
    deviceDetailsCommandAttempts: "Попытки",
    deviceDetailsCommandRetry: "Повторить",
    deviceDetailsCommandCancel: "Отменить",
    deviceDetailsCommandRetryQueued: "Команда повторно поставлена в очередь",
    deviceDetailsCommandRetryFailed: "Не удалось повторить команду",
    deviceDetailsCommandCancelDone: "Команда отменена",
    deviceDetailsCommandCancelFailed: "Не удалось отменить команду",
    deviceDetailsAppInstallPending: "Ожидает установки",
    deviceDetailsAppInstallFailed: "Не установлено",
    deviceDetailsAppInstallDone: "Установлено",
    deviceDetailsAppInstallReason: "Причина",
    deviceDetailsAppInstallReasonUnknown: "Причина не указана устройством",
    deviceDetailsProfileInstallPending: "Ожидает установки",
    deviceDetailsProfileInstallFailed: "Не установлено",
    deviceDetailsProfileInstallDone: "Установлено",
    deviceDetailsProfileInstallReason: "Причина",
    deviceDetailsProfileInstallReasonUnknown: "Причина не указана устройством",
    deviceDetailsRequestQueued: "Запрос устройства поставлен в очередь",
    deviceDetailsRequestFailed: "Не удалось отправить запрос устройства",
    deviceDetailsClearPasscodeQueued: "Команда удаления кода разблокировки поставлена в очередь",
    deviceDetailsClearPasscodeFailed: "Не удалось отправить команду удаления кода разблокировки",
    deviceDetailsDeviceLockQueued: "Команда блокировки устройства поставлена в очередь",
    deviceDetailsDeviceLockFailed: "Не удалось отправить команду блокировки устройства",
    deviceDetailsPlayLostModeSoundQueued: "Команда воспроизведения звука режима пропажи поставлена в очередь",
    deviceDetailsPlayLostModeSoundFailed: "Не удалось отправить команду воспроизведения звука режима пропажи",
    deviceDetailsTurnOnLostModeQueued: "Команда включения режима пропажи поставлена в очередь",
    deviceDetailsTurnOnLostModeFailed: "Не удалось отправить команду включения режима пропажи",
    deviceDetailsTurnOffLostModeQueued: "Команда выключения режима пропажи поставлена в очередь",
    deviceDetailsTurnOffLostModeFailed: "Не удалось отправить команду выключения режима пропажи",
    deviceDetailsLostModeMessageRequired: "Введите короткое сообщение для режима пропажи",
    deviceDetailsLostModePhoneRequired: "Введите телефон для режима пропажи",
    deviceDetailsRestartDeviceQueued: "Команда перезагрузки устройства поставлена в очередь",
    deviceDetailsRestartDeviceFailed: "Не удалось отправить команду перезагрузки устройства",
    deviceDetailsShutDownDeviceQueued: "Команда выключения устройства поставлена в очередь",
    deviceDetailsShutDownDeviceFailed: "Не удалось отправить команду выключения устройства",
    deviceDetailsScheduleOSUpdateScanQueued: "Команда сканирования обновлений ОС поставлена в очередь",
    deviceDetailsScheduleOSUpdateScanFailed: "Не удалось отправить команду сканирования обновлений ОС",
    deviceDetailsAvailableOSUpdatesQueued: "Команда проверки доступных обновлений ОС поставлена в очередь",
    deviceDetailsAvailableOSUpdatesFailed: "Не удалось отправить команду проверки доступных обновлений ОС",
    deviceDetailsScheduleOSUpdateQueued: "Команда планирования обновления ОС поставлена в очередь",
    deviceDetailsScheduleOSUpdateFailed: "Не удалось отправить команду планирования обновления ОС",
    devicesBulkRequestQueued: "Запрос устройств поставлен в очередь",
    devicesBulkRequestFailed: "Не удалось отправить запрос на устройства",
    devicesBulkRequestNoDevices: "Нет устройств для запроса",
    deviceDetailsResendProfiles: "Установить",
    deviceDetailsDisableProfiles: "Удалить",
    deviceDetailsNoManagedAppsSelected: "Выберите хотя бы одно управляемое приложение",
    deviceDetailsNoProfilesSelected: "Выберите хотя бы один профиль",
    deviceDetailsReinstallFailed: "Не удалось переустановить приложения",
    deviceDetailsRemoveFailed: "Не удалось удалить приложения",
    deviceDetailsProfileActionFailed: "Не удалось выполнить действие с профилями",
    deviceDetailsReinstallDone: "Переустановка приложений поставлена в очередь",
    deviceDetailsRemoveDone: "Удаление приложений поставлено в очередь",
    deviceDetailsProfilesResentDone: "Профили отправлены повторно",
    deviceDetailsProfilesDisabledDone: "Профили отключены",
    deviceDetailsMissingCatalogApps: "Для части приложений не найдено соответствие в каталоге. Добавьте их в каталог приложений, затем повторите.",
    noDevices: "Устройства не найдены",
    devicesDelete: "Delete",
    devicesDeleteFailed: "Не удалось удалить устройства",
    devicesDeleteConfirm: "Удалить выбранные устройства из MDM? Будет отправлена команда RemoveProfile.",
    adminOnlyAction: "Операция доступна только admin",
    profilesDeploy: "Деплой профиля",
    profilesDeployFailed: "Не удалось выполнить деплой профиля",
    profilesAppleOnlyBuilderHint: "Конструктор профиля сейчас доступен только для iOS/iPadOS и macOS. Для выбранной ОС добавим отдельный редактор позже.",
    profilesEditGroups: "Изменить группы",
    profilesEditGroupsModalTitle: "Изменить группы профилей",
    profilesSelectedCount: "Выбрано профилей",
    profilesAssignToGroups: "Изменение групп профилей",
    profilesAssignGroupsFailed: "Не удалось изменить группы профилей",
    profilesGroups: "Группы",
    profilesNoItems: "Профили не найдены",
    profilesColumns: "Столбцы",
    profilesResetColumns: "Сбросить выбор столбцов",
    profilesDeleteSelected: "Удалить выбранные",
    profilesDeleteSelectedConfirm: "Удалить выбранные элементы?",
    profilesDeleteSelectedFailed: "Не удалось удалить выбранные элементы",
    profilesColName: "Название",
    profilesColIdentifier: "Идентификатор",
    profilesColGroups: "Группы",
    profilesColDevices: "Устройства",
    profilesColStatus: "Статус",
    profilesColUpdatedAt: "Последнее изменение",
    profileDevicesModalTitle: "Устройства профиля",
    profileDevicesFilterAll: "Все устройства",
    profileDevicesFilterInstalled: "Установлено",
    profileDevicesFilterMissing: "Не установлено",
    profileDevicesInstalled: "Установлено",
    profileDevicesNotInstalled: "Не установлено",
    profileDevicesEmpty: "Назначенных устройств пока нет.",
    profileDevicesExportCSV: "Скачать CSV",
    profileDevicesExportDone: "Отчет по устройствам профиля скачан",
    profileDevicesExportFailed: "Не удалось скачать отчет по устройствам профиля",
    policiesColName: "Имя политики",
    policiesColPlatform: "Платформа",
    policiesColType: "Тип политики",
    policiesColUpdatedAt: "Последнее изменение",
    policiesColScopeTags: "Теги области",
    policiesColCompliance: "Соответствие требованиям",
    profileBuilderNotConfigured: "Not configured",
    profileBuilderMandatory: "Mandatory",
    profileBuilderConfigured: "1 Payload Configured",
    profileBuilderConfiguredMany: "Payloads Configured",
    profileBuilderAtLeastOnePayloadRequired: "Добавьте минимум один payload (например Wi-Fi, VPN, Passcode, Restrictions и т.д.)",
    profileBuilderGeneralRemovalDateRequired: "Выберите дату автоматического удаления профиля",
    profileBuilderGeneralRemovalDaysRequired: "Укажите количество дней для автоматического удаления профиля",
    profileSectionTitleGlobalHTTPProxySupervised: "Global HTTP Proxy (supervised only)",
    profileSectionTitleDNSProxySupervised: "DNS Proxy (iOS, supervised only)",
    profileSectionTitleContentFilterSupervised: "Content Filter (supervised only)",
    profileSectionWarningGlobalHTTPProxy: "Внимание: неверный адрес, порт или аутентификация Global HTTP Proxy могут оборвать интернет и MDM-связь с устройством. Тестируйте сначала на отдельном supervised устройстве, с доступным прокси без аутентификации. Перед массовым назначением проверьте, что Safari открывает http://example.com и устройство продолжает отвечать на MDM-команды.",
    profileSectionHintRestrictions: "Используйте этот раздел, чтобы ограничить доступные пользователю приложения, функции устройства и медиаконтент.",
    profileSectionHintDomains: "Используйте этот раздел, чтобы задать немаркированные email-домены и управляемые web-домены.",
    profileSectionHintGlobalHTTPProxy: "Используйте этот раздел, чтобы настроить прокси-сервер, через который будет проходить HTTP-трафик устройства.",
    profileSectionHintDNSProxy: "Используйте этот раздел, чтобы настроить DNS proxy network extension. Этот payload не устанавливается через Apple Configurator и должен устанавливаться через MDM на устройствах iOS 15+.",
    profileSectionHintContentFilter: "Используйте этот раздел, чтобы настроить доступ устройства к URL-адресам.",
    profileSectionHintCertificates: "Используйте этот раздел, чтобы добавить сертификаты PKCS1/PKCS12 в конфигурационный профиль.",
    profileSectionHintPasscode: "Используйте этот раздел, чтобы задать политики Passcode для устройства.",
    profileSectionHintVPN: "Используйте этот раздел, чтобы настроить подключение устройства к VPN.",
    profileSectionHintAirPlay: "Используйте этот раздел, чтобы настроить параметры подключения к AirPlay destination.",
    profileSectionHintFileVault: "Используйте этот раздел, чтобы включить FileVault и настроить escrow recovery key.",
    profileSectionHintPPPC: "Используйте этот раздел, чтобы управлять Privacy Preferences Policy Control для macOS приложений.",
    profileSectionHintSystemExtensions: "Используйте этот раздел, чтобы разрешить system extensions и network/security extensions на macOS.",
    profileSectionHintFirewall: "Используйте этот раздел, чтобы включить firewall macOS, stealth mode и правила приложений.",
    profileSectionHintLoginItems: "Используйте этот раздел, чтобы управлять login items и автостартом приложений на macOS.",
    profileSectionHintBackgroundTasks: "Используйте этот раздел, чтобы управлять фоновыми задачами macOS через Service Management.",
    profileSectionHintMacOSSoftwareUpdate: "Используйте этот раздел, чтобы задать политики обновлений macOS, автоустановку и deferral.",
    profileSectionHintLocalAdmin: "Turuk macOS workflow для локальных администраторов: учетная запись, скрытие, Secure Token и ротация пароля требуют macOS Agent.",
    profileSectionHintScriptsPackages: "macOS workflow: pkg/dmg готовятся для MDM InstallApplication, shell scripts требуют Turuk macOS Agent.",
    profileSectionWarningDNSProxy: "Этот payload не устанавливается через Apple Configurator и должен устанавливаться через MDM на устройствах iOS 15 или новее.",
    profileAirPlayPasswords: "Passwords",
    profileAirPlayAllowed: "Allowed",
    profileAirPlayDeviceName: "Device Name",
    profileAirPlayPassword: "Password",
    profileAirPlayDestinationDialogTitle: "Добавьте имя AirPlay устройства и, при необходимости, пароль.",
    profileAirPlayDestinationName: "Name",
    profileAirPlayDestinationPassword: "Password",
    profileRestrictionsAppPickerTitle: "Выбор приложения",
    profileRestrictionsAppPickerHint: "Введите название приложения или его bundle identifier, затем выберите приложение для добавления.",
    profileRestrictionsAppPickerSearchPlaceholder: "Название или bundle identifier",
    profileRestrictionsAppPickerChoose: "Выбрать",
    profileRestrictionsAppPickerNoItems: "Приложения не найдены",
    profileBundlePickerLabel: "Выбрать стандартное приложение iOS",
    profileBundlePickerPlaceholder: "— выберите приложение —",
    profileBuilderUploadFile: "Загрузить файл...",
    profileBuilderRemoveConfiguration: "Удалить конфигурацию",
    profileCertificatesLoadFailed: "Не удалось загрузить файл сертификата",
    cancel: "Отмена",
    save: "Сохранить",
    close: "Закрыть",
    clear: "Очистить",
    row: "Строка",
    create: "Создать",
    tableStatus: "Статус",
    tableAction: "Действие",
    accountsHint: "Аккаунты, к которым привязываются устройства.",
    accountsCreateAccount: "Создать аккаунт",
    accountsCreateModalTitle: "Новый аккаунт",
    accountsEditModalTitle: "Аккаунт",
    accountsImport: "Импорт",
    accountsImportTitle: "Импорт аккаунтов",
    accountsImportUpload: "Загрузить с компьютера",
    accountsImportFile: "Файл",
    accountsImportSubmit: "Импортировать",
    accountsImportDone: "Импортировано аккаунтов",
    accountsImportFailed: "Не удалось импортировать аккаунты",
    accountsImportEmpty: "Файл не содержит аккаунтов для импорта",
    accountsImportParseFailed: "Не удалось прочитать файл импорта",
    accountsImportExcelUnsupported: "Файл Excel нужно сохранить как CSV и загрузить повторно.",
    accountsImportFormat: "Поддерживается CSV из Excel. Одна строка — один аккаунт. Колонки: login,email,password,first_name,last_name,department.",
    accountsImportHelp: "Можно загрузить CSV с первой строкой-заголовком: login,email,password,first_name,last_name,department. Если заголовка нет, система читает колонки в этом же порядке. Обязательные поля: login, email, password. Пароль минимум 6 символов. Разделитель: запятая, точка с запятой или табуляция.",
    accountsImportMoreRows: "Еще строк: {count}",
    accountsFirstName: "Имя",
    accountsSecondName: "Фамилия",
    accountsDepartment: "Отдел",
    accountsName: "Логин",
    accountsLoginHint: "Логин используется для авторизации в приложении Turuk Companion.",
    accountsEmail: "Email",
    accountsPassword: "Пароль",
    accountsPasswordKeepHint: "Оставьте пустым, чтобы не менять пароль.",
    accountsActiveLabel: "Аккаунт активен",
    accountsSearch: "Поиск аккаунта (name/email/external)",
    accountsFilterActive: "Только активные",
    accountsFilterWithDevices: "Только с устройствами",
    accountsGroups: "Группы",
    accountsAddToGroupModalTitle: "Изменить группы аккаунтов",
    accountsSelectedCount: "Выбрано аккаунтов",
    accountsDeleteSelected: "Удалить выбранные",
    accountsDeleteSelectedConfirm: "Удалить выбранные аккаунты?",
    accountsDeleteSelectedFailed: "Не удалось удалить выбранные аккаунты",
    accountsAssignToGroups: "Добавить в группу",
    accountsAssignGroupsFailed: "Не удалось назначить аккаунты в группы",
    accountsNoItems: "Аккаунты не найдены",
    accountsCreateFailed: "Не удалось создать аккаунт",
    accountsAssign: "Привязать",
    accountsUnassign: "Отвязать",
    accountsDevicesTitle: "Устройства аккаунта",
    accountsMdmDevicesCount: "MDM устройства",
    accountsMobileBindingsTitle: "Мобильные привязки",
    accountsMobileDeviceName: "Устройство",
    accountsMobileLastSeen: "Последняя активность",
    accountsAssignFailed: "Не удалось привязать устройство",
    accountsUnassignFailed: "Не удалось отвязать устройство",
    accountsMobileUnbindFailed: "Не удалось отвязать мобильное устройство",
    accountsMobileUnbindDone: "Мобильное устройство отвязано",
    accountsUpdate: "Сохранить аккаунт",
    accountsUpdateFailed: "Не удалось обновить аккаунт",
    accountsDeleteFailed: "Не удалось удалить аккаунт",
    groupsTitle: "Группы",
    groupsName: "Название группы",
    groupsColAccounts: "Аккаунты",
    groupsColApps: "Приложения",
    groupsColProfiles: "Профили",
    groupsColDocuments: "Документы",
    groupsSelectedCount: "Выбрано групп",
    groupsNoItems: "Группы не созданы",
    groupsCreateModalTitle: "Новая группа",
    groupsCreateFailed: "Не удалось создать группу",
    groupsDeleteSelected: "Удалить выбранные",
    groupsDeleteSelectedConfirm: "Удалить выбранные группы?",
    groupsDeleteSelectedFailed: "Не удалось удалить выбранные группы",
    appsDeploy: "Деплой приложения",
    appsDeployFailed: "Не удалось выполнить деплой приложения",
    appsStoreTitleIOS: "Выбор приложения из iOS/iPadOS App Store",
    appsStoreTitleMac: "Выбор приложения из Mac App Store",
    appsStoreTitleAndroid: "Выбор приложения из Managed Google Play",
    appsStoreHintIOS: "Найдите iOS/iPadOS приложение по названию и добавьте его в каталог.",
    appsStoreHintMac: "Найдите macOS приложение по названию и добавьте его в каталог.",
    appsStoreHintAndroid: "Выберите Android-приложение в Managed Google Play. После выбора приложение будет добавлено в каталог.",
    appsStoreCountry: "Страна",
    appsStoreSearchButton: "Найти",
    appsStoreNoItems: "Ничего не найдено",
    appsStoreOpen: "Открыть в магазине",
    appsStoreAddToCatalog: "Добавить в консоль",
    appsStoreSearchFailed: "Не удалось выполнить поиск в App Store",
    appsStoreAddFailed: "Не удалось добавить приложение из App Store",
    appsStoreAdded: "Приложение добавлено",
    appsManagedPlayLoading: "Загрузка Managed Google Play...",
    appsManagedPlayInitFailed: "Не удалось открыть Managed Google Play",
    appsManagedPlayFrameTitle: "Managed Google Play",
    appsManagedPlayOpenFallback: "Открыть Managed Google Play в новой вкладке",
    appsManagedPlaySelected: "Выбрано в Managed Google Play",
    appsManagedPlaySelectHint: "Нажмите Select в карточке приложения, чтобы добавить его в каталог.",
    platformPickerTitleApps: "Добавить приложение",
    platformPickerTitleProfiles: "Создать профиль",
    platformPickerHintApps: "Выберите платформу. Затем откроется соответствующий магазин приложений.",
    platformPickerHintProfiles: "Выберите платформу. Затем откроется создание профиля для этой ОС.",
    platformIOS: "iOS/iPadOS",
    platformMacOS: "macOS",
    platformAndroid: "Android",
    platformWindows: "Windows",
    platformLinux: "Linux",
    settingsWorkspaceConsoleGroup: "Настройки консоли",
    settingsWorkspaceConsoleBranding: "Оформление",
    settingsWorkspaceConsoleDashboard: "Панель мониторинга",
    settingsWorkspaceConsoleUsers: "Пользователи консоли",
    settingsWorkspaceAgent: "Turuk Companion",
    settingsWorkspaceCertificates: "Apple Configurator",
    settingsWorkspaceADE: "Apple Business Manager",
    settingsWorkspaceVPP: "Apple Apps and Books",
    settingsWorkspaceDeclarative: "Автоматические политики Apple",
    settingsWorkspaceSecurity: "Безопасность",
    settingsWorkspaceNotifications: "Уведомления",
    settingsWorkspaceReports: "Отчеты",
    settingsWorkspaceSSO: "SSO",
    settingsWorkspaceTenants: "Организации",
    settingsWorkspaceRoleSettings: "Настройки ролей",
    certificatesTitle: "Apple Configurator",
    certificatesHint: "Настройка Apple Configurator 2 для автоматической регистрации устройств в Turuk MDM.",
    certificatesConfiguratorTitle: "Apple Configurator",
    certificatesConfiguratorHint: "Установите профиль через Apple Configurator. Устройство появится в консоли без аккаунта. После входа пользователя в Turuk Companion устройство будет автоматически привязано к его аккаунту.",
    certificatesConfiguratorServiceName: "Name",
    certificatesConfiguratorServiceNameValue: "Turuk MDM",
    certificatesConfiguratorHostURL: "Host name or URL",
    certificatesConfiguratorPublicURL: "Публичный URL для Apple Configurator",
    certificatesConfiguratorURLHint: "В Apple Configurator вставляйте именно публичный URL ниже. Он уникален для организации. Если ссылка попала не туда, перевыпустите ее.",
    certificatesConfiguratorURLMissing: "URL еще не создан",
    certificatesConfiguratorRotateURL: "Создать / перевыпустить URL",
    certificatesConfiguratorURLRotated: "Configurator URL обновлен",
    certificatesConfiguratorURLRotateFailed: "Не удалось обновить Configurator URL",
    certificatesConfiguratorCopyURL: "Копировать URL",
    certificatesConfiguratorURLCopied: "Configurator URL скопирован",
    certificatesConfiguratorLastUsed: "Последнее использование",
    certificatesConfiguratorRotatedAt: "Перевыпущен",
    certificatesDownloadConfiguratorProfile: "Скачать профиль .mobileconfig",
    certificatesDownloadConfiguratorProfileDone: "Профиль Apple Configurator скачан",
    certificatesDownloadConfiguratorProfileFailed: "Не удалось скачать профиль Apple Configurator",
    certificatesMacOSAgentTitle: "macOS Agent",
    certificatesMacOSAgentHint: "Turuk macOS Agent устанавливается через MDM после регистрации Mac и выполняет scripts/packages, local admin workflows, FileVault и compliance jobs.",
    certificatesMacOSAgentPackageURL: "Публичная ссылка на пакет",
    certificatesDownloadMacOSAgentPackage: "Скачать Turuk macOS Agent.pkg",
    certificatesDownloadMacOSAgentPackageDone: "Turuk macOS Agent.pkg скачан",
    certificatesDownloadMacOSAgentPackageFailed: "Не удалось скачать Turuk macOS Agent.pkg",
    certificatesMacOSAgentUnavailable: "Пакет еще не собран или не загружен на сервер.",
    certificatesMacOSAgentSigned: "Подписан",
    certificatesMacOSAgentNotarized: "Notarized",
    certificatesMacOSAgentVersion: "Версия",
    certificatesMacOSAgentSHA256: "SHA-256",
    certificatesAPNSTitle: "APNs MDM для организации",
    certificatesAPNSHint: "Правильный процесс обновления: создайте CSR в консоли, в Apple Push Certificates Portal нажмите Renew у текущего сертификата, скачайте обновленный Apple MDM Push Certificate и загрузите его обратно сюда.",
    certificatesAPNSStep1: "1. Создать CSR для Renew",
    certificatesAPNSStep2: "2. Открыть Apple Push Certificates Portal и нажать Renew у текущего сертификата",
    certificatesAPNSStep3: "3. Ввести Apple ID и загрузить обновленный Apple MDM Push Certificate",
    certificatesAPNSCreateCSR: "Создать CSR для обновления",
    certificatesAPNSDownloadCSR: "Скачать текущий CSR",
    certificatesAPNSCSRReady: "CSR создан",
    certificatesAPNSCSRNotReady: "CSR еще не создан",
    certificatesAPNSCSRRenewOnly: "CSR создается только перед Renew",
    certificatesAPNSApplePortal: "Открыть Apple Push Certificates Portal",
    certificatesAPNSAppleID: "Apple ID, использованный для сертификата",
    certificatesAPNSTopic: "Topic",
    certificatesAPNSCert: "Apple MDM Push Certificate (.pem)",
    certificatesAPNSConfigured: "Настроено",
    certificatesAPNSNotConfigured: "Не настроено",
    certificatesAPNSStatus: "Статус",
    certificatesAPNSStatusDisabled: "Отключено",
    certificatesAPNSStatusExpired: "Сертификат истек",
    certificatesAPNSStatusExpiresSoon: "Скоро истекает",
    certificatesAPNSStatusReady: "Готово к управлению",
    certificatesAPNSSource: "Источник",
    certificatesAPNSSourceGlobal: "Глобальная настройка платформы",
    certificatesAPNSSourceTenant: "Настройка организации",
    certificatesAPNSDaysLeft: "Дней до истечения",
    certificatesAPNSCSRState: "CSR",
    certificatesAPNSSave: "Загрузить обновленный сертификат",
    certificatesAPNSSaved: "APNs MDM обновлен",
    certificatesAPNSSaveFailed: "Не удалось сохранить APNs MDM",
    certificatesAPNSCSRFailed: "Не удалось создать CSR",
    certificatesAPNSCSRDownloaded: "CSR скачан",
    certificatesAPNSCSRDownloadFailed: "Не удалось скачать CSR",
    certificatesAPNSCertSubject: "Сертификат",
    certificatesAPNSExpires: "Истекает",
    certificatesAPNSRenewSafety: "Важно: используйте Renew существующего сертификата. Если загрузить сертификат с другим Topic, консоль отклонит его, чтобы не потерять управление текущими устройствами.",
    certificatesAPNSRenewBannerTitle: "Истекает MDM APNs сертификат",
    certificatesAPNSRenewBannerText: "Осталось {days} дн. Истекает: {date}. Обновите сертификат через профиль регистрации, иначе устройства перестанут получать команды.",
    certificatesAPNSRenewBannerAction: "Открыть обновление APNs",
    certificatesAPNSExpectedTopic: "Ожидаемый Topic",
    certificatesAPNSUploadGuard: "Загружайте только сертификат, созданный через Renew текущего Topic. Сертификат с другим Topic будет отклонен.",
    adeTitle: "Apple Business Manager / ADE",
    adeHint: "Загрузка server token .p7m, синхронизация ADE-устройств и назначение профилей регистрации через Apple Business Manager.",
    adeTokenTitle: "Server token",
    adeConfigured: "Настроено",
    adeNotConfigured: "Не настроено",
    adeUploadToken: "Загрузить server token .p7m",
    adeTokenFile: "Файл server token (.p7m)",
    adeAppleID: "Apple ID, использованный в Apple Business Manager",
    adeOrganization: "Организация",
    adeServerUUID: "Server UUID",
    adeTokenFilename: "Файл",
    adeUploadedAt: "Загружен",
    adeTokenExpires: "Истекает",
    adeLastSync: "Последняя синхронизация",
    adeSyncStatus: "Статус синхронизации",
    adeSyncNow: "Синхронизировать устройства",
    adeTokenUploadDone: "Server token загружен",
    adeTokenUploadFailed: "Не удалось загрузить server token",
    adeSyncQueued: "Синхронизация ADE выполнена",
    adeDevicesTitle: "ADE устройства",
    adeNoDevices: "ADE-устройства еще не синхронизированы",
    adeSerialNumber: "Серийный номер",
    adeModel: "Модель",
    adeProfile: "Профиль регистрации",
    adeProfileStatus: "Статус профиля",
    adeLastSeen: "Последнее обновление",
    adeProfilesTitle: "Профили регистрации ADE",
    adeCreateProfile: "Создать профиль",
    adeProfileName: "Название профиля",
    adeProfileURL: "URL регистрации",
    adeDefaultProfile: "Профиль по умолчанию",
    adeProfileSaved: "Профиль ADE сохранен",
    adeProfileSaveFailed: "Не удалось сохранить профиль ADE",
    adeAssignProfileDone: "Профиль назначен",
    adeAssignProfileFailed: "Не удалось назначить профиль",
    adeUploadHint: "Используйте token из Apple Business Manager или Apple School Manager. Секрет хранится зашифрованным на уровне организации.",
    vppTitle: "Apple Apps and Books / VPP",
    vppHint: "Загрузите VPP token, синхронизируйте купленные лицензии и назначайте лицензии приложениям по группам.",
    vppSyncNow: "Синхронизировать лицензии",
    vppConfigured: "Настроено",
    vppNotConfigured: "Не настроено",
    vppTokenTitle: "VPP token",
    vppUploadToken: "Загрузить VPP token",
    vppOrganization: "Организация",
    vppTokenExpires: "Истекает",
    vppLastSync: "Последняя синхронизация",
    vppAssetsTitle: "Лицензии приложений",
    vppAssignmentsTitle: "Назначения лицензий",
    vppUploadHint: "Загрузите server token из Apple Business Manager / Apps and Books. Секрет хранится зашифрованным на уровне организации.",
    vppAppleID: "Apple ID, использованный для VPP token",
    vppTokenFile: "Файл VPP token",
    vppTokenFilename: "Файл",
    vppTokenUploadFailed: "Не удалось загрузить VPP token",
    vppTokenUploadDone: "VPP token загружен",
    vppSyncDone: "Синхронизация VPP выполнена",
    vppSyncFailed: "Не удалось синхронизировать VPP",
    vppAssignFailed: "Не удалось назначить лицензию",
    vppAssignDone: "Лицензия назначена",
    vppRevokeFailed: "Не удалось отозвать лицензию",
    vppRevokeDone: "Лицензия отозвана",
    vppLicensesTotal: "Всего",
    vppLicensesAvailable: "Доступно",
    vppLicensesAssigned: "Назначено",
    vppAssignLicense: "Назначить лицензию",
    vppRevokeLicense: "Отозвать лицензию",
    vppNoAssets: "Лицензии еще не синхронизированы.",
    vppNoAssignments: "Назначений лицензий пока нет.",
    vppLastError: "Последняя ошибка",
    securityTitle: "Безопасность",
    securityHint: "Управление паролем и многофакторной аутентификацией пользователей консоли.",
    securityOwnPasswordTitle: "Изменение своего пароля",
    securityOwnPasswordHint: "Измените пароль текущей учетной записи консоли.",
    securityOwnMFATitle: "MFA для своей учетной записи",
    securityOwnMFAHint: "Подключите приложение-аутентификатор к текущей учетной записи.",
    securityMFAAlreadyEnabled: "MFA уже включена для этой учетной записи.",
    securityAdminPasswordTitle: "Изменить пароль администратора",
    securityAdminPasswordHint: "Задайте временный пароль другому пользователю консоли. Его активные сессии будут завершены.",
    securityAdminMFATitle: "MFA пользователей консоли",
    securityAdminMFAHint: "Требование MFA заставит пользователя настроить Authenticator при следующем входе.",
    securitySelectUser: "Пользователь консоли",
    securityRequirePasswordChange: "Потребовать смену пароля при следующем входе",
    securityPasswordChanged: "Пароль пользователя изменен",
    securityPasswordChangeFailed: "Не удалось изменить пароль пользователя",
    securityMFARequired: "Требовать MFA",
    securityMFAUpdated: "Настройки MFA пользователя обновлены",
    securityMFAUpdateFailed: "Не удалось обновить настройки MFA пользователя",
    securitySelectAnotherUser: "Выберите другого пользователя консоли",
    securityTenantExportTitle: "Экспорт данных организации",
    securityTenantExportHint: "Скачивает JSON-экспорт данных текущей организации для резервного копирования, аудита и переноса. Секреты и приватные ключи не включаются.",
    securityTenantExportButton: "Скачать экспорт",
    securityTenantExportDone: "Экспорт данных организации скачан",
    securityTenantExportFailed: "Не удалось скачать экспорт данных организации",
    securityTenantIsolationTitle: "Изоляция организаций",
    securityTenantIsolationHint: "Проверяет runtime-состояние tenant_id, RLS-политик и уникальных индексов перед переводом БД на отдельную app-role и FORCE RLS.",
    securityTenantIsolationUnavailable: "Отчет недоступен. Примените миграцию sql/032_tenant_app_role_and_audit.sql.",
    securityTenantIsolationAppRole: "Готовность app-role",
    securityTenantIsolationForceRLS: "FORCE RLS",
    securityTenantIsolationMissingTables: "Таблицы без tenant_id",
    securityTenantIsolationRLSIssues: "Проблемы RLS/политик",
    securityTenantIsolationUniqueIssues: "Уникальные индексы без tenant_id",
    securityTenantIsolationReady: "Готово",
    securityTenantIsolationNeedsAction: "Требует исправления",
    securityTenantIsolationPending: "Ожидает включения",
    consoleBrandingTitle: "Оформление",
    consoleBrandingHint: "Загрузите свой логотип, чтобы заменить стандартный логотип в левом верхнем углу консоли.",
    consoleBrandingPreview: "Предпросмотр",
    consoleBrandingDefaultPreview: "Используется стандартный логотип Turuk MDM Control Plane.",
    consoleBrandingUploadLabel: "Файл логотипа",
    consoleBrandingUploadHint: "Поддерживаются PNG, JPG, WEBP, GIF и SVG. Максимальный размер — 2 МБ. Рекомендуемое разрешение — 360x120.",
    consoleBrandingLogoClickLabel: "Действие при нажатии кнопки логотипа",
    consoleBrandingLogoClickHint: "По умолчанию — панель мониторинга (#/dashboard). Можно указать свою ссылку: #/devices/list, /docs или https://example.com.",
    consoleBrandingLogoClickPlaceholder: "#/dashboard",
    consoleBrandingLogoClickInvalid: "Некорректная ссылка. Разрешены #/..., /..., http://..., https://...",
    consoleBrandingChooseFile: "Выберите изображение с компьютера",
    consoleBrandingSave: "Сохранить логотип",
    consoleBrandingReset: "Вернуть стандартный",
    consoleBrandingSaved: "Логотип консоли сохранен",
    consoleBrandingResetDone: "Стандартный логотип восстановлен",
    consoleBrandingFileTooLarge: "Файл логотипа слишком большой",
    consoleBrandingInvalidFile: "Нужен файл изображения",
    consoleBrandingReadFailed: "Не удалось прочитать файл логотипа",
    consoleBrandingSaveFailed: "Не удалось сохранить логотип консоли",
    consoleBrandingLogoAlt: "Логотип консоли",
    consoleMainNavOrderTitle: "Порядок главных разделов",
    consoleMainNavOrderHint: "Перетаскивайте разделы, чтобы изменить порядок главного меню только для вашей учетной записи.",
    consoleMainNavOrderSave: "Сохранить порядок",
    consoleMainNavOrderReset: "Сбросить порядок",
    consoleMainNavOrderSaved: "Порядок главного меню сохранен",
    consoleMainNavOrderSaveFailed: "Не удалось сохранить порядок главного меню",
    consoleDashboardTitle: "Панель мониторинга",
    consoleDashboardPersonalHint: "Индивидуальные настройки консоли, каждый администратор может настроить индивидуальный формат отображения информации.",
    consoleDashboardHint: "Выберите, какие блоки и диаграммы будут отображаться на главной странице панели мониторинга.",
    consoleDashboardOrderTitle: "Порядок блоков",
    consoleDashboardOrderHint: "Перетаскивайте блоки мышкой, чтобы изменить порядок их отображения на панели мониторинга.",
    consoleDashboardContentTitle: "Содержимое блока",
    consoleDashboardSave: "Сохранить настройки",
    consoleDashboardReset: "Сбросить",
    consoleDashboardSaved: "Настройки панели мониторинга сохранены",
    consoleDashboardSaveFailed: "Не удалось сохранить настройки панели мониторинга",
    agentSettingsTitle: "Turuk Companion",
    agentSettingsHint: "Настройки приложения Turuk Companion: оформление, корпоративные сервисы и обращения пользователей.",
    agentSettingsBrandingTitle: "Оформление приложения",
    agentSettingsOrganizationName: "Название организации",
    agentSettingsPrimaryColor: "Основной цвет",
    agentSettingsLogoDataURL: "Логотип (Data URL)",
    agentSettingsSupportEmail: "Email поддержки",
    agentSettingsSupportPhone: "Телефон поддержки",
    agentSettingsServicesTitle: "Корпоративные сервисы",
    agentSettingsAddService: "Добавить сервис",
    agentSettingsEnabled: "Включено",
    agentSettingsServiceTitle: "Название сервиса",
    agentSettingsIcon: "Иконка",
    agentSettingsCategory: "Категория",
    agentSettingsNoServices: "Сервисы не настроены",
    agentSettingsSupportTicketsTitle: "Обращения в поддержку",
    supportBellLabel: "Запросы поддержки",
    supportBellUnread: "Новые запросы поддержки: {count}",
    agentSettingsSubject: "Тема",
    agentSettingsCreatedAt: "Создано",
    agentSettingsNoTickets: "Обращений пока нет",
    agentSettingsTicketOpen: "Открыть",
    agentSettingsTicketDetails: "Карточка обращения",
    agentSettingsTicketStatusOpen: "Открыто",
    agentSettingsTicketStatusInProgress: "В работе",
    agentSettingsTicketStatusClosed: "Закрыто",
    agentSettingsTicketReply: "Ответ администратора",
    agentSettingsTicketSendReply: "Сохранить и отправить ответ",
    agentSettingsTicketHistory: "История",
    agentSettingsTicketLogs: "Логи приложения",
    agentSettingsTicketDeviceInfo: "Данные устройства",
    agentSettingsTicketOriginalMessage: "Сообщение пользователя",
    agentSettingsTicketSaved: "Обращение обновлено",
    agentSettingsTicketSaveFailed: "Не удалось обновить обращение",
    agentSettingsTicketDevice: "Устройство",
    supportCategoryGeneral: "Общее",
    supportCategoryApps: "Приложения",
    supportCategoryProfiles: "Профили",
    supportCategoryDevice: "Устройство",
    supportCategoryAccess: "Доступ",
    supportCategoryOther: "Другое",
    supportEventCreated: "Создано",
    supportEventReply: "Ответ",
    supportEventStatusChanged: "Статус изменен",
    agentSettingsSaved: "Настройки Turuk Companion сохранены",
    agentSettingsSaveFailed: "Не удалось сохранить настройки Turuk Companion",
    agentSettingsServiceTitleRequired: "Укажите название сервиса",
    consoleDashboardSectionTotalDeployment: "Общее количество устройств",
    consoleDashboardSectionTotalDeploymentHint: "Верхняя итоговая плашка с основными счетчиками",
    consoleDashboardSectionSecurity: "Безопасность",
    consoleDashboardSectionSecurityHint: "Кольцевые диаграммы шифрования, passcode и supervised",
    consoleDashboardSectionSupportTickets: "Обращения в поддержку",
    consoleDashboardSectionSupportTicketsHint: "Счетчики открытых обращений из Turuk Companion",
    consoleDashboardSectionOwnership: "Владение",
    consoleDashboardSectionOwnershipHint: "Распределение устройств по типу владения",
    consoleDashboardSectionLastSeenOverview: "Обзор последней активности",
    consoleDashboardSectionLastSeenOverviewHint: "Карточка устройств, активных за последние 7 дней",
    consoleDashboardSectionLastSeenBreakdown: "Распределение по последней активности",
    consoleDashboardSectionLastSeenBreakdownHint: "Горизонтальные диаграммы по интервалам активности",
    consoleDashboardSectionPlatforms: "Платформы",
    consoleDashboardSectionPlatformsHint: "Вертикальная диаграмма распределения устройств по платформам",
    consoleDashboardSummaryAccounts: "Показывать счетчик аккаунтов",
    consoleDashboardSummaryProfiles: "Показывать счетчик профилей",
    consoleDashboardSummaryApps: "Показывать счетчик приложений",
    consoleDashboardSummaryGroups: "Показывать счетчик групп",
    consoleDashboardSecurityEncrypted: "Показывать шифрование",
    consoleDashboardSecurityPasscode: "Показывать passcode",
    consoleDashboardSecuritySupervised: "Показывать supervised",
    consoleDashboardOwnershipCorporate: "Показывать корпоративные",
    consoleDashboardOwnershipPersonal: "Показывать личные",
    consoleDashboardOwnershipUnassigned: "Показывать без аккаунта",
    consoleDashboardLastSeenBucket0to1: "Показывать 0-1 дней",
    consoleDashboardLastSeenBucket2to7: "Показывать 2-7 дней",
    consoleDashboardLastSeenBucket8to15: "Показывать 8-15 дней",
    consoleDashboardLastSeenBucket16to30: "Показывать 16-30 дней",
    consoleDashboardLastSeenBucket31Plus: "Показывать 31+ дней",
    consoleDashboardPlatformIOS: "Показывать iOS/iPadOS",
    consoleDashboardPlatformMacOS: "Показывать macOS",
    consoleDashboardPlatformWindows: "Показывать Windows",
    consoleDashboardPlatformAndroid: "Показывать Android",
    consoleUsersTitle: "Пользователи консоли",
    consoleUsersHint: "Создание пользователей с ролями read-only/admin/master admin.",
    consoleUsersAdd: "Добавить",
    consoleUsersFirstName: "First Name",
    consoleUsersLastName: "Last Name",
    consoleUsersEmail: "Email",
    consoleUsersLogin: "Login",
    consoleUsersTempPassword: "Temporary Password",
    consoleUsersRole: "Role",
    consoleUsersCreate: "Создать",
    consoleUsersCancel: "Отмена",
    consoleUsersRevoke: "Отключить",
    consoleUsersEdit: "Изменить",
    consoleUsersEditModalTitle: "Изменение пользователя",
    consoleUsersUpdate: "Сохранить",
    consoleUsersUpdateFailed: "Не удалось обновить пользователя",
    consoleUsersNoItems: "Пользователи консоли не созданы",
    consoleUsersCreateFailed: "Не удалось создать пользователя",
    consoleUsersRevokeFailed: "Не удалось отключить пользователя",
    consoleUsersMustChange: "Смена пароля",
    consoleUsersMFA: "MFA / Passkey",
    mfaEnabledRequired: "Включена / обязательна",
    mfaRequiredNotConfigured: "Обязательна / не настроена",
    consoleUsersName: "Имя",
    tenantsTitle: "Организации",
    tenantsHint: "Каталог организаций SaaS. При создании организации сразу создается первый администратор для входа на ее поддомен.",
    tenantsCreateTitle: "Новая организация",
    tenantsEditTitle: "Изменение организации",
    tenantsName: "Название",
    tenantsSlug: "Поддомен",
    tenantsPrimaryDomain: "Основной домен",
    tenantsPortalURL: "URL портала",
    tenantsDomainStatus: "Проверка домена",
    tenantsDomainCheck: "Проверить",
    tenantsDomainChecking: "Проверка...",
    tenantsDomainReady: "Готов",
    tenantsDomainNotReady: "Не готов",
    tenantsDomainAllowed: "Разрешен MDM",
    tenantsDomainDNSResolved: "DNS найден",
    tenantsDomainCheckFailed: "Не удалось проверить домен",
    tenantsReadiness: "Готовность",
    tenantsReadinessReady: "Готова",
    tenantsReadinessNotReady: "Не готова",
    tenantsReadinessStatus: "Активный статус",
    tenantsReadinessBilling: "Биллинг активен",
    tenantsReadinessDomain: "Домен",
    tenantsReadinessAdmin: "Первый администратор",
    tenantsReadinessMDMAPNS: "APNs MDM",
    tenantsReadinessEnrollmentProfile: "Профиль регистрации",
    tenantsReadinessFirstDevice: "Первое устройство",
    tenantsReadinessUnknown: "Проверка",
    tenantsReadinessReasonStatus: "Организация должна быть активна, не приостановлена и не удалена",
    tenantsReadinessReasonBilling: "Биллинг не должен быть просрочен или отменен",
    tenantsReadinessReasonDomain: "Укажите основной домен организации",
    tenantsReadinessReasonAdmin: "Создайте первого активного администратора",
    tenantsReadinessReasonMDMAPNS: "Загрузите действующий APNs MDM сертификат",
    tenantsReadinessReasonEnrollmentProfile: "Настройте профиль регистрации или токен регистрации",
    tenantsReadinessReasonFirstDevice: "Зарегистрируйте первое устройство",
    tenantsUsage: "Использование",
    tenantsUsageDevices: "Устройства",
    tenantsUsageAccounts: "Аккаунты",
    tenantsUsageAdmins: "Администраторы",
    tenantsUsageUnknown: "Лимит",
    tenantsUsageUnlimited: "без лимита",
    tenantsUsageOverLimit: "Превышен лимит",
    tenantsBilling: "Биллинг",
    tenantsBillingStatus: "Статус биллинга",
    tenantsBillingEmail: "Email для счетов",
    tenantsTrialEndsAt: "Окончание пробного периода",
    tenantsSubscriptionEndsAt: "Окончание подписки",
    tenantsStripeCustomerID: "Stripe Customer ID",
    tenantsStripeSubscriptionID: "Stripe Subscription ID",
    tenantsBillingStatusInternal: "Внутренний",
    tenantsBillingStatusTrialing: "Пробный период",
    tenantsBillingStatusManual: "Ручная оплата",
    tenantsBillingStatusActive: "Активна",
    tenantsBillingStatusPastDue: "Просрочена",
    tenantsBillingStatusCanceled: "Отменена",
    tenantsManualLimits: "Ручные лимиты",
    tenantsManualLimitsHint: "Пустое поле — лимит тарифа. 0 — без лимита.",
    tenantsDeviceLimit: "Лимит устройств",
    tenantsAccountLimit: "Лимит аккаунтов",
    tenantsAdminLimit: "Лимит администраторов",
    tenantsLimitInvalid: "Лимиты должны быть пустыми или целыми числами 0 и больше",
    tenantsAccountsCount: "Аккаунты",
    tenantsDevicesCount: "MDM устройства",
    tenantsAdminsCount: "Администраторы",
    tenantsInitialAdminTitle: "Первый администратор",
    tenantsInitialAdminHint: "Этот пользователь получит роль Master Admin, обязательный MFA и временный пароль для первого входа.",
    tenantsAdminFirstName: "Имя",
    tenantsAdminLastName: "Фамилия",
    tenantsAdminEmail: "Email администратора",
    tenantsAdminLogin: "Логин администратора",
    tenantsAdminTemporaryPassword: "Временный пароль",
    tenantsStatus: "Статус",
    tenantsPlan: "Тариф",
    tenantsStatusActive: "Активна",
    tenantsStatusTrial: "Пробный период",
    tenantsStatusSuspended: "Приостановлена",
    tenantsStatusDeleted: "Удалена",
    tenantsLifecycle: "Жизненный цикл",
    tenantsSuspendedAt: "Приостановлена",
    tenantsScheduledDeletionAt: "Удаление запланировано",
    tenantsDeletedAt: "Удалена",
    tenantsSuspendedReason: "Причина приостановки",
    tenantsDeletionReason: "Причина удаления",
    tenantsLifecycleSuspend: "Приостановить",
    tenantsLifecycleReactivate: "Активировать",
    tenantsLifecycleScheduleDelete: "Запланировать удаление",
    tenantsLifecycleCancelDelete: "Отменить удаление",
    tenantsLifecycleDeleteNow: "Удалить сейчас",
    tenantsLifecycleSuspendReasonPrompt: "Причина приостановки организации",
    tenantsLifecycleDeleteReasonPrompt: "Причина удаления организации",
    tenantsLifecycleScheduleDeletePrompt: "Дата удаления в формате YYYY-MM-DD или RFC3339",
    tenantsLifecycleScheduleDeleteRequired: "Укажите дату удаления",
    tenantsLifecycleDeleteNowConfirm: "Организация будет переведена в статус Удалена. Данные останутся в БД, доступ будет заблокирован. Продолжить?",
    tenantsLifecycleReactivateConfirm: "Активировать организацию и отменить блокировки жизненного цикла?",
    tenantsLifecycleCancelDeleteConfirm: "Отменить запланированное удаление организации?",
    tenantsLifecycleSaved: "Жизненный цикл организации обновлен",
    tenantsLifecycleFailed: "Не удалось обновить жизненный цикл организации",
    tenantsPlanInternal: "Внутренний",
    tenantsPlanTrial: "Пробный",
    tenantsPlanBusiness: "Business",
    tenantsPlanEnterprise: "Enterprise",
    tenantsSaved: "Организация сохранена",
    tenantsSaveFailed: "Не удалось сохранить организацию",
    tenantsConflictSlug: "Организация с таким поддоменом уже существует",
    tenantsConflictPrimaryDomain: "Организация с таким основным доменом уже существует",
    tenantsConflictGeneric: "Организация с такими параметрами уже существует",
    tenantsReservedDomain: "Этот поддомен зарезервирован системой и не может использоваться для организации",
    tenantsConfiguratorProfileDownload: "Скачать профиль регистрации",
    tenantsConfiguratorProfileDownloaded: "Профиль регистрации организации скачан",
    tenantsConfiguratorProfileDownloadFailed: "Не удалось скачать профиль регистрации организации",
    tenantsNoItems: "Организации не созданы",
    roleSettingsTitle: "Настройки ролей",
    roleSettingsHint: "Создавайте роли и настраивайте доступ к разделам, страницам и действиям (просмотр/изменение).",
    roleSettingsCreate: "Создать роль",
    roleSettingsDelete: "Удалить роль",
    roleSettingsCreateFailed: "Не удалось создать роль",
    roleSettingsUpdateFailed: "Не удалось обновить роль",
    roleSettingsDeleteFailed: "Не удалось удалить роль",
    roleSettingsNoItems: "Роли не настроены",
    roleSettingsRoleKey: "Ключ роли",
    roleSettingsDisplayName: "Название роли",
    roleSettingsType: "Тип",
    roleSettingsUsersCount: "Пользователи",
    roleSettingsPermissions: "Права",
    roleSettingsTemplates: "Шаблоны",
    roleSettingsTemplateSupport: "Read-only support operator",
    roleSettingsTemplateSupportHint: "Только просмотр устройств, аккаунтов и обращений поддержки.",
    roleSettingsTemplateHelpdesk: "Helpdesk",
    roleSettingsTemplateHelpdeskHint: "Операционные действия поддержки: аккаунты, команды устройств и Companion.",
    roleSettingsTemplateSecurity: "Security admin",
    roleSettingsTemplateSecurityHint: "Безопасность, SSO, пользователи консоли и настройки RBAC.",
    roleSettingsAccessScope: "Scope доступа",
    roleSettingsScopeHint: "Пустой scope означает доступ ко всей организации.",
    roleSettingsScopeGroups: "Группы",
    roleSettingsScopeAllGroups: "Все группы",
    roleSettingsScopePlatforms: "Платформы",
    roleSettingsScopeLocations: "Локации",
    roleSettingsScopeLocationsPlaceholder: "location-id-1, location-id-2",
    roleSettingsView: "Просмотр",
    roleSettingsEditAction: "Изменение",
    roleSettingsSystem: "Системная",
    roleSettingsCustom: "Пользовательская",
    roleSettingsSavePermissions: "Сохранить права",
    roleSettingsSelectHint: "Выберите роль в таблице, чтобы настроить права.",
    roleSettingsPermissionDashboard: "Панель мониторинга",
    roleSettingsPermissionAccounts: "Аккаунты",
    roleSettingsPermissionAccountsList: "Аккаунты · Все аккаунты",
    roleSettingsPermissionAccountsCreate: "Аккаунты · Создать аккаунт",
    roleSettingsPermissionAccountsConnect: "Аккаунты · Подключить устройства",
    roleSettingsPermissionGroups: "Группы",
    roleSettingsPermissionDevices: "Устройства",
    roleSettingsPermissionDevicesOverview: "Устройства · Обзор",
    roleSettingsPermissionDevicesList: "Устройства · Все устройства",
    roleSettingsPermissionDevicesRequest: "Устройства · Запрос устройства",
    roleSettingsPermissionDevicesMonitor: "Устройства · Монитор",
    roleSettingsPermissionDevicesPlatformIOS: "Устройства · iOS/iPadOS",
    roleSettingsPermissionDevicesPlatformMacOS: "Устройства · macOS",
    roleSettingsPermissionDevicesPlatformWindows: "Устройства · Windows",
    roleSettingsPermissionDevicesPlatformAndroid: "Устройства · Android",
    roleSettingsPermissionDevicesConfig: "Устройства · Конфигурация",
    roleSettingsPermissionDevicesCompliance: "Устройства · Соответствие требованиям",
    roleSettingsPermissionDevicesDetail: "Устройства · Карточка устройства",
    roleSettingsPermissionDevicesApps: "Устройства · Вкладка приложения",
    roleSettingsPermissionDevicesProfiles: "Устройства · Вкладка профили",
    roleSettingsPermissionDevicesCommands: "Устройства · Вкладка команды",
    roleSettingsPermissionProfiles: "Профили и Политики",
    roleSettingsPermissionProfilesAll: "Профили · Все профили",
    roleSettingsPermissionProfilesPlatformIOS: "Профили · iOS/iPadOS",
    roleSettingsPermissionProfilesPlatformMacOS: "Профили · macOS",
    roleSettingsPermissionProfilesPlatformWindows: "Профили · Windows",
    roleSettingsPermissionProfilesPlatformAndroid: "Профили · Android",
    roleSettingsPermissionPoliciesAll: "Политики · Все политики",
    roleSettingsPermissionPoliciesCompliance: "Политики · Соответствие требованиям",
    roleSettingsPermissionApps: "Приложения",
    roleSettingsPermissionAppsOverview: "Приложения · Обзор",
    roleSettingsPermissionAppsAll: "Приложения · Все приложения",
    roleSettingsPermissionAppsPlatformIOS: "Приложения · iOS/iPadOS",
    roleSettingsPermissionAppsPlatformMacOS: "Приложения · macOS",
    roleSettingsPermissionAppsPlatformWindows: "Приложения · Windows",
    roleSettingsPermissionAppsPlatformAndroid: "Приложения · Android",
    roleSettingsPermissionAppsConfig: "Приложения · Конфигурация",
    roleSettingsPermissionAppsUpload: "Приложения · Загрузить корпоративное",
    roleSettingsPermissionSettings: "Настройки",
    roleSettingsPermissionSettingsConsoleBranding: "Настройки · Настройки консоли",
    roleSettingsPermissionSettingsConsoleUsers: "Настройки · Пользователи консоли",
    roleSettingsPermissionSettingsCertificates: "Настройки · Профиль регистрации",
    roleSettingsPermissionSettingsADE: "Настройки · Apple Business Manager",
    roleSettingsPermissionSettingsVPP: "Настройки · Apple Apps and Books",
    roleSettingsPermissionSettingsDeclarative: "Настройки · Declarative Management",
    roleSettingsPermissionSettingsSecurity: "Настройки · Безопасность",
    roleSettingsPermissionSettingsNotifications: "Настройки · Уведомления",
    roleSettingsPermissionSettingsReports: "Настройки · Отчеты",
    roleSettingsPermissionSettingsSSO: "Настройки · SSO",
    roleSettingsPermissionSettingsAgent: "Настройки · Turuk Companion",
    roleSettingsPermissionSettingsTenants: "Настройки · Организации",
    roleSettingsPermissionSettingsOpenAPI: "Настройки · OpenAPI",
    roleSettingsPermissionSettingsRoles: "Настройки · Настройки ролей",
    alertSettingsTitle: "Уведомления",
    alertSettingsHint: "Настройки центра оповещений для системных событий, доставки email, in-app и webhook.",
    alertSettingsChannels: "Каналы",
    alertSettingsTriggers: "Триггеры",
    alertSettingsThresholds: "Пороги",
    alertSettingsEnabled: "Центр уведомлений включен",
    alertSettingsInApp: "In-app уведомления",
    alertSettingsEmail: "Email",
    alertSettingsWebhook: "Webhook",
    alertSettingsTriggerAPNS: "APNs истекает",
    alertSettingsTriggerADE: "ADE sync failed",
    alertSettingsTriggerVPP: "VPP sync failed",
    alertSettingsTriggerDeviceOffline: "Устройство не выходило на связь",
    alertSettingsTriggerEnrollmentSpike: "Enrollment spike",
    alertSettingsTriggerSuspiciousLogin: "Подозрительный admin login",
    alertSettingsTriggerManagementProfileRemoved: "Профиль управления удален",
    alertSettingsAPNSDays: "APNs: предупреждать за дней",
    alertSettingsDeviceOfflineDays: "Device offline после дней",
    alertSettingsEnrollmentWindow: "Enrollment spike окно, минут",
    alertSettingsEnrollmentThreshold: "Enrollment spike порог",
    alertSettingsEvaluate: "Проверить сейчас",
    alertSettingsOpenAlerts: "Открытые уведомления",
    alertSettingsSeverity: "Важность",
    alertSettingsAlert: "Уведомление",
    alertSettingsSource: "Источник",
    alertSettingsAcknowledge: "Подтвердить",
    alertSettingsNoAlerts: "Открытых уведомлений нет",
    alertSettingsSaved: "Настройки уведомлений сохранены",
    alertSettingsSaveFailed: "Не удалось сохранить настройки уведомлений",
    alertSettingsEvaluateDone: "Проверка завершена",
    alertSettingsEvaluateFailed: "Не удалось выполнить проверку",
    alertSettingsAckDone: "Уведомление подтверждено",
    alertSettingsAckFailed: "Не удалось подтвердить уведомление",
    supportSessionsLoadFailed: "Не удалось загрузить support sessions",
    supportSessionsRequested: "Запрос support-доступа создан",
    supportSessionsRequestFailed: "Не удалось создать запрос support-доступа",
    supportSessionsApproved: "Support-доступ подтвержден",
    supportSessionsAccessed: "Support-доступ отмечен",
    supportSessionsRevoked: "Support-доступ отозван",
    supportSessionsActionFailed: "Не удалось изменить support session",
    supportSessionsStatusPending: "ожидает",
    supportSessionsStatusApproved: "подтверждено",
    supportSessionsStatusRevoked: "отозвано",
    supportSessionsStatusExpired: "истекло",
    supportSessionsClientBannerTitle: "Turuk support accessed your tenant",
    supportSessionsClientBanner: "Причина: {reason}. Доступ активен до {expires}.",
    reportsPDF: "PDF",
    reportsCSV: "CSV",
    reportsExportDone: "Отчет скачан",
    reportsExportFailed: "Не удалось скачать отчет",
    reportsSettingsTitle: "Отчеты",
    reportsSettingsHint: "Экспорт PDF/CSV и расписание email-отчетов для руководителей, устройств, аккаунтов, групп и приложений.",
    reportsScheduleTitle: "Расписание",
    reportsScheduleEnabled: "Отправлять отчеты по email",
    reportsFrequency: "Частота",
    reportsFrequencyDaily: "Ежедневно",
    reportsFrequencyWeekly: "Еженедельно",
    reportsFrequencyMonthly: "Ежемесячно",
    reportsContentTitle: "Содержимое",
    reportsReportType: "Тип отчета",
    reportsFormat: "Формат",
    reportsDeliveryTitle: "Доставка",
    reportsRecipients: "Получатели",
    reportsRecipientsHint: "Если список пустой, отчет будет отправлен активным администраторам консоли.",
    reportsScheduleSaved: "Расписание отчетов сохранено",
    reportsScheduleSaveFailed: "Не удалось сохранить расписание отчетов",
    reportsExecutive: "Executive summary",
    reportsDevices: "Устройства",
    reportsAccounts: "Аккаунты",
    reportsGroups: "Группы",
    reportsApps: "Приложения",
    forcePasswordTitle: "Смена временного пароля",
    forcePasswordHint: "Перед продолжением нужно задать постоянный пароль.",
    currentPassword: "Текущий пароль",
    newPassword: "Новый пароль",
    confirmPassword: "Повторите пароль",
    passwordsDoNotMatch: "Пароли не совпадают",
    changePassword: "Сменить пароль",
    passwordChanged: "Пароль изменен",
    changePasswordFailed: "Не удалось сменить пароль",
    active: "active",
    enabled: "включено",
    disabled: "выключено",
    revoked: "revoked",
    notAvailable: "пока недоступно",
    unknown: "unknown",
    yes: "да",
    no: "нет",
    loginFailed: "Ошибка авторизации",
    authRequired: "Нужно указать логин и пароль",
    sessionExpired: "Сессия истекла. Войдите снова.",
    requiredField: "обязательное поле",
    all: "Все",
    addFilters: "Добавить фильтры",
    loading: "Загрузка...",
    edit: "Изменить",
    delete: "Удалить",
    description: "Описание",
    tableState: "Состояние",
    commandsType: "Тип команды",
    accountsConnectDevices: "Подключить устройства",
    accountsNoDevices: "Устройства не найдены",
    accountsMobileDeviceID: "ID устройства",
    accountsMobileUnbind: "Отвязать",
    accountsMobileNoBindings: "Мобильные привязки не найдены",
    devicesSectionAll: "Все устройства",
    devicesGroupPlatforms: "По платформам",
    devicesDeleteSelected: "Удалить выбранные",
    noDevicesHint: "Нет устройств, соответствующих условиям фильтра",
    noCommands: "Команды не найдены",
    deviceDetailsRequestRefresh: "Запрос",
    deviceDetailsReinstallApps: "Переустановить выбранные приложения",
    deviceDetailsRemoveApps: "Удалить выбранные приложения",
    profilesAll: "Все профили",
    profilesSearch: "Поиск профилей",
    profilesCount: "Профилей",
    policiesCompliance: "Политики соответствия требованиям",
    policiesSearch: "Поиск политик",
    policiesCount: "Политик",
    appsAll: "Все приложения",
    appsConfig: "Конфигурация",
    appsUpload: "Загрузить корпоративное приложение",
    appsCount: "Приложений",
    appsNoItems: "Приложения не найдены",
    groupsCreate: "Создать группу",
    modalTitleAddUser: "Новый пользователь консоли",
  },
  en: {
    appTitle: "Turuk MDM Portal",
    loginTitle: "Portal sign in",
    loginSubtitle: "Authenticate with login and password",
    loginTenantSubtitle: "Organization console sign in",
    login: "Login",
    password: "Password",
    signIn: "Sign in",
    ssoSignIn: "Sign in with SSO",
    ssoLocalLoginBlocked: "This organization enforces SSO. Use SSO login or a break-glass administrator.",
    ssoSettingsTitle: "Sign in with Microsoft, Google or Okta",
    ssoSettingsHint: "Configure administrator login through an external identity provider for this organization.",
    ssoStatusEnabled: "Enabled",
    ssoStatusDisabled: "Disabled",
    ssoProviderPreset: "Sign-in provider",
    ssoEnabled: "Enable SSO",
    ssoEnforce: "Enforce SSO",
    ssoAutoProvision: "Auto-provision administrators",
    ssoAllowedDomains: "Allowed email domains",
    ssoDefaultRole: "Default role",
    ssoTenantIssuerURL: "Tenant / Issuer URL",
    ssoOIDCClientID: "OIDC Client ID",
    ssoOIDCClientSecret: "OIDC Client Secret",
    ssoSecretConfiguredPlaceholder: "Secret is already saved; leave empty to keep it",
    ssoSecretEmptyPlaceholder: "Enter client secret",
    ssoOIDCScopes: "OIDC scopes",
    ssoOIDCAuthURL: "OIDC Authorization URL",
    ssoOIDCTokenURL: "OIDC Token URL",
    ssoOIDCUserinfoURL: "OIDC UserInfo URL",
    ssoOptionalDiscoveryPlaceholder: "Optional if discovery is available",
    ssoSAMLEntityID: "SAML Entity ID",
    ssoSAMLSSOURL: "SAML SSO URL",
    ssoSAMLX509Cert: "SAML X.509 certificate",
    ssoAdvancedSettings: "Advanced settings",
    ssoTest: "Test SSO",
    ssoEnforceWarning: "Before enforcing SSO, test provider sign-in. A break-glass administrator remains available for emergency local login.",
    ssoEnforceConfirm: "Before enforcing SSO, make sure provider sign-in works. A break-glass administrator remains available. Continue?",
    ssoSave: "Save SSO",
    ssoSaveDone: "SSO settings saved",
    ssoSaveFailed: "Failed to save SSO",
    ssoBreakGlassHint: "A break-glass local admin keeps local login even when SSO is enforced. By default this is master_admin.",
    ssoRedirectURIHint: "IdP redirect URI",
    reset: "Reset",
    signingIn: "Validating...",
    signOut: "Sign out",
    mfaCode: "MFA code",
    mfaCodeRequired: "Enter MFA code",
    mfaSetupTitle: "MFA setup",
    mfaSetupRequiredHint: "MFA is required for this account. Add the secret to an authenticator app and verify the code.",
    mfaStartSetup: "Create MFA secret",
    mfaSecret: "MFA secret",
    mfaSetupScanHint: "Scan the QR code in Authenticator/1Password/Google Authenticator or add the secret manually, then enter the 6-digit code.",
    mfaSetupSecretReady: "MFA secret created",
    mfaSetupFailed: "Failed to start MFA setup",
    mfaEnable: "Enable MFA",
    mfaEnabled: "MFA enabled",
    mfaEnableFailed: "Failed to enable MFA",
    passkeyRequired: "Confirm sign-in with Passkey",
    passkeyUnsupported: "This browser does not support Passkey/WebAuthn.",
    passkeyLoginFailed: "Failed to verify Passkey",
    passkeySetupTitle: "Passkey for your account",
    passkeySetupHint: "Passkey can be used instead of an MFA code when signing in to the console.",
    passkeyAlreadyEnabled: "Passkey is already configured for this account.",
    passkeyName: "Passkey name",
    passkeyNamePlaceholder: "MacBook Touch ID",
    passkeyRegister: "Add Passkey",
    passkeyRegistered: "Passkey added",
    passkeyRegisterFailed: "Failed to add Passkey",
    currentPasswordRequired: "Enter current password",
    reauthTitle: "Confirm action",
    reauthHint: "Enter your password again to perform this sensitive action.",
    reauthCancelled: "Confirmation cancelled",
    reauthPasswordRequired: "Enter password",
    confirm: "Confirm",
    refresh: "Refresh",
    navDashboard: "Dashboard",
    navDevices: "Devices",
    navProfiles: "Profiles & Policies",
    navAccounts: "Accounts",
    navAccountsGroups: "Groups",
    navNotifications: "Turuk Companion",
    navDocuments: "Documents",
    navActionLogs: "Action logs",
    navApps: "Apps",
    navSettings: "Settings",
    turukAgentTitle: "Turuk Companion",
    turukAgentNotificationsGroup: "Notifications",
    turukAgentNotificationsSent: "Sent",
    turukAgentSupportTickets: "Support requests",
    actionLogsTitle: "Action logs",
    actionLogsSubtitle: "Administrative and high-risk action log: MDM commands, APNs updates, enrollment profile downloads, and organization changes.",
    actionLogsRefresh: "Refresh logs",
    actionLogsRefreshDone: "Action logs refreshed.",
    actionLogsExportCSV: "Export CSV",
    actionLogsExportJSON: "Export JSON",
    actionLogsExportDone: "Audit logs export downloaded.",
    actionLogsExportFailed: "Failed to export audit logs",
    actionLogsFilterActor: "Admin/API key",
    actionLogsFilterDevice: "Device UDID",
    actionLogsFilterAction: "Action",
    actionLogsFilterFrom: "From date",
    actionLogsFilterTo: "To date",
    actionLogsApplyFilters: "Show",
    actionLogsClearFilters: "Clear filters",
    actionLogsColTime: "Time",
    actionLogsColActor: "Admin",
    actionLogsColAction: "Action",
    actionLogsColResult: "Result",
    actionLogsColIP: "IP",
    actionLogsColPath: "Endpoint",
    actionLogsColDetails: "Details",
    actionLogsColIntegrity: "Integrity",
    actionLogsSuccess: "Success",
    actionLogsFailed: "Failed",
    actionLogsSessionExpired: "Session expired",
    actionLogsNoItems: "No logs yet.",
    actionLogsLoadFailed: "Failed to load action logs",
    auditActionAuthMe: "Session check",
    auditActionAccountsManage: "Account creation",
    auditActionAccountsUpdate: "Account update",
    auditActionAccountsStatus: "Account status change",
    auditActionAccountsDelete: "Account deletion",
    auditActionAccountsDeviceAssign: "Assign device to account",
    auditActionAccountsDeviceUnassign: "Unassign device from account",
    auditActionAccountsMobileUnbind: "Unbind Companion device",
    auditActionDevicesDelete: "Device deletion",
    auditActionAppleCommand: "Apple MDM command",
    auditActionDeviceInformation: "Device information request",
    auditActionSecurityInfo: "Security info request",
    auditActionInstalledApps: "Installed apps request",
    auditActionProfileList: "Profile list request",
    auditActionDeviceLocation: "Device location request",
    auditActionClearPasscode: "Clear passcode",
    auditActionDeviceLock: "Lock device",
    auditActionLostModeSound: "Play Lost Mode sound",
    auditActionLostModeEnable: "Turn on Lost Mode",
    auditActionLostModeDisable: "Turn off Lost Mode",
    auditActionRestartDevice: "Restart device",
    auditActionShutdownDevice: "Shut down device",
    auditActionScheduleOSUpdate: "Schedule OS update",
    auditActionAvailableOSUpdates: "Check available OS updates",
    auditActionOSUpdateStatus: "Request OS update status",
    auditActionRemoveProfile: "Remove profile",
    auditActionRemoveApplication: "Remove application",
    auditActionReinstallApplication: "Reinstall application",
    auditActionAllowActivationLock: "Allow Activation Lock",
    auditActionAPNSCSR: "Create MDM APNs CSR",
    auditActionAPNSConfig: "Update MDM APNs",
    auditActionEnrollmentDownload: "Enrollment profile download",
    auditActionTenantDataExport: "Organization data export",
    auditActionTenantCreate: "Organization creation",
    auditActionTenantUpdate: "Organization update",
    auditActionCommandRetry: "Command retry",
    auditActionCommandCancel: "Command cancellation",
    auditActionAuditLogsExport: "Audit logs export",
    auditActionSupportTicket: "Support ticket access",
    auditActionSupportSessionRequest: "Support access request",
    auditActionSupportSessionApprove: "Support access approval",
    auditActionSupportSessionRevoke: "Support access revoke",
    auditActionSupportSessionAccess: "Support tenant access",
    auditActionSSOConfig: "SSO change",
    notificationsSubtitle: "Company news, required actions, and compliance alerts for Turuk Companion.",
    notificationsCreate: "Create notification",
    notificationsCategory: "Type",
    notificationsCategoryNews: "Company news",
    notificationsCategoryRequired: "Required action",
    notificationsCategoryCompliance: "Compliance alert",
    notificationsSeverity: "Severity",
    notificationsSeverityInfo: "Information",
    notificationsSeverityWarning: "Warning",
    notificationsSeverityCritical: "Critical",
    notificationsTitleField: "Title",
    notificationsBody: "Message",
    notificationsActionLabel: "Button label",
    notificationsActionURL: "Deep link or HTTPS URL",
    notificationsTarget: "Recipients",
    notificationsTargetAll: "All accounts",
    notificationsTargetAccount: "Account",
    notificationsTargetGroup: "Group",
    notificationsExpiresAt: "Expires at",
    notificationsSend: "Publish and send push",
    notificationsSent: "Notification published",
    notificationsSendFailed: "Failed to publish notification",
    notificationsDeleted: "Notification deleted",
    notificationsDeleteConfirm: "Delete this sent notification?",
    notificationsDeleteFailed: "Failed to delete notification",
    notificationsNoItems: "No notifications yet.",
    notificationsPushStats: "Push: sent {sent} of {targeted}",
    roleSettingsPermissionNotifications: "Turuk Companion notifications",
    documentsTitle: "Documents",
    documentsSubtitle: "Upload documents and assign them to groups.",
    documentsAdd: "Add",
    documentsEdit: "Edit document",
    documentsSave: "Save",
    documentsDelete: "Delete",
    documentsDeleteConfirm: "Delete document?",
    documentsDeleteSelected: "Delete selected",
    documentsDeleteSelectedConfirm: "Delete selected documents?",
    documentsDeleteSelectedFailed: "Failed to delete documents",
    documentsSelectedCount: "Selected documents",
    documentsSearch: "Search document",
    documentsNoItems: "No documents yet.",
    documentsColTitle: "Title",
    documentsColCreatedBy: "Added by",
    documentsColGroups: "Groups",
    documentsColStatus: "Status",
    documentsColUpdatedAt: "Last updated",
    documentsURL: "URL",
    documentsFile: "File",
    documentsFileName: "File name",
    documentsGroups: "Assign to groups",
    documentsActive: "Active",
    documentsStatusActive: "Active",
    documentsStatusInactive: "Disabled",
    documentsFileTooLarge: "File is too large. Maximum is 10 MB.",
    documentsFileTypeNotAllowed: "File type is not allowed",
    documentsAllowedFileTypes: "Allowed: {types}. Maximum size: 10 MB.",
    documentsReadFailed: "Failed to read file",
    documentsSaved: "Document saved",
    documentsSaveFailed: "Failed to save document",
    documentsDeleted: "Document deleted",
    roleSettingsPermissionDocuments: "Documents",
    complianceCenterTitle: "Compliance Center",
    complianceCenterSubtitle: "Group rules, current violations, and required actions.",
    complianceCompliant: "Compliant",
    complianceActionRequired: "Action required",
    complianceCritical: "Critical violations",
    complianceResolved: "Resolved",
    complianceEvaluate: "Evaluate all devices",
    complianceEvaluationDone: "Accounts evaluated: {count}. Notifications created: {notifications}",
    complianceEvaluationFailed: "Compliance evaluation failed",
    complianceRuleCreate: "New rule",
    complianceRuleEdit: "Edit rule",
    complianceRuleHint: "The rule applies to all active accounts and devices in the selected group.",
      complianceRuleName: "Rule name",
      complianceRuleGroup: "Group",
      complianceRuleGroups: "Groups",
      complianceRuleGroupsPlaceholder: "Select groups",
      complianceRuleGroupsRequired: "Select at least one group",
      complianceRuleType: "Requirement",
    complianceRulePlatform: "Platform",
    complianceRuleProfile: "Required profile",
    complianceRuleApp: "Required app",
    complianceRuleMinimumOSVersion: "Minimum OS version",
    complianceRuleSeverity: "Severity",
    complianceRuleDeadlineHours: "Deadline, hours",
    complianceRuleActive: "Rule is active",
    complianceNotifyCompanion: "Notify the user in Companion",
    complianceNotifyEmail: "Send email notification",
    complianceNotificationEmail: "Notification email",
	    complianceRuleNotifications: "Notifications",
	    complianceNotifyCompanionShort: "Companion",
	    complianceNotifyEmailShort: "Email",
	    complianceNotifyNoneShort: "Monitoring only",
	    complianceRuleAutomation: "Automation",
	    complianceAutoRemediationProfile: "Automatically install the missing profile",
	    complianceAutoRemediationApp: "Force-install the missing app",
	    complianceAutoRemediationOS: "Force-install the OS update",
	    complianceAutoRemediationShort: "Auto-remediation",
	    complianceMonitoringOnlyShort: "Audit only",
	    complianceRetryHours: "Retry auto-remediation, hours",
	    complianceEscalationHours: "Escalate after SLA, hours",
	    complianceEscalationEmail: "Escalation email",
	    complianceRuleTarget: "Requirement target",
    complianceRuleDeadline: "Deadline",
    complianceRuleProfileRequired: "Profile must be installed",
    complianceRuleAppRequired: "App must be installed",
    complianceRulePasscodeRequired: "Passcode is required",
	    complianceRuleMinimumOS: "Minimum OS version",
	    complianceRuleAttestationRequired: "Managed Device Attestation is required",
	    complianceRuleAllowedSIM: "Allowed SIM card",
	    complianceRuleCustomValue: "Custom MDM value",
	    complianceRuleTemplate: "Create from template",
	    complianceRuleTemplatePlaceholder: "Select template",
	    complianceRuleTemplateCustom: "Custom rule",
    complianceRuleTemplateMinimumOS: "Minimum iOS version",
    complianceRuleTemplatePasscode: "Passcode required",
    complianceRuleTemplateProfile: "Profile required",
    complianceRuleTemplateApp: "App required",
    complianceRuleTemplateAttestation: "Managed Device Attestation",
	    complianceRuleTemplateSIM: "SIM card check",
	    complianceRuleCustomField: "MDM field",
	    complianceRuleCustomFieldHint: "Choose a suggestion or enter a custom MDM value path. Device fields, security.*, and network.* are supported.",
	    complianceRuleCustomOperator: "Operator",
	    complianceRuleCustomValueInput: "Expected value",
	    complianceRuleCustomOperatorEquals: "equals",
	    complianceRuleCustomOperatorNotEquals: "does not equal",
	    complianceRuleCustomOperatorContains: "contains",
	    complianceRuleCustomOperatorNotContains: "does not contain",
	    complianceRuleCustomOperatorStartsWith: "starts with",
	    complianceRuleCustomOperatorEndsWith: "ends with",
	    complianceRuleCustomOperatorGreaterThan: "greater than",
	    complianceRuleCustomOperatorGreaterOrEqual: "greater or equal",
	    complianceRuleCustomOperatorLessThan: "less than",
	    complianceRuleCustomOperatorLessOrEqual: "less or equal",
	    complianceRuleCustomOperatorExists: "exists",
	    complianceRuleCustomOperatorNotExists: "does not exist",
	    complianceRuleSaved: "Compliance rule saved",
    complianceRuleSaveFailed: "Failed to save compliance rule",
    complianceRuleDeleteConfirm: "Delete this compliance rule?",
    complianceRuleDeleted: "Compliance rule deleted",
    complianceRuleDeleteFailed: "Failed to delete compliance rule",
    complianceRulesTitle: "Compliance rules",
    complianceIssuesTitle: "Violations and required actions",
    complianceEvidenceTitle: "Violation evidence",
    complianceEvidenceWhatHappened: "What happened",
    complianceEvidenceNextStep: "Next step",
    complianceEvidenceShowMoreTimeline: "Show full timeline",
    complianceEvidenceShowLessTimeline: "Collapse timeline",
    complianceEvidenceRule: "Rule",
	    complianceEvidenceRemediationHistory: "Remediation history",
    complianceEvidenceNoRemediationHistory: "No remediation attempts yet.",
    complianceEvidenceDeploymentType: "Type",
    complianceEvidenceDeploymentTarget: "Target",
    complianceEvidenceDeploymentState: "State",
    complianceEvidenceDeploymentResult: "Result",
    complianceEvidenceDeploymentQueuedAt: "Queued at",
    complianceEvidenceDeploymentUpdatedAt: "Updated at",
    complianceEvidenceTimeline: "Timeline",
    complianceEvidenceAdmin: "Admin evidence",
	    complianceEvidenceOpen: "Open details",
    complianceEvidenceClose: "Close details",
    complianceEvidenceNoData: "No evidence has been collected yet.",
    complianceEvidenceDownloadReport: "Download report",
    complianceEvidenceReportDownloaded: "Compliance evidence report downloaded",
    complianceEvidenceReportFailed: "Failed to download compliance evidence report",
    complianceIssueStartRemediation: "Start remediation",
    complianceIssueInstallProfile: "Install profile",
    complianceIssueInstallApp: "Install app",
    complianceIssueRequestAttestation: "Request attestation",
    complianceIssueSendOSUpdateInstruction: "Send iOS update instruction",
    complianceIssueSendPasscodeInstruction: "Send passcode instruction",
    complianceIssueSendSIMInstruction: "Send SIM check instruction",
    complianceIssueSendReminder: "Send reminder",
    complianceIssueReevaluate: "Re-evaluate",
    complianceIssueResolve: "Resolve manually",
    complianceIssueResolveReason: "Manual resolution reason",
    complianceIssueWaive: "Waive",
    complianceIssueWaiveReason: "Waiver reason",
    complianceIssueActionDone: "Action completed",
    complianceIssueActionFailed: "Failed to complete action",
    complianceIssueFilters: "Issue filters",
    complianceIssueFilterStatus: "Status",
    complianceIssueFilterRule: "Rule",
    complianceIssueFilterDevice: "Device",
    complianceIssueFilterSearch: "Search",
    complianceIssueFilterAllStatuses: "All statuses",
    complianceIssueFilterOpen: "Open",
    complianceIssueFilterCritical: "Critical",
    complianceIssueFilterOverdue: "Overdue",
    complianceIssueFilterResolved: "Resolved",
    complianceIssueFilterWaived: "Waived",
    complianceIssueFilterAllRules: "All rules",
    complianceIssueFilterAllDevices: "All devices",
    complianceIssueFilterSearchPlaceholder: "Account, device, reason",
    complianceIssueFilterReset: "Reset filters",
    complianceRemediationStatus: "Remediation status",
    complianceRemediationStatusWaitingAction: "Waiting for action",
    complianceRemediationStatusInstructionSent: "Instruction sent",
    complianceRemediationStatusWaitingSync: "Waiting for sync",
    complianceRemediationStatusConfirmed: "User confirmed",
    complianceRemediationStatusResolved: "Resolved",
    complianceRemediationStatusWaived: "Waived",
    complianceDeviceInstruction: "Device instruction",
    complianceInstructionProfile: "Open Turuk Companion and install the assigned profile. After installation, the device will send an updated profile list.",
    complianceInstructionApp: "Open Turuk Companion and install the assigned app. After installation, the device will send an updated app list.",
    complianceInstructionMinimumOS: "On the device, open Settings > General > Software Update, install the available iOS update, then return to Turuk Companion for a re-check.",
    complianceInstructionPasscode: "On the device, open Settings > Face ID/Touch ID & Passcode, enable a passcode, then return to Turuk Companion for a re-check.",
    complianceInstructionAttestation: "The admin will request Managed Device Attestation. Keep the device online until it responds to the MDM command.",
    complianceInstructionSIM: "Check the SIM/eSIM on the device. If it was replaced by mistake, return the allowed SIM and sync again.",
    complianceIssueToggleFilters: "Filters",
    complianceIssueExportExcel: "Download Excel",
    complianceIssueFilteredCount: "Showing: {count} of {total}",
    complianceIssueExportDone: "Compliance report downloaded",
    complianceIssueExportFailed: "Failed to download compliance report",
    complianceIssueDetectedAt: "Detected",
    complianceIssueLastAction: "Last action",
    complianceIssueLastActionStarted: "Remediation started",
    complianceIssueLastActionConfirmed: "User confirmed",
    complianceIssueLastActionResolved: "Resolved",
    complianceIssueLastActionWaived: "Waived",
    complianceIssueLastActionDetected: "Last check",
    complianceIssueNextStepAlreadyStarted: "Remediation has already started. Wait for device sync or run re-evaluation.",
    complianceIssueNextStepStartRemediation: "Start remediation from this card.",
    complianceIssueNextStepReviewDevice: "Review the device state and run re-evaluation.",
    complianceIssueShowMore: "Show more",
    complianceIssue: "Violation",
    complianceReason: "Exact reason",
    complianceDueAt: "Due at",
    complianceEvent: "Event",
    complianceEventAt: "Time",
    complianceEventDetected: "Violation detected",
    complianceEventReopened: "Violation detected again",
    complianceEventActionStarted: "Remediation started",
    complianceEventConfirmed: "User confirmed completion",
    complianceEventResolved: "Violation resolved",
    complianceEventWaived: "Exception approved",
    complianceNoRules: "No compliance rules have been created.",
    complianceNoIssues: "No violations detected.",
    complianceNoHistory: "No compliance history yet.",
    complianceImmediately: "Immediately",
    hoursShort: "h",
    inactive: "inactive",
    accountsTitle: "Account",
    appsColumns: "Columns",
    appsResetColumns: "Reset column selection",
    appsSearchAll: "Search",
    appsColName: "Name",
    appsColBundleID: "Bundle ID",
    appsColPlatform: "Platform",
    appsColType: "Type",
    appsColVersion: "Version",
    appsColVPPToken: "VPP token name",
    appsColAssigned: "Assigned",
    appsColGroups: "Groups",
    appsColDeveloper: "Developer",
    appsColStatus: "Status",
    appsColUpdatedAt: "Last modified",
    appsEditGroups: "Edit groups",
    appsEditGroupsModalTitle: "Edit app groups",
    appsSelectedCount: "Selected apps",
    appsDeleteSelected: "Delete selected",
    appsDeleteSelectedConfirm: "Delete selected apps?",
    appsDeleteSelectedFailed: "Failed to delete selected apps",
    appsAssignToGroups: "Update app groups",
    appsAssignGroupsFailed: "Failed to update app groups",
    appsUploadHint: "Add a corporate app hosted on your HTTPS distribution endpoint. For iOS/iPadOS, provide a manifest.plist that references the IPA.",
    appsUploadManifestRequired: "Enter a Manifest URL or App Store ID",
    appsEnterpriseManifestURL: "Manifest URL / package URL",
    appsEnterpriseManifestHint: "iOS/iPadOS: manifest.plist for enterprise IPA. macOS: pkg/dmg URL. Android/Windows: package URL is stored as the catalog source.",
    appsEnterpriseDescriptionPlaceholder: "Description, version, app owner, or admin notes",
    appsUploadCreate: "Add app",
    appsUploadClear: "Clear",
    roleReadOnly: "read-only",
    roleAdmin: "admin",
    roleMasterAdmin: "master admin",
    dashboardTotalDeployment: "Total deployment",
    dashboardSummaryAccounts: "Accounts",
    dashboardSummaryProfiles: "Profiles",
    dashboardSummaryApps: "Apps",
    dashboardSummaryGroups: "Groups",
    dashboardSectionSecurity: "Security",
    dashboardSectionSupportTickets: "Support tickets",
    dashboardSectionOwnership: "Ownership",
    dashboardSectionLastSeenOverview: "Last seen overview",
    dashboardSectionLastSeenBreakdown: "Last seen breakdown",
    dashboardSectionPlatforms: "Platforms",
    onboardingDemoSeeded: "Demo data created",
    onboardingDemoSeedFailed: "Failed to create demo data",
    dashboardNoSections: "No dashboard sections are currently enabled.",
    dashboardSecurityEncrypted: "Encrypted",
    dashboardSecurityPasscode: "Passcode compliant",
    dashboardSecuritySupervised: "Supervised",
    dashboardSupportOpen: "Open",
    dashboardSupportWaiting: "Waiting",
    dashboardOwnershipCorporate: "Corporate",
    dashboardOwnershipPersonal: "Personal",
    dashboardOwnershipUnassigned: "Unassigned",
    dashboardLastSeenRecent: "active in the last 7 days",
    dashboardLastSeenCoverage: "Share of devices with a check-in in the last 7 days",
    dashboardLastSeen0to1: "0-1 days",
    dashboardLastSeen2to7: "2-7 days",
    dashboardLastSeen8to15: "8-15 days",
    dashboardLastSeen16to30: "16-30 days",
    dashboardLastSeen31Plus: "31+ days",
    searchDevices: "Search by UDID / name / serial",
    devicesColumns: "Columns",
    devicesResetColumns: "Reset columns",
    devicesCount: "devices",
    devicesFilterLastSeen: "Last seen",
    devicesColName: "Device name",
    devicesColIMEI: "IMEI",
    devicesColWiFiMac: "Wi-Fi MAC address",
    devicesColMEID: "MEID",
    devicesColCarrier: "Carrier",
    devicesColOSVersion: "OS version",
    devicesColOwnership: "Ownership",
    devicesOwnershipPersonal: "Personal",
    devicesOwnershipCorporate: "Corporate",
    devicesColCertExpiry: "Management certificate expiration date",
    devicesColEnrollmentDate: "Enrollment date",
    devicesColEncrypted: "Encrypted",
    devicesColAccountName: "Account name",
    devicesColControlled: "Managed",
    devicesColModel: "Model",
    devicesColPhone: "Phone number",
    devicesColOS: "OS",
    devicesColTotalStorage: "Total storage",
    devicesColPrimaryEmail: "Primary user email",
    devicesColLastSync: "Last sync",
    devicesColFreeStorage: "Free storage",
    devicesColSerial: "Serial number",
    devicesColCompliance: "Compliance",
    deviceDetailsTabAbout: "About device",
    deviceDetailsBatteryLevel: "Battery level",
    deviceDetailsSupplementalBuild: "Supplemental build",
    deviceDetailsSupplementalOSExtra: "Supplemental OS extra",
    deviceDetailsActiveManagedUsers: "Active managed users",
    deviceDetailsManagedAdmins: "Managed local administrators",
    deviceDetailsDeviceID: "Device ID",
    deviceDetailsDoNotDisturb: "Do Not Disturb",
    deviceDetailsEASDeviceID: "EAS Device ID",
    deviceDetailsEID: "EID",
    deviceDetailsESIMIdentifier: "eSIM identifier",
    deviceDetailsAppleSilicon: "Apple silicon",
    deviceDetailsICloudBackup: "iCloud Backup enabled",
    deviceDetailsFindMy: "Find My enabled",
    deviceDetailsMDMLostMode: "Managed Lost Mode enabled",
    deviceDetailsMultiUser: "Shared iPad",
    deviceDetailsLastICloudBackup: "Last iCloud Backup",
    deviceDetailsMaximumResidents: "Maximum Shared iPad users",
    deviceDetailsModemFirmware: "Modem firmware version",
    deviceDetailsAuthGracePeriod: "Online authentication grace period",
    deviceDetailsQuotaSize: "Storage quota",
    deviceDetailsResidentUsers: "Shared iPad users",
    deviceDetailsSIP: "System Integrity Protection",
    deviceDetailsTimeZone: "Time Zone",
    deviceDetailsBluetoothMac: "Bluetooth MAC address",
    deviceDetailsEthernetMacs: "Ethernet MAC addresses",
    deviceDetailsConnectedWiFiSSID: "Connected Wi-Fi network",
    deviceDetailsConnectedWiFiBSSID: "Wi-Fi BSSID",
    deviceDetailsCarrierSettingsVersion: "Carrier settings version",
    deviceDetailsCurrentCarrier: "Current carrier",
    deviceDetailsCurrentMCC: "Current MCC",
    deviceDetailsCurrentMNC: "Current MNC",
    deviceDetailsDataRoaming: "Data roaming",
    deviceDetailsICCID: "ICCID",
    deviceDetailsAllowedICCID: "Allowed SIM card",
    deviceDetailsAllowedICCIDEdit: "Edit allowed ICCID",
    deviceDetailsAllowedICCIDUseCurrent: "Make current SIM allowed",
    deviceDetailsAllowedICCIDSaved: "Allowed SIM card updated",
    deviceDetailsAllowedICCIDFailed: "Failed to update allowed SIM card",
    deviceDetailsComplianceTitle: "Device compliance",
    deviceDetailsComplianceCheckNow: "Check device now",
    deviceDetailsComplianceOpenIssues: "Open issues",
    deviceDetailsComplianceNoIssues: "No open issues for this device",
    deviceDetailsComplianceRule: "Rule",
    deviceDetailsComplianceStatus: "Status",
    deviceDetailsComplianceDueAt: "Fix by",
    deviceDetailsComplianceLastCommand: "Latest verification command",
    deviceDetailsComplianceNoCommand: "No verification command has been sent yet",
    deviceDetailsSIMAuditTitle: "SIM audit",
    deviceDetailsSIMCurrentICCID: "Current ICCID",
    deviceDetailsSIMAllowedICCID: "Allowed ICCID",
    deviceDetailsSIMMatch: "Matches",
    deviceDetailsSIMObservedAt: "Last SIM check",
    deviceDetailsSIMModalTitle: "Allowed SIM card",
    deviceDetailsSIMModalSubtitle: "Allowlist editing and SIM change evidence for this device.",
    deviceDetailsSIMAuditOld: "Old value",
    deviceDetailsSIMAuditNew: "New value",
    deviceDetailsSIMAuditActor: "Changed by",
    deviceDetailsSIMAuditAt: "Changed at",
    deviceDetailsSIMAuditEmpty: "No change history yet",
    deviceDetailsSIMEvidenceTitle: "Evidence",
    deviceDetailsSIMSave: "Save SIM",
    deviceDetailsNetworkTethered: "Network tethered",
    deviceDetailsPersonalHotspot: "Personal Hotspot",
    deviceDetailsIsRoaming: "Roaming",
    deviceDetailsSIMCarrier: "SIM carrier",
    deviceDetailsSIMMCC: "SIM MCC",
    deviceDetailsSIMMNC: "SIM MNC",
    deviceDetailsSubscriberCarrier: "Subscriber carrier",
    deviceDetailsSubscriberMCC: "Subscriber MCC",
    deviceDetailsSubscriberMNC: "Subscriber MNC",
    deviceDetailsCellularDataUsage: "Cellular data usage",
    deviceDetailsWiFiDataUsage: "Wi-Fi data usage",
    deviceDetailsTabApps: "Applications",
    deviceDetailsTabProfiles: "Profiles",
    deviceDetailsTabLocation: "Location",
    deviceDetailsTabSharedIPad: "Shared iPad",
    deviceDetailsTabMacOSUEM: "macOS UEM",
    deviceDetailsTabCommands: "Requests",
    deviceDetailsTabRemoteAccess: "Remote access",
    deviceDetailsCommandsMenu: "Commands",
    deviceDetailsAppleCommandsSecurity: "Security and recovery",
    deviceDetailsAppleCommandsInventory: "Inventory",
    deviceDetailsAppleCommandsProfilesApps: "Profiles and applications",
    deviceDetailsAppleCommandsUsers: "macOS accounts",
    deviceDetailsAppleCommandsMacManagement: "macOS management",
    deviceDetailsAppleCommandsContent: "Content",
    deviceDetailsAppleCommandsAdvanced: "Advanced commands",
    deviceDetailsAppleCommandEraseDevice: "Erase device",
    deviceDetailsAppleCommandReturnToService: "Erase and return to service",
    deviceDetailsAppleCommandRestrictions: "Request device restrictions",
    deviceDetailsAppleCommandActivationLockBypassCode: "Request Activation Lock bypass code",
    deviceDetailsAppleCommandClearActivationLockBypassCode: "Clear Activation Lock bypass code",
    deviceDetailsAppleCommandRotateFileVaultKey: "Rotate FileVault key",
    deviceDetailsAppleCommandSetRecoveryLock: "Set Recovery Lock password",
    deviceDetailsAppleCommandVerifyRecoveryLock: "Verify Recovery Lock password",
    deviceDetailsAppleCommandSetFirmwarePassword: "Set firmware password",
    deviceDetailsAppleCommandVerifyFirmwarePassword: "Verify firmware password",
    deviceDetailsAppleCommandClearRestrictionsPassword: "Clear restrictions password",
    deviceDetailsAppleCommandManagedApplicationList: "Request managed applications",
    deviceDetailsAppleCommandCertificateList: "Request certificates",
    deviceDetailsAppleCommandProvisioningProfileList: "Request provisioning profiles",
    deviceDetailsAppleCommandContentCachingInformation: "Request Content Caching information",
    deviceDetailsAppleCommandInstallProvisioningProfile: "Install provisioning profile",
    deviceDetailsAppleCommandRemoveProvisioningProfile: "Remove provisioning profile",
    deviceDetailsAppleCommandValidateApplications: "Validate applications",
    deviceDetailsAppleCommandInstallEnterpriseApplication: "Install enterprise application",
    deviceDetailsAppleCommandManagedApplicationAttributes: "Request managed application attributes",
    deviceDetailsAppleCommandManagedApplicationConfiguration: "Request managed application configuration",
    deviceDetailsAppleCommandManagedApplicationFeedback: "Request managed application feedback",
    deviceDetailsAppleCommandRefreshCellularPlans: "Refresh cellular plans",
    deviceDetailsAppleCommandUserList: "Request local users",
    deviceDetailsAppleCommandUnlockUserAccount: "Unlock user account",
    deviceDetailsAppleCommandLogOutUser: "Log out user",
    deviceDetailsAppleCommandDeleteUser: "Delete user",
    deviceDetailsAppleCommandAccountConfiguration: "Configure local accounts",
    deviceDetailsAppleCommandSetAutoAdminPassword: "Set auto-admin password",
    deviceDetailsAppleCommandEnableRemoteDesktop: "Enable Remote Desktop",
    deviceDetailsAppleCommandDisableRemoteDesktop: "Disable Remote Desktop",
    deviceDetailsAppleCommandRequestMirroring: "Request screen mirroring",
    deviceDetailsAppleCommandStopMirroring: "Stop screen mirroring",
    deviceDetailsAppleCommandInstallMedia: "Install media",
    deviceDetailsAppleCommandRemoveMedia: "Remove media",
    deviceDetailsAppleCommandApplyRedemptionCode: "Apply redemption code",
    deviceDetailsAppleCommandManagedMediaList: "Request managed media",
    deviceDetailsAppleCommandActiveNSExtensionsMappings: "Request active network extension mappings",
    deviceDetailsAppleCommandNSExtensionsMappings: "Request network extension mappings",
    deviceDetailsAppleCommandUserConfigured: "Confirm user configured",
    deviceDetailsAppleCommandDeviceConfigured: "Confirm device configured",
    deviceDetailsAppleCommandInviteToProgram: "Invite to program",
    deviceDetailsAppleCommandLOMDeviceRequest: "Send Lights-Out Management request",
    deviceDetailsAppleCommandLOMSetupRequest: "Set up Lights-Out Management",
    deviceDetailsAppleCommandDeclarativeManagement: "Synchronize declarative management",
    deviceDetailsAppleCommandSettings: "Send MDM settings",
    deviceDetailsAppleCommandModalHint: "Command parameters are sent to the device as an Apple MDM dictionary. Remove optional fields or fill in values before sending.",
    deviceDetailsAppleCommandDestructiveWarning: "This command can remove data, access, or an account. Verify the parameters before sending.",
    deviceDetailsAppleCommandParameters: "Command parameters (JSON)",
    deviceDetailsAppleCommandSubmit: "Send command",
    deviceDetailsAppleCommandInvalidJSON: "Parameters must be a valid JSON object",
    deviceDetailsAppleCommandQueued: "Apple MDM command queued",
    deviceDetailsAppleCommandFailed: "Failed to queue Apple MDM command",
    deviceDetailsCommandClearPasscode: "Clear passcode",
    deviceDetailsCommandDeviceLock: "Lock screen",
    deviceDetailsCommandPlayLostModeSound: "Play Lost Mode sound",
    deviceDetailsCommandTurnOnLostMode: "Turn on Lost Mode",
    deviceDetailsCommandTurnOffLostMode: "Turn off Lost Mode",
    deviceDetailsCommandRestartDevice: "Restart device",
    deviceDetailsCommandShutDownDevice: "Shut down device",
    deviceDetailsCommandScheduleOSUpdateScan: "Schedule operating system update scan",
    deviceDetailsCommandAvailableOSUpdates: "Check available OS updates",
    deviceDetailsCommandScheduleOSUpdateDownload: "Schedule OS update download",
    deviceDetailsCommandScheduleOSUpdateInstall: "Schedule OS update install",
    deviceDetailsCommandRemoveMDMProfile: "Remove MDM management profile",
    deviceDetailsRemoveMDMProfileConfirm: "Remove the MDM management profile from this device? The device will be unenrolled from Turuk MDM.",
    deviceDetailsRemoveMDMProfileQueued: "MDM management profile removal queued",
    deviceDetailsRemoveMDMProfileFailed: "Failed to queue MDM management profile removal",
    deviceDetailsOSUpdateModalTitleDownload: "Schedule OS update download",
    deviceDetailsOSUpdateModalTitleInstall: "Schedule OS update install",
    deviceDetailsOSUpdateModalProductKey: "Product Key",
    deviceDetailsOSUpdateModalProductVersion: "OS version",
    deviceDetailsOSUpdateModalSubmitDownload: "Schedule download",
    deviceDetailsOSUpdateModalSubmitInstall: "Schedule install",
    deviceDetailsOSUpdateModalHint: "If Product Key and OS version are empty, the device downloads or installs the latest available update. Apple MDM does not provide Wi-Fi-only or exact overnight scheduling fields; network, power, and installation availability are controlled by the OS.",
    deviceDetailsOSUpdateModalTargets: "Selected devices",
    devicesBulkCommands: "Commands",
    devicesBulkOSUpdateDownload: "Download OS without installing",
    devicesBulkOSUpdateDownloadInstall: "Install a downloaded OS update",
    devicesBulkOSUpdateAppleOnly: "OS update commands are available only when all selected devices are Apple devices",
    deviceDetailsLostModeModalTitle: "Turn on Lost Mode",
    deviceDetailsLostModeModalMessage: "Short message",
    deviceDetailsLostModeModalPhone: "Phone number",
    deviceDetailsLostModeModalSubmit: "Turn on",
    deviceDetailsLocationRequest: "Request location",
    deviceDetailsLocationRequestQueued: "Location request queued",
    deviceDetailsLocationRequestFailed: "Failed to queue location request",
    deviceDetailsLocationRequiresIOS: "This command is available only for iOS and iPadOS devices",
    deviceDetailsLocationRequiresSupervised: "This command requires a supervised device",
    deviceDetailsLocationLostModeActiveHint: "Lost Mode is active. Location will be requested through Apple MDM.",
    deviceDetailsLocationLostModeRequiredHint: "Apple MDM location temporarily places the device in Lost Mode and then disables it automatically.",
    deviceDetailsLocationLostModeQueued: "Temporary Lost Mode queued; location will be requested automatically",
    deviceDetailsLocationLostModeModalTitle: "Turn on Lost Mode and request location",
    deviceDetailsLocationLostModeModalHint: "Apple allows MDM location only for a supervised device in Lost Mode. For location requests Turuk automatically turns Lost Mode off after coordinates are received.",
    deviceDetailsLocationLostModeModalSubmit: "Turn on and request",
    deviceDetailsNoApps: "No installed apps",
    deviceDetailsNoProfiles: "No console profiles found",
    deviceDetailsNoLocation: "No location points received yet",
    deviceDetailsLocationUpdatedAt: "Point time",
    deviceDetailsLocationLatitude: "Latitude",
    deviceDetailsLocationLongitude: "Longitude",
    deviceDetailsLocationAltitude: "Altitude",
    deviceDetailsLocationAccuracy: "Accuracy",
    deviceDetailsLocationSpeed: "Speed",
    deviceDetailsLocationSource: "Source",
    deviceDetailsLocationMap: "Map",
    deviceDetailsLocationOpenOSM: "Open in OpenStreetMap",
    deviceDetailsLocationOpenGoogle: "Open in Google Maps",
    deviceDetailsLocationHistory: "Location history",
    deviceDetailsSharedIPadTitle: "Shared iPad / Education",
    deviceDetailsSharedIPadHint: "Workflows for Shared iPad, Managed Apple Account, and classroom management.",
    deviceDetailsSharedIPadRefreshUsers: "Refresh users",
    deviceDetailsSharedIPadNoUsers: "Users have not been received yet. Send UserList.",
    deviceDetailsSharedIPadUserName: "User",
    deviceDetailsSharedIPadFullName: "Name",
    deviceDetailsSharedIPadLoggedIn: "Session",
    deviceDetailsSharedIPadData: "Data",
    deviceDetailsSharedIPadLogout: "Log out user",
    deviceDetailsSharedIPadDelete: "Delete user",
    deviceDetailsSharedIPadQueued: "Shared iPad command queued",
    deviceDetailsSharedIPadFailed: "Failed to queue Shared iPad command",
    deviceDetailsSharedIPadSettings: "Organization settings",
    deviceDetailsSharedIPadManagedDomain: "Managed Apple Account domain",
    deviceDetailsSharedIPadDefaultDomain: "Default domain",
    deviceDetailsSharedIPadClassroomNotes: "Classroom / Education notes",
    deviceDetailsSharedIPadSettingsSaved: "Shared iPad settings saved",
    deviceDetailsActivationLockTitle: "Activation Lock",
    deviceDetailsActivationLockHint: "Activation bypass code expiration is controlled by Apple, so use the latest code and refresh it before device recovery.",
    deviceDetailsActivationLockRefresh: "Request code",
    deviceDetailsActivationLockClear: "Clear code",
    deviceDetailsActivationLockNoCodes: "No bypass code has been received yet. Send an Activation Lock code request.",
    deviceDetailsActivationLockCode: "Bypass code",
    deviceDetailsActivationLockReportedAt: "Received",
    deviceDetailsActivationLockExpiresAt: "Expires",
    deviceDetailsActivationLockSource: "Command",
    deviceDetailsActivationLockRefreshQueued: "Bypass code request queued",
    deviceDetailsActivationLockClearQueued: "Bypass code clear queued",
    deviceDetailsActivationLockFailed: "Failed to perform Activation Lock action",
    deviceDetailsCommandStateQueued: "Queued",
    deviceDetailsCommandStateSent: "Sent to device",
    deviceDetailsCommandStateDone: "Acknowledged by device",
    deviceDetailsCommandStateDeadLetter: "Dead-letter",
    deviceDetailsCommandStateCanceled: "Canceled",
    deviceDetailsCommandStateNotNow: "Device deferred command (NotNow)",
    deviceDetailsCommandStateError: "Execution error on device",
    deviceDetailsCommandLastError: "Last error",
    deviceDetailsCommandAttempts: "Attempts",
    deviceDetailsCommandRetry: "Retry",
    deviceDetailsCommandCancel: "Cancel",
    deviceDetailsCommandRetryQueued: "Command was queued again",
    deviceDetailsCommandRetryFailed: "Failed to retry command",
    deviceDetailsCommandCancelDone: "Command canceled",
    deviceDetailsCommandCancelFailed: "Failed to cancel command",
    deviceDetailsAppInstallPending: "Pending install",
    deviceDetailsAppInstallFailed: "Install failed",
    deviceDetailsAppInstallDone: "Installed",
    deviceDetailsAppInstallReason: "Reason",
    deviceDetailsAppInstallReasonUnknown: "Device did not provide a reason",
    deviceDetailsProfileInstallPending: "Pending install",
    deviceDetailsProfileInstallFailed: "Install failed",
    deviceDetailsProfileInstallDone: "Installed",
    deviceDetailsProfileInstallReason: "Reason",
    deviceDetailsProfileInstallReasonUnknown: "Device did not provide a reason",
    deviceDetailsRequestQueued: "Device refresh request queued",
    deviceDetailsRequestFailed: "Failed to queue device refresh request",
    deviceDetailsClearPasscodeQueued: "Clear passcode command queued",
    deviceDetailsClearPasscodeFailed: "Failed to queue clear passcode command",
    deviceDetailsDeviceLockQueued: "Device lock command queued",
    deviceDetailsDeviceLockFailed: "Failed to queue device lock command",
    deviceDetailsPlayLostModeSoundQueued: "Play Lost Mode sound command queued",
    deviceDetailsPlayLostModeSoundFailed: "Failed to queue Play Lost Mode sound command",
    deviceDetailsTurnOnLostModeQueued: "Turn on Lost Mode command queued",
    deviceDetailsTurnOnLostModeFailed: "Failed to queue Turn on Lost Mode command",
    deviceDetailsTurnOffLostModeQueued: "Turn off Lost Mode command queued",
    deviceDetailsTurnOffLostModeFailed: "Failed to queue Turn off Lost Mode command",
    deviceDetailsLostModeMessageRequired: "Enter a short Lost Mode message",
    deviceDetailsLostModePhoneRequired: "Enter a Lost Mode phone number",
    deviceDetailsRestartDeviceQueued: "Restart device command queued",
    deviceDetailsRestartDeviceFailed: "Failed to queue restart device command",
    deviceDetailsShutDownDeviceQueued: "Shut down device command queued",
    deviceDetailsShutDownDeviceFailed: "Failed to queue shut down device command",
    deviceDetailsScheduleOSUpdateScanQueued: "Schedule operating system update scan command queued",
    deviceDetailsScheduleOSUpdateScanFailed: "Failed to queue schedule operating system update scan command",
    deviceDetailsAvailableOSUpdatesQueued: "Available OS updates command queued",
    deviceDetailsAvailableOSUpdatesFailed: "Failed to queue available OS updates command",
    deviceDetailsScheduleOSUpdateQueued: "Schedule OS update command queued",
    deviceDetailsScheduleOSUpdateFailed: "Failed to queue schedule OS update command",
    devicesBulkRequestQueued: "Device refresh requests queued",
    devicesBulkRequestFailed: "Failed to queue device refresh requests",
    devicesBulkRequestNoDevices: "No devices available for request",
    deviceDetailsResendProfiles: "Install",
    deviceDetailsDisableProfiles: "Remove",
    deviceDetailsNoManagedAppsSelected: "Select at least one managed app",
    deviceDetailsNoProfilesSelected: "Select at least one profile",
    deviceDetailsReinstallFailed: "Failed to reinstall apps",
    deviceDetailsRemoveFailed: "Failed to remove apps",
    deviceDetailsProfileActionFailed: "Failed to apply profile action",
    deviceDetailsReinstallDone: "App reinstall queued",
    deviceDetailsRemoveDone: "App removal queued",
    deviceDetailsProfilesResentDone: "Profiles redeployed",
    deviceDetailsProfilesDisabledDone: "Profiles disabled",
    deviceDetailsMissingCatalogApps: "Some installed apps are missing in catalog mapping. Add them to app catalog and retry.",
    noDevices: "No devices found",
    devicesDelete: "Delete",
    devicesDeleteFailed: "Failed to delete devices",
    devicesDeleteConfirm: "Delete selected devices from MDM? A RemoveProfile command will be queued.",
    adminOnlyAction: "Action is available only for admin",
    profilesDeploy: "Deploy profile",
    profilesDeployFailed: "Failed to deploy profile",
    profilesAppleOnlyBuilderHint: "Profile builder is currently available only for iOS/iPadOS and macOS. Dedicated editors for other platforms will be added later.",
    profilesEditGroups: "Edit groups",
    profilesEditGroupsModalTitle: "Edit profile groups",
    profilesSelectedCount: "Selected profiles",
    profilesAssignToGroups: "Update profile groups",
    profilesAssignGroupsFailed: "Failed to update profile groups",
    profilesGroups: "Groups",
    profilesNoItems: "No profiles found",
    profilesColumns: "Columns",
    profilesResetColumns: "Reset columns",
    profilesDeleteSelected: "Delete selected",
    profilesDeleteSelectedConfirm: "Delete selected items?",
    profilesDeleteSelectedFailed: "Failed to delete selected items",
    profilesColName: "Name",
    profilesColIdentifier: "Identifier",
    profilesColGroups: "Groups",
    profilesColDevices: "Devices",
    profilesColStatus: "Status",
    profilesColUpdatedAt: "Last changed",
    profileDevicesModalTitle: "Profile devices",
    profileDevicesFilterAll: "All devices",
    profileDevicesFilterInstalled: "Installed",
    profileDevicesFilterMissing: "Not installed",
    profileDevicesInstalled: "Installed",
    profileDevicesNotInstalled: "Not installed",
    profileDevicesEmpty: "No assigned devices yet.",
    profileDevicesExportCSV: "Download CSV",
    profileDevicesExportDone: "Profile devices report downloaded",
    profileDevicesExportFailed: "Failed to download profile devices report",
    policiesColName: "Policy name",
    policiesColPlatform: "Platform",
    policiesColType: "Policy type",
    policiesColUpdatedAt: "Last changed",
    policiesColScopeTags: "Scope tags",
    policiesColCompliance: "Compliance",
    profileBuilderNotConfigured: "Not configured",
    profileBuilderMandatory: "Mandatory",
    profileBuilderConfigured: "1 Payload Configured",
    profileBuilderConfiguredMany: "Payloads Configured",
    profileBuilderAtLeastOnePayloadRequired: "Add at least one payload (for example Wi-Fi, VPN, Passcode, Restrictions, etc.)",
    profileBuilderGeneralRemovalDateRequired: "Select a removal date",
    profileBuilderGeneralRemovalDaysRequired: "Enter the number of days before removal",
    profileSectionTitleGlobalHTTPProxySupervised: "Global HTTP Proxy (supervised only)",
    profileSectionTitleDNSProxySupervised: "DNS Proxy (iOS, supervised only)",
    profileSectionTitleContentFilterSupervised: "Content Filter (supervised only)",
    profileSectionWarningGlobalHTTPProxy: "Warning: an incorrect Global HTTP Proxy host, port, or authentication setting can break internet access and MDM connectivity for the device. Test first on a separate supervised device with an available proxy and no authentication. Before assigning broadly, verify that Safari opens http://example.com and the device still responds to MDM commands.",
    profileSectionHintRestrictions: "Use this section to restrict which apps, device functionality, and media content are available to the user.",
    profileSectionHintDomains: "Use this section to define unmarked email domains and managed web domains.",
    profileSectionHintGlobalHTTPProxy: "Use this section to configure settings for a proxy server through which all HTTP traffic from the device will pass.",
    profileSectionHintDNSProxy: "Use this section to configure a DNS proxy network extension. This payload cannot be installed by Apple Configurator and must be installed by a device management service on devices running iOS 15 or later.",
    profileSectionHintContentFilter: "Use this section to configure which URLs can be accessed by the device.",
    profileSectionHintCertificates: "Use this section to specify the PKCS1 and PKCS12 certificates to install in the profile.",
    profileSectionHintPasscode: "Use this section to specify passcode policies to be enforced on the device.",
    profileSectionHintVPN: "Use this section to configure how the device connects to your wireless network via VPN, including the necessary authentication information.",
    profileSectionHintAirPlay: "Use this section to define settings for connecting to AirPlay destinations.",
    profileSectionHintFileVault: "Use this section to enable FileVault and configure recovery key escrow.",
    profileSectionHintPPPC: "Use this section to manage Privacy Preferences Policy Control for macOS apps.",
    profileSectionHintSystemExtensions: "Use this section to allow system extensions and network/security extensions on macOS.",
    profileSectionHintFirewall: "Use this section to enable the macOS firewall, stealth mode, and application rules.",
    profileSectionHintLoginItems: "Use this section to manage login items and app auto-launch behavior on macOS.",
    profileSectionHintBackgroundTasks: "Use this section to manage macOS background tasks through Service Management.",
    profileSectionHintMacOSSoftwareUpdate: "Use this section to configure macOS update policies, automatic installation, and deferrals.",
    profileSectionHintLocalAdmin: "Turuk macOS workflow for local admins: account creation, hiding, Secure Token, and password rotation require the macOS Agent.",
    profileSectionHintScriptsPackages: "macOS workflow: pkg/dmg are prepared for MDM InstallApplication, shell scripts require Turuk macOS Agent.",
    profileSectionWarningDNSProxy: "This payload cannot be installed by Apple Configurator and must be installed by a device management service on devices running iOS 15 or later.",
    profileAirPlayPasswords: "Passwords",
    profileAirPlayAllowed: "Allowed",
    profileAirPlayDeviceName: "Device Name",
    profileAirPlayPassword: "Password",
    profileAirPlayDestinationDialogTitle: "Enter an AirPlay destination to which the device can connect.",
    profileAirPlayDestinationName: "Name",
    profileAirPlayDestinationPassword: "Password",
    profileRestrictionsAppPickerTitle: "Choose an app",
    profileRestrictionsAppPickerHint: "Enter the name of an app or its bundle identifier to search for it, then choose the app.",
    profileRestrictionsAppPickerSearchPlaceholder: "App name or bundle identifier",
    profileRestrictionsAppPickerChoose: "Choose",
    profileRestrictionsAppPickerNoItems: "No apps found",
    profileBundlePickerLabel: "Choose a built-in iOS app",
    profileBundlePickerPlaceholder: "— select an app —",
    profileBuilderUploadFile: "Upload File...",
    profileBuilderRemoveConfiguration: "Remove Configuration",
    profileCertificatesLoadFailed: "Failed to load certificate file",
    cancel: "Cancel",
    save: "Save",
    close: "Close",
    clear: "Clear",
    row: "Row",
    create: "Create",
    tableStatus: "Status",
    tableAction: "Action",
    accountsHint: "Accounts to which devices are attached.",
    accountsCreateAccount: "Create account",
    accountsCreateModalTitle: "New account",
    accountsEditModalTitle: "Account",
    accountsImport: "Import",
    accountsImportTitle: "Import accounts",
    accountsImportUpload: "Upload from computer",
    accountsImportFile: "File",
    accountsImportSubmit: "Import",
    accountsImportDone: "Imported accounts",
    accountsImportFailed: "Failed to import accounts",
    accountsImportEmpty: "The file does not contain accounts to import",
    accountsImportParseFailed: "Failed to read import file",
    accountsImportExcelUnsupported: "Save the Excel file as CSV and upload it again.",
    accountsImportFormat: "Excel CSV is supported. One row is one account. Columns: login,email,password,first_name,last_name,department.",
    accountsImportHelp: "Upload CSV with the first header row: login,email,password,first_name,last_name,department. Without a header, the system reads columns in the same order. Required fields: login, email, password. Password must be at least 6 characters. Delimiter: comma, semicolon, or tab.",
    accountsImportMoreRows: "More rows: {count}",
    accountsFirstName: "First name",
    accountsSecondName: "Second name",
    accountsDepartment: "Department",
    accountsName: "Login",
    accountsLoginHint: "This login is used for authentication in the Turuk Companion app.",
    accountsEmail: "Email",
    accountsPassword: "Password",
    accountsPasswordKeepHint: "Leave blank to keep the current password.",
    accountsActiveLabel: "Account is active",
    accountsSearch: "Search account (name/email/external)",
    accountsFilterActive: "Active only",
    accountsFilterWithDevices: "With devices only",
    accountsGroups: "Groups",
    accountsAddToGroupModalTitle: "Edit account groups",
    accountsSelectedCount: "Selected accounts",
    accountsDeleteSelected: "Delete selected",
    accountsDeleteSelectedConfirm: "Delete selected accounts?",
    accountsDeleteSelectedFailed: "Failed to delete selected accounts",
    accountsAssignToGroups: "Add to group",
    accountsAssignGroupsFailed: "Failed to assign accounts to groups",
    accountsNoItems: "No accounts found",
    accountsCreateFailed: "Failed to create account",
    accountsAssign: "Assign",
    accountsUnassign: "Unassign",
    accountsDevicesTitle: "Account devices",
    accountsMdmDevicesCount: "MDM devices",
    accountsMobileBindingsTitle: "Mobile app bindings",
    accountsMobileDeviceName: "Device",
    accountsMobileLastSeen: "Last activity",
    accountsAssignFailed: "Failed to assign device",
    accountsUnassignFailed: "Failed to unassign device",
    accountsMobileUnbindFailed: "Failed to unbind mobile device",
    accountsMobileUnbindDone: "Mobile device unbound",
    accountsUpdate: "Save account",
    accountsUpdateFailed: "Failed to update account",
    accountsDeleteFailed: "Failed to delete account",
    groupsTitle: "Groups",
    groupsName: "Group name",
    groupsColAccounts: "Accounts",
    groupsColApps: "Apps",
    groupsColProfiles: "Profiles",
    groupsColDocuments: "Documents",
    groupsSelectedCount: "Selected groups",
    groupsNoItems: "No groups yet",
    groupsCreateModalTitle: "New group",
    groupsCreateFailed: "Failed to create group",
    groupsDeleteSelected: "Delete selected",
    groupsDeleteSelectedConfirm: "Delete selected groups?",
    groupsDeleteSelectedFailed: "Failed to delete selected groups",
    appsDeploy: "Deploy app",
    appsDeployFailed: "Failed to deploy app",
    appsStoreTitleIOS: "Select app from iOS/iPadOS App Store",
    appsStoreTitleMac: "Select app from Mac App Store",
    appsStoreTitleAndroid: "Select app from Managed Google Play",
    appsStoreHintIOS: "Find an iOS/iPadOS app by name and add it to catalog.",
    appsStoreHintMac: "Find a macOS app by name and add it to catalog.",
    appsStoreHintAndroid: "Select an Android application in Managed Google Play. It will be added to the catalog after selection.",
    appsStoreCountry: "Country",
    appsStoreSearchButton: "Search",
    appsStoreNoItems: "No results found",
    appsStoreOpen: "Open in store",
    appsStoreAddToCatalog: "Add to console",
    appsStoreSearchFailed: "Failed to search App Store",
    appsStoreAddFailed: "Failed to add app from App Store",
    appsStoreAdded: "Application added",
    appsManagedPlayLoading: "Loading Managed Google Play...",
    appsManagedPlayInitFailed: "Failed to open Managed Google Play",
    appsManagedPlayFrameTitle: "Managed Google Play",
    appsManagedPlayOpenFallback: "Open Managed Google Play in a new tab",
    appsManagedPlaySelected: "Selected in Managed Google Play",
    appsManagedPlaySelectHint: "Click Select on the app card to add it to the catalog.",
    platformPickerTitleApps: "Add app",
    platformPickerTitleProfiles: "Create profile",
    platformPickerHintApps: "Choose platform. Then the corresponding app store flow will open.",
    platformPickerHintProfiles: "Choose platform. Then profile creation for that OS will open.",
    platformIOS: "iOS/iPadOS",
    platformMacOS: "macOS",
    platformAndroid: "Android",
    platformWindows: "Windows",
    platformLinux: "Linux",
    settingsWorkspaceConsoleGroup: "Console settings",
    settingsWorkspaceConsoleBranding: "Appearance",
    settingsWorkspaceConsoleDashboard: "Dashboard",
    settingsWorkspaceConsoleUsers: "Console users",
    settingsWorkspaceAgent: "Turuk Companion",
    settingsWorkspaceCertificates: "Apple Configurator",
    settingsWorkspaceADE: "Apple Business Manager",
    settingsWorkspaceVPP: "Apple Apps and Books",
    settingsWorkspaceDeclarative: "Apple Automatic Policies",
    settingsWorkspaceSecurity: "Security",
    settingsWorkspaceNotifications: "Notifications",
    settingsWorkspaceReports: "Reports",
    settingsWorkspaceSSO: "SSO",
    settingsWorkspaceTenants: "Organizations",
    settingsWorkspaceRoleSettings: "Role settings",
    certificatesTitle: "Apple Configurator",
    certificatesHint: "Configure Apple Configurator 2 for automatic device enrollment into Turuk MDM.",
    certificatesConfiguratorTitle: "Apple Configurator",
    certificatesConfiguratorHint: "Install this profile with Apple Configurator. The device will appear in the console without an account. After the user signs in to Turuk Companion, the device will be linked to that account automatically.",
    certificatesConfiguratorServiceName: "Name",
    certificatesConfiguratorServiceNameValue: "Turuk MDM",
    certificatesConfiguratorHostURL: "Host name or URL",
    certificatesConfiguratorPublicURL: "Public Apple Configurator URL",
    certificatesConfiguratorURLHint: "Paste the public URL below into Apple Configurator. It is unique for the organization. Rotate it if it was shared with the wrong party.",
    certificatesConfiguratorURLMissing: "URL has not been created yet",
    certificatesConfiguratorRotateURL: "Create / rotate URL",
    certificatesConfiguratorURLRotated: "Configurator URL updated",
    certificatesConfiguratorURLRotateFailed: "Failed to update Configurator URL",
    certificatesConfiguratorCopyURL: "Copy URL",
    certificatesConfiguratorURLCopied: "Configurator URL copied",
    certificatesConfiguratorLastUsed: "Last used",
    certificatesConfiguratorRotatedAt: "Rotated",
    certificatesDownloadConfiguratorProfile: "Download .mobileconfig profile",
    certificatesDownloadConfiguratorProfileDone: "Apple Configurator profile downloaded",
    certificatesDownloadConfiguratorProfileFailed: "Failed to download Apple Configurator profile",
    certificatesMacOSAgentTitle: "macOS Agent",
    certificatesMacOSAgentHint: "Turuk macOS Agent is installed through MDM after Mac enrollment and runs scripts/packages, local admin workflows, FileVault, and compliance jobs.",
    certificatesMacOSAgentPackageURL: "Public package link",
    certificatesDownloadMacOSAgentPackage: "Download Turuk macOS Agent.pkg",
    certificatesDownloadMacOSAgentPackageDone: "Turuk macOS Agent.pkg downloaded",
    certificatesDownloadMacOSAgentPackageFailed: "Failed to download Turuk macOS Agent.pkg",
    certificatesMacOSAgentUnavailable: "The package has not been built or uploaded to the server yet.",
    certificatesMacOSAgentSigned: "Signed",
    certificatesMacOSAgentNotarized: "Notarized",
    certificatesMacOSAgentVersion: "Version",
    certificatesMacOSAgentSHA256: "SHA-256",
    certificatesAPNSTitle: "Organization MDM APNs",
    certificatesAPNSHint: "Renewal flow: create a CSR in the console, click Renew for the current certificate in Apple Push Certificates Portal, download the renewed Apple MDM Push Certificate, then upload it back here.",
    certificatesAPNSStep1: "1. Create CSR for Renew",
    certificatesAPNSStep2: "2. Open Apple Push Certificates Portal and click Renew for the current certificate",
    certificatesAPNSStep3: "3. Enter Apple ID and upload the renewed Apple MDM Push Certificate",
    certificatesAPNSCreateCSR: "Create renewal CSR",
    certificatesAPNSDownloadCSR: "Download current CSR",
    certificatesAPNSCSRReady: "CSR created",
    certificatesAPNSCSRNotReady: "CSR not created yet",
    certificatesAPNSCSRRenewOnly: "CSR is created only before Renew",
    certificatesAPNSApplePortal: "Open Apple Push Certificates Portal",
    certificatesAPNSAppleID: "Apple ID used for the certificate",
    certificatesAPNSTopic: "Topic",
    certificatesAPNSCert: "Apple MDM Push Certificate (.pem)",
    certificatesAPNSConfigured: "Configured",
    certificatesAPNSNotConfigured: "Not configured",
    certificatesAPNSStatus: "Status",
    certificatesAPNSStatusDisabled: "Disabled",
    certificatesAPNSStatusExpired: "Certificate expired",
    certificatesAPNSStatusExpiresSoon: "Expires soon",
    certificatesAPNSStatusReady: "Ready for management",
    certificatesAPNSSource: "Source",
    certificatesAPNSSourceGlobal: "Platform global setting",
    certificatesAPNSSourceTenant: "Organization setting",
    certificatesAPNSDaysLeft: "Days until expiration",
    certificatesAPNSCSRState: "CSR",
    certificatesAPNSSave: "Upload renewed certificate",
    certificatesAPNSSaved: "MDM APNs renewed",
    certificatesAPNSSaveFailed: "Failed to save MDM APNs",
    certificatesAPNSCSRFailed: "Failed to create CSR",
    certificatesAPNSCSRDownloaded: "CSR downloaded",
    certificatesAPNSCSRDownloadFailed: "Failed to download CSR",
    certificatesAPNSCertSubject: "Certificate",
    certificatesAPNSExpires: "Expires",
    certificatesAPNSRenewSafety: "Important: use Renew for the existing certificate. If the uploaded certificate has a different Topic, the console will reject it to preserve management of current devices.",
    certificatesAPNSRenewBannerTitle: "MDM APNs certificate expires soon",
    certificatesAPNSRenewBannerText: "{days} days left. Expires: {date}. Renew the certificate in Enrollment Profile, otherwise devices will stop receiving commands.",
    certificatesAPNSRenewBannerAction: "Open APNs renewal",
    certificatesAPNSExpectedTopic: "Expected Topic",
    certificatesAPNSUploadGuard: "Upload only the certificate created through Renew for the current Topic. A certificate with another Topic will be rejected.",
    adeTitle: "Apple Business Manager / ADE",
    adeHint: "Upload the server token .p7m, synchronize ADE devices, and assign enrollment profiles through Apple Business Manager.",
    adeTokenTitle: "Server token",
    adeConfigured: "Configured",
    adeNotConfigured: "Not configured",
    adeUploadToken: "Upload server token .p7m",
    adeTokenFile: "Server token file (.p7m)",
    adeAppleID: "Apple ID used in Apple Business Manager",
    adeOrganization: "Organization",
    adeServerUUID: "Server UUID",
    adeTokenFilename: "File",
    adeUploadedAt: "Uploaded",
    adeTokenExpires: "Expires",
    adeLastSync: "Last sync",
    adeSyncStatus: "Sync status",
    adeSyncNow: "Sync devices",
    adeTokenUploadDone: "Server token uploaded",
    adeTokenUploadFailed: "Failed to upload server token",
    adeSyncQueued: "ADE sync completed",
    adeDevicesTitle: "ADE devices",
    adeNoDevices: "ADE devices have not been synchronized yet",
    adeSerialNumber: "Serial number",
    adeModel: "Model",
    adeProfile: "Enrollment profile",
    adeProfileStatus: "Profile status",
    adeLastSeen: "Last update",
    adeProfilesTitle: "ADE enrollment profiles",
    adeCreateProfile: "Create profile",
    adeProfileName: "Profile name",
    adeProfileURL: "Enrollment URL",
    adeDefaultProfile: "Default profile",
    adeProfileSaved: "ADE profile saved",
    adeProfileSaveFailed: "Failed to save ADE profile",
    adeAssignProfileDone: "Profile assigned",
    adeAssignProfileFailed: "Failed to assign profile",
    adeUploadHint: "Use the token from Apple Business Manager or Apple School Manager. The secret is encrypted at the organization level.",
    vppTitle: "Apple Apps and Books / VPP",
    vppHint: "Upload the VPP token, synchronize purchased app licenses, and assign licenses to groups.",
    vppSyncNow: "Sync licenses",
    vppConfigured: "Configured",
    vppNotConfigured: "Not configured",
    vppTokenTitle: "VPP token",
    vppUploadToken: "Upload VPP token",
    vppOrganization: "Organization",
    vppTokenExpires: "Expires",
    vppLastSync: "Last sync",
    vppAssetsTitle: "App licenses",
    vppAssignmentsTitle: "License assignments",
    vppUploadHint: "Upload the server token from Apple Business Manager / Apps and Books. The secret is encrypted at the organization level.",
    vppAppleID: "Apple ID used for the VPP token",
    vppTokenFile: "VPP token file",
    vppTokenFilename: "File",
    vppTokenUploadFailed: "Failed to upload VPP token",
    vppTokenUploadDone: "VPP token uploaded",
    vppSyncDone: "VPP sync completed",
    vppSyncFailed: "Failed to synchronize VPP",
    vppAssignFailed: "Failed to assign license",
    vppAssignDone: "License assigned",
    vppRevokeFailed: "Failed to revoke license",
    vppRevokeDone: "License revoked",
    vppLicensesTotal: "Total",
    vppLicensesAvailable: "Available",
    vppLicensesAssigned: "Assigned",
    vppAssignLicense: "Assign license",
    vppRevokeLicense: "Revoke license",
    vppNoAssets: "No licenses have been synchronized yet.",
    vppNoAssignments: "No license assignments yet.",
    vppLastError: "Last error",
    securityTitle: "Security",
    securityHint: "Manage console user passwords and multi-factor authentication.",
    securityOwnPasswordTitle: "Change your password",
    securityOwnPasswordHint: "Change the password for the current console account.",
    securityOwnMFATitle: "MFA for your account",
    securityOwnMFAHint: "Connect an authenticator application to the current account.",
    securityMFAAlreadyEnabled: "MFA is already enabled for this account.",
    securityAdminPasswordTitle: "Change administrator password",
    securityAdminPasswordHint: "Set a temporary password for another console user. Their active sessions will be revoked.",
    securityAdminMFATitle: "Console user MFA",
    securityAdminMFAHint: "Requiring MFA forces the user to configure an authenticator during their next sign-in.",
    securitySelectUser: "Console user",
    securityRequirePasswordChange: "Require password change at next sign-in",
    securityPasswordChanged: "User password changed",
    securityPasswordChangeFailed: "Failed to change user password",
    securityMFARequired: "Require MFA",
    securityMFAUpdated: "User MFA settings updated",
    securityMFAUpdateFailed: "Failed to update user MFA settings",
    securitySelectAnotherUser: "Select another console user",
    securityTenantExportTitle: "Organization data export",
    securityTenantExportHint: "Downloads a JSON export of the current organization data for backup, audit, and migration. Secrets and private keys are not included.",
    securityTenantExportButton: "Download export",
    securityTenantExportDone: "Organization data export downloaded",
    securityTenantExportFailed: "Failed to download organization data export",
    securityTenantIsolationTitle: "Organization isolation",
    securityTenantIsolationHint: "Checks runtime tenant_id, RLS policy, and unique-index state before moving the database to a dedicated app role and FORCE RLS.",
    securityTenantIsolationUnavailable: "Report is unavailable. Apply migration sql/032_tenant_app_role_and_audit.sql.",
    securityTenantIsolationAppRole: "App-role readiness",
    securityTenantIsolationForceRLS: "FORCE RLS",
    securityTenantIsolationMissingTables: "Tables without tenant_id",
    securityTenantIsolationRLSIssues: "RLS/policy issues",
    securityTenantIsolationUniqueIssues: "Unique indexes without tenant_id",
    securityTenantIsolationReady: "Ready",
    securityTenantIsolationNeedsAction: "Needs action",
    securityTenantIsolationPending: "Pending",
    consoleBrandingTitle: "Appearance",
    consoleBrandingHint: "Upload your own logo to replace the default logo in the top-left corner of the console.",
    consoleBrandingPreview: "Preview",
    consoleBrandingDefaultPreview: "The default Turuk MDM Control Plane logo is currently used.",
    consoleBrandingUploadLabel: "Logo file",
    consoleBrandingUploadHint: "Supported: PNG, JPG, WEBP, GIF and SVG. Maximum size: 2 MB. Recommended size: 360x120.",
    consoleBrandingLogoClickLabel: "Logo click action",
    consoleBrandingLogoClickHint: "Default is dashboard (#/dashboard). You can set a custom link: #/devices/list, /docs, or https://example.com.",
    consoleBrandingLogoClickPlaceholder: "#/dashboard",
    consoleBrandingLogoClickInvalid: "Invalid URL. Allowed: #/..., /..., http://..., https://...",
    consoleBrandingChooseFile: "Choose an image from your computer",
    consoleBrandingSave: "Save logo",
    consoleBrandingReset: "Restore default",
    consoleBrandingSaved: "Console logo saved",
    consoleBrandingResetDone: "Default logo restored",
    consoleBrandingFileTooLarge: "Logo file is too large",
    consoleBrandingInvalidFile: "An image file is required",
    consoleBrandingReadFailed: "Failed to read logo file",
    consoleBrandingSaveFailed: "Failed to save console logo",
    consoleBrandingLogoAlt: "Console logo",
    consoleMainNavOrderTitle: "Main navigation order",
    consoleMainNavOrderHint: "Drag sections to change the main menu order for your console account only.",
    consoleMainNavOrderSave: "Save order",
    consoleMainNavOrderReset: "Reset order",
    consoleMainNavOrderSaved: "Main menu order saved",
    consoleMainNavOrderSaveFailed: "Failed to save main menu order",
    consoleDashboardTitle: "Dashboard",
    consoleDashboardPersonalHint: "Individual console settings. Each administrator can customize how information is displayed.",
    consoleDashboardHint: "Choose which widgets and charts are visible on the main dashboard page.",
    consoleDashboardOrderTitle: "Widget order",
    consoleDashboardOrderHint: "Drag widgets with the mouse to change the order they appear on the dashboard.",
    consoleDashboardContentTitle: "Widget content",
    consoleDashboardSave: "Save settings",
    consoleDashboardReset: "Reset",
    consoleDashboardSaved: "Dashboard settings saved",
    consoleDashboardSaveFailed: "Failed to save dashboard settings",
    agentSettingsTitle: "Turuk Companion",
    agentSettingsHint: "Turuk Companion app settings: branding, corporate services, and user support tickets.",
    agentSettingsBrandingTitle: "App branding",
    agentSettingsOrganizationName: "Organization name",
    agentSettingsPrimaryColor: "Primary color",
    agentSettingsLogoDataURL: "Logo (Data URL)",
    agentSettingsSupportEmail: "Support email",
    agentSettingsSupportPhone: "Support phone",
    agentSettingsServicesTitle: "Corporate services",
    agentSettingsAddService: "Add service",
    agentSettingsEnabled: "Enabled",
    agentSettingsServiceTitle: "Service title",
    agentSettingsIcon: "Icon",
    agentSettingsCategory: "Category",
    agentSettingsNoServices: "No services configured",
    agentSettingsSupportTicketsTitle: "Support tickets",
    supportBellLabel: "Support requests",
    supportBellUnread: "New support requests: {count}",
    agentSettingsSubject: "Subject",
    agentSettingsCreatedAt: "Created",
    agentSettingsNoTickets: "No support tickets yet",
    agentSettingsTicketOpen: "Open",
    agentSettingsTicketDetails: "Ticket details",
    agentSettingsTicketStatusOpen: "Open",
    agentSettingsTicketStatusInProgress: "In progress",
    agentSettingsTicketStatusClosed: "Closed",
    agentSettingsTicketReply: "Admin reply",
    agentSettingsTicketSendReply: "Save and send reply",
    agentSettingsTicketHistory: "History",
    agentSettingsTicketLogs: "App logs",
    agentSettingsTicketDeviceInfo: "Device info",
    agentSettingsTicketOriginalMessage: "User message",
    agentSettingsTicketSaved: "Ticket updated",
    agentSettingsTicketSaveFailed: "Failed to update ticket",
    agentSettingsTicketDevice: "Device",
    supportCategoryGeneral: "General",
    supportCategoryApps: "Apps",
    supportCategoryProfiles: "Profiles",
    supportCategoryDevice: "Device",
    supportCategoryAccess: "Access",
    supportCategoryOther: "Other",
    supportEventCreated: "Created",
    supportEventReply: "Reply",
    supportEventStatusChanged: "Status changed",
    agentSettingsSaved: "Turuk Companion settings saved",
    agentSettingsSaveFailed: "Failed to save Turuk Companion settings",
    agentSettingsServiceTitleRequired: "Enter service title",
    consoleDashboardSectionTotalDeployment: "Total deployment",
    consoleDashboardSectionTotalDeploymentHint: "Top summary strip with key counters",
    consoleDashboardSectionSecurity: "Security",
    consoleDashboardSectionSecurityHint: "Ring charts for encryption, passcode and supervised status",
    consoleDashboardSectionSupportTickets: "Support tickets",
    consoleDashboardSectionSupportTicketsHint: "Open ticket counters from Turuk Companion",
    consoleDashboardSectionOwnership: "Ownership",
    consoleDashboardSectionOwnershipHint: "Distribution of devices by ownership type",
    consoleDashboardSectionLastSeenOverview: "Last seen overview",
    consoleDashboardSectionLastSeenOverviewHint: "Card for devices active in the last 7 days",
    consoleDashboardSectionLastSeenBreakdown: "Last seen breakdown",
    consoleDashboardSectionLastSeenBreakdownHint: "Horizontal activity breakdown by age buckets",
    consoleDashboardSectionPlatforms: "Platforms",
    consoleDashboardSectionPlatformsHint: "Vertical platform distribution chart",
    consoleDashboardSummaryAccounts: "Show accounts counter",
    consoleDashboardSummaryProfiles: "Show profiles counter",
    consoleDashboardSummaryApps: "Show apps counter",
    consoleDashboardSummaryGroups: "Show groups counter",
    consoleDashboardSecurityEncrypted: "Show encryption",
    consoleDashboardSecurityPasscode: "Show passcode",
    consoleDashboardSecuritySupervised: "Show supervised",
    consoleDashboardOwnershipCorporate: "Show corporate",
    consoleDashboardOwnershipPersonal: "Show personal",
    consoleDashboardOwnershipUnassigned: "Show unassigned",
    consoleDashboardLastSeenBucket0to1: "Show 0-1 days",
    consoleDashboardLastSeenBucket2to7: "Show 2-7 days",
    consoleDashboardLastSeenBucket8to15: "Show 8-15 days",
    consoleDashboardLastSeenBucket16to30: "Show 16-30 days",
    consoleDashboardLastSeenBucket31Plus: "Show 31+ days",
    consoleDashboardPlatformIOS: "Show iOS/iPadOS",
    consoleDashboardPlatformMacOS: "Show macOS",
    consoleDashboardPlatformWindows: "Show Windows",
    consoleDashboardPlatformAndroid: "Show Android",
    consoleUsersTitle: "Console users",
    consoleUsersHint: "Create read-only/admin/master admin users for MDM console access.",
    consoleUsersAdd: "Add",
    consoleUsersFirstName: "First Name",
    consoleUsersLastName: "Last Name",
    consoleUsersEmail: "Email",
    consoleUsersLogin: "Login",
    consoleUsersTempPassword: "Temporary Password",
    consoleUsersRole: "Role",
    consoleUsersCreate: "Create",
    consoleUsersCancel: "Cancel",
    consoleUsersRevoke: "Disable",
    consoleUsersEdit: "Edit",
    consoleUsersEditModalTitle: "Edit user",
    consoleUsersUpdate: "Save",
    consoleUsersUpdateFailed: "Failed to update user",
    consoleUsersNoItems: "No console users",
    consoleUsersCreateFailed: "Failed to create user",
    consoleUsersRevokeFailed: "Failed to disable user",
    consoleUsersMustChange: "Must change password",
    consoleUsersMFA: "MFA / Passkey",
    mfaEnabledRequired: "Enabled / required",
    mfaRequiredNotConfigured: "Required / not configured",
    consoleUsersName: "Name",
    tenantsTitle: "Organizations",
    tenantsHint: "SaaS organization catalog. Creating an organization also creates its first administrator for the organization subdomain.",
    tenantsCreateTitle: "New organization",
    tenantsEditTitle: "Edit organization",
    tenantsName: "Name",
    tenantsSlug: "Subdomain",
    tenantsPrimaryDomain: "Primary domain",
    tenantsPortalURL: "Portal URL",
    tenantsDomainStatus: "Domain check",
    tenantsDomainCheck: "Check",
    tenantsDomainChecking: "Checking...",
    tenantsDomainReady: "Ready",
    tenantsDomainNotReady: "Not ready",
    tenantsDomainAllowed: "MDM allowed",
    tenantsDomainDNSResolved: "DNS resolved",
    tenantsDomainCheckFailed: "Failed to check domain",
    tenantsReadiness: "Readiness",
    tenantsReadinessReady: "Ready",
    tenantsReadinessNotReady: "Not ready",
    tenantsReadinessStatus: "Active status",
    tenantsReadinessBilling: "Billing active",
    tenantsReadinessDomain: "Domain",
    tenantsReadinessAdmin: "First administrator",
    tenantsReadinessMDMAPNS: "MDM APNs",
    tenantsReadinessEnrollmentProfile: "Enrollment profile",
    tenantsReadinessFirstDevice: "First device",
    tenantsReadinessUnknown: "Check",
    tenantsReadinessReasonStatus: "Organization must be active, not suspended and not deleted",
    tenantsReadinessReasonBilling: "Billing must not be expired or canceled",
    tenantsReadinessReasonDomain: "Set the organization's primary domain",
    tenantsReadinessReasonAdmin: "Create the first active administrator",
    tenantsReadinessReasonMDMAPNS: "Upload a valid MDM APNs certificate",
    tenantsReadinessReasonEnrollmentProfile: "Configure an enrollment profile or enrollment token",
    tenantsReadinessReasonFirstDevice: "Enroll the first device",
    tenantsUsage: "Usage",
    tenantsUsageDevices: "Devices",
    tenantsUsageAccounts: "Accounts",
    tenantsUsageAdmins: "Admins",
    tenantsUsageUnknown: "Limit",
    tenantsUsageUnlimited: "unlimited",
    tenantsUsageOverLimit: "Over limit",
    tenantsBilling: "Billing",
    tenantsBillingStatus: "Billing status",
    tenantsBillingEmail: "Billing email",
    tenantsTrialEndsAt: "Trial ends",
    tenantsSubscriptionEndsAt: "Subscription ends",
    tenantsStripeCustomerID: "Stripe Customer ID",
    tenantsStripeSubscriptionID: "Stripe Subscription ID",
    tenantsBillingStatusInternal: "Internal",
    tenantsBillingStatusTrialing: "Trialing",
    tenantsBillingStatusManual: "Manual billing",
    tenantsBillingStatusActive: "Active",
    tenantsBillingStatusPastDue: "Past due",
    tenantsBillingStatusCanceled: "Canceled",
    tenantsManualLimits: "Manual limits",
    tenantsManualLimitsHint: "Empty field uses the plan limit. 0 means unlimited.",
    tenantsDeviceLimit: "Device limit",
    tenantsAccountLimit: "Account limit",
    tenantsAdminLimit: "Admin limit",
    tenantsLimitInvalid: "Limits must be empty or whole numbers 0 and above",
    tenantsAccountsCount: "Accounts",
    tenantsDevicesCount: "MDM devices",
    tenantsAdminsCount: "Admins",
    tenantsInitialAdminTitle: "First administrator",
    tenantsInitialAdminHint: "This user receives the Master Admin role, required MFA and a temporary password for first sign-in.",
    tenantsAdminFirstName: "First name",
    tenantsAdminLastName: "Last name",
    tenantsAdminEmail: "Admin email",
    tenantsAdminLogin: "Admin login",
    tenantsAdminTemporaryPassword: "Temporary password",
    tenantsStatus: "Status",
    tenantsPlan: "Plan",
    tenantsStatusActive: "Active",
    tenantsStatusTrial: "Trial",
    tenantsStatusSuspended: "Suspended",
    tenantsStatusDeleted: "Deleted",
    tenantsLifecycle: "Lifecycle",
    tenantsSuspendedAt: "Suspended at",
    tenantsScheduledDeletionAt: "Scheduled deletion",
    tenantsDeletedAt: "Deleted at",
    tenantsSuspendedReason: "Suspension reason",
    tenantsDeletionReason: "Deletion reason",
    tenantsLifecycleSuspend: "Suspend",
    tenantsLifecycleReactivate: "Reactivate",
    tenantsLifecycleScheduleDelete: "Schedule deletion",
    tenantsLifecycleCancelDelete: "Cancel deletion",
    tenantsLifecycleDeleteNow: "Delete now",
    tenantsLifecycleSuspendReasonPrompt: "Organization suspension reason",
    tenantsLifecycleDeleteReasonPrompt: "Organization deletion reason",
    tenantsLifecycleScheduleDeletePrompt: "Deletion date in YYYY-MM-DD or RFC3339 format",
    tenantsLifecycleScheduleDeleteRequired: "Set a deletion date",
    tenantsLifecycleDeleteNowConfirm: "The organization will move to Deleted. Data remains in the database, access is blocked. Continue?",
    tenantsLifecycleReactivateConfirm: "Reactivate the organization and clear lifecycle blocks?",
    tenantsLifecycleCancelDeleteConfirm: "Cancel scheduled organization deletion?",
    tenantsLifecycleSaved: "Organization lifecycle updated",
    tenantsLifecycleFailed: "Failed to update organization lifecycle",
    tenantsPlanInternal: "Internal",
    tenantsPlanTrial: "Trial",
    tenantsPlanBusiness: "Business",
    tenantsPlanEnterprise: "Enterprise",
    tenantsSaved: "Organization saved",
    tenantsSaveFailed: "Failed to save organization",
    tenantsConflictSlug: "An organization with this subdomain already exists",
    tenantsConflictPrimaryDomain: "An organization with this primary domain already exists",
    tenantsConflictGeneric: "An organization with these settings already exists",
    tenantsReservedDomain: "This subdomain is reserved by the system and cannot be used for an organization",
    tenantsConfiguratorProfileDownload: "Download enrollment profile",
    tenantsConfiguratorProfileDownloaded: "Organization enrollment profile downloaded",
    tenantsConfiguratorProfileDownloadFailed: "Failed to download organization enrollment profile",
    tenantsNoItems: "No organizations",
    roleSettingsTitle: "Role settings",
    roleSettingsHint: "Create roles and configure access to sections, pages and actions (view/edit).",
    roleSettingsCreate: "Create role",
    roleSettingsDelete: "Delete role",
    roleSettingsCreateFailed: "Failed to create role",
    roleSettingsUpdateFailed: "Failed to update role",
    roleSettingsDeleteFailed: "Failed to delete role",
    roleSettingsNoItems: "No roles",
    roleSettingsRoleKey: "Role key",
    roleSettingsDisplayName: "Role name",
    roleSettingsType: "Type",
    roleSettingsUsersCount: "Users",
    roleSettingsPermissions: "Permissions",
    roleSettingsTemplates: "Templates",
    roleSettingsTemplateSupport: "Read-only support operator",
    roleSettingsTemplateSupportHint: "View-only access to devices, accounts and support tickets.",
    roleSettingsTemplateHelpdesk: "Helpdesk",
    roleSettingsTemplateHelpdeskHint: "Support operations: accounts, device commands and Companion.",
    roleSettingsTemplateSecurity: "Security admin",
    roleSettingsTemplateSecurityHint: "Security, SSO, console users and RBAC settings.",
    roleSettingsAccessScope: "Access scope",
    roleSettingsScopeHint: "Empty scope means organization-wide access.",
    roleSettingsScopeGroups: "Groups",
    roleSettingsScopeAllGroups: "All groups",
    roleSettingsScopePlatforms: "Platforms",
    roleSettingsScopeLocations: "Locations",
    roleSettingsScopeLocationsPlaceholder: "location-id-1, location-id-2",
    roleSettingsView: "View",
    roleSettingsEditAction: "Edit",
    roleSettingsSystem: "System",
    roleSettingsCustom: "Custom",
    roleSettingsSavePermissions: "Save permissions",
    roleSettingsSelectHint: "Select a role from the table to configure permissions.",
    roleSettingsPermissionDashboard: "Dashboard",
    roleSettingsPermissionAccounts: "Accounts",
    roleSettingsPermissionAccountsList: "Accounts · List",
    roleSettingsPermissionAccountsCreate: "Accounts · Create",
    roleSettingsPermissionAccountsConnect: "Accounts · Connect devices",
    roleSettingsPermissionGroups: "Groups",
    roleSettingsPermissionDevices: "Devices",
    roleSettingsPermissionDevicesOverview: "Devices · Overview",
    roleSettingsPermissionDevicesList: "Devices · All devices",
    roleSettingsPermissionDevicesRequest: "Devices · Request device",
    roleSettingsPermissionDevicesMonitor: "Devices · Monitor",
    roleSettingsPermissionDevicesPlatformIOS: "Devices · iOS/iPadOS",
    roleSettingsPermissionDevicesPlatformMacOS: "Devices · macOS",
    roleSettingsPermissionDevicesPlatformWindows: "Devices · Windows",
    roleSettingsPermissionDevicesPlatformAndroid: "Devices · Android",
    roleSettingsPermissionDevicesConfig: "Devices · Configuration",
    roleSettingsPermissionDevicesCompliance: "Devices · Compliance",
    roleSettingsPermissionDevicesDetail: "Devices · Device page",
    roleSettingsPermissionDevicesApps: "Devices · Apps tab",
    roleSettingsPermissionDevicesProfiles: "Devices · Profiles tab",
    roleSettingsPermissionDevicesCommands: "Devices · Commands tab",
    roleSettingsPermissionProfiles: "Profiles & Policies",
    roleSettingsPermissionProfilesAll: "Profiles · All profiles",
    roleSettingsPermissionProfilesPlatformIOS: "Profiles · iOS/iPadOS",
    roleSettingsPermissionProfilesPlatformMacOS: "Profiles · macOS",
    roleSettingsPermissionProfilesPlatformWindows: "Profiles · Windows",
    roleSettingsPermissionProfilesPlatformAndroid: "Profiles · Android",
    roleSettingsPermissionPoliciesAll: "Policies · All policies",
    roleSettingsPermissionPoliciesCompliance: "Policies · Compliance",
    roleSettingsPermissionApps: "Apps",
    roleSettingsPermissionAppsOverview: "Apps · Overview",
    roleSettingsPermissionAppsAll: "Apps · All apps",
    roleSettingsPermissionAppsPlatformIOS: "Apps · iOS/iPadOS",
    roleSettingsPermissionAppsPlatformMacOS: "Apps · macOS",
    roleSettingsPermissionAppsPlatformWindows: "Apps · Windows",
    roleSettingsPermissionAppsPlatformAndroid: "Apps · Android",
    roleSettingsPermissionAppsConfig: "Apps · Configuration",
    roleSettingsPermissionAppsUpload: "Apps · Upload enterprise",
    roleSettingsPermissionSettings: "Settings",
    roleSettingsPermissionSettingsConsoleBranding: "Settings · Console settings",
    roleSettingsPermissionSettingsConsoleUsers: "Settings · Console users",
    roleSettingsPermissionSettingsCertificates: "Settings · Enrollment Profile",
    roleSettingsPermissionSettingsADE: "Settings · Apple Business Manager",
    roleSettingsPermissionSettingsVPP: "Settings · Apple Apps and Books",
    roleSettingsPermissionSettingsDeclarative: "Settings · Declarative Management",
    roleSettingsPermissionSettingsSecurity: "Settings · Security",
    roleSettingsPermissionSettingsNotifications: "Settings · Notifications",
    roleSettingsPermissionSettingsReports: "Settings · Reports",
    roleSettingsPermissionSettingsSSO: "Settings · SSO",
    roleSettingsPermissionSettingsAgent: "Settings · Turuk Companion",
    roleSettingsPermissionSettingsTenants: "Settings · Organizations",
    roleSettingsPermissionSettingsOpenAPI: "Settings · OpenAPI",
    roleSettingsPermissionSettingsRoles: "Settings · Role settings",
    alertSettingsTitle: "Notifications",
    alertSettingsHint: "Notification center settings for system events, email, in-app and webhook delivery.",
    alertSettingsChannels: "Channels",
    alertSettingsTriggers: "Triggers",
    alertSettingsThresholds: "Thresholds",
    alertSettingsEnabled: "Notification center enabled",
    alertSettingsInApp: "In-app notifications",
    alertSettingsEmail: "Email",
    alertSettingsWebhook: "Webhook",
    alertSettingsTriggerAPNS: "APNs expires",
    alertSettingsTriggerADE: "ADE sync failed",
    alertSettingsTriggerVPP: "VPP sync failed",
    alertSettingsTriggerDeviceOffline: "Device has not checked in",
    alertSettingsTriggerEnrollmentSpike: "Enrollment spike",
    alertSettingsTriggerSuspiciousLogin: "Suspicious admin login",
    alertSettingsTriggerManagementProfileRemoved: "Management profile removed",
    alertSettingsAPNSDays: "APNs: warn before days",
    alertSettingsDeviceOfflineDays: "Device offline after days",
    alertSettingsEnrollmentWindow: "Enrollment spike window, minutes",
    alertSettingsEnrollmentThreshold: "Enrollment spike threshold",
    alertSettingsEvaluate: "Check now",
    alertSettingsOpenAlerts: "Open alerts",
    alertSettingsSeverity: "Severity",
    alertSettingsAlert: "Alert",
    alertSettingsSource: "Source",
    alertSettingsAcknowledge: "Acknowledge",
    alertSettingsNoAlerts: "No open alerts",
    alertSettingsSaved: "Notification settings saved",
    alertSettingsSaveFailed: "Failed to save notification settings",
    alertSettingsEvaluateDone: "Check completed",
    alertSettingsEvaluateFailed: "Failed to run check",
    alertSettingsAckDone: "Alert acknowledged",
    alertSettingsAckFailed: "Failed to acknowledge alert",
    supportSessionsLoadFailed: "Failed to load support sessions",
    supportSessionsRequested: "Support access request created",
    supportSessionsRequestFailed: "Failed to create support access request",
    supportSessionsApproved: "Support access approved",
    supportSessionsAccessed: "Support access marked",
    supportSessionsRevoked: "Support access revoked",
    supportSessionsActionFailed: "Failed to update support session",
    supportSessionsStatusPending: "pending",
    supportSessionsStatusApproved: "approved",
    supportSessionsStatusRevoked: "revoked",
    supportSessionsStatusExpired: "expired",
    supportSessionsClientBannerTitle: "Turuk support accessed your tenant",
    supportSessionsClientBanner: "Reason: {reason}. Access is active until {expires}.",
    reportsPDF: "PDF",
    reportsCSV: "CSV",
    reportsExportDone: "Report downloaded",
    reportsExportFailed: "Failed to download report",
    reportsSettingsTitle: "Reports",
    reportsSettingsHint: "PDF/CSV exports and scheduled email reports for executives, devices, accounts, groups and apps.",
    reportsScheduleTitle: "Schedule",
    reportsScheduleEnabled: "Send reports by email",
    reportsFrequency: "Frequency",
    reportsFrequencyDaily: "Daily",
    reportsFrequencyWeekly: "Weekly",
    reportsFrequencyMonthly: "Monthly",
    reportsContentTitle: "Content",
    reportsReportType: "Report type",
    reportsFormat: "Format",
    reportsDeliveryTitle: "Delivery",
    reportsRecipients: "Recipients",
    reportsRecipientsHint: "If the list is empty, the report is sent to active console administrators.",
    reportsScheduleSaved: "Report schedule saved",
    reportsScheduleSaveFailed: "Failed to save report schedule",
    reportsExecutive: "Executive summary",
    reportsDevices: "Devices",
    reportsAccounts: "Accounts",
    reportsGroups: "Groups",
    reportsApps: "Apps",
    forcePasswordTitle: "Change temporary password",
    forcePasswordHint: "You must set a permanent password before using the portal.",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    passwordsDoNotMatch: "Passwords do not match",
    changePassword: "Change password",
    passwordChanged: "Password changed",
    changePasswordFailed: "Failed to change password",
    active: "active",
    enabled: "enabled",
    disabled: "disabled",
    revoked: "revoked",
    notAvailable: "not available yet",
    unknown: "unknown",
    yes: "yes",
    no: "no",
    loginFailed: "Authentication failed",
    authRequired: "Login and password are required",
    sessionExpired: "Session expired. Please sign in again.",
    requiredField: "required field",
    all: "All",
    addFilters: "Add filters",
    loading: "Loading...",
    edit: "Edit",
    delete: "Delete",
    description: "Description",
    tableState: "State",
    commandsType: "Command type",
    accountsConnectDevices: "Connect devices",
    accountsNoDevices: "No devices found",
    accountsMobileDeviceID: "Device ID",
    accountsMobileUnbind: "Unbind",
    accountsMobileNoBindings: "No mobile bindings found",
    devicesSectionAll: "All devices",
    devicesGroupPlatforms: "Platforms",
    devicesDeleteSelected: "Delete selected",
    noDevicesHint: "No devices match current filters",
    noCommands: "No commands found",
    deviceDetailsRequestRefresh: "Request",
    deviceDetailsReinstallApps: "Reinstall selected apps",
    deviceDetailsRemoveApps: "Remove selected apps",
    profilesAll: "All profiles",
    profilesSearch: "Search profiles",
    profilesCount: "Profiles",
    policiesCompliance: "Compliance policies",
    policiesSearch: "Search policies",
    policiesCount: "Policies",
    appsAll: "All apps",
    appsConfig: "Configuration",
    appsUpload: "Upload enterprise app",
    appsCount: "Apps",
    appsNoItems: "No apps found",
    groupsCreate: "Create group",
    modalTitleAddUser: "New console user",
  }
};

function text(lang, key) {
  return I18N[lang]?.[key] || key;
}

function pageFromHash(hashValue) {
  const raw = String(hashValue || "");
  const withoutHash = raw.startsWith("#") ? raw.slice(1) : raw;
  const path = withoutHash.split("?")[0] || "";
  if (!path) return "site";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return ROUTE_TO_PAGE[normalized] || "dashboard";
}

function hashQueryValue(hashValue, key) {
  const raw = String(hashValue || "");
  const withoutHash = raw.startsWith("#") ? raw.slice(1) : raw;
  const query = withoutHash.split("?")[1] || "";
  if (!query) return "";
  const value = new URLSearchParams(query).get(key);
  return value ? value.trim() : "";
}

function normalizeYesNoAllFilter(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (normalized === "yes" || normalized === "no" || normalized === "all") {
    return normalized;
  }
  return "";
}

function normalizeLastSeenFilter(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (
    normalized === "all" ||
    normalized === "recent7" ||
    normalized === "0to1" ||
    normalized === "2to7" ||
    normalized === "8to15" ||
    normalized === "16to30" ||
    normalized === "31plus"
  ) {
    return normalized;
  }
  return "";
}

function deviceLastSeenBucket(item, nowTs = Date.now()) {
  const timestamp = [
    item?.last_seen_at,
    item?.last_checkin_at,
    item?.last_token_update_at,
    item?.updated_at
  ]
    .map((value) => {
      const ts = value ? new Date(value).getTime() : 0;
      return Number.isFinite(ts) && ts > 0 ? ts : 0;
    })
    .find((value) => value > 0);
  if (!timestamp) return "31plus";
  const ageDays = (nowTs - timestamp) / (24 * 60 * 60 * 1000);
  if (ageDays <= 1) return "0to1";
  if (ageDays <= 7) return "2to7";
  if (ageDays <= 15) return "8to15";
  if (ageDays <= 30) return "16to30";
  return "31plus";
}

function matchesDeviceLastSeenFilter(item, filterValue, nowTs = Date.now()) {
  const filter = normalizeLastSeenFilter(filterValue);
  if (!filter || filter === "all") return true;
  const bucket = deviceLastSeenBucket(item, nowTs);
  if (filter === "recent7") {
    return bucket === "0to1" || bucket === "2to7";
  }
  return bucket === filter;
}

function deviceDetailsHash(pageKey, udid) {
  const targetPage = DEVICE_DETAILS_PAGES.includes(pageKey) ? pageKey : "devicesDetailAbout";
  const route = PAGE_TO_ROUTE[targetPage] || PAGE_TO_ROUTE.devicesDetailAbout;
  return `#${route}?udid=${encodeURIComponent(String(udid || "").trim())}`;
}

function parseBody(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function readableError(payload, fallback) {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object" && payload.error) return String(payload.error);
  return fallback;
}

function formatTimestamp(ts) {
  if (!ts) return "-";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleString();
}

function csvCell(value) {
  const text = Array.isArray(value) ? value.join(", ") : String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsvFile(filename, rows) {
  const csv = rows.map((row) => row.map(csvCell).join(",")).join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  downloadTextFile(filename, blob);
}

function downloadTextFile(filename, content, type = "text/plain;charset=utf-8") {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatYesNo(value, lang) {
  if (value === true || value === "true" || value === "yes" || value === 1) return lang === "ru" ? "Да" : "Yes";
  if (value === false || value === "false" || value === "no" || value === 0) return lang === "ru" ? "Нет" : "No";
  return "-";
}

function formatListValue(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "-";
  return value || "-";
}

function hasDisplayValue(value) {
  if (Array.isArray(value)) return value.filter((item) => String(item ?? "").trim()).length > 0;
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
}

function extractICCIDFromValue(value, depth = 0) {
  if (depth > 6 || value === null || value === undefined) return "";
  if (typeof value === "string") return "";
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = extractICCIDFromValue(item, depth + 1);
      if (found) return found;
    }
    return "";
  }
  if (typeof value !== "object") return "";
  for (const key of ["ICCID", "Iccid", "iccid"]) {
    const candidate = String(value[key] || "").trim();
    if (candidate) return candidate;
  }
  for (const item of Object.values(value)) {
    const found = extractICCIDFromValue(item, depth + 1);
    if (found) return found;
  }
  return "";
}

function currentDeviceICCID(device) {
  return firstNonEmptyText(
    device?.network?.iccid,
    device?.network?.raw?.ICCID,
    extractICCIDFromValue(device?.network?.raw)
  );
}

function formatBytesValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = n;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function formatPercentValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${Math.round(n * 100)}%`;
}

function formatAttestationStatus(status, lang) {
  const value = String(status || "").trim().toLowerCase();
  const ru = lang === "ru";
  switch (value) {
    case "requested":
      return ru ? "Ожидает ответа" : "Pending";
    case "received":
      return ru ? "Получена" : "Received";
    case "missing":
      return ru ? "Не предоставлена" : "Missing";
    case "invalid":
      return ru ? "Недействительна" : "Invalid";
    case "trusted":
      return ru ? "Доверенная" : "Trusted";
    default:
      return status || "-";
  }
}

function macOSAgentJobTypeLabel(value, lang) {
  const labels = {
    script: ["Скрипт", "Script"],
    package: ["Пакет", "Package"],
    agent_uninstall: ["Удалить Turuk macOS Agent", "Uninstall Turuk macOS Agent"],
    token_rotate: ["Сменить Agent token", "Rotate Agent token"],
    local_admin_create: ["Создать локального администратора", "Create local admin"],
    local_admin_rotate: ["Сменить пароль локального администратора", "Rotate local admin password"],
    local_admin_revoke: ["Отозвать локального администратора", "Revoke local admin"],
    filevault_rotate: ["Сменить ключ FileVault", "Rotate FileVault key"],
    compliance_collect: ["Собрать compliance macOS", "Collect macOS compliance"]
  };
  const pair = labels[String(value || "").trim()] || [String(value || "-"), String(value || "-")];
  return lang === "ru" ? pair[0] : pair[1];
}

function formatRemoteScreenBytes(bytes) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "-";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function secondsSinceTimestamp(ts, now) {
  if (!ts) return null;
  const time = new Date(ts).getTime();
  if (!Number.isFinite(time)) return null;
  return Math.max(0, Math.floor((Number(now || Date.now()) - time) / 1000));
}

function remoteScreenLastFrameLabel(metrics, now, lang) {
  const seconds = secondsSinceTimestamp(metrics?.last_frame_at, now);
  if (seconds == null) return "-";
  if (seconds < 2) return lang === "ru" ? "только что" : "just now";
  return lang === "ru" ? `${seconds} сек. назад` : `${seconds}s ago`;
}

function remoteScreenStatusLabel(item, metrics, now, lang) {
  const status = String(item?.status || "").toLowerCase();
  if (status === "pending") return lang === "ru" ? "ожидание пользователя" : "waiting for user";
  if (status === "active") {
    const lastFrameAge = secondsSinceTimestamp(metrics?.last_frame_at, now);
    if (metrics?.publisher_connected && (lastFrameAge == null || lastFrameAge <= 10)) {
      return lang === "ru" ? "stream подключен" : "stream connected";
    }
    if (lastFrameAge != null && lastFrameAge > 15) {
      return lang === "ru" ? "stream оборван" : "stream interrupted";
    }
    return lang === "ru" ? "пользователь подтвердил" : "user approved";
  }
  if (status === "ended") return lang === "ru" ? "завершен" : "ended";
  if (status === "revoked") return lang === "ru" ? "отозван" : "revoked";
  if (status === "rejected") return lang === "ru" ? "отклонен" : "rejected";
  if (status === "expired") return lang === "ru" ? "истек" : "expired";
  return status || "-";
}

function mdmAPNSExpiryMeta(config) {
  const raw = config?.cert_not_after;
  if (!raw) return null;
  const expiresAt = new Date(raw);
  if (Number.isNaN(expiresAt.getTime())) return null;
  const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / 86400000);
  return {
    daysLeft,
    expired: daysLeft < 0,
    expiresSoon: daysLeft >= 0 && daysLeft <= 30
  };
}

function mdmAPNSStatusMeta(config, t) {
  const expiry = mdmAPNSExpiryMeta(config);
  if (!config?.configured) {
    return {
      className: "not-configured",
      label: t("certificatesAPNSNotConfigured"),
      detail: t("certificatesAPNSCSRNotReady")
    };
  }
  if (!config?.enabled) {
    return {
      className: "disabled",
      label: t("certificatesAPNSStatusDisabled"),
      detail: t("certificatesAPNSConfigured")
    };
  }
  if (expiry?.expired) {
    return {
      className: "danger",
      label: t("certificatesAPNSStatusExpired"),
      detail: `${t("certificatesAPNSExpires")}: ${formatTimestamp(config.cert_not_after)}`
    };
  }
  if (expiry?.expiresSoon) {
    return {
      className: "warning",
      label: t("certificatesAPNSStatusExpiresSoon"),
      detail: `${t("certificatesAPNSDaysLeft")}: ${expiry.daysLeft}`
    };
  }
  return {
    className: "ready",
    label: t("certificatesAPNSStatusReady"),
    detail: `${t("certificatesAPNSExpires")}: ${formatTimestamp(config.cert_not_after)}`
  };
}

function mdmAPNSSourceLabel(config, t) {
  if (String(config?.source || "").toLowerCase() === "global") {
    return t("certificatesAPNSSourceGlobal");
  }
  return t("certificatesAPNSSourceTenant");
}

function formatJSONBlock(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function agentSupportStatusLabel(status, t) {
  switch (String(status || "open")) {
    case "in_progress":
      return t("agentSettingsTicketStatusInProgress");
    case "closed":
      return t("agentSettingsTicketStatusClosed");
    case "open":
    default:
      return t("agentSettingsTicketStatusOpen");
  }
}

function agentSupportCategoryLabel(category, t) {
  switch (String(category || "general")) {
    case "apps":
      return t("supportCategoryApps");
    case "profiles":
    case "profile":
      return t("supportCategoryProfiles");
    case "device":
      return t("supportCategoryDevice");
    case "access":
      return t("supportCategoryAccess");
    case "other":
      return t("supportCategoryOther");
    case "general":
    default:
      return t("supportCategoryGeneral");
  }
}

function agentSupportEventLabel(eventType, t) {
  switch (String(eventType || "")) {
    case "reply":
      return t("supportEventReply");
    case "status_changed":
      return t("supportEventStatusChanged");
    case "created":
    default:
      return t("supportEventCreated");
  }
}

function formatCoordinate(value, digits = 6) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return numeric.toFixed(digits);
}

function buildDeviceLocationMapLinks(latitude, longitude) {
  const lat = Number(latitude);
  const lon = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return {
      embedURL: "",
      osmURL: "",
      googleURL: ""
    };
  }
  const delta = 0.012;
  const minLat = lat - delta;
  const maxLat = lat + delta;
  const minLon = lon - delta;
  const maxLon = lon + delta;
  const embedURL = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(
    `${minLon},${minLat},${maxLon},${maxLat}`
  )}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lon}`)}`;
  const osmURL = `https://www.openstreetmap.org/?mlat=${encodeURIComponent(lat)}&mlon=${encodeURIComponent(
    lon
  )}#map=16/${encodeURIComponent(lat)}/${encodeURIComponent(lon)}`;
  const googleURL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lon}`)}`;
  return {
    embedURL,
    osmURL,
    googleURL
  };
}

function parseAccountMeta(account) {
  const fallback = {
    accountName: String(account?.name || "").trim(),
    firstName: "",
    secondName: "",
    department: "",
    email: String(account?.email || "").trim()
  };
  if (!account || typeof account !== "object") {
    return fallback;
  }

  let meta = {};
  if (typeof account.notes === "string" && account.notes.trim()) {
    try {
      const parsed = JSON.parse(account.notes);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        meta = parsed;
      }
    } catch {
      meta = {};
    }
  }

  let firstName = String(meta.first_name || meta.firstName || "").trim();
  let secondName = String(meta.second_name || meta.secondName || meta.last_name || meta.lastName || "").trim();
  const department = String(meta.department || account.external_id || "").trim();

  if (!firstName && !secondName) {
    const name = String(account.name || "").trim();
    if (name) {
      const chunks = name.split(/\s+/);
      firstName = chunks.shift() || "";
      secondName = chunks.join(" ");
    }
  }

  return {
    accountName: String(account.name || "").trim(),
    firstName,
    secondName,
    department,
    email: String(account.email || "").trim()
  };
}

function buildAccountNotesPayload({ firstName = "", secondName = "", department = "" }) {
  return JSON.stringify({
    first_name: String(firstName || "").trim(),
    second_name: String(secondName || "").trim(),
    department: String(department || "").trim()
  });
}

function createEmptyAccountEditorForm() {
  return {
    id: "",
    accountName: "",
    firstName: "",
    secondName: "",
    email: "",
    department: "",
    password: "",
    isActive: true
  };
}

function buildAccountEditorForm(account) {
  if (!account || typeof account !== "object") {
    return createEmptyAccountEditorForm();
  }
  const meta = parseAccountMeta(account);
  return {
    id: String(account.id || "").trim(),
    accountName: String(account.name || "").trim(),
    firstName: meta.firstName,
    secondName: meta.secondName,
    email: meta.email,
    department: meta.department,
    password: "",
    isActive: account.is_active !== false
  };
}

function parseDelimitedRows(text) {
  const raw = String(text || "").replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  let delimiter = ",";

  const firstLine = raw.split(/\r?\n/, 1)[0] || "";
  const delimiterCounts = [
    [",", (firstLine.match(/,/g) || []).length],
    [";", (firstLine.match(/;/g) || []).length],
    ["\t", (firstLine.match(/\t/g) || []).length]
  ];
  delimiter = delimiterCounts.sort((a, b) => b[1] - a[1])[0]?.[0] || ",";

  for (let i = 0; i < raw.length; i += 1) {
    const char = raw[i];
    const next = raw[i + 1];
    if (char === '"' && inQuotes && next === '"') {
      cell += '"';
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (char === delimiter && !inQuotes) {
      row.push(cell.trim());
      cell = "";
      continue;
    }
    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(cell.trim());
      cell = "";
      if (row.some((value) => value !== "")) rows.push(row);
      row = [];
      continue;
    }
    cell += char;
  }

  row.push(cell.trim());
  if (row.some((value) => value !== "")) rows.push(row);
  return rows;
}

function normalizeAccountImportHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function accountImportFieldFromHeader(value) {
  switch (normalizeAccountImportHeader(value)) {
    case "login":
    case "account":
    case "accountname":
    case "name":
    case "логин":
    case "аккаунт":
      return "accountName";
    case "email":
    case "mail":
    case "почта":
      return "email";
    case "password":
    case "pass":
    case "пароль":
      return "password";
    case "firstname":
    case "first":
    case "givenname":
    case "имя":
      return "firstName";
    case "lastname":
    case "secondname":
    case "surname":
    case "familyname":
    case "фамилия":
      return "secondName";
    case "department":
    case "dept":
    case "отдел":
      return "department";
    default:
      return "";
  }
}

function parseAccountImportText(text) {
  const rows = parseDelimitedRows(text);
  if (rows.length === 0) return [];
  const defaultOrder = ["accountName", "email", "password", "firstName", "secondName", "department"];
  const headerFields = rows[0].map(accountImportFieldFromHeader);
  const hasHeader = headerFields.filter(Boolean).length >= 2;
  const fields = hasHeader ? headerFields : defaultOrder;
  const dataRows = hasHeader ? rows.slice(1) : rows;

  return dataRows
    .map((row, index) => {
      const item = {
        rowNumber: index + (hasHeader ? 2 : 1),
        accountName: "",
        email: "",
        password: "",
        firstName: "",
        secondName: "",
        department: ""
      };
      row.forEach((value, valueIndex) => {
        const field = fields[valueIndex];
        if (field && Object.prototype.hasOwnProperty.call(item, field)) {
          item[field] = String(value || "").trim();
        }
      });
      return item;
    })
    .filter((item) => item.accountName || item.email || item.password || item.firstName || item.secondName || item.department);
}

function slugify(input) {
  return String(input || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function randomHex(len = 8) {
  const alphabet = "0123456789abcdef";
  let out = "";
  for (let i = 0; i < len; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

function randomUUIDLike() {
  return `${randomHex(8)}-${randomHex(4)}-${randomHex(4)}-${randomHex(4)}-${randomHex(12)}`;
}

function buildUniqueProfileIdentifier(profileName, usedIdentifiers = []) {
  const base = `pro.turuk.profile.${slugify(profileName) || "item"}`;
  const used = new Set(
    (Array.isArray(usedIdentifiers) ? usedIdentifiers : [])
      .map((item) => String(item || "").trim().toLowerCase())
      .filter(Boolean)
  );

  if (!used.has(base.toLowerCase())) return base;

  for (let attempt = 2; attempt < 1000; attempt += 1) {
    const candidate = `${base}.${attempt}`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  return `${base}.${Date.now()}`;
}

function xmlEscape(input) {
  return String(input || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function xmlUnescape(input) {
  return String(input || "")
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&");
}

function decodeBase64(value) {
  if (!value) return "";
  try {
    const binary = atob(value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return "";
  }
}

function escapeRegex(input) {
  return String(input || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractXmlString(block, key) {
  const re = new RegExp(`<key>${escapeRegex(key)}<\\/key>\\s*<string>([\\s\\S]*?)<\\/string>`, "i");
  const match = String(block || "").match(re);
  return match ? xmlUnescape(match[1].trim()) : "";
}

function extractXmlData(block, key) {
  const re = new RegExp(`<key>${escapeRegex(key)}<\\/key>\\s*<data>([\\s\\S]*?)<\\/data>`, "i");
  const match = String(block || "").match(re);
  return match ? String(match[1]).trim() : "";
}

function extractXmlArrayStrings(block, key) {
  const re = new RegExp(`<key>${escapeRegex(key)}<\\/key>\\s*<array>([\\s\\S]*?)<\\/array>`, "i");
  const match = String(block || "").match(re);
  if (!match) return "";
  const inner = match[1];
  const values = [...String(inner).matchAll(/<string>([\s\S]*?)<\/string>/gi)]
    .map((item) => xmlUnescape(String(item[1] || "").trim()))
    .filter(Boolean);
  return values.join("\n");
}

function extractXmlBool(block, key, fallback = false) {
  const re = new RegExp(`<key>${escapeRegex(key)}<\\/key>\\s*<(true|false)\\/>`, "i");
  const match = String(block || "").match(re);
  if (!match) return fallback;
  return String(match[1]).toLowerCase() === "true";
}

function hasXmlKey(block, key) {
  return new RegExp(`<key>${escapeRegex(key)}<\\/key>`, "i").test(String(block || ""));
}

function extractXmlInteger(block, key, fallback = 0) {
  const re = new RegExp(`<key>${escapeRegex(key)}<\\/key>\\s*<integer>(-?\\d+)<\\/integer>`, "i");
  const match = String(block || "").match(re);
  if (!match) return fallback;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function xmlElementChildren(node) {
  if (!node) return [];
  return Array.from(node.childNodes || []).filter((child) => child.nodeType === 1);
}

function parsePlistNode(node) {
  if (!node) return null;
  const tag = String(node.tagName || "").toLowerCase();
  if (tag === "string") return String(node.textContent || "");
  if (tag === "integer") {
    const parsed = Number.parseInt(String(node.textContent || "0"), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (tag === "real") {
    const parsed = Number.parseFloat(String(node.textContent || "0"));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (tag === "true") return true;
  if (tag === "false") return false;
  if (tag === "data") return String(node.textContent || "").trim();
  if (tag === "date") return String(node.textContent || "").trim();
  if (tag === "array") {
    return xmlElementChildren(node).map((child) => parsePlistNode(child));
  }
  if (tag === "dict") {
    const out = {};
    const children = xmlElementChildren(node);
    for (let idx = 0; idx < children.length; idx += 1) {
      const keyNode = children[idx];
      if (String(keyNode.tagName || "").toLowerCase() !== "key") continue;
      const valueNode = children[idx + 1];
      const key = String(keyNode.textContent || "").trim();
      if (!key || !valueNode) continue;
      out[key] = parsePlistNode(valueNode);
      idx += 1;
    }
    return out;
  }
  return String(node.textContent || "");
}

function parsePlistDictBlock(block) {
  if (typeof DOMParser === "undefined") return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<?xml version="1.0"?><plist version="1.0">${block}</plist>`, "application/xml");
    if (doc.querySelector("parsererror")) return null;
    const dictNode = doc.querySelector("plist > dict");
    const parsed = parsePlistNode(dictNode);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function serializeXmlNode(node) {
  if (!node) return "";
  try {
    if (typeof XMLSerializer !== "undefined") {
      return new XMLSerializer().serializeToString(node);
    }
  } catch {
    // Fall through to text fallback.
  }
  return String(node.outerHTML || "");
}

function extractProfilePayloadDictBlocksByType(xml, payloadTypes = []) {
  if (!xml || typeof DOMParser === "undefined") return [];
  const wanted = new Set(
    (Array.isArray(payloadTypes) ? payloadTypes : [payloadTypes])
      .map((item) => String(item || "").trim())
      .filter(Boolean)
  );
  if (wanted.size === 0) return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, "application/xml");
    if (doc.querySelector("parsererror")) return [];
    const root = parsePlistNode(doc.querySelector("plist > dict"));
    const payloadContent = Array.isArray(root?.PayloadContent) ? root.PayloadContent : [];
    const payloadDictNodes = Array.from(doc.querySelectorAll("plist > dict > array > dict"));
    return payloadContent
      .map((payloadItem, index) => {
        const payloadType = String(payloadItem?.PayloadType || "").trim();
        if (!wanted.has(payloadType)) return "";
        return serializeXmlNode(payloadDictNodes[index]);
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

function parseJsonField(rawValue, expectedType, fieldLabel, sectionLabel) {
  const text = String(rawValue ?? "").trim();
  if (!text) return null;
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new Error(`${sectionLabel}: invalid JSON in "${fieldLabel}"`);
  }
  if (expectedType === "dict" && (!parsed || Array.isArray(parsed) || typeof parsed !== "object")) {
    throw new Error(`${sectionLabel}: "${fieldLabel}" must be a JSON object`);
  }
  if (expectedType === "array" && !Array.isArray(parsed)) {
    throw new Error(`${sectionLabel}: "${fieldLabel}" must be a JSON array`);
  }
  return parsed;
}

function buildPlistValueXml(value, indent = "  ", forceType = "") {
  if (forceType === "data") {
    return `${indent}<data>${xmlEscape(String(value || "").trim())}</data>`;
  }
  if (value === true) return `${indent}<true/>`;
  if (value === false) return `${indent}<false/>`;
  if (Number.isFinite(value) && typeof value === "number") {
    if (Number.isInteger(value)) return `${indent}<integer>${value}</integer>`;
    return `${indent}<real>${value}</real>`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return `${indent}<array/>`;
    const lines = [`${indent}<array>`];
    value.forEach((entry) => {
      lines.push(buildPlistValueXml(entry, `${indent}  `));
    });
    lines.push(`${indent}</array>`);
    return lines.join("\n");
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value).filter(([, current]) => current !== undefined);
    if (entries.length === 0) return `${indent}<dict/>`;
    const lines = [`${indent}<dict>`];
    entries.forEach(([key, current]) => {
      lines.push(`${indent}  <key>${xmlEscape(key)}</key>`);
      lines.push(buildPlistValueXml(current, `${indent}  `));
    });
    lines.push(`${indent}</dict>`);
    return lines.join("\n");
  }
  return `${indent}<string>${xmlEscape(String(value ?? ""))}</string>`;
}

function parseSubjectPairs(subjectText) {
  const tokens = String(subjectText || "")
    .split(/\r?\n|,/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (tokens.length === 0) return [];
  const pairs = tokens
    .map((token) => {
      const separator = token.indexOf("=");
      if (separator <= 0) return ["CN", token];
      return [token.slice(0, separator).trim(), token.slice(separator + 1).trim()];
    })
    .filter(([name, value]) => name && value);
  return pairs;
}

function parseSubjectAltNameLines(rawValue) {
  const lines = String(rawValue || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  const out = {
    dNSName: [],
    rfc822Name: [],
    uniformResourceIdentifier: [],
    ntPrincipalName: []
  };
  lines.forEach((line) => {
    const separator = line.indexOf(":");
    if (separator <= 0) return;
    const key = line.slice(0, separator).trim().toUpperCase();
    const value = line.slice(separator + 1).trim();
    if (!value) return;
    if (key === "DNS") out.dNSName.push(value);
    else if (key === "EMAIL" || key === "RFC822") out.rfc822Name.push(value);
    else if (key === "URI") out.uniformResourceIdentifier.push(value);
    else if (key === "NT" || key === "UPN") out.ntPrincipalName.push(value);
  });
  Object.keys(out).forEach((key) => {
    if (!out[key].length) delete out[key];
  });
  return out;
}

function encodeBase64(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function bytesToBase64(bytes) {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function wrapPEM(base64Value) {
  return String(base64Value || "").replace(/.{1,64}/g, "$&\n").trim();
}

function certificatePEMFromBytes(bytes) {
  const text = new TextDecoder().decode(bytes).trim();
  if (text.includes("-----BEGIN CERTIFICATE-----")) {
    return text;
  }
  return `-----BEGIN CERTIFICATE-----\n${wrapPEM(bytesToBase64(bytes))}\n-----END CERTIFICATE-----`;
}

function createWiFiPayloadDraft() {
  return {
    id: randomHex(12),
    ssid: "",
    hiddenNetwork: false,
    autoJoin: true,
    captiveBypass: false,
    disableAssociationMACRandomization: false,
    proxyType: "None",
    securityType: "None",
    networkType: "Standard",
    fastLaneQosMarking: "Do not restrict QoS marking",
    password: ""
  };
}

const THIRD_PARTY_VPN_SUBTYPES = {
  CiscoAnyConnect: "com.cisco.anyconnect",
  CiscoLegacyAnyConnect: "com.cisco.anyconnect.applevpn.plugin",
  JuniperSSL: "net.juniper.sslvpn",
  PulseSecure: "net.pulsesecure.PulseSecure.vpnplugin",
  F5SSL: "com.f5.access.ios",
  SonicWALL: "com.sonicwall.SonicWALL-Mobile-Connect",
  ArubaVIA: "com.arubanetworks.arubavia",
  CheckPointMobileVPN: "com.checkpoint.CheckPoint-VPN",
  CustomSSL: ""
};

function isThirdPartyVPNType(vpnType) {
  return Object.prototype.hasOwnProperty.call(THIRD_PARTY_VPN_SUBTYPES, String(vpnType || ""));
}

function buildVPNSectionPayloadEntries(payloadItem) {
  const lines = [];
  const vpnType = String(payloadItem?.vpnType || "L2TP").trim() || "L2TP";
  const isThirdPartyVPN = isThirdPartyVPNType(vpnType);
  const profileVPNType = isThirdPartyVPN ? "VPN" : vpnType;
  const connectionName = String(payloadItem?.userDefinedName || "").trim();
  const server = String(payloadItem?.remoteAddress || "").trim();
  const account = String(payloadItem?.authName || "").trim();
  const userAuthenticationType = String(payloadItem?.userAuthenticationType || "Password").trim() || "Password";
  const machineAuthenticationType = String(payloadItem?.machineAuthenticationType || "SharedSecret").trim() || "SharedSecret";
  const sharedSecret = String(payloadItem?.sharedSecret || "").trim();
  const certificateUUID = String(payloadItem?.payloadCertificateUUID || "").trim();
  const proxyType = String(payloadItem?.proxyType || "None").trim() || "None";
  const vpnSubType =
    String(payloadItem?.vpnSubType || "").trim() || String(THIRD_PARTY_VPN_SUBTYPES[vpnType] || "").trim();

  if (connectionName) {
    lines.push(`  <key>UserDefinedName</key><string>${xmlEscape(connectionName)}</string>`);
  }
  lines.push(`  <key>VPNType</key><string>${xmlEscape(profileVPNType)}</string>`);
  if (isThirdPartyVPN && vpnSubType) {
    lines.push(`  <key>VPNSubType</key><string>${xmlEscape(vpnSubType)}</string>`);
  }

  const ipsec = {};
  if (server) ipsec.RemoteAddress = server;
  if (account) ipsec.AuthName = account;
  if (machineAuthenticationType === "Certificate") {
    ipsec.AuthenticationMethod = "Certificate";
    if (certificateUUID) ipsec.PayloadCertificateUUID = certificateUUID;
  } else if (machineAuthenticationType === "None") {
    ipsec.AuthenticationMethod = "None";
  } else {
    ipsec.AuthenticationMethod = "SharedSecret";
    if (sharedSecret) ipsec.SharedSecret = sharedSecret;
  }
  if (vpnType === "IPSec" || isThirdPartyVPN) {
    if (userAuthenticationType === "Password" && payloadItem?.authPassword) {
      ipsec.AuthPassword = String(payloadItem.authPassword);
    } else if (userAuthenticationType === "RSASecurID") {
      ipsec.TokenCard = true;
    }
    if (Boolean(payloadItem?.sendAllTraffic)) {
      ipsec.OverridePrimary = 1;
    }
  }

  if (vpnType === "IKEv2") {
    const ikev2 = {};
    if (server) ikev2.RemoteAddress = server;
    if (account) ikev2.AuthName = account;
    if (machineAuthenticationType === "Certificate") {
      ikev2.AuthenticationMethod = "Certificate";
      if (certificateUUID) ikev2.PayloadCertificateUUID = certificateUUID;
    } else if (machineAuthenticationType === "None") {
      ikev2.AuthenticationMethod = "None";
    } else {
      ikev2.AuthenticationMethod = "SharedSecret";
      if (sharedSecret) ikev2.SharedSecret = sharedSecret;
    }
    if (userAuthenticationType === "Password" && payloadItem?.authPassword) {
      ikev2.AuthPassword = String(payloadItem.authPassword);
    } else if (userAuthenticationType === "RSASecurID") {
      ikev2.TokenCard = true;
    }
    if (Boolean(payloadItem?.sendAllTraffic)) {
      ikev2.OverridePrimary = 1;
    }
    lines.push("  <key>IKEv2</key>");
    lines.push(buildPlistValueXml(ikev2, "  "));
  } else if (vpnType === "PPTP") {
    const ppp = {};
    if (server) ppp.RemoteAddress = server;
    if (account) ppp.AuthName = account;
    if (userAuthenticationType === "Password" && payloadItem?.authPassword) {
      ppp.AuthPassword = String(payloadItem.authPassword);
    } else if (userAuthenticationType === "RSASecurID") {
      ppp.TokenCard = true;
    }
    if (Boolean(payloadItem?.sendAllTraffic)) {
      ppp.OverridePrimary = 1;
    }
    lines.push("  <key>PPP</key>");
    lines.push(buildPlistValueXml(ppp, "  "));
  } else {
    lines.push("  <key>IPSec</key>");
    lines.push(buildPlistValueXml(ipsec, "  "));
  }

  if (vpnType === "L2TP") {
    const ppp = {};
    if (account) ppp.AuthName = account;
    if (userAuthenticationType === "Password" && payloadItem?.authPassword) {
      ppp.AuthPassword = String(payloadItem.authPassword);
    } else if (userAuthenticationType === "RSASecurID") {
      ppp.TokenCard = true;
    }
    if (Boolean(payloadItem?.sendAllTraffic)) {
      ppp.OverridePrimary = 1;
    }
    if (Object.keys(ppp).length > 0) {
      lines.push("  <key>PPP</key>");
      lines.push(buildPlistValueXml(ppp, "  "));
    }
  }

  if (proxyType === "Manual" && payloadItem?.proxyServer) {
    const proxyPort = Number.parseInt(String(payloadItem?.proxyServerPort || "0"), 10);
    const proxies = {
      HTTPEnable: 1,
      HTTPSEnable: 1,
      HTTPProxy: String(payloadItem.proxyServer),
      HTTPSProxy: String(payloadItem.proxyServer)
    };
    if (Number.isFinite(proxyPort) && proxyPort > 0) {
      proxies.HTTPPort = proxyPort;
      proxies.HTTPSPort = proxyPort;
    }
    lines.push("  <key>Proxies</key>");
    lines.push(buildPlistValueXml(proxies, "  "));
  } else if (proxyType === "Auto" && payloadItem?.proxyPACURL) {
    const proxies = {
      ProxyAutoConfigEnable: 1,
      ProxyAutoConfigURLString: String(payloadItem.proxyPACURL)
    };
    lines.push("  <key>Proxies</key>");
    lines.push(buildPlistValueXml(proxies, "  "));
  }

  return lines;
}

function buildSCEPSectionPayloadEntries(payloadItem) {
  const lines = [];
  const simpleStringKeys = ["URL", "Name", "Challenge"];
  simpleStringKeys.forEach((plistKey) => {
    const formKey = plistKey === "URL" ? "url" : plistKey === "Name" ? "name" : "challenge";
    const value = String(payloadItem?.[formKey] || "").trim();
    if (!value) return;
    lines.push(`  <key>${xmlEscape(plistKey)}</key><string>${xmlEscape(value)}</string>`);
  });

  if (payloadItem?.keyType) {
    lines.push(`  <key>KeyType</key><string>${xmlEscape(String(payloadItem.keyType))}</string>`);
  }
  const derivedKeyUsage =
    (payloadItem?.useAsDigitalSignature ? 1 : 0) |
    (payloadItem?.useForKeyEncipherment ? 4 : 0);
  ["keysize", "keyUsage", "retries", "retryDelay", "certificateRenewalTimeInterval"].forEach((formKey) => {
    const plistMap = {
      keysize: "Keysize",
      keyUsage: "KeyUsage",
      retries: "Retries",
      retryDelay: "RetryDelay",
      certificateRenewalTimeInterval: "CertificateRenewalTimeInterval"
    };
    const raw = formKey === "keyUsage" && derivedKeyUsage > 0 ? derivedKeyUsage : payloadItem?.[formKey];
    if (raw === "" || raw === undefined || raw === null) return;
    const parsed = Number.parseInt(String(raw), 10);
    if (!Number.isFinite(parsed)) return;
    lines.push(`  <key>${plistMap[formKey]}</key><integer>${parsed}</integer>`);
  });

  lines.push(`  <key>AllowAllAppsAccess</key>${xmlBoolTag(Boolean(payloadItem?.allowAllAppsAccess))}`);
  lines.push(`  <key>KeyIsExtractable</key>${xmlBoolTag(Boolean(payloadItem?.keyIsExtractable))}`);

  if (payloadItem?.caFingerprintData) {
    lines.push(`  <key>CAFingerprint</key><data>${xmlEscape(String(payloadItem.caFingerprintData).trim())}</data>`);
  } else if (payloadItem?.caFingerprint) {
    lines.push(`  <key>CAFingerprint</key><string>${xmlEscape(String(payloadItem.caFingerprint).trim())}</string>`);
  }

  let subjectAdvanced = null;
  if (payloadItem?.subjectAdvanced) {
    subjectAdvanced = parseJsonField(payloadItem.subjectAdvanced, "array", "Subject advanced (JSON array)", "SCEP");
  }
  const subjectPairs = parseSubjectPairs(payloadItem?.subject);
  if (Array.isArray(subjectAdvanced) && subjectAdvanced.length > 0) {
    lines.push("  <key>Subject</key>");
    lines.push(buildPlistValueXml(subjectAdvanced, "  "));
  } else if (subjectPairs.length > 0) {
    const subjectArray = subjectPairs.map(([name, value]) => [name, value]);
    lines.push("  <key>Subject</key>");
    lines.push(buildPlistValueXml(subjectArray, "  "));
  }

  let subjectAltAdvanced = null;
  if (payloadItem?.subjectAltNameAdvanced) {
    subjectAltAdvanced = parseJsonField(
      payloadItem.subjectAltNameAdvanced,
      "dict",
      "SubjectAltName advanced (JSON)",
      "SCEP"
    );
  }
  const subjectAltFromLines = parseSubjectAltNameLines(payloadItem?.subjectAltName);
  const subjectAlt = subjectAltAdvanced || subjectAltFromLines;
  if (subjectAlt && Object.keys(subjectAlt).length > 0) {
    lines.push("  <key>SubjectAltName</key>");
    lines.push(buildPlistValueXml(subjectAlt, "  "));
  }

  let extendedAdvanced = null;
  if (payloadItem?.extendedKeyUsageAdvanced) {
    extendedAdvanced = parseJsonField(
      payloadItem.extendedKeyUsageAdvanced,
      "array",
      "Extended key usage advanced (JSON array)",
      "SCEP"
    );
  }
  if (Array.isArray(extendedAdvanced) && extendedAdvanced.length > 0) {
    lines.push("  <key>ExtendedKeyUsage</key>");
    lines.push(buildPlistValueXml(extendedAdvanced, "  "));
  } else {
    const extended = String(payloadItem?.extendedKeyUsage || "")
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (extended.length > 0) {
      lines.push("  <key>ExtendedKeyUsage</key>");
      lines.push(buildPlistValueXml(extended, "  "));
    }
  }

  return lines;
}

function buildMailSectionPayloadEntries(payloadItem) {
  const lines = [];
  const topStringKeys = [
    ["EmailAccountDescription", "emailAccountDescription"],
    ["EmailAccountName", "emailAccountName"],
    ["EmailAddress", "emailAddress"],
    ["IncomingMailServerHostName", "incomingHost"],
    ["IncomingMailServerUsername", "incomingUsername"],
    ["IncomingPassword", "incomingPassword"],
    ["IncomingMailServerAuthentication", "incomingAuth"],
    ["IncomingMailServerIMAPPathPrefix", "incomingImapPath"],
    ["OutgoingMailServerHostName", "outgoingHost"],
    ["OutgoingMailServerUsername", "outgoingUsername"],
    ["OutgoingPassword", "outgoingPassword"],
    ["OutgoingMailServerAuthentication", "outgoingAuth"]
  ];
  topStringKeys.forEach(([plistKey, formKey]) => {
    const value = String(payloadItem?.[formKey] || "").trim();
    if (!value) return;
    lines.push(`  <key>${xmlEscape(plistKey)}</key><string>${xmlEscape(value)}</string>`);
  });

  const incomingPort = Number.parseInt(String(payloadItem?.incomingPort || ""), 10);
  if (Number.isFinite(incomingPort)) {
    lines.push(`  <key>IncomingMailServerPortNumber</key><integer>${incomingPort}</integer>`);
  }
  const outgoingPort = Number.parseInt(String(payloadItem?.outgoingPort || ""), 10);
  if (Number.isFinite(outgoingPort)) {
    lines.push(`  <key>OutgoingMailServerPortNumber</key><integer>${outgoingPort}</integer>`);
  }

  lines.push(`  <key>IncomingMailServerUseSSL</key>${xmlBoolTag(Boolean(payloadItem?.incomingUseSSL))}`);
  lines.push(`  <key>OutgoingMailServerUseSSL</key>${xmlBoolTag(Boolean(payloadItem?.outgoingUseSSL))}`);
  lines.push(
    `  <key>OutgoingPasswordSameAsIncomingPassword</key>${xmlBoolTag(Boolean(payloadItem?.outgoingPasswordSameAsIncoming))}`
  );
  lines.push(`  <key>PreventAppSheet</key>${xmlBoolTag(Boolean(payloadItem?.preventAppSheet))}`);
  lines.push(`  <key>PreventMove</key>${xmlBoolTag(Boolean(payloadItem?.preventMove))}`);
  lines.push(`  <key>allowMailDrop</key>${xmlBoolTag(Boolean(payloadItem?.allowMailDrop))}`);
  lines.push(
    `  <key>disableMailRecentsSyncing</key>${xmlBoolTag(Boolean(payloadItem?.disableMailRecentsSyncing))}`
  );
  const smimeEnabled = Boolean(
    payloadItem?.smimeEnabled ||
    payloadItem?.smimeSigningEnabled ||
    payloadItem?.smimeEncryptByDefault ||
    payloadItem?.smimeSigningUserOverrideable ||
    payloadItem?.smimeEncryptionUserOverrideable ||
    payloadItem?.smimeEnablePerMessageSwitch
  );
  lines.push(`  <key>SMIMEEnabled</key>${xmlBoolTag(smimeEnabled)}`);
  lines.push(`  <key>SMIMEEncryptByDefault</key>${xmlBoolTag(Boolean(payloadItem?.smimeEncryptByDefault))}`);
  lines.push(`  <key>SMIMESigningEnabled</key>${xmlBoolTag(Boolean(payloadItem?.smimeSigningEnabled))}`);
  lines.push(`  <key>SMIMESigningUserOverrideable</key>${xmlBoolTag(Boolean(payloadItem?.smimeSigningUserOverrideable))}`);
  lines.push(`  <key>SMIMEEncryptionUserOverrideable</key>${xmlBoolTag(Boolean(payloadItem?.smimeEncryptionUserOverrideable))}`);
  lines.push(`  <key>SMIMEEnablePerMessageSwitch</key>${xmlBoolTag(Boolean(payloadItem?.smimeEnablePerMessageSwitch))}`);

  if (payloadItem?.smimeSigningCertificateUUID) {
    lines.push(
      `  <key>SMIMESigningCertificateUUID</key><string>${xmlEscape(String(payloadItem.smimeSigningCertificateUUID))}</string>`
    );
  }
  if (payloadItem?.smimeEncryptionCertificateUUID) {
    lines.push(
      `  <key>SMIMEEncryptionCertificateUUID</key><string>${xmlEscape(String(payloadItem.smimeEncryptionCertificateUUID))}</string>`
    );
  }

  if (payloadItem?.incomingServerAdvanced) {
    const incomingAdvanced = parseJsonField(
      payloadItem.incomingServerAdvanced,
      "dict",
      "Incoming server advanced (JSON)",
      "Mail"
    );
    if (incomingAdvanced && Object.keys(incomingAdvanced).length > 0) {
      lines.push("  <key>IncomingMailServer</key>");
      lines.push(buildPlistValueXml(incomingAdvanced, "  "));
    }
  }
  if (payloadItem?.outgoingServerAdvanced) {
    const outgoingAdvanced = parseJsonField(
      payloadItem.outgoingServerAdvanced,
      "dict",
      "Outgoing server advanced (JSON)",
      "Mail"
    );
    if (outgoingAdvanced && Object.keys(outgoingAdvanced).length > 0) {
      lines.push("  <key>OutgoingMailServer</key>");
      lines.push(buildPlistValueXml(outgoingAdvanced, "  "));
    }
  }
  if (payloadItem?.smimeConfiguration) {
    const smimeAdvanced = parseJsonField(payloadItem.smimeConfiguration, "dict", "S/MIME advanced (JSON)", "Mail");
    if (smimeAdvanced && Object.keys(smimeAdvanced).length > 0) {
      lines.push("  <key>SMIME</key>");
      lines.push(buildPlistValueXml(smimeAdvanced, "  "));
    }
  }

  return lines;
}

function buildSectionPayloadXmlEntry(profileIdentifier, sectionKey, payloadItem, index) {
  const schema = APPLE_PROFILE_SECTION_SCHEMAS[sectionKey];
  if (!schema) return "";
  const payloadUUID = randomUUIDLike().toUpperCase();
  const payloadIdentifier = `${profileIdentifier}.${sectionKey}.${index + 1}`;
  const sectionLabel = PROFILE_BUILDER_SECTIONS.find((item) => item.key === sectionKey)?.label || sectionKey;
  const payloadDisplayName =
    sectionKey === "exchange_active_sync" && String(payloadItem?.accountName || "").trim()
      ? String(payloadItem.accountName).trim()
      : sectionLabel;
  const sectionPayloadType =
    sectionKey === "certificates"
      ? String(payloadItem?.certificatePayloadType || schema.payloadType || "com.apple.security.pkcs1")
      : schema.payloadType;

  const lines = [
    "<dict>",
    `  <key>PayloadDescription</key><string>${xmlEscape(sectionLabel)}</string>`,
    `  <key>PayloadDisplayName</key><string>${xmlEscape(payloadDisplayName)}</string>`,
    `  <key>PayloadIdentifier</key><string>${xmlEscape(payloadIdentifier)}</string>`,
    `  <key>PayloadType</key><string>${xmlEscape(sectionPayloadType)}</string>`,
    `  <key>PayloadUUID</key><string>${payloadUUID}</string>`,
    "  <key>PayloadVersion</key><integer>1</integer>"
  ];

  if (sectionKey === "vpn") {
    lines.push(...buildVPNSectionPayloadEntries(payloadItem));
    lines.push("</dict>");
    return lines.join("\n");
  }
  if (sectionKey === "scep") {
    lines.push(...buildSCEPSectionPayloadEntries(payloadItem));
    lines.push("</dict>");
    return lines.join("\n");
  }
  if (sectionKey === "mail") {
    lines.push(...buildMailSectionPayloadEntries(payloadItem));
    lines.push("</dict>");
    return lines.join("\n");
  }
  if (sectionKey === "airplay") {
    const passwordEntries = normalizeAirPlayPasswordEntries(payloadItem?.airplayPasswords);
    if (passwordEntries.length > 0) {
      lines.push("  <key>Passwords</key>");
      lines.push("  <array>");
      passwordEntries.forEach((entry) => {
        lines.push("    <dict>");
        lines.push(`      <key>DeviceName</key><string>${xmlEscape(entry.deviceName)}</string>`);
        if (entry.password) {
          lines.push(`      <key>Password</key><string>${xmlEscape(entry.password)}</string>`);
        }
        lines.push("    </dict>");
      });
      lines.push("  </array>");
    }

    const allowedEntries = sectionArrayItems(payloadItem?.allowedDestinations);
    if (allowedEntries.length > 0) {
      lines.push("  <key>Whitelist</key>");
      lines.push("  <array>");
      allowedEntries.forEach((entry) => {
        lines.push(`    <string>${xmlEscape(entry)}</string>`);
      });
      lines.push("  </array>");
    }

    lines.push("</dict>");
    return lines.join("\n");
  }
  if (sectionKey === "airprint") {
    const printerEntries = normalizeAirPrintPrinterEntries(airPrintPrinterRowsFromPayload(payloadItem))
      .filter((entry) => entry.hostName);
    if (printerEntries.length === 0) return "";
    lines.push("  <key>AirPrint</key>");
    lines.push("  <array>");
    printerEntries.forEach((entry) => {
      lines.push("    <dict>");
      lines.push(`      <key>IPAddress</key><string>${xmlEscape(entry.hostName)}</string>`);
      lines.push(`      <key>ForceTLS</key>${xmlBoolTag(Boolean(entry.forceTLS))}`);
      const port = Number.parseInt(String(entry.port || ""), 10);
      if (Number.isFinite(port) && port >= 1 && port <= 65535) {
        lines.push(`      <key>Port</key><integer>${port}</integer>`);
      }
      if (entry.resourcePath) {
        lines.push(`      <key>ResourcePath</key><string>${xmlEscape(entry.resourcePath)}</string>`);
      }
      lines.push("    </dict>");
    });
    lines.push("  </array>");
    lines.push("</dict>");
    return lines.join("\n");
  }
  if (sectionKey === "certificates") {
    const certContent = String(payloadItem?.payloadContent || "").trim();
    const certFileName = String(payloadItem?.certificateFileName || payloadItem?.payloadCertificateFileName || "").trim();
    const certDisplayName = String(payloadItem?.payloadDisplayName || certFileName || sectionLabel).trim();
    const certPassword = String(payloadItem?.password || "").trim();
    if (!certContent) return "";
    if (certFileName) {
      lines.push(`  <key>PayloadCertificateFileName</key><string>${xmlEscape(certFileName)}</string>`);
    }
    lines.push(`  <key>PayloadDisplayName</key><string>${xmlEscape(certDisplayName)}</string>`);
    lines.push(`  <key>PayloadContent</key><data>${xmlEscape(certContent)}</data>`);
    if (certPassword) {
      lines.push(`  <key>Password</key><string>${xmlEscape(certPassword)}</string>`);
    }
    lines.push("</dict>");
    return lines.join("\n");
  }

  if (sectionKey === "restrictions") {
    const usageMode = String(payloadItem?.restrictAppUsageMode || "AllowAll");
    const restrictedApps = normalizeRestrictionAppEntries(payloadItem?.restrictedApps);
    const bundleIDs = restrictedApps.map((entry) => String(entry.bundleId || "").trim()).filter(Boolean);
    if (usageMode === "DenySome" && bundleIDs.length > 0) {
      lines.push("  <key>blacklistedAppBundleIDs</key><array>");
      bundleIDs.forEach((bundleId) => {
        lines.push(`    <string>${xmlEscape(bundleId)}</string>`);
      });
      lines.push("  </array>");
    } else if (usageMode === "AllowSome" && bundleIDs.length > 0) {
      lines.push("  <key>whitelistedAppBundleIDs</key><array>");
      bundleIDs.forEach((bundleId) => {
        lines.push(`    <string>${xmlEscape(bundleId)}</string>`);
      });
      lines.push("  </array>");
    }

    const softwareUpdateDelay = Number.parseInt(String(payloadItem?.deferSoftwareUpdatesDays ?? ""), 10);
    if (Number.isFinite(softwareUpdateDelay) && softwareUpdateDelay >= 1) {
      lines.push("  <key>forceDelayedSoftwareUpdates</key><true/>");
    }
  }

  if (sectionKey === "content_filter") {
    const filterMode = String(payloadItem?.filterType || "BuiltIn");
    const isPlugin = filterMode === "Plugin";
    lines.push(`  <key>FilterType</key><string>${isPlugin ? "Plugin" : "BuiltIn"}</string>`);

    if (!isPlugin) {
      const allowedURLs = sectionArrayItems(payloadItem?.permittedURLs);
      const blockedURLs = sectionArrayItems(payloadItem?.blacklistedURLs);
      const specificWebsitesOnly = filterMode === "BuiltInSpecificWebsitesOnly";
      lines.push(`  <key>AutoFilterEnabled</key>${xmlBoolTag(!specificWebsitesOnly)}`);

      if (specificWebsitesOnly) {
        if (allowedURLs.length > 0) {
          lines.push("  <key>WhitelistedBookmarks</key>");
          lines.push("  <array>");
          allowedURLs.forEach((url) => {
            lines.push("    <dict>");
            lines.push(`      <key>URL</key><string>${xmlEscape(url)}</string>`);
            lines.push(`      <key>Title</key><string>${xmlEscape(url)}</string>`);
            lines.push("    </dict>");
          });
          lines.push("  </array>");
        }
      } else {
        if (allowedURLs.length > 0) {
          lines.push("  <key>PermittedURLs</key>");
          lines.push("  <array>");
          allowedURLs.forEach((url) => {
            lines.push(`    <string>${xmlEscape(url)}</string>`);
          });
          lines.push("  </array>");
        }
        if (blockedURLs.length > 0) {
          lines.push("  <key>BlacklistedURLs</key>");
          lines.push("  <array>");
          blockedURLs.forEach((url) => {
            lines.push(`    <string>${xmlEscape(url)}</string>`);
          });
          lines.push("  </array>");
        }
      }
    }
  }

  if (sectionKey === "ldap") {
    const searchSettings = normalizeLDAPSearchSettings(payloadItem?.searchSettings);
    if (searchSettings.length > 0) {
      lines.push("  <key>LDAPSearchSettings</key>");
      lines.push("  <array>");
      searchSettings.forEach((entry) => {
        lines.push("    <dict>");
        if (entry.description) {
          lines.push(`      <key>LDAPSearchSettingDescription</key><string>${xmlEscape(entry.description)}</string>`);
        }
        lines.push(
          `      <key>LDAPSearchSettingScope</key><string>${xmlEscape(
            entry.scope || "LDAPSearchSettingScopeSubtree"
          )}</string>`
        );
        if (entry.searchBase) {
          lines.push(`      <key>LDAPSearchSettingSearchBase</key><string>${xmlEscape(entry.searchBase)}</string>`);
        }
        lines.push("    </dict>");
      });
      lines.push("  </array>");
    }
  }

  schema.fields.forEach((field) => {
    if (sectionKey === "restrictions" && isTvOSOnlyProfileField(field)) return;
    if (!isSectionFieldVisible(field, payloadItem)) return;
    if (field.serialize === false) return;
    if (!field.plistKey) return;
    const rawValue = payloadItem?.[field.key];
    if (field.type === "boolean") {
      const uiValue = rawValue === undefined ? Boolean(field.defaultValue) : Boolean(rawValue);
      const plistValue = field.invertBool ? !uiValue : uiValue;
      lines.push(`  <key>${xmlEscape(field.plistKey)}</key>${xmlBoolTag(Boolean(plistValue))}`);
      return;
    }
    if (field.type === "number" || field.type === "select_number") {
      const source = rawValue ?? field.defaultValue ?? "";
      if (source === "") return;
      const parsed = Number.parseInt(String(source), 10);
      if (!Number.isFinite(parsed)) return;
      let normalized = parsed;
      if (Number.isFinite(Number(field.min))) normalized = Math.max(Number(field.min), normalized);
      if (Number.isFinite(Number(field.max))) normalized = Math.min(Number(field.max), normalized);
      lines.push(`  <key>${xmlEscape(field.plistKey)}</key><integer>${normalized}</integer>`);
      return;
    }
    if (field.type === "array_strings") {
      const values = (
        Array.isArray(rawValue)
          ? rawValue
          : String(rawValue ?? "")
              .split(/\r?\n|,/)
      )
        .map((item) => String(item || "").trim())
        .filter(Boolean);
      if (values.length === 0) return;
      lines.push(`  <key>${xmlEscape(field.plistKey)}</key><array>`);
      values.forEach((value) => {
        lines.push(`    <string>${xmlEscape(value)}</string>`);
      });
      lines.push("  </array>");
      return;
    }
    if (field.type === "data") {
      const value = String(rawValue ?? "").trim();
      if (!value) return;
      lines.push(`  <key>${xmlEscape(field.plistKey)}</key><data>${xmlEscape(value)}</data>`);
      return;
    }
    if (field.type === "json_dict") {
      const parsed = parseJsonField(rawValue, "dict", field.label, sectionLabel);
      if (!parsed || Object.keys(parsed).length === 0) return;
      lines.push(`  <key>${xmlEscape(field.plistKey)}</key>`);
      lines.push(buildPlistValueXml(parsed, "  "));
      return;
    }
    if (field.type === "json_array") {
      const parsed = parseJsonField(rawValue, "array", field.label, sectionLabel);
      if (!Array.isArray(parsed) || parsed.length === 0) return;
      lines.push(`  <key>${xmlEscape(field.plistKey)}</key>`);
      lines.push(buildPlistValueXml(parsed, "  "));
      return;
    }
    const value = String(rawValue ?? "").trim();
    if (!value) return;
    lines.push(`  <key>${xmlEscape(field.plistKey)}</key><string>${xmlEscape(value)}</string>`);
  });

  lines.push("</dict>");
  return lines.join("\n");
}

function buildNotificationsSectionPayloadXmlEntry(profileIdentifier, payloadItems = []) {
  const schema = APPLE_PROFILE_SECTION_SCHEMAS.notifications;
  if (!schema) return "";
  const items = Array.isArray(payloadItems) ? payloadItems : [];
  const validItems = items
    .map((item) => (item && typeof item === "object" ? item : {}))
    .filter((item) => String(item.bundleIdentifier || "").trim());
  if (validItems.length === 0) return "";

  const payloadUUID = randomUUIDLike().toUpperCase();
  const payloadIdentifier = `${profileIdentifier}.notifications.1`;
  const sectionLabel = PROFILE_BUILDER_SECTIONS.find((item) => item.key === "notifications")?.label || "notifications";
  const lines = [
    "<dict>",
    `  <key>PayloadDescription</key><string>${xmlEscape(sectionLabel)}</string>`,
    `  <key>PayloadDisplayName</key><string>${xmlEscape(sectionLabel)}</string>`,
    `  <key>PayloadIdentifier</key><string>${xmlEscape(payloadIdentifier)}</string>`,
    `  <key>PayloadType</key><string>${xmlEscape(schema.payloadType)}</string>`,
    `  <key>PayloadUUID</key><string>${payloadUUID}</string>`,
    "  <key>PayloadVersion</key><integer>1</integer>",
    "  <key>NotificationSettings</key>",
    "  <array>"
  ];

  validItems.forEach((payloadItem) => {
    const bundleIdentifier = String(payloadItem?.bundleIdentifier || "").trim();
    if (!bundleIdentifier) return;
    lines.push("    <dict>");
    lines.push(`      <key>BundleIdentifier</key><string>${xmlEscape(bundleIdentifier)}</string>`);

    const notificationsEnabled = payloadItem?.notificationsEnabled ?? true;
    const badgesEnabled = payloadItem?.badgesEnabled ?? true;
    const soundsEnabled = payloadItem?.soundsEnabled ?? true;
    const showInLockScreen = payloadItem?.showInLockScreen ?? true;
    const showInNotificationCenter = payloadItem?.showInNotificationCenter ?? true;
    const criticalAlertEnabled = payloadItem?.criticalAlertEnabled ?? false;
    const showInCarPlay = payloadItem?.showInCarPlay ?? false;

    lines.push(`      <key>NotificationsEnabled</key>${xmlBoolTag(Boolean(notificationsEnabled))}`);
    lines.push(
      `      <key>AlertType</key><integer>${mapNotificationEnumToInt(
        payloadItem?.alertType,
        NOTIFICATION_ALERT_TYPE_TO_INT,
        "Banners"
      )}</integer>`
    );
    lines.push(`      <key>BadgesEnabled</key>${xmlBoolTag(Boolean(badgesEnabled))}`);
    lines.push(`      <key>SoundsEnabled</key>${xmlBoolTag(Boolean(soundsEnabled))}`);
    lines.push(`      <key>ShowInLockScreen</key>${xmlBoolTag(Boolean(showInLockScreen))}`);
    lines.push(`      <key>ShowInNotificationCenter</key>${xmlBoolTag(Boolean(showInNotificationCenter))}`);
    lines.push(`      <key>CriticalAlertEnabled</key>${xmlBoolTag(Boolean(criticalAlertEnabled))}`);
    lines.push(`      <key>ShowInCarPlay</key>${xmlBoolTag(Boolean(showInCarPlay))}`);
    lines.push(
      `      <key>GroupingType</key><integer>${mapNotificationEnumToInt(
        payloadItem?.groupingType,
        NOTIFICATION_GROUPING_TYPE_TO_INT,
        "Automatic"
      )}</integer>`
    );
    lines.push(
      `      <key>PreviewType</key><integer>${mapNotificationEnumToInt(
        payloadItem?.previewType,
        NOTIFICATION_PREVIEW_TYPE_TO_INT,
        "Always"
      )}</integer>`
    );
    lines.push("    </dict>");
  });

  lines.push("  </array>");
  lines.push("</dict>");
  return lines.join("\n");
}

function parseProfileRootDictFromProfile(profile) {
  const payloadB64 = profile?.payload?.Payload;
  const xml = decodeBase64(payloadB64);
  if (!xml || typeof DOMParser === "undefined") return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, "application/xml");
    if (doc.querySelector("parsererror")) return null;
    const rootDictNode = doc.querySelector("plist > dict");
    const parsed = parsePlistNode(rootDictNode);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeProfileGeneralState(raw = {}) {
  const autoRemoveRaw = String(raw.autoRemove || "Never").trim();
  const autoRemove = PROFILE_GENERAL_AUTO_REMOVE_VALUES.has(autoRemoveRaw) ? autoRemoveRaw : "Never";
  const removalDateValue = String(raw.removalDate || "").trim();
  const removalDaysValue = String(raw.removalDays || "").trim();
  return {
    description: String(raw.description || "").trim(),
    autoRemove,
    removalDate: removalDateValue,
    removalDays: removalDaysValue
  };
}

function parseProfileGeneralStateFromProfile(profile) {
  const parsed = parseProfileRootDictFromProfile(profile);
  if (!parsed) return normalizeProfileGeneralState();

  let autoRemove = "Never";
  let removalDate = "";
  let removalDays = "";

  const rawRemovalDate = String(parsed.RemovalDate || "").trim();
  if (rawRemovalDate) {
    autoRemove = "OnDate";
    const dateMatch = rawRemovalDate.match(/^(\d{4}-\d{2}-\d{2})/);
    removalDate = dateMatch ? dateMatch[1] : rawRemovalDate;
  }

  if (parsed.DurationUntilRemoval !== undefined && parsed.DurationUntilRemoval !== null) {
    const seconds = Number(parsed.DurationUntilRemoval);
    if (Number.isFinite(seconds) && seconds > 0) {
      autoRemove = "AfterDays";
      removalDays = String(Math.max(1, Math.round(seconds / 86400)));
    }
  }

  return normalizeProfileGeneralState({
    description: String(parsed.PayloadDescription || ""),
    autoRemove,
    removalDate,
    removalDays
  });
}

function parseSectionPayloadsFromProfile(profile) {
  const out = createProfileSectionPayloadsState();
  const payloadB64 = profile?.payload?.Payload;
  const xml = decodeBase64(payloadB64);
  if (!xml) return out;

  Object.entries(APPLE_PROFILE_SECTION_SCHEMAS).forEach(([sectionKey, schema]) => {
    const schemaPayloadTypes = Array.isArray(schema.payloadTypes) && schema.payloadTypes.length > 0
      ? schema.payloadTypes
      : [schema.payloadType];
    const blocks = extractProfilePayloadDictBlocksByType(xml, schemaPayloadTypes);
    if (!Array.isArray(blocks) || blocks.length === 0) return;

    if (sectionKey === "notifications") {
      const items = [];
      blocks.forEach((block, blockIndex) => {
        const parsedBlock = parsePlistDictBlock(block) || {};
        const rawSettings = Array.isArray(parsedBlock.NotificationSettings) && parsedBlock.NotificationSettings.length > 0
          ? parsedBlock.NotificationSettings
          : [parsedBlock];
        rawSettings.forEach((entry, entryIndex) => {
          if (!entry || typeof entry !== "object") return;
          const bundleIdentifier = String(entry.BundleIdentifier || "").trim();
          if (!bundleIdentifier) return;
          const item = createSectionPayloadDraft(sectionKey);
          item.id = `${randomHex(8)}-${blockIndex}-${entryIndex}`;
          item.bundleIdentifier = bundleIdentifier;
          item.notificationsEnabled = entry.NotificationsEnabled !== undefined
            ? Boolean(entry.NotificationsEnabled)
            : Boolean(item.notificationsEnabled);
          item.alertType = mapNotificationIntToEnum(
            entry.AlertType,
            NOTIFICATION_ALERT_TYPE_TO_INT,
            String(item.alertType || "Banners")
          );
          item.badgesEnabled = entry.BadgesEnabled !== undefined
            ? Boolean(entry.BadgesEnabled)
            : Boolean(item.badgesEnabled);
          item.soundsEnabled = entry.SoundsEnabled !== undefined
            ? Boolean(entry.SoundsEnabled)
            : Boolean(item.soundsEnabled);
          item.showInLockScreen = entry.ShowInLockScreen !== undefined
            ? Boolean(entry.ShowInLockScreen)
            : Boolean(item.showInLockScreen);
          item.showInNotificationCenter = entry.ShowInNotificationCenter !== undefined
            ? Boolean(entry.ShowInNotificationCenter)
            : Boolean(item.showInNotificationCenter);
          item.criticalAlertEnabled = entry.CriticalAlertEnabled !== undefined
            ? Boolean(entry.CriticalAlertEnabled)
            : Boolean(item.criticalAlertEnabled);
          item.showInCarPlay = entry.ShowInCarPlay !== undefined
            ? Boolean(entry.ShowInCarPlay)
            : Boolean(item.showInCarPlay);
          item.groupingType = mapNotificationIntToEnum(
            entry.GroupingType,
            NOTIFICATION_GROUPING_TYPE_TO_INT,
            String(item.groupingType || "Automatic")
          );
          item.previewType = mapNotificationIntToEnum(
            entry.PreviewType,
            NOTIFICATION_PREVIEW_TYPE_TO_INT,
            String(item.previewType || "Always")
          );
          items.push(item);
        });
      });
      if (items.length > 0) {
        out[sectionKey] = items;
      }
      return;
    }

    out[sectionKey] = blocks.map((block, index) => {
      const item = createSectionPayloadDraft(sectionKey);
      item.id = `${randomHex(8)}-${index}`;
      const parsedBlock = parsePlistDictBlock(block) || {};
      if (sectionKey === "certificates") {
        item.certificatePayloadType = String(parsedBlock.PayloadType || schema.payloadType || "com.apple.security.pkcs1");
        item.payloadContent = extractXmlData(block, "PayloadContent") || String(parsedBlock.PayloadContent || "");
        item.certificateFileName = extractXmlString(block, "PayloadCertificateFileName") || "";
        item.payloadDisplayName = extractXmlString(block, "PayloadDisplayName") || "";
        item.password = extractXmlString(block, "Password") || "";
      }

      if (sectionKey === "restrictions") {
        const deniedApps = normalizeRestrictionAppEntries(parsedBlock.blacklistedAppBundleIDs);
        const allowedApps = normalizeRestrictionAppEntries(parsedBlock.whitelistedAppBundleIDs);
        if (allowedApps.length > 0) {
          item.restrictAppUsageMode = "AllowSome";
          item.restrictedApps = allowedApps;
        } else if (deniedApps.length > 0) {
          item.restrictAppUsageMode = "DenySome";
          item.restrictedApps = deniedApps;
        } else {
          item.restrictAppUsageMode = String(item.restrictAppUsageMode || "AllowAll");
          item.restrictedApps = normalizeRestrictionAppEntries(item.restrictedApps);
        }
      } else if (sectionKey === "content_filter") {
        const filterType = String(parsedBlock.FilterType || "BuiltIn");
        if (filterType === "Plugin") {
          item.filterType = "Plugin";
        } else {
          const autoFilterEnabled = parsedBlock.AutoFilterEnabled === undefined
            ? true
            : Boolean(parsedBlock.AutoFilterEnabled);
          item.filterType = autoFilterEnabled ? "BuiltIn" : "BuiltInSpecificWebsitesOnly";
          item.permittedURLs = [];
          const whitelistedBookmarks = Array.isArray(parsedBlock.WhitelistedBookmarks)
            ? parsedBlock.WhitelistedBookmarks
            : [];
          if (!autoFilterEnabled && whitelistedBookmarks.length > 0) {
            item.permittedURLs = whitelistedBookmarks
              .map((entry) => {
                if (entry && typeof entry === "object") return String(entry.URL || "").trim();
                return String(entry || "").trim();
              })
              .filter(Boolean);
          } else if (autoFilterEnabled) {
            item.permittedURLs = Array.isArray(parsedBlock.PermittedURLs)
              ? parsedBlock.PermittedURLs.map((entry) => String(entry || "").trim()).filter(Boolean)
              : [];
            item.blacklistedURLs = Array.isArray(parsedBlock.BlacklistedURLs)
              ? parsedBlock.BlacklistedURLs.map((entry) => String(entry || "").trim()).filter(Boolean)
              : [];
          }
        }
      }

      if (sectionKey === "airplay") {
        const passwordsRaw = Array.isArray(parsedBlock.Passwords) ? parsedBlock.Passwords : [];
        const passwords = passwordsRaw
          .filter((entry) => entry && typeof entry === "object")
          .map((entry) => ({
            deviceName: String(entry.DeviceName || entry.Name || "").trim(),
            password: String(entry.Password || "").trim()
          }))
          .filter((entry) => entry.deviceName);
        if (passwords.length > 0) {
          item.airplayPasswords = passwords;
        } else {
          const legacyDeviceName = String(parsedBlock.DeviceName || "").trim();
          const legacyPassword = String(parsedBlock.Password || "").trim();
          item.airplayPasswords = legacyDeviceName ? [{ deviceName: legacyDeviceName, password: legacyPassword }] : [];
        }

        const allowedCandidates = [
          parsedBlock.Whitelist,
          parsedBlock.AllowedDestinations,
          parsedBlock.Allowed
        ];
        const allowed = [];
        allowedCandidates.forEach((candidate) => {
          if (!Array.isArray(candidate)) return;
          candidate.forEach((entry) => {
            const value = String(entry || "").trim();
            if (value) allowed.push(value);
          });
        });
        item.allowedDestinations = Array.from(new Set(allowed));
      } else if (sectionKey === "airprint") {
        const rawPrinters = Array.isArray(parsedBlock.AirPrint) ? parsedBlock.AirPrint : [];
        item.printers = rawPrinters
          .filter((entry) => entry && typeof entry === "object")
          .map((entry) => ({
            hostName: String(entry.IPAddress || entry.HostName || "").trim(),
            forceTLS: Boolean(entry.ForceTLS),
            port: entry.Port === undefined || entry.Port === null ? "" : String(entry.Port),
            resourcePath: String(entry.ResourcePath || "").trim()
          }))
          .filter((entry) => entry.hostName || entry.resourcePath || entry.port || entry.forceTLS);
      } else if (sectionKey === "vpn") {
        item.userDefinedName = String(parsedBlock.UserDefinedName || "");
        item.vpnType = String(parsedBlock.VPNType || item.vpnType || "L2TP");
        item.vpnSubType = String(parsedBlock.VPNSubType || "");
        if (item.vpnType === "VPN" && item.vpnSubType) {
          const matchedVPNType = Object.entries(THIRD_PARTY_VPN_SUBTYPES).find(
            ([, value]) => value && value === item.vpnSubType
          );
          item.vpnType = matchedVPNType?.[0] || "CustomSSL";
        }

        const ipsec = parsedBlock.IPSec && typeof parsedBlock.IPSec === "object" ? parsedBlock.IPSec : {};
        const ikev2 = parsedBlock.IKEv2 && typeof parsedBlock.IKEv2 === "object" ? parsedBlock.IKEv2 : {};
        const ppp = parsedBlock.PPP && typeof parsedBlock.PPP === "object" ? parsedBlock.PPP : {};
        const nested = item.vpnType === "IKEv2" ? ikev2 : item.vpnType === "PPTP" ? ppp : ipsec;

        item.remoteAddress = String(nested.RemoteAddress || "");
        item.authName = String(nested.AuthName || ppp.AuthName || "");

        const machineMethod = String(nested.AuthenticationMethod || "").trim();
        if (machineMethod === "Certificate") {
          item.machineAuthenticationType = "Certificate";
          item.payloadCertificateUUID = String(nested.PayloadCertificateUUID || "");
        } else if (machineMethod === "None") {
          item.machineAuthenticationType = "None";
        } else {
          item.machineAuthenticationType = "SharedSecret";
          item.sharedSecret = String(nested.SharedSecret || "");
        }

        const userPassword = String(ppp.AuthPassword || nested.AuthPassword || "");
        const tokenCard = Boolean(ppp.TokenCard || nested.TokenCard);
        item.userAuthenticationType = tokenCard ? "RSASecurID" : "Password";
        item.authPassword = userPassword;

        const overridePrimary = ppp.OverridePrimary ?? nested.OverridePrimary;
        item.sendAllTraffic = overridePrimary === 1 || overridePrimary === true;

        const proxies = parsedBlock.Proxies;
        if (proxies && typeof proxies === "object" && !Array.isArray(proxies)) {
          if (proxies.ProxyAutoConfigEnable === 1 || proxies.ProxyAutoConfigEnable === true) {
            item.proxyType = "Auto";
            item.proxyPACURL = String(proxies.ProxyAutoConfigURLString || "");
          } else if (proxies.HTTPEnable === 1 || proxies.HTTPEnable === true) {
            item.proxyType = "Manual";
            item.proxyServer = String(proxies.HTTPProxy || "");
            item.proxyServerPort = proxies.HTTPPort ?? "";
          } else {
            item.proxyType = "None";
          }
        } else {
          item.proxyType = "None";
        }
      } else if (sectionKey === "scep") {
        item.url = String(parsedBlock.URL || "");
        item.name = String(parsedBlock.Name || "");
        item.challenge = String(parsedBlock.Challenge || "");
        item.keyType = String(parsedBlock.KeyType || item.keyType || "RSA");
        item.keysize = parsedBlock.Keysize ?? item.keysize;
        item.keyUsage = parsedBlock.KeyUsage ?? item.keyUsage;
        item.useAsDigitalSignature = Boolean((Number(parsedBlock.KeyUsage || 0) & 1) === 1);
        item.useForKeyEncipherment = Boolean((Number(parsedBlock.KeyUsage || 0) & 4) === 4);
        item.retries = parsedBlock.Retries ?? item.retries;
        item.retryDelay = parsedBlock.RetryDelay ?? item.retryDelay;
        item.certificateRenewalTimeInterval =
          parsedBlock.CertificateRenewalTimeInterval ?? item.certificateRenewalTimeInterval;
        item.allowAllAppsAccess = Boolean(parsedBlock.AllowAllAppsAccess);
        item.keyIsExtractable = Boolean(parsedBlock.KeyIsExtractable);
        item.caFingerprint = String(parsedBlock.CAFingerprint || "");
        item.caFingerprintData = String(parsedBlock.CAFingerprint || "");

        if (Array.isArray(parsedBlock.Subject)) {
          item.subjectAdvanced = JSON.stringify(parsedBlock.Subject, null, 2);
          const flatPairs = parsedBlock.Subject
            .map((pair) => (Array.isArray(pair) && pair.length >= 2 ? `${pair[0]}=${pair[1]}` : ""))
            .filter(Boolean);
          item.subject = flatPairs.join(", ");
        }
        if (parsedBlock.SubjectAltName && typeof parsedBlock.SubjectAltName === "object") {
          item.subjectAltNameAdvanced = JSON.stringify(parsedBlock.SubjectAltName, null, 2);
          const sanLines = [];
          const dnsList = Array.isArray(parsedBlock.SubjectAltName.dNSName)
            ? parsedBlock.SubjectAltName.dNSName
            : parsedBlock.SubjectAltName.dNSName
              ? [parsedBlock.SubjectAltName.dNSName]
              : [];
          dnsList.forEach((value) => sanLines.push(`DNS:${value}`));
          const emailList = Array.isArray(parsedBlock.SubjectAltName.rfc822Name)
            ? parsedBlock.SubjectAltName.rfc822Name
            : parsedBlock.SubjectAltName.rfc822Name
              ? [parsedBlock.SubjectAltName.rfc822Name]
              : [];
          emailList.forEach((value) => sanLines.push(`EMAIL:${value}`));
          const uriList = Array.isArray(parsedBlock.SubjectAltName.uniformResourceIdentifier)
            ? parsedBlock.SubjectAltName.uniformResourceIdentifier
            : parsedBlock.SubjectAltName.uniformResourceIdentifier
              ? [parsedBlock.SubjectAltName.uniformResourceIdentifier]
              : [];
          uriList.forEach((value) => sanLines.push(`URI:${value}`));
          const ntList = Array.isArray(parsedBlock.SubjectAltName.ntPrincipalName)
            ? parsedBlock.SubjectAltName.ntPrincipalName
            : parsedBlock.SubjectAltName.ntPrincipalName
              ? [parsedBlock.SubjectAltName.ntPrincipalName]
              : [];
          ntList.forEach((value) => sanLines.push(`NT:${value}`));
          if (emailList[0]) {
            item.subjectAltNameType = "RFC 822 Name";
            item.subjectAltNameValue = String(emailList[0]);
          } else if (dnsList[0]) {
            item.subjectAltNameType = "DNS Name";
            item.subjectAltNameValue = String(dnsList[0]);
          } else if (uriList[0]) {
            item.subjectAltNameType = "Uniform Resource Identifier";
            item.subjectAltNameValue = String(uriList[0]);
          } else {
            item.subjectAltNameType = "None";
            item.subjectAltNameValue = "";
          }
          item.ntPrincipalName = String(ntList[0] || "");
          item.subjectAltName = sanLines.join("\n");
        }
        if (Array.isArray(parsedBlock.ExtendedKeyUsage)) {
          item.extendedKeyUsageAdvanced = JSON.stringify(parsedBlock.ExtendedKeyUsage, null, 2);
          item.extendedKeyUsage = parsedBlock.ExtendedKeyUsage.join("\n");
        }
      } else if (sectionKey === "mail") {
        item.emailAccountDescription = String(parsedBlock.EmailAccountDescription || "");
        item.emailAccountName = String(parsedBlock.EmailAccountName || "");
        item.emailAddress = String(parsedBlock.EmailAddress || "");
        item.incomingHost = String(parsedBlock.IncomingMailServerHostName || "");
        item.incomingPort = parsedBlock.IncomingMailServerPortNumber ?? item.incomingPort;
        item.incomingUsername = String(parsedBlock.IncomingMailServerUsername || "");
        item.incomingPassword = String(parsedBlock.IncomingPassword || "");
        item.incomingAuth = String(parsedBlock.IncomingMailServerAuthentication || item.incomingAuth || "");
        item.incomingUseSSL =
          parsedBlock.IncomingMailServerUseSSL === undefined
            ? item.incomingUseSSL
            : Boolean(parsedBlock.IncomingMailServerUseSSL);
        item.incomingImapPath = String(parsedBlock.IncomingMailServerIMAPPathPrefix || "");
        item.outgoingHost = String(parsedBlock.OutgoingMailServerHostName || "");
        item.outgoingPort = parsedBlock.OutgoingMailServerPortNumber ?? item.outgoingPort;
        item.outgoingUsername = String(parsedBlock.OutgoingMailServerUsername || "");
        item.outgoingPassword = String(parsedBlock.OutgoingPassword || "");
        item.outgoingAuth = String(parsedBlock.OutgoingMailServerAuthentication || item.outgoingAuth || "");
        item.outgoingUseSSL =
          parsedBlock.OutgoingMailServerUseSSL === undefined
            ? item.outgoingUseSSL
            : Boolean(parsedBlock.OutgoingMailServerUseSSL);
        item.outgoingPasswordSameAsIncoming =
          parsedBlock.OutgoingPasswordSameAsIncomingPassword === undefined
            ? item.outgoingPasswordSameAsIncoming
            : Boolean(parsedBlock.OutgoingPasswordSameAsIncomingPassword);
        item.preventAppSheet =
          parsedBlock.PreventAppSheet === undefined ? item.preventAppSheet : Boolean(parsedBlock.PreventAppSheet);
        item.preventMove =
          parsedBlock.PreventMove === undefined ? item.preventMove : Boolean(parsedBlock.PreventMove);
        item.allowMailDrop =
          parsedBlock.allowMailDrop === undefined ? item.allowMailDrop : Boolean(parsedBlock.allowMailDrop);
        item.disableMailRecentsSyncing =
          parsedBlock.disableMailRecentsSyncing === undefined
            ? item.disableMailRecentsSyncing
            : Boolean(parsedBlock.disableMailRecentsSyncing);
        item.smimeEnabled = parsedBlock.SMIMEEnabled === undefined ? item.smimeEnabled : Boolean(parsedBlock.SMIMEEnabled);
        item.smimeEncryptByDefault =
          parsedBlock.SMIMEEncryptByDefault === undefined
            ? item.smimeEncryptByDefault
            : Boolean(parsedBlock.SMIMEEncryptByDefault);
        item.smimeSigningEnabled =
          parsedBlock.SMIMESigningEnabled === undefined
            ? item.smimeSigningEnabled
            : Boolean(parsedBlock.SMIMESigningEnabled);
        item.smimeSigningUserOverrideable =
          parsedBlock.SMIMESigningUserOverrideable === undefined
            ? item.smimeSigningUserOverrideable
            : Boolean(parsedBlock.SMIMESigningUserOverrideable);
        item.smimeEncryptionUserOverrideable =
          parsedBlock.SMIMEEncryptionUserOverrideable === undefined
            ? item.smimeEncryptionUserOverrideable
            : Boolean(parsedBlock.SMIMEEncryptionUserOverrideable);
        item.smimeEnablePerMessageSwitch =
          parsedBlock.SMIMEEnablePerMessageSwitch === undefined
            ? item.smimeEnablePerMessageSwitch
            : Boolean(parsedBlock.SMIMEEnablePerMessageSwitch);
        item.smimeSigningCertificateUUID = String(parsedBlock.SMIMESigningCertificateUUID || "");
        item.smimeEncryptionCertificateUUID = String(parsedBlock.SMIMEEncryptionCertificateUUID || "");
        if (parsedBlock.IncomingMailServer && typeof parsedBlock.IncomingMailServer === "object") {
          item.incomingServerAdvanced = JSON.stringify(parsedBlock.IncomingMailServer, null, 2);
        }
        if (parsedBlock.OutgoingMailServer && typeof parsedBlock.OutgoingMailServer === "object") {
          item.outgoingServerAdvanced = JSON.stringify(parsedBlock.OutgoingMailServer, null, 2);
        }
        if (parsedBlock.SMIME && typeof parsedBlock.SMIME === "object") {
          item.smimeConfiguration = JSON.stringify(parsedBlock.SMIME, null, 2);
        }
      }

      schema.fields.forEach((field) => {
        if (sectionKey === "vpn" || sectionKey === "scep" || sectionKey === "mail") {
          return;
        }
        if (field.serialize === false) return;
        if (!field.plistKey) return;
        if (field.type === "boolean") {
          const defaultUiValue = Boolean(field.defaultValue);
          const defaultPlistValue = field.invertBool ? !defaultUiValue : defaultUiValue;
          const parsedPlistValue = extractXmlBool(block, field.plistKey, defaultPlistValue);
          item[field.key] = field.invertBool ? !parsedPlistValue : parsedPlistValue;
          return;
        }
        if (field.type === "number" || field.type === "select_number") {
          if (hasXmlKey(block, field.plistKey)) {
            item[field.key] = extractXmlInteger(block, field.plistKey, Number(field.defaultValue || 0));
            return;
          }
          item[field.key] = field.defaultValue ?? "";
          return;
        }
        if (field.type === "array_strings") {
          const parsedArray = extractXmlArrayStrings(block, field.plistKey);
          if (field.ui === "list") {
            item[field.key] = parsedArray
              ? parsedArray
                  .split(/\r?\n|,/)
                  .map((entry) => String(entry || "").trim())
                  .filter(Boolean)
              : Array.isArray(field.defaultValue)
                ? [...field.defaultValue]
                : [];
          } else {
            item[field.key] = parsedArray || String(field.defaultValue ?? "");
          }
          return;
        }
        if (field.type === "data") {
          const parsedData = extractXmlData(block, field.plistKey);
          item[field.key] = parsedData || String(field.defaultValue ?? "");
          return;
        }
        if (field.type === "json_dict") {
          const parsedJson = parsedBlock[field.plistKey];
          item[field.key] =
            parsedJson && typeof parsedJson === "object" && !Array.isArray(parsedJson)
              ? JSON.stringify(parsedJson, null, 2)
              : "";
          return;
        }
        if (field.type === "json_array") {
          const parsedJson = parsedBlock[field.plistKey];
          item[field.key] = Array.isArray(parsedJson) ? JSON.stringify(parsedJson, null, 2) : "";
          return;
        }
        const parsed = extractXmlString(block, field.plistKey);
        if (parsed) {
          item[field.key] = parsed;
          return;
        }
        const parsedFromDict = parsedBlock[field.plistKey];
        if (parsedFromDict !== undefined && parsedFromDict !== null) {
          item[field.key] = String(parsedFromDict);
          return;
        }
        item[field.key] = String(field.defaultValue ?? "");
      });
      return item;
    });
  });

  return out;
}

function parseWiFiPayloadsFromProfile(profile) {
  const payloadB64 = profile?.payload?.Payload;
  const xml = decodeBase64(payloadB64);
  if (!xml) return [];

  const blocks = xml.match(
    /<dict>[\s\S]*?<key>PayloadType<\/key>\s*<string>com\.apple\.wifi\.managed<\/string>[\s\S]*?<\/dict>/gi
  );
  if (!Array.isArray(blocks) || blocks.length === 0) return [];

  return blocks.map((block, index) => ({
    ...createWiFiPayloadDraft(),
    id: `${randomHex(10)}-${index}`,
    ssid: extractXmlString(block, "SSID_STR"),
    hiddenNetwork: extractXmlBool(block, "HIDDEN_NETWORK", false),
    autoJoin: extractXmlBool(block, "AutoJoin", true),
    captiveBypass: extractXmlBool(block, "CaptiveBypass", false),
    disableAssociationMACRandomization: extractXmlBool(
      block,
      "DisableAssociationMACRandomization",
      false
    ),
    proxyType: extractXmlString(block, "ProxyType") || "None",
    securityType: extractXmlString(block, "EncryptionType") || "None",
    networkType: extractXmlBool(block, "IsHotspot", false) ? "Hotspot" : "Standard",
    password: extractXmlString(block, "Password")
  }));
}

function xmlBoolTag(value) {
  return value ? "<true/>" : "<false/>";
}

function buildWiFiPayloadXmlEntry(profileIdentifier, network, index) {
  const payloadUUID = randomUUIDLike().toUpperCase();
  const ssid = String(network?.ssid || "").trim();
  const proxyType = String(network?.proxyType || "None").trim() || "None";
  const securityType = String(network?.securityType || "None").trim() || "None";
  const password = String(network?.password || "");
  const payloadIdentifier = `${profileIdentifier}.wifi.${index + 1}`;

  const lines = [
    "<dict>",
    `  <key>AutoJoin</key>${xmlBoolTag(Boolean(network?.autoJoin))}`,
    `  <key>CaptiveBypass</key>${xmlBoolTag(Boolean(network?.captiveBypass))}`,
    `  <key>DisableAssociationMACRandomization</key>${xmlBoolTag(Boolean(network?.disableAssociationMACRandomization))}`,
    `  <key>EncryptionType</key><string>${xmlEscape(securityType)}</string>`,
    `  <key>HIDDEN_NETWORK</key>${xmlBoolTag(Boolean(network?.hiddenNetwork))}`,
    `  <key>IsHotspot</key>${xmlBoolTag(String(network?.networkType || "Standard") === "Hotspot")}`,
    "  <key>PayloadDescription</key><string>Configures Wi-Fi settings</string>",
    "  <key>PayloadDisplayName</key><string>Wi-Fi</string>",
    `  <key>PayloadIdentifier</key><string>${xmlEscape(payloadIdentifier)}</string>`,
    "  <key>PayloadType</key><string>com.apple.wifi.managed</string>",
    `  <key>PayloadUUID</key><string>${payloadUUID}</string>`,
    "  <key>PayloadVersion</key><integer>1</integer>",
    `  <key>ProxyType</key><string>${xmlEscape(proxyType)}</string>`,
    `  <key>SSID_STR</key><string>${xmlEscape(ssid)}</string>`
  ];

  if (password.trim()) {
    lines.splice(6, 0, `  <key>Password</key><string>${xmlEscape(password)}</string>`);
  }

  lines.push("</dict>");
  return lines.join("\n");
}

function buildInstallProfilePayload(profileName, identifier, groupIds = [], options = {}) {
  const cleanName = String(profileName || "Turuk Profile").trim() || "Turuk Profile";
  const profileIdentifier = String(identifier || "").trim() || `pro.turuk.profile.${slugify(cleanName) || "item"}`;
  const payloadUUID = randomUUIDLike();
  const profilePlatform = normalizeProfilePlatform(options?.platform || "ios");
  const generalOptions = normalizeProfileGeneralState(options?.general || {});
  let durationUntilRemoval = "";
  if (generalOptions.autoRemove === "AfterDays") {
    const parsedDays = Number.parseInt(String(generalOptions.removalDays || "").trim(), 10);
    if (Number.isFinite(parsedDays) && parsedDays > 0) {
      durationUntilRemoval = String(parsedDays * 86400);
    }
  }
  let removalDate = "";
  if (generalOptions.autoRemove === "OnDate") {
    const rawDate = String(generalOptions.removalDate || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      removalDate = `${rawDate}T00:00:00Z`;
    } else if (rawDate) {
      removalDate = rawDate;
    }
  }
  const wifiNetworksFromOptions = Array.isArray(options?.wifiNetworks) ? options.wifiNetworks : null;
  const sectionPayloads = options?.sectionPayloads && typeof options.sectionPayloads === "object"
    ? options.sectionPayloads
    : {};
  let wifiNetworks = wifiNetworksFromOptions;

  if (wifiNetworks === null) {
    wifiNetworks = [
      {
        ...createWiFiPayloadDraft(),
        ssid: "Turuk-WiFi"
      }
    ];
  }

  const wifiPayloadEntries = wifiNetworks.map((network, index) =>
    buildWiFiPayloadXmlEntry(profileIdentifier, network, index)
  );
  const sectionPayloadEntries = Object.entries(sectionPayloads)
    .flatMap(([sectionKey, items]) => {
      if (!profileBuilderSectionSupportsPlatform(sectionKey, profilePlatform)) return [];
      if (!Array.isArray(items) || items.length === 0) return [];
      if (sectionKey === "notifications") {
        const mergedNotifications = buildNotificationsSectionPayloadXmlEntry(profileIdentifier, items);
        return mergedNotifications ? [mergedNotifications] : [];
      }
      return items.map((item, index) => buildSectionPayloadXmlEntry(profileIdentifier, sectionKey, item, index));
    })
    .filter(Boolean);
  const payloadEntriesXml = [...wifiPayloadEntries, ...sectionPayloadEntries]
    .filter(Boolean)
    .join("\n");
  const xmlLines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">',
    '<plist version="1.0"><dict>',
    "  <key>PayloadType</key><string>Configuration</string>",
    "  <key>PayloadVersion</key><integer>1</integer>",
    `  <key>PayloadIdentifier</key><string>${xmlEscape(profileIdentifier)}</string>`,
    `  <key>PayloadUUID</key><string>${payloadUUID}</string>`,
    `  <key>PayloadDisplayName</key><string>${xmlEscape(cleanName)}</string>`,
    "  <key>PayloadRemovalDisallowed</key><false/>"
  ];

  if (generalOptions.description) {
    xmlLines.push(`  <key>PayloadDescription</key><string>${xmlEscape(generalOptions.description)}</string>`);
  }
  if (durationUntilRemoval) {
    xmlLines.push(`  <key>DurationUntilRemoval</key><integer>${durationUntilRemoval}</integer>`);
  } else if (removalDate) {
    xmlLines.push(`  <key>RemovalDate</key><date>${xmlEscape(removalDate)}</date>`);
  }

  xmlLines.push(`  <key>PayloadContent</key><array>${payloadEntriesXml ? `\n${payloadEntriesXml}\n` : ""}`);
  xmlLines.push("  </array>");
  xmlLines.push("</dict></plist>");
  const xml = xmlLines.join("\n");

  return {
    RequestType: "InstallProfile",
    Payload: encodeBase64(xml),
    PayloadIdentifier: profileIdentifier,
    TargetPlatform: profilePlatform,
    GroupIDs: Array.isArray(groupIds) ? groupIds : []
  };
}

function profileGroupIDs(profile) {
  const source = Array.isArray(profile?.group_ids)
    ? profile.group_ids
    : Array.isArray(profile?.payload?.GroupIDs)
      ? profile.payload.GroupIDs
      : [];
  const seen = new Set();
  return source
    .map((item) => String(item || "").trim())
    .filter((id) => {
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
}

function formatBool(value, t) {
  if (value === true) return t("yes");
  if (value === false) return t("no");
  return t("unknown");
}

function formatDeviceOwnership(value, t) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized.includes("corporate") || normalized.includes("корп")) {
    return t("devicesOwnershipCorporate");
  }
  if (normalized.includes("personal") || normalized.includes("лич")) {
    return t("devicesOwnershipPersonal");
  }
  return value || "-";
}

function formatConsoleRole(role, t) {
  const normalized = String(role || "").trim().toLowerCase();
  if (normalized === "read-only") return t("roleReadOnly");
  if (normalized === "admin") return t("roleAdmin");
  if (normalized === "master-admin") return t("roleMasterAdmin");
  return role || "-";
}

function formatCommandState(state, resultStatus, t) {
  const normalizedState = String(state || "").trim().toLowerCase();
  const normalizedResult = String(resultStatus || "").trim().toLowerCase();

  if (normalizedState === "queued") return t("deviceDetailsCommandStateQueued");
  if (normalizedState === "sent") return t("deviceDetailsCommandStateSent");
  if (normalizedState === "dead_letter") return t("deviceDetailsCommandStateDeadLetter");
  if (normalizedState === "canceled") return t("deviceDetailsCommandStateCanceled");

  if (normalizedState === "done") {
    if (!normalizedResult || normalizedResult === "acknowledged") {
      return t("deviceDetailsCommandStateDone");
    }
    if (normalizedResult === "notnow") {
      return t("deviceDetailsCommandStateNotNow");
    }
    return t("deviceDetailsCommandStateError");
  }

  if (normalizedResult === "acknowledged") return t("deviceDetailsCommandStateDone");
  if (normalizedResult === "notnow") return t("deviceDetailsCommandStateNotNow");
  if (normalizedResult) return t("deviceDetailsCommandStateError");

  return state || resultStatus || "-";
}

const AUDIT_ACTION_LABEL_KEYS = {
  "auth.me": "auditActionAuthMe",
  "accounts.manage": "auditActionAccountsManage",
  "accounts.update": "auditActionAccountsUpdate",
  "accounts.status": "auditActionAccountsStatus",
  "accounts.delete": "auditActionAccountsDelete",
  "accounts.devices.assign": "auditActionAccountsDeviceAssign",
  "accounts.devices.unassign": "auditActionAccountsDeviceUnassign",
  "accounts.mobile_bindings.unbind": "auditActionAccountsMobileUnbind",
  "devices.delete": "auditActionDevicesDelete",
  "commands.apple.enqueue": "auditActionAppleCommand",
  "commands.device_information.enqueue": "auditActionDeviceInformation",
  "commands.security_info.enqueue": "auditActionSecurityInfo",
  "commands.installed_apps.enqueue": "auditActionInstalledApps",
  "commands.profile_list.enqueue": "auditActionProfileList",
  "commands.device_location.enqueue": "auditActionDeviceLocation",
  "commands.clear_passcode.enqueue": "auditActionClearPasscode",
  "commands.device_lock.enqueue": "auditActionDeviceLock",
  "commands.play_lost_mode_sound.enqueue": "auditActionLostModeSound",
  "commands.lost_mode.enable": "auditActionLostModeEnable",
  "commands.lost_mode.disable": "auditActionLostModeDisable",
  "commands.restart_device.enqueue": "auditActionRestartDevice",
  "commands.shut_down_device.enqueue": "auditActionShutdownDevice",
  "commands.schedule_os_update.enqueue": "auditActionScheduleOSUpdate",
  "commands.available_os_updates.enqueue": "auditActionAvailableOSUpdates",
  "commands.os_update_status.enqueue": "auditActionOSUpdateStatus",
  "commands.retry": "auditActionCommandRetry",
  "commands.cancel": "auditActionCommandCancel",
  "commands.remove_profile.enqueue": "auditActionRemoveProfile",
  "commands.remove_application.enqueue": "auditActionRemoveApplication",
  "commands.reinstall_application.enqueue": "auditActionReinstallApplication",
  "commands.allow_activation_lock.enqueue": "auditActionAllowActivationLock",
  "mdm_apns.csr": "auditActionAPNSCSR",
  "mdm_apns.config": "auditActionAPNSConfig",
  "certificates.configurator_profile.download": "auditActionEnrollmentDownload",
  "tenant_data.export": "auditActionTenantDataExport",
  "tenants.manage": "auditActionTenantCreate",
  "tenants.update": "auditActionTenantUpdate",
  "audit_logs.export": "auditActionAuditLogsExport",
  "agent_settings.support_ticket": "auditActionSupportTicket",
  "support_sessions.manage": "auditActionSupportSessionRequest",
  "support_sessions.approve": "auditActionSupportSessionApprove",
  "support_sessions.revoke": "auditActionSupportSessionRevoke",
  "support_sessions.access": "auditActionSupportSessionAccess",
  "sso.config": "auditActionSSOConfig"
};

function auditActionLabel(action, t) {
  const key = AUDIT_ACTION_LABEL_KEYS[String(action || "").trim()];
  return key ? t(key) : action || "-";
}

function isAuditSessionExpired(item) {
  const action = String(item?.action || "").trim();
  const path = String(item?.path || "").trim();
  const statusCode = Number(item?.status_code || 0);
  return action === "auth.me" && statusCode === 401 && path.endsWith("/auth/me");
}

function auditDetailsText(details) {
  if (!details || typeof details !== "object") return "-";
  const entries = Object.entries(details)
    .filter(([key, value]) => String(key || "").trim() && value !== null && value !== undefined && String(value).trim() !== "")
    .sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return "-";
  return entries.map(([key, value]) => `${key}: ${String(value)}`).join("\n");
}

function auditDateFilterToRFC3339(value, endOfDay = false) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const date = new Date(`${raw}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toISOString();
}

function buildAuditLogsQuery(filters = {}, extras = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(extras.limit || 200));
  for (const key of ["actor", "device", "action", "from", "to", "format"]) {
    const value = String(extras[key] ?? filters[key] ?? "").trim();
    if (!value) continue;
    if (key === "from") {
      params.set(key, auditDateFilterToRFC3339(value, false));
    } else if (key === "to") {
      params.set(key, auditDateFilterToRFC3339(value, true));
    } else {
      params.set(key, value);
    }
  }
  return params.toString();
}

function normalizePlatform(device) {
  const haystack = [
    device?.manufacturer,
    device?.os_version,
    device?.product_name,
    device?.model_name,
    device?.device_name
  ]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");

  if (haystack.includes("windows")) return "windows";
  if (haystack.includes("android")) return "android";
  if (haystack.includes("linux")) return "linux";
  if (haystack.includes("mac")) return "macos";
  if (
    haystack.includes("ios") ||
    haystack.includes("ipad") ||
    haystack.includes("iphone")
  ) {
    return "ios";
  }
  if (String(device?.manufacturer || "").toLowerCase() === "apple") return "ios";
  return "unknown";
}

function deviceLocationUnavailableReason(device, t) {
  const platform = normalizePlatform(device || {});
  if (platform !== "ios") {
    return t("deviceDetailsLocationRequiresIOS");
  }
  if (device?.is_supervised !== true) {
    return t("deviceDetailsLocationRequiresSupervised");
  }
  return "";
}

function normalizeAppPlatform(app) {
  const explicit = [
    app?.platform,
    app?.os,
    app?.target_platform,
    app?.device_family
  ].map((value) => String(value || "").toLowerCase());
  const explicitJoined = explicit.join(" ");

  if (
    explicitJoined.includes("ios") ||
    explicitJoined.includes("ipados") ||
    explicitJoined.includes("ipad") ||
    explicitJoined.includes("iphone")
  ) {
    return "ios";
  }
  if (explicitJoined.includes("android")) return "android";
  if (explicitJoined.includes("windows")) return "windows";
  if (explicitJoined.includes("macos") || explicitJoined.includes("mac")) return "macos";

  const manifestURL = String(app?.manifest_url || "").toLowerCase();
  if (manifestURL.includes(".apk")) return "android";
  if (manifestURL.includes(".msi") || manifestURL.includes(".exe")) return "windows";
  if (manifestURL.includes(".pkg") || manifestURL.includes(".dmg")) return "macos";

  if (String(app?.itunes_store_id || "").trim()) return "ios";

  const haystack = [
    app?.name,
    app?.bundle_id,
    app?.description
  ]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");

  if (haystack.includes("ios") || haystack.includes("ipad") || haystack.includes("iphone")) return "ios";
  if (haystack.includes("android")) return "android";
  if (haystack.includes("windows")) return "windows";
  if (haystack.includes("macos") || haystack.includes("mac app store")) return "macos";
  return "ios";
}

function firstNonEmptyText(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function resolveProfilePayloadIdentifier(profileLike) {
  if (!profileLike || typeof profileLike !== "object") return "";
  return firstNonEmptyText(
    profileLike.identifier,
    profileLike.profile_identifier,
    profileLike.payload_identifier,
    profileLike.payload?.PayloadIdentifier
  );
}

function normalizeDeviceAppLookupPlatform(platformValue) {
  const normalized = normalizeProfilePlatform(platformValue);
  if (normalized === "macos") return "macos";
  if (normalized === "android") return "android";
  return "ios";
}

function deviceAppLookupKey(platformValue, bundleID) {
  const bundle = String(bundleID || "").trim().toLowerCase();
  if (!bundle) return "";
  return `${normalizeDeviceAppLookupPlatform(platformValue)}:${bundle}`;
}

function normalizeProfilePlatform(platformValue) {
  const raw = String(platformValue || "").trim().toLowerCase();
  if (!raw) return "ios";
  if (raw.includes("ipados") || raw.includes("ipad") || raw.includes("iphone") || raw === "ios") return "ios";
  if (raw.includes("mac")) return "macos";
  if (raw.includes("tvos") || raw.includes("apple tv")) return "tvos";
  if (raw.includes("vision")) return "visionos";
  if (raw.includes("android")) return "android";
  if (raw.includes("windows")) return "windows";
  if (raw.includes("linux")) return "linux";
  return "ios";
}

function profileBuilderMinOSForPlatform(minOS, platformKey) {
  if (!minOS) return "";
  if (typeof minOS === "string") return minOS;
  const normalized = normalizeProfilePlatform(platformKey);
  return String(minOS[normalized] || "").trim();
}

function profileBuilderLabelWithMinOS(label, minOS) {
  const base = String(label || "").trim();
  const suffix = String(minOS || "").trim();
  if (!base || !suffix) return base;
  return `${base} (min OS: ${suffix})`;
}

function profileSectionDisplayLabel(section, platformKey) {
  const label = String(section?.label || section?.key || "");
  return profileBuilderLabelWithMinOS(
    label,
    profileBuilderMinOSForPlatform(PROFILE_BUILDER_SECTION_MIN_OS[section?.key], platformKey)
  );
}

function profileSectionDisplayMinOS(section, platformKey) {
  return profileBuilderMinOSForPlatform(PROFILE_BUILDER_SECTION_MIN_OS[section?.key], platformKey);
}

function profileBuilderFieldLabelWithMinOS(label, field, platformKey) {
  return profileBuilderLabelWithMinOS(label, profileBuilderMinOSForPlatform(field?.minOS, platformKey));
}

function profilePlatformLabel(platformKey, t) {
  switch (normalizeProfilePlatform(platformKey)) {
    case "tvos":
      return "tvOS";
    case "visionos":
      return "visionOS";
    case "windows":
      return t("platformWindows");
    case "macos":
      return t("platformMacOS");
    case "android":
      return t("platformAndroid");
    case "linux":
      return t("platformLinux");
    case "ios":
    default:
      return t("platformIOS");
  }
}

function metric(label, value) {
  return (
    <div className="metric-card" key={label}>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
    </div>
  );
}

function dashboardPercent(value, total) {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
}

function dashboardRingBackground(percent, color) {
  const safePercent = Math.max(0, Math.min(100, Number(percent) || 0));
  return `conic-gradient(${color} 0deg ${safePercent * 3.6}deg, #e6edf5 ${safePercent * 3.6}deg 360deg)`;
}

function dashboardTimestamp(value) {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function buildDisplayName(user) {
  if (!user) return "-";
  return user.display_name || user.login || user.email || "-";
}

function buildTenantDisplayName(user) {
  const tenant = user?.tenant || {};
  return String(tenant.name || tenant.primary_domain || user?.tenant_id || "").trim();
}

function normalizeTenantSlugClient(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function buildTenantDomainPreview(form, baseDomain) {
  const base = String(baseDomain || "").trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0];
  if (!base) return "";
  const slug = normalizeTenantSlugClient(form?.slug) || normalizeTenantSlugClient(form?.name);
  return slug ? `${slug}.${base}` : `companyname.${base}`;
}

function buildTenantPortalURL(tenant, baseDomain) {
  const primaryDomain = String(tenant?.primary_domain || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .split("/")[0];
  const domain =
    primaryDomain ||
    buildTenantDomainPreview(
      { name: tenant?.name || "", slug: tenant?.slug || "" },
      baseDomain
    );
  return domain ? `https://${domain}/` : "";
}

function tenantLimitInputValue(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function isTenantLimitInputValid(value) {
  const raw = String(value ?? "").trim();
  return raw === "" || /^\d+$/.test(raw);
}

function tenantLimitInputToPayload(value) {
  const raw = String(value ?? "").trim();
  if (raw === "") return null;
  if (!/^\d+$/.test(raw)) return null;
  return Number.parseInt(raw, 10);
}

function tenantDateInputValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw.slice(0, 10);
  return parsed.toISOString().slice(0, 10);
}

function tenantBillingStatusLabel(value, t) {
  const normalized = String(value || "").trim().toLowerCase();
  switch (normalized) {
    case "trialing":
      return t("tenantsBillingStatusTrialing");
    case "manual":
      return t("tenantsBillingStatusManual");
    case "active":
      return t("tenantsBillingStatusActive");
    case "past_due":
      return t("tenantsBillingStatusPastDue");
    case "canceled":
      return t("tenantsBillingStatusCanceled");
    default:
      return t("tenantsBillingStatusInternal");
  }
}

function normalizeAPIBase(value) {
  const raw = String(value || "").trim();
  if (!raw) return DEFAULT_API_BASE;
  const normalized = raw.replace(/\/+$/, "");
  return normalized || DEFAULT_API_BASE;
}

function apiWebSocketURL(apiBase, path, params = {}) {
  if (typeof window === "undefined") return "";
  const base = normalizeAPIBase(apiBase || DEFAULT_API_BASE);
  const baseURL = new URL(`${base}/`, window.location.origin);
  const out = new URL(String(path || "").replace(/^\/+/, ""), baseURL);
  if (out.protocol === "https:") {
    out.protocol = "wss:";
  } else if (out.protocol === "http:") {
    out.protocol = "ws:";
  }
  Object.entries(params).forEach(([key, value]) => {
    const cleaned = String(value ?? "").trim();
    if (cleaned) out.searchParams.set(key, cleaned);
  });
  return out.toString();
}

function uniqueColumnKeys(keys, allowedSet) {
  if (!Array.isArray(keys)) return [];
  const out = [];
  keys.forEach((item) => {
    const key = String(item || "").trim();
    if (!key || !allowedSet.has(key) || out.includes(key)) return;
    out.push(key);
  });
  return out;
}

function normalizeVisibleColumnKeys(raw, allowedKeys, fallbackKeys) {
  const allowedSet = new Set(allowedKeys || []);
  const safeFallback = uniqueColumnKeys(fallbackKeys, allowedSet);
  const safeRaw = uniqueColumnKeys(raw, allowedSet);
  return safeRaw.length > 0 ? safeRaw : safeFallback;
}

function ensureVisibleColumnKeys(keys, requiredKeys, orderedKeys) {
  const selected = new Set(Array.isArray(keys) ? keys : []);
  (requiredKeys || []).forEach((key) => selected.add(key));
  return (orderedKeys || []).filter((key) => selected.has(key));
}

function healthStatusClass(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "ok") return "status-pill ok";
  if (normalized === "critical") return "status-pill danger";
  if (normalized === "warning") return "status-pill warning";
  return "status-pill muted";
}

function healthStatusLabel(value, lang) {
  const normalized = String(value || "").toLowerCase();
  const ru = { ok: "здорово", warning: "внимание", critical: "критично", unknown: "нет данных" };
  const en = { ok: "healthy", warning: "warning", critical: "critical", unknown: "unknown" };
  return (lang === "ru" ? ru : en)[normalized] || (lang === "ru" ? ru.unknown : en.unknown);
}

function healthCheckLabel(check, lang) {
  const label = String(check?.label || check?.key || "").trim();
  if (lang !== "ru") return label;
  const ru = {
    APNs: "Связь с устройствами Apple",
    ADE: "Автоматическая регистрация Apple",
    VPP: "Приложения Apple Business Manager",
    Backups: "Резервные копии",
    "Restore drill": "Проверка восстановления",
    Webhooks: "Вебхуки",
    "macOS Agent": "macOS Agent",
    "DDM status": "Декларативное управление",
    "Queue workers": "Очередь команд",
    Mail: "E-mail уведомления"
  };
  return ru[label] || label;
}

function healthDetailLabel(value, lang) {
  const detail = String(value || "").trim();
  if (lang !== "ru") return detail;
  const globalAPNSMatch = detail.match(/^global APNs certificate valid for (\d+) days$/);
  if (globalAPNSMatch) return `Глобальный APNs сертификат действителен ${globalAPNSMatch[1]} дн.`;
  const activeWebhooksMatch = detail.match(/^(\d+) active webhooks$/);
  if (activeWebhooksMatch) return `Активных вебхуков: ${activeWebhooksMatch[1]}`;
  const failedDeliveriesMatch = detail.match(/^(\d+) failed deliveries in 24h$/);
  if (failedDeliveriesMatch) return `Ошибок доставки за 24 часа: ${failedDeliveriesMatch[1]}`;
  const agentsMatch = detail.match(/^(\d+) agents$/);
  if (agentsMatch) return `Агентов: ${agentsMatch[1]}`;
  const agentIssuesMatch = detail.match(/^(\d+) stale, (\d+) with errors$/);
  if (agentIssuesMatch) return `Неактивных: ${agentIssuesMatch[1]}, с ошибками: ${agentIssuesMatch[2]}`;
  const ddmMatch = detail.match(/^(\d+) protocol captures in 24h$/);
  if (ddmMatch) return `DDM-событий за 24 часа: ${ddmMatch[1]}`;
  const queueMatch = detail.match(/^(\d+) queued, (\d+) sent(?:, (\d+) historical dead letter)?$/);
  if (queueMatch) {
    const dead = queueMatch[3] ? `, старых ошибок: ${queueMatch[3]}` : "";
    return `В очереди: ${queueMatch[1]}, отправлено: ${queueMatch[2]}${dead}`;
  }
  const deadLetterMatch = detail.match(/^(\d+) commands moved to dead letter in 24h$/);
  if (deadLetterMatch) return `Команд с ошибкой за 24 часа: ${deadLetterMatch[1]}`;
  const notNowDeadMatch = detail.match(/^(\d+) commands deferred by devices in 24h$/);
  if (notNowDeadMatch) return `Устройства отложили команд за 24 часа: ${notNowDeadMatch[1]}`;
  const staleSentMatch = detail.match(/^(\d+) sent commands stale for more than 30m$/);
  if (staleSentMatch) return `Зависших отправленных команд более 30 минут: ${staleSentMatch[1]}`;
  const ru = {
    "APNs is not configured": "APNs не настроен",
    "global APNs certificate expired": "Глобальный APNs сертификат истек",
    "certificate expiry is unknown": "Срок действия сертификата неизвестен",
    "certificate expired": "Сертификат истек",
    "ADE token is not configured": "ADE token не настроен",
    "VPP token is not configured": "VPP token не настроен",
    "token is not configured": "Token не настроен",
    configured: "Настроено",
    "last sync failed": "Последняя синхронизация завершилась ошибкой",
    "sync has not run yet": "Синхронизация еще не запускалась",
    "no events recorded": "Событий пока нет",
    "no active webhooks": "Активных вебхуков нет",
    "no agents enrolled": "Агенты не зарегистрированы",
    "no DDM traffic captured": "DDM-трафик не получен",
    "email alert delivery available": "Отправка e-mail уведомлений доступна"
  };
  return ru[detail] || detail;
}

function summarizeVisibleHealth(checks) {
  let overall = "ok";
  for (const check of checks || []) {
    if (check?.status === "critical") return "critical";
    if (check?.status === "warning" && overall !== "critical") overall = "warning";
    if (check?.status === "unknown" && overall === "ok") overall = "unknown";
  }
  return overall;
}

function commandCenterStateLabel(value, lang) {
  const normalized = String(value || "").toLowerCase();
  const ru = { queued: "В очереди", sent: "Отправлена", done: "Завершена", dead_letter: "Ошибка", canceled: "Отменена" };
  const en = { queued: "Queued", sent: "Sent", done: "Done", dead_letter: "Failed", canceled: "Canceled" };
  return (lang === "ru" ? ru : en)[normalized] || value || "-";
}

function commandCenterStateClass(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "done") return "status-pill ok";
  if (normalized === "dead_letter") return "status-pill danger";
  if (normalized === "queued" || normalized === "sent") return "status-pill warning";
  return "status-pill muted";
}

function organizationSettingsToDraft(value) {
  const tenant = value?.tenant || {};
  return {
    name: String(tenant.name || ""),
    primary_domain: String(tenant.primary_domain || ""),
    plan: String(tenant.plan || "business"),
    billing_status: String(tenant.billing_status || "manual"),
    billing_email: String(tenant.billing_email || ""),
    trial_ends_at: tenantDateInputValue(tenant.trial_ends_at),
    subscription_ends_at: tenantDateInputValue(tenant.subscription_ends_at),
    device_limit: tenant.device_limit ?? "",
    account_limit: tenant.account_limit ?? "",
    admin_limit: tenant.admin_limit ?? "",
    timezone: String(value?.timezone || "UTC"),
    data_retention_days: value?.data_retention_days || 365,
    support_email: String(value?.support_email || ""),
    security_email: String(value?.security_email || "")
  };
}

function optionalInteger(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function optionalDateISOString(value) {
  const raw = String(value || "").trim();
  return raw ? `${raw}T00:00:00Z` : null;
}

function buildColumnsPrefsScope(sessionLike) {
  const userKey = String(sessionLike?.user?.id || sessionLike?.user?.login || "")
    .trim()
    .toLowerCase();
  if (!userKey) return "";
  const apiBase = normalizeAPIBase(sessionLike?.apiBase || DEFAULT_API_BASE).toLowerCase();
  return `${apiBase}::${userKey}`;
}

function columnsPrefsStorageKey(scope) {
  const safeScope = String(scope || "").trim();
  if (!safeScope) return "";
  return `${STORAGE_COLUMNS_PREFS_PREFIX}:${safeScope}`;
}

function readColumnsPrefs(scope) {
  if (typeof window === "undefined") return {};
  const key = columnsPrefsStorageKey(scope);
  if (!key) return {};
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeColumnsPrefs(scope, value) {
  if (typeof window === "undefined") return;
  const key = columnsPrefsStorageKey(scope);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(value || {}));
  } catch {
    // ignore localStorage write failures (quota/private mode)
  }
}

const CSRF_COOKIE_NAME = "turuk_admin_csrf";

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const encoded = `${encodeURIComponent(name)}=`;
  const found = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(encoded));
  return found ? decodeURIComponent(found.slice(encoded.length)) : "";
}

function isUnsafeCSRFMethod(method) {
  const normalized = String(method || "GET").trim().toUpperCase();
  return normalized !== "GET" && normalized !== "HEAD" && normalized !== "OPTIONS";
}

function sessionIsAuthenticated(value) {
  return Boolean(value?.authMode === "cookie" || value?.token);
}

function sessionStoragePayload(value) {
  if (!value) return null;
  return {
    apiBase: normalizeAPIBase(value.apiBase || DEFAULT_API_BASE),
    authMode: "cookie",
    csrfToken: String(value.csrfToken || "").trim(),
    user: value.user || null,
    expiresAt: value.expiresAt || ""
  };
}

function normalizeSession(raw) {
  if (!raw || typeof raw !== "object") return null;
  const authMode = raw.authMode === "bearer" && raw.token ? "bearer" : "cookie";
  return {
    apiBase: normalizeAPIBase(raw.apiBase || DEFAULT_API_BASE),
    authMode,
    token: authMode === "bearer" ? String(raw.token || "").trim() : "",
    csrfToken: String(raw.csrfToken || raw.csrf_token || "").trim(),
    user: raw.user || null,
    expiresAt: raw.expiresAt || raw.expires_at || ""
  };
}

function base64URLToBuffer(value) {
  const input = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
  const padded = input.padEnd(input.length + ((4 - (input.length % 4)) % 4), "=");
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

function bufferToBase64URL(buffer) {
  const bytes = new Uint8Array(buffer || []);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function normalizePasskeyOptions(publicKey) {
  const out = { ...(publicKey || {}) };
  if (out.challenge) out.challenge = base64URLToBuffer(out.challenge);
  if (out.user?.id) out.user = { ...out.user, id: base64URLToBuffer(out.user.id) };
  if (Array.isArray(out.allowCredentials)) {
    out.allowCredentials = out.allowCredentials.map((item) => ({
      ...item,
      id: base64URLToBuffer(item.id)
    }));
  }
  if (Array.isArray(out.excludeCredentials)) {
    out.excludeCredentials = out.excludeCredentials.map((item) => ({
      ...item,
      id: base64URLToBuffer(item.id)
    }));
  }
  return out;
}

function publicKeyCredentialToJSON(credential) {
  if (!credential) return null;
  const response = credential.response || {};
  const out = {
    id: credential.id,
    rawId: bufferToBase64URL(credential.rawId),
    type: credential.type,
    response: {},
    clientExtensionResults: credential.getClientExtensionResults
      ? credential.getClientExtensionResults()
      : {}
  };
  if (credential.authenticatorAttachment) {
    out.authenticatorAttachment = credential.authenticatorAttachment;
  }
  [
    "clientDataJSON",
    "attestationObject",
    "authenticatorData",
    "signature",
    "userHandle"
  ].forEach((key) => {
    if (response[key]) out.response[key] = bufferToBase64URL(response[key]);
  });
  return out;
}

function roleDefaultPermission(role, action) {
  const normalizedRole = String(role || "").trim().toLowerCase();
  if (normalizedRole === "master-admin" || normalizedRole === "admin") return true;
  if (normalizedRole === "read-only") return action === "view";
  return false;
}

function normalizeRolePermissionsClient(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  Object.entries(raw).forEach(([key, value]) => {
    const permissionKey = String(key || "").trim().toLowerCase();
    if (!permissionKey) return;
    const view = Boolean(value?.view || value?.edit);
    const edit = Boolean(value?.edit);
    out[permissionKey] = { view, edit };
  });
  return out;
}

function rolePermissionAllowed(rolePermissions, role, permissionKey, action = "view") {
  const normalizedKey = String(permissionKey || "").trim().toLowerCase();
  const normalizedAction = action === "edit" ? "edit" : "view";
  if (!normalizedKey) return true;
  const perms = normalizeRolePermissionsClient(rolePermissions);
  let current = normalizedKey;
  while (current) {
    const value = perms[current];
    if (value) {
      if (normalizedAction === "edit") return Boolean(value.edit);
      return Boolean(value.view || value.edit);
    }
    const idx = current.lastIndexOf(".");
    if (idx < 0) break;
    current = current.slice(0, idx);
  }
  return roleDefaultPermission(role, normalizedAction);
}

function buildEmptyRolePermissionsState() {
  const out = {};
  ROLE_PERMISSION_GROUPS.forEach((group) => {
    (group.items || []).forEach((item) => {
      if (!item?.key) return;
      out[item.key] = { view: false, edit: false };
    });
  });
  return out;
}

function mergeRolePermissionsWithCatalog(raw) {
  const out = buildEmptyRolePermissionsState();
  const normalized = normalizeRolePermissionsClient(raw);
  Object.entries(normalized).forEach(([key, value]) => {
    if (!Object.prototype.hasOwnProperty.call(out, key)) return;
    out[key] = {
      view: Boolean(value?.view || value?.edit),
      edit: Boolean(value?.edit)
    };
  });
  return out;
}

function normalizeRoleAccessScopeClient(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const cleanList = (items) =>
    Array.from(
      new Set(
        (Array.isArray(items) ? items : [])
          .map((item) => String(item || "").trim())
          .filter(Boolean)
      )
    );
  const platforms = cleanList(source.platforms)
    .map((item) => {
      const value = item.toLowerCase();
      if (["ios", "ipados", "ipad", "iphone"].includes(value)) return "ios";
      if (["mac", "macos", "osx"].includes(value)) return "macos";
      if (["win", "windows"].includes(value)) return "windows";
      if (value === "android") return "android";
      return "";
    })
    .filter(Boolean);
  return {
    group_ids: cleanList(source.group_ids),
    platforms: Array.from(new Set(platforms)),
    location_ids: cleanList(source.location_ids)
  };
}

function roleAccessScopePayload(raw) {
  const normalized = normalizeRoleAccessScopeClient(raw);
  return {
    group_ids: normalized.group_ids,
    platforms: normalized.platforms,
    location_ids: normalized.location_ids
  };
}

function parseRoleScopeLocations(value) {
  return Array.from(
    new Set(
      String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function defaultAlertNotificationSettings() {
  return {
    enabled: true,
    in_app_enabled: true,
    email_enabled: false,
    webhook_enabled: false,
    email_recipients: [],
    webhook_url: "",
    apns_expiry_days: 30,
    device_offline_days: 7,
    enrollment_spike_window_minutes: 60,
    enrollment_spike_threshold: 25,
    alert_apns_expiring: true,
    alert_ade_sync_failed: true,
    alert_vpp_sync_failed: true,
    alert_device_offline: true,
    alert_enrollment_spike: true,
    alert_suspicious_admin_login: true,
    alert_management_profile_removed: true
  };
}

function normalizeAlertNotificationSettings(raw) {
  const defaults = defaultAlertNotificationSettings();
  const source = raw && typeof raw === "object" ? raw : {};
  const cleanNumber = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
  };
  return {
    ...defaults,
    ...source,
    enabled: source.enabled !== undefined ? Boolean(source.enabled) : defaults.enabled,
    in_app_enabled: source.in_app_enabled !== undefined ? Boolean(source.in_app_enabled) : defaults.in_app_enabled,
    email_enabled: Boolean(source.email_enabled),
    webhook_enabled: Boolean(source.webhook_enabled),
    email_recipients: Array.isArray(source.email_recipients) ? source.email_recipients.filter(Boolean) : [],
    webhook_url: String(source.webhook_url || ""),
    apns_expiry_days: cleanNumber(source.apns_expiry_days, defaults.apns_expiry_days),
    device_offline_days: cleanNumber(source.device_offline_days, defaults.device_offline_days),
    enrollment_spike_window_minutes: cleanNumber(
      source.enrollment_spike_window_minutes,
      defaults.enrollment_spike_window_minutes
    ),
    enrollment_spike_threshold: cleanNumber(source.enrollment_spike_threshold, defaults.enrollment_spike_threshold),
    alert_apns_expiring: source.alert_apns_expiring !== undefined ? Boolean(source.alert_apns_expiring) : true,
    alert_ade_sync_failed: source.alert_ade_sync_failed !== undefined ? Boolean(source.alert_ade_sync_failed) : true,
    alert_vpp_sync_failed: source.alert_vpp_sync_failed !== undefined ? Boolean(source.alert_vpp_sync_failed) : true,
    alert_device_offline: source.alert_device_offline !== undefined ? Boolean(source.alert_device_offline) : true,
    alert_enrollment_spike: source.alert_enrollment_spike !== undefined ? Boolean(source.alert_enrollment_spike) : true,
    alert_suspicious_admin_login:
      source.alert_suspicious_admin_login !== undefined ? Boolean(source.alert_suspicious_admin_login) : true,
    alert_management_profile_removed:
      source.alert_management_profile_removed !== undefined ? Boolean(source.alert_management_profile_removed) : true
  };
}

function alertSeverityLabelClass(severity) {
  const normalized = String(severity || "").toLowerCase();
  if (normalized === "critical") return "status-pill danger";
  if (normalized === "warning") return "status-pill warning";
  return "status-pill muted";
}

function supportSessionStatusLabel(status, t) {
  switch (String(status || "").toLowerCase()) {
    case "approved":
      return t("supportSessionsStatusApproved");
    case "revoked":
      return t("supportSessionsStatusRevoked");
    case "expired":
      return t("supportSessionsStatusExpired");
    case "pending":
    default:
      return t("supportSessionsStatusPending");
  }
}

function defaultReportScheduleSettings() {
  return {
    enabled: false,
    frequency: "weekly",
    recipients: [],
    report_type: "executive",
    format: "pdf"
  };
}

function normalizeReportScheduleSettings(raw) {
  const defaults = defaultReportScheduleSettings();
  const source = raw && typeof raw === "object" ? raw : {};
  const frequency = ["daily", "weekly", "monthly"].includes(String(source.frequency || "").toLowerCase())
    ? String(source.frequency).toLowerCase()
    : defaults.frequency;
  const reportType = ["executive", "devices", "accounts", "groups", "apps"].includes(
    String(source.report_type || "").toLowerCase()
  )
    ? String(source.report_type).toLowerCase()
    : defaults.report_type;
  const format = String(source.format || "").toLowerCase() === "csv" ? "csv" : "pdf";
  return {
    enabled: Boolean(source.enabled),
    frequency,
    recipients: Array.isArray(source.recipients) ? source.recipients.filter(Boolean) : [],
    report_type: reportType,
    format
  };
}

const APPLE_DEVICE_COMMANDS = [
  {
    key: "erase_device",
    labelKey: "deviceDetailsAppleCommandEraseDevice",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["ios", "macos"],
    destructive: true,
    example: { PreserveDataPlan: false, DisallowProximitySetup: false }
  },
  {
    key: "return_to_service",
    labelKey: "deviceDetailsAppleCommandReturnToService",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["ios", "macos"],
    destructive: true,
    example: { ReturnToService: { Enabled: true }, PreserveDataPlan: false }
  },
  {
    key: "restrictions",
    labelKey: "deviceDetailsAppleCommandRestrictions",
    sectionTitleKey: "deviceDetailsAppleCommandsInventory",
    platforms: ["ios", "macos"],
    hidden: true,
    example: {}
  },
  {
    key: "activation_lock_bypass_code",
    labelKey: "deviceDetailsAppleCommandActivationLockBypassCode",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["ios", "macos"],
    hidden: true,
    example: {}
  },
  {
    key: "clear_activation_lock_bypass_code",
    labelKey: "deviceDetailsAppleCommandClearActivationLockBypassCode",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["ios", "macos"],
    destructive: true,
    hidden: true,
    example: {}
  },
  {
    key: "rotate_filevault_key",
    labelKey: "deviceDetailsAppleCommandRotateFileVaultKey",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["macos"],
    destructive: true,
    example: { KeyType: "personal" }
  },
  {
    key: "set_recovery_lock",
    labelKey: "deviceDetailsAppleCommandSetRecoveryLock",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["macos"],
    destructive: true,
    example: { NewPassword: "", CurrentPassword: "" }
  },
  {
    key: "verify_recovery_lock",
    labelKey: "deviceDetailsAppleCommandVerifyRecoveryLock",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["macos"],
    example: { Password: "" }
  },
  {
    key: "clear_restrictions_password",
    labelKey: "deviceDetailsAppleCommandClearRestrictionsPassword",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["ios"],
    destructive: true,
    example: {}
  },
  {
    key: "set_firmware_password",
    labelKey: "deviceDetailsAppleCommandSetFirmwarePassword",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["macos"],
    destructive: true,
    example: { NewPassword: "", CurrentPassword: "" }
  },
  {
    key: "verify_firmware_password",
    labelKey: "deviceDetailsAppleCommandVerifyFirmwarePassword",
    sectionTitleKey: "deviceDetailsAppleCommandsSecurity",
    platforms: ["macos"],
    example: { Password: "" }
  },
  {
    key: "managed_application_list",
    labelKey: "deviceDetailsAppleCommandManagedApplicationList",
    sectionTitleKey: "deviceDetailsAppleCommandsInventory",
    platforms: ["ios", "macos"],
    hidden: true,
    example: {}
  },
  {
    key: "certificate_list",
    labelKey: "deviceDetailsAppleCommandCertificateList",
    sectionTitleKey: "deviceDetailsAppleCommandsInventory",
    platforms: ["ios", "macos"],
    hidden: true,
    example: {}
  },
  {
    key: "provisioning_profile_list",
    labelKey: "deviceDetailsAppleCommandProvisioningProfileList",
    sectionTitleKey: "deviceDetailsAppleCommandsInventory",
    platforms: ["ios", "macos"],
    hidden: true,
    example: {}
  },
  {
    key: "content_caching_information",
    labelKey: "deviceDetailsAppleCommandContentCachingInformation",
    sectionTitleKey: "deviceDetailsAppleCommandsInventory",
    platforms: ["macos"],
    example: {}
  },
  {
    key: "install_provisioning_profile",
    labelKey: "deviceDetailsAppleCommandInstallProvisioningProfile",
    sectionTitleKey: "deviceDetailsAppleCommandsProfilesApps",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { ProvisioningProfile: "BASE64_ENCODED_PROVISIONING_PROFILE" }
  },
  {
    key: "remove_provisioning_profile",
    labelKey: "deviceDetailsAppleCommandRemoveProvisioningProfile",
    sectionTitleKey: "deviceDetailsAppleCommandsProfilesApps",
    platforms: ["ios", "macos"],
    destructive: true,
    hidden: true,
    example: { UUID: "PROVISIONING_PROFILE_UUID" }
  },
  {
    key: "validate_applications",
    labelKey: "deviceDetailsAppleCommandValidateApplications",
    sectionTitleKey: "deviceDetailsAppleCommandsProfilesApps",
    platforms: ["ios"],
    hidden: true,
    example: {}
  },
  {
    key: "install_enterprise_application",
    labelKey: "deviceDetailsAppleCommandInstallEnterpriseApplication",
    sectionTitleKey: "deviceDetailsAppleCommandsProfilesApps",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { ManifestURL: "https://example.com/manifest.plist", ManagementFlags: 1 }
  },
  {
    key: "managed_application_attributes",
    labelKey: "deviceDetailsAppleCommandManagedApplicationAttributes",
    sectionTitleKey: "deviceDetailsAppleCommandsProfilesApps",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { Identifiers: ["com.example.app"] }
  },
  {
    key: "managed_application_configuration",
    labelKey: "deviceDetailsAppleCommandManagedApplicationConfiguration",
    sectionTitleKey: "deviceDetailsAppleCommandsProfilesApps",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { Identifiers: ["com.example.app"] }
  },
  {
    key: "managed_application_feedback",
    labelKey: "deviceDetailsAppleCommandManagedApplicationFeedback",
    sectionTitleKey: "deviceDetailsAppleCommandsProfilesApps",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { Identifiers: ["com.example.app"] }
  },
  {
    key: "refresh_cellular_plans",
    labelKey: "deviceDetailsAppleCommandRefreshCellularPlans",
    sectionTitleKey: "deviceDetailsAppleCommandsProfilesApps",
    platforms: ["ios"],
    example: {}
  },
  {
    key: "user_list",
    labelKey: "deviceDetailsAppleCommandUserList",
    sectionTitleKey: "deviceDetailsAppleCommandsUsers",
    platforms: ["macos"],
    example: {}
  },
  {
    key: "unlock_user_account",
    labelKey: "deviceDetailsAppleCommandUnlockUserAccount",
    sectionTitleKey: "deviceDetailsAppleCommandsUsers",
    platforms: ["macos"],
    example: { UserName: "local_user" }
  },
  {
    key: "log_out_user",
    labelKey: "deviceDetailsAppleCommandLogOutUser",
    sectionTitleKey: "deviceDetailsAppleCommandsUsers",
    platforms: ["macos"],
    destructive: true,
    example: { UserName: "local_user" }
  },
  {
    key: "delete_user",
    labelKey: "deviceDetailsAppleCommandDeleteUser",
    sectionTitleKey: "deviceDetailsAppleCommandsUsers",
    platforms: ["macos"],
    destructive: true,
    example: { UserName: "local_user", ForceDeletion: false }
  },
  {
    key: "account_configuration",
    labelKey: "deviceDetailsAppleCommandAccountConfiguration",
    sectionTitleKey: "deviceDetailsAppleCommandsUsers",
    platforms: ["macos"],
    destructive: true,
    example: { AutoSetupAdminAccounts: [], SkipPrimarySetupAccountCreation: false }
  },
  {
    key: "set_auto_admin_password",
    labelKey: "deviceDetailsAppleCommandSetAutoAdminPassword",
    sectionTitleKey: "deviceDetailsAppleCommandsUsers",
    platforms: ["macos"],
    destructive: true,
    example: { PasswordHash: "" }
  },
  {
    key: "enable_remote_desktop",
    labelKey: "deviceDetailsAppleCommandEnableRemoteDesktop",
    sectionTitleKey: "deviceDetailsAppleCommandsMacManagement",
    platforms: ["macos"],
    example: {}
  },
  {
    key: "disable_remote_desktop",
    labelKey: "deviceDetailsAppleCommandDisableRemoteDesktop",
    sectionTitleKey: "deviceDetailsAppleCommandsMacManagement",
    platforms: ["macos"],
    destructive: true,
    example: {}
  },
  {
    key: "request_mirroring",
    labelKey: "deviceDetailsAppleCommandRequestMirroring",
    sectionTitleKey: "deviceDetailsAppleCommandsMacManagement",
    platforms: ["macos"],
    example: { DestinationName: "", ScanTime: 30 }
  },
  {
    key: "stop_mirroring",
    labelKey: "deviceDetailsAppleCommandStopMirroring",
    sectionTitleKey: "deviceDetailsAppleCommandsMacManagement",
    platforms: ["macos"],
    destructive: true,
    example: {}
  },
  {
    key: "install_media",
    labelKey: "deviceDetailsAppleCommandInstallMedia",
    sectionTitleKey: "deviceDetailsAppleCommandsContent",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { MediaURL: "https://example.com/media" }
  },
  {
    key: "remove_media",
    labelKey: "deviceDetailsAppleCommandRemoveMedia",
    sectionTitleKey: "deviceDetailsAppleCommandsContent",
    platforms: ["ios", "macos"],
    destructive: true,
    hidden: true,
    example: { PersistentID: "" }
  },
  {
    key: "apply_redemption_code",
    labelKey: "deviceDetailsAppleCommandApplyRedemptionCode",
    sectionTitleKey: "deviceDetailsAppleCommandsContent",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { Identifier: "com.example.app", RedemptionCode: "" }
  },
  {
    key: "managed_media_list",
    labelKey: "deviceDetailsAppleCommandManagedMediaList",
    sectionTitleKey: "deviceDetailsAppleCommandsContent",
    platforms: ["ios", "macos"],
    hidden: true,
    example: {}
  },
  {
    key: "active_nsextensions_mappings",
    labelKey: "deviceDetailsAppleCommandActiveNSExtensionsMappings",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["ios", "macos"],
    hidden: true,
    example: {}
  },
  {
    key: "nsextensions_mappings",
    labelKey: "deviceDetailsAppleCommandNSExtensionsMappings",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["macos"],
    example: {}
  },
  {
    key: "invite_to_program",
    labelKey: "deviceDetailsAppleCommandInviteToProgram",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { ProgramID: "com.apple.cloudvpp", InvitationURL: "https://example.com/invite" }
  },
  {
    key: "lom_device_request",
    labelKey: "deviceDetailsAppleCommandLOMDeviceRequest",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["macos"],
    example: { RequestList: [] }
  },
  {
    key: "lom_setup_request",
    labelKey: "deviceDetailsAppleCommandLOMSetupRequest",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["macos"],
    example: {}
  },
  {
    key: "declarative_management",
    labelKey: "deviceDetailsAppleCommandDeclarativeManagement",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { Data: {} }
  },
  {
    key: "user_configured",
    labelKey: "deviceDetailsAppleCommandUserConfigured",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["macos"],
    example: {}
  },
  {
    key: "device_configured",
    labelKey: "deviceDetailsAppleCommandDeviceConfigured",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["ios", "macos"],
    hidden: true,
    example: {}
  },
  {
    key: "settings",
    labelKey: "deviceDetailsAppleCommandSettings",
    sectionTitleKey: "deviceDetailsAppleCommandsAdvanced",
    platforms: ["ios", "macos"],
    hidden: true,
    example: { Settings: [{ Item: "ApplicationAttributes", Attributes: {} }] }
  }
];

function appleDeviceCommandsForPlatform(platform) {
  const normalized = String(platform || "").toLowerCase();
  return APPLE_DEVICE_COMMANDS.filter((command) => {
    if (command.hidden) return false;
    const platforms = Array.isArray(command.platforms) ? command.platforms : ["ios", "macos"];
    return platforms.includes("all") || platforms.includes(normalized);
  });
}

function appleDeviceCommandSections(commands) {
  const sections = [];
  const byKey = new Map();
  commands.forEach((command) => {
    const sectionKey = command.sectionTitleKey || "deviceDetailsAppleCommandsAdvanced";
    if (!byKey.has(sectionKey)) {
      const section = { sectionTitleKey: sectionKey, items: [] };
      byKey.set(sectionKey, section);
      sections.push(section);
    }
    byKey.get(sectionKey).items.push(command);
  });
  return sections;
}

function isAppleDeviceCommandDestructive(commandKey) {
  const safeKey = String(commandKey || "").trim();
  return APPLE_DEVICE_COMMANDS.some((command) => command.key === safeKey && command.destructive === true);
}

function isDestructiveAppleCommandRequest(path, options = {}) {
  const safePath = String(path || "").startsWith("/") ? String(path || "") : `/${String(path || "")}`;
  if (!safePath.endsWith("/commands/apple")) return false;
  try {
    const body = JSON.parse(String(options?.body || "{}"));
    return isAppleDeviceCommandDestructive(body?.command);
  } catch {
    return true;
  }
}

function isSensitiveConsoleRequest(path, options = {}) {
  const safePath = String(path || "").startsWith("/") ? String(path || "") : `/${String(path || "")}`;
  const normalizedMethod = String(options?.method || "GET").toUpperCase();
  if (normalizedMethod === "GET") {
    return safePath.endsWith("/certificates/configurator-profile") ||
      safePath.endsWith("/tenant/export") ||
      safePath.endsWith("/tenants/export");
  }
  if (normalizedMethod !== "POST") return false;
  return [
    "/api-keys",
    "/api-keys/revoke",
    "/webhooks",
    "/webhooks/deactivate",
    "/security/settings",
    "/security/sessions/revoke",
    "/role-settings",
    "/role-settings/delete",
    "/alert-settings",
    "/alerts/evaluate",
    "/alerts/ack",
    "/reports/schedule",
    "/console-users",
    "/console-users/update",
    "/console-users/revoke",
    "/tenants",
    "/tenants/update",
    "/ade/server-token",
    "/ade/profiles",
    "/ade/sync",
    "/ade/assign-profile",
    "/mdm-apns/config",
    "/mdm-apns/csr",
    "/security/users/password",
    "/security/users/mfa-required",
    "/devices/delete",
    "/accounts/delete",
    "/groups/delete",
    "/groups/set-apps",
    "/groups/set-profiles",
    "/notifications",
    "/document-folders/delete",
    "/documents/status",
    "/documents/delete",
    "/documents/set-groups",
    "/profiles/delete",
    "/profiles/deploy",
    "/apps/delete",
    "/apps/deploy",
    "/enroll/rotate-token"
  ].some((suffix) => safePath.endsWith(suffix)) || isDestructiveAppleCommandRequest(safePath, options);
}

function App() {
  const [lang, setLang] = useState(() => localStorage.getItem(STORAGE_LANG) || "ru");
  const [publicTenantBranding, setPublicTenantBranding] = useState(() =>
    normalizePublicTenantBootstrap(null)
  );
  const [publicSSOConfig, setPublicSSOConfig] = useState(null);
  const [session, setSession] = useState(() => {
    const raw = localStorage.getItem(STORAGE_SESSION);
    if (!raw) return null;
    try {
      return normalizeSession(JSON.parse(raw));
    } catch {
      return null;
    }
  });

  const [activePage, setActivePage] = useState(() =>
    typeof window === "undefined" ? "dashboard" : pageFromHash(window.location.hash)
  );
  const prevProfilesWorkspacePageRef = useRef(activePage);
  const prevAppsWorkspacePageRef = useRef(activePage);
  const sessionRestoreAttemptedRef = useRef(false);
  const complianceGroupDropdownRef = useRef(null);
  const complianceFieldDropdownRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [commands, setCommands] = useState([]);
  const [commandCenter, setCommandCenter] = useState({ items: [], summary: {} });
  const [commandCenterFilters, setCommandCenterFilters] = useState({
    state: "",
    requestType: "",
    search: ""
  });
  const [selectedCommandCenterUUIDs, setSelectedCommandCenterUUIDs] = useState([]);
  const [systemHealth, setSystemHealth] = useState({ overall: "unknown", checks: [] });
  const [organizationSettings, setOrganizationSettings] = useState(null);
  const [organizationDraft, setOrganizationDraft] = useState({
    name: "",
    primary_domain: "",
    plan: "business",
    billing_status: "manual",
    billing_email: "",
    trial_ends_at: "",
    subscription_ends_at: "",
    device_limit: "",
    account_limit: "",
    admin_limit: "",
    timezone: "UTC",
    data_retention_days: 365,
    support_email: "",
    security_email: ""
  });
  const [accounts, setAccounts] = useState([]);
  const [accountDevices, setAccountDevices] = useState([]);
  const [accountMobileBindings, setAccountMobileBindings] = useState([]);
  const [groups, setGroups] = useState([]);
  const [mobileNotifications, setMobileNotifications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentFolders, setDocumentFolders] = useState([]);
  const [selectedDocumentFolderID, setSelectedDocumentFolderID] = useState("all");
  const [documentAckReport, setDocumentAckReport] = useState([]);
  const [documentAckReportFilterID, setDocumentAckReportFilterID] = useState("");
  const [documentAssignedDevices, setDocumentAssignedDevices] = useState([]);
  const [documentAssignedDevicesItem, setDocumentAssignedDevicesItem] = useState(null);
  const [documentAssignedDevicesLoading, setDocumentAssignedDevicesLoading] = useState(false);
  const [documentGroupsItem, setDocumentGroupsItem] = useState(null);
  const [complianceRules, setComplianceRules] = useState([]);
  const [complianceIssues, setComplianceIssues] = useState([]);
  const [selectedComplianceIssueID, setSelectedComplianceIssueID] = useState("");
  const [selectedComplianceIssueDetail, setSelectedComplianceIssueDetail] = useState(null);
  const [selectedComplianceIssueLoading, setSelectedComplianceIssueLoading] = useState(false);
  const [showComplianceIssueFilters, setShowComplianceIssueFilters] = useState(false);
  const [expandedComplianceTimeline, setExpandedComplianceTimeline] = useState(false);
  const [complianceIssueVisibleCount, setComplianceIssueVisibleCount] = useState(25);
  const [complianceIssueFilters, setComplianceIssueFilters] = useState({
    status: "open",
    ruleId: "",
    deviceUDID: "",
    search: ""
  });
    const [complianceRuleForm, setComplianceRuleForm] = useState({
      id: "",
      groupId: "",
      groupIds: [],
      name: "",
      type: "passcode_required",
      platform: "ios",
	    targetId: "",
	    minimumOSVersion: "",
	    customField: "",
	    customOperator: "equals",
	    customValue: "",
	    severity: "warning",
    deadlineHours: 72,
	    notifyCompanion: true,
	    notifyEmail: false,
	    notificationEmail: "",
	    autoRemediation: false,
	    retryHours: 0,
	    escalationHours: 0,
	    escalationEmail: "",
	      isActive: true
		    });
	  const [complianceRuleTemplateKey, setComplianceRuleTemplateKey] = useState("");
	    const [complianceGroupDropdownOpen, setComplianceGroupDropdownOpen] = useState(false);
	    const [complianceFieldDropdownOpen, setComplianceFieldDropdownOpen] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    category: "news",
    severity: "info",
    title: "",
    body: "",
    actionLabel: "",
    actionURL: "",
    targetType: "all",
    targetID: "",
    expiresAt: ""
  });
  const [documentForm, setDocumentForm] = useState(() => createEmptyDocumentForm());
  const [documentModalMode, setDocumentModalMode] = useState("create");
  const [documentsSearch, setDocumentsSearch] = useState("");
  const [selectedDocumentIDs, setSelectedDocumentIDs] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profileDeployments, setProfileDeployments] = useState([]);
  const [appsCatalog, setAppsCatalog] = useState([]);
  const [appDeployments, setAppDeployments] = useState([]);
  const [consoleUsers, setConsoleUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [roleSettings, setRoleSettings] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [alertNotificationSettings, setAlertNotificationSettings] = useState(() =>
    defaultAlertNotificationSettings()
  );
  const [alertNotificationDraft, setAlertNotificationDraft] = useState(() =>
    defaultAlertNotificationSettings()
  );
  const [alertNotifications, setAlertNotifications] = useState([]);
  const [supportSessions, setSupportSessions] = useState([]);
  const [supportSessionForm, setSupportSessionForm] = useState({
    reason: "",
    ttlMinutes: 120
  });
  const [remoteScreenSessions, setRemoteScreenSessions] = useState([]);
  const [remoteScreenFrameURLs, setRemoteScreenFrameURLs] = useState({});
  const [remoteScreenMetrics, setRemoteScreenMetrics] = useState({});
  const [remoteAssistActions, setRemoteAssistActions] = useState({});
  const [remoteAssistDrafts, setRemoteAssistDrafts] = useState({});
  const [remoteScreenNow, setRemoteScreenNow] = useState(() => Date.now());
  const [remoteScreenForm, setRemoteScreenForm] = useState({
    accountId: "",
    deviceUdid: "",
    reason: "",
    ttlMinutes: 60
  });
  const activeRemoteScreenSessionIDs = remoteScreenSessions
    .filter((item) => String(item?.status || "").toLowerCase() === "active")
    .map((item) => String(item?.id || "").trim())
    .filter(Boolean)
    .join(",");
  const [reportScheduleSettings, setReportScheduleSettings] = useState(() =>
    defaultReportScheduleSettings()
  );
  const [reportScheduleDraft, setReportScheduleDraft] = useState(() =>
    defaultReportScheduleSettings()
  );
  const [auditFilters, setAuditFilters] = useState({
    actor: "",
    device: "",
    action: "",
    from: "",
    to: ""
  });
  const [tenantIsolationReport, setTenantIsolationReport] = useState(null);
  const [meta, setMeta] = useState(null);
  const [deviceDetail, setDeviceDetail] = useState(null);
  const [deviceApps, setDeviceApps] = useState([]);
  const [deviceAppDeployments, setDeviceAppDeployments] = useState([]);
  const [deviceAppStoreMetadata, setDeviceAppStoreMetadata] = useState({});
  const [deviceProfilesReported, setDeviceProfilesReported] = useState([]);
  const [deviceCertificates, setDeviceCertificates] = useState([]);
  const [deviceLocations, setDeviceLocations] = useState([]);
  const [deviceActivationLockCodes, setDeviceActivationLockCodes] = useState([]);
  const [deviceSharedIPadUsers, setDeviceSharedIPadUsers] = useState([]);
  const [deviceAttestations, setDeviceAttestations] = useState([]);
  const [showAllowedSIMModal, setShowAllowedSIMModal] = useState(false);
  const [allowedSIMDraft, setAllowedSIMDraft] = useState("");
  const [deviceMediaForm, setDeviceMediaForm] = useState({
    source: "book_store",
    adamId: "",
    mediaURL: "",
    persistentID: "",
    kind: "pdf",
    title: "",
    author: "",
    version: ""
  });
  const [deviceSettingForm, setDeviceSettingForm] = useState({
    settingType: "device_name",
    value: "",
    enabled: true
  });
  const [sharedIPadSettings, setSharedIPadSettings] = useState({
    managed_apple_account_domain: "",
    default_shared_ipad_domain: "",
    classroom_notes: ""
  });
  const [sharedIPadSettingsDraft, setSharedIPadSettingsDraft] = useState({
    managed_apple_account_domain: "",
    default_shared_ipad_domain: "",
    classroom_notes: ""
  });
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const [routeDeviceUDID, setRouteDeviceUDID] = useState(() =>
    typeof window === "undefined" ? "" : hashQueryValue(window.location.hash, "udid")
  );
  const deviceDetailsTab = DEVICE_DETAILS_PAGE_TO_TAB[activePage] || "about";
  const [accountsDevicesLoading, setAccountsDevicesLoading] = useState(false);
  const [accountsMobileBindingsLoading, setAccountsMobileBindingsLoading] = useState(false);
  const [selectedUdid, setSelectedUdid] = useState("");
  const [selectedDeviceAppBundleIDs, setSelectedDeviceAppBundleIDs] = useState([]);
  const [selectedDeviceProfileIDs, setSelectedDeviceProfileIDs] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [selectedAppId, setSelectedAppId] = useState("");
  const [devicesSearch, setDevicesSearch] = useState("");
  const [showDevicesColumnsMenu, setShowDevicesColumnsMenu] = useState(false);
  const [showDevicesFilters, setShowDevicesFilters] = useState(false);
  const [showAccountsFilters, setShowAccountsFilters] = useState(false);
  const [showDeviceCommandsMenu, setShowDeviceCommandsMenu] = useState(false);
  const deviceCommandsMenuRef = useRef(null);
  const [showBulkDeviceCommandsMenu, setShowBulkDeviceCommandsMenu] = useState(false);
  const bulkDeviceCommandsMenuRef = useRef(null);
  const [showEnableLostModeModal, setShowEnableLostModeModal] = useState(false);
  const [enableLostModeForm, setEnableLostModeForm] = useState({
    message: "",
    phoneNumber: ""
  });
  const [enableLostModeRequestLocation, setEnableLostModeRequestLocation] = useState(false);
  const [showScheduleOSUpdateModal, setShowScheduleOSUpdateModal] = useState(false);
  const [scheduleOSUpdateForm, setScheduleOSUpdateForm] = useState({
    installAction: "DownloadOnly",
    productKey: "",
    productVersion: ""
  });
  const [scheduleOSUpdateTargetUDIDs, setScheduleOSUpdateTargetUDIDs] = useState([]);
  const [showAppleCommandModal, setShowAppleCommandModal] = useState(false);
  const [showDeviceSettingModal, setShowDeviceSettingModal] = useState(false);
  const [showDeviceActivationLockModal, setShowDeviceActivationLockModal] = useState(false);
  const [appleCommandForm, setAppleCommandForm] = useState({
    command: null,
    parametersJSON: "{}"
  });
  const [macOSUEMSummary, setMacOSUEMSummary] = useState(null);
  const [macOSUEMLoading, setMacOSUEMLoading] = useState(false);
  const [macOSAgentOnboardingStatus, setMacOSAgentOnboardingStatus] = useState(null);
  const [macOSAgentTokens, setMacOSAgentTokens] = useState([]);
  const [macOSAgentTokenName, setMacOSAgentTokenName] = useState("");
  const [issuedMacOSAgentToken, setIssuedMacOSAgentToken] = useState(null);
  const [macOSJobForm, setMacOSJobForm] = useState({
    jobType: "script",
    title: "",
    script: "",
    packageURL: "",
    packageSHA256: "",
    adminUsername: "",
    adminFullName: "",
    adminPassword: "",
    rotatePassword: ""
  });
  const [macOSLibraryForm, setMacOSLibraryForm] = useState({
    itemType: "script",
    name: "",
    shell: "/bin/zsh",
    script: "",
    packageURL: "",
    sha256: "",
    installArgs: "",
    timeoutSeconds: "1800",
    retryMaxAttempts: "3",
    target: "device",
    groupID: "",
    scheduledAt: ""
  });
  const [macOSFileVaultForm, setMacOSFileVaultForm] = useState({
    enabled: true,
    escrowRequired: true,
    staleAfterDays: "180"
  });
  const [visibleDeviceColumns, setVisibleDeviceColumns] = useState(DEVICE_DEFAULT_COLUMN_KEYS);
  const [deviceFilterPlatform, setDeviceFilterPlatform] = useState("all");
  const [deviceFilterManaged, setDeviceFilterManaged] = useState("all");
  const [deviceFilterEncrypted, setDeviceFilterEncrypted] = useState("all");
  const [deviceFilterCompliance, setDeviceFilterCompliance] = useState("all");
  const [deviceFilterLastSeen, setDeviceFilterLastSeen] = useState("all");
  const [deviceFilterAccountName, setDeviceFilterAccountName] = useState(() =>
    typeof window === "undefined" ? "" : hashQueryValue(window.location.hash, "account_name")
  );
  const [profilesSearch, setProfilesSearch] = useState("");
  const [policiesSearch, setPoliciesSearch] = useState("");
  const [showProfilesColumnsMenu, setShowProfilesColumnsMenu] = useState(false);
  const [showPoliciesColumnsMenu, setShowPoliciesColumnsMenu] = useState(false);
  const [showProfilesFilters, setShowProfilesFilters] = useState(false);
  const [showPoliciesFilters, setShowPoliciesFilters] = useState(false);
  const [visibleProfileColumns, setVisibleProfileColumns] = useState(PROFILE_DEFAULT_COLUMN_KEYS);
  const [visiblePolicyColumns, setVisiblePolicyColumns] = useState(POLICY_DEFAULT_COLUMN_KEYS);
  const [profilesStatusFilter, setProfilesStatusFilter] = useState("all");
  const [policiesPlatformFilter, setPoliciesPlatformFilter] = useState("all");
  const [policiesComplianceFilter, setPoliciesComplianceFilter] = useState("all");
  const [selectedProfileIDs, setSelectedProfileIDs] = useState([]);
  const [selectedAppIDs, setSelectedAppIDs] = useState([]);
  const [appsSearch, setAppsSearch] = useState("");
  const [showAppsColumnsMenu, setShowAppsColumnsMenu] = useState(false);
  const [showAppsFilters, setShowAppsFilters] = useState(false);
  const [visibleAppColumns, setVisibleAppColumns] = useState(APP_DEFAULT_COLUMN_KEYS);
  const [appsSortKey, setAppsSortKey] = useState("name");
  const [appsSortDirection, setAppsSortDirection] = useState("asc");
  const [appsPlatformFilter, setAppsPlatformFilter] = useState("all");
  const [appsTypeFilter, setAppsTypeFilter] = useState("all");
  const [closedNavDropdownKey, setClosedNavDropdownKey] = useState("");
  const [showAppStoreModal, setShowAppStoreModal] = useState(false);
  const [appsStorePlatform, setAppsStorePlatform] = useState("ios");
  const [showPlatformPickerModal, setShowPlatformPickerModal] = useState(false);
  const [platformPickerContext, setPlatformPickerContext] = useState("");
  const [appsStoreQuery, setAppsStoreQuery] = useState("");
  const [appsStoreCountry, setAppsStoreCountry] = useState("US");
  const [appsStoreLoading, setAppsStoreLoading] = useState(false);
  const [appsStoreResults, setAppsStoreResults] = useState([]);
  const [appsManagedPlayURL, setAppsManagedPlayURL] = useState("");
  const [appsManagedPlayError, setAppsManagedPlayError] = useState("");
  const [appsManagedPlayReady, setAppsManagedPlayReady] = useState(false);
  const managedPlayAddInFlightRef = useRef(false);
  const managedPlayContainerRef = useRef(null);
  const managedPlayChildRef = useRef(null);
  const deviceAppLookupInFlightRef = useRef(new Set());
  const [accountsSearch, setAccountsSearch] = useState("");
  const [accountsActiveOnly, setAccountsActiveOnly] = useState(true);
  const [accountsWithDevicesOnly, setAccountsWithDevicesOnly] = useState(false);
  const [selectedDeviceUDIDs, setSelectedDeviceUDIDs] = useState([]);
  const [selectedAccountIDs, setSelectedAccountIDs] = useState([]);
  const [selectedGroupIDs, setSelectedGroupIDs] = useState([]);
  const [accountAssignUdid, setAccountAssignUdid] = useState("");
  const [profileDeployUdid, setProfileDeployUdid] = useState("");
  const [appDeployUdid, setAppDeployUdid] = useState("");
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showAccountImportModal, setShowAccountImportModal] = useState(false);
  const [accountImportFileName, setAccountImportFileName] = useState("");
  const [accountImportRows, setAccountImportRows] = useState([]);
  const [accountImportErrors, setAccountImportErrors] = useState([]);
  const [accountImportInFlight, setAccountImportInFlight] = useState(false);
  const [accountModalMode, setAccountModalMode] = useState("create");
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showAssignAccountsGroupsModal, setShowAssignAccountsGroupsModal] = useState(false);
  const [showAssignAppsGroupsModal, setShowAssignAppsGroupsModal] = useState(false);
  const [showAssignProfilesGroupsModal, setShowAssignProfilesGroupsModal] = useState(false);
  const [showCreateProfileModal, setShowCreateProfileModal] = useState(false);
  const [showProfileViewModal, setShowProfileViewModal] = useState(false);
  const [profileDevicesModalItem, setProfileDevicesModalItem] = useState(null);
  const [profileDevices, setProfileDevices] = useState([]);
  const [profileDevicesLoading, setProfileDevicesLoading] = useState(false);
  const [profileDevicesFilter, setProfileDevicesFilter] = useState("all");
  const createCertificatesInputRef = useRef(null);
  const viewCertificatesInputRef = useRef(null);
  const consoleLogoInputRef = useRef(null);
  const columnsPrefsHydratingRef = useRef(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [tenantForm, setTenantForm] = useState({
    id: "",
    name: "",
    slug: "",
    primaryDomain: "",
    status: "active",
    plan: "trial",
    billingStatus: "trialing",
    billingEmail: "",
    trialEndsAt: "",
    subscriptionEndsAt: "",
    stripeCustomerID: "",
    stripeSubscriptionID: "",
    deviceLimit: "",
    accountLimit: "",
    adminLimit: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminLogin: "master_admin",
    adminTemporaryPassword: ""
  });
  const [tenantDomainChecks, setTenantDomainChecks] = useState({});
  const [tenantDomainCheckingID, setTenantDomainCheckingID] = useState("");
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [selectedRoleKey, setSelectedRoleKey] = useState("");
  const [consoleLogoDataURL, setConsoleLogoDataURL] = useState("");
  const [consoleLogoDraftDataURL, setConsoleLogoDraftDataURL] = useState("");
  const [consoleLogoClickURL, setConsoleLogoClickURL] = useState("");
  const [consoleLogoDraftClickURL, setConsoleLogoDraftClickURL] = useState("");
  const [agentBranding, setAgentBranding] = useState({});
  const [agentBrandingDraft, setAgentBrandingDraft] = useState({});
  const [agentServices, setAgentServices] = useState([]);
  const [agentServicesDraft, setAgentServicesDraft] = useState([]);
  const [agentSupportTickets, setAgentSupportTickets] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [selectedAgentSupportTicket, setSelectedAgentSupportTicket] = useState(null);
  const [agentSupportTicketStatus, setAgentSupportTicketStatus] = useState("open");
  const [agentSupportTicketReply, setAgentSupportTicketReply] = useState("");
  const [mdmAPNSConfig, setMDMAPNSConfig] = useState(null);
  const [mdmAPNSReminders, setMDMAPNSReminders] = useState({ items: [], active: null });
  const [configuratorEnrollmentURL, setConfiguratorEnrollmentURL] = useState(null);
  const [mdmAPNSForm, setMDMAPNSForm] = useState({
    appleID: "",
    certPEM: ""
  });
  const [adeStatus, setADEStatus] = useState(null);
  const [adeDevices, setADEDevices] = useState([]);
  const [adeProfiles, setADEProfiles] = useState([]);
  const [adeTokenForm, setADETokenForm] = useState({
    appleID: "",
    file: null
  });
  const [adeProfileForm, setADEProfileForm] = useState({
    name: "",
    url: "",
    isDefault: true
  });
  const [vppStatus, setVPPStatus] = useState(null);
  const [vppAssets, setVPPAssets] = useState([]);
  const [vppAssignments, setVPPAssignments] = useState([]);
  const [adeSyncEvents, setADESyncEvents] = useState([]);
  const [vppSyncEvents, setVPPSyncEvents] = useState([]);
  const [vppTokenForm, setVPPTokenForm] = useState({
    appleID: "",
    file: null
  });
  const [vppAssignDrafts, setVPPAssignDrafts] = useState({});
  const [ddmDeclarations, setDDMDeclarations] = useState([]);
  const [ddmStatuses, setDDMStatuses] = useState([]);
  const [ddmProtocolCaptures, setDDMProtocolCaptures] = useState([]);
  const [expandedDDMProtocolCaptureID, setExpandedDDMProtocolCaptureID] = useState("");
  const [ddmStatusSubscriptions, setDDMStatusSubscriptions] = useState(defaultDDMStatusSubscriptions());
  const [ddmForm, setDDMForm] = useState(() => createEmptyDDMForm());
  const [ssoConfig, setSSOConfig] = useState(null);
  const [ssoForm, setSSOForm] = useState(() => createEmptySSOForm());
  const [webhooks, setWebhooks] = useState([]);
  const [webhookEventTypes, setWebhookEventTypes] = useState([]);
  const [webhookForm, setWebhookForm] = useState({
    id: "",
    name: "",
    url: "",
    secret: "",
    eventTypes: []
  });
  const [securitySettings, setSecuritySettings] = useState({
    password_policy: {
      min_length: 12,
      require_upper: true,
      require_lower: true,
      require_number: true,
      require_symbol: true,
      expires_days: 90,
      prevent_reuse: 5
    },
    account_lockout: {
      threshold: 8,
      window_minutes: 15,
      duration_minutes: 15
    },
    trusted_ips: {
      enabled: false,
      cidrs: []
    }
  });
  const [securitySettingsDraft, setSecuritySettingsDraft] = useState(securitySettings);
  const [securitySessions, setSecuritySessions] = useState([]);
  const [securitySessionsPage, setSecuritySessionsPage] = useState(1);
  const [dashboardSectionSettings, setDashboardSectionSettings] = useState(() => ({
    ...DEFAULT_DASHBOARD_SECTION_SETTINGS
  }));
  const [dashboardSectionDraftSettings, setDashboardSectionDraftSettings] = useState(() => ({
    ...DEFAULT_DASHBOARD_SECTION_SETTINGS
  }));
  const [dashboardSectionOrder, setDashboardSectionOrder] = useState(() =>
    normalizeDashboardSectionOrder(DEFAULT_DASHBOARD_SECTION_ORDER)
  );
  const [dashboardSectionDraftOrder, setDashboardSectionDraftOrder] = useState(() =>
    normalizeDashboardSectionOrder(DEFAULT_DASHBOARD_SECTION_ORDER)
  );
  const [dashboardSectionDraggingKey, setDashboardSectionDraggingKey] = useState("");
  const [mainNavOrder, setMainNavOrder] = useState(() =>
    normalizeMainNavOrder(DEFAULT_MAIN_NAV_ORDER)
  );
  const [mainNavDraftOrder, setMainNavDraftOrder] = useState(() =>
    normalizeMainNavOrder(DEFAULT_MAIN_NAV_ORDER)
  );
  const [mainNavDraggingKey, setMainNavDraggingKey] = useState("");
  const [profileCreateSection, setProfileCreateSection] = useState("general");
  const [profileCreateRestrictionsTab, setProfileCreateRestrictionsTab] = useState("functionality");
  const [profileConfiguredSections, setProfileConfiguredSections] = useState(() =>
    createProfileBuilderConfiguredState()
  );
  const [profileCreateWiFiNetworks, setProfileCreateWiFiNetworks] = useState([]);
  const [profileCreateSectionPayloads, setProfileCreateSectionPayloads] = useState(() =>
    createProfileSectionPayloadsState()
  );
  const [profileViewSection, setProfileViewSection] = useState("general");
  const [profileViewRestrictionsTab, setProfileViewRestrictionsTab] = useState("functionality");
  const [profileViewConfiguredSections, setProfileViewConfiguredSections] = useState(() =>
    createProfileBuilderConfiguredState()
  );
  const [profileViewWiFiNetworks, setProfileViewWiFiNetworks] = useState([]);
  const [profileViewSectionPayloads, setProfileViewSectionPayloads] = useState(() =>
    createProfileSectionPayloadsState()
  );
  const [profileViewIsEditing, setProfileViewIsEditing] = useState(false);
  const [showRestrictionsAppPickerModal, setShowRestrictionsAppPickerModal] = useState(false);
  const [restrictionsAppPickerContext, setRestrictionsAppPickerContext] = useState({
    target: "create",
    payloadIndex: -1,
    mode: "restrictions",
    sectionKey: "restrictions",
    fieldKey: ""
  });
  const [restrictionsAppPickerQuery, setRestrictionsAppPickerQuery] = useState("");
  const [restrictionsAppPickerLoading, setRestrictionsAppPickerLoading] = useState(false);
  const [restrictionsAppPickerResults, setRestrictionsAppPickerResults] = useState([]);
  const [showAirPlayDestinationModal, setShowAirPlayDestinationModal] = useState(false);
  const [airPlayDestinationContext, setAirPlayDestinationContext] = useState({
    target: "create",
    payloadIndex: -1
  });
  const [airPlayDestinationForm, setAirPlayDestinationForm] = useState({
    deviceName: "",
    password: ""
  });
  const [showAirPrintPrinterModal, setShowAirPrintPrinterModal] = useState(false);
  const [airPrintPrinterContext, setAirPrintPrinterContext] = useState({
    target: "create",
    payloadIndex: -1,
    rowIndex: -1
  });
  const [airPrintPrinterForm, setAirPrintPrinterForm] = useState({
    configureMode: "manual",
    hostName: "",
    forceTLS: false,
    port: "",
    resourcePath: ""
  });
  const [profileViewForm, setProfileViewForm] = useState(() => createEmptyProfileViewForm("ios"));

  const [infoMessage, setInfoMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [signInForm, setSignInForm] = useState({
    apiBase: normalizeAPIBase(DEFAULT_API_BASE),
    login: "",
    password: "",
    mfaCode: ""
  });
  const [signInMFARequired, setSignInMFARequired] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [securityAdminPasswordForm, setSecurityAdminPasswordForm] = useState({
    consoleUserId: "",
    newPassword: "",
    confirmPassword: "",
    mustChangePassword: true
  });
  const [securityAdminMFAForm, setSecurityAdminMFAForm] = useState({
    consoleUserId: "",
    required: true
  });
  const [mfaSetupForm, setMFASetupForm] = useState({
    currentPassword: "",
    code: "",
    secret: "",
    otpauthURL: "",
    qrSVG: ""
  });
  const [passkeySetupForm, setPasskeySetupForm] = useState({
    currentPassword: "",
    name: ""
  });
  const [reauthModal, setReauthModal] = useState({
    open: false,
    password: "",
    error: "",
    resolve: null,
    reject: null
  });

  const [createAccountForm, setCreateAccountForm] = useState(() => createEmptyAccountEditorForm());
  const [createGroupForm, setCreateGroupForm] = useState({
    name: ""
  });
  const [assignGroupsForm, setAssignGroupsForm] = useState({
    groupIds: []
  });
  const [assignAppGroupsForm, setAssignAppGroupsForm] = useState({
    groupIds: []
  });
  const [assignProfileGroupsForm, setAssignProfileGroupsForm] = useState({
    groupIds: []
  });
  const [profileCreateForm, setProfileCreateForm] = useState(() => createEmptyProfileCreateForm("ios"));
  const [profilePreview, setProfilePreview] = useState(null);
  const [editAccountForm, setEditAccountForm] = useState({
    id: "",
    name: "",
    email: "",
    externalId: "",
    notes: "",
    isActive: true
  });
  const [appForm, setAppForm] = useState({
    id: "",
    name: "",
    bundleId: "",
    platform: "ios",
    manifestUrl: "",
    itunesStoreId: "",
    description: "",
    assignmentType: "optional",
    updatePolicy: "manual",
    installPriority: "normal",
    uninstallProtected: false,
    perAppVpnUuid: "",
    managedConfigJSON: "{}",
    requestManagedFeedback: false,
    requestManagedAttributes: false,
    pipelineJSON: "{}"
  });
  const [createConsoleUserForm, setCreateConsoleUserForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    login: "",
    temporaryPassword: "",
    role: "read-only",
    mfaRequired: false
  });
  const [editConsoleUserForm, setEditConsoleUserForm] = useState({
    id: "",
    firstName: "",
    lastName: "",
    login: "",
    email: "",
    role: "read-only",
    mustChangePassword: false,
    mfaRequired: false,
    temporaryPassword: ""
  });
  const [createRoleForm, setCreateRoleForm] = useState({
    roleKey: "",
    displayName: "",
    permissions: buildEmptyRolePermissionsState(),
    accessScope: { group_ids: [], platforms: [], location_ids: [] }
  });
  const [editRoleForm, setEditRoleForm] = useState({
    roleKey: "",
    displayName: "",
    permissions: {},
    accessScope: { group_ids: [], platforms: [], location_ids: [] }
  });

  const t = (key) => text(lang, key);
  const roleTemplateLabel = (template) => {
    switch (template?.key) {
      case "helpdesk":
        return t("roleSettingsTemplateHelpdesk");
      case "security-admin":
        return t("roleSettingsTemplateSecurity");
      case "read-only-support-operator":
      default:
        return t("roleSettingsTemplateSupport");
    }
  };
  const roleTemplateHint = (template) => {
    switch (template?.key) {
      case "helpdesk":
        return t("roleSettingsTemplateHelpdeskHint");
      case "security-admin":
        return t("roleSettingsTemplateSecurityHint");
      case "read-only-support-operator":
      default:
        return t("roleSettingsTemplateSupportHint");
    }
  };
  const alertTriggerLabel = (key) => {
    switch (key) {
      case "alert_ade_sync_failed":
        return t("alertSettingsTriggerADE");
      case "alert_vpp_sync_failed":
        return t("alertSettingsTriggerVPP");
      case "alert_device_offline":
        return t("alertSettingsTriggerDeviceOffline");
      case "alert_enrollment_spike":
        return t("alertSettingsTriggerEnrollmentSpike");
      case "alert_suspicious_admin_login":
        return t("alertSettingsTriggerSuspiciousLogin");
      case "alert_management_profile_removed":
        return t("alertSettingsTriggerManagementProfileRemoved");
      case "alert_apns_expiring":
      default:
        return t("alertSettingsTriggerAPNS");
    }
  };
  const sessionRole = String(session?.user?.role || "").toLowerCase();
  const rolePermissions = useMemo(
    () => normalizeRolePermissionsClient(session?.user?.permissions || {}),
    [session?.user?.permissions]
  );
  const hasPermission = useCallback(
    (permissionKey, action = "view") =>
      rolePermissionAllowed(rolePermissions, sessionRole, permissionKey, action),
    [rolePermissions, sessionRole]
  );
  const canViewPage = useCallback(
    (page) => hasPermission(PAGE_PERMISSION_KEY[page] || "dashboard", "view"),
    [hasPermission]
  );
  const isAdminUI = hasPermission("dashboard", "edit");
  const mustChangePassword = !!session?.user?.must_change_password;
  const mfaSetupRequired = !!session?.user?.mfa_setup_required;
  const isAuthenticated = sessionIsAuthenticated(session);
  const columnsPrefsScope = useMemo(
    () => buildColumnsPrefsScope(session),
    [session?.apiBase, session?.user?.id, session?.user?.login]
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_LANG, lang);
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    rawRequest("/public/tenant", {}, { apiBase: DEFAULT_API_BASE })
      .then((out) => {
        if (!cancelled) {
          setPublicTenantBranding(normalizePublicTenantBootstrap(out));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublicTenantBranding(normalizePublicTenantBootstrap(null));
        }
      });
    rawRequest("/auth/sso/public-config", {}, { apiBase: DEFAULT_API_BASE })
      .then((out) => {
        if (!cancelled) {
          setPublicSSOConfig(out && typeof out === "object" ? out : null);
        }
      })
      .catch(() => {
        if (!cancelled) setPublicSSOConfig(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!session) {
      localStorage.removeItem(STORAGE_SESSION);
      return;
    }
    localStorage.setItem(STORAGE_SESSION, JSON.stringify(sessionStoragePayload(session)));
  }, [session]);

  useEffect(() => {
    if (session || sessionRestoreAttemptedRef.current) return;
    sessionRestoreAttemptedRef.current = true;

    const apiBase = normalizeAPIBase(signInForm.apiBase || DEFAULT_API_BASE);
    rawRequest("/auth/me", {}, { apiBase, authMode: "cookie", csrfToken: readCookie(CSRF_COOKIE_NAME) })
      .then((out) => {
        if (!out?.user) return;
        setSession(
          normalizeSession({
            apiBase,
            authMode: "cookie",
            csrfToken: out.csrf_token || readCookie(CSRF_COOKIE_NAME),
            user: out.user,
            expiresAt: out.expires_at || ""
          })
        );
      })
      .catch(() => {
        // No valid cookie session. Keep login screen.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    columnsPrefsHydratingRef.current = true;
    if (!columnsPrefsScope) {
      setVisibleDeviceColumns(DEVICE_DEFAULT_COLUMN_KEYS);
      setVisibleProfileColumns(PROFILE_DEFAULT_COLUMN_KEYS);
      setVisiblePolicyColumns(POLICY_DEFAULT_COLUMN_KEYS);
      setVisibleAppColumns(APP_DEFAULT_COLUMN_KEYS);
      return;
    }
    const saved = readColumnsPrefs(columnsPrefsScope);
    setVisibleDeviceColumns(
      normalizeVisibleColumnKeys(saved.devices, DEVICE_COLUMN_KEYS, DEVICE_DEFAULT_COLUMN_KEYS)
    );
    setVisibleProfileColumns(
      ensureVisibleColumnKeys(
        normalizeVisibleColumnKeys(saved.profiles, PROFILE_COLUMN_KEYS, PROFILE_DEFAULT_COLUMN_KEYS),
        ["devices"],
        PROFILE_COLUMN_KEYS
      )
    );
    setVisiblePolicyColumns(
      normalizeVisibleColumnKeys(saved.policies, POLICY_COLUMN_KEYS, POLICY_DEFAULT_COLUMN_KEYS)
    );
    setVisibleAppColumns(normalizeVisibleColumnKeys(saved.apps, APP_COLUMN_KEYS, APP_DEFAULT_COLUMN_KEYS));
  }, [columnsPrefsScope]);

  useEffect(() => {
    if (!columnsPrefsScope) return;
    if (columnsPrefsHydratingRef.current) {
      columnsPrefsHydratingRef.current = false;
      return;
    }
    writeColumnsPrefs(columnsPrefsScope, {
      devices: normalizeVisibleColumnKeys(
        visibleDeviceColumns,
        DEVICE_COLUMN_KEYS,
        DEVICE_DEFAULT_COLUMN_KEYS
      ),
      profiles: normalizeVisibleColumnKeys(
        visibleProfileColumns,
        PROFILE_COLUMN_KEYS,
        PROFILE_DEFAULT_COLUMN_KEYS
      ),
      policies: normalizeVisibleColumnKeys(
        visiblePolicyColumns,
        POLICY_COLUMN_KEYS,
        POLICY_DEFAULT_COLUMN_KEYS
      ),
      apps: normalizeVisibleColumnKeys(visibleAppColumns, APP_COLUMN_KEYS, APP_DEFAULT_COLUMN_KEYS)
    });
  }, [
    columnsPrefsScope,
    visibleDeviceColumns,
    visibleProfileColumns,
    visiblePolicyColumns,
    visibleAppColumns
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleHashChange = () => {
      const page = pageFromHash(window.location.hash);
      const deviceUDID = hashQueryValue(window.location.hash, "udid");
      const managedFilter = normalizeYesNoAllFilter(hashQueryValue(window.location.hash, "managed"));
      const encryptedFilter = normalizeYesNoAllFilter(hashQueryValue(window.location.hash, "encrypted"));
      const complianceFilter = normalizeYesNoAllFilter(hashQueryValue(window.location.hash, "compliance"));
      const lastSeenFilter = normalizeLastSeenFilter(hashQueryValue(window.location.hash, "last_seen"));
      const accountNameFilter = hashQueryValue(window.location.hash, "account_name");
      setActivePage((prev) => (prev === page ? prev : page));
      setRouteDeviceUDID(deviceUDID);
      setDeviceFilterAccountName(accountNameFilter);
      if (managedFilter) {
        setDeviceFilterManaged(managedFilter);
      }
      if (encryptedFilter) {
        setDeviceFilterEncrypted(encryptedFilter);
      }
      if (complianceFilter) {
        setDeviceFilterCompliance(complianceFilter);
      }
      if (lastSeenFilter) {
        setDeviceFilterLastSeen(lastSeenFilter);
      }
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    if (!showDeviceCommandsMenu || typeof document === "undefined") return undefined;
    const closeOnOutsideInteraction = (event) => {
      if (deviceCommandsMenuRef.current?.contains(event.target)) return;
      setShowDeviceCommandsMenu(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setShowDeviceCommandsMenu(false);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showDeviceCommandsMenu]);

	  useEffect(() => {
	    setShowDeviceCommandsMenu(false);
	    setShowBulkDeviceCommandsMenu(false);
	  }, [activePage, routeDeviceUDID]);

	  useEffect(() => {
	    if ((!complianceGroupDropdownOpen && !complianceFieldDropdownOpen) || typeof document === "undefined") return undefined;
	    const closeOnOutsideInteraction = (event) => {
	      if (!complianceGroupDropdownRef.current?.contains(event.target)) {
	        setComplianceGroupDropdownOpen(false);
	      }
	      if (!complianceFieldDropdownRef.current?.contains(event.target)) {
	        setComplianceFieldDropdownOpen(false);
	      }
	    };
	    const closeOnEscape = (event) => {
	      if (event.key === "Escape") {
	        setComplianceGroupDropdownOpen(false);
	        setComplianceFieldDropdownOpen(false);
	      }
	    };
	    document.addEventListener("pointerdown", closeOnOutsideInteraction);
	    document.addEventListener("keydown", closeOnEscape);
	    return () => {
	      document.removeEventListener("pointerdown", closeOnOutsideInteraction);
	      document.removeEventListener("keydown", closeOnEscape);
	    };
	  }, [complianceGroupDropdownOpen, complianceFieldDropdownOpen]);

	  useEffect(() => {
    setExpandedComplianceTimeline(false);
    if (!selectedComplianceIssueID || !isAuthenticated || mustChangePassword || mfaSetupRequired) {
      setSelectedComplianceIssueDetail(null);
      setSelectedComplianceIssueLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSelectedComplianceIssueLoading(true);
    request(`/compliance/issues/detail?id=${encodeURIComponent(selectedComplianceIssueID)}`)
      .then((out) => {
        if (!cancelled) setSelectedComplianceIssueDetail(out || null);
      })
      .catch(() => {
        if (!cancelled) setSelectedComplianceIssueDetail(null);
      })
      .finally(() => {
        if (!cancelled) setSelectedComplianceIssueLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComplianceIssueID, isAuthenticated, mustChangePassword, mfaSetupRequired]);

  useEffect(() => {
    setComplianceIssueVisibleCount(25);
  }, [complianceIssueFilters.status, complianceIssueFilters.ruleId, complianceIssueFilters.deviceUDID, complianceIssueFilters.search]);

  useEffect(() => {
    if (!showBulkDeviceCommandsMenu || typeof document === "undefined") return undefined;
    const closeOnOutsideInteraction = (event) => {
      if (bulkDeviceCommandsMenuRef.current?.contains(event.target)) return;
      setShowBulkDeviceCommandsMenu(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setShowBulkDeviceCommandsMenu(false);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showBulkDeviceCommandsMenu]);

  useEffect(() => {
    if (!showCreateProfileModal && !showProfileViewModal && showRestrictionsAppPickerModal) {
      closeRestrictionsAppPicker();
    }
  }, [showCreateProfileModal, showProfileViewModal, showRestrictionsAppPickerModal]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const route = PAGE_TO_ROUTE[activePage] || PAGE_TO_ROUTE.dashboard;
    const nextHash = `#${route}`;
    const currentRoute = (() => {
      const raw = String(window.location.hash || "");
      const withoutHash = raw.startsWith("#") ? raw.slice(1) : raw;
      const path = withoutHash.split("?")[0] || "";
      return path.startsWith("/") ? path : `/${path}`;
    })();
    if (currentRoute === route) return;
    if (!window.location.hash) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}${nextHash}`
      );
      return;
    }
    window.location.hash = route;
  }, [activePage]);

  useEffect(() => {
    if (!session) return;
    if (canViewPage(activePage)) return;
    const nextPage =
      PAGE_ACCESS_FALLBACK_ORDER.find((item) => canViewPage(item)) || "dashboard";
    if (nextPage !== activePage) {
      setActivePage(nextPage);
    }
  }, [session, activePage, canViewPage]);

  useEffect(() => {
    const previousPage = prevProfilesWorkspacePageRef.current;
    if (
      PROFILE_WORKSPACE_PAGES.includes(previousPage) &&
      PROFILE_WORKSPACE_PAGES.includes(activePage) &&
      previousPage !== activePage
    ) {
      setSelectedProfileIDs([]);
    }
    prevProfilesWorkspacePageRef.current = activePage;
  }, [activePage]);

  useEffect(() => {
    const previousPage = prevAppsWorkspacePageRef.current;
    if (
      APPS_WORKSPACE_PAGES.includes(previousPage) &&
      APPS_WORKSPACE_PAGES.includes(activePage) &&
      previousPage !== activePage
    ) {
      setSelectedAppIDs([]);
    }
    prevAppsWorkspacePageRef.current = activePage;
  }, [activePage]);

  useEffect(() => {
    if (!Array.isArray(roleSettings) || roleSettings.length === 0) {
      setSelectedRoleKey("");
      setEditRoleForm({
        roleKey: "",
        displayName: "",
        permissions: {},
        accessScope: { group_ids: [], platforms: [], location_ids: [] }
      });
      return;
    }
    const exists = roleSettings.some(
      (item) => String(item.role_key || "").toLowerCase() === String(selectedRoleKey || "").toLowerCase()
    );
    const nextItem = exists
      ? roleSettings.find(
          (item) => String(item.role_key || "").toLowerCase() === String(selectedRoleKey || "").toLowerCase()
        )
      : roleSettings[0];
    if (!nextItem) return;
    setSelectedRoleKey(String(nextItem.role_key || "").toLowerCase());
    setEditRoleForm({
      roleKey: String(nextItem.role_key || "").toLowerCase(),
      displayName: nextItem.display_name || nextItem.role_key || "",
      permissions: normalizeRolePermissionsClient(nextItem.permissions || {}),
      accessScope: normalizeRoleAccessScopeClient(nextItem.access_scope || {})
    });
  }, [roleSettings, selectedRoleKey]);

  const filteredDevices = useMemo(() => {
    const q = devicesSearch.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((item) => {
      const udid = String(item.udid || "").toLowerCase();
      const name = String(item.device_name || "").toLowerCase();
      const serial = String(item.serial_number || "").toLowerCase();
      return udid.includes(q) || name.includes(q) || serial.includes(q);
    });
  }, [devices, devicesSearch]);

  const filteredAccounts = useMemo(() => {
    const q = accountsSearch.trim().toLowerCase();
    return accounts.filter((item) => {
      if (accountsActiveOnly && !item.is_active) return false;
      if (accountsWithDevicesOnly) {
        if (Number(item.device_count || 0) === 0) return false;
      }
      if (!q) return true;
      const values = [item.name, item.email, item.external_id]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return values.includes(q);
    });
  }, [accounts, accountsSearch, accountsActiveOnly, accountsWithDevicesOnly]);

  useEffect(() => {
    if (!showCreateAccountModal) return;
    const loginNeedle = String(session?.user?.login || "").trim().toLowerCase();
    const searchNeedle = accountsSearch.trim().toLowerCase();
    if (!loginNeedle || searchNeedle !== loginNeedle) return;
    const hasMatchingAccount = accounts.some((item) =>
      [item.name, item.email, item.external_id].some((value) =>
        String(value || "")
          .trim()
          .toLowerCase()
          .includes(searchNeedle)
      )
    );
    if (!hasMatchingAccount) {
      setAccountsSearch("");
    }
  }, [showCreateAccountModal, accountsSearch, accounts, session?.user?.login]);

  const roleOptions = useMemo(() => {
    const fromAPI = (Array.isArray(roleSettings) ? roleSettings : [])
      .map((item) => ({
        role_key: String(item.role_key || "").toLowerCase(),
        display_name: String(item.display_name || item.role_key || "").trim()
      }))
      .filter((item) => item.role_key);
    if (fromAPI.length > 0) {
      return fromAPI.sort((a, b) =>
        String(a.display_name || a.role_key).localeCompare(
          String(b.display_name || b.role_key),
          lang === "ru" ? "ru" : "en",
          { sensitivity: "base" }
        )
      );
    }
    return [
      { role_key: "read-only", display_name: t("roleReadOnly") },
      { role_key: "admin", display_name: t("roleAdmin") },
      { role_key: "master-admin", display_name: t("roleMasterAdmin") }
    ];
  }, [roleSettings, lang, t]);

  useEffect(() => {
    if (!Array.isArray(roleOptions) || roleOptions.length === 0) return;
    const allowed = new Set(roleOptions.map((item) => item.role_key));
    if (!allowed.has(createConsoleUserForm.role)) {
      setCreateConsoleUserForm((prev) => ({ ...prev, role: roleOptions[0].role_key }));
    }
    if (!allowed.has(editConsoleUserForm.role) && editConsoleUserForm.id) {
      setEditConsoleUserForm((prev) => ({ ...prev, role: roleOptions[0].role_key }));
    }
  }, [roleOptions, createConsoleUserForm.role, editConsoleUserForm.role, editConsoleUserForm.id]);

  const platformCounts = useMemo(() => {
    const initial = {
      windows: 0,
      ios: 0,
      macos: 0,
      android: 0,
      linux: 0
    };
    devices.forEach((device) => {
      const platform = normalizePlatform(device);
      if (platform in initial) {
        initial[platform] += 1;
      }
    });
    return initial;
  }, [devices]);

  const appPlatformCounts = useMemo(() => {
    const initial = {
      windows: 0,
      ios: 0,
      macos: 0,
      android: 0
    };
    appsCatalog.forEach((app) => {
      const platform = normalizeAppPlatform(app);
      if (platform in initial) {
        initial[platform] += 1;
      }
    });
    return initial;
  }, [appsCatalog]);

  const appCatalogNameByBundle = useMemo(() => {
    const out = new Map();
    appsCatalog.forEach((item) => {
      const bundleID = String(item?.bundle_id || "").trim().toLowerCase();
      const appName = String(item?.name || "").trim();
      if (!bundleID || !appName || out.has(bundleID)) return;
      out.set(bundleID, appName);
    });
    return out;
  }, [appsCatalog]);

  const selectedDevicePlatformForLookup = useMemo(() => {
    const fallbackDevice = selectedUdid
      ? devices.find((item) => item.udid === selectedUdid) || null
      : filteredDevices[0] || null;
    return normalizeDeviceAppLookupPlatform(normalizePlatform(deviceDetail || fallbackDevice || {}));
  }, [deviceDetail, devices, filteredDevices, selectedUdid]);

  const deviceAppsViewItems = useMemo(() => {
    const installedByBundle = new Map();
    const installedRows = deviceApps.map((item, index) => {
      const bundleID = String(item?.bundle_id || item?.bundleID || "").trim();
      const normalizedBundleID = bundleID.toLowerCase();
      const raw = item?.raw && typeof item.raw === "object" ? item.raw : {};
      const lookupKey = deviceAppLookupKey(selectedDevicePlatformForLookup, bundleID);
      const lookedUp = lookupKey ? deviceAppStoreMetadata[lookupKey] : null;
      const displayName = firstNonEmptyText(
        item?.app_name,
        item?.name,
        raw?.Name,
        lookedUp?.name,
        appCatalogNameByBundle.get(normalizedBundleID)
      );
      const displayVersion = firstNonEmptyText(
        item?.app_version,
        item?.app_short_version,
        item?.version,
        item?.short_version,
        raw?.Version,
        raw?.ShortVersion
      );
      const row = {
        ...item,
        _rowKey: bundleID || `app-${index}`,
        _bundleID: bundleID,
        _displayName: displayName || "-",
        _displayVersion: displayVersion || "-",
        _isAssignedNotInstalled: false,
        _assignmentFailed: false,
        _assignmentResultStatus: "",
        _assignmentResultDetail: "",
        _deploymentAppID: ""
      };
      if (bundleID) {
        installedByBundle.set(normalizedBundleID, row);
      }
      return row;
    });

    const latestDeploymentByBundle = new Map();
    deviceAppDeployments.forEach((item) => {
      const bundleID = String(item?.bundle_id || item?.bundleID || "").trim();
      if (!bundleID) return;
      const key = bundleID.toLowerCase();
      const prev = latestDeploymentByBundle.get(key);
      const currentTS = new Date(item?.updated_at || item?.queued_at || 0).getTime();
      const prevTS = prev ? new Date(prev?.updated_at || prev?.queued_at || 0).getTime() : 0;
      if (!prev || currentTS >= prevTS) {
        latestDeploymentByBundle.set(key, item);
      }
    });

    const assignedNotInstalledRows = [];
    latestDeploymentByBundle.forEach((deployment, normalizedBundleID) => {
      if (installedByBundle.has(normalizedBundleID)) return;

      const bundleID = String(deployment?.bundle_id || "").trim();
      const currentAssigned = deployment?.current_assigned === true;
      const installedReported = deployment?.installed_reported === true;
      const rawState = String(deployment?.state || "").trim().toLowerCase();
      const resultStatus = String(deployment?.result_status || "").trim();
      const normalizedResult = resultStatus.toLowerCase();
      const isPending =
        rawState !== "done" ||
        !normalizedResult ||
        normalizedResult === "notnow" ||
        normalizedResult === "idle";

      if (!currentAssigned && !isPending) {
        // Не показываем "призраки" из старых деплоев:
        // приложение не назначено устройству сейчас и нет активной установки.
        return;
      }

      const lookupKey = deviceAppLookupKey(selectedDevicePlatformForLookup, bundleID);
      const lookedUp = lookupKey ? deviceAppStoreMetadata[lookupKey] : null;
      const displayName = firstNonEmptyText(
        deployment?.installed_app_name,
        deployment?.app_name,
        lookedUp?.name,
        appCatalogNameByBundle.get(normalizedBundleID)
      );
      const installedManaged =
        typeof deployment?.installed_is_managed === "boolean"
          ? deployment.installed_is_managed
          : true;
      const displayVersionWhenInstalled = firstNonEmptyText(
        deployment?.installed_app_version,
        deployment?.installed_app_short_version
      );

      if (installedReported) {
        assignedNotInstalledRows.push({
          id: deployment?.app_id || "",
          bundle_id: bundleID,
          app_name: displayName || "-",
          app_version: displayVersionWhenInstalled || "",
          is_managed: installedManaged,
          _rowKey: `installed-${bundleID || deployment?.id || deployment?.command_uuid || normalizedBundleID}`,
          _bundleID: bundleID,
          _displayName: displayName || "-",
          _displayVersion: displayVersionWhenInstalled || "-",
          _isAssignedNotInstalled: false,
          _assignmentFailed: false,
          _assignmentResultStatus: "",
          _assignmentResultDetail: "",
          _deploymentAppID: String(deployment?.app_id || "").trim()
        });
        return;
      }

      assignedNotInstalledRows.push({
        id: deployment?.app_id || "",
        bundle_id: bundleID,
        app_name: displayName || "-",
        app_version: "",
        is_managed: true,
        _rowKey: `assigned-${bundleID || deployment?.id || deployment?.command_uuid || normalizedBundleID}`,
        _bundleID: bundleID,
        _displayName: displayName || "-",
        _displayVersion: "-",
        _isAssignedNotInstalled: true,
        _assignmentFailed: !isPending,
        _assignmentResultStatus: resultStatus,
        _assignmentResultDetail: firstNonEmptyText(
          deployment?.result_detail,
          deployment?.result_error,
          deployment?.error_detail,
          deployment?.error_message
        ),
        _deploymentAppID: String(deployment?.app_id || "").trim()
      });
    });

    const allRows = [...assignedNotInstalledRows, ...installedRows];
    allRows.sort((left, right) => {
      const leftPriority = left._isAssignedNotInstalled
        ? (left._assignmentFailed ? 0 : 1)
        : (left.is_managed === true ? 2 : 3);
      const rightPriority = right._isAssignedNotInstalled
        ? (right._assignmentFailed ? 0 : 1)
        : (right.is_managed === true ? 2 : 3);
      if (leftPriority !== rightPriority) return leftPriority - rightPriority;
      return String(left._displayName || left._bundleID || "").localeCompare(
        String(right._displayName || right._bundleID || ""),
        "en",
        { sensitivity: "base" }
      );
    });
    return allRows;
  }, [
    deviceApps,
    deviceAppDeployments,
    selectedDevicePlatformForLookup,
    deviceAppStoreMetadata,
    appCatalogNameByBundle
  ]);

  const selectedDeviceAppRows = useMemo(() => {
    if (selectedDeviceAppBundleIDs.length === 0) return [];
    const selectedSet = new Set(
      selectedDeviceAppBundleIDs.map((value) => String(value || "").trim()).filter(Boolean)
    );
    return deviceAppsViewItems.filter((item) => selectedSet.has(String(item._bundleID || "").trim()));
  }, [deviceAppsViewItems, selectedDeviceAppBundleIDs]);

  const hasSelectedReinstallableDeviceApps = useMemo(
    () =>
      selectedDeviceAppRows.some(
        (item) => item._isAssignedNotInstalled || item.is_managed === true
      ),
    [selectedDeviceAppRows]
  );

  const hasSelectedRemovableDeviceApps = useMemo(
    () =>
      selectedDeviceAppRows.some(
        (item) => !item._isAssignedNotInstalled && item.is_managed === true
      ),
    [selectedDeviceAppRows]
  );

  useEffect(() => {
    setSelectedDeviceAppBundleIDs((prev) => {
      if (!prev.length) return prev;
      const selectable = new Set(
        deviceAppsViewItems
          .filter((item) => item._isAssignedNotInstalled || item.is_managed === true)
          .map((item) => String(item._bundleID || "").trim())
          .filter(Boolean)
      );
      const next = prev.filter((bundleID) => selectable.has(String(bundleID || "").trim()));
      return next.length === prev.length ? prev : next;
    });
  }, [deviceAppsViewItems]);

  useEffect(() => {
    if (!isAuthenticated || !session?.apiBase) return;
    if (deviceDetailsTab !== "apps") return;
    if (!routeDeviceUDID && !selectedUdid) return;

    const platform = selectedDevicePlatformForLookup;
    if (platform !== "ios" && platform !== "macos" && platform !== "android") return;

    const missingBundleIDs = [];
    const seen = new Set();

    const lookupSourceItems = [
      ...deviceApps,
      ...deviceAppDeployments.map((item) => ({
        bundle_id: item?.bundle_id,
        app_name: item?.app_name
      }))
    ];

    lookupSourceItems.forEach((item) => {
      const bundleID = String(item?.bundle_id || item?.bundleID || "").trim();
      if (!bundleID) return;
      const key = deviceAppLookupKey(platform, bundleID);
      if (!key || seen.has(key)) return;
      seen.add(key);

      const directName = firstNonEmptyText(item?.app_name, item?.name, item?.raw?.Name);
      if (directName) return;
      if (deviceAppStoreMetadata[key]) return;
      if (deviceAppLookupInFlightRef.current.has(key)) return;

      missingBundleIDs.push(bundleID);
    });

    if (missingBundleIDs.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const bundleID of missingBundleIDs) {
        if (cancelled) break;
        const key = deviceAppLookupKey(platform, bundleID);
        if (!key) continue;
        if (deviceAppStoreMetadata[key]) continue;
        if (deviceAppLookupInFlightRef.current.has(key)) continue;
        deviceAppLookupInFlightRef.current.add(key);

        try {
          let name = "";
          if (platform === "android") {
            const out = await request(
              `/apps/android/managed-play/application?package_name=${encodeURIComponent(bundleID)}`
            );
            name = String(out?.item?.name || "").trim();
          } else {
            const appStorePlatform = platform === "macos" ? "macos" : "ios";
            const out = await request(
              `/apps/app-store/search?q=${encodeURIComponent(bundleID)}&country=US&platform=${encodeURIComponent(appStorePlatform)}&limit=8`
            );
            const list = Array.isArray(out?.items) ? out.items : [];
            const lowerBundle = bundleID.toLowerCase();
            const exact = list.find(
              (candidate) => String(candidate?.bundle_id || "").trim().toLowerCase() === lowerBundle
            );
            const picked = exact || list[0] || null;
            name = String(picked?.name || "").trim();
          }

          if (!cancelled) {
            setDeviceAppStoreMetadata((prev) => {
              if (prev[key]) return prev;
              return { ...prev, [key]: { name } };
            });
          }
        } catch {
          if (!cancelled) {
            setDeviceAppStoreMetadata((prev) => {
              if (prev[key]) return prev;
              return { ...prev, [key]: { name: "" } };
            });
          }
        } finally {
          deviceAppLookupInFlightRef.current.delete(key);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deviceDetailsTab,
    deviceApps,
    deviceAppDeployments,
    selectedDevicePlatformForLookup,
    routeDeviceUDID,
    selectedUdid,
    isAuthenticated,
    session?.apiBase
  ]);

  useEffect(() => {
    if (!showAppStoreModal || appsStorePlatform !== "android") return;

    let cancelled = false;
    setAppsStoreLoading(true);
    setAppsManagedPlayReady(false);
    setAppsManagedPlayError("");
    setAppsManagedPlayURL("");

    request("/apps/android/managed-play/token")
      .then((out) => {
        if (cancelled) return;
        const iframeURL = String(out?.iframe_url || "").trim();
        if (!iframeURL) {
          throw new Error(t("appsManagedPlayInitFailed"));
        }
        setAppsManagedPlayURL(iframeURL);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.message ? `${t("appsManagedPlayInitFailed")}: ${err.message}` : t("appsManagedPlayInitFailed");
        setAppsManagedPlayError(msg);
        setErrorMessage(msg);
      })
      .finally(() => {
        if (!cancelled) {
          setAppsStoreLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAppStoreModal, appsStorePlatform, isAuthenticated, session?.apiBase, lang]);

  useEffect(() => {
    if (!showAppStoreModal || appsStorePlatform !== "android" || !appsManagedPlayURL) return undefined;

    let cancelled = false;

    const initManagedPlayFrame = async () => {
      try {
        await loadGapiIframesScript();
        if (cancelled) return;

        const host = managedPlayContainerRef.current;
        if (!host) {
          throw new Error("Managed Google Play container is unavailable");
        }
        host.innerHTML = "";

        const context = window.gapi?.iframes?.getContext?.();
        if (!context) {
          throw new Error("gapi.iframes context is unavailable");
        }

        const child = context.openChild({
          url: appsManagedPlayURL,
          where: host,
          attributes: { style: "width:100%;height:100%;border:0;background:#fff;" }
        });
        managedPlayChildRef.current = child;

        const filter = window.gapi?.iframes?.CROSS_ORIGIN_IFRAMES_FILTER;
        if (typeof child?.register === "function") {
          child.register(
            "onproductselect",
            (payload) => {
              const packageName = parseManagedPlayProductSelection(payload);
              if (packageName) {
                addAndroidAppFromManagedPlay(packageName);
              }
            },
            filter
          );
        }

        setAppsManagedPlayReady(true);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.message ? `${t("appsManagedPlayInitFailed")}: ${err.message}` : t("appsManagedPlayInitFailed");
        setAppsManagedPlayError(msg);
        setErrorMessage(msg);
      }
    };

    initManagedPlayFrame();

    const handleManagedPlayMessage = (event) => {
      const origin = String(event?.origin || "");
      if (!origin.includes("play.google.com")) return;

      const packageName = parseManagedPlayProductSelection(event.data);
      if (!packageName) return;

      addAndroidAppFromManagedPlay(packageName);
    };

    window.addEventListener("message", handleManagedPlayMessage);
    return () => {
      cancelled = true;
      setAppsManagedPlayReady(false);
      if (managedPlayChildRef.current && typeof managedPlayChildRef.current.close === "function") {
        try {
          managedPlayChildRef.current.close();
        } catch {
          // no-op
        }
      }
      managedPlayChildRef.current = null;
      const host = managedPlayContainerRef.current;
      if (host) {
        host.innerHTML = "";
      }
      window.removeEventListener("message", handleManagedPlayMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAppStoreModal, appsStorePlatform, appsManagedPlayURL]);

  useEffect(() => {
    if (routeDeviceUDID && DEVICE_WORKSPACE_PAGES.includes(activePage)) {
      setSelectedUdid(routeDeviceUDID);
    }
    setSelectedDeviceAppBundleIDs([]);
    setSelectedDeviceProfileIDs([]);
  }, [routeDeviceUDID, activePage]);

  useEffect(() => {
    const valid = new Set(devices.map((item) => item.udid));
    setSelectedDeviceUDIDs((prev) => prev.filter((udid) => valid.has(udid)));
  }, [devices]);

  useEffect(() => {
    const valid = new Set(accounts.map((item) => item.id));
    setSelectedAccountIDs((prev) => prev.filter((id) => valid.has(id)));
  }, [accounts]);

  useEffect(() => {
    const valid = new Set(profiles.map((item) => item.id));
    setSelectedProfileIDs((prev) => prev.filter((id) => valid.has(id)));
  }, [profiles]);

  const selectedDevice = useMemo(() => {
    if (selectedUdid) {
      return devices.find((item) => item.udid === selectedUdid) || null;
    }
    return filteredDevices[0] || null;
  }, [devices, filteredDevices, selectedUdid]);

  const selectedAccount = useMemo(
    () => accounts.find((item) => item.id === selectedAccountId) || filteredAccounts[0] || null,
    [accounts, filteredAccounts, selectedAccountId]
  );

  const selectedProfile = useMemo(
    () => profiles.find((item) => item.id === selectedProfileId) || profiles[0] || null,
    [profiles, selectedProfileId]
  );

  const selectedApp = useMemo(
    () => appsCatalog.find((item) => item.id === selectedAppId) || appsCatalog[0] || null,
    [appsCatalog, selectedAppId]
  );

  const selectedDeviceProfiles = useMemo(() => {
    const udid = String(selectedUdid || "").trim();
    if (!udid) return [];
    const profileByID = new Map(
      profiles
        .map((item) => [String(item?.id || "").trim(), item])
        .filter(([id]) => Boolean(id))
    );

    const reportedByIdentifier = new Map();
    const reportedByDisplayName = new Map();
    (Array.isArray(deviceProfilesReported) ? deviceProfilesReported : []).forEach((item) => {
      const identifier = String(item?.payload_identifier || "").trim().toLowerCase();
      if (identifier && !reportedByIdentifier.has(identifier)) {
        reportedByIdentifier.set(identifier, item);
      }
      const displayName = String(item?.payload_display_name || "").trim().toLowerCase();
      if (displayName && !reportedByDisplayName.has(displayName)) {
        reportedByDisplayName.set(displayName, item);
      }
    });

    const latestByProfileID = new Map();
    profileDeployments
      .filter((item) => String(item.device_udid || "").trim() === udid)
      .forEach((item) => {
        const profileID = String(item.profile_id || "").trim();
        if (!profileID) return;
        const previous = latestByProfileID.get(profileID);
        const currentTS = new Date(item.updated_at || item.queued_at || 0).getTime();
        const previousTS = previous
          ? new Date(previous.updated_at || previous.queued_at || 0).getTime()
          : 0;
        if (!previous || currentTS >= previousTS) {
          latestByProfileID.set(profileID, item);
        }
      });

    return Array.from(latestByProfileID.values())
      .map((item) => {
        const profileID = String(item.profile_id || "").trim();
        const profile = profileByID.get(profileID) || null;
        const profileName = firstNonEmptyText(item.profile_name, profile?.name, profileID);
        const profileIdentifier = resolveProfilePayloadIdentifier(profile);
        const reportedByIdentifierMatch = profileIdentifier
          ? reportedByIdentifier.get(profileIdentifier.toLowerCase()) || null
          : null;
        const reportedByNameMatch =
          !reportedByIdentifierMatch && profileName
            ? reportedByDisplayName.get(profileName.toLowerCase()) || null
            : null;
        const installedByTelemetry = item?.installed_reported === true;
        const installedReported =
          installedByTelemetry || Boolean(reportedByIdentifierMatch || reportedByNameMatch);

        const rawState = String(item.state || "").trim().toLowerCase();
        const resultStatus = String(item.result_status || "").trim();
        const normalizedResult = resultStatus.toLowerCase();
        const isPending =
          rawState !== "done" ||
          !normalizedResult ||
          normalizedResult === "acknowledged" ||
          normalizedResult === "notnow" ||
          normalizedResult === "idle";
        const isAssignedNotInstalled = !installedReported;

        return {
          profile_id: profileID,
          profile_name: profileName || "-",
          profile_identifier: profileIdentifier,
          state: item.state || "",
          result_status: resultStatus,
          result_detail: firstNonEmptyText(
            item?.result_detail,
            item?.result_error,
            item?.error_detail,
            item?.error_message
          ),
          updated_at: item.updated_at || item.queued_at || "",
          _rowKey: profileID || item.command_uuid || item.id || profileName,
          _isAssignedNotInstalled: !installedReported,
          _assignmentFailed: isAssignedNotInstalled && !isPending,
          _assignmentResultStatus: resultStatus,
          _assignmentResultDetail: firstNonEmptyText(
            item?.result_detail,
            item?.result_error,
            item?.error_detail,
            item?.error_message
          ),
          _installedReported: installedReported,
          _displayState: !installedReported
            ? (!installedReported && !isPending
                ? "failed"
                : "pending")
            : "done"
        };
      })
      .sort((left, right) => {
        const leftPriority = left._isAssignedNotInstalled ? (left._assignmentFailed ? 0 : 1) : 2;
        const rightPriority = right._isAssignedNotInstalled ? (right._assignmentFailed ? 0 : 1) : 2;
        if (leftPriority !== rightPriority) return leftPriority - rightPriority;
        return String(left.profile_name || "").localeCompare(String(right.profile_name || ""), "en", {
          sensitivity: "base"
        });
      });
  }, [selectedUdid, profileDeployments, profiles, deviceProfilesReported]);

  const selectedDeviceCommands = useMemo(() => {
    const udid = String(selectedUdid || "").trim();
    if (!udid) return [];
    return commands
      .filter((item) => String(item.device_udid || "").trim() === udid)
      .sort((a, b) => {
        const left = new Date(
          b.queued_at || b.sent_at || b.acknowledged_at || b.updated_at || 0
        ).getTime();
        const right = new Date(
          a.queued_at || a.sent_at || a.acknowledged_at || a.updated_at || 0
        ).getTime();
        return left - right;
      })
      .slice(0, 15);
  }, [commands, selectedUdid]);

  const isDeviceLostModeEnabled = useMemo(() => {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) return false;

    let latestEnableTS = 0;
    let latestDisableTS = 0;

    commands.forEach((item) => {
      if (String(item?.device_udid || "").trim() !== udid) return;
      const requestType = String(item?.request_type || "").trim();
      if (requestType !== "EnableLostMode" && requestType !== "DisableLostMode") return;

      const state = String(item?.state || "").trim().toLowerCase();
      const resultStatus = String(item?.result_status || "").trim().toLowerCase();
      const isAcknowledged =
        resultStatus === "acknowledged" ||
        (state === "done" && (!resultStatus || resultStatus === "acknowledged"));
      if (!isAcknowledged) return;

      const ts = new Date(
        item?.acknowledged_at || item?.updated_at || item?.sent_at || item?.queued_at || 0
      ).getTime();
      if (!Number.isFinite(ts) || ts <= 0) return;

      if (requestType === "EnableLostMode") {
        latestEnableTS = Math.max(latestEnableTS, ts);
      } else {
        latestDisableTS = Math.max(latestDisableTS, ts);
      }
    });

    if (latestEnableTS > 0 || latestDisableTS > 0) {
      return latestEnableTS > latestDisableTS;
    }
    return deviceDetail?.udid === udid && deviceDetail?.lost_mode_enabled === true;
  }, [commands, deviceDetail, routeDeviceUDID, selectedUdid]);

  const selectedDeviceLocations = useMemo(() => {
    return (Array.isArray(deviceLocations) ? deviceLocations : [])
      .slice()
      .sort((left, right) => {
        const leftTS = new Date(left?.observed_at || 0).getTime();
        const rightTS = new Date(right?.observed_at || 0).getTime();
        return rightTS - leftTS;
      });
  }, [deviceLocations]);

  async function rawRequest(path, options = {}, authSession = session) {
    const base = normalizeAPIBase(authSession?.apiBase || DEFAULT_API_BASE);
    const safePath = String(path || "").startsWith("/") ? String(path || "") : `/${String(path || "")}`;
    const headers = new Headers(options.headers || {});
    const method = String(options.method || "GET").trim().toUpperCase();
    if (authSession?.authMode === "bearer" && authSession?.token) {
      headers.set("Authorization", `Bearer ${String(authSession.token || "").trim()}`);
    }
    if (isUnsafeCSRFMethod(method)) {
      const csrfToken = String(authSession?.csrfToken || readCookie(CSRF_COOKIE_NAME) || "").trim();
      if (csrfToken) {
        headers.set("X-CSRF-Token", csrfToken);
      }
    }
    const response = await fetch(`${base}${safePath}`, {
      ...options,
      cache: options.cache || "no-store",
      headers,
      credentials: "include"
    });
    const raw = await response.text();
    const parsed = parseBody(raw);
    if (!response.ok) {
      const err = new Error(readableError(parsed, response.statusText));
      err.status = response.status;
      throw err;
    }
    return parsed;
  }

  function requestReauthPassword() {
    return new Promise((resolve, reject) => {
      setReauthModal({
        open: true,
        password: "",
        error: "",
        resolve,
        reject
      });
    });
  }

  function closeReauthModalWithCancel() {
    if (typeof reauthModal.reject === "function") {
      reauthModal.reject(new Error(t("reauthCancelled")));
    }
    setReauthModal({ open: false, password: "", error: "", resolve: null, reject: null });
  }

  function submitReauthPassword(event) {
    event.preventDefault();
    const password = String(reauthModal.password || "");
    if (!password.trim()) {
      setReauthModal((prev) => ({ ...prev, error: t("reauthPasswordRequired") }));
      return;
    }
    if (typeof reauthModal.resolve === "function") {
      reauthModal.resolve(password);
    }
    setReauthModal({ open: false, password: "", error: "", resolve: null, reject: null });
  }

  function markMFASetupRequired() {
    setSession((prev) =>
      prev
        ? {
            ...prev,
            user: {
              ...(prev.user || {}),
              mfa_required: true,
              mfa_setup_required: true
            }
          }
        : prev
    );
    setInfoMessage(t("mfaSetupRequiredHint"));
  }

  async function request(path, options = {}, authSession = session) {
    if (!sessionIsAuthenticated(authSession)) {
      throw new Error(t("authRequired"));
    }
    try {
      let nextOptions = options;
      if (isSensitiveConsoleRequest(path, options)) {
        const password = await requestReauthPassword();
        const headers = new Headers(options.headers || {});
        headers.set("X-Reauth-Password", password);
        nextOptions = { ...options, headers };
      }
      return await rawRequest(path, nextOptions, authSession);
    } catch (err) {
      if (err?.status === 401) {
        clearSession();
        throw new Error(t("sessionExpired"));
      }
      if (err?.status === 403 && /mfa setup required/i.test(String(err.message || ""))) {
        markMFASetupRequired();
        throw new Error(t("mfaSetupRequiredHint"));
      }
      throw err;
    }
  }

  async function loadCommandCenter(overrides = {}) {
    if (!hasPermission("devices.commands", "view")) return;
    const filters = { ...commandCenterFilters, ...overrides };
    const params = new URLSearchParams();
    params.set("limit", "300");
    if (filters.state) params.set("state", filters.state);
    if (filters.requestType) params.set("request_type", filters.requestType);
    if (filters.search) params.set("search", filters.search);
    setErrorMessage("");
    try {
      const out = await request(`/command-center?${params.toString()}`);
      setCommandCenter({
        items: Array.isArray(out?.items) ? out.items : [],
        summary: out?.summary || {}
      });
      setSelectedCommandCenterUUIDs([]);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось загрузить Центр команд" : "Command Center load failed"}: ${err.message}`);
    }
  }

  async function bulkCommandCenter(action) {
    const commandUUIDs = selectedCommandCenterUUIDs.slice();
    if (commandUUIDs.length === 0) return;
    if (!hasPermission("devices.commands", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/command-center/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, command_uuids: commandUUIDs })
      });
      const items = Array.isArray(out?.items) ? out.items : [];
      const okCount = items.filter((item) => item.status !== "error").length;
      setInfoMessage(
        `${action === "retry" ? (lang === "ru" ? "Повторено" : "Retried") : (lang === "ru" ? "Отменено" : "Canceled")}: ${okCount}/${commandUUIDs.length}`
      );
      await loadCommandCenter();
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Операция не выполнена" : "Operation failed"}: ${err.message}`);
    }
  }

  async function commandCenterSingleAction(action, commandUUID) {
    const value = String(commandUUID || "").trim();
    if (!value) return;
    const previous = selectedCommandCenterUUIDs;
    setSelectedCommandCenterUUIDs([value]);
    if (!hasPermission("devices.commands", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/command-center/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, command_uuids: [value] })
      });
      const items = Array.isArray(out?.items) ? out.items : [];
      const okCount = items.filter((item) => item.status !== "error").length;
      setInfoMessage(`${action === "retry" ? (lang === "ru" ? "Повторено" : "Retried") : (lang === "ru" ? "Отменено" : "Canceled")}: ${okCount}/1`);
      await loadCommandCenter();
    } catch (err) {
      setSelectedCommandCenterUUIDs(previous);
      setErrorMessage(`${lang === "ru" ? "Операция не выполнена" : "Operation failed"}: ${err.message}`);
    }
  }

  async function loadSystemHealth() {
    if (!hasPermission("dashboard", "view")) return;
    setErrorMessage("");
    try {
      const out = await request("/system-health");
      setSystemHealth({
        overall: out?.overall || "unknown",
        checks: Array.isArray(out?.checks) ? out.checks : []
      });
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось загрузить состояние системы" : "System health load failed"}: ${err.message}`);
    }
  }

  async function loadOrganizationSettings() {
    if (!hasPermission("settings.tenants", "view")) return;
    setErrorMessage("");
    try {
      const out = await request("/organization/current");
      const item = out?.item || null;
      setOrganizationSettings(item);
      setOrganizationDraft(organizationSettingsToDraft(item));
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось загрузить организацию" : "Organization load failed"}: ${err.message}`);
    }
  }

  async function saveOrganizationSettings(event) {
    event?.preventDefault();
    if (!hasPermission("settings.tenants", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const body = {
        name: organizationDraft.name,
        primary_domain: organizationDraft.primary_domain,
        plan: organizationDraft.plan,
        billing_status: organizationDraft.billing_status,
        billing_email: organizationDraft.billing_email,
        trial_ends_at: optionalDateISOString(organizationDraft.trial_ends_at),
        subscription_ends_at: optionalDateISOString(organizationDraft.subscription_ends_at),
        device_limit: optionalInteger(organizationDraft.device_limit),
        account_limit: optionalInteger(organizationDraft.account_limit),
        admin_limit: optionalInteger(organizationDraft.admin_limit),
        timezone: organizationDraft.timezone,
        data_retention_days: optionalInteger(organizationDraft.data_retention_days) || 365,
        support_email: organizationDraft.support_email,
        security_email: organizationDraft.security_email
      };
      const out = await request("/organization/current", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const item = out?.item || null;
      setOrganizationSettings(item);
      setOrganizationDraft(organizationSettingsToDraft(item));
      setInfoMessage(lang === "ru" ? "Организация сохранена" : "Organization saved");
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось сохранить организацию" : "Organization save failed"}: ${err.message}`);
    }
  }

  async function downloadConfiguratorEnrollmentProfile() {
    if (!hasPermission("settings.certificates", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const base = normalizeAPIBase(session?.apiBase || DEFAULT_API_BASE);
      const headers = new Headers();
      headers.set("X-Reauth-Password", await requestReauthPassword());
      const response = await fetch(`${base}/certificates/configurator-profile`, {
        headers,
        credentials: "include"
      });
      if (!response.ok) {
        const parsed = parseBody(await response.text());
        if (response.status === 401) {
          clearSession();
        }
        throw new Error(readableError(parsed, response.statusText));
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = "turuk-configurator-enrollment.mobileconfig";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      setInfoMessage(t("certificatesDownloadConfiguratorProfileDone"));
    } catch (err) {
      setErrorMessage(`${t("certificatesDownloadConfiguratorProfileFailed")}: ${err.message}`);
    }
  }

  async function downloadMacOSAgentPackage() {
    const packageURL = String(
      configuratorEnrollmentURL?.macos_agent_package_url || `${window.location.origin}/macos-agent/package/TurukMacOSAgent.pkg`
    ).trim();
    if (!packageURL) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await fetch(packageURL, { credentials: "include" });
      if (!response.ok) {
        const parsed = parseBody(await response.text());
        throw new Error(readableError(parsed, response.statusText));
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = "TurukMacOSAgent.pkg";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      setInfoMessage(t("certificatesDownloadMacOSAgentPackageDone"));
    } catch (err) {
      setErrorMessage(`${t("certificatesDownloadMacOSAgentPackageFailed")}: ${err.message}`);
    }
  }

  async function rotateConfiguratorEnrollmentURL() {
    if (!hasPermission("settings.certificates", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/certificates/configurator-url", { method: "POST" });
      setConfiguratorEnrollmentURL(out || null);
      setInfoMessage(t("certificatesConfiguratorURLRotated"));
    } catch (err) {
      setErrorMessage(`${t("certificatesConfiguratorURLRotateFailed")}: ${err.message}`);
    }
  }

  async function copyConfiguratorEnrollmentURL() {
    const value = String(configuratorEnrollmentURL?.url || "").trim();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setInfoMessage(t("certificatesConfiguratorURLCopied"));
    } catch {
      setErrorMessage(value);
    }
  }

  async function downloadTenantDataExport() {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const base = normalizeAPIBase(session?.apiBase || DEFAULT_API_BASE);
      const headers = new Headers();
      headers.set("X-Reauth-Password", await requestReauthPassword());
      const response = await fetch(`${base}/tenant/export`, {
        headers,
        credentials: "include"
      });
      if (!response.ok) {
        const parsed = parseBody(await response.text());
        if (response.status === 401) {
          clearSession();
        }
        throw new Error(readableError(parsed, response.statusText));
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = href;
      link.download = `turuk-tenant-export-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      setInfoMessage(t("securityTenantExportDone"));
    } catch (err) {
      setErrorMessage(`${t("securityTenantExportFailed")}: ${err.message}`);
    }
  }

  async function downloadPlatformTenantDataExport(item) {
    const tenantID = String(item?.id || "").trim();
    const isMasterAdmin = String(session?.user?.role || "").toLowerCase() === "master-admin";
    if (!tenantID || !isMasterAdmin || !hasPermission("settings.tenants", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const base = normalizeAPIBase(session?.apiBase || DEFAULT_API_BASE);
      const headers = new Headers();
      headers.set("X-Reauth-Password", await requestReauthPassword());
      const response = await fetch(`${base}/tenants/export?id=${encodeURIComponent(tenantID)}`, {
        headers,
        credentials: "include"
      });
      if (!response.ok) {
        const parsed = parseBody(await response.text());
        if (response.status === 401) {
          clearSession();
        }
        throw new Error(readableError(parsed, response.statusText));
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const slug = String(item?.slug || item?.name || "tenant")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/^-+|-+$/g, "") || "tenant";
      link.href = href;
      link.download = `turuk-tenant-export-${slug}-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      setInfoMessage(`${t("securityTenantExportDone")}: ${item?.name || tenantID}`);
    } catch (err) {
      setErrorMessage(`${t("securityTenantExportFailed")}: ${err.message}`);
    }
  }

  async function downloadPlatformTenantConfiguratorProfile(item) {
    const tenantID = String(item?.id || "").trim();
    const isMasterAdmin = String(session?.user?.role || "").toLowerCase() === "master-admin";
    if (!tenantID || !isMasterAdmin || !hasPermission("settings.tenants", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const base = normalizeAPIBase(session?.apiBase || DEFAULT_API_BASE);
      const headers = new Headers();
      headers.set("X-Reauth-Password", await requestReauthPassword());
      const response = await fetch(`${base}/certificates/configurator-profile?tenant_id=${encodeURIComponent(tenantID)}`, {
        headers,
        credentials: "include"
      });
      if (!response.ok) {
        const parsed = parseBody(await response.text());
        if (response.status === 401) {
          clearSession();
        }
        throw new Error(readableError(parsed, response.statusText));
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const slug = String(item?.slug || item?.name || "tenant")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/^-+|-+$/g, "") || "tenant";
      link.href = href;
      link.download = `turuk-${slug}-configurator-enrollment.mobileconfig`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      setInfoMessage(`${t("tenantsConfiguratorProfileDownloaded")}: ${item?.name || tenantID}`);
    } catch (err) {
      setErrorMessage(`${t("tenantsConfiguratorProfileDownloadFailed")}: ${err.message}`);
    }
  }

  async function downloadMDMAPNSCSR() {
    if (!hasPermission("settings.certificates", "view")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const base = normalizeAPIBase(session?.apiBase || DEFAULT_API_BASE);
      const response = await fetch(`${base}/mdm-apns/csr`, {
        credentials: "include"
      });
      if (!response.ok) {
        const parsed = parseBody(await response.text());
        if (response.status === 401) {
          clearSession();
        }
        throw new Error(readableError(parsed, response.statusText));
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = "turuk-mdm-push-csr.csr";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      setInfoMessage(t("certificatesAPNSCSRDownloaded"));
    } catch (err) {
      setErrorMessage(`${t("certificatesAPNSCSRDownloadFailed")}: ${err.message}`);
    }
  }

  async function createAndDownloadMDMAPNSCSR() {
    if (!hasPermission("settings.certificates", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/mdm-apns/csr", { method: "POST" });
      setMDMAPNSConfig(response || null);
      setMDMAPNSForm((prev) => ({
        ...prev,
        appleID: "",
        certPEM: ""
      }));
      await downloadMDMAPNSCSR();
    } catch (err) {
      setErrorMessage(`${t("certificatesAPNSCSRFailed")}: ${err.message}`);
    }
  }

  async function handleMDMAPNSCertificateFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const buffer = await file.arrayBuffer();
      setMDMAPNSForm((prev) => ({ ...prev, certPEM: certificatePEMFromBytes(new Uint8Array(buffer)) }));
    } catch (err) {
      setErrorMessage(`${t("certificatesAPNSSaveFailed")}: ${err.message}`);
    }
  }

  async function saveMDMAPNSConfig(event) {
    event?.preventDefault();
    if (!hasPermission("settings.certificates", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/mdm-apns/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cert_pem: mdmAPNSForm.certPEM,
          apple_id: mdmAPNSForm.appleID
        })
      });
      setMDMAPNSConfig(response || null);
      setMDMAPNSForm((prev) => ({
        ...prev,
        appleID: String(response?.apple_id || prev.appleID || ""),
        certPEM: ""
      }));
      setInfoMessage(t("certificatesAPNSSaved"));
    } catch (err) {
      setErrorMessage(`${t("certificatesAPNSSaveFailed")}: ${err.message}`);
    }
  }

  async function reloadADEStatus() {
    if (!hasPermission("settings.ade", "view")) return;
    const response = await request("/ade/status");
    const eventsResponse = await request("/abm/sync-events?source=ade&limit=50");
    setADEStatus(response?.config || null);
    setADEDevices(Array.isArray(response?.devices) ? response.devices : []);
    setADEProfiles(Array.isArray(response?.profiles) ? response.profiles : []);
    setADESyncEvents(Array.isArray(eventsResponse?.items) ? eventsResponse.items : []);
    setADETokenForm((prev) => ({
      ...prev,
      appleID: String(response?.config?.apple_id || prev.appleID || ""),
      file: null
    }));
  }

  function handleADEServerTokenFile(event) {
    setADETokenForm((prev) => ({
      ...prev,
      file: event.target.files?.[0] || null
    }));
  }

  async function uploadADEServerToken(event) {
    event?.preventDefault();
    if (!hasPermission("settings.ade", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!adeTokenForm.file) {
      setErrorMessage(`${t("adeTokenUploadFailed")}: ${t("requiredField")}`);
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const formData = new FormData();
      formData.append("server_token", adeTokenForm.file);
      formData.append("apple_id", String(adeTokenForm.appleID || "").trim());
      const response = await request("/ade/server-token", {
        method: "POST",
        body: formData
      });
      setADEStatus(response?.config || response || null);
      await reloadADEStatus();
      setInfoMessage(t("adeTokenUploadDone"));
    } catch (err) {
      setErrorMessage(`${t("adeTokenUploadFailed")}: ${err.message}`);
    }
  }

  async function createADEProfile(event) {
    event?.preventDefault();
    if (!hasPermission("settings.ade", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/ade/profiles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: adeProfileForm.name,
          url: adeProfileForm.url,
          is_default: Boolean(adeProfileForm.isDefault)
        })
      });
      setADEProfileForm({ name: "", url: "", isDefault: true });
      await reloadADEStatus();
      setInfoMessage(t("adeProfileSaved"));
    } catch (err) {
      setErrorMessage(`${t("adeProfileSaveFailed")}: ${err.message}`);
    }
  }

  async function syncADEDevices() {
    if (!hasPermission("settings.ade", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/ade/sync", { method: "POST" });
      await reloadADEStatus();
      setInfoMessage(t("adeSyncQueued"));
    } catch (err) {
      setErrorMessage(`${t("adeSyncStatus")}: ${err.message}`);
    }
  }

  async function assignADEProfile(serialNumber, profileUUID) {
    if (!hasPermission("settings.ade", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/ade/assign-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          serial_number: serialNumber,
          profile_uuid: profileUUID
        })
      });
      await reloadADEStatus();
      setInfoMessage(t("adeAssignProfileDone"));
    } catch (err) {
      setErrorMessage(`${t("adeAssignProfileFailed")}: ${err.message}`);
    }
  }

  async function reloadVPPStatus() {
    if (!hasPermission("settings.vpp", "view")) return;
    const response = await request("/vpp/status");
    const eventsResponse = await request("/abm/sync-events?source=vpp&limit=50");
    setVPPStatus(response?.config || null);
    setVPPAssets(Array.isArray(response?.assets) ? response.assets : []);
    setVPPAssignments(Array.isArray(response?.assignments) ? response.assignments : []);
    setVPPSyncEvents(Array.isArray(eventsResponse?.items) ? eventsResponse.items : []);
    setVPPTokenForm((prev) => ({
      ...prev,
      appleID: String(response?.config?.apple_id || prev.appleID || ""),
      file: null
    }));
  }

  function handleVPPTokenFile(event) {
    setVPPTokenForm((prev) => ({
      ...prev,
      file: event.target.files?.[0] || null
    }));
  }

  async function uploadVPPToken(event) {
    event?.preventDefault();
    if (!hasPermission("settings.vpp", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!vppTokenForm.file) {
      setErrorMessage(`${t("vppTokenUploadFailed")}: ${t("requiredField")}`);
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const formData = new FormData();
      formData.append("vpp_token", vppTokenForm.file);
      formData.append("apple_id", String(vppTokenForm.appleID || "").trim());
      await request("/vpp/token", {
        method: "POST",
        body: formData
      });
      await reloadVPPStatus();
      setInfoMessage(t("vppTokenUploadDone"));
    } catch (err) {
      setErrorMessage(`${t("vppTokenUploadFailed")}: ${err.message}`);
    }
  }

  async function syncVPPAssets() {
    if (!hasPermission("settings.vpp", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/vpp/sync", { method: "POST" });
      await reloadVPPStatus();
      await loadAllData();
      setInfoMessage(`${t("vppSyncDone")}: ${response?.synced ?? 0}`);
    } catch (err) {
      setErrorMessage(`${t("vppSyncFailed")}: ${err.message}`);
    }
  }

  async function saveSSOConfig(event) {
    event?.preventDefault();
    if (!hasPermission("settings.sso", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (Boolean(ssoForm.enforce_sso) && !Boolean(ssoConfig?.enforce_sso)) {
      const confirmed = window.confirm(t("ssoEnforceConfirm"));
      if (!confirmed) {
        return;
      }
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/sso/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ssoFormToPayload(ssoForm))
      });
      setSSOConfig(response || null);
      setSSOForm(ssoConfigToForm(response));
      setInfoMessage(t("ssoSaveDone"));
    } catch (err) {
      setErrorMessage(`${t("ssoSaveFailed")}: ${err.message}`);
    }
  }

  function downloadSSOMetadata() {
    const base = normalizeAPIBase(session?.apiBase || signInForm.apiBase || DEFAULT_API_BASE);
    window.location.href = `${base}/sso/metadata`;
  }

  function testSSOLogin() {
    const base = normalizeAPIBase(session?.apiBase || signInForm.apiBase || DEFAULT_API_BASE);
    window.open(`${base}/auth/sso/start?redirect=${encodeURIComponent("/settings/sso")}`, "_blank", "noopener,noreferrer");
  }

  function applyDDMTemplate(templateKey) {
    const template = DDM_PAYLOAD_TEMPLATES[templateKey] || DDM_PAYLOAD_TEMPLATES.software_update;
    setDDMForm((prev) => ({
      ...prev,
      template: templateKey,
      declaration_type: template.declaration_type,
      channel: prev.channel || "device",
      identifier: template.identifier,
      display_name: template.display_name,
      payload_text: JSON.stringify(template.payload, null, 2),
      fields: ddmFieldsFromPayload(templateKey, template.payload)
    }));
  }

  function updateDDMField(key, value) {
    setDDMForm((prev) => {
      const next = {
        ...prev,
        fields: {
          ...(prev.fields || {}),
          [key]: value
        }
      };
      if (next.template !== "custom") {
        try {
          next.payload_text = JSON.stringify(ddmPayloadFromForm(next), null, 2);
        } catch {
          // Keep the previous preview until all required structured fields are valid.
        }
      }
      return next;
    });
  }

  function ddmAssetDeclarations() {
    return (ddmDeclarations || []).filter((item) => item.declaration_type === "asset" && item.is_active !== false);
  }

  function ddmAssetLabel(asset) {
    const kind = asset?.asset_kind ? `${asset.asset_kind} · ` : "";
    const reference = asset?.asset_reference ? ` (${asset.asset_reference})` : "";
    return `${kind}${asset?.display_name || asset?.identifier || "-"}${reference}`;
  }

  function editDDMDeclaration(item) {
    let parsedPayload = {};
    try {
      parsedPayload = typeof item?.payload === "string" ? JSON.parse(item.payload || "{}") : item?.payload || {};
    } catch {
      parsedPayload = {};
    }
    const template = ddmTemplateForPayload(item?.declaration_type || "configuration", parsedPayload);
    setDDMForm({
      id: item?.id || "",
      template,
      declaration_type: item?.declaration_type || "configuration",
      channel: item?.channel || "device",
      identifier: item?.identifier || "",
      display_name: item?.display_name || "",
      payload_text: JSON.stringify(parsedPayload, null, 2),
      fields: ddmFieldsFromPayload(template, parsedPayload),
      is_active: item?.is_active !== false,
      all_devices: Array.isArray(item?.assignments) && item.assignments.some((assignment) => assignment.all_devices),
      group_ids: Array.isArray(item?.assignments)
        ? item.assignments.map((assignment) => assignment.group_id).filter(Boolean)
        : [],
      device_udids_text: Array.isArray(item?.assignments)
        ? item.assignments.map((assignment) => assignment.device_udid).filter(Boolean).join("\n")
        : ""
    });
  }

  async function saveDDMDeclaration(event) {
    event?.preventDefault();
    if (!hasPermission("settings.declarative", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    let payload;
    try {
      payload = ddmPayloadFromForm(ddmForm);
    } catch {
      setErrorMessage(lang === "ru" ? "Payload должен быть валидным JSON" : "Payload must be valid JSON");
      return;
    }
    try {
      await request("/declarative/declarations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: ddmForm.id,
          declaration_type: ddmForm.declaration_type,
          channel: ddmForm.channel || "device",
          identifier: ddmForm.identifier,
          display_name: ddmForm.display_name,
          payload,
          asset_kind: ddmForm.declaration_type === "asset" ? ddmAssetKindFromPayload(payload) : "",
          asset_reference: ddmForm.declaration_type === "asset" ? ddmAssetReferenceFromPayload(payload) : "",
          asset_metadata: ddmAssetMetadataFromForm(ddmForm, payload),
          is_active: ddmForm.is_active,
          all_devices: ddmForm.all_devices,
          group_ids: ddmForm.group_ids,
          device_udids: String(ddmForm.device_udids_text || "")
            .split(/[\n,]+/)
            .map((value) => value.trim())
            .filter(Boolean)
        })
      });
      const response = await request("/declarative/declarations");
      setDDMDeclarations(Array.isArray(response?.items) ? response.items : []);
      setDDMForm(createEmptyDDMForm());
      setInfoMessage(
        session?.user?.platform_admin === true
          ? (lang === "ru" ? "DDM декларация сохранена" : "DDM declaration saved")
          : (lang === "ru" ? "Автоматическая политика Apple сохранена" : "Automatic Apple policy saved")
      );
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function setDDMDeclarationActive(item, active) {
    if (!hasPermission("settings.declarative", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/declarative/declarations/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, is_active: active })
      });
      const response = await request("/declarative/declarations");
      setDDMDeclarations(Array.isArray(response?.items) ? response.items : []);
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function syncDDMDeclarations() {
    if (!hasPermission("settings.declarative", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/declarative/sync", { method: "POST" });
      setInfoMessage(
        lang === "ru"
          ? `Синхронизация DDM поставлена в очередь: ${response?.queued ?? 0}`
          : `DDM sync queued: ${response?.queued ?? 0}`
      );
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function importLegacyProfilesToDDM() {
    if (!hasPermission("settings.declarative", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/declarative/import-legacy-profiles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile_ids: [] })
      });
      const declarations = await request("/declarative/declarations");
      setDDMDeclarations(Array.isArray(declarations?.items) ? declarations.items : []);
      setInfoMessage(
        lang === "ru"
          ? `Импорт старых профилей завершен: ${response?.created ?? 0}`
          : `Legacy profile import complete: ${response?.created ?? 0}`
      );
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось импортировать старые профили" : "Legacy profile import failed"}: ${err.message}`);
    }
  }

  async function saveDDMStatusSubscriptions() {
    if (!hasPermission("settings.declarative", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/declarative/status-subscriptions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: ddmStatusSubscriptions })
      });
      setDDMStatusSubscriptions(Array.isArray(response?.items) ? response.items : defaultDDMStatusSubscriptions());
      setInfoMessage(lang === "ru" ? "DDM status subscriptions сохранены" : "DDM status subscriptions saved");
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function saveSecuritySettings(event) {
    event?.preventDefault();
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/security/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(securitySettingsDraft)
      });
      setSecuritySettings(response);
      setSecuritySettingsDraft(response);
      setInfoMessage(lang === "ru" ? "Настройки безопасности сохранены" : "Security settings saved");
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function revokeSecuritySession(sessionID) {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/security/sessions/revoke", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionID })
      });
      const response = await request("/security/sessions");
      setSecuritySessions(Array.isArray(response?.items) ? response.items : []);
      setInfoMessage(lang === "ru" ? "Сессия отозвана" : "Session revoked");
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function saveWebhook(event) {
    event?.preventDefault();
    if (!hasPermission("settings.api_keys", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/webhooks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: webhookForm.id,
          name: webhookForm.name,
          url: webhookForm.url,
          secret: webhookForm.secret,
          event_types: webhookForm.eventTypes,
          is_active: true
        })
      });
      const list = await request("/webhooks?include_inactive=true");
      setWebhooks(Array.isArray(list?.items) ? list.items : response?.item ? [response.item] : []);
      setWebhookEventTypes(Array.isArray(list?.event_types) ? list.event_types : webhookEventTypes);
      setWebhookForm({ id: "", name: "", url: "", secret: "", eventTypes: [] });
      setInfoMessage(lang === "ru" ? "Webhook сохранен" : "Webhook saved");
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function deactivateWebhook(id) {
    if (!hasPermission("settings.api_keys", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/webhooks/deactivate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
      const response = await request("/webhooks?include_inactive=true");
      setWebhooks(Array.isArray(response?.items) ? response.items : []);
      setInfoMessage(lang === "ru" ? "Webhook отключен" : "Webhook deactivated");
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function assignVPPLicense(asset) {
    if (!hasPermission("settings.vpp", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const adamID = String(asset?.adam_id || "").trim();
    const groupID = String(vppAssignDrafts[adamID] || "").trim();
    if (!adamID || !groupID) {
      setErrorMessage(`${t("vppAssignFailed")}: ${t("requiredField")}`);
      return;
    }
    const linkedApp = appsCatalog.find((item) => String(item.itunes_store_id || "") === adamID);
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/vpp/assign-license", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adam_id: adamID,
          app_id: linkedApp?.id || "",
          group_id: groupID
        })
      });
      await reloadVPPStatus();
      setInfoMessage(t("vppAssignDone"));
    } catch (err) {
      setErrorMessage(`${t("vppAssignFailed")}: ${err.message}`);
    }
  }

  async function revokeVPPLicense(asset) {
    if (!hasPermission("settings.vpp", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const adamID = String(asset?.adam_id || "").trim();
    const groupID = String(vppAssignDrafts[adamID] || "").trim();
    if (!adamID || !groupID) {
      setErrorMessage(`${t("vppRevokeFailed")}: ${t("requiredField")}`);
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/vpp/revoke-license", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adam_id: adamID,
          group_id: groupID
        })
      });
      await reloadVPPStatus();
      setInfoMessage(t("vppRevokeDone"));
    } catch (err) {
      setErrorMessage(`${t("vppRevokeFailed")}: ${err.message}`);
    }
  }

  async function loadAllData() {
    if (!isAuthenticated || mustChangePassword || mfaSetupRequired) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const metaResp = await rawRequest("/meta/version", {}, session);
      const meResp = await request("/auth/me");
      if (meResp?.user) {
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            user: meResp.user,
            csrfToken: meResp.csrf_token || prev.csrfToken || readCookie(CSRF_COOKIE_NAME),
            expiresAt: meResp.expires_at || prev.expiresAt
          };
        });
        if (meResp.user.mfa_setup_required) {
          setInfoMessage(t("mfaSetupRequiredHint"));
          return;
        }
      }
      const effectiveUser = meResp?.user || session?.user || {};
      const isMasterAdmin = String(effectiveUser?.role || "").toLowerCase() === "master-admin";
      const isPlatformAdmin = effectiveUser?.platform_admin === true;

      const endpointRequests = [
        { name: "console_settings", path: "/console-settings", fallback: {} },
        { name: "onboarding_status", path: "/onboarding/status", fallback: null },
        ...(hasPermission("settings.agent", "view")
          ? [{ name: "agent_settings", path: "/agent-settings", fallback: { branding: {}, services: [], tickets: [] } }]
          : []),
        { name: "devices", path: "/devices?limit=200", fallback: { items: [] } },
        { name: "commands", path: "/commands?limit=100", fallback: { items: [] } },
        ...(hasPermission("devices.commands", "view")
          ? [{ name: "command_center", path: "/command-center?limit=300", fallback: { items: [], summary: {} } }]
          : []),
        ...(hasPermission("dashboard", "view")
          ? [{ name: "system_health", path: "/system-health", fallback: { overall: "unknown", checks: [] } }]
          : []),
        ...(hasPermission("settings.tenants", "view")
          ? [{ name: "organization_current", path: "/organization/current", fallback: { item: null } }]
          : []),
        { name: "accounts", path: "/accounts?limit=200", fallback: { items: [] } },
        { name: "groups", path: "/groups?limit=200", fallback: { items: [] } },
        ...(hasPermission("notifications", "view")
          ? [{ name: "notifications", path: "/notifications?limit=200", fallback: { items: [] } }]
          : []),
        ...(hasPermission("documents", "view")
          ? [
              { name: "documents", path: "/documents?limit=500", fallback: { items: [] } },
              { name: "document_folders", path: "/document-folders", fallback: { items: [] } },
              {
                name: "document_ack_report",
                path: `/documents/acknowledgements?limit=10000${
                  documentAckReportFilterID ? `&document_id=${encodeURIComponent(documentAckReportFilterID)}` : ""
                }`,
                fallback: { items: [] }
              }
            ]
          : []),
        ...(hasPermission("devices.management.compliance", "view") ||
        hasPermission("profiles.policies.compliance", "view")
          ? [
              { name: "compliance_rules", path: "/compliance/rules?limit=2000", fallback: { items: [] } },
              { name: "compliance_issues", path: "/compliance/issues?limit=2000", fallback: { items: [] } }
            ]
          : []),
        { name: "profiles", path: "/profiles?limit=200", fallback: { items: [] } },
        { name: "profile_deployments", path: "/profiles/deployments?limit=50", fallback: { items: [] } },
        { name: "apps", path: "/apps?limit=200", fallback: { items: [] } },
        { name: "app_deployments", path: "/apps/deployments?limit=50", fallback: { items: [] } },
        ...(isPlatformAdmin && hasPermission("settings.security", "view")
          ? [{ name: "audit_logs", path: "/audit-logs?limit=200", fallback: { items: [] } }]
          : []),
        ...(hasPermission("settings.security", "view")
          ? [{ name: "support_sessions", path: "/support-sessions?limit=100", fallback: { items: [] } }]
          : []),
        ...(hasPermission("settings.security", "view")
          ? [{ name: "remote_screen_sessions", path: "/remote-screen-sessions?limit=100", fallback: { items: [] } }]
          : []),
        ...(hasPermission("settings.security", "view")
          ? [
              { name: "security_settings", path: "/security/settings", fallback: null },
              { name: "security_sessions", path: "/security/sessions", fallback: { items: [] } }
            ]
          : []),
        ...(hasPermission("settings.api_keys", "view")
          ? [{ name: "webhooks", path: "/webhooks?include_inactive=true", fallback: { items: [], event_types: [] } }]
          : []),
        ...(hasPermission("settings.notifications", "view")
          ? [
              { name: "alert_settings", path: "/alert-settings", fallback: { settings: defaultAlertNotificationSettings() } },
              { name: "alerts", path: "/alerts?status=open", fallback: { items: [] } }
            ]
          : []),
        ...(hasPermission("settings.reports", "view")
          ? [{ name: "report_schedule", path: "/reports/schedule", fallback: { settings: defaultReportScheduleSettings() } }]
          : []),
        ...(isMasterAdmin && effectiveUser?.platform_admin !== false && hasPermission("settings.security", "view")
          ? [{ name: "tenant_isolation", path: "/security/tenant-isolation", fallback: { available: false, migration_required: true } }]
          : []),
        ...(hasPermission("settings.console_users", "view")
          ? [{ name: "console_users", path: "/console-users?limit=200", fallback: { items: [] } }]
          : hasPermission("settings.security", "edit")
            ? [{ name: "console_users", path: "/security/users?limit=200", fallback: { items: [] } }]
            : []),
        ...(isMasterAdmin && session?.user?.platform_admin !== false && hasPermission("settings.tenants", "view")
          ? [{ name: "tenants", path: "/tenants?limit=200", fallback: { items: [] } }]
          : []),
        ...(hasPermission("settings.certificates", "view")
          ? [
              { name: "configurator_url", path: "/certificates/configurator-url", fallback: null },
              { name: "mdm_apns_config", path: "/mdm-apns/config", fallback: { configured: false, enabled: false } },
              { name: "mdm_apns_reminders", path: "/mdm-apns/reminders", fallback: { items: [], active: null } }
            ]
          : []),
        ...(hasPermission("settings.ade", "view")
          ? [
              { name: "ade_status", path: "/ade/status", fallback: { config: { configured: false }, devices: [], profiles: [] } },
              { name: "ade_sync_events", path: "/abm/sync-events?source=ade&limit=50", fallback: { items: [] } }
            ]
          : []),
        ...(hasPermission("settings.vpp", "view")
          ? [
              { name: "vpp_status", path: "/vpp/status", fallback: { config: { configured: false }, assets: [], assignments: [] } },
              { name: "vpp_sync_events", path: "/abm/sync-events?source=vpp&limit=50", fallback: { items: [] } }
            ]
          : []),
        ...(hasPermission("settings.declarative", "view")
          ? [
              { name: "ddm_declarations", path: "/declarative/declarations", fallback: { items: [] } },
              { name: "ddm_status", path: "/declarative/status", fallback: { items: [] } },
              { name: "ddm_protocol_captures", path: "/declarative/protocol-captures?limit=100", fallback: { items: [] } },
              { name: "ddm_status_subscriptions", path: "/declarative/status-subscriptions", fallback: { items: defaultDDMStatusSubscriptions() } }
            ]
          : []),
        ...(hasPermission("settings.sso", "view")
          ? [{ name: "sso_config", path: "/sso/config", fallback: createEmptySSOForm() }]
          : []),
        ...(hasPermission("settings.role_settings", "view")
          ? [{ name: "role_settings", path: "/role-settings?limit=500", fallback: { items: [] } }]
          : [])
      ];

      const settled = await Promise.allSettled(
        endpointRequests.map((item) => request(item.path))
      );
      const failedEndpoints = [];
      const resolved = settled.map((result, index) => {
        if (result.status === "fulfilled") {
          return result.value;
        }
        failedEndpoints.push(endpointRequests[index].name);
        return endpointRequests[index].fallback;
      });

      const byName = new Map();
      endpointRequests.forEach((item, index) => {
        byName.set(item.name, resolved[index]);
      });

      const consoleSettingsResp = byName.get("console_settings") || {};
      const onboardingResp = byName.get("onboarding_status") || null;
      const agentSettingsResp = byName.get("agent_settings") || { branding: {}, services: [], tickets: [] };
      const devicesResp = byName.get("devices") || { items: [] };
      const commandsResp = byName.get("commands") || { items: [] };
      const commandCenterResp = byName.get("command_center") || { items: [], summary: {} };
      const systemHealthResp = byName.get("system_health") || { overall: "unknown", checks: [] };
      const organizationCurrentResp = byName.get("organization_current") || { item: null };
      const accountsResp = byName.get("accounts") || { items: [] };
      const groupsResp = byName.get("groups") || { items: [] };
      const notificationsResp = byName.get("notifications") || { items: [] };
      const documentsResp = byName.get("documents") || { items: [] };
      const documentFoldersResp = byName.get("document_folders") || { items: [] };
      const documentAckReportResp = byName.get("document_ack_report") || { items: [] };
      const complianceRulesResp = byName.get("compliance_rules") || { items: [] };
      const complianceIssuesResp = byName.get("compliance_issues") || { items: [] };
      const profilesResp = byName.get("profiles") || { items: [] };
      const profileDepsResp = byName.get("profile_deployments") || { items: [] };
      const appsResp = byName.get("apps") || { items: [] };
      const appDepsResp = byName.get("app_deployments") || { items: [] };
      const auditResp = byName.get("audit_logs") || { items: [] };
      const supportSessionsResp = byName.get("support_sessions") || { items: [] };
      const remoteScreenSessionsResp = byName.get("remote_screen_sessions") || { items: [] };
      const securitySettingsResp = byName.get("security_settings") || null;
      const securitySessionsResp = byName.get("security_sessions") || { items: [] };
      const webhooksResp = byName.get("webhooks") || { items: [], event_types: [] };
      const alertSettingsResp = byName.get("alert_settings") || { settings: defaultAlertNotificationSettings() };
      const alertsResp = byName.get("alerts") || { items: [] };
      const reportScheduleResp = byName.get("report_schedule") || { settings: defaultReportScheduleSettings() };
      const tenantIsolationResp = byName.get("tenant_isolation") || null;
      const usersResp = byName.get("console_users") || { items: [] };
      const tenantsResp = byName.get("tenants") || { items: [] };
      const configuratorURLResp = byName.get("configurator_url") || null;
      const mdmAPNSResp = byName.get("mdm_apns_config") || { configured: false, enabled: false };
      const mdmAPNSRemindersResp = byName.get("mdm_apns_reminders") || { items: [], active: null };
      const adeResp = byName.get("ade_status") || { config: { configured: false }, devices: [], profiles: [] };
      const vppResp = byName.get("vpp_status") || { config: { configured: false }, assets: [], assignments: [] };
      const adeSyncEventsResp = byName.get("ade_sync_events") || { items: [] };
      const vppSyncEventsResp = byName.get("vpp_sync_events") || { items: [] };
      const ddmDeclarationsResp = byName.get("ddm_declarations") || { items: [] };
      const ddmStatusResp = byName.get("ddm_status") || { items: [] };
      const ddmProtocolCapturesResp = byName.get("ddm_protocol_captures") || { items: [] };
      const ddmStatusSubscriptionsResp = byName.get("ddm_status_subscriptions") || { items: defaultDDMStatusSubscriptions() };
      const ssoResp = byName.get("sso_config") || null;
      const roleSettingsResp = byName.get("role_settings") || { items: [] };

      setMeta(metaResp || null);
      const nextConsoleLogoDataURL = normalizeConsoleLogoDataURL(consoleSettingsResp?.logo_data_url);
      const nextConsoleLogoClickURL = normalizeConsoleLogoClickURL(consoleSettingsResp?.logo_click_url);
      const nextDashboardSectionSettings = normalizeDashboardSectionSettings(
        consoleSettingsResp?.dashboard_sections
      );
      const nextDashboardSectionOrder = normalizeDashboardSectionOrder(
        consoleSettingsResp?.dashboard_order
      );
      const nextMainNavOrder = normalizeMainNavOrder(consoleSettingsResp?.main_nav_order);
      setConsoleLogoDataURL(nextConsoleLogoDataURL);
      setConsoleLogoDraftDataURL(nextConsoleLogoDataURL);
      setConsoleLogoClickURL(nextConsoleLogoClickURL);
      setConsoleLogoDraftClickURL(nextConsoleLogoClickURL);
      setDashboardSectionSettings(nextDashboardSectionSettings);
      setDashboardSectionDraftSettings(nextDashboardSectionSettings);
      setDashboardSectionOrder(nextDashboardSectionOrder);
      setDashboardSectionDraftOrder(nextDashboardSectionOrder);
      setMainNavOrder(nextMainNavOrder);
      setMainNavDraftOrder(nextMainNavOrder);
      setMainNavDraggingKey("");
      setOnboardingStatus(onboardingResp);
      const nextAgentBranding = normalizeAgentBranding(agentSettingsResp?.branding);
      const nextAgentServices = normalizeAgentServices(agentSettingsResp?.services);
      setAgentBranding(nextAgentBranding);
      setAgentBrandingDraft(nextAgentBranding);
      setAgentServices(nextAgentServices);
      setAgentServicesDraft(nextAgentServices);
      setAgentSupportTickets(Array.isArray(agentSettingsResp?.tickets) ? agentSettingsResp.tickets : []);
      setDevices(Array.isArray(devicesResp?.items) ? devicesResp.items : []);
      setCommands(Array.isArray(commandsResp?.items) ? commandsResp.items : []);
      setCommandCenter({
        items: Array.isArray(commandCenterResp?.items) ? commandCenterResp.items : [],
        summary: commandCenterResp?.summary || {}
      });
      setSystemHealth({
        overall: systemHealthResp?.overall || "unknown",
        checks: Array.isArray(systemHealthResp?.checks) ? systemHealthResp.checks : []
      });
      setOrganizationSettings(organizationCurrentResp?.item || null);
      setOrganizationDraft(organizationSettingsToDraft(organizationCurrentResp?.item || null));
      setAccounts(Array.isArray(accountsResp?.items) ? accountsResp.items : []);
      setGroups(Array.isArray(groupsResp?.items) ? groupsResp.items : []);
      setMobileNotifications(Array.isArray(notificationsResp?.items) ? notificationsResp.items : []);
      setDocuments(Array.isArray(documentsResp?.items) ? documentsResp.items : []);
      setDocumentFolders(Array.isArray(documentFoldersResp?.items) ? documentFoldersResp.items : []);
      setDocumentAckReport(Array.isArray(documentAckReportResp?.items) ? documentAckReportResp.items : []);
      setComplianceRules(Array.isArray(complianceRulesResp?.items) ? complianceRulesResp.items : []);
      setComplianceIssues(Array.isArray(complianceIssuesResp?.items) ? complianceIssuesResp.items : []);
      setProfiles(Array.isArray(profilesResp?.items) ? profilesResp.items : []);
      setProfileDeployments(Array.isArray(profileDepsResp?.items) ? profileDepsResp.items : []);
      setAppsCatalog(Array.isArray(appsResp?.items) ? appsResp.items : []);
      setAppDeployments(Array.isArray(appDepsResp?.items) ? appDepsResp.items : []);
      setAuditLogs(Array.isArray(auditResp?.items) ? auditResp.items : []);
      setSupportSessions(Array.isArray(supportSessionsResp?.items) ? supportSessionsResp.items : []);
      setRemoteScreenSessions(Array.isArray(remoteScreenSessionsResp?.items) ? remoteScreenSessionsResp.items : []);
      const nextSecuritySettings = securitySettingsResp || {
        password_policy: {
          min_length: 12,
          require_upper: true,
          require_lower: true,
          require_number: true,
          require_symbol: true,
          expires_days: 90,
          prevent_reuse: 5
        },
        account_lockout: { threshold: 8, window_minutes: 15, duration_minutes: 15 },
        trusted_ips: { enabled: false, cidrs: [] }
      };
      setSecuritySettings(nextSecuritySettings);
      setSecuritySettingsDraft(nextSecuritySettings);
      setSecuritySessions(Array.isArray(securitySessionsResp?.items) ? securitySessionsResp.items : []);
      setWebhooks(Array.isArray(webhooksResp?.items) ? webhooksResp.items : []);
      setWebhookEventTypes(Array.isArray(webhooksResp?.event_types) ? webhooksResp.event_types : []);
      const nextAlertSettings = normalizeAlertNotificationSettings(alertSettingsResp?.settings);
      setAlertNotificationSettings(nextAlertSettings);
      setAlertNotificationDraft(nextAlertSettings);
      setAlertNotifications(Array.isArray(alertsResp?.items) ? alertsResp.items : []);
      const nextReportSchedule = normalizeReportScheduleSettings(reportScheduleResp?.settings);
      setReportScheduleSettings(nextReportSchedule);
      setReportScheduleDraft(nextReportSchedule);
      setTenantIsolationReport(tenantIsolationResp || null);
      setConsoleUsers(Array.isArray(usersResp?.items) ? usersResp.items : []);
      setTenants(Array.isArray(tenantsResp?.items) ? tenantsResp.items : []);
      setConfiguratorEnrollmentURL(configuratorURLResp || null);
      setMDMAPNSConfig(mdmAPNSResp || null);
      setMDMAPNSReminders(mdmAPNSRemindersResp || { items: [], active: null });
      setMDMAPNSForm((prev) => ({
        ...prev,
        appleID: String(mdmAPNSResp?.apple_id || prev.appleID || ""),
        certPEM: ""
      }));
      setADEStatus(adeResp?.config || null);
      setADEDevices(Array.isArray(adeResp?.devices) ? adeResp.devices : []);
      setADEProfiles(Array.isArray(adeResp?.profiles) ? adeResp.profiles : []);
      setADESyncEvents(Array.isArray(adeSyncEventsResp?.items) ? adeSyncEventsResp.items : []);
      setADETokenForm((prev) => ({
        ...prev,
        appleID: String(adeResp?.config?.apple_id || prev.appleID || ""),
        file: null
      }));
      setVPPStatus(vppResp?.config || null);
      setVPPAssets(Array.isArray(vppResp?.assets) ? vppResp.assets : []);
      setVPPAssignments(Array.isArray(vppResp?.assignments) ? vppResp.assignments : []);
      setVPPSyncEvents(Array.isArray(vppSyncEventsResp?.items) ? vppSyncEventsResp.items : []);
      setVPPTokenForm((prev) => ({
        ...prev,
        appleID: String(vppResp?.config?.apple_id || prev.appleID || ""),
        file: null
      }));
      setDDMDeclarations(Array.isArray(ddmDeclarationsResp?.items) ? ddmDeclarationsResp.items : []);
      setDDMStatuses(Array.isArray(ddmStatusResp?.items) ? ddmStatusResp.items : []);
      setDDMProtocolCaptures(Array.isArray(ddmProtocolCapturesResp?.items) ? ddmProtocolCapturesResp.items : []);
      setDDMStatusSubscriptions(Array.isArray(ddmStatusSubscriptionsResp?.items) ? ddmStatusSubscriptionsResp.items : defaultDDMStatusSubscriptions());
      setSSOConfig(ssoResp);
      setSSOForm(ssoConfigToForm(ssoResp));
      setRoleSettings(Array.isArray(roleSettingsResp?.items) ? roleSettingsResp.items : []);

      if (failedEndpoints.length > 0) {
        setErrorMessage(`Partial load failed: ${failedEndpoints.join(", ")}`);
      }
    } catch (err) {
      setErrorMessage(err.message || "Request failed");
      if (err.status === 401) {
        clearSession();
      }
    } finally {
      setLoading(false);
    }
  }

  async function refreshAuditLogs() {
    if (!hasPermission("settings.security", "view")) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request(`/audit-logs?${buildAuditLogsQuery(auditFilters)}`);
      setAuditLogs(Array.isArray(response?.items) ? response.items : []);
      setInfoMessage(t("actionLogsRefreshDone"));
    } catch (err) {
      setErrorMessage(`${t("actionLogsLoadFailed")}: ${err.message}`);
    }
  }

  async function exportAuditLogs(format) {
    if (!hasPermission("settings.security", "view")) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const safeFormat = String(format || "csv").toLowerCase() === "json" ? "json" : "csv";
      const base = normalizeAPIBase(session?.apiBase || DEFAULT_API_BASE);
      const response = await fetch(`${base}/audit-logs?${buildAuditLogsQuery(auditFilters, { format: safeFormat, limit: 10000 })}`, {
        credentials: "include",
        cache: "no-store"
      });
      if (!response.ok) {
        const parsed = parseBody(await response.text());
        if (response.status === 401) clearSession();
        throw new Error(readableError(parsed, response.statusText));
      }
      const blob = await response.blob();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = `turuk-audit-logs-${stamp}.${safeFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      setInfoMessage(t("actionLogsExportDone"));
    } catch (err) {
      setErrorMessage(`${t("actionLogsExportFailed")}: ${err.message}`);
    }
  }

  async function saveAlertNotificationSettings() {
    if (!hasPermission("settings.notifications", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/alert-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings: normalizeAlertNotificationSettings(alertNotificationDraft) })
      });
      const next = normalizeAlertNotificationSettings(out?.settings);
      setAlertNotificationSettings(next);
      setAlertNotificationDraft(next);
      setInfoMessage(t("alertSettingsSaved"));
    } catch (err) {
      setErrorMessage(`${t("alertSettingsSaveFailed")}: ${err.message}`);
    }
  }

  async function evaluateAlertNotifications() {
    if (!hasPermission("settings.notifications", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/alerts/evaluate", { method: "POST" });
      const alertsResp = await request("/alerts?status=open");
      setAlertNotifications(Array.isArray(alertsResp?.items) ? alertsResp.items : []);
      const created = Number(out?.summary?.created || 0);
      const updated = Number(out?.summary?.updated || 0);
      setInfoMessage(`${t("alertSettingsEvaluateDone")}: ${created}/${updated}`);
    } catch (err) {
      setErrorMessage(`${t("alertSettingsEvaluateFailed")}: ${err.message}`);
    }
  }

  async function acknowledgeAlertNotification(id) {
    if (!hasPermission("settings.notifications", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const cleaned = String(id || "").trim();
    if (!cleaned) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/alerts/ack", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: cleaned })
      });
      setAlertNotifications((prev) => prev.filter((item) => String(item.id || "") !== cleaned));
      setInfoMessage(t("alertSettingsAckDone"));
    } catch (err) {
      setErrorMessage(`${t("alertSettingsAckFailed")}: ${err.message}`);
    }
  }

  async function downloadReport(reportType, format) {
    const safeType = String(reportType || "executive").toLowerCase();
    const safeFormat = String(format || "pdf").toLowerCase() === "csv" ? "csv" : "pdf";
    setErrorMessage("");
    setInfoMessage("");
    try {
      const base = normalizeAPIBase(session?.apiBase || DEFAULT_API_BASE);
      const response = await fetch(
        `${base}/reports/export?type=${encodeURIComponent(safeType)}&format=${encodeURIComponent(safeFormat)}`,
        { credentials: "include", cache: "no-store" }
      );
      if (!response.ok) {
        const parsed = parseBody(await response.text());
        if (response.status === 401) clearSession();
        throw new Error(readableError(parsed, response.statusText));
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = url;
      link.download = `turuk-${safeType}-report-${stamp}.${safeFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setInfoMessage(t("reportsExportDone"));
    } catch (err) {
      setErrorMessage(`${t("reportsExportFailed")}: ${err.message}`);
    }
  }

  async function saveReportSchedule() {
    if (!hasPermission("settings.reports", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/reports/schedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings: normalizeReportScheduleSettings(reportScheduleDraft) })
      });
      const next = normalizeReportScheduleSettings(out?.settings);
      setReportScheduleSettings(next);
      setReportScheduleDraft(next);
      setInfoMessage(t("reportsScheduleSaved"));
    } catch (err) {
      setErrorMessage(`${t("reportsScheduleSaveFailed")}: ${err.message}`);
    }
  }

  async function reloadSupportSessions() {
    if (!hasPermission("settings.security", "view")) return;
    try {
      const out = await request("/support-sessions?limit=100");
      setSupportSessions(Array.isArray(out?.items) ? out.items : []);
    } catch (err) {
      setErrorMessage(`${t("supportSessionsLoadFailed")}: ${err.message}`);
    }
  }

  async function reloadRemoteScreenSessions() {
    if (!hasPermission("settings.security", "view")) return;
    try {
      const out = await request("/remote-screen-sessions?limit=100");
      setRemoteScreenSessions(Array.isArray(out?.items) ? out.items : []);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось загрузить заявки удаленного экрана" : "Failed to load remote screen sessions"}: ${err.message}`);
    }
  }

  async function fetchRemoteScreenFrame(sessionID) {
    if (!sessionIsAuthenticated(session)) {
      throw new Error(t("authRequired"));
    }
    const base = normalizeAPIBase(session?.apiBase || DEFAULT_API_BASE);
    const headers = new Headers();
    if (session?.authMode === "bearer" && session?.token) {
      headers.set("Authorization", `Bearer ${String(session.token || "").trim()}`);
    }
    const response = await fetch(`${base}/remote-screen-sessions/frame?id=${encodeURIComponent(sessionID)}&_=${Date.now()}`, {
      cache: "no-store",
      credentials: "include",
      headers
    });
    if (!response.ok) {
      throw new Error(response.statusText || "frame unavailable");
    }
    const blob = await response.blob();
    if (!String(blob.type || "").startsWith("image/")) {
      throw new Error("invalid frame");
    }
    return URL.createObjectURL(blob);
  }

  async function fetchRemoteScreenMetrics(sessionID) {
    if (!sessionIsAuthenticated(session)) {
      throw new Error(t("authRequired"));
    }
    const out = await request(`/remote-screen-sessions/metrics?id=${encodeURIComponent(sessionID)}`);
    return out?.metrics || {};
  }

  useEffect(() => {
    const interval = window.setInterval(() => setRemoteScreenNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !hasPermission("settings.security", "view")) return undefined;
    const ids = activeRemoteScreenSessionIDs.split(",").filter(Boolean);
    if (ids.length === 0) {
      setRemoteScreenFrameURLs((prev) => {
        Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
      return undefined;
    }

    let cancelled = false;
    const sockets = [];
    const loadFrame = async (id) => {
      try {
        const url = await fetchRemoteScreenFrame(id);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
          setRemoteScreenFrameURLs((prev) => {
            if (prev[id]) URL.revokeObjectURL(prev[id]);
            return { ...prev, [id]: url };
          });
          const nowISO = new Date().toISOString();
          setRemoteScreenMetrics((prev) => ({
            ...prev,
            [id]: {
              ...(prev[id] || {}),
              session_id: id,
              last_frame_at: nowISO,
              updated_at: nowISO
            }
          }));
        } catch {
        if (!cancelled) {
          setRemoteScreenFrameURLs((prev) => {
            if (!prev[id]) return prev;
            URL.revokeObjectURL(prev[id]);
            const next = { ...prev };
            delete next[id];
            return next;
          });
        }
      }
    };
    const loadFrames = async () => {
      for (const id of ids) {
        await loadFrame(id);
      }
    };

    loadFrames();
    ids.forEach((id) => {
      try {
        const ws = new WebSocket(
          apiWebSocketURL(session?.apiBase || DEFAULT_API_BASE, "/remote-screen-sessions/stream", {
            id,
            token: session?.authMode === "bearer" ? session?.token : ""
          })
        );
        ws.binaryType = "blob";
        ws.onopen = () => {
          setRemoteScreenMetrics((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), session_id: id, viewer_connected: true, viewer_disconnected: false }
          }));
        };
        ws.onmessage = (event) => {
          if (cancelled || !(event.data instanceof Blob)) return;
          const url = URL.createObjectURL(event.data);
          const now = Date.now();
          const nowISO = new Date(now).toISOString();
          setRemoteScreenFrameURLs((prev) => {
            if (prev[id]) URL.revokeObjectURL(prev[id]);
            return { ...prev, [id]: url };
          });
          setRemoteScreenMetrics((prev) => {
            const current = prev[id] || {};
            const previousFrameAt = current.last_frame_at ? new Date(current.last_frame_at).getTime() : 0;
            const deltaSeconds = previousFrameAt > 0 ? Math.max((now - previousFrameAt) / 1000, 0.001) : 0;
            return {
              ...prev,
              [id]: {
                ...current,
                session_id: id,
                viewer_connected: true,
                viewer_disconnected: false,
                last_frame_at: nowISO,
                updated_at: nowISO,
                last_frame_bytes: event.data.size || current.last_frame_bytes || 0,
                frame_count: Number(current.frame_count || 0) + 1,
                fps: deltaSeconds > 0 ? 1 / deltaSeconds : Number(current.fps || 0)
              }
            };
          });
        };
        ws.onclose = () => {
          setRemoteScreenMetrics((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), session_id: id, viewer_connected: false, viewer_disconnected: true }
          }));
          if (!cancelled) {
            window.setTimeout(() => loadFrame(id), 1000);
          }
        };
        sockets.push(ws);
      } catch {
        loadFrame(id);
      }
    });
    const interval = window.setInterval(loadFrames, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      sockets.forEach((ws) => {
        try {
          ws.close();
        } catch {
          // Ignore close errors during cleanup.
        }
      });
    };
  }, [activeRemoteScreenSessionIDs, isAuthenticated, session?.apiBase, session?.authMode, session?.token]);

  useEffect(() => {
    if (!isAuthenticated || !hasPermission("settings.security", "view")) return undefined;
    const ids = activeRemoteScreenSessionIDs.split(",").filter(Boolean);
    if (ids.length === 0) {
      setRemoteScreenMetrics({});
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      for (const id of ids) {
        try {
          const metrics = await fetchRemoteScreenMetrics(id);
          if (cancelled) return;
          setRemoteScreenMetrics((prev) => ({
            ...prev,
            [id]: {
              ...(prev[id] || {}),
              ...metrics
            }
          }));
        } catch {
          // Keep local stream metrics when server metrics are temporarily unavailable.
        }
        if (!cancelled) {
          await reloadRemoteAssistActions(id);
        }
      }
    };
    load();
    const interval = window.setInterval(load, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeRemoteScreenSessionIDs, isAuthenticated, session?.apiBase, session?.authMode, session?.token]);

  async function createSupportSession() {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/support-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: supportSessionForm.reason.trim(),
          ttl_minutes: Number(supportSessionForm.ttlMinutes || 120)
        })
      });
      if (out?.item) {
        setSupportSessions((prev) => [out.item, ...prev.filter((item) => item.id !== out.item.id)]);
      }
      setSupportSessionForm({ reason: "", ttlMinutes: 120 });
      setInfoMessage(t("supportSessionsRequested"));
      await reloadSupportSessions();
    } catch (err) {
      setErrorMessage(`${t("supportSessionsRequestFailed")}: ${err.message}`);
    }
  }

  async function createRemoteScreenSession() {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/remote-screen-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: remoteScreenForm.accountId,
          device_udid: remoteScreenForm.deviceUdid.trim(),
          reason: remoteScreenForm.reason.trim(),
          ttl_minutes: Number(remoteScreenForm.ttlMinutes || 60)
        })
      });
      if (out?.item) {
        setRemoteScreenSessions((prev) => [out.item, ...prev.filter((item) => item.id !== out.item.id)]);
      }
      setRemoteScreenForm({ accountId: "", deviceUdid: "", reason: "", ttlMinutes: 60 });
      setInfoMessage(lang === "ru" ? "Заявка на просмотр экрана создана" : "Remote screen request created");
      await reloadRemoteScreenSessions();
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось создать заявку" : "Failed to create request"}: ${err.message}`);
    }
  }

  async function createDeviceRemoteScreenSession(device) {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const deviceUDID = String(device?.udid || routeDeviceUDID || "").trim();
    const accountID = String(device?.account_id || remoteScreenForm.accountId || "").trim();
    if (!deviceUDID && !accountID) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/remote-screen-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: accountID,
          device_udid: deviceUDID,
          reason: remoteScreenForm.reason.trim(),
          ttl_minutes: Number(remoteScreenForm.ttlMinutes || 60)
        })
      });
      if (out?.item) {
        setRemoteScreenSessions((prev) => [out.item, ...prev.filter((item) => item.id !== out.item.id)]);
      }
      setRemoteScreenForm((prev) => ({ ...prev, reason: "", ttlMinutes: 60 }));
      setInfoMessage(lang === "ru" ? "Заявка на удаленный доступ создана" : "Remote access request created");
      await reloadRemoteScreenSessions();
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось создать заявку" : "Failed to create request"}: ${err.message}`);
    }
  }

  async function revokeRemoteScreenSession(id) {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const cleaned = String(id || "").trim();
    if (!cleaned) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/remote-screen-sessions/revoke", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: cleaned })
      });
      if (out?.item) {
        setRemoteScreenSessions((prev) => [out.item, ...prev.filter((item) => item.id !== out.item.id)]);
      }
      setInfoMessage(lang === "ru" ? "Заявка отозвана" : "Remote screen request revoked");
      await reloadRemoteScreenSessions();
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось отозвать заявку" : "Failed to revoke request"}: ${err.message}`);
    }
  }

  async function endRemoteScreenSession(id) {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const cleaned = String(id || "").trim();
    if (!cleaned) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/remote-screen-sessions/end", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: cleaned })
      });
      if (out?.item) {
        setRemoteScreenSessions((prev) => [out.item, ...prev.filter((item) => item.id !== out.item.id)]);
      }
      setRemoteScreenMetrics((prev) => ({
        ...prev,
        [cleaned]: { ...(prev[cleaned] || {}), publisher_connected: false, viewer_connected: false }
      }));
      setInfoMessage(lang === "ru" ? "Удаленный доступ завершен" : "Remote access ended");
      await reloadRemoteScreenSessions();
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось завершить удаленный доступ" : "Failed to end remote access"}: ${err.message}`);
    }
  }

  async function reloadRemoteAssistActions(sessionID) {
    const cleaned = String(sessionID || "").trim();
    if (!cleaned) return;
    try {
      const out = await request(`/remote-screen-sessions/actions?id=${encodeURIComponent(cleaned)}`);
      setRemoteAssistActions((prev) => ({
        ...prev,
        [cleaned]: Array.isArray(out?.items) ? out.items : []
      }));
    } catch {
      setRemoteAssistActions((prev) => ({ ...prev, [cleaned]: prev[cleaned] || [] }));
    }
  }

  async function createRemoteAssistAction(sessionID) {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const cleaned = String(sessionID || "").trim();
    const draft = remoteAssistDrafts[cleaned] || {};
    const targetURL = String(draft.targetURL || "").trim();
    const title = String(draft.title || "").trim();
    if (!cleaned || !targetURL) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/remote-screen-sessions/actions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: cleaned,
          title,
          target_url: targetURL
        })
      });
      if (out?.item) {
        setRemoteAssistActions((prev) => ({
          ...prev,
          [cleaned]: [out.item, ...(prev[cleaned] || []).filter((item) => item.id !== out.item.id)]
        }));
      }
      setRemoteAssistDrafts((prev) => ({
        ...prev,
        [cleaned]: { title: "", targetURL: "" }
      }));
      setInfoMessage(lang === "ru" ? "Запрос на открытие отправлен пользователю" : "Open request sent to user");
      await reloadRemoteAssistActions(cleaned);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось отправить запрос" : "Failed to send request"}: ${err.message}`);
    }
  }

  function updateRemoteAssistDraft(sessionID, patch) {
    const cleaned = String(sessionID || "").trim();
    if (!cleaned) return;
    setRemoteAssistDrafts((prev) => ({
      ...prev,
      [cleaned]: { ...(prev[cleaned] || { title: "", targetURL: "" }), ...patch }
    }));
  }

  function selectRemoteAssistService(sessionID, serviceID) {
    const service = agentServices.find((item) => String(item.id || "") === String(serviceID || ""));
    updateRemoteAssistDraft(sessionID, {
      title: service?.title || "",
      targetURL: service?.url || ""
    });
  }

  async function updateSupportSession(id, action) {
    if (!hasPermission("settings.security", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const cleaned = String(id || "").trim();
    if (!cleaned) return;
    const actionPath = action === "approve" ? "approve" : action === "access" ? "access" : "revoke";
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request(`/support-sessions/${actionPath}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: cleaned })
      });
      if (out?.item) {
        setSupportSessions((prev) => [out.item, ...prev.filter((item) => item.id !== out.item.id)]);
      }
      if (actionPath === "approve") {
        setInfoMessage(t("supportSessionsApproved"));
      } else if (actionPath === "access") {
        setInfoMessage(t("supportSessionsAccessed"));
      } else {
        setInfoMessage(t("supportSessionsRevoked"));
      }
      await reloadSupportSessions();
    } catch (err) {
      setErrorMessage(`${t("supportSessionsActionFailed")}: ${err.message}`);
    }
  }

  async function loadDeviceTelemetry(udid) {
    if (!isAuthenticated || !udid || mustChangePassword || mfaSetupRequired) return;
    setTelemetryLoading(true);
    try {
      const [
        detailResp,
        appsResp,
        profilesResp,
        certificatesResp,
        locationsResp,
        activationLockResp,
        sharedIPadUsersResp,
        attestationsResp,
        sharedIPadSettingsResp,
        appDeploymentsResp,
        profileDeploymentsResp
      ] = await Promise.all([
        request(`/devices/detail?udid=${encodeURIComponent(udid)}`),
        request(`/devices/apps?udid=${encodeURIComponent(udid)}&limit=1000`),
        request(`/devices/profiles?udid=${encodeURIComponent(udid)}&limit=300`),
        request(`/devices/certificates?udid=${encodeURIComponent(udid)}&limit=300`).catch(() => ({ items: [] })),
        request(`/devices/locations?udid=${encodeURIComponent(udid)}&limit=50`),
        request(`/devices/activation-lock?udid=${encodeURIComponent(udid)}&limit=50`).catch(() => ({ items: [] })),
        request(`/devices/shared-ipad/users?udid=${encodeURIComponent(udid)}&limit=200`).catch(() => ({ items: [] })),
        request(`/devices/attestations?udid=${encodeURIComponent(udid)}&limit=20`).catch(() => ({ items: [] })),
        request("/settings/shared-ipad").catch(() => ({})),
        request(`/apps/deployments?udid=${encodeURIComponent(udid)}&limit=500`).catch(() => ({ items: [] })),
        request(`/profiles/deployments?udid=${encodeURIComponent(udid)}&limit=500`).catch(() => ({ items: [] }))
      ]);
      setDeviceDetail(detailResp || null);
      setDeviceApps(Array.isArray(appsResp?.items) ? appsResp.items : []);
      setDeviceAppDeployments(Array.isArray(appDeploymentsResp?.items) ? appDeploymentsResp.items : []);
      setDeviceProfilesReported(Array.isArray(profilesResp?.items) ? profilesResp.items : []);
      setDeviceCertificates(Array.isArray(certificatesResp?.items) ? certificatesResp.items : []);
      setDeviceLocations(Array.isArray(locationsResp?.items) ? locationsResp.items : []);
      setDeviceActivationLockCodes(Array.isArray(activationLockResp?.items) ? activationLockResp.items : []);
      setDeviceSharedIPadUsers(Array.isArray(sharedIPadUsersResp?.items) ? sharedIPadUsersResp.items : []);
      setDeviceAttestations(Array.isArray(attestationsResp?.items) ? attestationsResp.items : []);
      setSharedIPadSettings(sharedIPadSettingsResp && typeof sharedIPadSettingsResp === "object" ? sharedIPadSettingsResp : {});
      setSharedIPadSettingsDraft({
        managed_apple_account_domain: String(sharedIPadSettingsResp?.managed_apple_account_domain || ""),
        default_shared_ipad_domain: String(sharedIPadSettingsResp?.default_shared_ipad_domain || ""),
        classroom_notes: String(sharedIPadSettingsResp?.classroom_notes || "")
      });
      setProfileDeployments((prev) => {
        const incoming = Array.isArray(profileDeploymentsResp?.items) ? profileDeploymentsResp.items : [];
        if (!Array.isArray(prev) || prev.length === 0) return incoming;
        const keep = prev.filter((item) => String(item?.device_udid || "").trim() !== udid);
        return [...incoming, ...keep];
      });
    } catch (err) {
      if (err.status === 404) {
        setDeviceDetail(null);
        setDeviceApps([]);
        setDeviceAppDeployments([]);
        setDeviceProfilesReported([]);
        setDeviceCertificates([]);
        setDeviceLocations([]);
        setDeviceActivationLockCodes([]);
        setDeviceSharedIPadUsers([]);
        setDeviceAttestations([]);
        setProfileDeployments((prev) =>
          Array.isArray(prev)
            ? prev.filter((item) => String(item?.device_udid || "").trim() !== String(udid || "").trim())
            : []
        );
      } else {
        setErrorMessage(err.message || "Telemetry request failed");
      }
    } finally {
      setTelemetryLoading(false);
    }
  }

  async function updateDeviceAllowedSIM(device, explicitValue) {
    const udid = String(device?.udid || routeDeviceUDID || selectedUdid || "").trim();
    if (!udid || !hasPermission("devices.detail", "edit")) return;
    const currentValue = String(device?.network?.allowed_iccid || currentDeviceICCID(device) || "").trim();
    const nextValue =
      explicitValue === undefined
        ? window.prompt(t("deviceDetailsAllowedICCIDEdit"), currentValue)
        : String(explicitValue || "");
    if (nextValue === null) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const updated = await request("/devices/allowed-sim", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid,
          allowed_iccid: nextValue.trim()
        })
      });
      setDeviceDetail(updated || null);
      setInfoMessage(t("deviceDetailsAllowedICCIDSaved"));
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsAllowedICCIDFailed")}: ${err.message}`);
    }
  }

  function openAllowedSIMModal(device) {
    setAllowedSIMDraft(String(device?.network?.allowed_iccid || currentDeviceICCID(device) || "").trim());
    setShowAllowedSIMModal(true);
  }

  function closeAllowedSIMModal() {
    setShowAllowedSIMModal(false);
    setAllowedSIMDraft("");
  }

  async function saveAllowedSIMFromModal(device, value = allowedSIMDraft) {
    await updateDeviceAllowedSIM(device, value);
    setAllowedSIMDraft(String(value || "").trim());
  }

  function handleConsoleLogoFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const isSupportedImage =
      String(file.type || "").startsWith("image/") ||
      /\.(png|jpe?g|webp|gif|svg)$/i.test(String(file.name || ""));
    if (!isSupportedImage) {
      setErrorMessage(t("consoleBrandingInvalidFile"));
      event.target.value = "";
      return;
    }
    if (file.size > MAX_CONSOLE_LOGO_FILE_BYTES) {
      setErrorMessage(t("consoleBrandingFileTooLarge"));
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const nextValue = normalizeConsoleLogoDataURL(reader.result);
      if (!nextValue) {
        setErrorMessage(t("consoleBrandingReadFailed"));
        return;
      }
      setConsoleLogoDraftDataURL(nextValue);
      setErrorMessage("");
      setInfoMessage("");
    };
    reader.onerror = () => {
      setErrorMessage(t("consoleBrandingReadFailed"));
    };
    reader.readAsDataURL(file);
  }

  async function saveConsoleBranding(
    nextLogoDataURL = consoleLogoDraftDataURL,
    nextLogoClickURL = consoleLogoDraftClickURL
  ) {
    const normalizedClickURL = normalizeConsoleLogoClickURL(nextLogoClickURL);
    if (String(nextLogoClickURL || "").trim() && !normalizedClickURL) {
      setErrorMessage(t("consoleBrandingLogoClickInvalid"));
      return;
    }
    try {
      const out = await request("/console-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          logo_data_url: normalizeConsoleLogoDataURL(nextLogoDataURL),
          logo_click_url: normalizedClickURL
        })
      });
      const savedValue = normalizeConsoleLogoDataURL(out?.logo_data_url);
      const savedClickValue = normalizeConsoleLogoClickURL(out?.logo_click_url);
      setConsoleLogoDataURL(savedValue);
      setConsoleLogoDraftDataURL(savedValue);
      setConsoleLogoClickURL(savedClickValue);
      setConsoleLogoDraftClickURL(savedClickValue);
      setInfoMessage(savedValue ? t("consoleBrandingSaved") : t("consoleBrandingResetDone"));
      setErrorMessage("");
      if (consoleLogoInputRef.current) {
        consoleLogoInputRef.current.value = "";
      }
    } catch (err) {
      setErrorMessage(err.message || t("consoleBrandingSaveFailed"));
    }
  }

  async function resetConsoleBranding() {
    await saveConsoleBranding("", consoleLogoDraftClickURL);
  }

  async function saveConsoleDashboardSettings(
    nextSettings = dashboardSectionDraftSettings,
    nextOrder = dashboardSectionDraftOrder
  ) {
    try {
      const out = await request("/console-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dashboard_sections: normalizeDashboardSectionSettings(nextSettings),
          dashboard_order: normalizeDashboardSectionOrder(nextOrder)
        })
      });
      const savedValue = normalizeDashboardSectionSettings(out?.dashboard_sections);
      const savedOrder = normalizeDashboardSectionOrder(out?.dashboard_order);
      setDashboardSectionSettings(savedValue);
      setDashboardSectionDraftSettings(savedValue);
      setDashboardSectionOrder(savedOrder);
      setDashboardSectionDraftOrder(savedOrder);
      setDashboardSectionDraggingKey("");
      setInfoMessage(t("consoleDashboardSaved"));
      setErrorMessage("");
    } catch (err) {
      setErrorMessage(err.message || t("consoleDashboardSaveFailed"));
    }
  }

  async function seedOnboardingDemoData() {
    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/onboarding/demo-data", { method: "POST" });
      setOnboardingStatus(out || null);
      await loadAllData();
      setInfoMessage(t("onboardingDemoSeeded"));
    } catch (err) {
      setErrorMessage(`${t("onboardingDemoSeedFailed")}: ${err.message}`);
    }
  }

  async function saveMainNavOrder(nextOrder = mainNavDraftOrder) {
    try {
      const out = await request("/console-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          main_nav_order: normalizeMainNavOrder(nextOrder)
        })
      });
      const savedOrder = normalizeMainNavOrder(out?.main_nav_order);
      setMainNavOrder(savedOrder);
      setMainNavDraftOrder(savedOrder);
      setMainNavDraggingKey("");
      setInfoMessage(t("consoleMainNavOrderSaved"));
      setErrorMessage("");
    } catch (err) {
      setErrorMessage(err.message || t("consoleMainNavOrderSaveFailed"));
    }
  }

  function updateAgentServiceDraft(index, patch) {
    setAgentServicesDraft((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
    );
  }

  async function saveAgentSettings() {
    setErrorMessage("");
    setInfoMessage("");
    const normalizedBranding = normalizeAgentBranding(agentBrandingDraft);
    const normalizedServices = normalizeAgentServices(agentServicesDraft).map((item, index) => ({
      ...item,
      sort_order: index + 1
    }));
    if (normalizedServices.some((item) => !item.title)) {
      setErrorMessage(t("agentSettingsServiceTitleRequired"));
      return;
    }
    setLoading(true);
    try {
      await request("/agent-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          branding: normalizedBranding,
          services: normalizedServices
        })
      });
      setAgentBranding(normalizedBranding);
      setAgentBrandingDraft(normalizedBranding);
      setAgentServices(normalizedServices);
      setAgentServicesDraft(normalizedServices);
      setInfoMessage(t("agentSettingsSaved"));
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("agentSettingsSaveFailed")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function refreshAgentSupportTickets() {
    if (!isAuthenticated || mustChangePassword || mfaSetupRequired || !hasPermission("settings.agent", "view")) {
      return;
    }
    try {
      const out = await request("/agent-settings");
      setAgentSupportTickets(Array.isArray(out?.tickets) ? out.tickets : []);
    } catch {
      // Keep the current UI state; support notifications are non-blocking.
    }
  }

  async function loadAgentSupportTicket(ticketID) {
    if (!ticketID) return;
    setErrorMessage("");
    try {
      const out = await request(`/agent-settings/support-ticket?id=${encodeURIComponent(ticketID)}`);
      setSelectedAgentSupportTicket(out || null);
      setAgentSupportTicketStatus(out?.ticket?.status || "open");
      setAgentSupportTicketReply("");
      if (out?.ticket?.id) {
        setAgentSupportTickets((prev) =>
          prev.map((rawItem) => {
            const item = rawItem?.ticket || rawItem;
            return item?.id === out.ticket.id ? out.ticket : rawItem;
          })
        );
      }
    } catch (err) {
      setErrorMessage(err.message || t("agentSettingsTicketSaveFailed"));
    }
  }

  async function saveAgentSupportTicketUpdate() {
    const ticketID = selectedAgentSupportTicket?.ticket?.id;
    if (!ticketID) return;
    setErrorMessage("");
    setInfoMessage("");
    setLoading(true);
    try {
      const out = await request("/agent-settings/support-ticket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticket_id: ticketID,
          status: agentSupportTicketStatus,
          reply: agentSupportTicketReply
        })
      });
      setSelectedAgentSupportTicket(out || null);
      setAgentSupportTicketStatus(out?.ticket?.status || agentSupportTicketStatus || "open");
      setAgentSupportTicketReply("");
      if (out?.ticket?.id) {
        setAgentSupportTickets((prev) =>
          prev.map((rawItem) => {
            const item = rawItem?.ticket || rawItem;
            return item?.id === out.ticket.id ? out.ticket : rawItem;
          })
        );
      }
      setInfoMessage(t("agentSettingsTicketSaved"));
    } catch (err) {
      setErrorMessage(`${t("agentSettingsTicketSaveFailed")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function loadAccountDevices(accountID) {
    if (!isAuthenticated || !accountID || mustChangePassword || mfaSetupRequired) {
      setAccountDevices([]);
      return;
    }
    setAccountsDevicesLoading(true);
    try {
      const out = await request(`/accounts/devices?account_id=${encodeURIComponent(accountID)}&limit=200`);
      setAccountDevices(Array.isArray(out?.items) ? out.items : []);
    } catch (err) {
      setAccountDevices([]);
      if (err.status !== 404) {
        setErrorMessage(err.message || "Account devices request failed");
      }
    } finally {
      setAccountsDevicesLoading(false);
    }
  }

  async function loadAccountMobileBindings(accountID) {
    if (!isAuthenticated || !accountID || mustChangePassword || mfaSetupRequired) {
      setAccountMobileBindings([]);
      return;
    }
    setAccountsMobileBindingsLoading(true);
    try {
      const out = await request(`/accounts/mobile-bindings?account_id=${encodeURIComponent(accountID)}&limit=200`);
      setAccountMobileBindings(Array.isArray(out?.items) ? out.items : []);
    } catch (err) {
      setAccountMobileBindings([]);
      if (err.status !== 404) {
        setErrorMessage(err.message || "Account mobile bindings request failed");
      }
    } finally {
      setAccountsMobileBindingsLoading(false);
    }
  }

  useEffect(() => {
    if (isAuthenticated && !mustChangePassword && !mfaSetupRequired) {
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, session?.apiBase, mustChangePassword, mfaSetupRequired]);

  useEffect(() => {
    if (!isAuthenticated || mustChangePassword || mfaSetupRequired || !hasPermission("settings.agent", "view")) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      refreshAgentSupportTickets();
    }, 30000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, session?.apiBase, mustChangePassword, mfaSetupRequired, session?.user?.role, session?.user?.permissions]);

  useEffect(() => {
    if (routeDeviceUDID && DEVICE_WORKSPACE_PAGES.includes(activePage)) {
      return;
    }
    if (selectedDevice?.udid) {
      setSelectedUdid(selectedDevice.udid);
    }
  }, [selectedDevice?.udid, routeDeviceUDID, activePage]);

  useEffect(() => {
    if (deviceDetailsTab !== "macOSUEM") return;
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) return;
    loadMacOSUEM(udid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceDetailsTab, routeDeviceUDID, selectedUdid, isAuthenticated, session?.apiBase]);

  useEffect(() => {
    const udid = selectedUdid || selectedDevice?.udid;
    if (!udid || !isAuthenticated || mustChangePassword || mfaSetupRequired) {
      setDeviceDetail(null);
      setDeviceApps([]);
      setDeviceAppDeployments([]);
      setDeviceProfilesReported([]);
      setDeviceLocations([]);
      setDeviceActivationLockCodes([]);
      setDeviceSharedIPadUsers([]);
      setDeviceAttestations([]);
      return;
    }
    loadDeviceTelemetry(udid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUdid, selectedDevice?.udid, isAuthenticated, session?.apiBase, mustChangePassword, mfaSetupRequired]);

  useEffect(() => {
    const valid = new Set(
      deviceAppsViewItems
        .map((item) => String(item._bundleID || item.bundle_id || item.bundleID || "").trim())
        .filter(Boolean)
    );
    setSelectedDeviceAppBundleIDs((prev) => prev.filter((bundleID) => valid.has(bundleID)));
  }, [deviceAppsViewItems]);

  useEffect(() => {
    if (!filteredAccounts.length) {
      setSelectedAccountId("");
      setAccountDevices([]);
      setAccountMobileBindings([]);
      return;
    }
    const selectedExists = filteredAccounts.some((item) => item.id === selectedAccountId);
    if (!selectedExists) {
      setSelectedAccountId(filteredAccounts[0].id);
    }
  }, [filteredAccounts, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccount?.id || mustChangePassword || mfaSetupRequired) {
      setAccountDevices([]);
      setAccountMobileBindings([]);
      return;
    }
    loadAccountDevices(selectedAccount.id);
    loadAccountMobileBindings(selectedAccount.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id, isAuthenticated, mustChangePassword, mfaSetupRequired]);

  useEffect(() => {
    if (!selectedAccount) {
      setEditAccountForm({
        id: "",
        name: "",
        email: "",
        externalId: "",
        notes: "",
        isActive: true
      });
      return;
    }
    setEditAccountForm({
      id: selectedAccount.id,
      name: selectedAccount.name || "",
      email: selectedAccount.email || "",
      externalId: selectedAccount.external_id || "",
      notes: selectedAccount.notes || "",
      isActive: !!selectedAccount.is_active
    });
  }, [selectedAccount]);

  useEffect(() => {
    if (!profiles.length) {
      setSelectedProfileId("");
      return;
    }
    const selectedExists = profiles.some((item) => item.id === selectedProfileId);
    if (!selectedExists) {
      setSelectedProfileId(profiles[0].id);
    }
  }, [profiles, selectedProfileId]);

  useEffect(() => {
    if (!appsCatalog.length) {
      setSelectedAppId("");
      return;
    }
    const selectedExists = appsCatalog.some((item) => item.id === selectedAppId);
    if (!selectedExists) {
      setSelectedAppId(appsCatalog[0].id);
    }
  }, [appsCatalog, selectedAppId]);

  useEffect(() => {
    if (!selectedApp) {
      setAppForm({
        id: "",
        name: "",
        bundleId: "",
      platform: "ios",
      manifestUrl: "",
      itunesStoreId: "",
      description: "",
      assignmentType: "optional",
      updatePolicy: "manual",
      installPriority: "normal",
      uninstallProtected: false,
      perAppVpnUuid: "",
      managedConfigJSON: "{}",
      requestManagedFeedback: false,
      requestManagedAttributes: false,
      pipelineJSON: "{}"
    });
    return;
  }
    setAppForm({
      id: selectedApp.id,
      name: selectedApp.name || "",
      bundleId: selectedApp.bundle_id || "",
    platform: selectedApp.platform || "ios",
    manifestUrl: selectedApp.manifest_url || "",
    itunesStoreId: selectedApp.itunes_store_id || "",
    description: selectedApp.description || "",
    assignmentType: selectedApp.assignment_type || "optional",
    updatePolicy: selectedApp.update_policy || "manual",
    installPriority: selectedApp.install_priority || "normal",
    uninstallProtected: Boolean(selectedApp.uninstall_protected),
    perAppVpnUuid: selectedApp.per_app_vpn_uuid || "",
    managedConfigJSON: JSON.stringify(selectedApp.managed_config || {}, null, 2),
    requestManagedFeedback: Boolean(selectedApp.request_managed_feedback),
    requestManagedAttributes: Boolean(selectedApp.request_managed_attributes),
    pipelineJSON: JSON.stringify(selectedApp.in_house_pipeline || {}, null, 2)
  });
  }, [selectedApp]);

  useEffect(() => {
    const existing = new Set(appsCatalog.map((item) => String(item.id || "").trim()).filter(Boolean));
    setSelectedAppIDs((prev) => prev.filter((id) => existing.has(id)));
  }, [appsCatalog]);

  useEffect(() => {
    const existing = new Set(groups.map((item) => String(item.id || "").trim()).filter(Boolean));
    setSelectedGroupIDs((prev) => prev.filter((id) => existing.has(id)));
  }, [groups]);

  useEffect(() => {
    const valid = new Set(
      selectedDeviceProfiles
        .map((item) => String(item.profile_id || "").trim())
        .filter(Boolean)
    );
    setSelectedDeviceProfileIDs((prev) => prev.filter((id) => valid.has(id)));
  }, [selectedDeviceProfiles]);

  function clearSession() {
    localStorage.removeItem(STORAGE_SESSION);
    setSession(null);
    setDevices([]);
    setCommands([]);
    setAccounts([]);
    setAccountDevices([]);
    setGroups([]);
    setMobileNotifications([]);
    setDocuments([]);
    setDocumentFolders([]);
    setDocumentAckReport([]);
    setSelectedDocumentFolderID("all");
    setProfiles([]);
    setProfileDeployments([]);
    setAppsCatalog([]);
    setAppDeployments([]);
    setAuditLogs([]);
    setConsoleUsers([]);
    setMeta(null);
    setDeviceDetail(null);
    setDeviceApps([]);
    setDeviceAppDeployments([]);
    setDeviceProfilesReported([]);
    setDeviceLocations([]);
    setSelectedUdid("");
    setSelectedDeviceUDIDs([]);
    setSelectedAccountId("");
    setSelectedAccountIDs([]);
    setSelectedGroupIDs([]);
    setSelectedProfileId("");
    setSelectedProfileIDs([]);
    setSelectedAppId("");
    setSelectedAppIDs([]);
    setAppsSearch("");
    setShowAppsColumnsMenu(false);
    setShowAppsFilters(false);
    setVisibleDeviceColumns(DEVICE_DEFAULT_COLUMN_KEYS);
    setVisibleProfileColumns(PROFILE_DEFAULT_COLUMN_KEYS);
    setVisiblePolicyColumns(POLICY_DEFAULT_COLUMN_KEYS);
    setVisibleAppColumns(APP_DEFAULT_COLUMN_KEYS);
    setAppsSortKey("name");
    setAppsSortDirection("asc");
    setAppsPlatformFilter("all");
    setAppsTypeFilter("all");
    setShowAppStoreModal(false);
    setAppsStorePlatform("ios");
    setShowPlatformPickerModal(false);
    setPlatformPickerContext("");
    setAppsStoreQuery("");
    setAppsStoreCountry("US");
    setAppsStoreLoading(false);
    setAppsStoreResults([]);
    setInfoMessage("");
    setErrorMessage("");
    setSignInForm((prev) => ({ ...prev, password: "" }));
    setCreateAccountForm({ firstName: "", secondName: "", email: "", department: "" });
    setChangePasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setShowCreateProfileModal(false);
    setShowAssignAccountsGroupsModal(false);
    setShowAssignAppsGroupsModal(false);
    setShowAssignProfilesGroupsModal(false);
    setProfileDevicesModalItem(null);
    setProfileDevices([]);
    setProfileDevicesFilter("all");
    setShowDocumentModal(false);
    setShowDeviceActivationLockModal(false);
    setSelectedDocumentIDs([]);
    setDocumentsSearch("");
    setDocumentForm(createEmptyDocumentForm());
    setShowProfileViewModal(false);
    setShowRestrictionsAppPickerModal(false);
    setRestrictionsAppPickerContext({ target: "create", payloadIndex: -1 });
    setRestrictionsAppPickerQuery("");
    setRestrictionsAppPickerResults([]);
    setRestrictionsAppPickerLoading(false);
    setShowAirPlayDestinationModal(false);
    setAirPlayDestinationContext({ target: "create", payloadIndex: -1 });
    setAirPlayDestinationForm({ deviceName: "", password: "" });
    setShowEditUserModal(false);
    setProfilePreview(null);
    setProfileCreateForm(createEmptyProfileCreateForm("ios"));
    setProfileViewForm(createEmptyProfileViewForm("ios"));
    setProfileCreateRestrictionsTab("functionality");
    setProfileViewRestrictionsTab("functionality");
    setProfileCreateSectionPayloads(createProfileSectionPayloadsState());
    setProfileViewSectionPayloads(createProfileSectionPayloadsState());
    setAssignGroupsForm({ groupIds: [] });
    setAssignAppGroupsForm({ groupIds: [] });
    setEditConsoleUserForm({
      id: "",
      firstName: "",
      lastName: "",
      login: "",
      email: "",
      role: "read-only",
      mustChangePassword: false,
      mfaRequired: false,
      temporaryPassword: ""
    });
    setAppForm({
      id: "",
      name: "",
      bundleId: "",
      platform: "ios",
      manifestUrl: "",
      itunesStoreId: "",
      description: ""
    });
  }

  async function handleSignIn(event) {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");

    const apiBase = normalizeAPIBase(signInForm.apiBase.trim() || DEFAULT_API_BASE);
    const login = signInForm.login.trim();
    const password = signInForm.password;
    const mfaCode = String(signInForm.mfaCode || "").trim();
    if (!login || !password) {
      setErrorMessage(t("authRequired"));
      return;
    }

    setLoading(true);
    try {
      const out = await rawRequest(
        "/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ login, password, mfa_code: mfaCode })
        },
        { apiBase }
      );

      if (out?.mfa_required) {
        setSignInMFARequired(true);
        setInfoMessage(t("mfaCodeRequired"));
        return;
      }
      if (out?.passkey_required) {
        if (!window.PublicKeyCredential) {
          setErrorMessage(t("passkeyUnsupported"));
          return;
        }
        setInfoMessage(t("passkeyRequired"));
        const assertion = await navigator.credentials.get({
          publicKey: normalizePasskeyOptions(out.public_key)
        });
        const passkeyOut = await rawRequest(
          "/auth/login",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              login,
              password,
              passkey_challenge_id: out.passkey_challenge_id,
              passkey_assertion: publicKeyCredentialToJSON(assertion)
            })
          },
          { apiBase }
        );
        const nextSession = normalizeSession({
          apiBase,
          authMode: "cookie",
          csrfToken: passkeyOut.csrf_token || readCookie(CSRF_COOKIE_NAME),
          user: passkeyOut.user,
          expiresAt: passkeyOut.expires_at || ""
        });
        setSession(nextSession);
        setSignInMFARequired(false);
        setSignInForm((prev) => ({ ...prev, password: "", mfaCode: "" }));
        setActivePage("dashboard");
        if (passkeyOut?.must_change_password) {
          setInfoMessage(t("forcePasswordHint"));
        } else if (passkeyOut?.user?.mfa_setup_required) {
          setInfoMessage(t("mfaSetupRequiredHint"));
        } else {
          setInfoMessage("");
        }
        return;
      }

      const nextSession = normalizeSession({
        apiBase,
        authMode: "cookie",
        csrfToken: out.csrf_token || readCookie(CSRF_COOKIE_NAME),
        user: out.user,
        expiresAt: out.expires_at || ""
      });
      setSession(nextSession);
      setSignInMFARequired(false);
      setSignInForm((prev) => ({ ...prev, password: "", mfaCode: "" }));
      setActivePage("dashboard");
      if (out?.must_change_password) {
        setInfoMessage(t("forcePasswordHint"));
      } else if (out?.user?.mfa_setup_required) {
        setInfoMessage(t("mfaSetupRequiredHint"));
      }
    } catch (err) {
      if (err?.status === 403 && String(err.message || "").toLowerCase().includes("sso")) {
        setErrorMessage(t("ssoLocalLoginBlocked"));
      } else if (String(err?.message || "").toLowerCase().includes("passkey")) {
        setErrorMessage(`${t("passkeyLoginFailed")}: ${err.message}`);
      } else {
        setErrorMessage(`${t("loginFailed")}: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }

  function startSSOLogin() {
    const apiBase = normalizeAPIBase(signInForm.apiBase.trim() || DEFAULT_API_BASE);
    const redirect = encodeURIComponent(PAGE_TO_ROUTE[activePage || "dashboard"] || "/dashboard");
    window.location.href = `${apiBase}/auth/sso/start?redirect=${redirect}`;
  }

  async function handleChangePassword(event) {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");

    if (!changePasswordForm.currentPassword || !changePasswordForm.newPassword) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await request("/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: changePasswordForm.currentPassword,
          new_password: changePasswordForm.newPassword
        })
      });
      const me = await request("/auth/me");
      setSession((prev) =>
        prev
          ? {
              ...prev,
              user: me.user || prev.user,
              csrfToken: me.csrf_token || prev.csrfToken || readCookie(CSRF_COOKIE_NAME),
              expiresAt: me.expires_at || prev.expiresAt
            }
          : prev
      );
      setChangePasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setInfoMessage(t("passwordChanged"));
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("changePasswordFailed")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function changeConsoleUserPassword(event) {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");

    if (!securityAdminPasswordForm.consoleUserId) {
      setErrorMessage(t("securitySelectAnotherUser"));
      return;
    }
    if (
      !securityAdminPasswordForm.newPassword ||
      securityAdminPasswordForm.newPassword !== securityAdminPasswordForm.confirmPassword
    ) {
      setErrorMessage(
        securityAdminPasswordForm.newPassword
          ? t("passwordsDoNotMatch")
          : t("requiredField")
      );
      return;
    }

    setLoading(true);
    try {
      await request("/security/users/password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          console_user_id: securityAdminPasswordForm.consoleUserId,
          new_password: securityAdminPasswordForm.newPassword,
          must_change_password: securityAdminPasswordForm.mustChangePassword
        })
      });
      setSecurityAdminPasswordForm((prev) => ({
        ...prev,
        newPassword: "",
        confirmPassword: ""
      }));
      setInfoMessage(t("securityPasswordChanged"));
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("securityPasswordChangeFailed")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function updateConsoleUserMFARequirement(event) {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");
    if (!securityAdminMFAForm.consoleUserId) {
      setErrorMessage(t("securitySelectUser"));
      return;
    }

    setLoading(true);
    try {
      await request("/security/users/mfa-required", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          console_user_id: securityAdminMFAForm.consoleUserId,
          required: securityAdminMFAForm.required
        })
      });
      setInfoMessage(t("securityMFAUpdated"));
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("securityMFAUpdateFailed")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function unlockConsoleUser(consoleUserId) {
    if (!canEditSecurity || String(session?.user?.role || "").toLowerCase() !== "master-admin") {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    setLoading(true);
    try {
      await request("/security/users/unlock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ console_user_id: consoleUserId })
      });
      setInfoMessage(lang === "ru" ? "Администратор разблокирован" : "Administrator unlocked");
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function forceConsoleUserPasswordReset(consoleUserId) {
    if (!canEditSecurity) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setInfoMessage("");
    setLoading(true);
    try {
      await request("/security/users/force-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          console_user_id: consoleUserId,
          reason: "forced by administrator"
        })
      });
      setInfoMessage(lang === "ru" ? "Смена пароля запрошена" : "Password reset required");
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function startMFASetup(event) {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");
    if (!mfaSetupForm.currentPassword) {
      setErrorMessage(t("currentPasswordRequired"));
      return;
    }
    setLoading(true);
    try {
      const out = await request("/auth/mfa/setup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: mfaSetupForm.currentPassword })
      });
      setMFASetupForm((prev) => ({
        ...prev,
        secret: out?.secret || "",
        otpauthURL: out?.otpauth_url || "",
        qrSVG: out?.qr_svg || ""
      }));
      setInfoMessage(t("mfaSetupSecretReady"));
    } catch (err) {
      setErrorMessage(`${t("mfaSetupFailed")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function enableMFA(event) {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");
    if (!mfaSetupForm.code.trim()) {
      setErrorMessage(t("mfaCodeRequired"));
      return;
    }
    setLoading(true);
    try {
      const out = await request("/auth/mfa/enable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: mfaSetupForm.code.trim() })
      });
      setSession((prev) =>
        prev
          ? {
              ...prev,
              user: out?.user || { ...(prev.user || {}), mfa_enabled: true, mfa_setup_required: false }
            }
          : prev
      );
      setMFASetupForm({ currentPassword: "", code: "", secret: "", otpauthURL: "", qrSVG: "" });
      setInfoMessage(t("mfaEnabled"));
    } catch (err) {
      setErrorMessage(`${t("mfaEnableFailed")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function registerPasskey(event) {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");
    if (!window.PublicKeyCredential) {
      setErrorMessage(t("passkeyUnsupported"));
      return;
    }
    if (!passkeySetupForm.currentPassword) {
      setErrorMessage(t("currentPasswordRequired"));
      return;
    }
    setLoading(true);
    try {
      const start = await request("/auth/passkeys/register/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: passkeySetupForm.currentPassword,
          name: passkeySetupForm.name
        })
      });
      const credential = await navigator.credentials.create({
        publicKey: normalizePasskeyOptions(start.public_key)
      });
      const finish = await request("/auth/passkeys/register/finish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challenge_id: start.challenge_id,
          name: passkeySetupForm.name,
          credential: publicKeyCredentialToJSON(credential)
        })
      });
      setSession((prev) =>
        prev
          ? {
              ...prev,
              user: finish?.user || {
                ...(prev.user || {}),
                passkey_enabled: true,
                mfa_setup_required: false
              }
            }
          : prev
      );
      setPasskeySetupForm({ currentPassword: "", name: "" });
      setInfoMessage(t("passkeyRegistered"));
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("passkeyRegisterFailed")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    if (isAuthenticated) {
      try {
        await request("/auth/logout", { method: "POST" });
      } catch {
        // ignore
      }
    }
    clearSession();
  }

  async function queueCommand(udid, endpoint, okText, failText) {
    if (!udid) return;
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udid })
      });
      setInfoMessage(`${okText}: ${out.command_uuid || "-"}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${failText}: ${err.message}`);
    }
  }

  async function queueSharedIPadCommand(endpoint, body, okText = t("deviceDetailsSharedIPadQueued")) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      setInfoMessage(`${okText}: ${out.command_uuid || "-"}`);
      await loadDeviceTelemetry(body.udid);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsSharedIPadFailed")}: ${err.message}`);
    }
  }

  async function saveSharedIPadSettings() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request("/settings/shared-ipad", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sharedIPadSettingsDraft)
      });
      setSharedIPadSettings(out || {});
      setInfoMessage(t("deviceDetailsSharedIPadSettingsSaved"));
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsSharedIPadFailed")}: ${err.message}`);
    }
  }

  async function queueActivationLockAction(endpoint, okText) {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udid })
      });
      setInfoMessage(`${okText}: ${out.command_uuid || "-"}`);
      await loadDeviceTelemetry(udid);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsActivationLockFailed")}: ${err.message}`);
    }
  }

  function openEnableLostModeModal(
    udidValue = routeDeviceUDID || selectedUdid,
    { requestLocation = false } = {}
  ) {
    const udid = String(udidValue || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setErrorMessage("");
    setShowDeviceCommandsMenu(false);
    setEnableLostModeForm({
      message: "",
      phoneNumber: ""
    });
    setEnableLostModeRequestLocation(Boolean(requestLocation));
    setShowEnableLostModeModal(true);
  }

  function closeEnableLostModeModal() {
    setShowEnableLostModeModal(false);
    setEnableLostModeRequestLocation(false);
    setEnableLostModeForm({
      message: "",
      phoneNumber: ""
    });
  }

  async function submitEnableLostMode() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }

    const message = String(enableLostModeForm.message || "").trim();
    const phoneNumber = String(enableLostModeForm.phoneNumber || "").trim();
    if (!message) {
      setErrorMessage(t("deviceDetailsLostModeMessageRequired"));
      return;
    }
    if (!phoneNumber) {
      setErrorMessage(t("deviceDetailsLostModePhoneRequired"));
      return;
    }

    setErrorMessage("");
    try {
      const out = await request("/commands/lost-mode/enable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid,
          message,
          phone_number: phoneNumber
        })
      });
      setInfoMessage(
        `${
          enableLostModeRequestLocation
            ? t("deviceDetailsLocationLostModeQueued")
            : t("deviceDetailsTurnOnLostModeQueued")
        }: ${out.command_uuid || "-"}`
      );
      closeEnableLostModeModal();
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsTurnOnLostModeFailed")}: ${err.message}`);
    }
  }

  async function requestDeviceLocation() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }

    const device =
      deviceDetail ||
      selectedDevice ||
      devices.find((item) => String(item?.udid || "").trim() === udid) ||
      null;
    const unavailableReason = deviceLocationUnavailableReason(device, t);
    if (unavailableReason) {
      setErrorMessage(unavailableReason);
      return;
    }

    await queueCommand(
      udid,
      "/commands/device-location",
      isDeviceLostModeEnabled
        ? t("deviceDetailsLocationRequestQueued")
        : t("deviceDetailsLocationLostModeQueued"),
      t("deviceDetailsLocationRequestFailed")
    );
  }

  function openScheduleOSUpdateModal(installAction, targetUDIDs = []) {
    const requestedTargets =
      Array.isArray(targetUDIDs) && targetUDIDs.length > 0
        ? targetUDIDs
        : [routeDeviceUDID || selectedUdid];
    const targets = Array.from(
      new Set(requestedTargets.map((value) => String(value || "").trim()).filter(Boolean))
    );
    if (targets.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setShowDeviceCommandsMenu(false);
    setShowBulkDeviceCommandsMenu(false);
    setScheduleOSUpdateTargetUDIDs(targets);
    setScheduleOSUpdateForm({
      installAction,
      productKey: "",
      productVersion: ""
    });
    setShowScheduleOSUpdateModal(true);
  }

  function closeScheduleOSUpdateModal() {
    setShowScheduleOSUpdateModal(false);
    setScheduleOSUpdateForm({
      installAction: "DownloadOnly",
      productKey: "",
      productVersion: ""
    });
    setScheduleOSUpdateTargetUDIDs([]);
  }

  function openBulkScheduleOSUpdateModal(installAction) {
    const targets = selectedDeviceUDIDs
      .map((udid) => devices.find((item) => String(item?.udid || "").trim() === String(udid || "").trim()))
      .filter(Boolean);
    if (targets.length !== selectedDeviceUDIDs.length || targets.some((item) => {
      const platform = normalizePlatform(item);
      return platform !== "ios" && platform !== "macos";
    })) {
      setErrorMessage(t("devicesBulkOSUpdateAppleOnly"));
      setShowBulkDeviceCommandsMenu(false);
      return;
    }
    openScheduleOSUpdateModal(installAction, selectedDeviceUDIDs);
  }

  async function submitScheduleOSUpdate() {
    const targets =
      scheduleOSUpdateTargetUDIDs.length > 0
        ? scheduleOSUpdateTargetUDIDs
        : [String(routeDeviceUDID || selectedUdid || "").trim()].filter(Boolean);
    if (targets.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }

    const installAction = String(scheduleOSUpdateForm.installAction || "").trim();
    const productKey = String(scheduleOSUpdateForm.productKey || "").trim();
    const productVersion = String(scheduleOSUpdateForm.productVersion || "").trim();

    if (hasActiveDDMSoftwareUpdatePolicy(ddmDeclarations)) {
      setErrorMessage(
        lang === "ru"
          ? "Активная DDM Software Update policy имеет приоритет. Отключите DDM policy перед отправкой обычной команды ScheduleOSUpdate."
          : "Active DDM Software Update policy has precedence. Disable the DDM policy before sending a regular ScheduleOSUpdate command."
      );
      return;
    }

    setErrorMessage("");
    try {
      const out = await request("/commands/schedule-os-update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(targets.length === 1 ? { udid: targets[0] } : { udids: targets }),
          install_action: installAction,
          product_key: productKey,
          product_version: productVersion
        })
      });
      const queued = Number(out?.summary?.queued ?? (out?.command_uuid ? 1 : 0));
      const failed = Number(out?.summary?.failed ?? 0);
      setInfoMessage(`${t("deviceDetailsScheduleOSUpdateQueued")}: ${queued}`);
      if (failed > 0) {
        const details = Array.isArray(out?.errors)
          ? out.errors
              .slice(0, 3)
              .map((item) => [item?.udid, item?.error].filter(Boolean).join(": "))
              .filter(Boolean)
              .join("; ")
          : "";
        setErrorMessage(
          details
            ? `${t("deviceDetailsScheduleOSUpdateFailed")}: ${details}`
            : `${t("deviceDetailsScheduleOSUpdateFailed")}: ${failed}`
        );
      }
      closeScheduleOSUpdateModal();
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsScheduleOSUpdateFailed")}: ${err.message}`);
    }
  }

  function openAppleCommandModal(command) {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid || !command) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    setShowDeviceCommandsMenu(false);
    if (!command.destructive) {
      queueAppleCommand(command, command.example || {});
      return;
    }
    setAppleCommandForm({
      command,
      parametersJSON: JSON.stringify(command.example || {}, null, 2)
    });
    setShowAppleCommandModal(true);
  }

  function closeAppleCommandModal() {
    setShowAppleCommandModal(false);
    setAppleCommandForm({
      command: null,
      parametersJSON: "{}"
    });
  }

  function openDeviceSettingModal(settingType = "device_name") {
    setShowDeviceCommandsMenu(false);
    setErrorMessage("");
    setDeviceSettingForm((prev) => ({
      ...prev,
      settingType,
      value: settingType === "device_name" ? String(deviceDetail?.device?.device_name || deviceDetail?.device_name || "") : prev.value,
      enabled: true
    }));
    setShowDeviceSettingModal(true);
  }

  function closeDeviceSettingModal() {
    setShowDeviceSettingModal(false);
  }

  async function queueAppleCommand(command, parameters = {}) {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid || !command) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }

    setErrorMessage("");
    try {
      const out = await request("/commands/apple", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid,
          command: command.key,
          parameters: parameters || {}
        })
      });
      setInfoMessage(
        `${t("deviceDetailsAppleCommandQueued")}: ${out.request_type || command.key}, ${out.command_uuid || "-"}`
      );
      closeAppleCommandModal();
      await loadDeviceTelemetry(udid);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsAppleCommandFailed")}: ${err.message}`);
    }
  }

  async function loadMacOSUEM(udidValue = routeDeviceUDID || selectedUdid) {
    const udid = String(udidValue || "").trim();
    if (!udid || !isAuthenticated || mustChangePassword || mfaSetupRequired) return;
    setMacOSUEMLoading(true);
    try {
      const [summary, tokens, onboarding] = await Promise.all([
        request(`/macos/uem/summary?udid=${encodeURIComponent(udid)}`),
        request(`/macos/uem/agent-tokens?udid=${encodeURIComponent(udid)}`),
        request(`/macos/uem/onboarding?udid=${encodeURIComponent(udid)}`)
      ]);
      setMacOSUEMSummary(summary || null);
      if (summary?.filevault_policy) {
        setMacOSFileVaultForm({
          enabled: Boolean(summary.filevault_policy.enabled),
          escrowRequired: Boolean(summary.filevault_policy.escrow_required),
          staleAfterDays: String(summary.filevault_policy.stale_after_days || 180)
        });
      }
      setMacOSAgentTokens(Array.isArray(tokens?.items) ? tokens.items : []);
      setMacOSAgentOnboardingStatus(onboarding || null);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось загрузить macOS UEM" : "Failed to load macOS UEM"}: ${err.message}`);
    } finally {
      setMacOSUEMLoading(false);
    }
  }

  function macOSAgentStatusLabel(status) {
    switch (String(status || "")) {
      case "installed":
        return lang === "ru" ? "Agent установлен" : "Agent installed";
      case "token_issued":
        return lang === "ru" ? "Token активирован" : "Token activated";
      case "installing":
        return lang === "ru" ? "Установка отправлена" : "Install queued";
      case "not_installed":
      default:
        return lang === "ru" ? "Agent не установлен" : "Agent not installed";
    }
  }

  async function startMacOSAgentOnboarding() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setErrorMessage("");
    try {
      await request("/macos/uem/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udid })
      });
      setInfoMessage(lang === "ru" ? "Установка Turuk macOS Agent отправлена через MDM" : "Turuk macOS Agent install was queued through MDM");
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось отправить установку Agent" : "Failed to queue Agent install"}: ${err.message}`);
    }
  }

  async function createMacOSAgentToken() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request("/macos/uem/agent-tokens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid,
          name: macOSAgentTokenName || (lang === "ru" ? "macOS Agent" : "macOS Agent")
        })
      });
      setIssuedMacOSAgentToken(out?.plain_token || "");
      setMacOSAgentTokenName("");
      setInfoMessage(lang === "ru" ? "Agent token создан. Скопируйте его сейчас." : "Agent token created. Copy it now.");
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось создать Agent token" : "Failed to create Agent token"}: ${err.message}`);
    }
  }

  async function revokeMacOSAgentToken(id) {
    if (!id) return;
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    setErrorMessage("");
    try {
      await request("/macos/uem/agent-tokens/revoke", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
      setIssuedMacOSAgentToken(null);
      setInfoMessage(lang === "ru" ? "Agent token отозван" : "Agent token revoked");
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось отозвать Agent token" : "Failed to revoke Agent token"}: ${err.message}`);
    }
  }

  function buildMacOSJobPayload(jobType) {
    switch (jobType) {
      case "script":
        return {
          shell: "/bin/zsh",
          script: String(macOSJobForm.script || ""),
          timeout_seconds: 1800
        };
      case "package":
        return {
          package_url: String(macOSJobForm.packageURL || "").trim(),
          sha256: String(macOSJobForm.packageSHA256 || "").trim(),
          installer: "installer"
        };
      case "agent_uninstall":
        return {
          delete_config: false
        };
      case "local_admin_create":
        return {
          username: String(macOSJobForm.adminUsername || "").trim(),
          full_name: String(macOSJobForm.adminFullName || "").trim(),
          temporary_password: String(macOSJobForm.adminPassword || ""),
          hidden: true,
          require_password_rotation: true
        };
      case "local_admin_rotate":
        return {
          username: String(macOSJobForm.adminUsername || "").trim(),
          new_password: String(macOSJobForm.rotatePassword || "")
        };
      case "local_admin_revoke":
        return {
          username: String(macOSJobForm.adminUsername || "").trim(),
          delete_home: false
        };
      case "filevault_rotate":
        return {
          escrow_after_rotation: true
        };
      case "compliance_collect":
        return {
          collect: ["filevault", "firewall", "profiles", "pppc", "local_admins"]
        };
      default:
        return {};
    }
  }

  async function createMacOSAgentJob(jobType = macOSJobForm.jobType) {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    const payload = buildMacOSJobPayload(jobType);
    const title = String(macOSJobForm.title || macOSAgentJobTypeLabel(jobType, lang)).trim();
    setErrorMessage("");
    try {
      await request("/macos/uem/jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udid, job_type: jobType, title, payload })
      });
      setInfoMessage(lang === "ru" ? "macOS Agent job создан" : "macOS Agent job created");
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось создать macOS Agent job" : "Failed to create macOS Agent job"}: ${err.message}`);
    }
  }

  async function saveMacOSLibraryItem() {
    const itemType = String(macOSLibraryForm.itemType || "script").trim();
    const name = String(macOSLibraryForm.name || "").trim();
    if (!name) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setErrorMessage("");
    try {
      await request("/macos/uem/library", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_type: itemType,
          name,
          shell: macOSLibraryForm.shell || "/bin/zsh",
          script: macOSLibraryForm.script,
          package_url: macOSLibraryForm.packageURL,
          sha256: macOSLibraryForm.sha256,
          install_args: macOSLibraryForm.installArgs,
          timeout_seconds: Number(macOSLibraryForm.timeoutSeconds || 1800),
          retry_max_attempts: Number(macOSLibraryForm.retryMaxAttempts || 3)
        })
      });
      setInfoMessage(lang === "ru" ? "Элемент библиотеки сохранен" : "Library item saved");
      await loadMacOSUEM(routeDeviceUDID);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось сохранить элемент библиотеки" : "Failed to save library item"}: ${err.message}`);
    }
  }

  async function assignMacOSLibraryItem(item) {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!item?.id || !item?.item_type) return;
    const target = String(macOSLibraryForm.target || "device");
    const groupID = target === "group" ? String(macOSLibraryForm.groupID || "").trim() : "";
    if (target === "group" && !groupID) {
      setErrorMessage(t("requiredField"));
      return;
    }
    const scheduledAt = macOSLibraryForm.scheduledAt ? new Date(macOSLibraryForm.scheduledAt).toISOString() : "";
    setErrorMessage("");
    try {
      await request("/macos/uem/library/assign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_type: item.item_type,
          item_id: item.id,
          udid: target === "device" ? udid : "",
          group_id: groupID,
          scheduled_at: scheduledAt,
          retry_max_attempts: Number(macOSLibraryForm.retryMaxAttempts || item.retry_max_attempts || 3)
        })
      });
      setInfoMessage(lang === "ru" ? "Назначение создано" : "Assignment created");
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось назначить элемент библиотеки" : "Failed to assign library item"}: ${err.message}`);
    }
  }

  async function saveMacOSFileVaultPolicy() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setErrorMessage("");
    try {
      await request("/macos/uem/filevault/policy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid,
          enabled: Boolean(macOSFileVaultForm.enabled),
          escrow_required: Boolean(macOSFileVaultForm.escrowRequired),
          stale_after_days: Number(macOSFileVaultForm.staleAfterDays || 180)
        })
      });
      setInfoMessage(lang === "ru" ? "FileVault profile отправлен на устройство" : "FileVault profile was queued for the device");
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось отправить FileVault profile" : "Failed to queue FileVault profile"}: ${err.message}`);
    }
  }

  async function queueMacOSAgentPackageJob(action = "update") {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    const packageURL = String(macOSAgentOnboardingStatus?.package_install_url || "").trim();
    if (!udid || !packageURL) {
      setErrorMessage(lang === "ru" ? "Пакет Turuk macOS Agent недоступен" : "Turuk macOS Agent package is unavailable");
      return;
    }
    setErrorMessage("");
    try {
      await request("/macos/uem/jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid,
          job_type: "package",
          title: action === "reinstall"
            ? (lang === "ru" ? "Переустановить Turuk macOS Agent" : "Reinstall Turuk macOS Agent")
            : (lang === "ru" ? "Обновить Turuk macOS Agent" : "Update Turuk macOS Agent"),
          payload: {
            package_url: packageURL,
            sha256: String(macOSAgentOnboardingStatus?.package?.sha256 || macOSAgentOnboardingStatus?.package_sha256 || "").trim(),
            installer: "installer"
          }
        })
      });
      setInfoMessage(action === "reinstall"
        ? (lang === "ru" ? "Переустановка Agent добавлена в очередь" : "Agent reinstall was queued")
        : (lang === "ru" ? "Обновление Agent добавлено в очередь" : "Agent update was queued"));
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось создать job для Agent" : "Failed to queue Agent job"}: ${err.message}`);
    }
  }

  async function queueMacOSAgentUninstall() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setErrorMessage("");
    try {
      await request("/macos/uem/jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid,
          job_type: "agent_uninstall",
          title: lang === "ru" ? "Удалить Turuk macOS Agent" : "Uninstall Turuk macOS Agent",
          payload: { delete_config: false }
        })
      });
      setInfoMessage(lang === "ru" ? "Удаление Agent добавлено в очередь" : "Agent uninstall was queued");
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось создать job удаления Agent" : "Failed to queue Agent uninstall"}: ${err.message}`);
    }
  }

  async function evaluateMacOSCompliance() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) return;
    setErrorMessage("");
    try {
      await request("/macos/uem/compliance/evaluate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udid })
      });
      setInfoMessage(lang === "ru" ? "macOS compliance обновлен" : "macOS compliance updated");
      await loadMacOSUEM(udid);
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось проверить macOS compliance" : "Failed to evaluate macOS compliance"}: ${err.message}`);
    }
  }

  async function queueDeviceWorkflow(endpoint, body, successText, failureText) {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udid, ...(body || {}) })
      });
      setInfoMessage(`${successText}: ${out.request_type || "DeviceInformation"}, ${out.command_uuid || "-"}`);
      await loadDeviceTelemetry(udid);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${failureText}: ${err.message}`);
    }
  }

  function installManagedBook() {
    const source = String(deviceMediaForm.source || "book_store");
    queueDeviceWorkflow(
      "/commands/media/install",
      {
        source,
        adam_id: deviceMediaForm.adamId,
        media_url: deviceMediaForm.mediaURL,
        persistent_id: deviceMediaForm.persistentID,
        kind: deviceMediaForm.kind,
        title: deviceMediaForm.title,
        author: deviceMediaForm.author,
        version: deviceMediaForm.version
      },
      lang === "ru" ? "Установка книги поставлена в очередь" : "Book install queued",
      lang === "ru" ? "Не удалось поставить установку книги в очередь" : "Failed to queue book install"
    );
  }

  function removeManagedBook() {
    queueDeviceWorkflow(
      "/commands/media/remove",
      {
        adam_id: deviceMediaForm.adamId,
        persistent_id: deviceMediaForm.persistentID
      },
      lang === "ru" ? "Удаление книги поставлено в очередь" : "Book removal queued",
      lang === "ru" ? "Не удалось поставить удаление книги в очередь" : "Failed to queue book removal"
    );
  }

  function refreshManagedBooks() {
    queueDeviceWorkflow(
      "/commands/media/list",
      {},
      lang === "ru" ? "Запрос списка книг поставлен в очередь" : "Managed media list queued",
      lang === "ru" ? "Не удалось запросить список книг" : "Failed to queue managed media list"
    );
  }

  async function configureManagedSetting() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request("/commands/settings/configure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid,
          setting_type: deviceSettingForm.settingType,
          value: deviceSettingForm.value,
          enabled: Boolean(deviceSettingForm.enabled)
        })
      });
      setInfoMessage(
        `${lang === "ru" ? "Настройка поставлена в очередь" : "Setting queued"}: ${out.request_type || "Settings"}, ${out.command_uuid || "-"}`
      );
      closeDeviceSettingModal();
      await loadDeviceTelemetry(udid);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${lang === "ru" ? "Не удалось отправить настройку" : "Failed to queue setting"}: ${err.message}`);
    }
  }

  function requestDeviceAttestation() {
    queueDeviceWorkflow(
      "/commands/device-attestation",
      {},
      lang === "ru" ? "Запрос аттестации поставлен в очередь" : "Device attestation queued",
      lang === "ru" ? "Не удалось запросить аттестацию" : "Failed to queue device attestation"
    );
  }

  function requestDeviceCertificates() {
    queueAppleCommand(
      { key: "certificate_list" },
      {}
    );
  }

  async function submitAppleCommand() {
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    const command = appleCommandForm.command;
    if (!udid || !command) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }

    let parameters;
    try {
      parameters = JSON.parse(String(appleCommandForm.parametersJSON || "{}"));
    } catch {
      setErrorMessage(t("deviceDetailsAppleCommandInvalidJSON"));
      return;
    }
    if (!parameters || Array.isArray(parameters) || typeof parameters !== "object") {
      setErrorMessage(t("deviceDetailsAppleCommandInvalidJSON"));
      return;
    }

    await queueAppleCommand(command, parameters);
  }

  function closeAccountModal() {
    setShowCreateAccountModal(false);
    setAccountModalMode("create");
    setCreateAccountForm(createEmptyAccountEditorForm());
  }

  function openCreateAccountModal() {
    setAccountModalMode("create");
    setCreateAccountForm(createEmptyAccountEditorForm());
    setShowCreateAccountModal(true);
  }

  function closeAccountImportModal() {
    if (accountImportInFlight) return;
    setShowAccountImportModal(false);
    setAccountImportFileName("");
    setAccountImportRows([]);
    setAccountImportErrors([]);
  }

  function openAccountImportModal() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setAccountImportFileName("");
    setAccountImportRows([]);
    setAccountImportErrors([]);
    setShowAccountImportModal(true);
  }

  async function handleAccountImportFile(event) {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;

    const lowerName = String(file.name || "").toLowerCase();
    if (lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls")) {
      setAccountImportFileName(file.name || "");
      setAccountImportRows([]);
      setAccountImportErrors([t("accountsImportExcelUnsupported")]);
      return;
    }

    try {
      const text = await file.text();
      const rows = parseAccountImportText(text);
      const validationErrors = [];
      rows.forEach((row) => {
        if (!row.accountName) validationErrors.push(`${t("row")} ${row.rowNumber}: ${t("accountsName")} ${t("requiredField")}`);
        if (!row.email) validationErrors.push(`${t("row")} ${row.rowNumber}: ${t("accountsEmail")} ${t("requiredField")}`);
        if (!row.password || row.password.length < 6) {
          validationErrors.push(`${t("row")} ${row.rowNumber}: ${t("accountsPassword")} min 6`);
        }
      });
      setAccountImportFileName(file.name || "");
      setAccountImportRows(rows);
      setAccountImportErrors(validationErrors);
    } catch (err) {
      setAccountImportFileName(file.name || "");
      setAccountImportRows([]);
      setAccountImportErrors([err.message || t("accountsImportParseFailed")]);
    }
  }

  async function submitAccountImport() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (accountImportRows.length === 0) {
      setAccountImportErrors([t("accountsImportEmpty")]);
      return;
    }
    if (accountImportErrors.length > 0) return;

    setAccountImportInFlight(true);
    const failures = [];
    let created = 0;
    try {
      for (const row of accountImportRows) {
        const notesPayload = buildAccountNotesPayload({
          firstName: row.firstName,
          secondName: row.secondName,
          department: row.department
        });
        try {
          await request("/accounts", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: row.accountName,
              email: row.email,
              external_id: row.department,
              notes: notesPayload,
              password: row.password
            })
          });
          created += 1;
        } catch (err) {
          failures.push(`${t("row")} ${row.rowNumber}: ${err.message || t("accountsCreateFailed")}`);
        }
      }

      if (failures.length > 0) {
        setAccountImportErrors(failures);
        setErrorMessage(`${t("accountsImportFailed")}: ${failures.length}`);
      } else {
        setInfoMessage(`${t("accountsImportDone")}: ${created}`);
        setShowAccountImportModal(false);
        setAccountImportFileName("");
        setAccountImportRows([]);
        setAccountImportErrors([]);
      }
      await loadAllData();
    } finally {
      setAccountImportInFlight(false);
    }
  }

  function openEditAccountModal(account) {
    if (!account) return;
    setSelectedAccountId(account.id || "");
    setAccountModalMode("edit");
    setCreateAccountForm(buildAccountEditorForm(account));
    setShowCreateAccountModal(true);
  }

  async function submitAccountModal() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!createAccountForm.email.trim()) {
      setErrorMessage(`${t("accountsEmail")} ${t("requiredField")}`);
      return;
    }
    if (!createAccountForm.accountName.trim()) {
      setErrorMessage(`${t("accountsName")} ${t("requiredField")}`);
      return;
    }
    if (accountModalMode === "create" && !String(createAccountForm.password || "").trim()) {
      setErrorMessage(`${t("accountsPassword")} ${t("requiredField")}`);
      return;
    }

    const firstName = createAccountForm.firstName.trim();
    const secondName = createAccountForm.secondName.trim();
    const email = createAccountForm.email.trim();
    const department = createAccountForm.department.trim();
    const accountName = createAccountForm.accountName.trim();
    const notesPayload = buildAccountNotesPayload({ firstName, secondName, department });
    const payload = {
      name: accountName,
      email,
      external_id: department,
      notes: notesPayload,
      password: String(createAccountForm.password || "").trim()
    };

    try {
      const out = await request(accountModalMode === "edit" ? "/accounts/update" : "/accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          accountModalMode === "edit"
            ? {
                id: createAccountForm.id,
                ...payload,
                is_active: !!createAccountForm.isActive
              }
            : payload
        )
      });
      const savedAccount = out?.item || null;
      setInfoMessage(
        accountModalMode === "edit"
          ? `Account updated: ${savedAccount?.email || email}`
          : `Account created: ${savedAccount?.email || email}`
      );
      closeAccountModal();
      await loadAllData();
      if (savedAccount?.id) {
        setSelectedAccountId(savedAccount.id);
      }
    } catch (err) {
      setErrorMessage(
        `${accountModalMode === "edit" ? t("accountsUpdateFailed") : t("accountsCreateFailed")}: ${err.message}`
      );
    }
  }

  async function assignDeviceToAccount() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedAccount?.id || !accountAssignUdid.trim()) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      await request("/accounts/assign-device", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: selectedAccount.id,
          udid: accountAssignUdid.trim()
        })
      });
      setInfoMessage(`Assigned: ${accountAssignUdid.trim()}`);
      setAccountAssignUdid("");
      await loadAllData();
      await loadAccountDevices(selectedAccount.id);
    } catch (err) {
      setErrorMessage(`${t("accountsAssignFailed")}: ${err.message}`);
    }
  }

  async function unassignDeviceFromAccount(udid = accountAssignUdid.trim()) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      await request("/accounts/unassign-device", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: selectedAccount?.id || "",
          udid
        })
      });
      setInfoMessage(`Unassigned: ${udid}`);
      setAccountAssignUdid("");
      await loadAllData();
      if (selectedAccount?.id) {
        await loadAccountDevices(selectedAccount.id);
      }
    } catch (err) {
      setErrorMessage(`${t("accountsUnassignFailed")}: ${err.message}`);
    }
  }

  async function unbindMobileDeviceFromAccount(bindingID, deviceID = "") {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const normalizedBindingID = String(bindingID || "").trim();
    const normalizedDeviceID = String(deviceID || "").trim();
    if (!normalizedBindingID && !normalizedDeviceID) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      await request("/accounts/mobile-bindings/unbind", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: selectedAccount?.id || "",
          binding_id: normalizedBindingID,
          device_id: normalizedDeviceID
        })
      });
      setInfoMessage(`${t("accountsMobileUnbindDone")}: ${normalizedDeviceID || normalizedBindingID}`);
      await loadAllData();
      if (selectedAccount?.id) {
        await loadAccountMobileBindings(selectedAccount.id);
      }
    } catch (err) {
      setErrorMessage(`${t("accountsMobileUnbindFailed")}: ${err.message}`);
    }
  }

  async function updateAccount() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!editAccountForm.id || !editAccountForm.name.trim()) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      await request("/accounts/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: editAccountForm.id,
          name: editAccountForm.name.trim(),
          email: editAccountForm.email.trim(),
          external_id: editAccountForm.externalId.trim(),
          notes: editAccountForm.notes.trim(),
          is_active: editAccountForm.isActive
        })
      });
      setInfoMessage(`Account updated: ${editAccountForm.name}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("accountsUpdateFailed")}: ${err.message}`);
    }
  }

  async function setAccountStatusAction(isActive) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedAccount?.id) return;
    try {
      await request("/accounts/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedAccount.id,
          is_active: isActive
        })
      });
      setInfoMessage(`Account status: ${isActive ? t("active") : t("revoked")}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("accountsUpdateFailed")}: ${err.message}`);
    }
  }

  async function deleteAccount() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedAccount?.id) return;
    try {
      await request("/accounts/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedAccount.id })
      });
      setInfoMessage(`Account deleted: ${selectedAccount.name}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("accountsDeleteFailed")}: ${err.message}`);
    }
  }

  async function deleteSelectedAccounts(ids = selectedAccountIDs) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const uniqueIDs = Array.from(new Set((ids || []).filter(Boolean)));
    if (uniqueIDs.length === 0) return;
    if (!window.confirm(t("accountsDeleteSelectedConfirm"))) return;

    try {
      const settled = await Promise.allSettled(
        uniqueIDs.map((id) =>
          request("/accounts/delete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id })
          })
        )
      );
      const successCount = settled.filter((item) => item.status === "fulfilled").length;
      const failedCount = settled.length - successCount;
      setSelectedAccountIDs((prev) => prev.filter((id) => !uniqueIDs.includes(id)));
      if (uniqueIDs.includes(selectedAccountId)) {
        setSelectedAccountId("");
        closeAccountModal();
      }
      if (failedCount > 0) {
        setErrorMessage(`${t("accountsDeleteSelectedFailed")}: ${failedCount}`);
      } else {
        setInfoMessage(`${t("accountsDeleteSelected")}: ${successCount}`);
      }
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("accountsDeleteSelectedFailed")}: ${err.message}`);
    }
  }

  async function createGroup() {
    if (!hasPermission("groups", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const name = createGroupForm.name.trim();
    if (!name) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      await request("/groups", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name })
      });
      setCreateGroupForm({ name: "" });
      setShowCreateGroupModal(false);
      setInfoMessage(`${t("groupsTitle")}: ${name}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("groupsCreateFailed")}: ${err.message}`);
    }
  }

  function toggleProfileGroup(setter, groupID) {
    setter((prev) => {
      const has = prev.groupIds.includes(groupID);
      return {
        ...prev,
        groupIds: has ? prev.groupIds.filter((id) => id !== groupID) : [...prev.groupIds, groupID]
      };
    });
  }

  function sectionPayloadCount(sectionPayloads, sectionKey) {
    if (sectionKey === "wifi") return 0;
    if (!sectionPayloads || typeof sectionPayloads !== "object") return 0;
    const items = sectionPayloads[sectionKey];
    return Array.isArray(items) ? items.length : 0;
  }

  function profileSectionDisplayTitle(sectionKey, fallbackLabel = "") {
    const key = PROFILE_BUILDER_SECTION_TITLE_KEYS[sectionKey];
    if (!key) return fallbackLabel;
    const value = t(key);
    return value === key ? fallbackLabel : value;
  }

  function profileSectionDisplayHint(sectionKey) {
    const key = PROFILE_BUILDER_SECTION_HINT_KEYS[sectionKey] || "profileBuilderSectionHint";
    return t(key);
  }

  function profileSectionDisplayWarning(sectionKey) {
    const key = PROFILE_BUILDER_SECTION_WARNING_KEYS[sectionKey];
    if (!key) return "";
    const value = t(key);
    return value === key ? "" : value;
  }

  function hasAnyConfiguredProfilePayload(wifiNetworks = [], sectionPayloads = {}) {
    if (Array.isArray(wifiNetworks) && wifiNetworks.length > 0) return true;
    if (!sectionPayloads || typeof sectionPayloads !== "object") return false;
    return Object.keys(APPLE_PROFILE_SECTION_SCHEMAS).some((sectionKey) => {
      if (sectionKey === "wifi") return false;
      const items = sectionPayloads[sectionKey];
      return Array.isArray(items) && items.length > 0;
    });
  }

  function profileValidationSectionLabel(sectionKey) {
    const fallback = PROFILE_BUILDER_SECTIONS.find((item) => item.key === sectionKey)?.label || sectionKey;
    return profileSectionDisplayTitle(sectionKey, fallback) || fallback;
  }

  function profileValidationPayloadLabel(sectionKey, payloadIndex, payloadCount) {
    const base = profileValidationSectionLabel(sectionKey);
    if (payloadCount > 1) {
      return `${base} #${payloadIndex + 1}`;
    }
    return base;
  }

  function profileValidationFieldLabel(sectionKey, fieldKey) {
    const schema = APPLE_PROFILE_SECTION_SCHEMAS[sectionKey];
    const field = Array.isArray(schema?.fields)
      ? schema.fields.find((item) => item?.key === fieldKey)
      : null;
    if (!field) return fieldKey;
    return sectionFieldLabel(field) || fieldKey;
  }

  function profileValidationMessage(sectionLabel, ruText, enText) {
    return `${sectionLabel}: ${lang === "ru" ? ruText : enText}`;
  }

  function profileValidationRequiredFieldMessage(sectionLabel, fieldLabel) {
    return profileValidationMessage(
      sectionLabel,
      `не заполнено поле ${fieldLabel}`,
      `${fieldLabel} is required`
    );
  }

  function profileValidationValueIsEmpty(fieldType, rawValue) {
    if (fieldType === "array_strings") {
      return sectionArrayItems(rawValue).length === 0;
    }
    if (fieldType === "number" || fieldType === "select_number") {
      return String(rawValue ?? "").trim() === "";
    }
    return String(rawValue ?? "").trim() === "";
  }

  function profileValidationNumericFieldMessage(sectionLabel, fieldLabel, mode, boundary) {
    if (mode === "number") {
      return profileValidationMessage(
        sectionLabel,
        `поле ${fieldLabel} должно быть числом`,
        `${fieldLabel} must be a number`
      );
    }
    if (mode === "min") {
      return profileValidationMessage(
        sectionLabel,
        `поле ${fieldLabel} должно быть не меньше ${boundary}`,
        `${fieldLabel} must be greater than or equal to ${boundary}`
      );
    }
    return profileValidationMessage(
      sectionLabel,
      `поле ${fieldLabel} должно быть не больше ${boundary}`,
      `${fieldLabel} must be less than or equal to ${boundary}`
    );
  }

  function profileValidationJsonFieldMessage(sectionLabel, fieldLabel, mode) {
    if (mode === "format") {
      return profileValidationMessage(
        sectionLabel,
        `поле ${fieldLabel} содержит невалидный JSON`,
        `${fieldLabel} contains invalid JSON`
      );
    }
    if (mode === "dict") {
      return profileValidationMessage(
        sectionLabel,
        `поле ${fieldLabel} должно быть JSON-объектом`,
        `${fieldLabel} must be a JSON object`
      );
    }
    return profileValidationMessage(
      sectionLabel,
      `поле ${fieldLabel} должно быть JSON-массивом`,
      `${fieldLabel} must be a JSON array`
    );
  }

  function validateProfilePayloadBeforeSubmit(wifiNetworks = [], sectionPayloads = {}) {
    if (Array.isArray(wifiNetworks) && wifiNetworks.length > 0) {
      for (let idx = 0; idx < wifiNetworks.length; idx += 1) {
        const network = wifiNetworks[idx] || {};
        if (!String(network.ssid || "").trim()) {
          const sectionLabel = profileValidationPayloadLabel("wifi", idx, wifiNetworks.length);
          return profileValidationRequiredFieldMessage(sectionLabel, "SSID");
        }
      }
    }

    const normalizedSectionPayloads =
      sectionPayloads && typeof sectionPayloads === "object" ? sectionPayloads : {};

    for (const [sectionKey, schema] of Object.entries(APPLE_PROFILE_SECTION_SCHEMAS)) {
      const items = Array.isArray(normalizedSectionPayloads[sectionKey])
        ? normalizedSectionPayloads[sectionKey]
        : [];
      if (items.length === 0) continue;

      for (let payloadIndex = 0; payloadIndex < items.length; payloadIndex += 1) {
        const payloadItem = items[payloadIndex] && typeof items[payloadIndex] === "object" ? items[payloadIndex] : {};
        const sectionLabel = profileValidationPayloadLabel(sectionKey, payloadIndex, items.length);

        if (sectionKey === "certificates") {
          if (!String(payloadItem?.payloadContent || "").trim()) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              lang === "ru" ? "Файл сертификата" : "Certificate file"
            );
          }
        }

        if (sectionKey === "airplay") {
          const rawEntries = Array.isArray(payloadItem?.airplayPasswords) ? payloadItem.airplayPasswords : [];
          for (const entry of rawEntries) {
            if (!entry || typeof entry !== "object") continue;
            const hasDeviceName = String(entry.deviceName || "").trim().length > 0;
            const hasPassword = String(entry.password || "").trim().length > 0;
            if (!hasDeviceName && hasPassword) {
              return profileValidationRequiredFieldMessage(
                sectionLabel,
                lang === "ru" ? "Device Name" : "Device Name"
              );
            }
          }
        }

        if (sectionKey === "airprint") {
          const rawPrinters = airPrintPrinterRowsFromPayload(payloadItem);
          const printers = normalizeAirPrintPrinterEntries(rawPrinters);
          if (printers.length === 0) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              lang === "ru" ? "Имя хоста или IP-адрес" : "Host Name or IP Address"
            );
          }
          for (const printer of printers) {
            if (!printer.hostName) {
              return profileValidationRequiredFieldMessage(
                sectionLabel,
                lang === "ru" ? "Имя хоста или IP-адрес" : "Host Name or IP Address"
              );
            }
            if (printer.port) {
              const port = Number.parseInt(String(printer.port), 10);
              if (!Number.isFinite(port) || port < 1 || port > 65535) {
                return profileValidationMessage(
                  sectionLabel,
                  lang === "ru" ? "порт должен быть числом от 1 до 65535" : "port must be a number from 1 to 65535"
                );
              }
            }
          }
        }

        if (sectionKey === "subscribed_calendars") {
          if (!String(payloadItem?.accountURL || "").trim()) {
            return profileValidationRequiredFieldMessage(sectionLabel, "URL");
          }
        }

        if (sectionKey === "restrictions") {
          const usageMode = String(payloadItem?.restrictAppUsageMode || "AllowAll");
          if (usageMode !== "AllowAll") {
            const restrictedApps = normalizeRestrictionAppEntries(payloadItem?.restrictedApps);
            if (restrictedApps.length === 0) {
              return profileValidationRequiredFieldMessage(
                sectionLabel,
                lang === "ru" ? "Список приложений" : "Apps list"
              );
            }
            for (const appEntry of restrictedApps) {
              const bundleID = String(appEntry?.bundleId || "").trim();
              if (!isLikelyBundleIdentifier(bundleID)) {
                return profileValidationMessage(
                  sectionLabel,
                  `некорректный Bundle identifier: ${bundleID || "-"}`,
                  `invalid bundle identifier: ${bundleID || "-"}`
                );
              }
            }
          }
        }

        if (sectionKey === "notifications") {
          const bundleID = String(payloadItem?.bundleIdentifier || "").trim();
          if (!bundleID) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              profileValidationFieldLabel(sectionKey, "bundleIdentifier")
            );
          }
          if (!isLikelyBundleIdentifier(bundleID)) {
            return profileValidationMessage(
              sectionLabel,
              `некорректный Bundle identifier: ${bundleID || "-"}`,
              `invalid bundle identifier: ${bundleID || "-"}`
            );
          }
        }

        const fields = Array.isArray(schema?.fields) ? schema.fields : [];
        for (const field of fields) {
          if (!field?.key) continue;
          if (!isSectionFieldVisible(field, payloadItem)) continue;

          const fieldLabel = sectionFieldLabel(field) || field.key;
          const rawValue = payloadItem?.[field.key];
          const requiredByPlaceholder = String(field.placeholder || "")
            .trim()
            .toLowerCase()
            .includes("[required]");

          if (requiredByPlaceholder && profileValidationValueIsEmpty(field.type, rawValue)) {
            return profileValidationRequiredFieldMessage(sectionLabel, fieldLabel);
          }

          const bundleFieldCandidate = /bundleidentifier/i.test(String(field.plistKey || field.key || ""));
          const bundleFieldText = String(rawValue ?? "").trim();
          if (bundleFieldCandidate && bundleFieldText && !isLikelyBundleIdentifier(bundleFieldText)) {
            return profileValidationMessage(
              sectionLabel,
              `некорректный Bundle identifier: ${bundleFieldText}`,
              `invalid bundle identifier: ${bundleFieldText}`
            );
          }

          if ((field.type === "number" || field.type === "select_number") && String(rawValue ?? "").trim() !== "") {
            const parsed = Number.parseInt(String(rawValue), 10);
            if (!Number.isFinite(parsed)) {
              return profileValidationNumericFieldMessage(sectionLabel, fieldLabel, "number");
            }
            if (Number.isFinite(field.min) && parsed < field.min) {
              return profileValidationNumericFieldMessage(sectionLabel, fieldLabel, "min", field.min);
            }
            if (Number.isFinite(field.max) && parsed > field.max) {
              return profileValidationNumericFieldMessage(sectionLabel, fieldLabel, "max", field.max);
            }
          }

          if (field.type === "json_dict" || field.type === "json_array") {
            const textValue = String(rawValue ?? "").trim();
            if (!textValue) continue;
            let parsed = null;
            try {
              parsed = JSON.parse(textValue);
            } catch {
              return profileValidationJsonFieldMessage(sectionLabel, fieldLabel, "format");
            }
            if (field.type === "json_dict" && (!parsed || Array.isArray(parsed) || typeof parsed !== "object")) {
              return profileValidationJsonFieldMessage(sectionLabel, fieldLabel, "dict");
            }
            if (field.type === "json_array" && !Array.isArray(parsed)) {
              return profileValidationJsonFieldMessage(sectionLabel, fieldLabel, "array");
            }
          }
        }

        if (sectionKey === "global_http_proxy") {
          const proxyType = String(payloadItem?.proxyType || "Manual").trim() || "Manual";
          if (proxyType === "Manual") {
            const proxyServer = String(payloadItem?.proxyServer || "").trim();
            const proxyPortRaw = String(payloadItem?.proxyServerPort || "").trim();
            if (!proxyServer) {
              return profileValidationRequiredFieldMessage(
                sectionLabel,
                profileValidationFieldLabel(sectionKey, "proxyServer")
              );
            }
            if (!proxyPortRaw) {
              return profileValidationRequiredFieldMessage(
                sectionLabel,
                profileValidationFieldLabel(sectionKey, "proxyServerPort")
              );
            }
            const proxyPort = Number.parseInt(proxyPortRaw, 10);
            if (!Number.isFinite(proxyPort) || proxyPort <= 0 || proxyPort > 65535) {
              return profileValidationMessage(
                sectionLabel,
                `поле ${profileValidationFieldLabel(sectionKey, "proxyServerPort")} должно быть от 1 до 65535`,
                `${profileValidationFieldLabel(sectionKey, "proxyServerPort")} must be between 1 and 65535`
              );
            }
          } else if (proxyType === "Auto" && !String(payloadItem?.proxyPACURL || "").trim()) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              profileValidationFieldLabel(sectionKey, "proxyPACURL")
            );
          }
        }

        if (sectionKey === "dns_proxy" && !String(payloadItem?.appBundleIdentifier || "").trim()) {
          return profileValidationRequiredFieldMessage(
            sectionLabel,
            profileValidationFieldLabel(sectionKey, "appBundleIdentifier")
          );
        }

        if (sectionKey === "content_filter" && String(payloadItem?.filterType || "BuiltIn") === "Plugin") {
          if (!String(payloadItem?.userDefinedName || "").trim()) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              profileValidationFieldLabel(sectionKey, "userDefinedName")
            );
          }
          if (!String(payloadItem?.pluginBundleID || "").trim()) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              profileValidationFieldLabel(sectionKey, "pluginBundleID")
            );
          }
        }

        if (sectionKey === "vpn") {
          if (!String(payloadItem?.userDefinedName || "").trim()) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              profileValidationFieldLabel(sectionKey, "userDefinedName")
            );
          }
          if (!String(payloadItem?.remoteAddress || "").trim()) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              profileValidationFieldLabel(sectionKey, "remoteAddress")
            );
          }
          const proxyType = String(payloadItem?.proxyType || "None").trim() || "None";
          if (proxyType === "Manual") {
            const proxyServer = String(payloadItem?.proxyServer || "").trim();
            const proxyPortRaw = String(payloadItem?.proxyServerPort || "").trim();
            if (!proxyServer) {
              return profileValidationRequiredFieldMessage(
                sectionLabel,
                profileValidationFieldLabel(sectionKey, "proxyServer")
              );
            }
            if (!proxyPortRaw) {
              return profileValidationRequiredFieldMessage(
                sectionLabel,
                profileValidationFieldLabel(sectionKey, "proxyServerPort")
              );
            }
            const proxyPort = Number.parseInt(proxyPortRaw, 10);
            if (!Number.isFinite(proxyPort) || proxyPort <= 0 || proxyPort > 65535) {
              return profileValidationMessage(
                sectionLabel,
                `поле ${profileValidationFieldLabel(sectionKey, "proxyServerPort")} должно быть от 1 до 65535`,
                `${profileValidationFieldLabel(sectionKey, "proxyServerPort")} must be between 1 and 65535`
              );
            }
          } else if (proxyType === "Auto" && !String(payloadItem?.proxyPACURL || "").trim()) {
            return profileValidationRequiredFieldMessage(
              sectionLabel,
              profileValidationFieldLabel(sectionKey, "proxyPACURL")
            );
          }
        }
      }
    }

    return "";
  }

  function profileSectionState(
    sectionKey,
    configuredSections = profileConfiguredSections,
    wifiNetworks = profileCreateWiFiNetworks,
    sectionPayloads = profileCreateSectionPayloads
  ) {
    if (sectionKey === "general") return "mandatory";
    if (sectionKey === "wifi") {
      return Array.isArray(wifiNetworks) && wifiNetworks.length > 0 ? "configured" : "not_configured";
    }
    if (APPLE_PROFILE_SECTION_SCHEMAS[sectionKey]) {
      const count = Array.isArray(sectionPayloads?.[sectionKey]) ? sectionPayloads[sectionKey].length : 0;
      return count > 0 ? "configured" : "not_configured";
    }
    return configuredSections?.[sectionKey] ? "configured" : "not_configured";
  }

  function profileSectionStateLabel(
    sectionKey,
    configuredSections = profileConfiguredSections,
    wifiNetworks = profileCreateWiFiNetworks,
    sectionPayloads = profileCreateSectionPayloads
  ) {
    const state = profileSectionState(sectionKey, configuredSections, wifiNetworks, sectionPayloads);
    const payloadCount =
      sectionKey === "wifi"
        ? Array.isArray(wifiNetworks)
          ? wifiNetworks.length
          : 0
        : Array.isArray(sectionPayloads?.[sectionKey])
          ? sectionPayloads[sectionKey].length
          : 0;
    if (payloadCount > 1) {
      return `${payloadCount} ${t("profileBuilderConfiguredMany")}`;
    }
    if (state === "mandatory") return t("profileBuilderMandatory");
    if (state === "configured") return t("profileBuilderConfigured");
    return t("profileBuilderNotConfigured");
  }

  function configureWiFiSection(setConfiguredSections, setWiFiNetworks) {
    setConfiguredSections((prev) => ({ ...prev, wifi: true }));
    setWiFiNetworks((prev) => (prev.length > 0 ? prev : [createWiFiPayloadDraft()]));
  }

  function clearWiFiSection(setConfiguredSections, setWiFiNetworks) {
    setConfiguredSections((prev) => ({ ...prev, wifi: false }));
    setWiFiNetworks([]);
  }

  function addWiFiPayload(setConfiguredSections, setWiFiNetworks) {
    setConfiguredSections((prev) => ({ ...prev, wifi: true }));
    setWiFiNetworks((prev) => [...prev, createWiFiPayloadDraft()]);
  }

  function removeWiFiPayloadByIndex(setConfiguredSections, setWiFiNetworks, index) {
    setWiFiNetworks((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      setConfiguredSections((old) => ({ ...old, wifi: next.length > 0 }));
      return next;
    });
  }

  function updateWiFiPayloadField(setWiFiNetworks, index, field, value) {
    setWiFiNetworks((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              [field]: value
            }
          : item
      )
    );
  }

  function configureSection(setConfiguredSections, setSectionPayloads, sectionKey) {
    setConfiguredSections((prev) => ({ ...prev, [sectionKey]: true }));
    setSectionPayloads((prev) => {
      const current = Array.isArray(prev?.[sectionKey]) ? prev[sectionKey] : [];
      if (current.length > 0) return prev;
      return {
        ...prev,
        [sectionKey]: [createSectionPayloadDraft(sectionKey)]
      };
    });
  }

  function addSectionPayload(setConfiguredSections, setSectionPayloads, sectionKey) {
    setConfiguredSections((prev) => ({ ...prev, [sectionKey]: true }));
    setSectionPayloads((prev) => {
      const current = Array.isArray(prev?.[sectionKey]) ? prev[sectionKey] : [];
      return {
        ...prev,
        [sectionKey]: [...current, createSectionPayloadDraft(sectionKey)]
      };
    });
  }

  function removeSectionPayload(setConfiguredSections, setSectionPayloads, sectionKey, index) {
    setSectionPayloads((prev) => {
      const current = Array.isArray(prev?.[sectionKey]) ? prev[sectionKey] : [];
      const next = current.filter((_, idx) => idx !== index);
      setConfiguredSections((old) => ({ ...old, [sectionKey]: next.length > 0 }));
      return {
        ...prev,
        [sectionKey]: next
      };
    });
  }

  function updateSectionPayloadField(setSectionPayloads, sectionKey, index, field, value) {
    setSectionPayloads((prev) => {
      const current = Array.isArray(prev?.[sectionKey]) ? prev[sectionKey] : [];
      return {
        ...prev,
        [sectionKey]: current.map((item, idx) =>
          idx === index
            ? {
                ...item,
                [field]: value
              }
            : item
        )
      };
    });
  }

  function configureCreateWiFiSection() {
    configureWiFiSection(setProfileConfiguredSections, setProfileCreateWiFiNetworks);
  }

  function addCreateWiFiPayload() {
    addWiFiPayload(setProfileConfiguredSections, setProfileCreateWiFiNetworks);
  }

  function removeCreateWiFiPayload(index) {
    removeWiFiPayloadByIndex(setProfileConfiguredSections, setProfileCreateWiFiNetworks, index);
  }

  function updateCreateWiFiPayload(index, field, value) {
    updateWiFiPayloadField(setProfileCreateWiFiNetworks, index, field, value);
  }

  function configureCreateSection(sectionKey) {
    configureSection(setProfileConfiguredSections, setProfileCreateSectionPayloads, sectionKey);
  }

  function addCreateSectionPayload(sectionKey) {
    addSectionPayload(setProfileConfiguredSections, setProfileCreateSectionPayloads, sectionKey);
  }

  function removeCreateSectionPayload(sectionKey, index) {
    removeSectionPayload(setProfileConfiguredSections, setProfileCreateSectionPayloads, sectionKey, index);
  }

  function updateCreateSectionPayload(sectionKey, index, field, value) {
    updateSectionPayloadField(setProfileCreateSectionPayloads, sectionKey, index, field, value);
  }

  function addViewWiFiPayload() {
    addWiFiPayload(setProfileViewConfiguredSections, setProfileViewWiFiNetworks);
  }

  function removeViewWiFiPayload(index) {
    removeWiFiPayloadByIndex(setProfileViewConfiguredSections, setProfileViewWiFiNetworks, index);
  }

  function updateViewWiFiPayload(index, field, value) {
    updateWiFiPayloadField(setProfileViewWiFiNetworks, index, field, value);
  }

  function configureViewSection(sectionKey) {
    configureSection(setProfileViewConfiguredSections, setProfileViewSectionPayloads, sectionKey);
  }

  function addViewSectionPayload(sectionKey) {
    addSectionPayload(setProfileViewConfiguredSections, setProfileViewSectionPayloads, sectionKey);
  }

  function removeViewSectionPayload(sectionKey, index) {
    removeSectionPayload(setProfileViewConfiguredSections, setProfileViewSectionPayloads, sectionKey, index);
  }

  function updateViewSectionPayload(sectionKey, index, field, value) {
    updateSectionPayloadField(setProfileViewSectionPayloads, sectionKey, index, field, value);
  }

  function restrictionFieldsForTab(schema, tabKey, payloadItem) {
    return (schema?.fields || []).filter(
      (field) =>
        !isTvOSOnlyProfileField(field) &&
        restrictionFieldTab(field.key) === tabKey &&
        isSectionFieldVisible(field, payloadItem)
    );
  }

  function updateRestrictionsAppsForPayload(target, payloadIndex, updater) {
    const setPayloads =
      target === "view" ? setProfileViewSectionPayloads : setProfileCreateSectionPayloads;
    setPayloads((prev) => {
      const restrictionsPayloads = Array.isArray(prev?.restrictions) ? prev.restrictions : [];
      if (payloadIndex < 0 || payloadIndex >= restrictionsPayloads.length) return prev;
      const nextPayloads = restrictionsPayloads.map((payloadItem, index) => {
        if (index !== payloadIndex) return payloadItem;
        const currentApps = normalizeRestrictionAppEntries(payloadItem?.restrictedApps);
        const nextApps = normalizeRestrictionAppEntries(updater(currentApps));
        return {
          ...payloadItem,
          restrictedApps: nextApps
        };
      });
      return {
        ...prev,
        restrictions: nextPayloads
      };
    });
  }

  function openRestrictionsAppPicker(target, payloadIndex) {
    setRestrictionsAppPickerContext({
      target,
      payloadIndex,
      mode: "restrictions",
      sectionKey: "restrictions",
      fieldKey: ""
    });
    setRestrictionsAppPickerQuery("");
    setRestrictionsAppPickerResults(iosSystemAppsPickerResults("", lang));
    setShowRestrictionsAppPickerModal(true);
  }

  function openCommunicationServiceAppPicker(target, sectionKey, payloadIndex, fieldKey) {
    setRestrictionsAppPickerContext({
      target,
      payloadIndex,
      mode: "communication_service",
      sectionKey,
      fieldKey
    });
    setRestrictionsAppPickerQuery("");
    setRestrictionsAppPickerResults(iosSystemAppsPickerResults("", lang));
    setShowRestrictionsAppPickerModal(true);
  }

  function closeRestrictionsAppPicker() {
    setShowRestrictionsAppPickerModal(false);
    setRestrictionsAppPickerContext({
      target: "create",
      payloadIndex: -1,
      mode: "restrictions",
      sectionKey: "restrictions",
      fieldKey: ""
    });
    setRestrictionsAppPickerQuery("");
    setRestrictionsAppPickerResults([]);
    setRestrictionsAppPickerLoading(false);
  }

  async function searchRestrictionsApps() {
    const query = String(restrictionsAppPickerQuery || "").trim();
    const systemItems = iosSystemAppsPickerResults(query, lang);
    if (!query) {
      setRestrictionsAppPickerResults(systemItems);
      return;
    }
    try {
      setRestrictionsAppPickerLoading(true);
      const out = await request(
        `/apps/app-store/search?q=${encodeURIComponent(query)}&country=US&platform=ios&limit=25`
      );
      setRestrictionsAppPickerResults(
        mergeRestrictionsPickerResults(lang, systemItems, Array.isArray(out?.items) ? out.items : [])
      );
    } catch (err) {
      setRestrictionsAppPickerResults(systemItems);
      setErrorMessage(`${t("appsStoreSearchFailed")}: ${err.message}`);
    } finally {
      setRestrictionsAppPickerLoading(false);
    }
  }

  function chooseRestrictionsApp(appItem) {
    const bundleId = String(appItem?.bundle_id || "").trim();
    if (!bundleId) return;
    const name = String(appItem?.name || bundleId).trim() || bundleId;
    const bundleIDLower = bundleId.toLowerCase();
    const { target, payloadIndex, mode, sectionKey, fieldKey } = restrictionsAppPickerContext || {};
    if (mode === "communication_service" && sectionKey && fieldKey) {
      setProfileSectionPayloadField(target === "view" ? "view" : "create", sectionKey, payloadIndex, fieldKey, bundleId);
      closeRestrictionsAppPicker();
      return;
    }
    updateRestrictionsAppsForPayload(target === "view" ? "view" : "create", payloadIndex, (currentApps) => {
      if (currentApps.some((entry) => String(entry?.bundleId || "").trim().toLowerCase() === bundleIDLower)) {
        return currentApps;
      }
      return [...currentApps, { bundleId, name }];
    });
    closeRestrictionsAppPicker();
  }

  function removeLastRestrictionsApp(target, payloadIndex) {
    updateRestrictionsAppsForPayload(target, payloadIndex, (currentApps) => currentApps.slice(0, -1));
  }

  function removeRestrictionsAppByBundle(target, payloadIndex, bundleId) {
    const cleanBundle = String(bundleId || "").trim();
    if (!cleanBundle) return;
    const cleanBundleLower = cleanBundle.toLowerCase();
    updateRestrictionsAppsForPayload(target, payloadIndex, (currentApps) =>
      currentApps.filter(
        (entry) => String(entry?.bundleId || "").trim().toLowerCase() !== cleanBundleLower
      )
    );
  }

  function updateAirPlayPayload(target, payloadIndex, updater) {
    const setPayloads =
      target === "view" ? setProfileViewSectionPayloads : setProfileCreateSectionPayloads;
    setPayloads((prev) => {
      const current = Array.isArray(prev?.airplay) ? prev.airplay : [];
      if (payloadIndex < 0 || payloadIndex >= current.length) return prev;
      const next = current.map((payloadItem, index) =>
        index === payloadIndex ? updater(payloadItem || {}) : payloadItem
      );
      return {
        ...prev,
        airplay: next
      };
    });
  }

  function openAirPlayDestinationModal(target, payloadIndex) {
    setAirPlayDestinationContext({
      target: target === "view" ? "view" : "create",
      payloadIndex
    });
    setAirPlayDestinationForm({
      deviceName: "",
      password: ""
    });
    setShowAirPlayDestinationModal(true);
  }

  function closeAirPlayDestinationModal() {
    setShowAirPlayDestinationModal(false);
    setAirPlayDestinationContext({
      target: "create",
      payloadIndex: -1
    });
    setAirPlayDestinationForm({
      deviceName: "",
      password: ""
    });
  }

  function saveAirPlayDestination() {
    const deviceName = String(airPlayDestinationForm.deviceName || "").trim();
    const password = String(airPlayDestinationForm.password || "").trim();
    if (!deviceName) {
      setErrorMessage(t("requiredField"));
      return;
    }
    const { target, payloadIndex } = airPlayDestinationContext;
    updateAirPlayPayload(target, payloadIndex, (payloadItem) => {
      const current = normalizeAirPlayPasswordEntries(payloadItem?.airplayPasswords);
      return {
        ...payloadItem,
        airplayPasswords: [...current, { deviceName, password }]
      };
    });
    closeAirPlayDestinationModal();
  }

  function removeLastAirPlayDestination(target, payloadIndex) {
    updateAirPlayPayload(target, payloadIndex, (payloadItem) => {
      const current = normalizeAirPlayPasswordEntries(payloadItem?.airplayPasswords);
      return {
        ...payloadItem,
        airplayPasswords: current.slice(0, -1)
      };
    });
  }

  function updateAirPlayPasswordEntryField(target, payloadIndex, rowIndex, field, fieldValue) {
    updateAirPlayPayload(target, payloadIndex, (payloadItem) => {
      const current = normalizeAirPlayPasswordEntries(payloadItem?.airplayPasswords);
      const next = [...current];
      while (next.length <= rowIndex) {
        next.push({ deviceName: "", password: "" });
      }
      const row = next[rowIndex] || { deviceName: "", password: "" };
      next[rowIndex] = {
        ...row,
        [field]: fieldValue
      };
      return {
        ...payloadItem,
        airplayPasswords: next.filter((entry) => String(entry.deviceName || "").trim() || String(entry.password || "").trim())
      };
    });
  }

  function setAirPlayAllowedDestinations(target, payloadIndex, nextValues) {
    updateAirPlayPayload(target, payloadIndex, (payloadItem) => ({
      ...payloadItem,
      allowedDestinations: sectionArrayValue(nextValues)
    }));
  }

  function updateAirPlayAllowedDestination(target, payloadIndex, rowIndex, rowValue) {
    updateAirPlayPayload(target, payloadIndex, (payloadItem) => {
      const values = sectionArrayItems(payloadItem?.allowedDestinations);
      values[rowIndex] = rowValue;
      return {
        ...payloadItem,
        allowedDestinations: sectionArrayValue(values)
      };
    });
  }

  function updateAirPrintPayload(target, payloadIndex, updater) {
    const setPayloads =
      target === "view" ? setProfileViewSectionPayloads : setProfileCreateSectionPayloads;
    setPayloads((prev) => {
      const current = Array.isArray(prev?.airprint) ? prev.airprint : [];
      if (payloadIndex < 0 || payloadIndex >= current.length) return prev;
      const next = current.map((payloadItem, index) =>
        index === payloadIndex ? updater(payloadItem || {}) : payloadItem
      );
      return {
        ...prev,
        airprint: next
      };
    });
  }

  function openAirPrintPrinterModal(target, payloadIndex, rowIndex = -1, row = null) {
    setAirPrintPrinterContext({
      target: target === "view" ? "view" : "create",
      payloadIndex,
      rowIndex
    });
    setAirPrintPrinterForm({
      configureMode: "manual",
      hostName: String(row?.hostName || row?.ipAddress || row?.IPAddress || "").trim(),
      forceTLS: Boolean(row?.forceTLS || row?.ForceTLS),
      port: String(row?.port ?? row?.Port ?? "").trim(),
      resourcePath: String(row?.resourcePath || row?.ResourcePath || "").trim()
    });
    setShowAirPrintPrinterModal(true);
  }

  function closeAirPrintPrinterModal() {
    setShowAirPrintPrinterModal(false);
    setAirPrintPrinterContext({
      target: "create",
      payloadIndex: -1,
      rowIndex: -1
    });
    setAirPrintPrinterForm({
      configureMode: "manual",
      hostName: "",
      forceTLS: false,
      port: "",
      resourcePath: ""
    });
  }

  function saveAirPrintPrinter() {
    const hostName = String(airPrintPrinterForm.hostName || "").trim();
    if (!hostName) {
      setErrorMessage(lang === "ru" ? "Укажите имя хоста или IP-адрес принтера" : "Enter printer host name or IP address");
      return;
    }
    const port = String(airPrintPrinterForm.port || "").trim();
    if (port) {
      const parsedPort = Number.parseInt(port, 10);
      if (!Number.isFinite(parsedPort) || parsedPort < 1 || parsedPort > 65535) {
        setErrorMessage(lang === "ru" ? "Порт должен быть числом от 1 до 65535" : "Port must be a number from 1 to 65535");
        return;
      }
    }
    const { target, payloadIndex, rowIndex } = airPrintPrinterContext;
    const nextPrinter = {
      hostName,
      forceTLS: Boolean(airPrintPrinterForm.forceTLS),
      port,
      resourcePath: String(airPrintPrinterForm.resourcePath || "").trim()
    };
    updateAirPrintPayload(target, payloadIndex, (payloadItem) => {
      const current = normalizeAirPrintPrinterEntries(airPrintPrinterRowsFromPayload(payloadItem));
      const next = [...current];
      if (rowIndex >= 0 && rowIndex < next.length) {
        next[rowIndex] = nextPrinter;
      } else {
        next.push(nextPrinter);
      }
      return {
        ...payloadItem,
        printers: next
      };
    });
    closeAirPrintPrinterModal();
  }

  function removeLastAirPrintPrinter(target, payloadIndex) {
    updateAirPrintPayload(target, payloadIndex, (payloadItem) => {
      const current = normalizeAirPrintPrinterEntries(airPrintPrinterRowsFromPayload(payloadItem));
      return {
        ...payloadItem,
        printers: current.slice(0, -1)
      };
    });
  }

  function updateExchangeActiveSyncPayload(target, payloadIndex, updater) {
    const setPayloads = target === "view" ? setProfileViewSectionPayloads : setProfileCreateSectionPayloads;
    setPayloads((prev) => {
      const current = Array.isArray(prev?.exchange_active_sync) ? prev.exchange_active_sync : [];
      if (payloadIndex < 0 || payloadIndex >= current.length) return prev;
      return {
        ...prev,
        exchange_active_sync: current.map((item, index) =>
          index === payloadIndex ? updater(item || {}) : item
        )
      };
    });
  }

  function setExchangeActiveSyncField(target, payloadIndex, fieldKey, value) {
    updateExchangeActiveSyncPayload(target, payloadIndex, (payloadItem) => ({
      ...payloadItem,
      [fieldKey]: value
    }));
  }

  function setProfileSectionPayloadField(target, sectionKey, payloadIndex, fieldKey, value) {
    const setPayloads = target === "view" ? setProfileViewSectionPayloads : setProfileCreateSectionPayloads;
    setPayloads((prev) => {
      const current = Array.isArray(prev?.[sectionKey]) ? prev[sectionKey] : [];
      if (payloadIndex < 0 || payloadIndex >= current.length) return prev;
      return {
        ...prev,
        [sectionKey]: current.map((item, index) =>
          index === payloadIndex ? { ...(item || {}), [fieldKey]: value } : item
        )
      };
    });
  }

  function sectionFieldLabel(field) {
    const label = lang === "ru" && field?.labelRu ? field.labelRu : field?.label || "";
    return translateProfileBuilderText(label, lang);
  }

  function sectionFieldHint(field) {
    const hint = lang === "ru" && field?.hintRu ? field.hintRu : field?.hint || "";
    return translateProfileBuilderText(hint, lang);
  }

  function sectionFieldOptionLabel(option) {
    const label =
      typeof option !== "object" || option === null
        ? String(option ?? "")
        : lang === "ru" && option.labelRu
          ? String(option.labelRu)
          : String(option.label ?? option.value ?? "");
    return translateProfileBuilderText(label, lang);
  }

  function renderProfileBuilderLabelText(label) {
    const text = String(label || "");
    const marker = "(supervised only)";
    const index = text.toLowerCase().lastIndexOf(marker);
    if (index < 0) return text;
    const before = text.slice(0, index).trimEnd();
    const suffix = text.slice(index);
    return (
      <>
        {before}
        {before ? " " : ""}
        <strong className="profile-checkbox-supervised">{suffix}</strong>
      </>
    );
  }

  function sectionArrayValue(items) {
    return (Array.isArray(items) ? items : []).map((item) => String(item ?? ""));
  }

  function certificatePayloadTypeByFileName(fileName) {
    const lower = String(fileName || "").trim().toLowerCase();
    if (lower.endsWith(".p12") || lower.endsWith(".pfx")) {
      return "com.apple.security.pkcs12";
    }
    return "com.apple.security.pkcs1";
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const out = String(reader.result || "");
        const comma = out.indexOf(",");
        resolve(comma >= 0 ? out.slice(comma + 1) : out);
      };
      reader.onerror = () => reject(new Error("FILE_READ_FAILED"));
      reader.readAsDataURL(file);
    });
  }

  async function appendCertificatesFromFiles(target, files) {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    try {
      const payloads = await Promise.all(
        list.map(async (file) => ({
          id: randomHex(12),
          certificateFileName: String(file.name || "").trim(),
          payloadDisplayName: String(file.name || "").trim(),
          payloadContent: await fileToBase64(file),
          certificatePayloadType: certificatePayloadTypeByFileName(file.name)
        }))
      );
      if (target === "view") {
        setProfileViewConfiguredSections((prev) => ({ ...prev, certificates: true }));
        setProfileViewSectionPayloads((prev) => {
          const current = Array.isArray(prev?.certificates) ? prev.certificates : [];
          return { ...prev, certificates: [...current, ...payloads] };
        });
      } else {
        setProfileConfiguredSections((prev) => ({ ...prev, certificates: true }));
        setProfileCreateSectionPayloads((prev) => {
          const current = Array.isArray(prev?.certificates) ? prev.certificates : [];
          return { ...prev, certificates: [...current, ...payloads] };
        });
      }
    } catch (err) {
      setErrorMessage(`${t("profileCertificatesLoadFailed")}: ${err.message || "Failed to load certificate file"}`);
    }
  }

  function openCertificatesPicker(target) {
    const ref = target === "view" ? viewCertificatesInputRef : createCertificatesInputRef;
    if (!ref?.current) return;
    ref.current.click();
  }

  async function onCertificatesPicked(event, target) {
    const input = event?.target;
    const files = input?.files;
    if (!files || files.length === 0) return;
    await appendCertificatesFromFiles(target, files);
    if (input) {
      input.value = "";
    }
  }

  function renderSectionField(field, value, onChange, readOnly = false, platformKey = "ios") {
    const label = profileBuilderFieldLabelWithMinOS(sectionFieldLabel(field), field, platformKey);
    const hint = sectionFieldHint(field);
    const showIOSBundlePicker = fieldAcceptsIOSBundleIdentifier(field);
    const iosBundleOptions = showIOSBundlePicker ? iosSystemBundleOptionsForLanguage(lang) : [];
    if (field.type === "boolean") {
      return (
        <label className="profile-wifi-check" key={field.key}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => onChange(event.target.checked)}
            disabled={readOnly}
          />
          <span className="profile-checkbox-copy">
            <span className="profile-checkbox-label">{renderProfileBuilderLabelText(label)}</span>
            {hint && <small>{hint}</small>}
          </span>
        </label>
      );
    }

    if (field.type === "radio") {
      const options = Array.isArray(field.options) ? field.options : [];
      return (
        <div className="profile-builder-field" key={field.key}>
          <label>{label}</label>
          {hint && <p className="muted">{hint}</p>}
          <div className="profile-radio-group">
            {options.map((option) => {
              const optionValue = typeof option === "object" ? option.value : option;
              const optionLabel = sectionFieldOptionLabel(option);
              return (
                <label className="profile-radio-option" key={`${field.key}-${String(optionValue)}`}>
                  <input
                    type="radio"
                    name={field.key}
                    value={String(optionValue)}
                    checked={String(value ?? field.defaultValue ?? "") === String(optionValue)}
                    onChange={(event) => onChange(event.target.value)}
                    disabled={readOnly}
                  />
                  <span>{String(optionLabel)}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    if (field.type === "select" || field.type === "select_number") {
      return (
        <div className="profile-builder-field" key={field.key}>
          <label>{label}</label>
          <select
            className="text-input"
            value={String(value ?? field.defaultValue ?? "")}
            onChange={(event) => onChange(event.target.value)}
            disabled={readOnly}
          >
            {(Array.isArray(field.options) ? field.options : []).map((option) => {
              const optionValue = typeof option === "object" ? option.value : option;
              const optionLabel = sectionFieldOptionLabel(option);
              return (
                <option key={String(optionValue)} value={String(optionValue)}>
                  {String(optionLabel)}
                </option>
              );
            })}
          </select>
          {hint && <p className="muted">{hint}</p>}
        </div>
      );
    }

    if (field.type === "array_strings" && field.ui === "list") {
      const items = sectionArrayItems(value);
      const visibleRows = Math.max(5, items.length);
      return (
        <div className="profile-builder-field profile-list-field" key={field.key}>
          <label>{label}</label>
          {hint && <p className="muted">{hint}</p>}
          <div className="profile-list-editor">
            <div className="profile-list-editor-table">
              {Array.from({ length: visibleRows }).map((_, rowIndex) => {
                const rowValue = items[rowIndex] ?? "";
                return (
                  <input
                    key={`${field.key}-row-${rowIndex}`}
                    className="text-input profile-list-editor-input"
                    value={rowValue}
                    onChange={(event) => {
                      const next = [...items];
                      next[rowIndex] = event.target.value;
                      onChange(sectionArrayValue(next));
                    }}
                    readOnly={readOnly}
                    placeholder={rowIndex === 0 ? field.itemPlaceholder || "" : ""}
                  />
                );
              })}
            </div>
            {!readOnly && (
              <div className="profile-list-editor-actions">
                <button
                  type="button"
                  className="profile-list-square-btn"
                  onClick={() => onChange([...items, ""])}
                >
                  +
                </button>
                <button
                  type="button"
                  className="profile-list-square-btn"
                  onClick={() => onChange(items.slice(0, -1))}
                  disabled={items.length === 0}
                >
                  −
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (
      field.type === "textarea" ||
      field.type === "array_strings" ||
      field.type === "data" ||
      field.type === "json_dict" ||
      field.type === "json_array"
    ) {
      const placeholder =
        field.type === "array_strings"
          ? "one value per line"
          : field.type === "json_dict"
            ? `{\n  "key": "value"\n}`
            : field.type === "json_array"
              ? `[\n  { "key": "value" }\n]`
              : field.placeholder || "";
      return (
        <div className="profile-builder-field" key={field.key}>
          <label>{label}</label>
          <textarea
            className="text-input"
            rows={Number.isFinite(Number(field.rows)) ? Number(field.rows) : 3}
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            readOnly={readOnly}
            placeholder={placeholder}
          />
          {field.fileLoader && !readOnly && (
            <div className="action-row">
              <input
                type="file"
                accept=".json,.txt,.plist"
                style={{ display: "none" }}
                id={`${field.key}-loader`}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  try {
                    const textValue = await file.text();
                    onChange(textValue);
                  } catch (err) {
                    setErrorMessage(err.message || "Failed to read file");
                  } finally {
                    event.target.value = "";
                  }
                }}
              />
              <button
                type="button"
                className="secondary"
                onClick={() => document.getElementById(`${field.key}-loader`)?.click()}
              >
                {t("profileBuilderUploadFile")}
              </button>
              <button type="button" className="secondary" onClick={() => onChange("")}>
                {t("profileBuilderRemoveConfiguration")}
              </button>
            </div>
          )}
          {hint && <p className="muted">{hint}</p>}
        </div>
      );
    }

    if (field.type === "number") {
      return (
        <div className="profile-builder-field" key={field.key}>
          <label>{label}</label>
          <input
            className="text-input"
            type="number"
            value={value === null || value === undefined ? "" : String(value)}
            onChange={(event) => onChange(event.target.value)}
            readOnly={readOnly}
            min={field.min}
            max={field.max}
            step={field.step || 1}
            placeholder={field.placeholder || ""}
          />
          {hint && <p className="muted">{hint}</p>}
        </div>
      );
    }

    return (
      <div className="profile-builder-field" key={field.key}>
        <label>{label}</label>
        {showIOSBundlePicker && (
          <select
            className="text-input"
            defaultValue=""
            onChange={(event) => {
              const selectedValue = String(event.target.value || "").trim();
              if (selectedValue) {
                onChange(selectedValue);
              }
              event.target.value = "";
            }}
            disabled={readOnly}
          >
            <option value="">{t("profileBundlePickerPlaceholder")}</option>
            {iosBundleOptions.map((item) => (
              <option key={item.bundleId} value={item.bundleId}>
                {item.name} ({item.bundleId})
              </option>
            ))}
          </select>
        )}
        <input
          className="text-input"
          type={field.type === "password" ? "password" : "text"}
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
          readOnly={readOnly}
          placeholder={field.placeholder || ""}
        />
        {showIOSBundlePicker && <p className="muted">{t("profileBundlePickerLabel")}</p>}
        {hint && <p className="muted">{hint}</p>}
      </div>
    );
  }

  function renderAirPlayPayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const passwordRows = normalizeAirPlayPasswordEntries(payloadItem?.airplayPasswords);
    const allowedRows = sectionArrayItems(payloadItem?.allowedDestinations);
    const visibleAllowedRows = Math.max(5, allowedRows.length);

    return (
      <>
        <div className="profile-builder-field profile-list-field">
          <label>{t("profileAirPlayPasswords")}</label>
          <div className="profile-airplay-passwords-table">
            <div className="profile-airplay-passwords-head">
              <span>{t("profileAirPlayDeviceName")}</span>
              <span>{t("profileAirPlayPassword")}</span>
            </div>
            {Array.from({ length: Math.max(5, passwordRows.length) }).map((_, rowIndex) => {
              const row = passwordRows[rowIndex] || { deviceName: "", password: "" };
              return (
                <div className="profile-airplay-passwords-row" key={`airplay-pass-${rowIndex}`}>
                  <input
                    className="text-input profile-list-editor-input"
                    value={row.deviceName}
                    onChange={(event) =>
                      updateAirPlayPasswordEntryField(
                        target,
                        payloadIndex,
                        rowIndex,
                        "deviceName",
                        event.target.value
                      )
                    }
                    readOnly={readOnly}
                    placeholder={rowIndex === 0 ? t("profileAirPlayDeviceName") : ""}
                  />
                  <input
                    className="text-input profile-list-editor-input"
                    type="password"
                    value={row.password}
                    onChange={(event) =>
                      updateAirPlayPasswordEntryField(
                        target,
                        payloadIndex,
                        rowIndex,
                        "password",
                        event.target.value
                      )
                    }
                    readOnly={readOnly}
                    placeholder={rowIndex === 0 ? t("profileAirPlayPassword") : ""}
                  />
                </div>
              );
            })}
          </div>
          {!readOnly && (
            <div className="profile-list-editor-actions">
              <button
                type="button"
                className="profile-list-square-btn"
                onClick={() => openAirPlayDestinationModal(target, payloadIndex)}
              >
                +
              </button>
              <button
                type="button"
                className="profile-list-square-btn"
                onClick={() => removeLastAirPlayDestination(target, payloadIndex)}
                disabled={passwordRows.length === 0}
              >
                −
              </button>
            </div>
          )}
        </div>

        <div className="profile-builder-field profile-list-field">
          <label>{t("profileAirPlayAllowed")}</label>
          <div className="profile-list-editor">
            <div className="profile-list-editor-table">
              {Array.from({ length: visibleAllowedRows }).map((_, rowIndex) => {
                const rowValue = allowedRows[rowIndex] ?? "";
                return (
                  <input
                    key={`airplay-allow-${rowIndex}`}
                    className="text-input profile-list-editor-input"
                    value={rowValue}
                    onChange={(event) =>
                      updateAirPlayAllowedDestination(target, payloadIndex, rowIndex, event.target.value)
                    }
                    readOnly={readOnly}
                    placeholder={rowIndex === 0 ? "00:00:00:00:00:00" : ""}
                  />
                );
              })}
            </div>
            {!readOnly && (
              <div className="profile-list-editor-actions">
                <button
                  type="button"
                  className="profile-list-square-btn"
                  onClick={() => setAirPlayAllowedDestinations(target, payloadIndex, [...allowedRows, ""])}
                >
                  +
                </button>
                <button
                  type="button"
                  className="profile-list-square-btn"
                  onClick={() => setAirPlayAllowedDestinations(target, payloadIndex, allowedRows.slice(0, -1))}
                  disabled={allowedRows.length === 0}
                >
                  −
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  function renderAirPrintPayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const rows = normalizeAirPrintPrinterEntries(airPrintPrinterRowsFromPayload(payloadItem));
    const rowCount = Math.max(5, rows.length);

    return (
      <div className="profile-builder-field profile-list-field profile-airprint-editor">
        <label>{lang === "ru" ? "Принтеры" : "Printers"}</label>
        <div className="profile-airprint-table">
          <div className="profile-airprint-head">
            <span>{lang === "ru" ? "Имя хоста или IP-адрес" : "Host name or IP Address"}</span>
            <span>{lang === "ru" ? "Использовать TLS" : "Use TLS"}</span>
            <span>{lang === "ru" ? "Порт" : "Port"}</span>
            <span>{lang === "ru" ? "Путь ресурса" : "Resource path"}</span>
          </div>
          {Array.from({ length: rowCount }).map((_, rowIndex) => {
            const row = rows[rowIndex];
            const isClickable = Boolean(row) && !readOnly;
            return (
              <button
                type="button"
                className={`profile-airprint-row profile-airprint-summary-row${isClickable ? " clickable" : ""}`}
                key={`airprint-${payloadIndex}-${rowIndex}`}
                onClick={() => {
                  if (isClickable) openAirPrintPrinterModal(target, payloadIndex, rowIndex, row);
                }}
                disabled={!isClickable}
              >
                <span>{row?.hostName || ""}</span>
                <span>{row?.forceTLS ? "✓" : ""}</span>
                <span>{row?.port || ""}</span>
                <span>{row?.resourcePath || ""}</span>
              </button>
            );
          })}
        </div>
        {!readOnly && (
          <div className="profile-list-editor-actions">
            <button
              type="button"
              className="profile-list-square-btn"
              onClick={() => openAirPrintPrinterModal(target, payloadIndex)}
            >
              +
            </button>
            <button
              type="button"
              className="profile-list-square-btn"
              onClick={() => removeLastAirPrintPrinter(target, payloadIndex)}
              disabled={rows.length === 0}
            >
              −
            </button>
          </div>
        )}
      </div>
    );
  }

  function renderContactsPayloadFields(payloadItem, payloadIndex, readOnly = false) {
    const copy =
      lang === "ru"
        ? {
            accountDescription: "Описание учетной записи",
            accountDescriptionHint: "Отображаемое имя учетной записи, например \"Company CardDAV Account\"",
            hostAndPort: "Имя хоста и порт учетной записи",
            hostAndPortHint: "Имя хоста CardDAV или IP-адрес и номер порта",
            principalURL: "Principal URL",
            principalURLHint: "Principal URL учетной записи CardDAV",
            userName: "Имя пользователя учетной записи",
            userNameHint: "Имя пользователя CardDAV",
            password: "Пароль учетной записи",
            passwordHint: "Пароль CardDAV",
            useSSL: "Использовать SSL",
            communicationRules: "Правила сервиса связи",
            communicationRulesHint: "Выберите приложение по умолчанию для звонков контактам из этой учетной записи",
            choose: "Выбрать...",
            optional: "Необязательно"
          }
        : {
            accountDescription: "Account Description",
            accountDescriptionHint: "The display name of the account (e.g. \"Company CardDAV Account\")",
            hostAndPort: "Account Host Name and Port",
            hostAndPortHint: "The CardDAV host name or IP address and port number",
            principalURL: "Principal URL",
            principalURLHint: "The Principal URL for the CardDAV account",
            userName: "Account User Name",
            userNameHint: "The CardDAV user name",
            password: "Account Password",
            passwordHint: "The CardDAV password",
            useSSL: "Use SSL",
            communicationRules: "Communication Service Rules",
            communicationRulesHint: "Choose a default app to be used when calling contacts from this account",
            choose: "Choose...",
            optional: "Optional"
          };

    const setField = (fieldKey, value) => updateSectionField("contacts", payloadIndex, fieldKey, value);
    const fieldValue = (fieldKey, defaultValue = "") => payloadItem?.[fieldKey] ?? defaultValue;
    const boolValue = (fieldKey, defaultValue = false) =>
      payloadItem?.[fieldKey] === undefined ? defaultValue : Boolean(payloadItem?.[fieldKey]);

    const renderTextField = (fieldKey, label, hint, placeholder = "", type = "text") => (
      <div className="profile-eas-field">
        <label>{label}</label>
        {hint && <p>{hint}</p>}
        <input
          className="text-input"
          type={type}
          value={String(fieldValue(fieldKey))}
          placeholder={placeholder}
          onChange={(event) => setField(fieldKey, event.target.value)}
          readOnly={readOnly}
        />
      </div>
    );

    return (
      <div className="profile-eas-form">
        {renderTextField("accountDescription", copy.accountDescription, copy.accountDescriptionHint, "", "text")}

        <div className="profile-eas-field">
          <label>{copy.hostAndPort}</label>
          <p>{copy.hostAndPortHint}</p>
          <div className="profile-carddav-host-row">
            <input
              className="text-input"
              type="text"
              value={String(fieldValue("hostName"))}
              placeholder="[required]"
              onChange={(event) => setField("hostName", event.target.value)}
              readOnly={readOnly}
            />
            <span>:</span>
            <input
              className="text-input"
              type="number"
              value={String(fieldValue("port", 443))}
              placeholder="443"
              min="1"
              max="65535"
              onChange={(event) => setField("port", event.target.value)}
              readOnly={readOnly}
            />
          </div>
        </div>

        {renderTextField("principalURL", copy.principalURL, copy.principalURLHint, "[optional]")}
        {renderTextField("username", copy.userName, copy.userNameHint, "[set on device]")}
        {renderTextField("password", copy.password, copy.passwordHint, "[optional]", "password")}

        <label className="profile-eas-check">
          <input
            type="checkbox"
            checked={boolValue("useSSL", true)}
            onChange={(event) => setField("useSSL", event.target.checked)}
            disabled={readOnly}
          />
          <span>
            <strong>{copy.useSSL}</strong>
          </span>
        </label>

        <div className="profile-eas-field">
          <label>{copy.communicationRules}</label>
          <p>{copy.communicationRulesHint}</p>
          <div className="profile-eas-app-rule">
            <button type="button" className="secondary" disabled>
              {copy.choose}
            </button>
            <input
              className="text-input"
              value={String(fieldValue("communicationServiceAppBundleID"))}
              placeholder={copy.optional}
              onChange={(event) => setField("communicationServiceAppBundleID", event.target.value)}
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>
    );
  }

  function renderGoogleAccountPayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const copy =
      lang === "ru"
        ? {
            accountDescription: "Описание учетной записи",
            accountDescriptionHint: "Отображаемое имя учетной записи",
            accountName: "Имя учетной записи",
            accountNameHint: "Полное имя пользователя для этой учетной записи",
            emailAddress: "Email-адрес",
            emailAddressHint: "Email-адрес Google для этой учетной записи",
            communicationRules: "Правила сервиса связи",
            communicationRulesHint: "Выберите приложение по умолчанию для звонков контактам из этой учетной записи",
            choose: "Выбрать...",
            optional: "Необязательно"
          }
        : {
            accountDescription: "Account Description",
            accountDescriptionHint: "The display name of the account",
            accountName: "Account Name",
            accountNameHint: "The full name of the user for the account",
            emailAddress: "Email Address",
            emailAddressHint: "The Google email address of the account",
            communicationRules: "Communication Service Rules",
            communicationRulesHint: "Choose a default app to be used when calling contacts from this account",
            choose: "Choose...",
            optional: "Optional"
          };

    const setField = (fieldKey, value) =>
      setProfileSectionPayloadField(target, "google_account", payloadIndex, fieldKey, value);
    const fieldValue = (fieldKey) => String(payloadItem?.[fieldKey] ?? "");

    const renderTextField = (fieldKey, label, hint, placeholder = "") => (
      <div className="profile-eas-field">
        <label>{label}</label>
        {hint && <p>{hint}</p>}
        <input
          className="text-input"
          type="text"
          value={fieldValue(fieldKey)}
          placeholder={placeholder}
          onChange={(event) => setField(fieldKey, event.target.value)}
          readOnly={readOnly}
        />
      </div>
    );

    return (
      <div className="profile-eas-form">
        {renderTextField("accountDescription", copy.accountDescription, copy.accountDescriptionHint, "[optional]")}
        {renderTextField("accountName", copy.accountName, copy.accountNameHint, "[optional]")}
        {renderTextField("emailAddress", copy.emailAddress, copy.emailAddressHint, "[required]")}

        <div className="profile-eas-field">
          <label>{copy.communicationRules}</label>
          <p>{copy.communicationRulesHint}</p>
          <div className="profile-eas-app-rule">
            <button
              type="button"
              className="secondary"
              onClick={() =>
                openCommunicationServiceAppPicker(
                  target,
                  "google_account",
                  payloadIndex,
                  "communicationServiceAppBundleID"
                )
              }
              disabled={readOnly}
            >
              {copy.choose}
            </button>
            <span
              className={
                fieldValue("communicationServiceAppBundleID")
                  ? "profile-eas-app-rule-value has-value"
                  : "profile-eas-app-rule-value"
              }
            >
              {fieldValue("communicationServiceAppBundleID") || copy.optional}
            </span>
            {fieldValue("communicationServiceAppBundleID") && !readOnly && (
              <button
                type="button"
                className="secondary profile-eas-clear-app"
                onClick={() => setField("communicationServiceAppBundleID", "")}
                aria-label={lang === "ru" ? "Очистить выбранное приложение" : "Clear selected app"}
              >
                -
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderLDAPPayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const copy =
      lang === "ru"
        ? {
            accountDescription: "Описание учетной записи",
            accountDescriptionHint: "Отображаемое имя учетной записи, например \"Company LDAP Account\"",
            userName: "Имя пользователя учетной записи",
            userNameHint: "Имя пользователя этой LDAP-учетной записи",
            password: "Пароль учетной записи",
            passwordHint: "Пароль этой LDAP-учетной записи",
            hostName: "Имя хоста учетной записи",
            hostNameHint: "Имя хоста LDAP или IP-адрес",
            useSSL: "Использовать SSL",
            searchSettings: "Настройки поиска",
            searchSettingsHint: "Настройки поиска для этого LDAP-сервера",
            description: "Описание",
            scope: "Scope",
            searchBase: "Search Base"
          }
        : {
            accountDescription: "Account Description",
            accountDescriptionHint: "The display name of the account (e.g. \"Company LDAP Account\")",
            userName: "Account User Name",
            userNameHint: "The user name for this LDAP account",
            password: "Account Password",
            passwordHint: "The password for this LDAP account",
            hostName: "Account Host Name",
            hostNameHint: "The LDAP host name or IP address",
            useSSL: "Use SSL",
            searchSettings: "Search Settings",
            searchSettingsHint: "Search settings for this LDAP server",
            description: "Description",
            scope: "Scope",
            searchBase: "Search Base"
          };
    const scopeOptions = [
      { value: "LDAPSearchSettingScopeBase", label: "Base" },
      { value: "LDAPSearchSettingScopeOneLevel", label: "One Level" },
      { value: "LDAPSearchSettingScopeSubtree", label: "Subtree" }
    ];
    const searchSettings = normalizeLDAPSearchSettings(payloadItem?.searchSettings);
    const editableRows = searchSettings.length > 0 ? searchSettings : [blankLDAPSearchSetting()];
    const fillerRows = Math.max(0, 7 - editableRows.length);
    const setField = (fieldKey, value) => setProfileSectionPayloadField(target, "ldap", payloadIndex, fieldKey, value);
    const fieldValue = (fieldKey) => String(payloadItem?.[fieldKey] ?? "");
    const boolValue = (fieldKey, defaultValue = false) =>
      payloadItem?.[fieldKey] === undefined ? defaultValue : Boolean(payloadItem?.[fieldKey]);

    const setSearchSettings = (nextRows) => {
      setField(
        "searchSettings",
        nextRows.filter((row) => row.description || row.searchBase)
      );
    };

    const updateSearchSetting = (rowIndex, fieldKey, value) => {
      const nextRows = editableRows.map((row, index) =>
        index === rowIndex ? { ...row, [fieldKey]: value } : row
      );
      setSearchSettings(nextRows);
    };

    const renderTextField = (fieldKey, label, hint, placeholder = "", type = "text") => (
      <div className="profile-eas-field">
        <label>{label}</label>
        {hint && <p>{hint}</p>}
        <input
          className="text-input"
          type={type}
          value={fieldValue(fieldKey)}
          placeholder={placeholder}
          onChange={(event) => setField(fieldKey, event.target.value)}
          readOnly={readOnly}
        />
      </div>
    );

    return (
      <div className="profile-eas-form">
        {renderTextField("accountDescription", copy.accountDescription, copy.accountDescriptionHint, "[optional]")}
        {renderTextField("userName", copy.userName, copy.userNameHint, "[optional]")}
        {renderTextField("password", copy.password, copy.passwordHint, "[optional]", "password")}
        {renderTextField("hostName", copy.hostName, copy.hostNameHint, "[required]")}

        <label className="profile-eas-check">
          <input
            type="checkbox"
            checked={boolValue("useSSL", true)}
            onChange={(event) => setField("useSSL", event.target.checked)}
            disabled={readOnly}
          />
          <span>
            <strong>{copy.useSSL}</strong>
          </span>
        </label>

        <div className="profile-eas-field">
          <label>{copy.searchSettings}</label>
          <p>{copy.searchSettingsHint}</p>
          <div className="profile-ldap-search-table">
            <div className="profile-ldap-search-header">
              <span>{copy.description}</span>
              <span>{copy.scope}</span>
              <span>{copy.searchBase}</span>
            </div>
            {editableRows.map((row, rowIndex) => (
              <div className="profile-ldap-search-row" key={`ldap-search-${payloadIndex}-${rowIndex}`}>
                <input
                  className="profile-ldap-search-input"
                  value={row.description}
                  placeholder={rowIndex === 0 ? "My Search" : ""}
                  onChange={(event) => updateSearchSetting(rowIndex, "description", event.target.value)}
                  readOnly={readOnly}
                />
                <select
                  value={row.scope || "LDAPSearchSettingScopeSubtree"}
                  onChange={(event) => updateSearchSetting(rowIndex, "scope", event.target.value)}
                  disabled={readOnly}
                >
                  {scopeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  className="profile-ldap-search-input"
                  value={row.searchBase}
                  placeholder={rowIndex === 0 ? "o=My Company,ou=My Group" : ""}
                  onChange={(event) => updateSearchSetting(rowIndex, "searchBase", event.target.value)}
                  readOnly={readOnly}
                />
              </div>
            ))}
            {Array.from({ length: fillerRows }).map((_, rowIndex) => (
              <div className="profile-ldap-search-row profile-ldap-search-empty" key={`ldap-empty-${rowIndex}`}>
                <span />
                <span />
                <span />
              </div>
            ))}
          </div>
          {!readOnly && (
            <div className="profile-list-editor-actions">
              <button
                type="button"
                className="profile-list-square-btn"
                onClick={() => setSearchSettings([...searchSettings, blankLDAPSearchSetting()])}
              >
                +
              </button>
              <button
                type="button"
                className="profile-list-square-btn"
                onClick={() => setSearchSettings(searchSettings.slice(0, -1))}
                disabled={searchSettings.length === 0}
              >
                −
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderExchangeActiveSyncPayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const copy =
      lang === "ru"
        ? {
            accountName: "Название учетной записи",
            accountNameHint: "Название учетной записи Exchange ActiveSync",
            host: "Узел Exchange ActiveSync",
            hostHint: "Сервер Microsoft Exchange",
            useSSL: "Использовать SSL",
            user: "Пользователь",
            userHint: "Пользователь учетной записи с необязательным доменом, например user или domain\\user",
            email: "Email-адрес",
            emailHint: "Адрес учетной записи, например john@example.com",
            oauth: "Использовать OAuth для аутентификации",
            password: "Пароль",
            passwordHint: "Пароль учетной записи",
            overridePassword: "Заменить предыдущий пароль",
            mailDays: "Синхронизировать почту за период",
            credentialName: "Название учетных данных аутентификации",
            credentialNameHint: "Название или описание учетных данных для ActiveSync",
            credential: "Учетные данные аутентификации",
            credentialHint: "Учетные данные для аутентификации учетной записи ActiveSync",
            noValue: "Нет значения",
            allowMove: "Разрешить перемещение сообщений",
            allowMoveHint: "Сообщения можно перемещать из этой учетной записи в другую",
            allowRecents: "Разрешить синхронизацию недавних адресов",
            allowRecentsHint: "Включить эту учетную запись в синхронизацию недавних адресов",
            allowMailDrop: "Разрешить Mail Drop",
            allowMailDropHint: "Разрешить Mail Drop для этой учетной записи",
            useOnlyInMail: "Использовать только в Mail",
            useOnlyInMailHint: "Отправлять исходящую почту из этой учетной записи только из приложения Mail",
            smimeSigning: "Включить подпись S/MIME",
            smimeSigningToggle: "Разрешить пользователю включать или отключать подпись S/MIME",
            smimeEncryption: "Включить шифрование S/MIME по умолчанию",
            smimeEncryptionToggle: "Разрешить пользователю включать или отключать шифрование S/MIME",
            smimePerMessage: "Включить переключатель шифрования для каждого сообщения",
            enabledServices: "Включенные сервисы",
            enabledServicesHint: "Включенные сервисы для этой учетной записи. Должен быть включен хотя бы один.",
            accountModification: "Изменение учетной записи",
            accountModificationHint: "Разрешить пользователям изменять состояние следующих сервисов",
            communicationRules: "Правила сервиса связи",
            communicationRulesHint: "Выберите приложение по умолчанию для звонков контактам из этой учетной записи",
            choose: "Выбрать...",
            optional: "Необязательно"
          }
        : {
            accountName: "Account Name",
            accountNameHint: "Name for the Exchange ActiveSync account",
            host: "Exchange ActiveSync Host",
            hostHint: "Microsoft Exchange Server",
            useSSL: "Use SSL",
            user: "User",
            userHint: "The user of the account with optional domain (e.g. \"user\" or \"domain\\user\")",
            email: "Email Address",
            emailHint: "The address of the account (e.g. \"john@example.com\")",
            oauth: "Use OAuth for authentication",
            password: "Password",
            passwordHint: "The password for the account (e.g. \"MyP4ssw0rd!\")",
            overridePassword: "Override previous password",
            mailDays: "Past Days of Mail to Sync",
            credentialName: "Authentication Credential Name",
            credentialNameHint: "Name or description for ActiveSync",
            credential: "Authentication Credential",
            credentialHint: "Credential for authenticating the ActiveSync account",
            noValue: "No Value",
            allowMove: "Allow messages to be moved",
            allowMoveHint: "Messages can be moved from this account to another",
            allowRecents: "Allow recent addresses to be synced",
            allowRecentsHint: "Include this account in recent address syncing",
            allowMailDrop: "Allow Mail Drop",
            allowMailDropHint: "Allow Mail Drop for this account",
            useOnlyInMail: "Use only in Mail",
            useOnlyInMailHint: "Send outgoing mail from this account only from Mail app",
            smimeSigning: "Enable S/MIME signing",
            smimeSigningToggle: "Allow user to enable or disable S/MIME signing",
            smimeEncryption: "Enable S/MIME encryption by default",
            smimeEncryptionToggle: "Allow user to enable or disable S/MIME encryption",
            smimePerMessage: "Enable per-message encryption switch",
            enabledServices: "Enabled Services",
            enabledServicesHint: "Enabled services for this account. At least one of them should be enabled",
            accountModification: "Account Modification",
            accountModificationHint: "Allow users to modify the state of the following services",
            communicationRules: "Communication Service Rules",
            communicationRulesHint: "Choose a default app to be used when calling contacts from this account",
            choose: "Choose...",
            optional: "Optional"
          };

    const mailDayOptions = APPLE_PROFILE_SECTION_SCHEMAS.exchange_active_sync.fields
      .find((field) => field.key === "mailDaysToSync")?.options || [];
    const services = [
      { label: "Mail", disableKey: "disableMailSync", modifyKey: "allowModifyMail" },
      { label: lang === "ru" ? "Контакты" : "Contacts", disableKey: "disableContactsSync", modifyKey: "allowModifyContacts" },
      { label: lang === "ru" ? "Календари" : "Calendars", disableKey: "disableCalendarSync", modifyKey: "allowModifyCalendars" },
      { label: lang === "ru" ? "Напоминания" : "Reminders", disableKey: "disableRemindersSync", modifyKey: "allowModifyReminders" },
      { label: lang === "ru" ? "Заметки" : "Notes", disableKey: "disableNotesSync", modifyKey: "allowModifyNotes" }
    ];
    const setField = (fieldKey, value) => setExchangeActiveSyncField(target, payloadIndex, fieldKey, value);
    const fieldValue = (fieldKey, defaultValue = "") => payloadItem?.[fieldKey] ?? defaultValue;
    const boolValue = (fieldKey, defaultValue = false) =>
      payloadItem?.[fieldKey] === undefined ? defaultValue : Boolean(payloadItem?.[fieldKey]);

    const renderTextField = (fieldKey, label, hint, placeholder = "", type = "text") => (
      <div className="profile-eas-field">
        <label>{label}</label>
        {hint && <p>{hint}</p>}
        <input
          className="text-input"
          type={type}
          value={String(fieldValue(fieldKey))}
          placeholder={placeholder}
          onChange={(event) => setField(fieldKey, event.target.value)}
          readOnly={readOnly}
        />
      </div>
    );

    const renderCheckbox = (label, hint, checked, onChange, key) => (
      <label className="profile-eas-check" key={key || label}>
        <input
          type="checkbox"
          checked={Boolean(checked)}
          onChange={(event) => onChange(event.target.checked)}
          disabled={readOnly}
        />
        <span>
          <strong>{label}</strong>
          {hint && <p>{hint}</p>}
        </span>
      </label>
    );

    return (
      <div className="profile-eas-form">
        {renderTextField("accountName", copy.accountName, copy.accountNameHint, "Exchange ActiveSync")}
        {renderTextField("host", copy.host, copy.hostHint, "[required]")}
        {renderCheckbox(copy.useSSL, "", boolValue("ssl", true), (next) => setField("ssl", next), "ssl")}
        {renderTextField("userName", copy.user, copy.userHint, "[set on device]")}
        {renderTextField("emailAddress", copy.email, copy.emailHint, "[defaults to username@host]")}
        {renderCheckbox(copy.oauth, "", boolValue("oauth", false), (next) => setField("oauth", next), "oauth")}
        {renderTextField("password", copy.password, copy.passwordHint, "", "password")}
        {renderCheckbox(
          copy.overridePassword,
          "",
          boolValue("overridePreviousPassword", false),
          (next) => setField("overridePreviousPassword", next),
          "overridePreviousPassword"
        )}
        <div className="profile-eas-field profile-eas-select-short">
          <label>{copy.mailDays}</label>
          <select
            className="text-input"
            value={String(fieldValue("mailDaysToSync", 7))}
            onChange={(event) => setField("mailDaysToSync", event.target.value)}
            disabled={readOnly}
          >
            {mailDayOptions.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {sectionFieldOptionLabel(option)}
              </option>
            ))}
          </select>
        </div>
        {renderTextField("authenticationCredentialName", copy.credentialName, copy.credentialNameHint)}
        <div className="profile-eas-field profile-eas-select-short">
          <label>{copy.credential}</label>
          <p>{copy.credentialHint}</p>
          <select className="text-input" value="" disabled>
            <option value="">{copy.noValue}</option>
          </select>
        </div>

        <div className="profile-eas-check-list">
          {renderCheckbox(copy.allowMove, copy.allowMoveHint, !boolValue("preventMove", false), (next) => setField("preventMove", !next), "preventMove")}
          {renderCheckbox(copy.allowRecents, copy.allowRecentsHint, !boolValue("disableMailRecentsSyncing", false), (next) => setField("disableMailRecentsSyncing", !next), "disableMailRecentsSyncing")}
          {renderCheckbox(copy.allowMailDrop, copy.allowMailDropHint, boolValue("allowMailDrop", false), (next) => setField("allowMailDrop", next), "allowMailDrop")}
          {renderCheckbox(copy.useOnlyInMail, copy.useOnlyInMailHint, boolValue("preventAppSheet", false), (next) => setField("preventAppSheet", next), "preventAppSheet")}
          {renderCheckbox(copy.smimeSigning, "", boolValue("smimeSigningEnabled", false), (next) => setField("smimeSigningEnabled", next), "smimeSigningEnabled")}
          {renderCheckbox(copy.smimeSigningToggle, "", boolValue("smimeSigningUserOverrideable", false), (next) => setField("smimeSigningUserOverrideable", next), "smimeSigningUserOverrideable")}
          {renderCheckbox(copy.smimeEncryption, "", boolValue("smimeEncryptByDefault", false), (next) => setField("smimeEncryptByDefault", next), "smimeEncryptByDefault")}
          {renderCheckbox(copy.smimeEncryptionToggle, "", boolValue("smimeEncryptionUserOverrideable", false), (next) => setField("smimeEncryptionUserOverrideable", next), "smimeEncryptionUserOverrideable")}
          {renderCheckbox(copy.smimePerMessage, "", boolValue("smimeEnablePerMessageSwitch", false), (next) => setField("smimeEnablePerMessageSwitch", next), "smimeEnablePerMessageSwitch")}
        </div>

        <div className="profile-eas-service-section">
          <h4>{copy.enabledServices}</h4>
          <p>{copy.enabledServicesHint}</p>
          <div className="profile-eas-service-grid">
            {services.map((service) =>
              renderCheckbox(
                service.label,
                "",
                !boolValue(service.disableKey, false),
                (next) => setField(service.disableKey, !next),
                `enabled-${service.disableKey}`
              )
            )}
          </div>
        </div>

        <div className="profile-eas-service-section">
          <h4>{copy.accountModification}</h4>
          <p>{copy.accountModificationHint}</p>
          <div className="profile-eas-service-grid">
            {services.map((service) =>
              renderCheckbox(
                service.label,
                "",
                boolValue(service.modifyKey, true),
                (next) => setField(service.modifyKey, next),
                `modify-${service.modifyKey}`
              )
            )}
          </div>
        </div>

        <div className="profile-eas-field">
          <label>{copy.communicationRules}</label>
          <p>{copy.communicationRulesHint}</p>
          <div className="profile-eas-app-rule">
            <button type="button" className="secondary" disabled>
              {copy.choose}
            </button>
            <input
              className="text-input"
              value={String(fieldValue("communicationServiceAppBundleID"))}
              placeholder={copy.optional}
              onChange={(event) => setField("communicationServiceAppBundleID", event.target.value)}
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>
    );
  }

  function renderConfiguratorTextField(setField, payloadItem, fieldKey, label, hint, placeholder = "", readOnly = false, type = "text", className = "") {
    return (
      <div className={`profile-configurator-field ${className}`.trim()}>
        <label>{label}</label>
        {hint && <p>{hint}</p>}
        <input
          className="text-input"
          type={type}
          value={String(payloadItem?.[fieldKey] ?? "")}
          placeholder={placeholder}
          onChange={(event) => setField(fieldKey, event.target.value)}
          readOnly={readOnly}
        />
      </div>
    );
  }

  function renderConfiguratorCheckbox(setField, payloadItem, fieldKey, label, hint, defaultValue = false, readOnly = false) {
    const checked = payloadItem?.[fieldKey] === undefined ? defaultValue : Boolean(payloadItem?.[fieldKey]);
    return (
      <label className="profile-configurator-check">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => setField(fieldKey, event.target.checked)}
          disabled={readOnly}
        />
        <span>
          <strong>{label}</strong>
          {hint && <p>{hint}</p>}
        </span>
      </label>
    );
  }

  function renderConfiguratorListEditor(setField, payloadItem, fieldKey, label, hint, readOnly = false) {
    const rows = sectionArrayItems(payloadItem?.[fieldKey]);
    const visibleRows = Math.max(5, rows.length);
    const setRows = (nextRows) => setField(fieldKey, sectionArrayValue(nextRows));
    return (
      <div className="profile-configurator-field profile-configurator-list">
        <label>{label}</label>
        {hint && <p>{hint}</p>}
        <div className="profile-configurator-table">
          {Array.from({ length: visibleRows }).map((_, rowIndex) => (
            <input
              key={`${fieldKey}-${rowIndex}`}
              className="profile-configurator-table-input"
              value={rows[rowIndex] ?? ""}
              onChange={(event) => {
                const next = [...rows];
                next[rowIndex] = event.target.value;
                setRows(next);
              }}
              readOnly={readOnly}
            />
          ))}
        </div>
        {!readOnly && (
          <div className="profile-list-editor-actions">
            <button type="button" className="profile-list-square-btn" onClick={() => setRows([...rows, ""])}>
              +
            </button>
            <button
              type="button"
              className="profile-list-square-btn"
              onClick={() => setRows(rows.slice(0, -1))}
              disabled={rows.length === 0}
            >
              −
            </button>
          </div>
        )}
      </div>
    );
  }

  function renderWebClipPayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const setField = (fieldKey, value) => setProfileSectionPayloadField(target, "web_clips", payloadIndex, fieldKey, value);
    const iconInputID = `webclip-icon-${target}-${payloadIndex}`;
    return (
      <div className="profile-configurator-form">
        {renderConfiguratorTextField(setField, payloadItem, "label", "Label", "The name to display for the Web Clip", "[required]", readOnly)}
        {renderConfiguratorTextField(setField, payloadItem, "url", "URL", "The URL to be displayed when opening the Web Clip", "[required]", readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "isRemovable", "Removable", "Enable removal of the Web Clip", true, readOnly)}
        <div className="profile-configurator-field">
          <label>Icon</label>
          <p>The icon to use for the Web Clip</p>
          <div className="profile-webclip-icon-preview" />
          {!readOnly && (
            <>
              <input
                id={iconInputID}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  try {
                    setField("icon", await fileToBase64(file));
                  } catch (err) {
                    setErrorMessage(err.message || "Failed to read icon");
                  } finally {
                    event.target.value = "";
                  }
                }}
              />
              <button type="button" className="secondary profile-configurator-choose" onClick={() => document.getElementById(iconInputID)?.click()}>
                Choose...
              </button>
            </>
          )}
        </div>
        {renderConfiguratorCheckbox(setField, payloadItem, "precomposed", "Precomposed Icon", "The icon will be displayed with no added visual effects", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "fullScreen", "Full Screen", "Displays the web clip as a full screen application", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "ignoreManifestScope", "Ignore Manifest Scope", "Allow web pages that are not in the manifest to load in full screen mode", false, readOnly)}
      </div>
    );
  }

  function renderLockScreenMessagePayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const setField = (fieldKey, value) => setProfileSectionPayloadField(target, "lock_screen_message", payloadIndex, fieldKey, value);
    return (
      <div className="profile-configurator-form">
        {renderConfiguratorTextField(setField, payloadItem, "lockScreenMessage", "\"If Lost, Return to...\" Message", "Message displayed on the login window and lock screen", "[optional]", readOnly)}
        {renderConfiguratorTextField(setField, payloadItem, "assetTagInformation", "Asset Tag Information", "Message displayed at the bottom of the login window and lock screen", "[optional]", readOnly)}
      </div>
    );
  }

  function renderTVRemotePayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const setField = (fieldKey, value) => setProfileSectionPayloadField(target, "tv_remote", payloadIndex, fieldKey, value);
    return (
      <div className="profile-configurator-form">
        {renderConfiguratorListEditor(setField, payloadItem, "allowedRemotes", "Allowed Remotes (tvOS only)", "Add the MAC addresses of permitted iOS devices to this list to enable remote control with only these devices", readOnly)}
        {renderConfiguratorListEditor(setField, payloadItem, "allowedTVs", "Allowed TVs (iOS only)", "Add the MAC addresses of permitted tvOS devices to this list to restrict remote control to only these devices", readOnly)}
      </div>
    );
  }

  function renderSCEPPayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const setField = (fieldKey, value) => setProfileSectionPayloadField(target, "scep", payloadIndex, fieldKey, value);
    const setSANField = (fieldKey, value) => {
      const next = { ...(payloadItem || {}), [fieldKey]: value };
      const sanLines = [];
      const sanType = String(next.subjectAltNameType || "None");
      const sanValue = String(next.subjectAltNameValue || "").trim();
      const ntPrincipalName = String(next.ntPrincipalName || "").trim();
      if (sanValue && sanType === "RFC 822 Name") sanLines.push(`EMAIL:${sanValue}`);
      if (sanValue && sanType === "DNS Name") sanLines.push(`DNS:${sanValue}`);
      if (sanValue && sanType === "Uniform Resource Identifier") sanLines.push(`URI:${sanValue}`);
      if (ntPrincipalName) sanLines.push(`NT:${ntPrincipalName}`);
      setField(fieldKey, value);
      setField("subjectAltName", sanLines.join("\n"));
    };
    return (
      <div className="profile-configurator-form">
        {renderConfiguratorTextField(setField, payloadItem, "url", "URL", "The base URL for the SCEP server", "[required]", readOnly)}
        {renderConfiguratorTextField(setField, payloadItem, "name", "Name", "The name of the instance: CA-IDENT", "[optional]", readOnly)}
        {renderConfiguratorTextField(setField, payloadItem, "subject", "Subject", "Representation of a X.500 name", "[optional] e.g. O=Company Name/CN=Foo", readOnly)}
        <div className="profile-configurator-field profile-configurator-select-short">
          <label>Subject Alternative Name Type</label>
          <p>The type of a subject alternative name</p>
          <select
            className="text-input"
            value={String(payloadItem?.subjectAltNameType || "None")}
            onChange={(event) => setSANField("subjectAltNameType", event.target.value)}
            disabled={readOnly}
          >
            {["None", "RFC 822 Name", "DNS Name", "Uniform Resource Identifier"].map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        {renderConfiguratorTextField(setSANField, payloadItem, "subjectAltNameValue", "Subject Alternative Name Value", "The value of a subject alternative name", "", readOnly)}
        {renderConfiguratorTextField(setSANField, payloadItem, "ntPrincipalName", "NT Principal Name", "An NT principal name for use in the certificate request", "[optional]", readOnly)}
        {renderConfiguratorTextField(setField, payloadItem, "retries", "Retries", "The number of times to poll the SCEP server for a signed certificate before giving up", "", readOnly, "number", "profile-configurator-number")}
        {renderConfiguratorTextField(setField, payloadItem, "retryDelay", "Retry Delay", "The number of seconds to wait between poll attempts", "", readOnly, "number", "profile-configurator-number")}
        {renderConfiguratorTextField(setField, payloadItem, "challenge", "Challenge", "Used as the pre-shared secret for automatic enrollment", "[optional]", readOnly, "password")}
        <div className="profile-configurator-field profile-configurator-select-short">
          <label>Key Size</label>
          <p>Keysize in bits. 4096 is not supported in iOS 13, iPadOS 13 or tvOS 13 and earlier.</p>
          <select
            className="text-input"
            value={String(payloadItem?.keysize || 1024)}
            onChange={(event) => setField("keysize", event.target.value)}
            disabled={readOnly}
          >
            {[1024, 2048, 4096].map((option) => (
              <option key={option} value={String(option)}>{option}</option>
            ))}
          </select>
        </div>
        {renderConfiguratorCheckbox(setField, payloadItem, "useAsDigitalSignature", "Use as digital signature", "", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "useForKeyEncipherment", "Use for key encipherment", "", false, readOnly)}
        {renderConfiguratorTextField(setField, payloadItem, "caFingerprint", "Fingerprint", "Enter hex string to be used as a fingerprint or use button to create fingerprint from Certificate", "", readOnly)}
        {!readOnly && <button type="button" className="secondary profile-configurator-align-right" disabled>Create from Certificate...</button>}
      </div>
    );
  }

  function renderMailPayloadFields(payloadItem, payloadIndex, target, readOnly = false) {
    const setField = (fieldKey, value) => setProfileSectionPayloadField(target, "mail", payloadIndex, fieldKey, value);
    const activeTab = String(payloadItem?.mailServerTab || "incoming");
    const authOptions = [
      ["EmailAuthNone", "None"],
      ["EmailAuthPassword", "Password"],
      ["EmailAuthCRAMMD5", "MD5 Challenge-Response"],
      ["EmailAuthNTLM", "NTLM"],
      ["EmailAuthHTTPMD5", "HTTP MD5 Digest"]
    ];
    const renderServerFields = (mode) => {
      const isIncoming = mode === "incoming";
      const hostKey = isIncoming ? "incomingHost" : "outgoingHost";
      const portKey = isIncoming ? "incomingPort" : "outgoingPort";
      const userKey = isIncoming ? "incomingUsername" : "outgoingUsername";
      const authKey = isIncoming ? "incomingAuth" : "outgoingAuth";
      const passwordKey = isIncoming ? "incomingPassword" : "outgoingPassword";
      const sslKey = isIncoming ? "incomingUseSSL" : "outgoingUseSSL";
      return (
        <>
          <div className="profile-configurator-field">
            <label>Mail Server and Port</label>
            <p>Host name or IP address, and port number for {isIncoming ? "incoming" : "outgoing"} mail</p>
            <div className="profile-mail-host-row">
              <input
                className="text-input"
                value={String(payloadItem?.[hostKey] || "")}
                placeholder="[required]"
                onChange={(event) => setField(hostKey, event.target.value)}
                readOnly={readOnly}
              />
              <span>:</span>
              <input
                className="text-input"
                type="number"
                value={String(payloadItem?.[portKey] || (isIncoming ? 993 : 587))}
                onChange={(event) => setField(portKey, event.target.value)}
                readOnly={readOnly}
              />
            </div>
          </div>
          {renderConfiguratorTextField(setField, payloadItem, userKey, "User Name", `The user name used to connect to the server for ${isIncoming ? "incoming" : "outgoing"} mail`, "[set on device]", readOnly)}
          <div className="profile-configurator-field profile-configurator-select-short">
            <label>Authentication Type</label>
            <p>The authentication method for the {isIncoming ? "incoming" : "outgoing"} mail server</p>
            <select
              className="text-input"
              value={String(payloadItem?.[authKey] || "EmailAuthPassword")}
              onChange={(event) => setField(authKey, event.target.value)}
              disabled={readOnly}
            >
              {authOptions.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          {renderConfiguratorTextField(setField, payloadItem, passwordKey, "Password", `The password for the ${isIncoming ? "incoming" : "outgoing"} mail server`, "", readOnly, "password")}
          {!isIncoming && renderConfiguratorCheckbox(setField, payloadItem, "outgoingPasswordSameAsIncoming", "Outgoing password same as incoming", "SMTP authentication uses the same password as POP/IMAP", true, readOnly)}
          {renderConfiguratorCheckbox(setField, payloadItem, sslKey, "Use SSL", `${isIncoming ? "Retrieve incoming" : "Send outgoing"} mail through secure socket layer`, true, readOnly)}
        </>
      );
    };
    return (
      <div className="profile-configurator-form">
        {renderConfiguratorTextField(setField, payloadItem, "emailAccountDescription", "Account Description", "The display name of the account (e.g. \"Company Mail Account\")", "[optional]", readOnly)}
        <div className="profile-configurator-field">
          <label>Account Type</label>
          <p>The protocol for accessing the email account</p>
          <div className="profile-mail-account-row">
            <select
              className="text-input"
              value={String(payloadItem?.accountType || "IMAP")}
              onChange={(event) => setField("accountType", event.target.value)}
              disabled={readOnly}
            >
              <option value="IMAP">IMAP</option>
              <option value="POP">POP</option>
            </select>
            <span>Path Prefix:</span>
            <input
              className="text-input"
              value={String(payloadItem?.incomingImapPath || "")}
              placeholder="[optional]"
              onChange={(event) => setField("incomingImapPath", event.target.value)}
              readOnly={readOnly}
            />
          </div>
        </div>
        {renderConfiguratorTextField(setField, payloadItem, "emailAccountName", "User Display Name", "The display name of the user (e.g. \"John Appleseed\")", "[optional]", readOnly)}
        {renderConfiguratorTextField(setField, payloadItem, "emailAddress", "Email Address", "The address of the account (e.g. \"john@example.com\")", "[set on device]", readOnly)}
        <label className="profile-configurator-check">
          <input
            type="checkbox"
            checked={!Boolean(payloadItem?.preventMove)}
            onChange={(event) => setField("preventMove", !event.target.checked)}
            disabled={readOnly}
          />
          <span>
            <strong>Allow user to move messages from this account</strong>
            <p>Messages can be moved from this account to another</p>
          </span>
        </label>
        <label className="profile-configurator-check">
          <input
            type="checkbox"
            checked={!Boolean(payloadItem?.disableMailRecentsSyncing)}
            onChange={(event) => setField("disableMailRecentsSyncing", !event.target.checked)}
            disabled={readOnly}
          />
          <span>
            <strong>Allow recent addresses to be synced</strong>
            <p>Include this account in recent address syncing</p>
          </span>
        </label>
        {renderConfiguratorCheckbox(setField, payloadItem, "allowMailDrop", "Allow Mail Drop", "Allow Mail Drop for this account", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "preventAppSheet", "Use only in Mail", "Send outgoing mail from this account only from Mail app", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "smimeSigningEnabled", "Enable S/MIME signing", "", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "smimeSigningUserOverrideable", "Allow user to enable or disable S/MIME signing", "", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "smimeEncryptByDefault", "Enable S/MIME encryption by default", "", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "smimeEncryptionUserOverrideable", "Allow user to enable or disable S/MIME encryption", "", false, readOnly)}
        {renderConfiguratorCheckbox(setField, payloadItem, "smimeEnablePerMessageSwitch", "Enable per-message encryption switch", "", false, readOnly)}
        <div className="profile-configurator-tabs">
          <button type="button" className={activeTab === "incoming" ? "active" : ""} onClick={() => setField("mailServerTab", "incoming")}>Incoming Mail</button>
          <button type="button" className={activeTab === "outgoing" ? "active" : ""} onClick={() => setField("mailServerTab", "outgoing")}>Outgoing Mail</button>
        </div>
        {renderServerFields(activeTab)}
      </div>
    );
  }

  function renderProfileBuilderModal(target = "create") {
    const isView = target === "view";
    const form = isView ? profileViewForm : profileCreateForm;
    const setForm = isView ? setProfileViewForm : setProfileCreateForm;
    const currentSection = isView ? profileViewSection : profileCreateSection;
    const setCurrentSection = isView ? setProfileViewSection : setProfileCreateSection;
    const currentRestrictionsTab = isView ? profileViewRestrictionsTab : profileCreateRestrictionsTab;
    const setCurrentRestrictionsTab = isView ? setProfileViewRestrictionsTab : setProfileCreateRestrictionsTab;
    const configuredSections = isView ? profileViewConfiguredSections : profileConfiguredSections;
    const wifiNetworks = isView ? profileViewWiFiNetworks : profileCreateWiFiNetworks;
    const sectionPayloads = isView ? profileViewSectionPayloads : profileCreateSectionPayloads;
    const readOnly = isView && !profileViewIsEditing;
    const builderPlatform = normalizeProfilePlatform(form.platform);
    const visibleBuilderSections = profileBuilderSectionsForPlatform(builderPlatform);
    const activeSection = visibleBuilderSections.some((section) => section.key === currentSection)
      ? currentSection
      : "general";
    const sectionMeta = visibleBuilderSections.find((item) => item.key === activeSection);
    const sectionLabel = profileSectionDisplayLabel(sectionMeta, builderPlatform) || activeSection;
    const sectionMinOS = profileBuilderMinOSForPlatform(PROFILE_BUILDER_SECTION_MIN_OS[sectionMeta?.key], builderPlatform);
    const sectionTitle = profileSectionDisplayTitle(activeSection, sectionMeta?.label || activeSection);
    const sectionHintRaw = profileSectionDisplayHint(activeSection);
    const sectionHint = sectionHintRaw === "profileBuilderSectionHint" ? "" : sectionHintRaw;
    const sectionWarning = profileSectionDisplayWarning(activeSection);
    const configureLabel = lang === "ru" ? "Настроить" : "Configure";

    const setGeneralField = (key, value) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    };

    const toggleGroup = (groupID) => {
      if (readOnly) return;
      toggleProfileGroup(setForm, groupID);
    };

    const openCertificates = () => {
      if (readOnly) return;
      openCertificatesPicker(isView ? "view" : "create");
    };

    const configureCurrentSection = () => {
      if (readOnly) return;
      if (activeSection === "wifi") {
        configureWiFiSection(
          isView ? setProfileViewConfiguredSections : setProfileConfiguredSections,
          isView ? setProfileViewWiFiNetworks : setProfileCreateWiFiNetworks
        );
        return;
      }
      if (activeSection === "certificates") {
        openCertificates();
        return;
      }
      if (isView) configureViewSection(activeSection);
      else configureCreateSection(activeSection);
    };

    const addPayloadForSection = () => {
      if (readOnly) return;
      if (activeSection === "wifi") {
        if (isView) addViewWiFiPayload();
        else addCreateWiFiPayload();
        return;
      }
      if (activeSection === "certificates") {
        openCertificates();
        return;
      }
      if (isView) addViewSectionPayload(activeSection);
      else addCreateSectionPayload(activeSection);
    };

    const removePayloadForSection = () => {
      if (readOnly) return;
      if (activeSection === "wifi") {
        const payloads = Array.isArray(wifiNetworks) ? wifiNetworks : [];
        if (payloads.length === 0) return;
        if (isView) removeViewWiFiPayload(payloads.length - 1);
        else removeCreateWiFiPayload(payloads.length - 1);
        return;
      }
      if (activeSection === "certificates") {
        const certs = Array.isArray(sectionPayloads?.certificates) ? sectionPayloads.certificates : [];
        if (certs.length === 0) return;
        if (isView) removeViewSectionPayload("certificates", certs.length - 1);
        else removeCreateSectionPayload("certificates", certs.length - 1);
        return;
      }
      const payloads = Array.isArray(sectionPayloads?.[activeSection]) ? sectionPayloads[activeSection] : [];
      if (payloads.length === 0) return;
      if (isView) removeViewSectionPayload(activeSection, payloads.length - 1);
      else removeCreateSectionPayload(activeSection, payloads.length - 1);
    };

    const updateSectionField = (sectionKey, payloadIndex, fieldKey, value) => {
      if (readOnly) return;
      if (isView) updateViewSectionPayload(sectionKey, payloadIndex, fieldKey, value);
      else updateCreateSectionPayload(sectionKey, payloadIndex, fieldKey, value);
    };

    const updateRestrictionsVisibleBooleanFields = (payloadIndex, fields, checked) => {
      if (readOnly) return;
      const booleanKeys = fields
        .filter((field) => field?.type === "boolean" && field?.key)
        .map((field) => field.key);
      if (booleanKeys.length === 0) return;
      const setPayloads = isView ? setProfileViewSectionPayloads : setProfileCreateSectionPayloads;
      setPayloads((prev) => {
        const current = Array.isArray(prev?.restrictions) ? prev.restrictions : [];
        if (payloadIndex < 0 || payloadIndex >= current.length) return prev;
        return {
          ...prev,
          restrictions: current.map((payloadItem, index) => {
            if (index !== payloadIndex) return payloadItem;
            const nextPayload = { ...(payloadItem || {}) };
            booleanKeys.forEach((key) => {
              nextPayload[key] = checked;
            });
            return nextPayload;
          })
        };
      });
    };

    const renderGeneralSection = () => {
      const generatedIdentifierHint =
        lang === "ru" ? "Будет сгенерирован автоматически при сохранении" : "Generated automatically on save";
      const hasDate = form.autoRemove === "OnDate";
      const hasDays = form.autoRemove === "AfterDays";
      return (
        <div className="profile-builder-general">
          <div className="profile-builder-field">
            <label>{lang === "ru" ? "Название" : "Name"}</label>
            <input
              className="text-input"
              value={form.name || ""}
              onChange={(event) => setGeneralField("name", event.target.value)}
              readOnly={readOnly}
            />
          </div>
          <div className="profile-builder-field">
            <label>{lang === "ru" ? "Идентификатор" : "Identifier"}</label>
            <input
              className="text-input"
              value={isView ? form.identifier || "" : generatedIdentifierHint}
              readOnly
            />
          </div>
          <div className="profile-builder-field">
            <label>{lang === "ru" ? "Описание" : "Description"}</label>
            <textarea
              className="profile-builder-textarea profile-builder-textarea-compact"
              value={form.description || ""}
              onChange={(event) => setGeneralField("description", event.target.value)}
              readOnly={readOnly}
            />
          </div>
          <div className="profile-builder-field">
            <label>{lang === "ru" ? "Платформа" : "Platform"}</label>
            <select
              className="text-input"
              value={normalizeProfilePlatform(form.platform)}
              onChange={(event) => {
                const nextPlatform = normalizeProfilePlatform(event.target.value);
                setGeneralField("platform", nextPlatform);
                const nextSections = profileBuilderSectionsForPlatform(nextPlatform);
                if (!nextSections.some((section) => section.key === activeSection)) {
                  setCurrentSection("general");
                }
                const setConfigured = isView ? setProfileViewConfiguredSections : setProfileConfiguredSections;
                const setPayloads = isView ? setProfileViewSectionPayloads : setProfileCreateSectionPayloads;
                setConfigured((prev) => pruneProfileConfiguredSectionsForPlatform(prev, nextPlatform));
                setPayloads((prev) => pruneProfileSectionPayloadsForPlatform(prev, nextPlatform));
              }}
              disabled={readOnly || isView}
            >
              <option value="ios">{t("platformIOS")}</option>
              <option value="macos">{t("platformMacOS")}</option>
              <option value="tvos">tvOS</option>
              <option value="visionos">visionOS</option>
            </select>
          </div>
          <div className="profile-builder-field">
            <label>{lang === "ru" ? "Автоматически удалить профиль" : "Automatically Remove Profile"}</label>
            <select
              className="text-input"
              value={form.autoRemove || "Never"}
              onChange={(event) => setGeneralField("autoRemove", event.target.value)}
              disabled={readOnly}
            >
              <option value="Never">{lang === "ru" ? "Никогда" : "Never"}</option>
              <option value="OnDate">{lang === "ru" ? "В указанную дату" : "On Date"}</option>
              <option value="AfterDays">{lang === "ru" ? "Через N дней" : "After Days"}</option>
            </select>
          </div>
          {hasDate && (
            <div className="profile-builder-field">
              <label>{lang === "ru" ? "Дата удаления" : "Removal Date"}</label>
              <input
                className="text-input"
                type="date"
                value={form.removalDate || ""}
                onChange={(event) => setGeneralField("removalDate", event.target.value)}
                readOnly={readOnly}
              />
            </div>
          )}
          {hasDays && (
            <div className="profile-builder-field">
              <label>{lang === "ru" ? "Дней до удаления" : "Days before removal"}</label>
              <input
                className="text-input"
                type="number"
                min={1}
                value={form.removalDays || ""}
                onChange={(event) => setGeneralField("removalDays", event.target.value)}
                readOnly={readOnly}
              />
            </div>
          )}
          <div className="profile-builder-field">
            <label>{t("profilesGroups")}</label>
            <div className="check-list">
              {groups.map((group) => (
                <label key={group.id} className="check-inline">
                  <input
                    type="checkbox"
                    checked={(form.groupIds || []).includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                    disabled={readOnly}
                  />
                  {group.name}
                </label>
              ))}
              {groups.length === 0 && <p className="empty">{t("groupsNoItems")}</p>}
            </div>
          </div>
        </div>
      );
    };

    const renderWiFiSection = () => {
      const payloads = Array.isArray(wifiNetworks) ? wifiNetworks : [];
      if (payloads.length === 0) {
        return (
          <div className="profile-wifi-empty">
            <div className="profile-wifi-empty-card">
              {!readOnly && <button onClick={configureCurrentSection}>{configureLabel}</button>}
            </div>
          </div>
        );
      }
      return (
        <div className="profile-wifi-editor">
          <div className="profile-wifi-toolbar">
            <h4>{sectionTitle || "Wi-Fi"}</h4>
            {!readOnly && (
              <div className="profile-wifi-toolbar-actions">
                <button className="profile-wifi-circle-btn" type="button" onClick={removePayloadForSection}>
                  −
                </button>
                <button className="profile-wifi-circle-btn" type="button" onClick={addPayloadForSection}>
                  +
                </button>
              </div>
            )}
          </div>
          <div className="profile-wifi-payload-list">
            {payloads.map((network, index) => (
              <div className="profile-wifi-payload-card" key={network.id || `wifi-${index}`}>
                <div className="profile-wifi-payload-head">
                  <strong>Wi-Fi #{index + 1}</strong>
                </div>
                <div className="profile-builder-field">
                  <label>Service Set Identifier (SSID)</label>
                  <input
                    className="text-input"
                    value={network.ssid || ""}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "ssid", event.target.value)
                        : updateCreateWiFiPayload(index, "ssid", event.target.value)
                    }
                    readOnly={readOnly}
                    placeholder="[required]"
                  />
                </div>
                <label className="profile-wifi-check">
                  <input
                    type="checkbox"
                    checked={Boolean(network.hiddenNetwork)}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "hiddenNetwork", event.target.checked)
                        : updateCreateWiFiPayload(index, "hiddenNetwork", event.target.checked)
                    }
                    disabled={readOnly}
                  />
                  <span>
                    <span className="profile-checkbox-label">{lang === "ru" ? "Скрытая сеть" : "Hidden Network"}</span>
                    <small>{lang === "ru" ? "Включите, если целевая сеть скрыта или не транслирует имя" : "Enable if target network is not open or broadcasting"}</small>
                  </span>
                </label>
                <label className="profile-wifi-check">
                  <input
                    type="checkbox"
                    checked={Boolean(network.autoJoin)}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "autoJoin", event.target.checked)
                        : updateCreateWiFiPayload(index, "autoJoin", event.target.checked)
                    }
                    disabled={readOnly}
                  />
                  <span>
                    <span className="profile-checkbox-label">{lang === "ru" ? "Автоподключение" : "Auto Join"}</span>
                    <small>{lang === "ru" ? "Автоматически подключаться к этой Wi-Fi сети" : "Automatically join this wireless network"}</small>
                  </span>
                </label>
                <label className="profile-wifi-check">
                  <input
                    type="checkbox"
                    checked={Boolean(network.captiveBypass)}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "captiveBypass", event.target.checked)
                        : updateCreateWiFiPayload(index, "captiveBypass", event.target.checked)
                    }
                    disabled={readOnly}
                  />
                  <span>
                    <span className="profile-checkbox-label">{lang === "ru" ? "Отключить обнаружение captive network" : "Disable Captive Network Detection"}</span>
                    <small>{lang === "ru" ? "Не показывать помощник captive network" : "Do not show the captive network assistant"}</small>
                  </span>
                </label>
                <label className="profile-wifi-check">
                  <input
                    type="checkbox"
                    checked={Boolean(network.disableAssociationMACRandomization)}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "disableAssociationMACRandomization", event.target.checked)
                        : updateCreateWiFiPayload(index, "disableAssociationMACRandomization", event.target.checked)
                    }
                    disabled={readOnly}
                  />
                  <span>
                    <span className="profile-checkbox-label">{lang === "ru" ? "Отключить MAC randomization при подключении" : "Disable Association MAC Randomization"}</span>
                  </span>
                </label>
                <div className="profile-builder-field">
                  <label>{lang === "ru" ? "Настройка прокси" : "Proxy Setup"}</label>
                  <select
                    className="text-input"
                    value={network.proxyType || "None"}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "proxyType", event.target.value)
                        : updateCreateWiFiPayload(index, "proxyType", event.target.value)
                    }
                    disabled={readOnly}
                  >
                    <option value="None">None</option>
                    <option value="Manual">Manual</option>
                    <option value="Auto">Auto</option>
                  </select>
                </div>
                <div className="profile-builder-field">
                  <label>{lang === "ru" ? "Тип безопасности" : "Security Type"}</label>
                  <select
                    className="text-input"
                    value={network.securityType || "None"}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "securityType", event.target.value)
                        : updateCreateWiFiPayload(index, "securityType", event.target.value)
                    }
                    disabled={readOnly}
                  >
                    <option value="None">None</option>
                    <option value="WEP">WEP</option>
                    <option value="WPA">WPA</option>
                    <option value="WPA2">WPA2</option>
                    <option value="Any">Any</option>
                  </select>
                </div>
                <div className="profile-builder-field">
                  <label>{lang === "ru" ? "Тип сети" : "Network Type"}</label>
                  <select
                    className="text-input"
                    value={network.networkType || "Standard"}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "networkType", event.target.value)
                        : updateCreateWiFiPayload(index, "networkType", event.target.value)
                    }
                    disabled={readOnly}
                  >
                    <option value="Standard">Standard</option>
                    <option value="Hotspot">Hotspot</option>
                  </select>
                </div>
                <div className="profile-builder-field">
                  <label>{lang === "ru" ? "Пароль" : "Password"}</label>
                  <input
                    className="text-input"
                    type="password"
                    value={network.password || ""}
                    onChange={(event) =>
                      isView
                        ? updateViewWiFiPayload(index, "password", event.target.value)
                        : updateCreateWiFiPayload(index, "password", event.target.value)
                    }
                    readOnly={readOnly}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderCurrentPayloadSection = () => {
      if (activeSection === "general") return renderGeneralSection();
      if (activeSection === "wifi") return renderWiFiSection();

      const schema = APPLE_PROFILE_SECTION_SCHEMAS[activeSection];
      if (!schema) {
        return (
          <div className="profile-builder-placeholder">
          </div>
        );
      }

      const payloads = Array.isArray(sectionPayloads?.[activeSection]) ? sectionPayloads[activeSection] : [];
      if (payloads.length === 0) {
        return (
          <div className="profile-builder-placeholder">
            {!readOnly && <button onClick={configureCurrentSection}>{configureLabel}</button>}
          </div>
        );
      }

      const canAddMore =
        activeSection === "certificates" ||
        schema.supportsMultiple ||
        payloads.length === 0;

      return (
        <div className="profile-wifi-editor">
          <div className="profile-wifi-toolbar">
            <h4>{sectionTitle || sectionLabel}</h4>
            {!readOnly && (
              <div className="profile-wifi-toolbar-actions">
                <button className="profile-wifi-circle-btn" type="button" onClick={removePayloadForSection}>
                  −
                </button>
                {canAddMore && (
                  <button className="profile-wifi-circle-btn" type="button" onClick={addPayloadForSection}>
                    +
                  </button>
                )}
              </div>
            )}
          </div>

          {payloads.map((payloadItem, payloadIndex) => {
            const currentPayload = payloadItem || createSectionPayloadDraft(activeSection);
            const fieldsToRender =
              activeSection === "restrictions"
                ? restrictionFieldsForTab(schema, currentRestrictionsTab, currentPayload)
                : (schema.fields || []).filter((field) => isSectionFieldVisible(field, currentPayload));
            const usageMode = String(currentPayload.restrictAppUsageMode || "AllowAll");
            const restrictedApps = normalizeRestrictionAppEntries(currentPayload.restrictedApps);
            const visibleRestrictionBooleanFields =
              activeSection === "restrictions" ? fieldsToRender.filter((field) => field?.type === "boolean") : [];
            const allVisibleRestrictionsSelected =
              visibleRestrictionBooleanFields.length > 0 &&
              visibleRestrictionBooleanFields.every((field) => Boolean(currentPayload?.[field.key]));

            return (
              <div className="profile-wifi-payload-card" key={currentPayload.id || `${activeSection}-${payloadIndex}`}>
                {schema.supportsMultiple && (
                  <div className="profile-wifi-payload-head">
                    <strong>
                      {sectionLabel || activeSection} #{payloadIndex + 1}
                    </strong>
                    {!readOnly && (
                      <button
                        type="button"
                        className="profile-list-square-btn"
                        onClick={() =>
                          isView
                            ? removeViewSectionPayload(activeSection, payloadIndex)
                            : removeCreateSectionPayload(activeSection, payloadIndex)
                        }
                      >
                        −
                      </button>
                    )}
                  </div>
                )}

                {activeSection === "restrictions" && (
                  <div className="profile-restrictions-controls">
                    <div className="profile-restrictions-tabs">
                      {RESTRICTIONS_TAB_ORDER.map((tabKey) => (
                        <button
                          key={`restrictions-tab-${tabKey}`}
                          type="button"
                          className={currentRestrictionsTab === tabKey ? "profile-restrictions-tab active" : "profile-restrictions-tab"}
                          onClick={() => setCurrentRestrictionsTab(tabKey)}
                        >
                          {translateProfileBuilderText(
                            tabKey === "functionality" ? "Functionality" : tabKey === "apps" ? "Apps" : "Media Content",
                            lang
                          )}
                        </button>
                      ))}
                    </div>
                    {!readOnly && visibleRestrictionBooleanFields.length > 0 && (
                      <button
                        type="button"
                        className="secondary profile-restrictions-select-toggle"
                        onClick={() =>
                          updateRestrictionsVisibleBooleanFields(
                            payloadIndex,
                            visibleRestrictionBooleanFields,
                            !allVisibleRestrictionsSelected
                          )
                        }
                      >
                        {allVisibleRestrictionsSelected
                          ? lang === "ru"
                            ? "Снять выбор"
                            : "Clear selection"
                          : lang === "ru"
                            ? "Выбрать все"
                            : "Select all"}
                      </button>
                    )}
                  </div>
                )}

                {activeSection === "certificates" && (
                  <div className="profile-cert-summary">
                    {payloads.map((certItem, certIndex) => (
                      <div className="profile-cert-row" key={certItem.id || `cert-${certIndex}`}>
                        <span>{certItem.payloadDisplayName || certItem.certificateFileName || `Certificate ${certIndex + 1}`}</span>
                        {!readOnly && (
                          <button
                            type="button"
                            className="profile-list-square-btn"
                            onClick={() =>
                              isView
                                ? removeViewSectionPayload("certificates", certIndex)
                                : removeCreateSectionPayload("certificates", certIndex)
                            }
                          >
                            −
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {activeSection !== "certificates" && activeSection !== "airprint" && activeSection !== "contacts" && activeSection !== "google_account" && activeSection !== "ldap" && activeSection !== "exchange_active_sync" && activeSection !== "web_clips" && activeSection !== "lock_screen_message" && activeSection !== "tv_remote" && activeSection !== "scep" && activeSection !== "mail" && fieldsToRender.map((field) =>
                  renderSectionField(
                    field,
                    currentPayload?.[field.key],
                    (nextValue) => {
                      if (activeSection === "restrictions" && field.key === "restrictAppUsageMode" && nextValue === "AllowAll") {
                        updateSectionField(activeSection, payloadIndex, "restrictedApps", []);
                      }
                      updateSectionField(activeSection, payloadIndex, field.key, nextValue);
                    },
                    readOnly,
                    builderPlatform
                  )
                )}

                {activeSection === "airplay" &&
                  renderAirPlayPayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "airprint" &&
                  renderAirPrintPayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "contacts" &&
                  renderContactsPayloadFields(currentPayload, payloadIndex, readOnly)}

                {activeSection === "google_account" &&
                  renderGoogleAccountPayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "ldap" &&
                  renderLDAPPayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "exchange_active_sync" &&
                  renderExchangeActiveSyncPayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "web_clips" &&
                  renderWebClipPayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "lock_screen_message" &&
                  renderLockScreenMessagePayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "tv_remote" &&
                  renderTVRemotePayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "scep" &&
                  renderSCEPPayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "mail" &&
                  renderMailPayloadFields(currentPayload, payloadIndex, isView ? "view" : "create", readOnly)}

                {activeSection === "restrictions" && usageMode !== "AllowAll" && (
                  <div className="profile-restrictions-apps-editor">
                    <div className="profile-restrictions-apps-list">
                      {restrictedApps.map((entry) => (
                        <div key={entry.bundleId} className="profile-restrictions-app-row">
                          <div className="profile-restrictions-app-meta">
                            <strong>{entry.name || entry.bundleId}</strong>
                            <span>{entry.bundleId}</span>
                          </div>
                          {!readOnly && (
                            <button
                              type="button"
                              className="profile-list-square-btn"
                              onClick={() =>
                                removeRestrictionsAppByBundle(
                                  isView ? "view" : "create",
                                  payloadIndex,
                                  entry.bundleId
                                )
                              }
                            >
                              −
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {!readOnly && (
                      <div className="profile-restrictions-app-actions">
                        <button
                          type="button"
                          className="profile-list-square-btn"
                          onClick={() => openRestrictionsAppPicker(isView ? "view" : "create", payloadIndex)}
                        >
                          +
                        </button>
                        <button
                          type="button"
                          className="profile-list-square-btn"
                          onClick={() => removeLastRestrictionsApp(isView ? "view" : "create", payloadIndex)}
                          disabled={restrictedApps.length === 0}
                        >
                          −
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="modal-backdrop" onClick={() => (isView ? closeProfileViewModal() : closeCreateProfileModal())}>
        <div className="modal-card profile-builder-modal" onClick={(event) => event.stopPropagation()}>
          <div className="profile-builder-heading">
            <div className="modal-heading">
              <h3>
                {isView
                  ? `${form.name || "-"}${profileViewIsEditing ? " — Edited" : ""}`
                  : form.name || "Untitled"}
              </h3>
              <button className="icon-button" onClick={() => (isView ? closeProfileViewModal() : closeCreateProfileModal())}>
                ×
              </button>
            </div>
          </div>

          <div className="profile-builder-body">
            <aside className="profile-builder-sidebar">
              <div className="profile-builder-sidebar-scroll">
                {visibleBuilderSections.map((section) => {
                  const state = profileSectionState(section.key, configuredSections, wifiNetworks, sectionPayloads);
                  const stateLabel = profileSectionStateLabel(
                    section.key,
                    configuredSections,
                    wifiNetworks,
                    sectionPayloads
                  );
                  const payloadCount =
                    section.key === "wifi"
                      ? Array.isArray(wifiNetworks)
                        ? wifiNetworks.length
                        : 0
                      : sectionPayloadCount(sectionPayloads, section.key);
                  return (
                    <button
                      key={section.key}
                      type="button"
                      className={activeSection === section.key ? "profile-section-row active" : "profile-section-row"}
                      onClick={() => setCurrentSection(section.key)}
                    >
                      <span className="profile-section-icon">{section.icon}</span>
                      <span className="profile-section-copy">
                        <span className="profile-section-title-line">
                          <span className="profile-section-name">{section.label || section.key}</span>
                          {payloadCount > 0 && <span className="profile-section-count">{payloadCount}</span>}
                        </span>
                        {profileSectionDisplayMinOS(section, builderPlatform) && (
                          <span className="profile-section-min-os">
                            min OS: {profileSectionDisplayMinOS(section, builderPlatform)}
                          </span>
                        )}
                        <span className={`profile-section-state ${state === "not_configured" ? "muted" : "ready"}`}>
                          {stateLabel}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="profile-builder-content">
              <div className="profile-builder-section-heading">
                <h3>{sectionTitle || sectionLabel}</h3>
                {sectionMinOS && <p className="profile-builder-section-min-os">min OS: {sectionMinOS}</p>}
              </div>
              {sectionWarning && <p className="profile-section-warning">{sectionWarning}</p>}
              {sectionHint && activeSection !== "general" && activeSection !== "wifi" && (
                <p className="profile-builder-current-state muted">{sectionHint}</p>
              )}
              <input
                ref={isView ? viewCertificatesInputRef : createCertificatesInputRef}
                type="file"
                accept=".cer,.crt,.pem,.der,.p12,.pfx,.mobileconfig"
                multiple
                style={{ display: "none" }}
                onChange={(event) => onCertificatesPicked(event, isView ? "view" : "create")}
              />
              {renderCurrentPayloadSection()}
            </div>
          </div>

          <div className="action-row profile-builder-actions">
            {isView ? (
              <>
                {!profileViewIsEditing ? (
                  <button onClick={() => setProfileViewIsEditing(true)}>{lang === "ru" ? "Изменить" : "Edit"}</button>
                ) : (
                  <>
                    <button onClick={saveProfileFromView}>{t("save")}</button>
                    <button
                      className="secondary"
                      onClick={() => {
                        if (profilePreview) {
                          openProfileView(profilePreview);
                        } else {
                          setProfileViewIsEditing(false);
                        }
                      }}
                    >
                      {t("cancel")}
                    </button>
                  </>
                )}
                <button className="secondary" onClick={closeProfileViewModal}>
                  {t("close")}
                </button>
              </>
            ) : (
              <>
                <button onClick={createProfile}>{t("create")}</button>
                <button className="secondary" onClick={closeCreateProfileModal}>
                  {t("close")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  async function createProfile() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const name = profileCreateForm.name.trim();
    if (!name) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (profileCreateForm.autoRemove === "OnDate" && !String(profileCreateForm.removalDate || "").trim()) {
      setErrorMessage(t("profileBuilderGeneralRemovalDateRequired"));
      return;
    }
    if (profileCreateForm.autoRemove === "AfterDays") {
      const parsedDays = Number.parseInt(String(profileCreateForm.removalDays || "").trim(), 10);
      if (!Number.isFinite(parsedDays) || parsedDays <= 0) {
        setErrorMessage(t("profileBuilderGeneralRemovalDaysRequired"));
        return;
      }
    }
    const platform = normalizeProfilePlatform(profileCreateForm.platform);
    const compatibleSectionPayloads = pruneProfileSectionPayloadsForPlatform(profileCreateSectionPayloads, platform);
    if (!hasAnyConfiguredProfilePayload(profileCreateWiFiNetworks, compatibleSectionPayloads)) {
      setErrorMessage(t("profileBuilderAtLeastOnePayloadRequired"));
      return;
    }
    const validationError = validateProfilePayloadBeforeSubmit(profileCreateWiFiNetworks, compatibleSectionPayloads);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    const uniqueIdentifier = buildUniqueProfileIdentifier(
      name,
      profiles.map((item) => item.identifier)
    );
    let payload;
    try {
      payload = buildInstallProfilePayload(name, uniqueIdentifier, profileCreateForm.groupIds, {
        wifiNetworks: profileCreateWiFiNetworks,
        sectionPayloads: compatibleSectionPayloads,
        platform,
        general: profileCreateForm
      });
    } catch (err) {
      setErrorMessage(err?.message || "Failed to validate profile payload");
      return;
    }
    try {
      await request("/profiles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          identifier: uniqueIdentifier,
          description: String(profileCreateForm.description || "").trim(),
          payload
        })
      });
      setInfoMessage(`Profile created: ${name}`);
      setProfileCreateForm(createEmptyProfileCreateForm(platform));
      setProfileCreateSection("general");
      setProfileCreateRestrictionsTab("functionality");
      setProfileConfiguredSections(createProfileBuilderConfiguredState());
      setProfileCreateWiFiNetworks([]);
      setProfileCreateSectionPayloads(createProfileSectionPayloadsState());
      setShowCreateProfileModal(false);
      closeRestrictionsAppPicker();
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to create profile");
    }
  }

  async function saveProfileFromView() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const id = profileViewForm.id;
    const name = profileViewForm.name.trim();
    if (!id || !name) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (profileViewForm.autoRemove === "OnDate" && !String(profileViewForm.removalDate || "").trim()) {
      setErrorMessage(t("profileBuilderGeneralRemovalDateRequired"));
      return;
    }
    if (profileViewForm.autoRemove === "AfterDays") {
      const parsedDays = Number.parseInt(String(profileViewForm.removalDays || "").trim(), 10);
      if (!Number.isFinite(parsedDays) || parsedDays <= 0) {
        setErrorMessage(t("profileBuilderGeneralRemovalDaysRequired"));
        return;
      }
    }
    const platform = normalizeProfilePlatform(profileViewForm.platform);
    const compatibleSectionPayloads = pruneProfileSectionPayloadsForPlatform(profileViewSectionPayloads, platform);
    if (!hasAnyConfiguredProfilePayload(profileViewWiFiNetworks, compatibleSectionPayloads)) {
      setErrorMessage(t("profileBuilderAtLeastOnePayloadRequired"));
      return;
    }
    const validationError = validateProfilePayloadBeforeSubmit(profileViewWiFiNetworks, compatibleSectionPayloads);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    const identifier = profileViewForm.identifier.trim();
    let payload;
    try {
      payload = buildInstallProfilePayload(name, identifier, profileViewForm.groupIds, {
        wifiNetworks: profileViewWiFiNetworks,
        sectionPayloads: compatibleSectionPayloads,
        platform,
        general: profileViewForm
      });
    } catch (err) {
      setErrorMessage(err?.message || "Failed to validate profile payload");
      return;
    }
    try {
      await request("/profiles/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id,
          name,
          identifier,
          description: String(profileViewForm.description || "").trim(),
          payload
        })
      });
      setInfoMessage(`Profile updated: ${name}`);
      setProfileViewIsEditing(false);
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to update profile");
    }
  }

  async function setProfileStatusAction(isActive) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedProfile?.id) return;
    try {
      await request("/profiles/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedProfile.id,
          is_active: isActive
        })
      });
      setInfoMessage(`Profile status: ${isActive ? t("active") : t("revoked")}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to update profile status");
    }
  }

  async function deleteProfile() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedProfile?.id) return;
    try {
      await request("/profiles/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedProfile.id })
      });
      setInfoMessage(`Profile deleted: ${selectedProfile.name}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to delete profile");
    }
  }

  async function deleteSelectedProfiles(ids = selectedProfileIDs) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const uniqueIDs = Array.from(new Set((ids || []).filter(Boolean)));
    if (uniqueIDs.length === 0) return;
    if (!window.confirm(t("profilesDeleteSelectedConfirm"))) return;

    try {
      const settled = await Promise.allSettled(
        uniqueIDs.map((id) =>
          request("/profiles/delete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id })
          })
        )
      );
      const successCount = settled.filter((item) => item.status === "fulfilled").length;
      const failedCount = settled.length - successCount;
      setSelectedProfileIDs((prev) => prev.filter((id) => !uniqueIDs.includes(id)));

      if (failedCount > 0) {
        setErrorMessage(`${t("profilesDeleteSelectedFailed")}: ${failedCount}`);
      } else {
        setInfoMessage(`${t("profilesDeleteSelected")}: ${successCount}`);
      }
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("profilesDeleteSelectedFailed")}: ${err.message}`);
    }
  }

  async function deployProfile() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedProfile?.id || !profileDeployUdid.trim()) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      await request("/profiles/deploy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile_id: selectedProfile.id,
          udid: profileDeployUdid.trim()
        })
      });
      setInfoMessage(`${t("profilesDeploy")}: ${profileDeployUdid.trim()}`);
      setProfileDeployUdid("");
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("profilesDeployFailed")}: ${err.message}`);
    }
  }

  function openCreateProfileModal(platformKey = "ios") {
    const platform = normalizeProfilePlatform(platformKey);
    closeRestrictionsAppPicker();
    closeAirPlayDestinationModal();
    setProfileCreateForm(createEmptyProfileCreateForm(platform));
    setProfileCreateSection("general");
    setProfileCreateRestrictionsTab("functionality");
    setProfileConfiguredSections(createProfileBuilderConfiguredState());
    setProfileCreateWiFiNetworks([]);
    setProfileCreateSectionPayloads(createProfileSectionPayloadsState());
    setShowCreateProfileModal(true);
  }

  function closeCreateProfileModal() {
    setShowCreateProfileModal(false);
    setProfileCreateSection("general");
    setProfileCreateRestrictionsTab("functionality");
    closeRestrictionsAppPicker();
    closeAirPlayDestinationModal();
  }

  function openProfileView(item) {
    if (!item) return;
    closeRestrictionsAppPicker();
    closeAirPlayDestinationModal();
    const wifiNetworks = parseWiFiPayloadsFromProfile(item);
    const sectionPayloads = parseSectionPayloadsFromProfile(item);
    const generalState = parseProfileGeneralStateFromProfile(item);
    const configured = createProfileBuilderConfiguredState();
    configured.wifi = wifiNetworks.length > 0;
    Object.keys(sectionPayloads).forEach((sectionKey) => {
      configured[sectionKey] = Array.isArray(sectionPayloads[sectionKey]) && sectionPayloads[sectionKey].length > 0;
    });
    const platform = normalizeProfilePlatform(
      item?.payload?.TargetPlatform || item?.platform || item?.target_platform || item?.os
    );

    setSelectedProfileId(item.id);
    setProfilePreview(item);
    setProfileViewForm({
      ...createEmptyProfileViewForm(platform),
      ...generalState,
      id: item.id || "",
      name: item.name || "",
      identifier: item.identifier || "",
      description: generalState.description || String(item.description || ""),
      groupIds: profileGroupIDs(item),
      platform
    });
    setProfileViewSection("general");
    setProfileViewRestrictionsTab("functionality");
    setProfileViewConfiguredSections(configured);
    setProfileViewWiFiNetworks(wifiNetworks);
    setProfileViewSectionPayloads(sectionPayloads);
    setProfileViewIsEditing(false);
    setShowProfileViewModal(true);
  }

  function closeProfileViewModal() {
    setShowProfileViewModal(false);
    setProfilePreview(null);
    setProfileViewForm(createEmptyProfileViewForm("ios"));
    setProfileViewSection("general");
    setProfileViewRestrictionsTab("functionality");
    setProfileViewConfiguredSections(createProfileBuilderConfiguredState());
    setProfileViewWiFiNetworks([]);
    setProfileViewSectionPayloads(createProfileSectionPayloadsState());
    setProfileViewIsEditing(false);
    closeRestrictionsAppPicker();
    closeAirPlayDestinationModal();
  }

  async function openProfileDevicesModal(item) {
    if (!item?.id) return;
    setProfileDevicesModalItem(item);
    setProfileDevices([]);
    setProfileDevicesFilter("all");
    setProfileDevicesLoading(true);
    setErrorMessage("");
    try {
      const response = await request(`/profiles/devices?profile_id=${encodeURIComponent(item.id)}&limit=10000`);
      setProfileDevices(Array.isArray(response?.items) ? response.items : []);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setProfileDevicesLoading(false);
    }
  }

  function closeProfileDevicesModal() {
    setProfileDevicesModalItem(null);
    setProfileDevices([]);
    setProfileDevicesFilter("all");
    setProfileDevicesLoading(false);
  }

  function exportProfileDevicesReport(items = profileDevices) {
    setErrorMessage("");
    setInfoMessage("");
    try {
      const rows = [
        [
          lang === "ru" ? "Аккаунт" : "Account",
          "Email",
          t("devicesColName"),
          "UDID",
          t("devicesColSerial"),
          t("devicesColOSVersion"),
          t("profilesColGroups"),
          t("profilesColStatus"),
          t("devicesColLastSync")
        ],
        ...(Array.isArray(items) ? items : []).map((item) => [
          item.account_name || "",
          item.account_email || "",
          item.device_name || "",
          item.udid || "",
          item.serial_number || "",
          item.os_version || "",
          Array.isArray(item.group_names) ? item.group_names.join(", ") : "",
          item.installed ? t("profileDevicesInstalled") : t("profileDevicesNotInstalled"),
          item.installed_last_seen_at ? formatTimestamp(item.installed_last_seen_at) : formatTimestamp(item.last_checkin_at)
        ])
      ];
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      downloadCsvFile(`turuk-profile-devices-${stamp}.csv`, rows);
      setInfoMessage(t("profileDevicesExportDone"));
    } catch (err) {
      setErrorMessage(`${t("profileDevicesExportFailed")}: ${err.message}`);
    }
  }

  function removeGroupIDsFromForms(groupIDs = []) {
    const ids = Array.from(new Set((groupIDs || []).filter(Boolean)));
    if (ids.length === 0) return;
    const shouldKeep = (id) => !ids.includes(id);
    setProfileCreateForm((prev) => ({
      ...prev,
      groupIds: (prev.groupIds || []).filter(shouldKeep)
    }));
    setProfileViewForm((prev) => ({
      ...prev,
      groupIds: (prev.groupIds || []).filter(shouldKeep)
    }));
    setAssignGroupsForm((prev) => ({
      ...prev,
      groupIds: (prev.groupIds || []).filter(shouldKeep)
    }));
    setAssignAppGroupsForm((prev) => ({
      ...prev,
      groupIds: (prev.groupIds || []).filter(shouldKeep)
    }));
  }

  async function deleteSelectedGroups(ids = selectedGroupIDs) {
    if (!hasPermission("groups", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const uniqueIDs = Array.from(new Set((ids || []).filter(Boolean)));
    if (uniqueIDs.length === 0) return;
    if (!window.confirm(t("groupsDeleteSelectedConfirm"))) return;

    try {
      const settled = await Promise.allSettled(
        uniqueIDs.map((id) =>
          request("/groups/delete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id })
          })
        )
      );
      const successCount = settled.filter((item) => item.status === "fulfilled").length;
      const failedCount = settled.length - successCount;
      removeGroupIDsFromForms(uniqueIDs);
      setSelectedGroupIDs((prev) => prev.filter((id) => !uniqueIDs.includes(id)));
      if (failedCount > 0) {
        setErrorMessage(`${t("groupsDeleteSelectedFailed")}: ${failedCount}`);
      } else {
        setInfoMessage(`${t("groupsDeleteSelected")}: ${successCount}`);
      }
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("groupsDeleteSelectedFailed")}: ${err.message}`);
    }
  }

  function toggleGroupSelection(groupID) {
    setSelectedGroupIDs((prev) =>
      prev.includes(groupID) ? prev.filter((id) => id !== groupID) : [...prev, groupID]
    );
  }

  function toggleSelectAllVisibleGroups(visibleGroupIDs, allVisibleSelected) {
    if (allVisibleSelected) {
      const visibleSet = new Set(visibleGroupIDs);
      setSelectedGroupIDs((prev) => prev.filter((id) => !visibleSet.has(id)));
      return;
    }
    const next = new Set(selectedGroupIDs);
    visibleGroupIDs.forEach((groupID) => next.add(groupID));
    setSelectedGroupIDs(Array.from(next));
  }

  function toggleAccountSelection(accountID) {
    setSelectedAccountIDs((prev) =>
      prev.includes(accountID) ? prev.filter((id) => id !== accountID) : [...prev, accountID]
    );
  }

  function toggleAppSelection(appID) {
    setSelectedAppIDs((prev) => (prev.includes(appID) ? prev.filter((id) => id !== appID) : [...prev, appID]));
  }

  function toggleDeviceSelection(udid) {
    if (!udid) return;
    setSelectedDeviceUDIDs((prev) =>
      prev.includes(udid) ? prev.filter((id) => id !== udid) : [...prev, udid]
    );
  }

  function toggleSelectAllVisibleDevices(visibleUDIDs, allVisibleSelected) {
    if (allVisibleSelected) {
      const visibleSet = new Set(visibleUDIDs);
      setSelectedDeviceUDIDs((prev) => prev.filter((udid) => !visibleSet.has(udid)));
      return;
    }
    const next = new Set(selectedDeviceUDIDs);
    visibleUDIDs.forEach((udid) => next.add(udid));
    setSelectedDeviceUDIDs(Array.from(next));
  }

  async function deleteSelectedDevices() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (selectedDeviceUDIDs.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!window.confirm(t("devicesDeleteConfirm"))) {
      return;
    }

    setErrorMessage("");
    setInfoMessage("");
    try {
      const out = await request("/devices/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udids: selectedDeviceUDIDs })
      });
      const queued = Number(out?.summary?.queued ?? 0);
      const failed = Number(out?.summary?.failed ?? 0);
      const queuedUDIDs = Array.isArray(out?.items) ? out.items.map((item) => item.udid).filter(Boolean) : [];
      if (queuedUDIDs.length > 0) {
        const queuedSet = new Set(queuedUDIDs);
        setSelectedDeviceUDIDs((prev) => prev.filter((udid) => !queuedSet.has(udid)));
        if (queuedSet.has(selectedUdid)) {
          setSelectedUdid("");
        }
      }
      if (failed > 0) {
        const errors = Array.isArray(out?.errors) ? out.errors : [];
        const details = errors
          .slice(0, 3)
          .map((item) => {
            const udid = String(item?.udid || "").trim();
            const msg = String(item?.error || "").trim();
            return [udid, msg].filter(Boolean).join(": ");
          })
          .filter(Boolean)
          .join("; ");
        setErrorMessage(details ? `${t("devicesDeleteFailed")}: ${details}` : `${t("devicesDeleteFailed")}: ${failed}`);
      }
      setInfoMessage(`${t("devicesDelete")}: ${queued}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("devicesDeleteFailed")}: ${err.message}`);
    }
  }

  async function removeCurrentDeviceMDMProfile() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const udid = String(routeDeviceUDID || selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!window.confirm(t("deviceDetailsRemoveMDMProfileConfirm"))) {
      return;
    }

    setErrorMessage("");
    setInfoMessage("");
    setShowDeviceCommandsMenu(false);
    try {
      const out = await request("/devices/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udids: [udid] })
      });
      const queued = Number(out?.summary?.queued ?? 0);
      const failed = Number(out?.summary?.failed ?? 0);
      if (failed > 0) {
        const errors = Array.isArray(out?.errors) ? out.errors : [];
        const details = errors
          .slice(0, 3)
          .map((item) => {
            const failedUDID = String(item?.udid || "").trim();
            const msg = String(item?.error || "").trim();
            return [failedUDID, msg].filter(Boolean).join(": ");
          })
          .filter(Boolean)
          .join("; ");
        setErrorMessage(
          details
            ? `${t("deviceDetailsRemoveMDMProfileFailed")}: ${details}`
            : `${t("deviceDetailsRemoveMDMProfileFailed")}: ${failed}`
        );
      }
      if (queued > 0) {
        setInfoMessage(`${t("deviceDetailsRemoveMDMProfileQueued")}: ${queued}`);
      }
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsRemoveMDMProfileFailed")}: ${err.message}`);
    }
  }

  function deviceDetailsLinkHash(udid, pageKey = activePage) {
    const targetPage = DEVICE_DETAILS_PAGES.includes(pageKey) ? pageKey : "devicesDetailAbout";
    return deviceDetailsHash(targetPage, udid);
  }

  function openDeviceView(udid, pageKey = activePage) {
    const normalized = String(udid || "").trim();
    if (!normalized) return;
    const hash = deviceDetailsLinkHash(normalized, pageKey);
    if (typeof window !== "undefined") {
      if (window.location.hash !== hash) {
        window.location.hash = hash.slice(1);
      } else {
        setRouteDeviceUDID(normalized);
      }
    }
    setSelectedUdid(normalized);
  }

  async function refreshDeviceCommands(udidValue = routeDeviceUDID || selectedUdid) {
    const udid = String(udidValue || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request(`/commands?limit=100&udid=${encodeURIComponent(udid)}`);
      setCommands(Array.isArray(out?.items) ? out.items : []);
      await loadDeviceTelemetry(udid);
    } catch (err) {
      setErrorMessage(err.message || "Request failed");
    }
  }

  async function retryCommand(commandUUID) {
    const value = String(commandUUID || "").trim();
    if (!value) return;
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    try {
      const out = await request("/commands/retry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ command_uuid: value })
      });
      setInfoMessage(`${t("deviceDetailsCommandRetryQueued")}: ${out.command_uuid || "-"}`);
      await refreshDeviceCommands(routeDeviceUDID || selectedUdid);
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsCommandRetryFailed")}: ${err.message}`);
    }
  }

  async function cancelQueuedCommand(commandUUID) {
    const value = String(commandUUID || "").trim();
    if (!value) return;
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    setErrorMessage("");
    try {
      await request("/commands/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ command_uuid: value })
      });
      setInfoMessage(t("deviceDetailsCommandCancelDone"));
      await refreshDeviceCommands(routeDeviceUDID || selectedUdid);
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsCommandCancelFailed")}: ${err.message}`);
    }
  }

  function refreshCommandRequests(udid) {
    return [
      {
        endpoint: "/commands/device-information",
        body: { udid }
      },
      {
        endpoint: "/commands/security-info",
        body: { udid }
      },
      {
        endpoint: "/commands/installed-apps",
        body: { udid }
      },
      {
        endpoint: "/commands/profile-list",
        body: { udid }
      },
      {
        endpoint: "/commands/apple",
        body: { udid, command: "certificate_list", parameters: {} }
      },
      {
        endpoint: "/commands/device-attestation",
        body: { udid }
      }
    ];
  }

  async function requestDeviceRefresh(udidValue = selectedUdid) {
    const udid = String(udidValue || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }

    setErrorMessage("");
    const sourceDevice =
      (deviceDetail?.udid === udid ? deviceDetail : null) ||
      devices.find((item) => item.udid === udid) ||
      filteredDevices.find((item) => item.udid === udid) ||
      selectedDevice ||
      null;
    const requests = refreshCommandRequests(udid);
    try {
      const settled = await Promise.allSettled(
        requests.map((item) =>
          request(item.endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(item.body)
          })
        )
      );

      const succeeded = settled.filter((item) => item.status === "fulfilled").length;
      const failed = settled.length - succeeded;
      if (succeeded > 0) {
        setInfoMessage(`${t("deviceDetailsRequestQueued")}: ${succeeded}/${settled.length}`);
      }
      if (failed > 0) {
        const firstError = settled.find((item) => item.status === "rejected");
        const errText = firstError?.reason?.message || `${failed}`;
        setErrorMessage(`${t("deviceDetailsRequestFailed")}: ${errText}`);
      }

      await loadAllData();
      setTimeout(() => {
        loadDeviceTelemetry(udid);
      }, 4500);
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsRequestFailed")}: ${err.message}`);
    }
  }

  async function requestDevicesRefreshBulk(deviceItems = []) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const sourceRows = Array.isArray(deviceItems) ? deviceItems : [];
    const byUDID = new Map();
    sourceRows.forEach((item) => {
      const udid = String(item?.udid || "").trim();
      if (!udid || byUDID.has(udid)) return;
      byUDID.set(udid, {
        udid
      });
    });
    const targets = Array.from(byUDID.values());
    if (targets.length === 0) {
      setErrorMessage(t("devicesBulkRequestNoDevices"));
      return;
    }

    setErrorMessage("");
    let total = 0;
    let succeeded = 0;
    let failed = 0;
    let firstError = "";

    for (const target of targets) {
      const requests = refreshCommandRequests(target.udid);
      for (const item of requests) {
        total += 1;
        try {
          await request(item.endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(item.body)
          });
          succeeded += 1;
        } catch (err) {
          failed += 1;
          if (!firstError) {
            firstError = `${target.udid}: ${err?.message || "Request failed"}`;
          }
        }
      }
    }

    if (succeeded > 0) {
      setInfoMessage(`${t("devicesBulkRequestQueued")}: ${succeeded}/${total}`);
    }
    if (failed > 0) {
      setErrorMessage(`${t("devicesBulkRequestFailed")}: ${firstError || failed}`);
    }
    await loadAllData();
  }

  function toggleDeviceAppSelection(bundleID) {
    const key = String(bundleID || "").trim();
    if (!key) return;
    setSelectedDeviceAppBundleIDs((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  }

  function toggleDeviceProfileSelection(profileID) {
    const key = String(profileID || "").trim();
    if (!key) return;
    setSelectedDeviceProfileIDs((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  }

  async function reinstallSelectedDeviceApps() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const udid = String(selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }

    const selectedRows = selectedDeviceAppRows.filter((item) => {
      const bundleID = String(item._bundleID || item.bundle_id || "").trim();
      if (!bundleID) return false;
      return item._isAssignedNotInstalled || item.is_managed === true;
    });
    if (selectedRows.length === 0) {
      setErrorMessage(t("deviceDetailsNoManagedAppsSelected"));
      return;
    }

    const selectedPlatform = normalizePlatform(deviceDetail || selectedDevice || {});
    const catalogCandidates = appsCatalog
      .filter((item) => item?.is_active !== false)
      .map((item) => ({
        id: String(item.id || "").trim(),
        bundle_id: String(item.bundle_id || "").trim(),
        platform: normalizeAppPlatform(item)
      }))
      .filter((item) => item.id && item.bundle_id);

    const catalogByBundle = new Map();
    catalogCandidates.forEach((item) => {
      const bundleKey = String(item.bundle_id || "").trim().toLowerCase();
      if (!bundleKey) return;
      const current = catalogByBundle.get(bundleKey);
      if (!current) {
        catalogByBundle.set(bundleKey, item);
        return;
      }
      if (item.platform === selectedPlatform && current.platform !== selectedPlatform) {
        catalogByBundle.set(bundleKey, item);
      }
    });

    const reinstallRequests = [];
    const reinstallDedup = new Set();
    const missing = [];
    selectedRows.forEach((item) => {
      const deploymentAppID = String(item._deploymentAppID || "").trim();
      const bundleID = String(item._bundleID || item.bundle_id || "").trim();
      const candidate = bundleID ? catalogByBundle.get(bundleID.toLowerCase()) : null;
      const appID = deploymentAppID || String(candidate?.id || "").trim();
      if (!appID) {
        missing.push(bundleID);
        return;
      }
      // Reinstall/update should be non-destructive:
      // queue InstallApplication again so iOS performs install/update in place.
      const removeFirst = false;
      const dedupKey = `${appID}|${bundleID.toLowerCase()}|${removeFirst ? "1" : "0"}`;
      if (reinstallDedup.has(dedupKey)) return;
      reinstallDedup.add(dedupKey);
      reinstallRequests.push({ appID, bundleID, removeFirst });
    });

    if (reinstallRequests.length === 0) {
      setErrorMessage(t("deviceDetailsMissingCatalogApps"));
      return;
    }

    try {
      const settled = await Promise.allSettled(
        reinstallRequests.map((item) =>
          request("/commands/reinstall-application", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              udid,
              app_id: item.appID,
              bundle_id: item.bundleID,
              remove_first: item.removeFirst
            })
          })
        )
      );
      const successCount = settled.filter((item) => item.status === "fulfilled").length;
      const failedCount = settled.length - successCount;
      if (successCount > 0) {
        setInfoMessage(`${t("deviceDetailsReinstallDone")}: ${successCount}`);
      }
      if (missing.length > 0 || failedCount > 0) {
        setErrorMessage(
          `${t("deviceDetailsReinstallFailed")}: ${failedCount}${missing.length > 0 ? `, ${t("deviceDetailsMissingCatalogApps")}` : ""}`
        );
      }
      await request("/commands/installed-apps", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udid })
      }).catch(() => null);
      setTimeout(() => {
        loadDeviceTelemetry(udid);
      }, 4500);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsReinstallFailed")}: ${err.message}`);
    }
  }

  async function removeSelectedManagedApps() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const udid = String(selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }

    const selectedManaged = selectedDeviceAppRows.filter((item) => {
      const bundleID = String(item._bundleID || item.bundle_id || "").trim();
      return bundleID && !item._isAssignedNotInstalled && item.is_managed === true;
    });
    if (selectedManaged.length === 0) {
      setErrorMessage(t("deviceDetailsNoManagedAppsSelected"));
      return;
    }

    try {
      const settled = await Promise.allSettled(
        selectedManaged.map((item) =>
          request("/commands/remove-application", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ udid, bundle_id: item.bundle_id })
          })
        )
      );
      const successCount = settled.filter((item) => item.status === "fulfilled").length;
      const failedCount = settled.length - successCount;
      if (successCount > 0) {
        setInfoMessage(`${t("deviceDetailsRemoveDone")}: ${successCount}`);
      }
      if (failedCount > 0) {
        const firstError = settled.find((item) => item.status === "rejected");
        const errText = firstError?.reason?.message || `${failedCount}`;
        setErrorMessage(`${t("deviceDetailsRemoveFailed")}: ${errText}`);
      }
      await request("/commands/installed-apps", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ udid })
      }).catch(() => null);
      setTimeout(() => {
        loadDeviceTelemetry(udid);
      }, 4500);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsRemoveFailed")}: ${err.message}`);
    }
  }

  async function resendSelectedDeviceProfiles() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const udid = String(selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    const profileIDs = selectedDeviceProfileIDs.filter(Boolean);
    if (profileIDs.length === 0) {
      setErrorMessage(t("deviceDetailsNoProfilesSelected"));
      return;
    }

    try {
      const settled = await Promise.allSettled(
        profileIDs.map((profileID) =>
          request("/profiles/deploy", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ profile_id: profileID, udid })
          })
        )
      );
      const successCount = settled.filter((item) => item.status === "fulfilled").length;
      const failedCount = settled.length - successCount;
      if (successCount > 0) {
        setInfoMessage(`${t("deviceDetailsProfilesResentDone")}: ${successCount}`);
      }
      if (failedCount > 0) {
        const firstError = settled.find((item) => item.status === "rejected");
        const errText = firstError?.reason?.message || `${failedCount}`;
        setErrorMessage(`${t("deviceDetailsProfileActionFailed")}: ${errText}`);
      }
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsProfileActionFailed")}: ${err.message}`);
    }
  }

  async function disableSelectedDeviceProfiles() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const udid = String(selectedUdid || "").trim();
    if (!udid) {
      setErrorMessage(t("requiredField"));
      return;
    }
    const selectedRows = selectedDeviceProfiles.filter((item) =>
      selectedDeviceProfileIDs.includes(String(item.profile_id || "").trim())
    );
    if (selectedRows.length === 0) {
      setErrorMessage(t("deviceDetailsNoProfilesSelected"));
      return;
    }

    const removable = selectedRows.filter((item) => String(item.profile_identifier || "").trim());
    if (removable.length === 0) {
      setErrorMessage(t("deviceDetailsProfileActionFailed"));
      return;
    }

    try {
      const settled = await Promise.allSettled(
        removable.map((item) =>
          request("/commands/remove-profile", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ udid, identifier: item.profile_identifier })
          })
        )
      );
      const successCount = settled.filter((item) => item.status === "fulfilled").length;
      const failedCount = settled.length - successCount;
      if (successCount > 0) {
        setInfoMessage(`${t("deviceDetailsProfilesDisabledDone")}: ${successCount}`);
      }
      if (failedCount > 0) {
        const firstError = settled.find((item) => item.status === "rejected");
        const errText = firstError?.reason?.message || `${failedCount}`;
        setErrorMessage(`${t("deviceDetailsProfileActionFailed")}: ${errText}`);
      }
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("deviceDetailsProfileActionFailed")}: ${err.message}`);
    }
  }

  function toggleAssignGroupSelection(groupID) {
    setAssignGroupsForm((prev) => {
      const has = prev.groupIds.includes(groupID);
      return {
        ...prev,
        groupIds: has ? prev.groupIds.filter((id) => id !== groupID) : [...prev.groupIds, groupID]
      };
    });
  }

  function toggleAssignAppGroupSelection(groupID) {
    setAssignAppGroupsForm((prev) => {
      const has = prev.groupIds.includes(groupID);
      return {
        ...prev,
        groupIds: has ? prev.groupIds.filter((id) => id !== groupID) : [...prev.groupIds, groupID]
      };
    });
  }

  function toggleAssignProfileGroupSelection(groupID) {
    setAssignProfileGroupsForm((prev) => {
      const has = prev.groupIds.includes(groupID);
      return {
        ...prev,
        groupIds: has ? prev.groupIds.filter((id) => id !== groupID) : [...prev.groupIds, groupID]
      };
    });
  }

  function selectedAccountsCommonGroupIDs() {
    const selected = accounts.filter((item) => selectedAccountIDs.includes(item.id));
    if (selected.length === 0) return [];
    const existingGroupIDs = new Set(groups.map((group) => String(group.id || "").trim()).filter(Boolean));

    return selected.reduce((commonIDs, account, index) => {
      const accountGroupIDs = Array.isArray(account.group_ids)
        ? account.group_ids
            .map((id) => String(id || "").trim())
            .filter((id) => id && existingGroupIDs.has(id))
        : [];
      if (index === 0) return accountGroupIDs;
      const groupSet = new Set(accountGroupIDs);
      return commonIDs.filter((id) => groupSet.has(id));
    }, []);
  }

  function selectedAppsCommonGroupIDs() {
    const selected = appsCatalog.filter((item) => selectedAppIDs.includes(item.id));
    if (selected.length === 0) return [];
    const existingGroupIDs = new Set(groups.map((group) => String(group.id || "").trim()).filter(Boolean));

    return selected.reduce((commonIDs, app, index) => {
      const appGroupIDs = Array.isArray(app.group_ids)
        ? app.group_ids
            .map((id) => String(id || "").trim())
            .filter((id) => id && existingGroupIDs.has(id))
        : [];
      if (index === 0) return appGroupIDs;
      const groupSet = new Set(appGroupIDs);
      return commonIDs.filter((id) => groupSet.has(id));
    }, []);
  }

  function selectedProfilesCommonGroupIDs() {
    const selected = profiles.filter((item) => selectedProfileIDs.includes(item.id));
    if (selected.length === 0) return [];
    const existingGroupIDs = new Set(groups.map((group) => String(group.id || "").trim()).filter(Boolean));

    return selected.reduce((commonIDs, profile, index) => {
      const profileGroups = profileGroupIDs(profile).filter((id) => existingGroupIDs.has(id));
      if (index === 0) return profileGroups;
      const groupSet = new Set(profileGroups);
      return commonIDs.filter((id) => groupSet.has(id));
    }, []);
  }

  function openAssignAccountsToGroupsModal() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (selectedAccountIDs.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setAssignGroupsForm({ groupIds: selectedAccountsCommonGroupIDs() });
    setShowAssignAccountsGroupsModal(true);
  }

  function openAssignAppsToGroupsModal() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (selectedAppIDs.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setAssignAppGroupsForm({ groupIds: selectedAppsCommonGroupIDs() });
    setShowAssignAppsGroupsModal(true);
  }

  function openAssignProfilesToGroupsModal() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (selectedProfileIDs.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    setAssignProfileGroupsForm({ groupIds: selectedProfilesCommonGroupIDs() });
    setShowAssignProfilesGroupsModal(true);
  }

  async function assignSelectedAccountsToGroups() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (selectedAccountIDs.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      const out = await request("/groups/set-accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_ids: selectedAccountIDs,
          group_ids: assignGroupsForm.groupIds
        })
      });
      setInfoMessage(`${t("accountsAssignToGroups")}: +${out.added_count ?? 0} / -${out.removed_count ?? 0}`);
      setSelectedAccountIDs([]);
      setAssignGroupsForm({ groupIds: [] });
      setShowAssignAccountsGroupsModal(false);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("accountsAssignGroupsFailed")}: ${err.message}`);
    }
  }

  async function assignSelectedAppsToGroups() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (selectedAppIDs.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      const out = await request("/groups/set-apps", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          app_ids: selectedAppIDs,
          group_ids: assignAppGroupsForm.groupIds
        })
      });
      setInfoMessage(`${t("appsAssignToGroups")}: +${out.added_count ?? 0} / -${out.removed_count ?? 0}`);
      setSelectedAppIDs([]);
      setAssignAppGroupsForm({ groupIds: [] });
      setShowAssignAppsGroupsModal(false);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("appsAssignGroupsFailed")}: ${err.message}`);
    }
  }

  async function assignSelectedProfilesToGroups() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (selectedProfileIDs.length === 0) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      const out = await request("/groups/set-profiles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile_ids: selectedProfileIDs,
          group_ids: assignProfileGroupsForm.groupIds
        })
      });
      setInfoMessage(`${t("profilesAssignToGroups")}: +${out.added_count ?? 0} / -${out.removed_count ?? 0}`);
      setSelectedProfileIDs([]);
      setAssignProfileGroupsForm({ groupIds: [] });
      setShowAssignProfilesGroupsModal(false);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("profilesAssignGroupsFailed")}: ${err.message}`);
    }
  }

  function parseAppJSONField(value, label) {
    const raw = String(value || "").trim();
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
        throw new Error("object expected");
      }
      return parsed;
    } catch (err) {
      throw new Error(`${label}: ${err.message}`);
    }
  }

  function appFormPayload() {
    return {
      name: appForm.name.trim(),
      bundle_id: appForm.bundleId.trim(),
      platform: String(appForm.platform || "").trim(),
      manifest_url: appForm.manifestUrl.trim(),
      itunes_store_id: appForm.itunesStoreId.trim(),
      description: appForm.description.trim(),
      assignment_type: appForm.assignmentType || "optional",
      update_policy: appForm.updatePolicy || "manual",
      install_priority: appForm.installPriority || "normal",
      uninstall_protected: Boolean(appForm.uninstallProtected),
      per_app_vpn_uuid: String(appForm.perAppVpnUuid || "").trim(),
      managed_config: parseAppJSONField(appForm.managedConfigJSON, "Managed app config"),
      request_managed_feedback: Boolean(appForm.requestManagedFeedback),
      request_managed_attributes: Boolean(appForm.requestManagedAttributes),
      in_house_pipeline: parseAppJSONField(appForm.pipelineJSON, "In-house pipeline")
    };
  }

  async function createApp(event) {
    if (event?.preventDefault) event.preventDefault();
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!appForm.name.trim() || !appForm.bundleId.trim()) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (!appForm.manifestUrl.trim() && !appForm.itunesStoreId.trim()) {
      setErrorMessage(t("appsUploadManifestRequired"));
      return;
    }
    try {
      const payload = appFormPayload();
      await request("/apps", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      setInfoMessage(`App created: ${appForm.name.trim()}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to create app");
    }
  }

  async function updateApp() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!appForm.id || !appForm.name.trim() || !appForm.bundleId.trim()) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      const payload = appFormPayload();
      await request("/apps/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          id: appForm.id,
        })
      });
      setInfoMessage(`App updated: ${appForm.name.trim()}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to update app");
    }
  }

  async function setAppStatusAction(isActive) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedApp?.id) return;
    try {
      await request("/apps/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedApp.id,
          is_active: isActive
        })
      });
      setInfoMessage(`App status: ${isActive ? t("active") : t("revoked")}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to update app status");
    }
  }

  async function deleteApp() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedApp?.id) return;
    try {
      await request("/apps/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedApp.id })
      });
      setInfoMessage(`App deleted: ${selectedApp.name}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to delete app");
    }
  }

  async function deleteSelectedApps(ids = selectedAppIDs) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const uniqueIDs = Array.from(new Set((ids || []).filter(Boolean)));
    if (uniqueIDs.length === 0) return;
    if (!window.confirm(t("appsDeleteSelectedConfirm"))) return;

    try {
      const settled = await Promise.allSettled(
        uniqueIDs.map((id) =>
          request("/apps/delete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id })
          })
        )
      );
      const successCount = settled.filter((item) => item.status === "fulfilled").length;
      const failedCount = settled.length - successCount;
      setSelectedAppIDs((prev) => prev.filter((id) => !uniqueIDs.includes(id)));
      if (uniqueIDs.includes(selectedAppId)) {
        setSelectedAppId("");
        if (activePage === "appsConfig") {
          setActivePage("appsAll");
        }
      }
      if (failedCount > 0) {
        setErrorMessage(`${t("appsDeleteSelectedFailed")}: ${failedCount}`);
      } else {
        setInfoMessage(`${t("appsDeleteSelected")}: ${successCount}`);
      }
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("appsDeleteSelectedFailed")}: ${err.message}`);
    }
  }

  async function deployApp() {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedApp?.id || !appDeployUdid.trim()) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      await request("/apps/deploy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          app_id: selectedApp.id,
          udid: appDeployUdid.trim()
        })
      });
      setInfoMessage(`${t("appsDeploy")}: ${appDeployUdid.trim()}`);
      setAppDeployUdid("");
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("appsDeployFailed")}: ${err.message}`);
    }
  }

  async function requestManagedAppInfo(command) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!selectedApp?.bundle_id || !appDeployUdid.trim()) {
      setErrorMessage("UDID and Bundle ID are required");
      return;
    }
    try {
      await request("/commands/apple", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          udid: appDeployUdid.trim(),
          command,
          parameters: {
            Identifiers: [selectedApp.bundle_id]
          }
        })
      });
      setInfoMessage(`${command}: queued`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(err.message || `Failed to queue ${command}`);
    }
  }

  function openStoreByPlatform(platformKey) {
    const normalized = String(platformKey || "").trim().toLowerCase();
    if (!normalized) return;

    if (normalized === "ios" || normalized === "macos" || normalized === "android") {
      setAppsStorePlatform(normalized);
      setAppsStoreResults([]);
      setAppsManagedPlayURL("");
      setAppsManagedPlayError("");
      setAppsManagedPlayReady(false);
      setShowAppStoreModal(true);
      return;
    }

    const query = appsSearch.trim();
    const encoded = encodeURIComponent(query);
    let url = "";
    if (normalized === "windows") {
      url = `https://apps.microsoft.com/search?query=${encoded}`;
    } else {
      return;
    }

    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (!opened) {
      setErrorMessage(t("notAvailable"));
    }
  }

  function openPlatformPicker(context) {
    setPlatformPickerContext(context === "profiles" ? "profiles" : "apps");
    setShowPlatformPickerModal(true);
  }

  function closePlatformPicker() {
    setShowPlatformPickerModal(false);
    setPlatformPickerContext("");
  }

  function handleAppsAdd(forcedPlatform = "") {
    const normalized = String(forcedPlatform || "").trim().toLowerCase();
    if (normalized) {
      openStoreByPlatform(normalized);
      return;
    }
    openPlatformPicker("apps");
  }

  function handleProfilesAdd(forcedPlatform = "") {
    const normalized = normalizeForcedProfilePlatform(forcedPlatform);
    if (normalized) {
      if (profileBuilderCanCreateForPlatform(normalized)) {
        openCreateProfileModal(normalized);
      } else {
        setInfoMessage(t("profilesAppleOnlyBuilderHint"));
      }
      return;
    }
    openPlatformPicker("profiles");
  }

  function handlePlatformPickerSelection(platformKey) {
    const normalized = normalizeProfilePlatform(platformKey);
    if (platformPickerContext === "profiles") {
      closePlatformPicker();
      if (profileBuilderCanCreateForPlatform(normalized)) {
        openCreateProfileModal(normalized);
      } else {
        setInfoMessage(t("profilesAppleOnlyBuilderHint"));
      }
      return;
    }

    const appPageByPlatform = {
      ios: "appsPlatformIOS",
      macos: "appsPlatformMacOS",
      android: "appsPlatformAndroid",
      windows: "appsPlatformWindows"
    };
    setActivePage(appPageByPlatform[normalized] || "appsAll");
    closePlatformPicker();
    openStoreByPlatform(normalized);
  }

  function getPlatformPickerOptions() {
    if (platformPickerContext === "profiles") {
      return [
        { key: "ios", label: t("platformIOS") },
        { key: "macos", label: t("platformMacOS") },
        { key: "android", label: t("platformAndroid"), disabled: true },
        { key: "windows", label: t("platformWindows"), disabled: true }
      ];
    }
    return [
      { key: "ios", label: t("platformIOS") },
      { key: "macos", label: t("platformMacOS") },
      { key: "android", label: t("platformAndroid") },
      { key: "windows", label: t("platformWindows") }
    ];
  }

  function parseManagedPlayProductSelection(rawMessage) {
    let payload = rawMessage;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch {
        return "";
      }
    }
    if (!payload || typeof payload !== "object") return "";

    const directValue = String(payload.packageName || payload.package_name || payload.productId || "").trim();
    if (directValue) {
      return directValue.replace(/^app:/i, "");
    }

    const nestedData =
      payload.data && typeof payload.data === "object"
        ? String(payload.data.packageName || payload.data.package_name || payload.data.productId || "").trim()
        : "";
    if (nestedData) {
      return nestedData.replace(/^app:/i, "");
    }

    const eventType = String(payload.eventType || payload.type || payload.event || "").toLowerCase();
    if (eventType !== "onproductselect") return "";

    return "";
  }

  async function addAndroidAppFromManagedPlay(packageName) {
    const cleanedPackageName = String(packageName || "").trim();
    if (!cleanedPackageName) return;
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (managedPlayAddInFlightRef.current) return;

    managedPlayAddInFlightRef.current = true;
    setAppsManagedPlayError("");
    try {
      let appMeta = null;
      try {
        const metaResp = await request(
          `/apps/android/managed-play/application?package_name=${encodeURIComponent(cleanedPackageName)}`
        );
        appMeta = metaResp?.item || null;
      } catch {
        appMeta = null;
      }

      const androidStoreURL = `https://play.google.com/store/apps/details?id=${encodeURIComponent(cleanedPackageName)}`;
      const payload = {
        name: String(appMeta?.name || cleanedPackageName).trim(),
        bundle_id: cleanedPackageName,
        platform: "android",
        manifest_url: androidStoreURL,
        itunes_store_id: "",
        description: String(appMeta?.description || "").trim()
      };

      const out = await request("/apps", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (out?.item?.id) {
        setSelectedAppId(out.item.id);
      }
      setInfoMessage(`${t("appsManagedPlaySelected")}: ${payload.name}`);
      setShowAppStoreModal(false);
      setActivePage("appsConfig");
      await loadAllData();
    } catch (err) {
      setAppsManagedPlayError(err.message || t("appsStoreAddFailed"));
      setErrorMessage(`${t("appsStoreAddFailed")}: ${err.message}`);
    } finally {
      managedPlayAddInFlightRef.current = false;
    }
  }

  async function searchIOSAppStore() {
    const query = appsStoreQuery.trim();
    if (!query) {
      setAppsStoreResults([]);
      return;
    }
    try {
      setAppsStoreLoading(true);
      const country = String(appsStoreCountry || "US").trim().toUpperCase() || "US";
      const platform = appsStorePlatform === "macos" ? "macos" : "ios";
      const out = await request(
        `/apps/app-store/search?q=${encodeURIComponent(query)}&country=${encodeURIComponent(country)}&platform=${encodeURIComponent(platform)}&limit=25`
      );
      setAppsStoreResults(Array.isArray(out?.items) ? out.items : []);
    } catch (err) {
      setErrorMessage(`${t("appsStoreSearchFailed")}: ${err.message}`);
    } finally {
      setAppsStoreLoading(false);
    }
  }

  async function addAppFromStore(item) {
    if (!isAdminUI) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!item?.name || !item?.bundle_id) {
      setErrorMessage(t("requiredField"));
      return;
    }

    try {
      const out = await request("/apps", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(item.name || "").trim(),
          bundle_id: String(item.bundle_id || "").trim(),
          platform: String(item.platform || appsStorePlatform || "ios").trim(),
          itunes_store_id: String(item.itunes_store_id || "").trim(),
          manifest_url: "",
          description: String(item.description || "").trim()
        })
      });
      if (out?.item?.id) {
        setSelectedAppId(out.item.id);
      }
      setInfoMessage(`${t("appsStoreAdded")}: ${item.name}`);
      setShowAppStoreModal(false);
      setActivePage("appsConfig");
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("appsStoreAddFailed")}: ${err.message}`);
    }
  }

  async function createConsoleUser() {
    if (!hasPermission("settings.console_users", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const required = [
      createConsoleUserForm.firstName,
      createConsoleUserForm.lastName,
      createConsoleUserForm.email,
      createConsoleUserForm.login,
      createConsoleUserForm.temporaryPassword
    ].every((value) => value.trim());
    if (!required) {
      setErrorMessage(t("requiredField"));
      return;
    }

    try {
      const out = await request("/console-users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: createConsoleUserForm.firstName.trim(),
          last_name: createConsoleUserForm.lastName.trim(),
          email: createConsoleUserForm.email.trim(),
          login: createConsoleUserForm.login.trim(),
          temporary_password: createConsoleUserForm.temporaryPassword,
          role: createConsoleUserForm.role,
          mfa_required:
            String(createConsoleUserForm.role || "").toLowerCase() === "master-admin" ||
            !!createConsoleUserForm.mfaRequired
        })
      });
      setInfoMessage(`User created: ${out?.item?.login || createConsoleUserForm.login.trim()}`);
      setCreateConsoleUserForm({
        firstName: "",
        lastName: "",
        email: "",
        login: "",
        temporaryPassword: "",
        role: roleOptions[0]?.role_key || "read-only",
        mfaRequired: false
      });
      setShowCreateUserModal(false);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("consoleUsersCreateFailed")}: ${err.message}`);
    }
  }

  function openEditConsoleUserModal(item) {
    if (!item) return;
    setEditConsoleUserForm({
      id: item.id || "",
      firstName: item.first_name || "",
      lastName: item.last_name || "",
      login: item.login || "",
      email: item.email || "",
      role: String(item.role || "read-only"),
      mustChangePassword: !!item.must_change_password,
      mfaRequired: !!item.mfa_required,
      temporaryPassword: ""
    });
    setShowEditUserModal(true);
  }

  async function updateConsoleUser() {
    if (!hasPermission("settings.console_users", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (
      !editConsoleUserForm.id ||
      !editConsoleUserForm.firstName.trim() ||
      !editConsoleUserForm.lastName.trim() ||
      !editConsoleUserForm.email.trim() ||
      !editConsoleUserForm.login.trim() ||
      !editConsoleUserForm.role
    ) {
      setErrorMessage(t("requiredField"));
      return;
    }

    try {
      const out = await request("/console-users/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: editConsoleUserForm.id,
          first_name: editConsoleUserForm.firstName.trim(),
          last_name: editConsoleUserForm.lastName.trim(),
          email: editConsoleUserForm.email.trim(),
          login: editConsoleUserForm.login.trim(),
          role: editConsoleUserForm.role
        })
      });
      const login = out?.item?.login || editConsoleUserForm.login;
      setInfoMessage(`${t("consoleUsersUpdate")}: ${login}`);
      setShowEditUserModal(false);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("consoleUsersUpdateFailed")}: ${err.message}`);
    }
  }

  async function revokeConsoleUser(id) {
    if (!hasPermission("settings.console_users", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    if (!id) return;
    try {
      await request("/console-users/revoke", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
      setInfoMessage(`Console user disabled: ${id}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("consoleUsersRevokeFailed")}: ${err.message}`);
    }
  }

  function resetTenantForm() {
    setTenantForm({
      id: "",
      name: "",
      slug: "",
      primaryDomain: "",
      status: "active",
      plan: "trial",
      billingStatus: "trialing",
      billingEmail: "",
      trialEndsAt: "",
      subscriptionEndsAt: "",
      stripeCustomerID: "",
      stripeSubscriptionID: "",
      deviceLimit: "",
      accountLimit: "",
      adminLimit: "",
      adminFirstName: "",
      adminLastName: "",
      adminEmail: "",
      adminLogin: "master_admin",
      adminTemporaryPassword: ""
    });
  }

  function editTenant(item) {
    if (!item) return;
    setTenantForm({
      id: item.id || "",
      name: item.name || "",
      slug: item.slug || "",
      primaryDomain: item.primary_domain || "",
      status: item.status || "active",
      plan: item.plan || "trial",
      billingStatus: item.billing_status || "internal",
      billingEmail: item.billing_email || "",
      trialEndsAt: tenantDateInputValue(item.trial_ends_at),
      subscriptionEndsAt: tenantDateInputValue(item.subscription_ends_at),
      stripeCustomerID: item.stripe_customer_id || "",
      stripeSubscriptionID: item.stripe_subscription_id || "",
      deviceLimit: tenantLimitInputValue(item.device_limit),
      accountLimit: tenantLimitInputValue(item.account_limit),
      adminLimit: tenantLimitInputValue(item.admin_limit),
      adminFirstName: "",
      adminLastName: "",
      adminEmail: "",
      adminLogin: "master_admin",
      adminTemporaryPassword: ""
    });
  }

  async function saveTenant(event) {
    event?.preventDefault?.();
    const isMasterAdmin = String(session?.user?.role || "").toLowerCase() === "master-admin";
    if (!isMasterAdmin || !hasPermission("settings.tenants", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const isUpdate = Boolean(tenantForm.id);
    if (!tenantForm.name.trim()) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (
      !isUpdate &&
      (!tenantForm.adminEmail.trim() ||
        !tenantForm.adminLogin.trim() ||
        !tenantForm.adminTemporaryPassword.trim())
    ) {
      setErrorMessage(t("requiredField"));
      return;
    }
    if (
      ![
        tenantForm.deviceLimit,
        tenantForm.accountLimit,
        tenantForm.adminLimit
      ].every(isTenantLimitInputValid)
    ) {
      setErrorMessage(t("tenantsLimitInvalid"));
      return;
    }
    try {
      const out = await request(isUpdate ? "/tenants/update" : "/tenants", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: tenantForm.id,
          name: tenantForm.name.trim(),
          slug: tenantForm.slug.trim(),
          primary_domain: tenantForm.primaryDomain.trim(),
          status: tenantForm.status || "active",
          plan: tenantForm.plan || "trial",
          billing_status: tenantForm.billingStatus || "",
          billing_email: tenantForm.billingEmail.trim(),
          trial_ends_at: tenantForm.trialEndsAt || "",
          subscription_ends_at: tenantForm.subscriptionEndsAt || "",
          stripe_customer_id: tenantForm.stripeCustomerID.trim(),
          stripe_subscription_id: tenantForm.stripeSubscriptionID.trim(),
          device_limit: tenantLimitInputToPayload(tenantForm.deviceLimit),
          account_limit: tenantLimitInputToPayload(tenantForm.accountLimit),
          admin_limit: tenantLimitInputToPayload(tenantForm.adminLimit),
          admin_first_name: tenantForm.adminFirstName.trim(),
          admin_last_name: tenantForm.adminLastName.trim(),
          admin_email: tenantForm.adminEmail.trim(),
          admin_login: tenantForm.adminLogin.trim(),
          admin_temporary_password: tenantForm.adminTemporaryPassword.trim()
        })
      });
      setInfoMessage(`${t("tenantsSaved")}: ${out?.item?.name || tenantForm.name.trim()}`);
      resetTenantForm();
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("tenantsSaveFailed")}: ${tenantSaveErrorMessage(err, t)}`);
    }
  }

  async function checkTenantDomain(item) {
    const id = String(item?.id || "").trim();
    if (!id) return;
    setTenantDomainCheckingID(id);
    setErrorMessage("");
    try {
      const out = await request(`/tenants/domain-status?id=${encodeURIComponent(id)}`);
      setTenantDomainChecks((prev) => ({ ...prev, [id]: out || {} }));
    } catch (err) {
      setTenantDomainChecks((prev) => ({
        ...prev,
        [id]: {
          allowed: false,
          dns_resolved: false,
          message: err.message || t("tenantsDomainCheckFailed")
        }
      }));
      setErrorMessage(`${t("tenantsDomainCheckFailed")}: ${err.message}`);
    } finally {
      setTenantDomainCheckingID("");
    }
  }

  async function updateTenantLifecycle(item, action) {
    const id = String(item?.id || "").trim();
    if (!id) return;
    const isMasterAdmin = String(session?.user?.role || "").toLowerCase() === "master-admin";
    if (!isMasterAdmin || !hasPermission("settings.tenants", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const payload = { id, action };
    if (action === "suspend") {
      const reason = window.prompt(t("tenantsLifecycleSuspendReasonPrompt"), item?.suspended_reason || "");
      if (reason === null) return;
      payload.reason = reason.trim();
    } else if (action === "schedule_delete") {
      const scheduledAt = window.prompt(t("tenantsLifecycleScheduleDeletePrompt"), "");
      if (scheduledAt === null) return;
      payload.scheduled_deletion_at = scheduledAt.trim();
      if (!payload.scheduled_deletion_at) {
        setErrorMessage(t("tenantsLifecycleScheduleDeleteRequired"));
        return;
      }
      const reason = window.prompt(t("tenantsLifecycleDeleteReasonPrompt"), item?.deletion_reason || "");
      if (reason === null) return;
      payload.reason = reason.trim();
    } else if (action === "delete_now") {
      if (!window.confirm(t("tenantsLifecycleDeleteNowConfirm"))) return;
      const reason = window.prompt(t("tenantsLifecycleDeleteReasonPrompt"), item?.deletion_reason || "");
      if (reason === null) return;
      payload.reason = reason.trim();
    } else if (action === "reactivate") {
      if (!window.confirm(t("tenantsLifecycleReactivateConfirm"))) return;
    } else if (action === "cancel_delete") {
      if (!window.confirm(t("tenantsLifecycleCancelDeleteConfirm"))) return;
    }

    try {
      const out = await request("/tenants/lifecycle", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      setInfoMessage(`${t("tenantsLifecycleSaved")}: ${out?.item?.name || item?.name || id}`);
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("tenantsLifecycleFailed")}: ${err.message}`);
    }
  }

  function openRoleEditor(item) {
    if (!item) return;
    const roleKey = String(item.role_key || "").toLowerCase();
    if (!roleKey) return;
    setSelectedRoleKey(roleKey);
    setEditRoleForm({
      roleKey,
      displayName: item.display_name || roleKey,
      permissions: mergeRolePermissionsWithCatalog(item.permissions || {}),
      accessScope: normalizeRoleAccessScopeClient(item.access_scope || {})
    });
  }

  function applyRoleTemplateToCreateForm(template) {
    if (!template) return;
    setCreateRoleForm((prev) => ({
      ...prev,
      roleKey: template.roleKey,
      displayName: template.displayName,
      permissions: mergeRolePermissionsWithCatalog(template.permissions || {}),
      accessScope: normalizeRoleAccessScopeClient(prev.accessScope || {})
    }));
  }

  function applyRoleTemplateToEditor(template) {
    if (!template) return;
    setEditRoleForm((prev) => ({
      ...prev,
      roleKey: prev.roleKey || template.roleKey,
      displayName: prev.displayName || template.displayName,
      permissions: mergeRolePermissionsWithCatalog(template.permissions || {}),
      accessScope: normalizeRoleAccessScopeClient(prev.accessScope || {})
    }));
  }

  function setRolePermissionValue(permissionKey, action, nextValue) {
    setEditRoleForm((prev) => {
      const permissions = mergeRolePermissionsWithCatalog(prev.permissions || {});
      const current = permissions[permissionKey] || { view: false, edit: false };
      const next = { ...current };
      if (action === "edit") {
        next.edit = Boolean(nextValue);
        if (next.edit) next.view = true;
      } else {
        next.view = Boolean(nextValue);
        if (!next.view) next.edit = false;
      }
      permissions[permissionKey] = next;
      return { ...prev, permissions };
    });
  }

  function setRoleScopeValue(field, value, checked) {
    setEditRoleForm((prev) => {
      const scope = normalizeRoleAccessScopeClient(prev.accessScope || {});
      const current = new Set(scope[field] || []);
      const cleaned = String(value || "").trim();
      if (!cleaned) return prev;
      if (checked) {
        current.add(cleaned);
      } else {
        current.delete(cleaned);
      }
      return {
        ...prev,
        accessScope: {
          ...scope,
          [field]: Array.from(current)
        }
      };
    });
  }

  function setRoleScopeLocations(value) {
    setEditRoleForm((prev) => ({
      ...prev,
      accessScope: {
        ...normalizeRoleAccessScopeClient(prev.accessScope || {}),
        location_ids: parseRoleScopeLocations(value)
      }
    }));
  }

  async function createRoleSetting() {
    if (!hasPermission("settings.role_settings", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const roleKey = String(createRoleForm.roleKey || "").trim().toLowerCase();
    const displayName = String(createRoleForm.displayName || "").trim();
    if (!roleKey || !displayName) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      const out = await request("/role-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role_key: roleKey,
          display_name: displayName,
          permissions: mergeRolePermissionsWithCatalog(createRoleForm.permissions || {}),
          access_scope: roleAccessScopePayload(createRoleForm.accessScope || {})
        })
      });
      const item = out?.item || null;
      if (item?.role_key) {
        setSelectedRoleKey(String(item.role_key || "").toLowerCase());
        openRoleEditor(item);
      }
      setShowCreateRoleModal(false);
      setCreateRoleForm({
        roleKey: "",
        displayName: "",
        permissions: buildEmptyRolePermissionsState(),
        accessScope: { group_ids: [], platforms: [], location_ids: [] }
      });
      await loadAllData();
      setInfoMessage(`${t("roleSettingsCreate")}: ${displayName}`);
    } catch (err) {
      setErrorMessage(`${t("roleSettingsCreateFailed")}: ${err.message}`);
    }
  }

  async function saveRoleSetting() {
    if (!hasPermission("settings.role_settings", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const roleKey = String(editRoleForm.roleKey || "").trim().toLowerCase();
    const displayName = String(editRoleForm.displayName || "").trim();
    if (!roleKey || !displayName) {
      setErrorMessage(t("requiredField"));
      return;
    }
    try {
      const out = await request("/role-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role_key: roleKey,
          display_name: displayName,
          permissions: mergeRolePermissionsWithCatalog(editRoleForm.permissions || {}),
          access_scope: roleAccessScopePayload(editRoleForm.accessScope || {})
        })
      });
      if (out?.item) {
        openRoleEditor(out.item);
      }
      await loadAllData();
      setInfoMessage(`${t("roleSettingsSavePermissions")}: ${displayName}`);
    } catch (err) {
      setErrorMessage(`${t("roleSettingsUpdateFailed")}: ${err.message}`);
    }
  }

  async function deleteRoleSetting(roleKey) {
    if (!hasPermission("settings.role_settings", "edit")) {
      setErrorMessage(t("adminOnlyAction"));
      return;
    }
    const cleaned = String(roleKey || "").trim().toLowerCase();
    if (!cleaned) return;
    try {
      await request("/role-settings/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role_key: cleaned })
      });
      if (cleaned === selectedRoleKey) {
        setSelectedRoleKey("");
        setEditRoleForm({ roleKey: "", displayName: "", permissions: {} });
      }
      await loadAllData();
      setInfoMessage(`${t("roleSettingsDelete")}: ${cleaned}`);
    } catch (err) {
      setErrorMessage(`${t("roleSettingsDeleteFailed")}: ${err.message}`);
    }
  }

  function renderAccountsWorkspace() {
    const isConnectPage = activePage === "accountsConnect";

    return (
      <div className="page">
          <div className="panel devices-table-panel">
            <div className="panel-heading">
              <h3>{t("accountsHint")}</h3>
              <div className="devices-toolbar-actions">
                <button className="toolbar-action secondary" onClick={() => downloadReport("accounts", "pdf")}>
                  {t("reportsPDF")}
                </button>
                <button className="toolbar-action secondary" onClick={() => downloadReport("accounts", "csv")}>
                  {t("reportsCSV")}
                </button>
                <button className="toolbar-action" onClick={openAssignAccountsToGroupsModal}>
                  {t("accountsAssignToGroups")}
                </button>
                <button className="toolbar-action" onClick={openAccountImportModal}>
                  {t("accountsImport")}
                </button>
                <button className="toolbar-action" onClick={openCreateAccountModal}>
                  {t("accountsCreateAccount")}
                </button>
                {selectedAccountIDs.length > 0 && (
                  <button className="toolbar-action danger" onClick={() => deleteSelectedAccounts()}>
                    {t("accountsDeleteSelected")}
                  </button>
                )}
                <button className="toolbar-action" onClick={() => setActivePage("accountsConnect")}>
                  {t("accountsConnectDevices")}
                </button>
              </div>
            </div>

            <div className="devices-search-line">
              <input
                className="text-input"
                placeholder={t("accountsSearch")}
                value={accountsSearch}
                onChange={(event) => setAccountsSearch(event.target.value)}
              />
              <button className="toolbar-action secondary" onClick={() => setShowAccountsFilters((prev) => !prev)}>
                {t("addFilters")}
              </button>
            </div>

            {showAccountsFilters && (
              <div className="filters-row">
              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={accountsActiveOnly}
                  onChange={(event) => setAccountsActiveOnly(event.target.checked)}
                />
                {t("accountsFilterActive")}
              </label>
              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={accountsWithDevicesOnly}
                  onChange={(event) => setAccountsWithDevicesOnly(event.target.checked)}
                />
                {t("accountsFilterWithDevices")}
              </label>
            </div>
            )}

            {selectedAccountIDs.length > 0 && (
              <div className="muted">
                {t("accountsSelectedCount")}: {selectedAccountIDs.length}
              </div>
            )}

            <div className="devices-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th />
                    <th>{t("accountsName")}</th>
                    <th>{t("accountsFirstName")}</th>
                    <th>{t("accountsSecondName")}</th>
                    <th>{t("accountsEmail")}</th>
                    <th>{t("accountsGroups")}</th>
                    <th className="accounts-mdm-col">{t("accountsMdmDevicesCount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((item) => {
                    const accountMeta = parseAccountMeta(item);
                    const isSelected = selectedAccountIDs.includes(item.id);
                    const isCurrent = selectedAccountId === item.id;
                    return (
                      <tr key={item.id} className={isCurrent ? "row-selected" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleAccountSelection(item.id)}
                          />
                        </td>
                        <td>
                          <button
                            className="table-link-button"
                            onClick={() => {
                              setSelectedAccountId(item.id);
                              openEditAccountModal(item);
                            }}
                          >
                            {item.name || "-"}
                          </button>
                        </td>
                        <td>{accountMeta.firstName || "-"}</td>
                        <td>{accountMeta.secondName || "-"}</td>
                        <td>{item.email || "-"}</td>
                        <td>
                          {Array.isArray(item.group_names) && item.group_names.length > 0
                            ? item.group_names.join(", ")
                            : "-"}
                        </td>
                        <td className="accounts-mdm-col">
                          {Number(item.device_count || 0) > 0 ? (
                            <a
                              className="table-link-anchor"
                              href={`#${PAGE_TO_ROUTE.devicesList}?account_name=${encodeURIComponent(
                                String(item.name || "").trim()
                              )}`}
                            >
                              {Number(item.device_count || 0)}
                            </a>
                          ) : (
                            Number(item.device_count || 0)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredAccounts.length === 0 && <p className="devices-empty">{t("accountsNoItems")}</p>}
            </div>
          </div>

          {isConnectPage && selectedAccount && (
            <>
              <div className="panel">
                <div className="panel-heading">
                  <h3>{t("accountsDevicesTitle")}</h3>
                </div>
                <div className="inline-form inline-form-wide">
                  <input
                    className="text-input"
                    placeholder="UDID"
                    value={accountAssignUdid}
                    onChange={(event) => setAccountAssignUdid(event.target.value)}
                  />
                  <button onClick={assignDeviceToAccount}>{t("accountsAssign")}</button>
                  <button className="secondary" onClick={() => unassignDeviceFromAccount()}>
                    {t("accountsUnassign")}
                  </button>
                </div>
                {accountsDevicesLoading ? (
                  <p className="muted">{t("loading")}</p>
                ) : (
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>UDID</th>
                          <th>{t("devicesColName")}</th>
                          <th>{t("devicesColOS")}</th>
                          <th>{t("devicesColOSVersion")}</th>
                          <th>{t("devicesColLastSync")}</th>
                          <th>{t("tableAction")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accountDevices.map((item) => (
                          <tr key={item.udid}>
                            <td>{item.udid || "-"}</td>
                            <td>{item.device_name || "-"}</td>
                            <td>{item.product_name || "-"}</td>
                            <td>{item.os_version || "-"}</td>
                            <td>{formatTimestamp(item.last_seen_at || item.last_checkin_at)}</td>
                            <td>
                              <button
                                className="danger"
                                onClick={() => unassignDeviceFromAccount(String(item.udid || "").trim())}
                              >
                                {t("accountsUnassign")}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {accountDevices.length === 0 && <p className="devices-empty">{t("accountsNoDevices")}</p>}
                  </div>
                )}
              </div>

              <div className="panel">
                <div className="panel-heading">
                  <h3>{t("accountsMobileBindingsTitle")}</h3>
                </div>
                {accountsMobileBindingsLoading ? (
                  <p className="muted">{t("loading")}</p>
                ) : (
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("accountsMobileDeviceID")}</th>
                          <th>{t("accountsMobileDeviceName")}</th>
                          <th>{t("accountsMobileLastSeen")}</th>
                          <th>{t("tableAction")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accountMobileBindings.map((item) => (
                          <tr key={item.id || item.binding_id || item.device_id}>
                            <td>{item.device_id || "-"}</td>
                            <td>{item.device_name || "-"}</td>
                            <td>{formatTimestamp(item.last_seen_at)}</td>
                            <td>
                              <button
                                className="danger"
                                onClick={() =>
                                  unbindMobileDeviceFromAccount(item.id || item.binding_id, item.device_id)
                                }
                              >
                                {t("accountsMobileUnbind")}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {accountMobileBindings.length === 0 && (
                      <p className="devices-empty">{t("accountsMobileNoBindings")}</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
      </div>
    );
  }

  function renderAccountsGroups() {
    const visibleGroupIDs = groups.map((item) => item.id).filter(Boolean);
    const allVisibleSelected =
      visibleGroupIDs.length > 0 && visibleGroupIDs.every((id) => selectedGroupIDs.includes(id));
    const canEditGroups = hasPermission("groups", "edit");

    return (
      <div className="page">
        <div className="panel devices-table-panel">
          <div className="panel-heading">
            <h3>{t("groupsTitle")}</h3>
            <div className="devices-toolbar-actions">
              <button className="toolbar-action secondary" onClick={() => downloadReport("groups", "pdf")}>
                {t("reportsPDF")}
              </button>
              <button className="toolbar-action secondary" onClick={() => downloadReport("groups", "csv")}>
                {t("reportsCSV")}
              </button>
              {canEditGroups && (
                <button className="toolbar-action" onClick={() => setShowCreateGroupModal(true)}>
                  {t("groupsCreate")}
                </button>
              )}
              {canEditGroups && selectedGroupIDs.length > 0 && (
                <button className="toolbar-action danger" onClick={() => deleteSelectedGroups()}>
                  {t("groupsDeleteSelected")}
                </button>
              )}
            </div>
          </div>

          {selectedGroupIDs.length > 0 && (
            <div className="muted">
              {t("groupsSelectedCount")}: {selectedGroupIDs.length}
            </div>
          )}

          <div className="devices-table-wrap">
            <table className="groups-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={() => toggleSelectAllVisibleGroups(visibleGroupIDs, allVisibleSelected)}
                    />
                  </th>
                  <th>{t("groupsName")}</th>
                  <th>{t("groupsColAccounts")}</th>
                  <th>{t("groupsColApps")}</th>
                  <th>{t("groupsColProfiles")}</th>
                  <th>{t("groupsColDocuments")}</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((item) => (
                  <tr key={item.id} className={selectedGroupIDs.includes(item.id) ? "row-selected" : ""}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedGroupIDs.includes(item.id)}
                        onChange={() => toggleGroupSelection(item.id)}
                      />
                    </td>
                    <td>{item.name || "-"}</td>
                    <td>{Number(item.accounts_count || 0)}</td>
                    <td>{Number(item.apps_count || 0)}</td>
                    <td>{Number(item.profiles_count || 0)}</td>
                    <td>{Number(item.documents_count || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {groups.length === 0 && <p className="devices-empty">{t("groupsNoItems")}</p>}
          </div>
        </div>
      </div>
    );
  }

	    function resetComplianceRuleForm() {
	      setComplianceRuleTemplateKey("");
	      setComplianceFieldDropdownOpen(false);
	      setComplianceRuleForm({
        id: "",
        groupId: "",
        groupIds: [],
        name: "",
        type: "passcode_required",
        platform: "ios",
	      targetId: "",
	      minimumOSVersion: "",
	      customField: "",
	      customOperator: "equals",
	      customValue: "",
	      severity: "warning",
      deadlineHours: 72,
	      notifyCompanion: true,
	      notifyEmail: false,
	        notificationEmail: "",
	        autoRemediation: false,
	        retryHours: 0,
	        escalationHours: 0,
	        escalationEmail: "",
	        isActive: true
	      });
      setComplianceGroupDropdownOpen(false);
    }

	    function applyComplianceRuleTemplate(templateKey) {
	      if (!templateKey) return;
	      const groupId = complianceRuleForm.groupId;
	      const groupIds = complianceRuleForm.groupIds;
      const templates = {
	        custom: {
	          name: t("complianceRuleTemplateCustom"),
	          type: "custom_mdm_value",
	          platform: "ios",
	          targetId: "",
	          minimumOSVersion: "",
	          customField: "",
	          customOperator: "equals",
	          customValue: "",
	          severity: "warning",
          deadlineHours: 72,
	          notifyCompanion: true,
	          notifyEmail: false,
	          notificationEmail: "",
	          autoRemediation: false,
	          retryHours: 0,
	          escalationHours: 0,
	          escalationEmail: ""
	        },
        minimum_os: {
          name: t("complianceRuleTemplateMinimumOS"),
          type: "minimum_os_version",
          platform: "ios",
	          targetId: "",
	          minimumOSVersion: "18.0",
	          customField: "",
	          customOperator: "equals",
	          customValue: "",
	          severity: "warning",
          deadlineHours: 72,
	          notifyCompanion: true,
	          notifyEmail: false,
	          notificationEmail: "",
	          autoRemediation: false,
	          retryHours: 0,
	          escalationHours: 0,
	          escalationEmail: ""
	        },
        passcode: {
          name: t("complianceRuleTemplatePasscode"),
          type: "passcode_required",
          platform: "ios",
	          targetId: "",
	          minimumOSVersion: "",
	          customField: "",
	          customOperator: "equals",
	          customValue: "",
	          severity: "critical",
          deadlineHours: 24,
	          notifyCompanion: true,
	          notifyEmail: false,
	          notificationEmail: "",
	          autoRemediation: false,
	          retryHours: 0,
	          escalationHours: 0,
	          escalationEmail: ""
	        },
        profile: {
          name: t("complianceRuleTemplateProfile"),
          type: "profile_required",
          platform: "ios",
	          targetId: "",
	          minimumOSVersion: "",
	          customField: "",
	          customOperator: "equals",
	          customValue: "",
	          severity: "warning",
          deadlineHours: 24,
	          notifyCompanion: true,
	          notifyEmail: false,
	          notificationEmail: "",
	          autoRemediation: true,
	          retryHours: 6,
	          escalationHours: 24,
	          escalationEmail: ""
	        },
        app: {
          name: t("complianceRuleTemplateApp"),
          type: "app_required",
          platform: "ios",
	          targetId: "",
	          minimumOSVersion: "",
	          customField: "",
	          customOperator: "equals",
	          customValue: "",
	          severity: "warning",
          deadlineHours: 24,
	          notifyCompanion: true,
	          notifyEmail: false,
	          notificationEmail: "",
	          autoRemediation: false,
	          retryHours: 0,
	          escalationHours: 0,
	          escalationEmail: ""
	        },
        attestation: {
          name: t("complianceRuleTemplateAttestation"),
          type: "device_attestation_required",
          platform: "ios",
	          targetId: "",
	          minimumOSVersion: "",
	          customField: "",
	          customOperator: "equals",
	          customValue: "",
	          severity: "critical",
          deadlineHours: 24,
	          notifyCompanion: true,
	          notifyEmail: false,
	          notificationEmail: "",
	          autoRemediation: false,
	          retryHours: 0,
	          escalationHours: 0,
	          escalationEmail: ""
	        },
        allowed_sim: {
          name: t("complianceRuleTemplateSIM"),
          type: "allowed_sim_iccid",
          platform: "ios",
	          targetId: "",
	          minimumOSVersion: "",
	          customField: "network.ServiceSubscriptions.0.ICCID",
	          customOperator: "equals",
	          customValue: "",
	          severity: "critical",
          deadlineHours: 24,
	          notifyCompanion: true,
	          notifyEmail: false,
	          notificationEmail: "",
	          autoRemediation: false,
	          retryHours: 0,
	          escalationHours: 0,
	          escalationEmail: ""
	        }
      };
	      const next = templates[templateKey];
	      if (!next) return;
	      setComplianceRuleTemplateKey(templateKey);
	      setComplianceRuleForm((prev) => ({
        ...prev,
        ...next,
        id: "",
        groupId,
        groupIds,
        isActive: true
      }));
      setComplianceGroupDropdownOpen(false);
    }

  async function saveComplianceRule(event) {
      event.preventDefault();
      setErrorMessage("");
      setInfoMessage("");
      const selectedGroupIDs = complianceRuleForm.groupIds.filter(Boolean);
      if (selectedGroupIDs.length === 0) {
        setErrorMessage(`${t("complianceRuleSaveFailed")}: ${t("complianceRuleGroupsRequired")}`);
        return;
      }
      try {
        await request("/compliance/rules", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: complianceRuleForm.id,
            group_id: selectedGroupIDs[0] || "",
            group_ids: selectedGroupIDs,
            name: complianceRuleForm.name.trim(),
            type: complianceRuleForm.type,
          platform: complianceRuleForm.platform,
	          target_id: complianceRuleForm.targetId,
	          minimum_os_version: complianceRuleForm.minimumOSVersion.trim(),
	          custom_field: complianceRuleForm.customField.trim(),
	          custom_operator: complianceRuleForm.customOperator,
	          custom_value: complianceRuleForm.customValue.trim(),
	          severity: complianceRuleForm.severity,
          deadline_hours: Number(complianceRuleForm.deadlineHours || 0),
	          notify_companion: Boolean(complianceRuleForm.notifyCompanion),
	          notify_email: Boolean(complianceRuleForm.notifyEmail),
	          notification_email: complianceRuleForm.notificationEmail.trim(),
	          auto_remediation: Boolean(complianceRuleForm.autoRemediation),
	          retry_hours: Number(complianceRuleForm.retryHours || 0),
	          escalation_hours: Number(complianceRuleForm.escalationHours || 0),
	          escalation_email: complianceRuleForm.escalationEmail.trim(),
	          is_active: Boolean(complianceRuleForm.isActive)
	        })
      });
      resetComplianceRuleForm();
      setInfoMessage(t("complianceRuleSaved"));
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("complianceRuleSaveFailed")}: ${err.message}`);
    }
  }

  async function deleteComplianceRule(item) {
    if (!window.confirm(t("complianceRuleDeleteConfirm"))) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/compliance/rules/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id })
      });
      if (complianceRuleForm.id === item.id) resetComplianceRuleForm();
      setInfoMessage(t("complianceRuleDeleted"));
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("complianceRuleDeleteFailed")}: ${err.message}`);
    }
  }

  async function evaluateCompliance() {
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/compliance/evaluate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      setInfoMessage(
        t("complianceEvaluationDone")
          .replace("{count}", String(response?.evaluated || 0))
          .replace("{notifications}", String(response?.notifications_created || 0))
      );
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("complianceEvaluationFailed")}: ${err.message}`);
    }
  }

  async function runComplianceIssueAction(issue, action) {
    if (
      !issue?.id ||
      (!hasPermission("devices.management.compliance", "edit") &&
        !hasPermission("profiles.policies.compliance", "edit"))
    ) {
      return;
    }
    let reason = "";
    if (action === "waive" || action === "resolve") {
      const promptValue = window.prompt(
        action === "resolve" ? t("complianceIssueResolveReason") : t("complianceIssueWaiveReason")
      );
      if (promptValue === null) return;
      reason = promptValue;
    }
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/compliance/issues/action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          issue_id: issue.id,
          action,
          reason: reason.trim()
        })
      });
      setInfoMessage(t("complianceIssueActionDone"));
      setSelectedComplianceIssueID("");
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("complianceIssueActionFailed")}: ${err.message}`);
    }
  }

	    function editComplianceRule(item) {
	    setComplianceRuleTemplateKey("");
	    setComplianceFieldDropdownOpen(false);
	    setComplianceRuleForm({
        id: item.id || "",
        groupId: item.group_id || "",
        groupIds: item.group_id ? [item.group_id] : [],
        name: item.name || "",
      type: item.type || "passcode_required",
      platform: item.platform || "ios",
	      targetId: item.target_id || "",
	      minimumOSVersion: item.minimum_os_version || "",
	      customField: item.custom_field || "",
	      customOperator: item.custom_operator || "equals",
	      customValue: item.custom_value || "",
	      severity: item.severity || "warning",
      deadlineHours: Number(item.deadline_hours || 0),
	      notifyCompanion: item.notify_companion !== false,
	      notifyEmail: Boolean(item.notify_email),
	      notificationEmail: item.notification_email || "",
	      autoRemediation: Boolean(item.auto_remediation),
	      retryHours: Number(item.retry_hours || 0),
	      escalationHours: Number(item.escalation_hours || 0),
	      escalationEmail: item.escalation_email || "",
	      isActive: item.is_active !== false
	    });
  }

  function renderComplianceCenter() {
    const canEditCompliance =
      hasPermission("devices.management.compliance", "edit") ||
      hasPermission("profiles.policies.compliance", "edit");
    const openIssues = complianceIssues.filter((item) => item.status === "open");
    const resolvedIssues = complianceIssues.filter((item) => item.status === "resolved");
    const criticalIssues = openIssues.filter((item) => item.severity === "critical");
    const now = Date.now();
    const ruleTypeLabels = {
      profile_required: t("complianceRuleProfileRequired"),
      app_required: t("complianceRuleAppRequired"),
      passcode_required: t("complianceRulePasscodeRequired"),
	      minimum_os_version: t("complianceRuleMinimumOS"),
	      device_attestation_required: t("complianceRuleAttestationRequired"),
	      allowed_sim_iccid: t("complianceRuleAllowedSIM"),
	      custom_mdm_value: t("complianceRuleCustomValue")
	    };
	    const customComplianceOperators = [
	      ["equals", t("complianceRuleCustomOperatorEquals")],
	      ["not_equals", t("complianceRuleCustomOperatorNotEquals")],
	      ["contains", t("complianceRuleCustomOperatorContains")],
	      ["not_contains", t("complianceRuleCustomOperatorNotContains")],
	      ["starts_with", t("complianceRuleCustomOperatorStartsWith")],
	      ["ends_with", t("complianceRuleCustomOperatorEndsWith")],
	      ["greater_than", t("complianceRuleCustomOperatorGreaterThan")],
	      ["greater_or_equal", t("complianceRuleCustomOperatorGreaterOrEqual")],
	      ["less_than", t("complianceRuleCustomOperatorLessThan")],
	      ["less_or_equal", t("complianceRuleCustomOperatorLessOrEqual")],
	      ["exists", t("complianceRuleCustomOperatorExists")],
	      ["not_exists", t("complianceRuleCustomOperatorNotExists")]
	    ];
	    const customComplianceFieldSuggestions = [
	      ["os_version", t("devicesColOSVersion")],
	      ["device.device_name", t("devicesColName")],
	      ["device.product_name", t("devicesColModel")],
	      ["device.platform", t("complianceRulePlatform")],
	      ["device.current_iccid", "ICCID"],
	      ["device.allowed_iccid", "Allowed ICCID"],
	      ["security.PasscodePresent", t("complianceRulePasscodeRequired")],
	      ["network.ICCID", "ICCID"],
	      ["network.ServiceSubscriptions.0.ICCID", "ICCID SIM 1"],
	      ["network.ServiceSubscriptions.1.ICCID", "ICCID SIM 2"],
	      ["network.CurrentCarrierNetwork", t("deviceDetailsCurrentCarrier")],
	      ["network.SIMCarrierNetwork", t("deviceDetailsSIMCarrier")],
	      ["network.PhoneNumber", t("devicesColPhone")],
	      ["network.BatteryLevel", t("deviceDetailsBatteryLevel")],
	      ["network.IsRoaming", lang === "ru" ? "Роуминг" : "Roaming"],
	      ["network.DataRoamingEnabled", t("deviceDetailsDataRoaming")],
	      ["network.ModemFirmwareVersion", t("deviceDetailsModemFirmware")],
	      ["network.EID", t("deviceDetailsEID")]
	    ];
	    const customComplianceFieldQuery = String(complianceRuleForm.customField || "").trim().toLowerCase();
	    const visibleCustomComplianceFieldSuggestions = customComplianceFieldSuggestions.filter(([value, label]) => {
	      if (!customComplianceFieldQuery) return true;
	      return `${value} ${label}`.toLowerCase().includes(customComplianceFieldQuery);
	    });
	    const formatComplianceRuleTarget = (item) => {
	      if (item.type === "custom_mdm_value") {
	        const operatorLabel = customComplianceOperators.find(([value]) => value === item.custom_operator)?.[1] || item.custom_operator || "";
	        if (item.custom_operator === "exists" || item.custom_operator === "not_exists") {
	          return [item.custom_field, operatorLabel].filter(Boolean).join(" ");
	        }
	        return [item.custom_field, operatorLabel, item.custom_value].filter(Boolean).join(" ");
	      }
	      return item.target_name || item.minimum_os_version || "-";
	    };
	    const formatComplianceRuleNotifications = (item) => {
	      const channels = [];
	      if (item.notify_companion !== false) channels.push(t("complianceNotifyCompanionShort"));
      if (item.notify_email) {
        channels.push(item.notification_email
          ? `${t("complianceNotifyEmailShort")}: ${item.notification_email}`
          : t("complianceNotifyEmailShort"));
	      }
	      return channels.length ? channels.join(", ") : t("complianceNotifyNoneShort");
	    };
	    const formatComplianceRuleAutomation = (item) => {
	      const parts = [];
	      if (item.auto_remediation) {
	        parts.push(t("complianceAutoRemediationShort"));
	        if (Number(item.retry_hours || 0) > 0) {
	          parts.push(`${t("complianceRetryHours")}: ${item.retry_hours}`);
	        }
	      } else {
	        parts.push(t("complianceMonitoringOnlyShort"));
	      }
	      if (Number(item.escalation_hours || 0) > 0) {
	        parts.push(`${t("complianceEscalationHours")}: ${item.escalation_hours}`);
	      }
	      if (item.escalation_email) {
	        parts.push(`${t("complianceEscalationEmail")}: ${item.escalation_email}`);
	      }
	      return parts.join(", ");
	    };
	    const issueRuleOptions = complianceRules
      .map((item) => ({ id: item.id, label: item.name || item.id }))
      .sort((left, right) => left.label.localeCompare(right.label));
    const issueDeviceOptions = Array.from(
      new Map(
        complianceIssues
          .filter((item) => item.device_udid)
          .map((item) => [item.device_udid, item.device_name || item.device_udid])
      ).entries()
    )
      .map(([id, label]) => ({ id, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
    const filteredComplianceIssues = complianceIssues.filter((item) => {
      const status = String(complianceIssueFilters.status || "");
      if (status === "open" && item.status !== "open") return false;
      if (status === "critical" && !(item.status === "open" && item.severity === "critical")) return false;
      if (status === "overdue") {
        const dueAt = item.due_at ? new Date(item.due_at).getTime() : 0;
        if (!(item.status === "open" && dueAt > 0 && dueAt < now)) return false;
      }
      if (status === "resolved" && item.status !== "resolved") return false;
      if (status === "waived" && item.status !== "waived") return false;
      if (complianceIssueFilters.ruleId && item.rule_id !== complianceIssueFilters.ruleId) return false;
      if (complianceIssueFilters.deviceUDID && item.device_udid !== complianceIssueFilters.deviceUDID) return false;
      const query = String(complianceIssueFilters.search || "").trim().toLowerCase();
      if (query) {
        const haystack = [
          item.rule_name,
          item.rule_type,
          item.account_name,
          item.account_id,
          item.device_name,
          item.device_udid,
          item.reason_text,
          item.reason_code,
          item.target_name
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
    const eventLabels = {
      detected: t("complianceEventDetected"),
      reopened: t("complianceEventReopened"),
      action_started: t("complianceEventActionStarted"),
      confirmed: t("complianceEventConfirmed"),
      resolved: t("complianceEventResolved"),
      waived: t("complianceEventWaived")
    };
    const visibleComplianceIssues = filteredComplianceIssues.slice(0, complianceIssueVisibleCount);
    const targetOptions =
      complianceRuleForm.type === "profile_required"
        ? profiles.map((item) => ({ id: item.id, label: item.name || item.profile_name || item.id }))
        : complianceRuleForm.type === "app_required"
          ? appsCatalog.map((item) => ({ id: item.id, label: item.name || item.app_name || item.bundle_id || item.id }))
          : [];
    const selectedIssue = selectedComplianceIssueDetail?.item || complianceIssues.find((item) => item.id === selectedComplianceIssueID) || null;
    const selectedIssueEvents = selectedIssue
      ? (Array.isArray(selectedComplianceIssueDetail?.events) ? selectedComplianceIssueDetail.events : [])
        .filter((item) => item.issue_id === selectedIssue.id)
        .sort((left, right) => new Date(left.created_at || 0) - new Date(right.created_at || 0))
      : [];
    const visibleSelectedIssueEvents = expandedComplianceTimeline ? selectedIssueEvents : selectedIssueEvents.slice(-5);
    const detailRemediationHistory = selectedComplianceIssueDetail?.remediation_history || {};
    const detailProfileDeployments = Array.isArray(detailRemediationHistory.profile_deployments)
      ? detailRemediationHistory.profile_deployments
      : profileDeployments;
    const detailAppDeployments = Array.isArray(detailRemediationHistory.app_deployments)
      ? detailRemediationHistory.app_deployments
      : appDeployments;
    const selectedIssueRemediationDeployments = selectedIssue
      ? [
          ...(selectedIssue.remediation_type === "install_profile"
            ? detailProfileDeployments
              .filter((item) =>
                item.profile_id === selectedIssue.remediation_target_id &&
                item.device_udid === selectedIssue.device_udid
              )
              .map((item) => ({
                id: item.id,
                type: t("complianceRuleProfile"),
                target: item.profile_name || selectedIssue.target_name || item.profile_id,
                state: item.state,
                result: item.result_status || item.result_detail || "-",
                queuedAt: item.queued_at,
                updatedAt: item.updated_at
              }))
            : []),
          ...(selectedIssue.remediation_type === "install_app"
            ? detailAppDeployments
              .filter((item) =>
                item.app_id === selectedIssue.remediation_target_id &&
                item.device_udid === selectedIssue.device_udid
              )
              .map((item) => ({
                id: item.id,
                type: t("complianceRuleApp"),
                target: item.app_name || selectedIssue.target_name || item.app_id,
                state: item.state,
                result: item.result_status || item.result_detail || "-",
                queuedAt: item.queued_at,
                updatedAt: item.updated_at
              }))
            : [])
        ].sort((left, right) => new Date(right.updatedAt || right.queuedAt || 0) - new Date(left.updatedAt || left.queuedAt || 0))
      : [];
    const formatEvidenceLabel = (value) =>
      String(value || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    const formatEvidenceValue = (value) => {
      if (value === null || value === undefined || value === "") return "-";
      if (typeof value === "boolean") return value ? t("yes") : t("no");
      if (Array.isArray(value)) return value.length ? value.map(formatEvidenceValue).join(", ") : "-";
      if (typeof value === "object") {
        return Object.entries(value)
          .map(([key, nestedValue]) => `${formatEvidenceLabel(key)}: ${formatEvidenceValue(nestedValue)}`)
          .join("; ");
      }
      return String(value);
    };
    const renderEvidenceBlock = (title, value) => {
      const entries = value && typeof value === "object" && !Array.isArray(value) ? Object.entries(value) : [];
      return (
        <section className="compliance-evidence-section">
          <h4>{title}</h4>
          {entries.length > 0 ? (
            <dl className="compliance-evidence-list">
              {entries.map(([key, entryValue]) => (
                <div key={key}>
                  <dt>{formatEvidenceLabel(key)}</dt>
                  <dd>{formatEvidenceValue(entryValue)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="muted">{t("complianceEvidenceNoData")}</p>
          )}
        </section>
      );
    };
    const renderComplianceIssueDeviceLink = (issue) => {
      const label = issue?.device_name || issue?.device_udid || "-";
      if (!issue?.device_udid) return label;
      const href = deviceDetailsHash("devicesDetailAbout", issue.device_udid);
      return (
        <a
          className="table-link-anchor"
          href={href}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
            event.preventDefault();
            setSelectedComplianceIssueID("");
            openDeviceView(issue.device_udid, "devicesDetailAbout");
          }}
        >
          {label}
        </a>
      );
    };
    const getComplianceIssueLastAction = (issue) => {
      if (!issue) return { label: "-", at: "" };
      if (issue.status === "waived") return { label: t("complianceIssueLastActionWaived"), at: issue.resolved_at || issue.updated_at };
      if (issue.resolved_at) return { label: t("complianceIssueLastActionResolved"), at: issue.resolved_at };
      if (issue.confirmed_at) return { label: t("complianceIssueLastActionConfirmed"), at: issue.confirmed_at };
      if (issue.action_started_at) return { label: t("complianceIssueLastActionStarted"), at: issue.action_started_at };
      return { label: t("complianceIssueLastActionDetected"), at: issue.last_detected_at || issue.first_detected_at || issue.updated_at };
    };
    const getComplianceIssueStatusLabel = (issue) => {
      if (issue?.status === "open") return t("complianceActionRequired");
      if (issue?.status === "waived") return t("complianceIssueWaive");
      return t("complianceResolved");
    };
    const renderComplianceIssueLastAction = (issue) => {
      const action = getComplianceIssueLastAction(issue);
      return (
        <span className="compliance-action-summary">
          <strong>{action.label}</strong>
          <span>{formatTimestamp(action.at)}</span>
        </span>
      );
    };
	    const buildComplianceIssueNarrative = (issue) => {
	      if (!issue) return "-";
	      return issue.reason_text || issue.reason_code || "-";
	    };
    const buildComplianceIssueNextStep = (issue) => {
      if (!issue) return "-";
      if (issue.status !== "open") return getComplianceIssueLastAction(issue).label;
      if (issue.action_started_at) return t("complianceIssueNextStepAlreadyStarted");
      if (issue.remediation_type === "install_profile" || issue.remediation_type === "install_app") {
        return t("complianceIssueNextStepStartRemediation");
      }
      const details = issue.details && typeof issue.details === "object" ? issue.details : {};
      const remediation = formatEvidenceValue(details.remediation);
      if (remediation !== "-") return remediation;
      return t("complianceIssueNextStepReviewDevice");
    };
    const getComplianceIssueRemediationStatus = (issue) => {
      if (!issue) return { label: "-", className: "status-pill" };
      if (issue.status === "waived") return { label: t("complianceRemediationStatusWaived"), className: "status-pill muted" };
      if (issue.status === "resolved" || issue.resolved_at) return { label: t("complianceRemediationStatusResolved"), className: "status-pill ok" };
      if (issue.confirmed_at) return { label: t("complianceRemediationStatusConfirmed"), className: "status-pill warning" };
      if (issue.action_started_at) return { label: t("complianceRemediationStatusWaitingSync"), className: "status-pill warning" };
      if (issue.notification_id) return { label: t("complianceRemediationStatusInstructionSent"), className: "status-pill warning" };
      return { label: t("complianceRemediationStatusWaitingAction"), className: "status-pill danger" };
    };
    const renderComplianceIssueRemediationStatus = (issue) => {
      const status = getComplianceIssueRemediationStatus(issue);
      return <span className={status.className}>{status.label}</span>;
    };
    const getComplianceIssuePrimaryActionLabel = (issue) => {
      if (!issue) return t("complianceIssueStartRemediation");
      if (issue.remediation_type === "install_profile") return t("complianceIssueInstallProfile");
      if (issue.remediation_type === "install_app") return t("complianceIssueInstallApp");
      if (issue.rule_type === "device_attestation_required") return t("complianceIssueRequestAttestation");
      if (issue.rule_type === "minimum_os_version") return t("complianceIssueSendOSUpdateInstruction");
      if (issue.rule_type === "passcode_required") return t("complianceIssueSendPasscodeInstruction");
      if (issue.rule_type === "allowed_sim_iccid") return t("complianceIssueSendSIMInstruction");
      return t("complianceIssueStartRemediation");
    };
    const getComplianceIssueDeviceInstruction = (issue) => {
      if (!issue) return "-";
      if (issue.status !== "open") return getComplianceIssueLastAction(issue).label;
      if (issue.remediation_type === "install_profile") return t("complianceInstructionProfile");
      if (issue.remediation_type === "install_app") return t("complianceInstructionApp");
      if (issue.rule_type === "minimum_os_version") return t("complianceInstructionMinimumOS");
      if (issue.rule_type === "passcode_required") return t("complianceInstructionPasscode");
      if (issue.rule_type === "device_attestation_required") return t("complianceInstructionAttestation");
      if (issue.rule_type === "allowed_sim_iccid") return t("complianceInstructionSIM");
      return buildComplianceIssueNextStep(issue);
    };
    const getComplianceIssueActionURL = (issue) => {
      if (!issue?.id) return "turukagent://compliance";
      let path = "overview";
      if (issue.remediation_type === "install_profile") path = "remediate/profile";
      else if (issue.remediation_type === "install_app") path = "remediate/app";
      else if (issue.rule_type === "minimum_os_version") path = "settings/software-update";
      else if (issue.rule_type === "passcode_required") path = "settings/passcode";
      else if (issue.rule_type === "allowed_sim_iccid") path = "settings/cellular";
      else if (issue.remediation_type === "refresh") path = "refresh";
      return `turukagent://compliance/${path}?issue_id=${encodeURIComponent(issue.id)}`;
    };
    const buildComplianceIssueAdminEvidence = (issue) => {
      if (!issue) return {};
      return {
        remediation_status: getComplianceIssueRemediationStatus(issue).label,
        remediation_type: issue.remediation_type || "-",
        action_url: getComplianceIssueActionURL(issue),
        notification_id: issue.notification_id || "-",
        first_detected_at: formatTimestamp(issue.first_detected_at),
        last_detected_at: formatTimestamp(issue.last_detected_at),
        action_started_at: formatTimestamp(issue.action_started_at),
        confirmed_at: formatTimestamp(issue.confirmed_at),
        resolved_at: formatTimestamp(issue.resolved_at)
      };
    };
    const runComplianceIssuePrimaryAction = (issue) => {
      const action = issue?.remediation_type === "open_settings" ? "remind" : "start";
      return runComplianceIssueAction(issue, action);
    };
    const exportFilteredComplianceIssues = () => {
      try {
        const rows = [
          [
            "status",
            "severity",
            "rule",
            "rule_type",
            "account",
            "account_id",
            "device",
            "device_udid",
            "reason",
            "reason_code",
            "due_at",
            "first_detected_at",
            "last_detected_at",
            "last_action",
            "last_action_at",
            "remediation_status",
            "device_instruction",
            "action_started_at",
            "confirmed_at",
            "resolved_at",
            "target",
            "remediation_type",
            "expected",
            "actual",
            "source",
            "remediation"
          ],
          ...filteredComplianceIssues.map((item) => {
            const details = item.details && typeof item.details === "object" ? item.details : {};
            const lastAction = getComplianceIssueLastAction(item);
            return [
              item.status || "",
              item.severity || "",
              item.rule_name || "",
              item.rule_type || "",
              item.account_name || "",
              item.account_id || "",
              item.device_name || "",
              item.device_udid || "",
              item.reason_text || "",
              item.reason_code || "",
              item.due_at || "",
              item.first_detected_at || "",
              item.last_detected_at || "",
              lastAction.label || "",
              lastAction.at || "",
              getComplianceIssueRemediationStatus(item).label || "",
              getComplianceIssueDeviceInstruction(item) || "",
              item.action_started_at || "",
              item.confirmed_at || "",
              item.resolved_at || "",
              item.target_name || item.remediation_target_id || "",
              item.remediation_type || "",
              JSON.stringify(details.expected || {}),
              JSON.stringify(details.actual || {}),
              JSON.stringify(details.source || {}),
              JSON.stringify(details.remediation || {})
            ];
          })
        ];
        const stamp = new Date().toISOString().slice(0, 10);
        downloadCsvFile(`turuk-compliance-issues-${stamp}.csv`, rows);
        setInfoMessage(t("complianceIssueExportDone"));
      } catch (err) {
        setErrorMessage(`${t("complianceIssueExportFailed")}: ${err.message}`);
      }
    };
    const downloadSelectedComplianceEvidenceReport = () => {
      if (!selectedIssue) return;
      try {
        const details = selectedIssue.details && typeof selectedIssue.details === "object" ? selectedIssue.details : {};
        const remediationStatus = getComplianceIssueRemediationStatus(selectedIssue);
        const renderKeyValues = (title, value) => {
          const entries = value && typeof value === "object" && !Array.isArray(value) ? Object.entries(value) : [];
          const body = entries.length
            ? entries
              .map(([key, entryValue]) => `
                <tr>
                  <th>${escapeHtml(formatEvidenceLabel(key))}</th>
                  <td>${escapeHtml(formatEvidenceValue(entryValue))}</td>
                </tr>
              `)
              .join("")
            : `<tr><td colspan="2">${escapeHtml(t("complianceEvidenceNoData"))}</td></tr>`;
          return `<section><h2>${escapeHtml(title)}</h2><table>${body}</table></section>`;
        };
        const deploymentRows = selectedIssueRemediationDeployments.length
          ? selectedIssueRemediationDeployments
            .map((item) => `
              <tr>
                <td>${escapeHtml(item.type)}</td>
                <td>${escapeHtml(item.target || "-")}</td>
                <td>${escapeHtml(item.state || "-")}</td>
                <td>${escapeHtml(item.result || "-")}</td>
                <td>${escapeHtml(formatTimestamp(item.queuedAt))}</td>
                <td>${escapeHtml(formatTimestamp(item.updatedAt))}</td>
              </tr>
            `)
            .join("")
          : `<tr><td colspan="6">${escapeHtml(t("complianceEvidenceNoRemediationHistory"))}</td></tr>`;
        const eventRows = selectedIssueEvents.length
          ? selectedIssueEvents
            .map((item) => `
              <tr>
                <td>${escapeHtml(eventLabels[item.event_type] || item.event_type)}</td>
                <td>${escapeHtml(formatTimestamp(item.created_at))}</td>
              </tr>
            `)
            .join("")
          : `<tr><td colspan="2">${escapeHtml(t("complianceNoHistory"))}</td></tr>`;
        const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(t("complianceEvidenceTitle"))}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; margin: 32px; color: #172033; }
    h1 { margin: 0 0 8px; font-size: 28px; }
    h2 { margin: 24px 0 10px; font-size: 18px; }
    .muted { color: #667085; }
    .summary { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 24px; margin: 20px 0; }
    .summary div { border-bottom: 1px solid #e5e7eb; padding: 8px 0; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
    th, td { text-align: left; vertical-align: top; border: 1px solid #e5e7eb; padding: 8px 10px; }
    th { width: 240px; background: #f8fafc; }
    pre { white-space: pre-wrap; overflow-wrap: anywhere; background: #0f172a; color: #e2e8f0; padding: 12px; border-radius: 8px; }
  </style>
</head>
<body>
  <h1>${escapeHtml(selectedIssue.rule_name || ruleTypeLabels[selectedIssue.rule_type] || selectedIssue.rule_type)}</h1>
  <p class="muted">${escapeHtml(t("complianceEvidenceTitle"))} · ${escapeHtml(new Date().toLocaleString())}</p>
  <div class="summary">
    <div><strong>${escapeHtml(t("tableStatus"))}</strong><br>${escapeHtml(selectedIssue.status || "-")}</div>
    <div><strong>${escapeHtml(t("complianceRuleSeverity"))}</strong><br>${escapeHtml(selectedIssue.severity || "-")}</div>
    <div><strong>${escapeHtml(t("accountsTitle"))}</strong><br>${escapeHtml(selectedIssue.account_name || selectedIssue.account_id || "-")}</div>
    <div><strong>${escapeHtml(t("devicesColName"))}</strong><br>${escapeHtml(selectedIssue.device_name || selectedIssue.device_udid || "-")}</div>
    <div><strong>${escapeHtml(t("complianceReason"))}</strong><br>${escapeHtml(selectedIssue.reason_text || selectedIssue.reason_code || "-")}</div>
    <div><strong>${escapeHtml(t("complianceDueAt"))}</strong><br>${escapeHtml(formatTimestamp(selectedIssue.due_at))}</div>
    <div><strong>${escapeHtml(t("complianceRemediationStatus"))}</strong><br>${escapeHtml(remediationStatus.label)}</div>
    <div><strong>${escapeHtml(t("complianceDeviceInstruction"))}</strong><br>${escapeHtml(getComplianceIssueDeviceInstruction(selectedIssue))}</div>
	  </div>
	  ${renderKeyValues(t("complianceEvidenceRule"), {
	    rule_type: ruleTypeLabels[selectedIssue.rule_type] || selectedIssue.rule_type,
	    group: details.group_name || details.group_id || "-"
	  })}
	  ${renderKeyValues(t("complianceEvidenceAdmin"), buildComplianceIssueAdminEvidence(selectedIssue))}
  <section>
    <h2>${escapeHtml(t("complianceEvidenceRemediationHistory"))}</h2>
    <table>
      <thead><tr><th>${escapeHtml(t("complianceEvidenceDeploymentType"))}</th><th>${escapeHtml(t("complianceEvidenceDeploymentTarget"))}</th><th>${escapeHtml(t("complianceEvidenceDeploymentState"))}</th><th>${escapeHtml(t("complianceEvidenceDeploymentResult"))}</th><th>${escapeHtml(t("complianceEvidenceDeploymentQueuedAt"))}</th><th>${escapeHtml(t("complianceEvidenceDeploymentUpdatedAt"))}</th></tr></thead>
      <tbody>${deploymentRows}</tbody>
    </table>
  </section>
  <section>
    <h2>${escapeHtml(t("complianceEvidenceTimeline"))}</h2>
    <table><thead><tr><th>${escapeHtml(t("complianceEvent"))}</th><th>${escapeHtml(t("complianceEventAt"))}</th></tr></thead><tbody>${eventRows}</tbody></table>
  </section>
	</body>
	</html>`;
        const stamp = new Date().toISOString().slice(0, 10);
        const issueName = String(selectedIssue.rule_name || selectedIssue.rule_type || selectedIssue.id || "issue")
          .toLowerCase()
          .replace(/[^a-z0-9а-яё]+/gi, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 60) || "issue";
        downloadTextFile(`turuk-compliance-evidence-${issueName}-${stamp}.html`, html, "text/html;charset=utf-8");
        setInfoMessage(t("complianceEvidenceReportDownloaded"));
      } catch (err) {
        setErrorMessage(`${t("complianceEvidenceReportFailed")}: ${err.message}`);
      }
    };
    const renderComplianceIssueDrawer = () => {
      if (!selectedIssue) return null;
      const details = selectedIssue.details && typeof selectedIssue.details === "object" ? selectedIssue.details : {};
      const statusClass =
        selectedIssue.status === "open"
          ? selectedIssue.severity === "critical"
            ? "status-pill danger"
            : "status-pill warning"
          : "status-pill ok";
      return (
        <div className="modal-backdrop" role="presentation" onClick={() => setSelectedComplianceIssueID("")}>
          <div
            className="modal-card compliance-evidence-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={t("complianceEvidenceTitle")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-heading">
              <div>
                <p className="eyebrow">{t("complianceEvidenceTitle")}</p>
                <h3>{selectedIssue.rule_name || ruleTypeLabels[selectedIssue.rule_type] || selectedIssue.rule_type}</h3>
                {selectedComplianceIssueLoading && <p className="muted">{t("loading")}</p>}
              </div>
              <button type="button" className="secondary" onClick={() => setSelectedComplianceIssueID("")}>
                {t("complianceEvidenceClose")}
              </button>
            </div>
            <div className="compliance-evidence-meta">
              <span className={statusClass}>
                {getComplianceIssueStatusLabel(selectedIssue)}
              </span>
              <span>{selectedIssue.severity || "-"}</span>
              <span>{selectedIssue.due_at ? formatTimestamp(selectedIssue.due_at) : t("complianceImmediately")}</span>
            </div>
            <div className="compliance-evidence-reason">
              <strong>{selectedIssue.reason_text || selectedIssue.reason_code || "-"}</strong>
              <span>{selectedIssue.account_name || selectedIssue.account_id || "-"}</span>
              <span>{renderComplianceIssueDeviceLink(selectedIssue)}</span>
            </div>
            <div className="compliance-evidence-actions">
              <button type="button" className="secondary" onClick={downloadSelectedComplianceEvidenceReport}>
                {t("complianceEvidenceDownloadReport")}
              </button>
              {canEditCompliance && (
                <>
                {selectedIssue.status === "open" &&
                  !selectedIssue.action_started_at && (
                    <button type="button" onClick={() => runComplianceIssuePrimaryAction(selectedIssue)}>
                      {getComplianceIssuePrimaryActionLabel(selectedIssue)}
                    </button>
                  )}
                <button type="button" className="secondary" onClick={() => runComplianceIssueAction(selectedIssue, "reevaluate")}>
                  {t("complianceIssueReevaluate")}
                </button>
                {selectedIssue.status === "open" && (
                  <>
                    <button type="button" className="secondary" onClick={() => runComplianceIssueAction(selectedIssue, "remind")}>
                      {t("complianceIssueSendReminder")}
                    </button>
                    <button type="button" className="secondary" onClick={() => runComplianceIssueAction(selectedIssue, "resolve")}>
                      {t("complianceIssueResolve")}
                    </button>
                    <button type="button" className="secondary" onClick={() => runComplianceIssueAction(selectedIssue, "waive")}>
                      {t("complianceIssueWaive")}
                    </button>
                  </>
                )}
                </>
              )}
            </div>
            <div className="compliance-evidence-grid">
              <section className="compliance-evidence-section">
                <h4>{t("complianceEvidenceWhatHappened")}</h4>
                <p>{buildComplianceIssueNarrative(selectedIssue)}</p>
              </section>
              <section className="compliance-evidence-section">
                <h4>{t("complianceEvidenceNextStep")}</h4>
                <p>{getComplianceIssueDeviceInstruction(selectedIssue)}</p>
              </section>
            </div>
            <div className="compliance-evidence-grid">
	              {renderEvidenceBlock(t("complianceEvidenceRule"), {
	                rule_type: ruleTypeLabels[selectedIssue.rule_type] || selectedIssue.rule_type,
	                group: details.group_name || details.group_id || "-"
	              })}
	              {renderEvidenceBlock(t("complianceEvidenceAdmin"), buildComplianceIssueAdminEvidence(selectedIssue))}
            </div>
            <section className="compliance-evidence-section">
              <h4>{t("complianceEvidenceRemediationHistory")}</h4>
              {selectedIssueRemediationDeployments.length > 0 ? (
                <div className="devices-table-wrap compliance-remediation-table">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("complianceEvidenceDeploymentType")}</th>
                        <th>{t("complianceEvidenceDeploymentTarget")}</th>
                        <th>{t("complianceEvidenceDeploymentState")}</th>
                        <th>{t("complianceEvidenceDeploymentResult")}</th>
                        <th>{t("complianceEvidenceDeploymentQueuedAt")}</th>
                        <th>{t("complianceEvidenceDeploymentUpdatedAt")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedIssueRemediationDeployments.map((item) => (
                        <tr key={item.id}>
                          <td>{item.type}</td>
                          <td>{item.target || "-"}</td>
                          <td>{item.state || "-"}</td>
                          <td>{item.result || "-"}</td>
                          <td>{formatTimestamp(item.queuedAt)}</td>
                          <td>{formatTimestamp(item.updatedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="muted">{t("complianceEvidenceNoRemediationHistory")}</p>
              )}
            </section>
            <section className="compliance-evidence-section">
              <h4>{t("complianceEvidenceTimeline")}</h4>
              {selectedIssueEvents.length > 0 ? (
                <>
                  <ol className="compliance-timeline">
                    {visibleSelectedIssueEvents.map((item) => (
                      <li key={item.id}>
                        <strong>{eventLabels[item.event_type] || item.event_type}</strong>
                        <span>{formatTimestamp(item.created_at)}</span>
                      </li>
                    ))}
                  </ol>
                  {selectedIssueEvents.length > 5 && (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => setExpandedComplianceTimeline((prev) => !prev)}
                    >
                      {expandedComplianceTimeline
                        ? t("complianceEvidenceShowLessTimeline")
                        : t("complianceEvidenceShowMoreTimeline")}
                    </button>
                  )}
                </>
              ) : (
                <p className="muted">{t("complianceNoHistory")}</p>
              )}
            </section>
	          </div>
        </div>
      );
    };

    return (
      <div className="page compliance-page">
        <div className="panel compliance-hero">
          <div>
            <h2>{t("complianceCenterTitle")}</h2>
            <p className="muted">{t("complianceCenterSubtitle")}</p>
          </div>
          <button type="button" onClick={evaluateCompliance}>
            {t("complianceEvaluate")}
          </button>
        </div>

        <div className="compliance-summary-grid">
          <div className="panel compliance-summary-card compliant">
            <span>{t("complianceCompliant")}</span>
            <strong>{Math.max(0, devices.length - new Set(openIssues.map((item) => item.device_udid).filter(Boolean)).size)}</strong>
          </div>
          <div className="panel compliance-summary-card warning">
            <span>{t("complianceActionRequired")}</span>
            <strong>{openIssues.length}</strong>
          </div>
          <div className="panel compliance-summary-card critical">
            <span>{t("complianceCritical")}</span>
            <strong>{criticalIssues.length}</strong>
          </div>
          <div className="panel compliance-summary-card resolved">
            <span>{t("complianceResolved")}</span>
            <strong>{resolvedIssues.length}</strong>
          </div>
        </div>

        {canEditCompliance && (
          <form className="panel compliance-rule-form" onSubmit={saveComplianceRule}>
            <div className="panel-heading">
              <div>
                <h3>{complianceRuleForm.id ? t("complianceRuleEdit") : t("complianceRuleCreate")}</h3>
                <p className="muted">{t("complianceRuleHint")}</p>
              </div>
              {complianceRuleForm.id && (
                <button type="button" className="secondary" onClick={resetComplianceRuleForm}>
                  {t("cancel")}
                </button>
              )}
            </div>
            <div className="compliance-form-grid">
              <label>
                {t("complianceRuleTemplate")}
	                <select
	                  className="text-input"
	                  value={complianceRuleTemplateKey}
	                  onChange={(event) => applyComplianceRuleTemplate(event.target.value)}
		                >
	                  <option value="">{t("complianceRuleTemplatePlaceholder")}</option>
	                  <option value="minimum_os">{t("complianceRuleTemplateMinimumOS")}</option>
                  <option value="passcode">{t("complianceRuleTemplatePasscode")}</option>
                  <option value="profile">{t("complianceRuleTemplateProfile")}</option>
                  <option value="app">{t("complianceRuleTemplateApp")}</option>
                  <option value="attestation">{t("complianceRuleTemplateAttestation")}</option>
                  <option value="allowed_sim">{t("complianceRuleTemplateSIM")}</option>
                  <option value="custom">{t("complianceRuleTemplateCustom")}</option>
                </select>
              </label>
              <label>
                {t("complianceRuleName")}
                <input
                  className="text-input"
                  required
                  value={complianceRuleForm.name}
                  onChange={(event) => setComplianceRuleForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </label>
	              <div className="compliance-multiselect-field" ref={complianceGroupDropdownRef}>
                <span>{t("complianceRuleGroups")}</span>
                <div className="compliance-multiselect">
                  <button
                    type="button"
                    className="text-input compliance-multiselect-trigger"
                    onClick={() => setComplianceGroupDropdownOpen((value) => !value)}
                    aria-expanded={complianceGroupDropdownOpen}
                  >
                    <span>
                      {complianceRuleForm.groupIds.length
                        ? groups
                          .filter((item) => complianceRuleForm.groupIds.includes(item.id))
                          .map((item) => item.name || item.id)
                          .join(", ")
                        : t("complianceRuleGroupsPlaceholder")}
                    </span>
                    <span aria-hidden="true">⌄</span>
                  </button>
                  {complianceGroupDropdownOpen && (
                    <div className="compliance-multiselect-menu">
                      {groups.map((item) => {
                        const checked = complianceRuleForm.groupIds.includes(item.id);
                        return (
                          <label key={item.id} className="compliance-multiselect-option">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                setComplianceRuleForm((prev) => {
                                  const nextGroupIds = event.target.checked
                                    ? Array.from(new Set([...prev.groupIds, item.id]))
                                    : prev.groupIds.filter((id) => id !== item.id);
                                  return {
                                    ...prev,
                                    groupId: nextGroupIds[0] || "",
                                    groupIds: nextGroupIds
                                  };
                                });
                              }}
                            />
                            <span>{item.name || item.id}</span>
                          </label>
                        );
                      })}
                      {groups.length === 0 && (
                        <div className="compliance-multiselect-empty">{t("groupsNoItems")}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <label>
                {t("complianceRuleType")}
                <select
	                  className="text-input"
	                  value={complianceRuleForm.type}
	                  onChange={(event) => {
	                    setComplianceRuleTemplateKey("");
		                    setComplianceRuleForm((prev) => ({
		                      ...prev,
			                      type: event.target.value,
		                      targetId: "",
		                      minimumOSVersion: "",
		                      customField: event.target.value === "custom_mdm_value" ? prev.customField : "",
		                      customOperator: event.target.value === "custom_mdm_value" ? prev.customOperator || "equals" : "equals",
		                      customValue: event.target.value === "custom_mdm_value" ? prev.customValue : "",
		                      autoRemediation: ["profile_required", "app_required", "minimum_os_version"].includes(event.target.value)
	                        ? prev.autoRemediation
	                        : false,
	                      retryHours: ["profile_required", "app_required", "minimum_os_version"].includes(event.target.value)
	                        ? prev.retryHours
		                        : 0
		                    }))
		                  }}
	                >
                  {Object.entries(ruleTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                {t("complianceRulePlatform")}
                <select
                  className="text-input"
                  value={complianceRuleForm.platform}
                  onChange={(event) => setComplianceRuleForm((prev) => ({ ...prev, platform: event.target.value }))}
                >
                  <option value="all">{t("all")}</option>
                  <option value="ios">iOS/iPadOS</option>
                  <option value="macos">macOS</option>
                  <option value="android">Android</option>
                  <option value="windows">Windows</option>
                </select>
              </label>
              {(complianceRuleForm.type === "profile_required" || complianceRuleForm.type === "app_required") && (
                <label>
                  {complianceRuleForm.type === "profile_required"
                    ? t("complianceRuleProfile")
                    : t("complianceRuleApp")}
                  <select
                    className="text-input"
                    required
                    value={complianceRuleForm.targetId}
                    onChange={(event) => setComplianceRuleForm((prev) => ({ ...prev, targetId: event.target.value }))}
                  >
                    <option value="">-</option>
                    {targetOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                  </select>
                </label>
              )}
	              {complianceRuleForm.type === "minimum_os_version" && (
                <label>
                  {t("complianceRuleMinimumOSVersion")}
                  <input
                    className="text-input"
                    required
                    placeholder="18.5"
                    value={complianceRuleForm.minimumOSVersion}
                    onChange={(event) =>
                      setComplianceRuleForm((prev) => ({ ...prev, minimumOSVersion: event.target.value }))
                    }
                  />
                </label>
              )}
              <label>
                {t("complianceRuleSeverity")}
                <select
                  className="text-input"
                  value={complianceRuleForm.severity}
                  onChange={(event) => setComplianceRuleForm((prev) => ({ ...prev, severity: event.target.value }))}
                >
                  <option value="warning">{t("notificationsSeverityWarning")}</option>
                  <option value="critical">{t("notificationsSeverityCritical")}</option>
                </select>
              </label>
              <label>
                {t("complianceRuleDeadlineHours")}
                <input
                  className="text-input"
                  type="number"
                  min="0"
                  max="8760"
                  value={complianceRuleForm.deadlineHours}
                  onChange={(event) =>
                    setComplianceRuleForm((prev) => ({ ...prev, deadlineHours: event.target.value }))
                  }
                />
              </label>
              <label className="compliance-checkbox-label">
                <input
                  type="checkbox"
                  checked={complianceRuleForm.isActive}
                  onChange={(event) => setComplianceRuleForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
                {t("complianceRuleActive")}
              </label>
              <label className="compliance-checkbox-label">
                <input
                  type="checkbox"
                  checked={complianceRuleForm.notifyCompanion}
                  onChange={(event) =>
                    setComplianceRuleForm((prev) => ({ ...prev, notifyCompanion: event.target.checked }))
                  }
                />
                {t("complianceNotifyCompanion")}
              </label>
              <label className="compliance-checkbox-label">
                <input
                  type="checkbox"
                  checked={complianceRuleForm.notifyEmail}
                  onChange={(event) =>
                    setComplianceRuleForm((prev) => ({ ...prev, notifyEmail: event.target.checked }))
                  }
                />
                {t("complianceNotifyEmail")}
              </label>
	              {complianceRuleForm.notifyEmail && (
	                <label>
	                  {t("complianceNotificationEmail")}
	                  <input
                    className="text-input"
                    type="email"
                    required
                    placeholder="security@example.com"
                    value={complianceRuleForm.notificationEmail}
                    onChange={(event) =>
                      setComplianceRuleForm((prev) => ({ ...prev, notificationEmail: event.target.value }))
                    }
	                  />
	                </label>
	              )}
	              {complianceRuleForm.type === "custom_mdm_value" && (
	                <>
		                  <label className="compliance-field-picker" ref={complianceFieldDropdownRef}>
		                    {t("complianceRuleCustomField")}
			                    <input
			                      className="text-input"
			                      required
			                      placeholder="network.ServiceSubscriptions.0.ICCID"
		                      value={complianceRuleForm.customField}
		                      onFocus={() => setComplianceFieldDropdownOpen(true)}
		                      onChange={(event) => {
		                        setComplianceRuleForm((prev) => ({ ...prev, customField: event.target.value }));
		                        setComplianceFieldDropdownOpen(true);
		                      }}
			                    />
		                    {complianceFieldDropdownOpen && (
		                      <div className="compliance-field-picker-menu">
		                        {visibleCustomComplianceFieldSuggestions.map(([value, label]) => (
		                          <button
		                            key={value}
		                            type="button"
		                            className={value === complianceRuleForm.customField ? "active" : ""}
		                            onMouseDown={(event) => event.preventDefault()}
		                            onClick={() => {
		                              setComplianceRuleForm((prev) => ({ ...prev, customField: value }));
		                              setComplianceFieldDropdownOpen(false);
		                            }}
			                          >
			                            <strong>{label}</strong>
			                            <span>{value}</span>
			                          </button>
		                        ))}
		                        {visibleCustomComplianceFieldSuggestions.length === 0 && (
		                          <div className="compliance-field-picker-empty">
		                            {complianceRuleForm.customField}
		                          </div>
		                        )}
		                      </div>
		                    )}
		                    <span className="field-help">{t("complianceRuleCustomFieldHint")}</span>
		                  </label>
	                  <label>
	                    {t("complianceRuleCustomOperator")}
	                    <select
	                      className="text-input"
	                      value={complianceRuleForm.customOperator}
	                      onChange={(event) =>
	                        setComplianceRuleForm((prev) => ({ ...prev, customOperator: event.target.value }))
	                      }
	                    >
	                      {customComplianceOperators.map(([value, label]) => (
	                        <option key={value} value={value}>{label}</option>
	                      ))}
	                    </select>
	                  </label>
	                  {!["exists", "not_exists"].includes(complianceRuleForm.customOperator) && (
	                    <label>
	                      {t("complianceRuleCustomValueInput")}
	                      <input
	                        className="text-input"
	                        required
	                        value={complianceRuleForm.customValue}
	                        onChange={(event) =>
	                          setComplianceRuleForm((prev) => ({ ...prev, customValue: event.target.value }))
	                        }
	                      />
	                    </label>
	                  )}
	                </>
	              )}
	              {["profile_required", "app_required", "minimum_os_version"].includes(complianceRuleForm.type) && (
	                <label className="compliance-checkbox-label">
	                  <input
	                    type="checkbox"
	                    checked={complianceRuleForm.autoRemediation}
	                    onChange={(event) =>
	                      setComplianceRuleForm((prev) => ({
	                        ...prev,
	                        autoRemediation: event.target.checked,
	                        retryHours: event.target.checked ? prev.retryHours : 0
	                      }))
	                    }
	                  />
	                  {complianceRuleForm.type === "profile_required"
	                    ? t("complianceAutoRemediationProfile")
	                    : complianceRuleForm.type === "app_required"
	                      ? t("complianceAutoRemediationApp")
	                      : t("complianceAutoRemediationOS")}
	                </label>
	              )}
	              {complianceRuleForm.autoRemediation && (
	                <label>
	                  {t("complianceRetryHours")}
	                  <input
	                    className="text-input"
	                    type="number"
	                    min="0"
	                    max="8760"
	                    value={complianceRuleForm.retryHours}
	                    onChange={(event) =>
	                      setComplianceRuleForm((prev) => ({ ...prev, retryHours: event.target.value }))
	                    }
	                  />
	                </label>
	              )}
	              <label>
	                {t("complianceEscalationHours")}
	                <input
	                  className="text-input"
	                  type="number"
	                  min="0"
	                  max="8760"
	                  value={complianceRuleForm.escalationHours}
	                  onChange={(event) =>
	                    setComplianceRuleForm((prev) => ({ ...prev, escalationHours: event.target.value }))
	                  }
	                />
	              </label>
	              {Number(complianceRuleForm.escalationHours || 0) > 0 && (
	                <label>
	                  {t("complianceEscalationEmail")}
	                  <input
	                    className="text-input"
	                    type="email"
	                    placeholder="security@example.com"
	                    value={complianceRuleForm.escalationEmail}
	                    onChange={(event) =>
	                      setComplianceRuleForm((prev) => ({ ...prev, escalationEmail: event.target.value }))
	                    }
	                  />
	                </label>
	              )}
	            </div>
            <button type="submit">{t("save")}</button>
          </form>
        )}

        <div className="panel">
          <h3>{t("complianceRulesTitle")}</h3>
          <div className="devices-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("complianceRuleName")}</th>
                  <th>{t("complianceRuleGroup")}</th>
                  <th>{t("complianceRuleType")}</th>
                  <th>{t("complianceRuleTarget")}</th>
	                  <th>{t("complianceRuleDeadline")}</th>
	                  <th>{t("complianceRuleNotifications")}</th>
	                  <th>{t("complianceRuleAutomation")}</th>
	                  <th>{t("tableStatus")}</th>
                  {canEditCompliance && <th>{t("tableAction")}</th>}
                </tr>
              </thead>
              <tbody>
                {complianceRules.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.group_name || "-"}</td>
                    <td>{ruleTypeLabels[item.type] || item.type}</td>
	                    <td>{formatComplianceRuleTarget(item)}</td>
	                    <td>{item.deadline_hours ? `${item.deadline_hours} ${t("hoursShort")}` : t("complianceImmediately")}</td>
	                    <td>{formatComplianceRuleNotifications(item)}</td>
	                    <td>{formatComplianceRuleAutomation(item)}</td>
	                    <td>{item.is_active ? t("active") : t("inactive")}</td>
                    {canEditCompliance && (
                      <td className="compliance-table-actions">
                        <button type="button" className="secondary" onClick={() => editComplianceRule(item)}>
                          {t("edit")}
                        </button>
                        <button type="button" className="danger" onClick={() => deleteComplianceRule(item)}>
                          {t("delete")}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {complianceRules.length === 0 && <p className="devices-empty">{t("complianceNoRules")}</p>}
          </div>
        </div>

        <div className="panel">
          <div className="panel-heading">
            <div>
              <h3>{t("complianceIssuesTitle")}</h3>
              <p className="muted">
                {t("complianceIssueFilteredCount")
                  .replace("{count}", String(filteredComplianceIssues.length))
                  .replace("{total}", String(complianceIssues.length))}
              </p>
            </div>
            <div className="compliance-table-actions">
              <button type="button" className="secondary" onClick={exportFilteredComplianceIssues}>
                {t("complianceIssueExportExcel")}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => setShowComplianceIssueFilters((prev) => !prev)}
              >
                {t("complianceIssueToggleFilters")}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => setComplianceIssueFilters({ status: "open", ruleId: "", deviceUDID: "", search: "" })}
              >
                {t("complianceIssueFilterReset")}
              </button>
            </div>
          </div>
          {showComplianceIssueFilters && (
            <div className="compliance-issue-filters" aria-label={t("complianceIssueFilters")}>
              <label>
                {t("complianceIssueFilterStatus")}
                <select
                  className="text-input"
                  value={complianceIssueFilters.status}
                  onChange={(event) => setComplianceIssueFilters((prev) => ({ ...prev, status: event.target.value }))}
                >
                  <option value="">{t("complianceIssueFilterAllStatuses")}</option>
                  <option value="open">{t("complianceIssueFilterOpen")}</option>
                  <option value="critical">{t("complianceIssueFilterCritical")}</option>
                  <option value="overdue">{t("complianceIssueFilterOverdue")}</option>
                  <option value="resolved">{t("complianceIssueFilterResolved")}</option>
                  <option value="waived">{t("complianceIssueFilterWaived")}</option>
                </select>
              </label>
              <label>
                {t("complianceIssueFilterRule")}
                <select
                  className="text-input"
                  value={complianceIssueFilters.ruleId}
                  onChange={(event) => setComplianceIssueFilters((prev) => ({ ...prev, ruleId: event.target.value }))}
                >
                  <option value="">{t("complianceIssueFilterAllRules")}</option>
                  {issueRuleOptions.map((item) => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </label>
              <label>
                {t("complianceIssueFilterDevice")}
                <select
                  className="text-input"
                  value={complianceIssueFilters.deviceUDID}
                  onChange={(event) => setComplianceIssueFilters((prev) => ({ ...prev, deviceUDID: event.target.value }))}
                >
                  <option value="">{t("complianceIssueFilterAllDevices")}</option>
                  {issueDeviceOptions.map((item) => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </label>
              <label>
                {t("complianceIssueFilterSearch")}
                <input
                  className="text-input"
                  value={complianceIssueFilters.search}
                  placeholder={t("complianceIssueFilterSearchPlaceholder")}
                  onChange={(event) => setComplianceIssueFilters((prev) => ({ ...prev, search: event.target.value }))}
                />
              </label>
            </div>
          )}
          <div className="devices-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("tableStatus")}</th>
                  <th>{t("complianceIssue")}</th>
                  <th>{t("accountsTitle")}</th>
                  <th>{t("devicesColName")}</th>
                  <th>{t("complianceReason")}</th>
                  <th>{t("complianceIssueDetectedAt")}</th>
                  <th>{t("complianceIssueLastAction")}</th>
                  <th>{t("complianceRemediationStatus")}</th>
                  <th>{t("complianceDueAt")}</th>
                  <th>{t("tableAction")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleComplianceIssues.map((item) => (
                  <tr key={item.id} className={item.status === "open" ? `compliance-row-${item.severity}` : ""}>
                    <td>{getComplianceIssueStatusLabel(item)}</td>
                    <td>{item.rule_name || ruleTypeLabels[item.rule_type] || item.rule_type}</td>
                    <td>{item.account_name || item.account_id || "-"}</td>
                    <td>{renderComplianceIssueDeviceLink(item)}</td>
                    <td>{item.reason_text || item.reason_code || "-"}</td>
                    <td>{formatTimestamp(item.first_detected_at)}</td>
                    <td>{renderComplianceIssueLastAction(item)}</td>
                    <td>{renderComplianceIssueRemediationStatus(item)}</td>
                    <td>{item.due_at ? formatTimestamp(item.due_at) : "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => setSelectedComplianceIssueID(item.id)}
                      >
                        {t("complianceEvidenceOpen")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredComplianceIssues.length === 0 && <p className="devices-empty">{t("complianceNoIssues")}</p>}
          </div>
          {filteredComplianceIssues.length > visibleComplianceIssues.length && (
            <div className="table-footer-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => setComplianceIssueVisibleCount((prev) => prev + 25)}
              >
                {t("complianceIssueShowMore")}
              </button>
            </div>
          )}
        </div>
        {renderComplianceIssueDrawer()}
      </div>
    );
  }

  async function createMobileNotification(event) {
    event.preventDefault();
    if (!hasPermission("notifications", "edit")) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const expiresAt = notificationForm.expiresAt
        ? new Date(notificationForm.expiresAt).toISOString()
        : "";
      const response = await request("/notifications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category: notificationForm.category,
          severity: notificationForm.severity,
          title: notificationForm.title.trim(),
          body: notificationForm.body.trim(),
          action_label: notificationForm.actionLabel.trim(),
          action_url: notificationForm.actionURL.trim(),
          target_type: notificationForm.targetType,
          target_id: notificationForm.targetType === "all" ? "" : notificationForm.targetID,
          starts_at: new Date().toISOString(),
          expires_at: expiresAt
        })
      });
      setMobileNotifications((prev) => [response.item, ...prev.filter((item) => item.id !== response.item?.id)]);
      setNotificationForm((prev) => ({
        ...prev,
        title: "",
        body: "",
        actionLabel: "",
        actionURL: "",
        expiresAt: ""
      }));
      const push = response?.push || {};
      setInfoMessage(
        `${t("notificationsSent")}. ${t("notificationsPushStats")
          .replace("{sent}", String(push.sent || 0))
          .replace("{targeted}", String(push.targeted || 0))}`
      );
    } catch (err) {
      setErrorMessage(`${t("notificationsSendFailed")}: ${err.message}`);
    }
  }

  async function deleteMobileNotification(item) {
    if (!hasPermission("notifications", "edit") || !item?.id) return;
    if (!window.confirm(t("notificationsDeleteConfirm"))) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/notifications/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id })
      });
      setMobileNotifications((prev) => prev.filter((entry) => entry.id !== item.id));
      setInfoMessage(t("notificationsDeleted"));
    } catch (err) {
      setErrorMessage(`${t("notificationsDeleteFailed")}: ${err.message}`);
    }
  }


  function openCreateDocumentModal() {
    if (!hasPermission("documents", "edit")) return;
    setDocumentModalMode("create");
    setDocumentForm({
      ...createEmptyDocumentForm(),
      documentType: "file",
      folderId:
        selectedDocumentFolderID === "all" || selectedDocumentFolderID === "unfiled" || selectedDocumentFolderID === "ack_report"
          ? ""
          : selectedDocumentFolderID
    });
    setShowDocumentModal(true);
  }

  function openEditDocumentModal(item) {
    if (!hasPermission("documents", "edit")) return;
    setDocumentModalMode("edit");
    setDocumentForm({
      id: item.id || "",
      title: item.title || "",
      description: item.description || "",
      documentType: item.document_type || "link",
      url: item.url || "",
      fileName: item.file_name || "",
      contentType: item.content_type || "",
      fileData: item.file_data || "",
      documentFiles: [],
      folderId: item.folder_id || "",
      groupIds: Array.isArray(item.group_ids) ? item.group_ids.filter(Boolean) : [],
      requireAck: item.require_acknowledgement === true,
      ackDueDate: item.acknowledgement_due_at ? String(item.acknowledgement_due_at).slice(0, 10) : "",
      isActive: item.is_active !== false
    });
    setShowDocumentModal(true);
  }

  function closeDocumentModal() {
    setShowDocumentModal(false);
    setDocumentForm(createEmptyDocumentForm());
    setDocumentModalMode("create");
  }

  function toggleDocumentSelection(documentID) {
    setSelectedDocumentIDs((prev) =>
      prev.includes(documentID) ? prev.filter((id) => id !== documentID) : [...prev, documentID]
    );
  }

  function toggleSelectAllVisibleDocuments(visibleDocumentIDs, allVisibleSelected) {
    if (allVisibleSelected) {
      const visibleSet = new Set(visibleDocumentIDs);
      setSelectedDocumentIDs((prev) => prev.filter((id) => !visibleSet.has(id)));
      return;
    }
    const next = new Set(selectedDocumentIDs);
    visibleDocumentIDs.forEach((documentID) => next.add(documentID));
    setSelectedDocumentIDs(Array.from(next));
  }

  function toggleDocumentGroupSelection(groupID) {
    setDocumentForm((prev) => {
      const next = new Set(prev.groupIds || []);
      if (next.has(groupID)) {
        next.delete(groupID);
      } else {
        next.add(groupID);
      }
      return { ...prev, groupIds: Array.from(next) };
    });
  }

  async function createDocumentFolder(parentID = selectedDocumentFolderID, options = {}) {
    if (!hasPermission("documents", "edit")) return;
    const normalizedParentID =
      parentID === "all" || parentID === "unfiled" || parentID === "ack_report" ? "" : String(parentID || "");
    const name = window.prompt(lang === "ru" ? "Название папки" : "Folder name");
    if (!name || !name.trim()) return;
    const shouldSelect = options.select !== false;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const response = await request("/document-folders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_id: normalizedParentID, name: name.trim() })
      });
      if (response?.item) {
        setDocumentFolders((prev) => [...prev.filter((item) => item.id !== response.item.id), response.item]);
        if (shouldSelect) {
          setSelectedDocumentFolderID(response.item.id);
        }
      }
      setInfoMessage(lang === "ru" ? "Папка создана" : "Folder created");
      return response?.item || null;
    } catch (err) {
      setErrorMessage(err.message);
      return null;
    }
  }

  async function createDocumentFolderForDocumentForm() {
    const item = await createDocumentFolder(documentForm.folderId || "", { select: false });
    if (item?.id) {
      setDocumentForm((prev) => ({ ...prev, folderId: item.id }));
    }
  }

  async function deleteSelectedDocumentFolder() {
    if (!hasPermission("documents", "edit")) return;
    const folderID = selectedDocumentFolderID;
    if (!folderID || folderID === "all" || folderID === "unfiled" || folderID === "ack_report") return;
    const hasChildren = documentFolders.some((item) => item.parent_id === folderID);
    const hasDocuments = documents.some((item) => item.folder_id === folderID);
    const message = hasChildren || hasDocuments
      ? (lang === "ru"
        ? "Удалить папку? Документы из нее будут перемещены без папки, подпапки будут удалены."
        : "Delete folder? Documents in it will move to no folder, subfolders will be deleted.")
      : (lang === "ru" ? "Удалить папку?" : "Delete folder?");
    if (!window.confirm(message)) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      await request("/document-folders/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: folderID })
      });
      setSelectedDocumentFolderID("all");
      await loadAllData();
      setInfoMessage(lang === "ru" ? "Папка удалена" : "Folder deleted");
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  function handleDocumentFileChange(event) {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const oversized = files.find((file) => file.size > MAX_DOCUMENT_UPLOAD_BYTES);
    if (oversized) {
      setErrorMessage(`${t("documentsFileTooLarge")}: ${oversized.name}`);
      event.target.value = "";
      return;
    }
    const unsupported = files.find((file) => {
      const name = String(file.name || "").toLowerCase();
      return !ALLOWED_DOCUMENT_UPLOAD_EXTENSIONS.some((ext) => name.endsWith(ext));
    });
    if (unsupported) {
      setErrorMessage(`${t("documentsFileTypeNotAllowed")}: ${unsupported.name}`);
      event.target.value = "";
      return;
    }
    const readFile = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve({
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
            name: file.name,
            size: file.size,
            contentType: file.type || "application/octet-stream",
            data: String(reader.result || "")
          });
        reader.onerror = () => reject(new Error(t("documentsReadFailed")));
        reader.readAsDataURL(file);
      });
    Promise.all(files.map(readFile))
      .then((items) => {
        setDocumentForm((prev) => ({
          ...prev,
          documentType: "file",
          documentFiles: [...(prev.documentFiles || []), ...items],
          fileName: items[0]?.name || prev.fileName,
          contentType: items[0]?.contentType || prev.contentType,
          fileData: items[0]?.data || prev.fileData,
          url: ""
        }));
        setErrorMessage("");
      })
      .catch((err) => setErrorMessage(err.message || t("documentsReadFailed")))
      .finally(() => {
        event.target.value = "";
      });
  }

  function removeDocumentUploadFile(fileID) {
    setDocumentForm((prev) => {
      const nextFiles = (prev.documentFiles || []).filter((item) => item.id !== fileID);
      return {
        ...prev,
        documentFiles: nextFiles,
        fileName: nextFiles[0]?.name || "",
        contentType: nextFiles[0]?.contentType || "",
        fileData: nextFiles[0]?.data || ""
      };
    });
  }

  function formatDocumentFileSize(bytes) {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) return "0 KB";
    if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function saveDocument(event) {
    event.preventDefault();
    if (!hasPermission("documents", "edit")) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      if (documentModalMode !== "edit") {
        const files = Array.isArray(documentForm.documentFiles) ? documentForm.documentFiles : [];
        if (files.length === 0) {
          setErrorMessage(lang === "ru" ? "Выберите один или несколько файлов" : "Select one or more files");
          return;
        }
        const responses = [];
        for (const file of files) {
          const response = await request("/documents", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title: file.name,
              description: "",
              document_type: "file",
              url: "",
              file_name: file.name,
              content_type: file.contentType || "application/octet-stream",
              file_data: file.data,
              folder_id: documentForm.folderId || "",
              group_ids: documentForm.groupIds || [],
              require_acknowledgement: Boolean(documentForm.requireAck),
              acknowledgement_due_at: documentForm.requireAck ? documentForm.ackDueDate || "" : "",
              is_active: true
            })
          });
          if (response?.item) {
            responses.push(response.item);
          }
        }
        if (responses.length > 0) {
          setDocuments((prev) => [
            ...responses,
            ...prev.filter((item) => !responses.some((created) => created.id === item.id))
          ]);
        }
        closeDocumentModal();
        setInfoMessage(t("documentsSaved"));
        await loadAllData();
        return;
      }
      const isFile = documentForm.documentType === "file";
      const normalizedFileName = documentForm.fileName.trim();
      const normalizedTitle = isFile ? normalizedFileName || documentForm.title.trim() : documentForm.title.trim();
      const payload = {
        title: normalizedTitle,
        description: "",
        document_type: isFile ? "file" : "link",
        url: isFile ? "" : documentForm.url.trim(),
        file_name: isFile ? normalizedFileName : "",
        content_type: isFile ? documentForm.contentType.trim() : "",
        file_data: isFile ? documentForm.fileData : "",
        folder_id: documentForm.folderId || "",
        group_ids: documentForm.groupIds || [],
        require_acknowledgement: Boolean(documentForm.requireAck),
        acknowledgement_due_at: documentForm.requireAck ? documentForm.ackDueDate || "" : "",
        is_active: Boolean(documentForm.isActive)
      };
      const endpoint = documentModalMode === "edit" ? "/documents/update" : "/documents";
      if (documentModalMode === "edit") {
        payload.id = documentForm.id;
      }
      const response = await request(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (response?.item) {
        setDocuments((prev) => [response.item, ...prev.filter((item) => item.id !== response.item.id)]);
      }
      closeDocumentModal();
      setInfoMessage(t("documentsSaved"));
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("documentsSaveFailed")}: ${err.message}`);
    }
  }

  async function deleteSelectedDocuments(ids = selectedDocumentIDs) {
    if (!hasPermission("documents", "edit")) return;
    const uniqueIDs = Array.from(new Set((ids || []).filter(Boolean)));
    if (uniqueIDs.length === 0) return;
    if (!window.confirm(t("documentsDeleteSelectedConfirm"))) return;
    setErrorMessage("");
    setInfoMessage("");
    try {
      const settled = await Promise.allSettled(
        uniqueIDs.map((id) =>
          request("/documents/delete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id })
          })
        )
      );
      const failedCount = settled.filter((item) => item.status === "rejected").length;
      setSelectedDocumentIDs((prev) => prev.filter((id) => !uniqueIDs.includes(id)));
      if (failedCount > 0) {
        setErrorMessage(`${t("documentsDeleteSelectedFailed")}: ${failedCount}`);
      } else {
        setInfoMessage(t("documentsDeleted"));
      }
      await loadAllData();
    } catch (err) {
      setErrorMessage(`${t("documentsDeleteSelectedFailed")}: ${err.message}`);
    }
  }

  async function deleteDocumentFromModal() {
    if (!documentForm.id) return;
    if (!window.confirm(t("documentsDeleteConfirm"))) return;
    await deleteSelectedDocuments([documentForm.id]);
    closeDocumentModal();
  }

  async function refreshDocumentAcknowledgementReport(documentID = documentAckReportFilterID) {
    try {
      const suffix = documentID ? `&document_id=${encodeURIComponent(documentID)}` : "";
      const response = await request(`/documents/acknowledgements?limit=10000${suffix}`);
      setDocumentAckReport(Array.isArray(response?.items) ? response.items : []);
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function exportDocumentAcknowledgementReport(documentID = documentAckReportFilterID) {
    try {
      const suffix = documentID ? `&document_id=${encodeURIComponent(documentID)}` : "";
      const response = await fetch(`${base}/documents/acknowledgements?format=csv&limit=10000${suffix}`, {
        headers: authHeaders()
      });
      if (!response.ok) throw new Error(await response.text());
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "turuk-document-acknowledgements.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  async function openDocumentAssignedDevicesModal(item) {
    if (!item?.id) return;
    setDocumentAssignedDevicesItem(item);
    setDocumentAssignedDevices([]);
    setDocumentAssignedDevicesLoading(true);
    setErrorMessage("");
    try {
      const response = await request(`/documents/assigned-devices?document_id=${encodeURIComponent(item.id)}&limit=10000`);
      setDocumentAssignedDevices(Array.isArray(response?.items) ? response.items : []);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setDocumentAssignedDevicesLoading(false);
    }
  }

  function closeDocumentAssignedDevicesModal() {
    setDocumentAssignedDevicesItem(null);
    setDocumentAssignedDevices([]);
    setDocumentAssignedDevicesLoading(false);
  }

  function openDocumentGroupsModal(item) {
    if (!item?.id) return;
    setDocumentGroupsItem(item);
  }

  function closeDocumentGroupsModal() {
    setDocumentGroupsItem(null);
  }

  async function exportDocumentAssignedDevicesReport() {
    const documentID = documentAssignedDevicesItem?.id;
    if (!documentID) return;
    try {
      const response = await fetch(
        `${base}/documents/assigned-devices?format=csv&limit=10000&document_id=${encodeURIComponent(documentID)}`,
        { headers: authHeaders() }
      );
      if (!response.ok) throw new Error(await response.text());
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "turuk-document-assigned-devices.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(err.message);
    }
  }

  function buildDocumentFolderOptions() {
    const foldersByParent = new Map();
    documentFolders.forEach((folder) => {
      const parentID = String(folder.parent_id || "");
      if (!foldersByParent.has(parentID)) foldersByParent.set(parentID, []);
      foldersByParent.get(parentID).push(folder);
    });
    foldersByParent.forEach((items) =>
      items.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), lang === "ru" ? "ru" : "en"))
    );
    const options = [];
    const collect = (parentID = "", prefix = "") => {
      (foldersByParent.get(parentID) || []).forEach((folder) => {
        const label = `${prefix}${folder.name || folder.id}`;
        options.push({ id: folder.id, label });
        collect(folder.id, `${label} / `);
      });
    };
    collect();
    return options;
  }

  function renderDocumentsPage() {
    const canEditDocuments = hasPermission("documents", "edit");
    const query = documentsSearch.trim().toLowerCase();
    const foldersByParent = new Map();
    documentFolders.forEach((folder) => {
      const parentID = String(folder.parent_id || "");
      if (!foldersByParent.has(parentID)) foldersByParent.set(parentID, []);
      foldersByParent.get(parentID).push(folder);
    });
    foldersByParent.forEach((items) =>
      items.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), lang === "ru" ? "ru" : "en"))
    );
    const folderLabelByID = new Map();
    const collectFolderLabels = (parentID = "", prefix = "") => {
      (foldersByParent.get(parentID) || []).forEach((folder) => {
        const label = `${prefix}${folder.name || folder.id}`;
        folderLabelByID.set(folder.id, label);
        collectFolderLabels(folder.id, `${label} / `);
      });
    };
    collectFolderLabels();
    const selectedFolderName =
      selectedDocumentFolderID === "all"
        ? (lang === "ru" ? "Все документы" : "All documents")
        : selectedDocumentFolderID === "unfiled"
          ? (lang === "ru" ? "Без папки" : "No folder")
          : selectedDocumentFolderID === "ack_report"
            ? (lang === "ru" ? "Отчет по ознакомлению" : "Acknowledgement report")
          : folderLabelByID.get(selectedDocumentFolderID) || (lang === "ru" ? "Папка" : "Folder");
    const visibleDocuments = documents
      .filter((item) => {
        if (selectedDocumentFolderID === "ack_report") return false;
        if (selectedDocumentFolderID === "all") return true;
        if (selectedDocumentFolderID === "unfiled") return !item.folder_id;
        return String(item.folder_id || "") === String(selectedDocumentFolderID);
      })
      .filter((item) => {
        if (!query) return true;
        return [
          item.title,
          item.description,
          item.url,
          item.file_name,
          ...(Array.isArray(item.group_names) ? item.group_names : [])
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      })
      .sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), lang === "ru" ? "ru" : "en"));
    const visibleDocumentIDs = visibleDocuments.map((item) => item.id).filter(Boolean);
    const allVisibleSelected =
      visibleDocumentIDs.length > 0 && visibleDocumentIDs.every((id) => selectedDocumentIDs.includes(id));
    const showDocumentsFolderColumn = selectedDocumentFolderID === "all";
    const acknowledgementDocumentOptions = documents
      .filter((item) => item.require_acknowledgement === true)
      .sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), lang === "ru" ? "ru" : "en"));
    const renderFolderRows = (parentID = "", level = 0) =>
      (foldersByParent.get(parentID) || []).map((folder) => (
        <div key={folder.id}>
          <button
            type="button"
            className={selectedDocumentFolderID === folder.id ? "document-folder-row active" : "document-folder-row"}
            style={{ paddingLeft: `${12 + level * 16}px` }}
            onClick={() => {
              setSelectedDocumentFolderID(folder.id);
              setSelectedDocumentIDs([]);
            }}
          >
            <span className="document-folder-icon">▸</span>
            <span>{folder.name || folder.id}</span>
          </button>
          {renderFolderRows(folder.id, level + 1)}
        </div>
      ));
    const renderAcknowledgementReport = () => (
      <div className="document-acknowledgement-report-page">
        <div className="panel-heading inline-panel-heading">
          <div>
            <h3>{lang === "ru" ? "Отчет по ознакомлению" : "Acknowledgement report"}</h3>
            <p className="muted">
              {lang === "ru"
                ? "Кому назначено ознакомление, кто ознакомился, а кто еще нет."
                : "Assigned acknowledgements, completed acknowledgements, and pending users."}
            </p>
          </div>
          <div className="devices-toolbar-actions">
            <button className="toolbar-action" onClick={() => refreshDocumentAcknowledgementReport()}>
              {lang === "ru" ? "Обновить" : "Refresh"}
            </button>
            <button className="toolbar-action secondary" onClick={() => exportDocumentAcknowledgementReport()}>
              CSV
            </button>
          </div>
        </div>
        <div className="filters-row document-report-filters">
          <select
            className="text-input"
            value={documentAckReportFilterID}
            onChange={(event) => {
              const nextDocumentID = event.target.value;
              setDocumentAckReportFilterID(nextDocumentID);
              refreshDocumentAcknowledgementReport(nextDocumentID);
            }}
          >
            <option value="">{lang === "ru" ? "Все документы к ознакомлению" : "All acknowledgement documents"}</option>
            {acknowledgementDocumentOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title || item.file_name || item.id}
              </option>
            ))}
          </select>
        </div>
        <div className="devices-table-wrap">
          <table>
            <thead>
              <tr>
                <th>{lang === "ru" ? "Документ" : "Document"}</th>
                <th>{lang === "ru" ? "Аккаунт" : "Account"}</th>
                <th>{lang === "ru" ? "Группы" : "Groups"}</th>
                <th>{lang === "ru" ? "Срок" : "Due"}</th>
                <th>{lang === "ru" ? "Статус" : "Status"}</th>
                <th>{lang === "ru" ? "Ознакомлен" : "Acknowledged"}</th>
              </tr>
            </thead>
            <tbody>
              {documentAckReport.map((item, index) => (
                <tr key={`${item.document_id || ""}-${item.account_id || ""}-${index}`}>
                  <td>
                    {item.document_title || "-"}
                    {item.file_name && <div className="muted small-text">{item.file_name}</div>}
                  </td>
                  <td>
                    {item.account_name || item.account_email || item.account_id || "-"}
                    {item.account_email && <div className="muted small-text">{item.account_email}</div>}
                  </td>
                  <td>
                    {Array.isArray(item.group_names) && item.group_names.length > 0
                      ? item.group_names.join(", ")
                      : "-"}
                  </td>
                  <td>{formatTimestamp(item.due_at)}</td>
                  <td>
                    {item.status === "acknowledged"
                      ? (lang === "ru" ? "Ознакомлен" : "Acknowledged")
                      : (lang === "ru" ? "Не ознакомлен" : "Pending")}
                  </td>
                  <td>{item.acknowledged_at ? formatTimestamp(item.acknowledged_at) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {documentAckReport.length === 0 && (
            <p className="devices-empty">
              {lang === "ru" ? "Документов к ознакомлению пока нет." : "No acknowledgement assignments yet."}
            </p>
          )}
        </div>
      </div>
    );

    return (
      <div className="page">
        <div className="panel devices-table-panel">
          <div className="panel-heading">
            <div>
              <h3>{t("documentsTitle")}</h3>
              <p className="muted">{t("documentsSubtitle")}</p>
            </div>
            <div className="devices-toolbar-actions">
              {canEditDocuments && selectedDocumentIDs.length > 0 && (
                <button className="toolbar-action danger" onClick={() => deleteSelectedDocuments()}>
                  {t("documentsDeleteSelected")}
                </button>
              )}
              {canEditDocuments && (
                <button className="toolbar-action" onClick={openCreateDocumentModal}>
                  {lang === "ru" ? "Добавить файл" : "Add file"}
                </button>
              )}
              {canEditDocuments && (
                <button className="toolbar-action secondary" onClick={() => createDocumentFolder()}>
                  {lang === "ru" ? "Создать папку" : "Create folder"}
                </button>
              )}
            </div>
          </div>

          <div className="documents-layout">
            <aside className="documents-folder-pane">
              <button
                type="button"
                className={selectedDocumentFolderID === "all" ? "document-folder-row active" : "document-folder-row"}
                onClick={() => {
                  setSelectedDocumentFolderID("all");
                  setSelectedDocumentIDs([]);
                }}
              >
                {lang === "ru" ? "Все документы" : "All documents"}
              </button>
              <button
                type="button"
                className={selectedDocumentFolderID === "unfiled" ? "document-folder-row active" : "document-folder-row"}
                onClick={() => {
                  setSelectedDocumentFolderID("unfiled");
                  setSelectedDocumentIDs([]);
                }}
              >
                {lang === "ru" ? "Без папки" : "No folder"}
              </button>
              <button
                type="button"
                className={selectedDocumentFolderID === "ack_report" ? "document-folder-row active" : "document-folder-row"}
                onClick={() => {
                  setSelectedDocumentFolderID("ack_report");
                  setSelectedDocumentIDs([]);
                }}
              >
                {lang === "ru" ? "Отчет по ознакомлению" : "Acknowledgement report"}
              </button>
              <div className="document-folder-tree">{renderFolderRows()}</div>
              {canEditDocuments && selectedDocumentFolderID !== "ack_report" && (
                <div className="documents-folder-actions">
                  <button type="button" className="secondary" onClick={() => createDocumentFolder()}>
                    {lang === "ru" ? "Новая папка" : "New folder"}
                  </button>
                  {selectedDocumentFolderID !== "all" && selectedDocumentFolderID !== "unfiled" && (
                    <button type="button" className="secondary danger" onClick={deleteSelectedDocumentFolder}>
                      {lang === "ru" ? "Удалить" : "Delete"}
                    </button>
                  )}
                </div>
              )}
            </aside>
            <div className="documents-content-pane">
              {selectedDocumentFolderID === "ack_report" ? (
                renderAcknowledgementReport()
              ) : (
                <>
                  <div className="devices-toolbar">
                    <input
                      className="text-input"
                      value={documentsSearch}
                      placeholder={t("documentsSearch")}
                      onChange={(event) => setDocumentsSearch(event.target.value)}
                    />
                  </div>

                  <div className="documents-current-folder">{selectedFolderName}</div>

                  {selectedDocumentIDs.length > 0 && (
                    <div className="muted">
                      {t("documentsSelectedCount")}: {selectedDocumentIDs.length}
                    </div>
                  )}

                  <div className="devices-table-wrap">
                    <table className="documents-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={allVisibleSelected}
                              onChange={() => toggleSelectAllVisibleDocuments(visibleDocumentIDs, allVisibleSelected)}
                            />
                          </th>
                          <th>{t("documentsColTitle")}</th>
                          <th>{t("documentsColCreatedBy")}</th>
                          {showDocumentsFolderColumn && <th>{lang === "ru" ? "Папка" : "Folder"}</th>}
                          <th>{t("documentsColStatus")}</th>
                          <th>{t("documentsColUpdatedAt")}</th>
                          <th>{t("documentsColGroups")}</th>
                          <th>{lang === "ru" ? "Устройства" : "Devices"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleDocuments.map((item) => (
                          <tr key={item.id} className={selectedDocumentIDs.includes(item.id) ? "row-selected" : ""}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedDocumentIDs.includes(item.id)}
                                onChange={() => toggleDocumentSelection(item.id)}
                              />
                            </td>
                            <td>
                              {canEditDocuments ? (
                                <button className="table-link-button" onClick={() => openEditDocumentModal(item)}>
                                  {item.title || "-"}
                                </button>
                              ) : (
                                item.title || "-"
                              )}
                              {item.description && <div className="muted small-text">{item.description}</div>}
                            </td>
                            <td>{item.created_by || "-"}</td>
                            {showDocumentsFolderColumn && (
                              <td>{item.folder_id ? folderLabelByID.get(item.folder_id) || "-" : "-"}</td>
                            )}
                            <td>{item.is_active === false ? t("documentsStatusInactive") : t("documentsStatusActive")}</td>
                            <td>{formatTimestamp(item.updated_at || item.created_at)}</td>
                            <td>
                              {Array.isArray(item.group_names) && item.group_names.length > 0 ? (
                                <button className="table-link-button" onClick={() => openDocumentGroupsModal(item)}>
                                  {item.group_names.length}
                                </button>
                              ) : (
                                0
                              )}
                            </td>
                            <td>
                              {Number(item.assigned_device_count || 0) > 0 ? (
                                <button className="table-link-button" onClick={() => openDocumentAssignedDevicesModal(item)}>
                                  {Number(item.assigned_device_count || 0)}
                                </button>
                              ) : (
                                0
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {visibleDocuments.length === 0 && <p className="devices-empty">{t("documentsNoItems")}</p>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderActionLogsPage() {
    const items = [...auditLogs].sort(
      (a, b) => new Date(b.occurred_at || 0).getTime() - new Date(a.occurred_at || 0).getTime()
    );
    const auditActorOptions = consoleUsers
      .map((item) => ({
        value: item.id || item.login || item.email || item.name || "",
        label: [item.first_name, item.last_name].filter(Boolean).join(" ") || item.name || item.login || item.email || item.id || ""
      }))
      .filter((item) => item.value && item.label)
      .sort((a, b) => a.label.localeCompare(b.label, lang === "ru" ? "ru" : "en"));

    return (
      <div className="page">
        <div className="panel devices-table-panel">
          <div className="panel-heading">
            <div>
              <h3>{t("actionLogsTitle")}</h3>
              <p className="muted">{t("actionLogsSubtitle")}</p>
            </div>
            <div className="devices-toolbar-actions">
              <button className="toolbar-action" onClick={refreshAuditLogs}>
                {t("actionLogsRefresh")}
              </button>
              <button className="toolbar-action secondary" onClick={() => exportAuditLogs("csv")}>
                {t("actionLogsExportCSV")}
              </button>
              <button className="toolbar-action secondary" onClick={() => exportAuditLogs("json")}>
                {t("actionLogsExportJSON")}
              </button>
            </div>
          </div>

          <div className="filters-row audit-filters-row">
            <select
              className="text-input"
              value={auditFilters.actor}
              onChange={(event) => setAuditFilters((prev) => ({ ...prev, actor: event.target.value }))}
            >
              <option value="">{t("actionLogsFilterActor")}</option>
              {auditActorOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <input
              className="text-input"
              value={auditFilters.device}
              placeholder={t("actionLogsFilterDevice")}
              onChange={(event) => setAuditFilters((prev) => ({ ...prev, device: event.target.value }))}
            />
            <input
              className="text-input"
              value={auditFilters.action}
              placeholder={t("actionLogsFilterAction")}
              onChange={(event) => setAuditFilters((prev) => ({ ...prev, action: event.target.value }))}
            />
            <input
              className="text-input"
              type="date"
              value={auditFilters.from}
              title={t("actionLogsFilterFrom")}
              onChange={(event) => setAuditFilters((prev) => ({ ...prev, from: event.target.value }))}
            />
            <input
              className="text-input"
              type="date"
              value={auditFilters.to}
              title={t("actionLogsFilterTo")}
              onChange={(event) => setAuditFilters((prev) => ({ ...prev, to: event.target.value }))}
            />
            <button className="toolbar-action" onClick={refreshAuditLogs}>
              {t("actionLogsApplyFilters")}
            </button>
            <button
              className="toolbar-action secondary"
              onClick={() => setAuditFilters({ actor: "", device: "", action: "", from: "", to: "" })}
            >
              {t("actionLogsClearFilters")}
            </button>
          </div>

          <div className="devices-table-wrap action-logs-table-wrap">
            <table className="action-logs-table">
              <thead>
                <tr>
                  <th>{t("actionLogsColTime")}</th>
                  <th>{t("actionLogsColActor")}</th>
                  <th>{t("actionLogsColAction")}</th>
                  <th>{t("actionLogsColResult")}</th>
                  <th>{t("actionLogsColIP")}</th>
                  <th>{t("actionLogsColPath")}</th>
                  <th>{t("actionLogsColDetails")}</th>
                  <th>{t("actionLogsColIntegrity")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const sessionExpired = isAuditSessionExpired(item);
                  const success = item.success !== false;
                  const resultText = sessionExpired
                    ? t("actionLogsSessionExpired")
                    : success
                      ? t("actionLogsSuccess")
                      : `${t("actionLogsFailed")}${item.error ? `: ${item.error}` : ""}`;
                  const resultClass = sessionExpired
                    ? "audit-result-info"
                    : success
                      ? "audit-result-success"
                      : "audit-result-failed";
                  return (
                    <tr key={`${item.occurred_at || ""}-${item.action || ""}-${index}`}>
                      <td>{formatTimestamp(item.occurred_at)}</td>
                      <td>
                        {item.actor_name || item.actor_id || item.actor_type || "-"}
                        {item.actor_type && <div className="muted small-text">{item.actor_type}</div>}
                      </td>
                      <td>
                        <strong>{auditActionLabel(item.action, t)}</strong>
                        <div className="muted small-text">{item.action || "-"}</div>
                      </td>
                      <td>
                        <span className={resultClass}>{resultText}</span>
                        <div className="muted small-text">{item.status_code || "-"}</div>
                      </td>
                      <td>{item.actor_ip || "-"}</td>
                      <td>
                        <span className="mono-text">
                          {item.method || "-"} {item.path || "-"}
                        </span>
                        {item.query && <div className="muted small-text">{item.query}</div>}
                      </td>
                      <td>
                        <pre className="code-block audit-details">{auditDetailsText(item.details)}</pre>
                      </td>
                      <td>
                        <span className="mono-text">{item.integrity_hash ? item.integrity_hash.slice(0, 16) : "-"}</span>
                        {item.retained_until && (
                          <div className="muted small-text">{formatTimestamp(item.retained_until)}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {items.length === 0 && <p className="devices-empty">{t("actionLogsNoItems")}</p>}
          </div>
        </div>
      </div>
    );
  }

  function renderAgentSupportTicketsPanel(canEditAgent) {
    return (
      <div className="panel">
        <h4>{t("agentSettingsSupportTicketsTitle")}</h4>
        <div className="devices-table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t("tableState")}</th>
                <th>{t("accountsName")}</th>
                <th>{t("agentSettingsTicketDevice")}</th>
                <th>{t("agentSettingsCategory")}</th>
                <th>{t("agentSettingsSubject")}</th>
                <th>{t("agentSettingsCreatedAt")}</th>
                <th>{t("tableAction")}</th>
              </tr>
            </thead>
            <tbody>
              {agentSupportTickets.map((rawItem) => {
                const item = rawItem?.ticket || rawItem;
                return (
                  <tr key={item.id}>
                    <td>{agentSupportStatusLabel(item.status, t)}</td>
                    <td>{item.account_name || item.account_email || item.account_id || "-"}</td>
                    <td>
                      {item.device_udid ? (
                        <a href={deviceDetailsHash("devicesDetailAbout", item.device_udid)}>{item.device_udid}</a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{agentSupportCategoryLabel(item.category, t)}</td>
                    <td title={item.message || ""}>{item.subject || "-"}</td>
                    <td>{formatTimestamp(item.created_at)}</td>
                    <td>
                      <button className="secondary" onClick={() => loadAgentSupportTicket(item.id)}>
                        {t("agentSettingsTicketOpen")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {agentSupportTickets.length === 0 && <p className="empty">{t("agentSettingsNoTickets")}</p>}

        {selectedAgentSupportTicket?.ticket && (
          <div className="panel" style={{ marginTop: 16 }}>
            <h4>{t("agentSettingsTicketDetails")}</h4>
            <div className="agent-ticket-meta-grid">
              <div><b>{t("agentSettingsSubject")}</b><br />{selectedAgentSupportTicket.ticket.subject || "-"}</div>
              <div><b>{t("accountsName")}</b><br />{selectedAgentSupportTicket.ticket.account_name || selectedAgentSupportTicket.ticket.account_email || selectedAgentSupportTicket.ticket.account_id || "-"}</div>
              <div>
                <b>{t("agentSettingsTicketDevice")}</b><br />
                {selectedAgentSupportTicket.ticket.device_udid ? (
                  <a href={deviceDetailsHash("devicesDetailAbout", selectedAgentSupportTicket.ticket.device_udid)}>
                    {selectedAgentSupportTicket.ticket.device_udid}
                  </a>
                ) : (
                  "-"
                )}
              </div>
              <div><b>{t("agentSettingsCategory")}</b><br />{agentSupportCategoryLabel(selectedAgentSupportTicket.ticket.category, t)}</div>
              <div><b>{t("agentSettingsCreatedAt")}</b><br />{formatTimestamp(selectedAgentSupportTicket.ticket.created_at)}</div>
            </div>

            <label className="form-label">
              {t("tableState")}
              <select
                className="text-input"
                value={agentSupportTicketStatus}
                disabled={!canEditAgent}
                onChange={(event) => setAgentSupportTicketStatus(event.target.value)}
              >
                <option value="open">{t("agentSettingsTicketStatusOpen")}</option>
                <option value="in_progress">{t("agentSettingsTicketStatusInProgress")}</option>
                <option value="closed">{t("agentSettingsTicketStatusClosed")}</option>
              </select>
            </label>

            <div className="panel" style={{ marginTop: 12 }}>
              <h4>{t("agentSettingsTicketOriginalMessage")}</h4>
              <p style={{ whiteSpace: "pre-wrap" }}>{selectedAgentSupportTicket.ticket.message || "-"}</p>
            </div>

            <label className="form-label">
              {t("agentSettingsTicketReply")}
              <textarea
                className="text-input"
                rows={4}
                value={agentSupportTicketReply}
                disabled={!canEditAgent}
                onChange={(event) => setAgentSupportTicketReply(event.target.value)}
              />
            </label>
            <button disabled={!canEditAgent || loading} onClick={saveAgentSupportTicketUpdate}>
              {t("agentSettingsTicketSendReply")}
            </button>

            <div className="panel" style={{ marginTop: 12 }}>
              <h4>{t("agentSettingsTicketHistory")}</h4>
              {(selectedAgentSupportTicket.events || []).map((event) => (
                <div key={event.id} className="activity-row">
                  <b>{agentSupportEventLabel(event.event_type, t)}</b>
                  <span>{formatTimestamp(event.created_at)}</span>
                  <span>{event.actor_name || event.actor_type || "-"}</span>
                  {event.event_type === "status_changed" && (
                    <span>
                      {agentSupportStatusLabel(event.from_status, t)} → {agentSupportStatusLabel(event.to_status, t)}
                    </span>
                  )}
                  {event.message && <p style={{ whiteSpace: "pre-wrap" }}>{event.message}</p>}
                </div>
              ))}
            </div>

            <div className="agent-ticket-json-grid" style={{ marginTop: 12 }}>
              <div className="panel">
                <h4>{t("agentSettingsTicketLogs")}</h4>
                <pre className="code-block">{formatJSONBlock(selectedAgentSupportTicket.ticket.logs)}</pre>
              </div>
              <div className="panel">
                <h4>{t("agentSettingsTicketDeviceInfo")}</h4>
                <pre className="code-block">{formatJSONBlock(selectedAgentSupportTicket.ticket.device_info)}</pre>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderNotificationCreatePanel() {
    const targetOptions =
      notificationForm.targetType === "account"
        ? accounts.map((item) => ({ id: item.id, label: item.name || item.email || item.id }))
        : notificationForm.targetType === "group"
          ? groups.map((item) => ({ id: item.id, label: item.name || item.id }))
          : [];

    return (
      <form className="panel notification-compose" onSubmit={createMobileNotification}>
        <h3>{t("notificationsCreate")}</h3>
        <div className="notification-compose-grid">
          <label>
            {t("notificationsCategory")}
            <select
              className="text-input"
              value={notificationForm.category}
              onChange={(event) => setNotificationForm((prev) => ({ ...prev, category: event.target.value }))}
            >
              <option value="news">{t("notificationsCategoryNews")}</option>
              <option value="required">{t("notificationsCategoryRequired")}</option>
              <option value="compliance">{t("notificationsCategoryCompliance")}</option>
            </select>
          </label>
          <label>
            {t("notificationsSeverity")}
            <select
              className="text-input"
              value={notificationForm.severity}
              onChange={(event) => setNotificationForm((prev) => ({ ...prev, severity: event.target.value }))}
            >
              <option value="info">{t("notificationsSeverityInfo")}</option>
              <option value="warning">{t("notificationsSeverityWarning")}</option>
              <option value="critical">{t("notificationsSeverityCritical")}</option>
            </select>
          </label>
          <label>
            {t("notificationsTarget")}
            <select
              className="text-input"
              value={notificationForm.targetType}
              onChange={(event) =>
                setNotificationForm((prev) => ({ ...prev, targetType: event.target.value, targetID: "" }))
              }
            >
              <option value="all">{t("notificationsTargetAll")}</option>
              <option value="account">{t("notificationsTargetAccount")}</option>
              <option value="group">{t("notificationsTargetGroup")}</option>
            </select>
          </label>
          {notificationForm.targetType !== "all" && (
            <label>
              {notificationForm.targetType === "account"
                ? t("notificationsTargetAccount")
                : t("notificationsTargetGroup")}
              <select
                className="text-input"
                required
                value={notificationForm.targetID}
                onChange={(event) => setNotificationForm((prev) => ({ ...prev, targetID: event.target.value }))}
              >
                <option value="">-</option>
                {targetOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="notification-span-two">
            {t("notificationsTitleField")}
            <input
              className="text-input"
              required
              maxLength={160}
              value={notificationForm.title}
              onChange={(event) => setNotificationForm((prev) => ({ ...prev, title: event.target.value }))}
            />
          </label>
          <label className="notification-span-two">
            {t("notificationsBody")}
            <textarea
              className="text-input notification-body-input"
              required
              maxLength={4000}
              value={notificationForm.body}
              onChange={(event) => setNotificationForm((prev) => ({ ...prev, body: event.target.value }))}
            />
          </label>
          <label>
            {t("notificationsActionLabel")}
            <input
              className="text-input"
              value={notificationForm.actionLabel}
              onChange={(event) => setNotificationForm((prev) => ({ ...prev, actionLabel: event.target.value }))}
            />
          </label>
          <label>
            {t("notificationsActionURL")}
            <input
              className="text-input"
              placeholder="turukagent://apps"
              value={notificationForm.actionURL}
              onChange={(event) => setNotificationForm((prev) => ({ ...prev, actionURL: event.target.value }))}
            />
          </label>
          <label>
            {t("notificationsExpiresAt")}
            <input
              className="text-input"
              type="datetime-local"
              value={notificationForm.expiresAt}
              onChange={(event) => setNotificationForm((prev) => ({ ...prev, expiresAt: event.target.value }))}
            />
          </label>
        </div>
        <button type="submit">{t("notificationsSend")}</button>
      </form>
    );
  }

  function renderSentNotificationsPanel() {
    const categoryLabels = {
      news: t("notificationsCategoryNews"),
      required: t("notificationsCategoryRequired"),
      compliance: t("notificationsCategoryCompliance")
    };
    const severityLabels = {
      info: t("notificationsSeverityInfo"),
      warning: t("notificationsSeverityWarning"),
      critical: t("notificationsSeverityCritical")
    };

    return (
      <div className="notification-list">
        {mobileNotifications.map((item) => (
          <article key={item.id} className={`panel notification-card severity-${item.severity || "info"}`}>
            <div className="notification-card-meta">
              <span>{categoryLabels[item.category] || item.category}</span>
              <span>{severityLabels[item.severity] || item.severity}</span>
              <span>{new Date(item.created_at).toLocaleString(lang === "ru" ? "ru-RU" : "en-CA")}</span>
            </div>
            <div className="notification-card-heading">
              <h3>{item.title}</h3>
              {hasPermission("notifications", "edit") && (
                <button type="button" className="danger" onClick={() => deleteMobileNotification(item)}>
                  {t("delete")}
                </button>
              )}
            </div>
            <p>{item.body}</p>
            {item.action_url && (
              <a href={item.action_url} target="_blank" rel="noreferrer">
                {item.action_label || item.action_url}
              </a>
            )}
          </article>
        ))}
        {mobileNotifications.length === 0 && <div className="panel muted">{t("notificationsNoItems")}</div>}
      </div>
    );
  }

  function renderTurukAgentWorkspace() {
    const isNotificationsCreatePage = activePage === "agentNotificationsCreate" || activePage === "notifications";
    const isNotificationsSentPage = activePage === "agentNotificationsSent";
    const isSupportTicketsPage = activePage === "agentSupportTickets";
    const canViewNotifications = hasPermission("notifications", "view");
    const canEditNotifications = hasPermission("notifications", "edit");
    const canViewAgent = hasPermission("settings.agent", "view");
    const canEditAgent = hasPermission("settings.agent", "edit");
    const shouldShowSentNotifications =
      isNotificationsSentPage || (isNotificationsCreatePage && canViewNotifications && !canEditNotifications);

    return (
      <div className="devices-shell">
        <aside className="panel devices-subnav">
          <div className="devices-subnav-scroll">
            {canViewNotifications && (
              <details className="devices-nav-group" open>
                <summary>{t("turukAgentNotificationsGroup")}</summary>
                {canEditNotifications && (
                  <a
                    className={isNotificationsCreatePage ? "devices-nav-btn active" : "devices-nav-btn"}
                    href={`#${PAGE_TO_ROUTE.agentNotificationsCreate}`}
                  >
                    {t("notificationsCreate")}
                  </a>
                )}
                <a
                  className={shouldShowSentNotifications ? "devices-nav-btn active" : "devices-nav-btn"}
                  href={`#${PAGE_TO_ROUTE.agentNotificationsSent}`}
                >
                  {t("turukAgentNotificationsSent")}
                </a>
              </details>
            )}
            {canViewAgent && (
              <a
                className={isSupportTicketsPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.agentSupportTickets}`}
              >
                {t("turukAgentSupportTickets")}
              </a>
            )}
          </div>
        </aside>

        <div className="page notifications-page">
          <div className="panel">
            <h2>{t("turukAgentTitle")}</h2>
            <p className="muted">{t("notificationsSubtitle")}</p>
          </div>

          {isNotificationsCreatePage && canEditNotifications && renderNotificationCreatePanel()}
          {shouldShowSentNotifications && canViewNotifications && renderSentNotificationsPanel()}
          {isSupportTicketsPage && canViewAgent && renderAgentSupportTicketsPanel(canEditAgent)}
        </div>
      </div>
    );
  }

  function renderDevicesWorkspace() {
    const isAllPage = activePage === "devices" || activePage === "devicesList";
    const pagePlatformFilter =
      activePage === "devicesPlatformWindows"
        ? "windows"
        : activePage === "devicesPlatformIOS"
          ? "ios"
          : activePage === "devicesPlatformMacOS"
            ? "macos"
            : activePage === "devicesPlatformAndroid"
              ? "android"
              : "all";
    const effectivePlatformFilter = pagePlatformFilter === "all" ? deviceFilterPlatform : pagePlatformFilter;

    const deviceColumns = [
      { key: "device_name", label: t("devicesColName"), value: (item) => item.device_name || item.udid || "-" },
      { key: "imei", label: t("devicesColIMEI"), value: (item) => item.imei || "-" },
      { key: "wifi_mac", label: t("devicesColWiFiMac"), value: (item) => item.wifi_mac || "-" },
      { key: "meid", label: t("devicesColMEID"), value: (item) => item.meid || "-" },
      { key: "carrier", label: t("devicesColCarrier"), value: (item) => item.carrier || "-" },
      { key: "os_version", label: t("devicesColOSVersion"), value: (item) => item.os_version || "-" },
      {
        key: "ownership",
        label: t("devicesColOwnership"),
        value: (item) => formatDeviceOwnership(item.ownership, t)
      },
      { key: "cert_expiry", label: t("devicesColCertExpiry"), value: (item) => formatTimestamp(item.cert_expiry_at) },
      { key: "enrollment_date", label: t("devicesColEnrollmentDate"), value: (item) => formatTimestamp(item.created_at) },
      { key: "encrypted", label: t("devicesColEncrypted"), value: (item) => formatBool(item.encrypted, t) },
      { key: "account_name", label: t("devicesColAccountName"), value: (item) => item.account_name || "-" },
      { key: "model", label: t("devicesColModel"), value: (item) => item.model || item.product_name || "-" },
      { key: "phone", label: t("devicesColPhone"), value: (item) => item.phone_number || "-" },
      { key: "os", label: t("devicesColOS"), value: (item) => profilePlatformLabel(normalizePlatform(item), t) },
      { key: "total_storage", label: t("devicesColTotalStorage"), value: (item) => item.total_storage_mb ?? "-" },
      { key: "primary_email", label: t("devicesColPrimaryEmail"), value: (item) => item.primary_email || "-" },
      { key: "last_sync", label: t("devicesColLastSync"), value: (item) => formatTimestamp(item.last_seen_at || item.last_checkin_at || item.last_token_update_at) },
      { key: "free_storage", label: t("devicesColFreeStorage"), value: (item) => item.free_storage_mb ?? "-" },
      { key: "serial", label: t("devicesColSerial"), value: (item) => item.serial_number || "-" },
      {
        key: "compliance",
        label: t("devicesColCompliance"),
        value: (item) =>
          item.compliance_status === "action_required"
            ? `${t("complianceActionRequired")} (${Number(item.compliance_issue_count || 0)})`
            : t("complianceCompliant")
      }
    ];

    const nowTs = Date.now();
    const accountFilterNeedle = String(deviceFilterAccountName || "")
      .trim()
      .toLowerCase();
    const shownDevices = filteredDevices.filter((item) => {
      const platform = normalizePlatform(item);
      if (effectivePlatformFilter !== "all" && platform !== effectivePlatformFilter) return false;
      if (accountFilterNeedle) {
        const itemAccount = String(item.account_name || "")
          .trim()
          .toLowerCase();
        if (itemAccount !== accountFilterNeedle) return false;
      }
      if (deviceFilterManaged !== "all") {
        const managed = Boolean(item.is_managed ?? item.controlled ?? item.token_len);
        if (deviceFilterManaged === "yes" && !managed) return false;
        if (deviceFilterManaged === "no" && managed) return false;
      }
      if (deviceFilterEncrypted !== "all") {
        const encrypted = Boolean(item.encrypted);
        if (deviceFilterEncrypted === "yes" && !encrypted) return false;
        if (deviceFilterEncrypted === "no" && encrypted) return false;
      }
      if (deviceFilterCompliance !== "all") {
        const compliant = item.compliance_status !== "action_required";
        if (deviceFilterCompliance === "yes" && !compliant) return false;
        if (deviceFilterCompliance === "no" && compliant) return false;
      }
      if (!matchesDeviceLastSeenFilter(item, deviceFilterLastSeen, nowTs)) return false;
      return true;
    });

    const visibleColumns = deviceColumns.filter((column) => visibleDeviceColumns.includes(column.key));
    const visibleUDIDs = shownDevices.map((item) => item.udid).filter(Boolean);
    const allVisibleSelected =
      visibleUDIDs.length > 0 && visibleUDIDs.every((udid) => selectedDeviceUDIDs.includes(udid));
    const canBulkRequestFromListPage =
      activePage === "devices" ||
      activePage === "devicesList" ||
      activePage === "devicesPlatformWindows" ||
      activePage === "devicesPlatformIOS" ||
      activePage === "devicesPlatformMacOS" ||
      activePage === "devicesPlatformAndroid";

    if (routeDeviceUDID) {
      const device = deviceDetail || selectedDevice || shownDevices.find((item) => item.udid === routeDeviceUDID);
      const devicePlatform = normalizePlatform(device || {});
      const locationUnavailableReason = deviceLocationUnavailableReason(device, t);
      const appleCommandSections = appleDeviceCommandSections(appleDeviceCommandsForPlatform(devicePlatform));
      const canViewDeviceRemoteAccess = hasPermission("settings.security", "view");
      const canEditDeviceRemoteAccess = hasPermission("settings.security", "edit");
      const deviceComplianceIssues = complianceIssues
        .filter((item) => String(item?.device_udid || "").trim() === routeDeviceUDID)
        .sort((left, right) => {
          const leftOpen = left.status === "open" ? 0 : 1;
          const rightOpen = right.status === "open" ? 0 : 1;
          if (leftOpen !== rightOpen) return leftOpen - rightOpen;
          return new Date(right.updated_at || right.last_detected_at || 0) - new Date(left.updated_at || left.last_detected_at || 0);
        });
      const openDeviceComplianceIssues = deviceComplianceIssues.filter((item) => item.status === "open");
      const verificationRequestTypes = new Set([
        "DeviceInformation",
        "SecurityInfo",
        "ProfileList",
        "InstalledApplicationList",
        "AvailableOSUpdates"
      ]);
      const allSelectedDeviceVerificationCommands = commands
        .filter((item) =>
          String(item?.device_udid || "").trim() === routeDeviceUDID &&
          verificationRequestTypes.has(String(item?.request_type || "").trim())
        )
        .sort((a, b) => {
          const left = new Date(b.updated_at || b.acknowledged_at || b.sent_at || b.queued_at || 0).getTime();
          const right = new Date(a.updated_at || a.acknowledged_at || a.sent_at || a.queued_at || 0).getTime();
          return left - right;
        });
      const latestVerificationCommandForIssue = (issue) => {
        const types =
          issue?.rule_type === "profile_required"
            ? ["ProfileList"]
            : issue?.rule_type === "app_required"
              ? ["InstalledApplicationList"]
              : issue?.rule_type === "passcode_required"
                ? ["SecurityInfo"]
                : issue?.rule_type === "minimum_os_version"
                  ? ["DeviceInformation", "AvailableOSUpdates"]
                  : ["DeviceInformation"];
        return allSelectedDeviceVerificationCommands.find((item) => types.includes(String(item?.request_type || "").trim())) || null;
      };
      const deviceComplianceStatus = (issue) => {
        if (issue?.status === "resolved" || issue?.resolved_at) return t("complianceRemediationStatusResolved");
        if (issue?.status === "waived") return t("complianceRemediationStatusWaived");
        if (issue?.confirmed_at) return t("complianceRemediationStatusConfirmed");
        if (issue?.action_started_at) return t("complianceRemediationStatusWaitingSync");
        if (issue?.notification_id) return t("complianceRemediationStatusInstructionSent");
        return t("complianceRemediationStatusWaitingAction");
      };
      const deviceCompliancePrimaryActionLabel = (issue) => {
        if (issue?.remediation_type === "install_profile") return t("complianceIssueInstallProfile");
        if (issue?.remediation_type === "install_app") return t("complianceIssueInstallApp");
        if (issue?.rule_type === "device_attestation_required") return t("complianceIssueRequestAttestation");
        if (issue?.rule_type === "minimum_os_version") return t("complianceIssueSendOSUpdateInstruction");
        if (issue?.rule_type === "passcode_required") return t("complianceIssueSendPasscodeInstruction");
        if (issue?.rule_type === "allowed_sim_iccid") return t("complianceIssueSendSIMInstruction");
        return t("complianceIssueStartRemediation");
      };
      const runDeviceCompliancePrimaryAction = (issue) => {
        const action = issue?.remediation_type === "open_settings" ? "remind" : "start";
        return runComplianceIssueAction(issue, action);
      };
      const renderDeviceCompliancePanel = () => (
        <div className="detail-block device-compliance-panel">
          <div className="detail-block-heading">
            <div>
              <h4>{t("deviceDetailsComplianceTitle")}</h4>
              <p className="muted">{t("deviceDetailsComplianceOpenIssues")}: {openDeviceComplianceIssues.length}</p>
            </div>
            <button className="toolbar-action" type="button" onClick={() => requestDeviceRefresh(routeDeviceUDID)}>
              {t("deviceDetailsComplianceCheckNow")}
            </button>
          </div>
          {openDeviceComplianceIssues.length > 0 ? (
            <div className="devices-table-wrap compact-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("deviceDetailsComplianceRule")}</th>
                    <th>{t("deviceDetailsComplianceStatus")}</th>
                    <th>{t("complianceReason")}</th>
                    <th>{t("deviceDetailsComplianceDueAt")}</th>
                    <th>{t("deviceDetailsComplianceLastCommand")}</th>
                    <th>{t("tableAction")}</th>
                  </tr>
                </thead>
                <tbody>
                  {openDeviceComplianceIssues.map((issue) => {
                    const lastCommand = latestVerificationCommandForIssue(issue);
                    return (
                      <tr key={issue.id}>
                        <td>{issue.rule_name || issue.rule_type || "-"}</td>
                        <td><span className="status-pill warning">{deviceComplianceStatus(issue)}</span></td>
                        <td>{issue.reason_text || issue.reason_code || "-"}</td>
                        <td>{issue.due_at ? formatTimestamp(issue.due_at) : "-"}</td>
                        <td>
                          {lastCommand ? (
                            <span className="compliance-action-summary">
                              <strong>{lastCommand.request_type}</strong>
                              <span>{formatCommandState(lastCommand.state, lastCommand.result_status, t)}</span>
                              <span>{formatTimestamp(lastCommand.updated_at || lastCommand.acknowledged_at || lastCommand.sent_at || lastCommand.queued_at)}</span>
                            </span>
                          ) : (
                            <span className="muted">{t("deviceDetailsComplianceNoCommand")}</span>
                          )}
                        </td>
                        <td>
                          <div className="inline-actions">
                            {hasPermission("devices.management.compliance", "edit") && !issue.action_started_at && (
                              <button type="button" className="small-action" onClick={() => runDeviceCompliancePrimaryAction(issue)}>
                                {deviceCompliancePrimaryActionLabel(issue)}
                              </button>
                            )}
                            <a
                              className="small-action link-action"
                              href={`#${PAGE_TO_ROUTE.policiesCompliance}`}
                              onClick={() => setSelectedComplianceIssueID(issue.id)}
                            >
                              {t("complianceEvidenceOpen")}
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="devices-empty">{t("deviceDetailsComplianceNoIssues")}</p>
          )}
        </div>
      );
      const renderDeviceSIMAuditPanel = () => {
        const currentICCID = String(currentDeviceICCID(device) || "").trim();
        const allowedICCID = String(device?.network?.allowed_iccid || "").trim();
        const normalizeSIMICCID = (value) => String(value || "").replace(/[\s.-]+/g, "").toUpperCase();
        const simMatches = currentICCID && allowedICCID && normalizeSIMICCID(currentICCID) === normalizeSIMICCID(allowedICCID);
        const canPromoteCurrentSIM =
          hasPermission("devices.detail", "edit") &&
          currentICCID &&
          normalizeSIMICCID(currentICCID) !== normalizeSIMICCID(allowedICCID);
        return (
          <DetailBlock title={t("deviceDetailsSIMAuditTitle")}>
            <DetailRow label={t("deviceDetailsSIMCurrentICCID")} value={currentICCID} />
            <DetailRow
              label={t("deviceDetailsSIMAllowedICCID")}
              value={(
                <span className="inline-edit-value">
                  <span>{allowedICCID || "-"}</span>
                  {hasPermission("devices.detail", "edit") && (
                    <>
                      <button
                        type="button"
                        className="icon-button small"
                        aria-label={t("deviceDetailsSIMModalTitle")}
                        title={t("deviceDetailsSIMModalTitle")}
                        onClick={() => openAllowedSIMModal(device)}
                      >
                        i
                      </button>
                      {canPromoteCurrentSIM && (
                        <button
                          type="button"
                          className="small-action"
                          onClick={() => updateDeviceAllowedSIM(device, currentICCID)}
                        >
                          {t("deviceDetailsAllowedICCIDUseCurrent")}
                        </button>
                      )}
                      <button
                        type="button"
                        className="icon-button small"
                        aria-label={t("deviceDetailsAllowedICCIDEdit")}
                        title={t("deviceDetailsAllowedICCIDEdit")}
                        onClick={() => updateDeviceAllowedSIM(device)}
                      >
                        ✎
                      </button>
                    </>
                  )}
                </span>
              )}
            />
            <DetailRow
              className={allowedICCID && !simMatches ? "detail-row-warning" : ""}
              label={t("deviceDetailsSIMMatch")}
              value={
                allowedICCID && !simMatches ? (
                  <span className="sim-mismatch-value">
                    <span className="sim-mismatch-icon">!</span>
                    <span>{formatYesNo(false, lang)}</span>
                  </span>
                ) : (
                  allowedICCID ? formatYesNo(simMatches, lang) : "-"
                )
              }
            />
            <DetailRow label={t("deviceDetailsSIMObservedAt")} value={formatTimestamp(device?.network?.updated_at)} />
          </DetailBlock>
        );
      };
      const renderAllowedSIMModal = () => {
        if (!showAllowedSIMModal) return null;
        const currentICCID = String(currentDeviceICCID(device) || "").trim();
        const allowedICCID = String(device?.network?.allowed_iccid || "").trim();
        const auditRows = Array.isArray(device?.network?.allowed_sim_audit)
          ? device.network.allowed_sim_audit
          : [];
        const normalizeSIMICCID = (value) => String(value || "").replace(/[\s.-]+/g, "").toUpperCase();
        const simMatches = currentICCID && allowedICCID && normalizeSIMICCID(currentICCID) === normalizeSIMICCID(allowedICCID);
        const canPromoteCurrentSIM =
          hasPermission("devices.detail", "edit") &&
          currentICCID &&
          normalizeSIMICCID(currentICCID) !== normalizeSIMICCID(allowedICCID);
        return (
          <div className="modal-backdrop" onClick={closeAllowedSIMModal}>
            <div className="modal-card sim-audit-modal" onClick={(event) => event.stopPropagation()}>
              <div className="modal-heading">
                <div>
                  <h3>{t("deviceDetailsSIMModalTitle")}</h3>
                  <p className="muted">{t("deviceDetailsSIMModalSubtitle")}</p>
                </div>
                <button type="button" className="secondary" onClick={closeAllowedSIMModal}>
                  {t("close")}
                </button>
              </div>
              <div className="form-grid two">
                <label>
                  {t("deviceDetailsSIMCurrentICCID")}
                  <input className="text-input" value={currentICCID || "-"} readOnly />
                </label>
                <label>
                  {t("deviceDetailsSIMAllowedICCID")}
                  <input
                    className="text-input"
                    value={allowedSIMDraft}
                    onChange={(event) => setAllowedSIMDraft(event.target.value)}
                    disabled={!hasPermission("devices.detail", "edit")}
                  />
                </label>
              </div>
              {hasPermission("devices.detail", "edit") && (
                <div className="action-row">
                  <button type="button" className="toolbar-action" onClick={() => saveAllowedSIMFromModal(device)}>
                    {t("deviceDetailsSIMSave")}
                  </button>
                  {canPromoteCurrentSIM && (
                    <button
                      type="button"
                      className="toolbar-action secondary"
                      onClick={() => saveAllowedSIMFromModal(device, currentICCID)}
                    >
                      {t("deviceDetailsAllowedICCIDUseCurrent")}
                    </button>
                  )}
                  <button type="button" className="toolbar-action secondary" onClick={() => setAllowedSIMDraft("")}>
                    {t("clear")}
                  </button>
                </div>
              )}
              <DetailBlock title={t("deviceDetailsSIMEvidenceTitle")}>
                <DetailRow label={t("deviceDetailsSIMCurrentICCID")} value={currentICCID} />
                <DetailRow label={t("deviceDetailsSIMAllowedICCID")} value={allowedICCID} />
                <DetailRow
                  className={allowedICCID && !simMatches ? "detail-row-warning" : ""}
                  label={t("deviceDetailsSIMMatch")}
                  value={
                    allowedICCID && !simMatches ? (
                      <span className="sim-mismatch-value">
                        <span className="sim-mismatch-icon">!</span>
                        <span>{formatYesNo(false, lang)}</span>
                      </span>
                    ) : (
                      allowedICCID ? formatYesNo(simMatches, lang) : "-"
                    )
                  }
                />
                <DetailRow label={t("deviceDetailsSIMObservedAt")} value={formatTimestamp(device?.network?.updated_at)} />
                <DetailRow label="UDID" value={device?.udid || routeDeviceUDID} />
              </DetailBlock>
              <div className="devices-table-wrap compact-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("deviceDetailsSIMAuditOld")}</th>
                      <th>{t("deviceDetailsSIMAuditNew")}</th>
                      <th>{t("deviceDetailsSIMAuditActor")}</th>
                      <th>{t("deviceDetailsSIMAuditAt")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditRows.map((item) => (
                      <tr key={item.id || `${item.old_iccid}-${item.new_iccid}-${item.created_at}`}>
                        <td>{item.old_iccid || "-"}</td>
                        <td>{item.new_iccid || "-"}</td>
                        <td>{item.actor || "-"}</td>
                        <td>{formatTimestamp(item.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {auditRows.length === 0 && <p className="devices-empty">{t("deviceDetailsSIMAuditEmpty")}</p>}
              </div>
            </div>
          </div>
        );
      };
      const optionalDetailRow = (label, value) => (
        hasDisplayValue(value) ? <DetailRow label={label} value={value} /> : null
      );
      const optionalFormattedDetailRow = (label, rawValue, formattedValue) => (
        hasDisplayValue(rawValue) && formattedValue !== "-" ? <DetailRow label={label} value={formattedValue} /> : null
      );
      const deviceRemoteScreenSessions = remoteScreenSessions.filter((item) => {
        const itemUDID = String(item.device_udid || "").trim();
        const itemAccountID = String(item.account_id || "").trim();
        const deviceAccountID = String(device?.account_id || "").trim();
        return itemUDID === routeDeviceUDID || (!itemUDID && deviceAccountID && itemAccountID === deviceAccountID);
      });
      const renderActivationLockPanel = () => (
        <div className="panel compact-panel">
          <section className="detail-block activation-lock-status-block">
            <div className="activation-lock-status-header">
              <h4>{t("deviceDetailsActivationLockTitle")}</h4>
              <div className="inline-actions">
                <button
                  className="toolbar-action"
                  type="button"
                  onClick={() =>
                    queueActivationLockAction(
                      "/commands/activation-lock/bypass-refresh",
                      t("deviceDetailsActivationLockRefreshQueued")
                    )
                  }
                >
                  {t("deviceDetailsActivationLockRefresh")}
                </button>
                <button
                  className="toolbar-action danger"
                  type="button"
                  disabled={deviceActivationLockCodes.length === 0}
                  onClick={() =>
                    queueActivationLockAction(
                      "/commands/activation-lock/clear-bypass-code",
                      t("deviceDetailsActivationLockClearQueued")
                    )
                  }
                >
                  {t("deviceDetailsActivationLockClear")}
                </button>
              </div>
            </div>
            <div className="detail-grid">
              <DetailRow
                label={lang === "ru" ? "Политика применена" : "Policy applied"}
                value={formatBool(device?.activation_lock_policy_applied, t)}
              />
              <DetailRow
                label={lang === "ru" ? "Устройство сообщило включено" : "Reported enabled"}
                value={formatBool(device?.activation_lock_reported_enabled, t)}
              />
            </div>
          </section>
          <div className="devices-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("deviceDetailsActivationLockCode")}</th>
                  <th>{t("deviceDetailsActivationLockReportedAt")}</th>
                  <th>{t("deviceDetailsActivationLockExpiresAt")}</th>
                  <th>{t("deviceDetailsActivationLockSource")}</th>
                </tr>
              </thead>
              <tbody>
                {deviceActivationLockCodes.map((item) => (
                  <tr key={item.id || `${item.code}-${item.reported_at}`}>
                    <td className="monospace-value">{item.code || "-"}</td>
                    <td>{formatTimestamp(item.reported_at)}</td>
                    <td>{item.expires_at ? formatTimestamp(item.expires_at) : "-"}</td>
                    <td>{item.source_command_uuid || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {deviceActivationLockCodes.length === 0 && (
              <p className="devices-empty">{t("deviceDetailsActivationLockNoCodes")}</p>
            )}
          </div>
        </div>
      );
      return (
        <div className="page">
          <div className="panel">
            <div className="device-details-page-header">
              <div className="device-details-title-group">
                <h3>{device?.device_name || device?.udid || routeDeviceUDID}</h3>
              </div>
              <div className="device-details-page-header-actions">
                <button className="toolbar-action" onClick={() => requestDeviceRefresh(routeDeviceUDID)}>
                  {t("deviceDetailsRequestRefresh")}
                </button>
                {deviceDetailsTab === "commands" && (
                  <button
                    className="toolbar-action icon-only"
                    onClick={() => refreshDeviceCommands(routeDeviceUDID)}
                    title={t("refresh")}
                    aria-label={t("refresh")}
                  >
                    ↻
                  </button>
                )}
              </div>
            </div>

            <div className="device-details-tabs">
              <a
                className={deviceDetailsTab === "about" ? "device-details-tab active" : "device-details-tab"}
                href={deviceDetailsHash("devicesDetailAbout", routeDeviceUDID)}
              >
                {t("deviceDetailsTabAbout")}
              </a>
              <a
                className={deviceDetailsTab === "apps" ? "device-details-tab active" : "device-details-tab"}
                href={deviceDetailsHash("devicesDetailApps", routeDeviceUDID)}
              >
                {t("deviceDetailsTabApps")}
              </a>
              <a
                className={deviceDetailsTab === "profiles" ? "device-details-tab active" : "device-details-tab"}
                href={deviceDetailsHash("devicesDetailProfiles", routeDeviceUDID)}
              >
                {t("deviceDetailsTabProfiles")}
              </a>
              <a
                className={deviceDetailsTab === "location" ? "device-details-tab active" : "device-details-tab"}
                href={deviceDetailsHash("devicesDetailLocation", routeDeviceUDID)}
              >
                {t("deviceDetailsTabLocation")}
              </a>
              <a
                className={deviceDetailsTab === "sharedIPad" ? "device-details-tab active" : "device-details-tab"}
                href={deviceDetailsHash("devicesDetailSharedIPad", routeDeviceUDID)}
              >
                {t("deviceDetailsTabSharedIPad")}
              </a>
              {devicePlatform === "macos" && (
                <a
                  className={deviceDetailsTab === "macOSUEM" ? "device-details-tab active" : "device-details-tab"}
                  href={deviceDetailsHash("devicesDetailMacOSUEM", routeDeviceUDID)}
                >
                  {t("deviceDetailsTabMacOSUEM")}
                </a>
              )}
              <a
                className={deviceDetailsTab === "commands" ? "device-details-tab active" : "device-details-tab"}
                href={deviceDetailsHash("devicesDetailCommands", routeDeviceUDID)}
              >
                {t("deviceDetailsTabCommands")}
              </a>
              <div className="device-details-command-menu-wrap" ref={deviceCommandsMenuRef}>
                <button
                  className="device-details-tab"
                  type="button"
                  onClick={() => setShowDeviceCommandsMenu((prev) => !prev)}
                >
                  {t("deviceDetailsCommandsMenu")}
                </button>
                {showDeviceCommandsMenu && (
                  <div className="device-details-command-menu">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeviceCommandsMenu(false);
                        queueCommand(
                          routeDeviceUDID,
                          "/commands/clear-passcode",
                          t("deviceDetailsClearPasscodeQueued"),
                          t("deviceDetailsClearPasscodeFailed")
                        );
                      }}
                    >
                      {t("deviceDetailsCommandClearPasscode")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeviceCommandsMenu(false);
                        queueCommand(
                          routeDeviceUDID,
                          "/commands/device-lock",
                          t("deviceDetailsDeviceLockQueued"),
                          t("deviceDetailsDeviceLockFailed")
                        );
                      }}
                    >
                      {t("deviceDetailsCommandDeviceLock")}
                    </button>
                    {isDeviceLostModeEnabled ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowDeviceCommandsMenu(false);
                          queueCommand(
                            routeDeviceUDID,
                            "/commands/lost-mode/disable",
                            t("deviceDetailsTurnOffLostModeQueued"),
                            t("deviceDetailsTurnOffLostModeFailed")
                          );
                        }}
                      >
                        {t("deviceDetailsCommandTurnOffLostMode")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          openEnableLostModeModal();
                        }}
                      >
                        {t("deviceDetailsCommandTurnOnLostMode")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeviceCommandsMenu(false);
                        queueCommand(
                          routeDeviceUDID,
                          "/commands/play-lost-mode-sound",
                          t("deviceDetailsPlayLostModeSoundQueued"),
                          t("deviceDetailsPlayLostModeSoundFailed")
                        );
                      }}
                    >
                      {t("deviceDetailsCommandPlayLostModeSound")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeviceCommandsMenu(false);
                        queueCommand(
                          routeDeviceUDID,
                          "/commands/restart-device",
                          t("deviceDetailsRestartDeviceQueued"),
                          t("deviceDetailsRestartDeviceFailed")
                        );
                      }}
                    >
                      {t("deviceDetailsCommandRestartDevice")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeviceCommandsMenu(false);
                        queueCommand(
                          routeDeviceUDID,
                          "/commands/shut-down-device",
                          t("deviceDetailsShutDownDeviceQueued"),
                          t("deviceDetailsShutDownDeviceFailed")
                        );
                      }}
                    >
                      {t("deviceDetailsCommandShutDownDevice")}
                    </button>
                    <button
                      className="destructive-command"
                      type="button"
                      onClick={removeCurrentDeviceMDMProfile}
                    >
                      {t("deviceDetailsCommandRemoveMDMProfile")}
                    </button>
                    {devicePlatform === "macos" ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowDeviceCommandsMenu(false);
                          queueCommand(
                            routeDeviceUDID,
                            "/commands/schedule-os-update-scan",
                            t("deviceDetailsScheduleOSUpdateScanQueued"),
                            t("deviceDetailsScheduleOSUpdateScanFailed")
                          );
                        }}
                      >
                        {t("deviceDetailsCommandScheduleOSUpdateScan")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowDeviceCommandsMenu(false);
                          queueCommand(
                            routeDeviceUDID,
                            "/commands/available-os-updates",
                            t("deviceDetailsAvailableOSUpdatesQueued"),
                            t("deviceDetailsAvailableOSUpdatesFailed")
                          );
                        }}
                      >
                        {t("deviceDetailsCommandAvailableOSUpdates")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        openScheduleOSUpdateModal("DownloadOnly");
                      }}
                    >
                      {t("deviceDetailsCommandScheduleOSUpdateDownload")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        openScheduleOSUpdateModal("InstallASAP");
                      }}
                    >
                      {t("deviceDetailsCommandScheduleOSUpdateInstall")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeviceCommandsMenu(false);
                        setShowDeviceActivationLockModal(true);
                      }}
                    >
                      {t("deviceDetailsActivationLockTitle")}
                    </button>
                    <div className="device-details-command-section">
                      <div className="device-details-command-section-title">
                        {lang === "ru" ? "Settings / Configure settings" : "Settings / Configure settings"}
                      </div>
                      {[
                        ["device_name", lang === "ru" ? "Изменить имя устройства" : "Set device name"],
                        ["time_zone", lang === "ru" ? "Установить часовой пояс" : "Set time zone"],
                        ["bluetooth", "Bluetooth"],
                        ["data_roaming", lang === "ru" ? "Роуминг данных" : "Data roaming"],
                        ["personal_hotspot", lang === "ru" ? "Режим модема" : "Personal Hotspot"],
                        ["app_analytics", lang === "ru" ? "App Analytics" : "App Analytics"]
                      ].map(([settingType, label]) => (
                        <button
                          key={settingType}
                          type="button"
                          onClick={() => openDeviceSettingModal(settingType)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {appleCommandSections.map((section) => (
                      <div className="device-details-command-section" key={section.sectionTitleKey}>
                        <div className="device-details-command-section-title">
                          {t(section.sectionTitleKey)}
                        </div>
                        {section.items.map((command) => (
                          <button
                            className={command.destructive ? "destructive-command" : ""}
                            key={command.key}
                            type="button"
                            onClick={() => openAppleCommandModal(command)}
                          >
                            {t(command.labelKey)}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {canViewDeviceRemoteAccess && (
                <a
                  className={deviceDetailsTab === "remoteAccess" ? "device-details-tab active" : "device-details-tab"}
                  href={deviceDetailsHash("devicesDetailRemoteAccess", routeDeviceUDID)}
                >
                  {t("deviceDetailsTabRemoteAccess")}
                </a>
              )}
            </div>

            {telemetryLoading && <p className="muted">{t("loading")}</p>}

            {deviceDetailsTab === "about" && (
              <>
                {deviceComplianceIssues.length > 0 && renderDeviceCompliancePanel()}
                {renderDeviceSIMAuditPanel()}
                <DetailBlock title={t("deviceDetailsTabAbout")}>
                  <DetailRow label="UDID" value={device?.udid || routeDeviceUDID} />
                  <DetailRow label={t("devicesColName")} value={device?.device_name} />
                  <DetailRow label={t("devicesColSerial")} value={device?.serial_number} />
                  <DetailRow label={t("devicesColModel")} value={device?.model || device?.product_name} />
                  <DetailRow label={t("devicesColOS")} value={profilePlatformLabel(normalizePlatform(device || {}), t)} />
                  <DetailRow label={t("devicesColOSVersion")} value={device?.os_version} />
                  <DetailRow label={t("devicesColIMEI")} value={device?.imei} />
                  {optionalDetailRow(t("devicesColMEID"), device?.meid)}
                  <DetailRow label={t("devicesColLastSync")} value={formatTimestamp(device?.network?.updated_at || device?.last_seen_at || device?.last_checkin_at)} />
                  {optionalFormattedDetailRow(t("deviceDetailsBatteryLevel"), device?.network?.battery_level, formatPercentValue(device?.network?.battery_level))}
                  {optionalDetailRow(t("deviceDetailsSupplementalBuild"), device?.network?.supplemental_build_version)}
                  {optionalDetailRow(t("deviceDetailsSupplementalOSExtra"), device?.network?.supplemental_os_version_extra)}
                  {optionalDetailRow(t("deviceDetailsTimeZone"), device?.network?.time_zone)}
                  {optionalDetailRow(t("deviceDetailsDeviceID"), device?.network?.device_id)}
                  {optionalFormattedDetailRow(t("deviceDetailsDoNotDisturb"), device?.network?.do_not_disturb_in_effect, formatYesNo(device?.network?.do_not_disturb_in_effect, lang))}
                  {optionalFormattedDetailRow(t("deviceDetailsICloudBackup"), device?.network?.icloud_backup_enabled, formatYesNo(device?.network?.icloud_backup_enabled, lang))}
                  {optionalDetailRow(t("deviceDetailsLastICloudBackup"), device?.network?.last_icloud_backup_date ? formatTimestamp(device.network.last_icloud_backup_date) : "")}
                  {optionalFormattedDetailRow(t("deviceDetailsFindMy"), device?.network?.find_my_enabled, formatYesNo(device?.network?.find_my_enabled, lang))}
                  {optionalFormattedDetailRow(t("deviceDetailsMDMLostMode"), device?.network?.mdm_lost_mode_enabled, formatYesNo(device?.network?.mdm_lost_mode_enabled, lang))}
                  {optionalDetailRow(t("deviceDetailsModemFirmware"), device?.network?.modem_firmware_version)}
                  {optionalDetailRow(t("deviceDetailsEASDeviceID"), device?.network?.eas_device_identifier)}
                  {optionalDetailRow(t("deviceDetailsEID"), device?.network?.eid)}
                  {optionalDetailRow(t("deviceDetailsESIMIdentifier"), device?.network?.esim_identifier)}
                  {optionalFormattedDetailRow(t("deviceDetailsMultiUser"), device?.network?.multi_user, formatYesNo(device?.network?.multi_user, lang))}
                  {optionalDetailRow(t("deviceDetailsMaximumResidents"), device?.network?.maximum_resident_users)}
                  {optionalDetailRow(t("deviceDetailsAuthGracePeriod"), device?.network?.online_authentication_grace_period)}
                  {optionalDetailRow(t("deviceDetailsQuotaSize"), device?.network?.quota_size)}
                  {optionalDetailRow(t("deviceDetailsResidentUsers"), device?.network?.resident_users)}
                  {optionalDetailRow(t("deviceDetailsActiveManagedUsers"), device?.network?.active_managed_users)}
                  {optionalDetailRow(t("deviceDetailsManagedAdmins"), device?.network?.admin_accounts)}
                  {optionalFormattedDetailRow(t("deviceDetailsAppleSilicon"), device?.network?.is_apple_silicon, formatYesNo(device?.network?.is_apple_silicon, lang))}
                  {optionalFormattedDetailRow(t("deviceDetailsSIP"), device?.network?.sip_enabled, formatYesNo(device?.network?.sip_enabled, lang))}
                  {optionalDetailRow(t("devicesColPhone"), device?.phone_number || device?.network?.phone_number)}
                  {optionalDetailRow(t("devicesColWiFiMac"), device?.wifi_mac)}
                  {optionalDetailRow(t("deviceDetailsBluetoothMac"), device?.bluetooth_mac)}
                  {(devicePlatform === "macos" || hasDisplayValue(device?.network?.ethernet_macs)) && (
                    <DetailRow label={t("deviceDetailsEthernetMacs")} value={formatListValue(device?.network?.ethernet_macs)} />
                  )}
                  {hasDisplayValue(device?.network?.connected_wifi_ssid) && (
                    <DetailRow label={t("deviceDetailsConnectedWiFiSSID")} value={device?.network?.connected_wifi_ssid} />
                  )}
                  {hasDisplayValue(device?.network?.connected_wifi_bssid) && (
                    <DetailRow label={t("deviceDetailsConnectedWiFiBSSID")} value={device?.network?.connected_wifi_bssid} />
                  )}
                  {optionalDetailRow(t("deviceDetailsCarrierSettingsVersion"), device?.network?.carrier_settings_version)}
                  {optionalDetailRow(t("deviceDetailsCurrentCarrier"), device?.network?.current_carrier_network || device?.network?.carrier_name || device?.carrier)}
                  {optionalDetailRow(t("deviceDetailsCurrentMCC"), device?.network?.current_mcc)}
                  {optionalDetailRow(t("deviceDetailsCurrentMNC"), device?.network?.current_mnc)}
                  {optionalFormattedDetailRow(t("deviceDetailsDataRoaming"), device?.network?.data_roaming_enabled, formatYesNo(device?.network?.data_roaming_enabled, lang))}
                  {optionalDetailRow(t("deviceDetailsICCID"), currentDeviceICCID(device))}
                  <DetailRow
                    label={t("deviceDetailsAllowedICCID")}
                    value={(
                      <span className="inline-edit-value">
                        <span>{device?.network?.allowed_iccid || currentDeviceICCID(device) || "-"}</span>
                        <button
                          type="button"
                          className="icon-button small"
                          aria-label={t("deviceDetailsSIMModalTitle")}
                          title={t("deviceDetailsSIMModalTitle")}
                          onClick={() => openAllowedSIMModal(device)}
                        >
                          i
                        </button>
                        {hasPermission("devices.detail", "edit") && (
                          <button
                            type="button"
                            className="icon-button small"
                            aria-label={t("deviceDetailsAllowedICCIDEdit")}
                            title={t("deviceDetailsAllowedICCIDEdit")}
                            onClick={() => updateDeviceAllowedSIM(device)}
                          >
                            ✎
                          </button>
                        )}
                      </span>
                    )}
                  />
                  {optionalFormattedDetailRow(t("deviceDetailsNetworkTethered"), device?.network?.network_tethered, formatYesNo(device?.network?.network_tethered, lang))}
                  {optionalFormattedDetailRow(t("deviceDetailsPersonalHotspot"), device?.network?.personal_hotspot_enabled, formatYesNo(device?.network?.personal_hotspot_enabled, lang))}
                  {optionalFormattedDetailRow(t("deviceDetailsIsRoaming"), device?.network?.is_roaming, formatYesNo(device?.network?.is_roaming, lang))}
                  {optionalDetailRow(t("deviceDetailsSIMCarrier"), device?.network?.sim_carrier_network)}
                  {optionalDetailRow(t("deviceDetailsSIMMCC"), device?.network?.sim_mcc)}
                  {optionalDetailRow(t("deviceDetailsSIMMNC"), device?.network?.sim_mnc)}
                  {optionalDetailRow(t("deviceDetailsSubscriberCarrier"), device?.network?.subscriber_carrier_network)}
                  {optionalDetailRow(t("deviceDetailsSubscriberMCC"), device?.network?.subscriber_mcc)}
                  {optionalDetailRow(t("deviceDetailsSubscriberMNC"), device?.network?.subscriber_mnc)}
                  {optionalFormattedDetailRow(t("deviceDetailsCellularDataUsage"), device?.network?.cellular_data_bytes, formatBytesValue(device?.network?.cellular_data_bytes))}
                  {optionalFormattedDetailRow(t("deviceDetailsWiFiDataUsage"), device?.network?.wifi_data_bytes, formatBytesValue(device?.network?.wifi_data_bytes))}
                </DetailBlock>
              </>
            )}

            {deviceDetailsTab === "apps" && (
              <>
                {(hasSelectedReinstallableDeviceApps || hasSelectedRemovableDeviceApps) && (
                  <div className="action-row">
                    {hasSelectedReinstallableDeviceApps && (
                      <button onClick={reinstallSelectedDeviceApps}>{t("deviceDetailsReinstallApps")}</button>
                    )}
                    {hasSelectedRemovableDeviceApps && (
                      <button className="danger" onClick={removeSelectedManagedApps}>
                        {t("deviceDetailsRemoveApps")}
                      </button>
                    )}
                  </div>
                )}
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th />
                        <th>{t("appsColName")}</th>
                        <th>{t("appsColBundleID")}</th>
                        <th>{t("appsColVersion")}</th>
                        <th>{t("tableState")}</th>
                        <th>{t("devicesColControlled")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceAppsViewItems.map((item) => {
                        const bundleID = String(item._bundleID || "").trim();
                        const isManagedSelectable = item._isAssignedNotInstalled || item.is_managed === true;
                        const canSelect = Boolean(bundleID) && isManagedSelectable;
                        const assignmentReason = String(item._assignmentResultDetail || "").trim();
                        const stateLabel = item._isAssignedNotInstalled
                          ? item._assignmentFailed
                            ? item._assignmentResultStatus
                              ? `${t("deviceDetailsAppInstallFailed")} (${item._assignmentResultStatus})`
                              : t("deviceDetailsAppInstallFailed")
                            : t("deviceDetailsAppInstallPending")
                          : t("deviceDetailsAppInstallDone");
                        const stateClass = item._isAssignedNotInstalled
                          ? item._assignmentFailed
                            ? "device-app-state failed"
                            : "device-app-state pending"
                          : "device-app-state done";
                        const failureTitle = item._assignmentResultStatus
                          ? `${t("deviceDetailsAppInstallFailed")}: ${item._assignmentResultStatus}`
                          : t("deviceDetailsAppInstallFailed");
                        const failureTitleFull =
                          item._assignmentFailed && assignmentReason
                            ? `${failureTitle}. ${t("deviceDetailsAppInstallReason")}: ${assignmentReason}`
                            : failureTitle;
                        return (
                          <tr
                            key={item._rowKey}
                            className={
                              item._isAssignedNotInstalled
                                ? item._assignmentFailed
                                  ? "device-app-row assigned-not-installed failed"
                                  : "device-app-row assigned-not-installed"
                                : "device-app-row"
                            }
                          >
                            <td className={canSelect ? "device-app-checkbox-cell" : "device-app-checkbox-cell disabled"}>
                              <input
                                type="checkbox"
                                checked={bundleID ? selectedDeviceAppBundleIDs.includes(bundleID) : false}
                                onChange={() => {
                                  if (!canSelect) return;
                                  toggleDeviceAppSelection(bundleID);
                                }}
                                disabled={!canSelect}
                              />
                            </td>
                            <td>
                              <span className={item._isAssignedNotInstalled ? "device-app-name pending" : "device-app-name"}>
                                {item._assignmentFailed && (
                                  <span
                                    className="device-app-failed-mark"
                                    title={failureTitleFull}
                                    aria-label={failureTitleFull}
                                  >
                                    ✖
                                  </span>
                                )}
                                <span>{item._displayName}</span>
                              </span>
                            </td>
                            <td>{item._bundleID || "-"}</td>
                            <td>{item._displayVersion}</td>
                            <td>
                              <span className={stateClass}>{stateLabel}</span>
                              {item._assignmentFailed && (
                                <div className="device-app-state-detail">
                                  {t("deviceDetailsAppInstallReason")}:{" "}
                                  {assignmentReason || t("deviceDetailsAppInstallReasonUnknown")}
                                </div>
                              )}
                            </td>
                            <td>{formatBool(item.is_managed, t)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {deviceAppsViewItems.length === 0 && <p className="devices-empty">{t("deviceDetailsNoApps")}</p>}
                </div>
              </>
            )}

            {deviceDetailsTab === "profiles" && (
              <>
                {selectedDeviceProfileIDs.length > 0 && (
                  <div className="action-row">
                    <button onClick={resendSelectedDeviceProfiles}>{t("deviceDetailsResendProfiles")}</button>
                    <button className="danger" onClick={disableSelectedDeviceProfiles}>
                      {t("deviceDetailsDisableProfiles")}
                    </button>
                  </div>
                )}
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th />
                        <th>{t("profilesColName")}</th>
                        <th>{t("profilesColIdentifier")}</th>
                        <th>{t("tableState")}</th>
                        <th>{t("profilesColUpdatedAt")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDeviceProfiles.map((item) => {
                        const id = String(item.profile_id || "").trim();
                        const assignmentReason = String(item._assignmentResultDetail || "").trim();
                        const stateLabel = item._isAssignedNotInstalled
                          ? item._assignmentFailed
                            ? item._assignmentResultStatus
                              ? `${t("deviceDetailsProfileInstallFailed")} (${item._assignmentResultStatus})`
                              : t("deviceDetailsProfileInstallFailed")
                            : t("deviceDetailsProfileInstallPending")
                          : t("deviceDetailsProfileInstallDone");
                        const stateClass = item._isAssignedNotInstalled
                          ? item._assignmentFailed
                            ? "device-app-state failed"
                            : "device-app-state pending"
                          : "device-app-state done";
                        const failureTitle = item._assignmentResultStatus
                          ? `${t("deviceDetailsProfileInstallFailed")}: ${item._assignmentResultStatus}`
                          : t("deviceDetailsProfileInstallFailed");
                        const failureTitleFull =
                          item._assignmentFailed && assignmentReason
                            ? `${failureTitle}. ${t("deviceDetailsProfileInstallReason")}: ${assignmentReason}`
                            : failureTitle;
                        return (
                          <tr
                            key={item._rowKey || id || item.profile_name}
                            className={
                              item._isAssignedNotInstalled
                                ? item._assignmentFailed
                                  ? "device-app-row assigned-not-installed failed"
                                  : "device-app-row assigned-not-installed"
                                : "device-app-row"
                            }
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={id ? selectedDeviceProfileIDs.includes(id) : false}
                                onChange={() => toggleDeviceProfileSelection(id)}
                                disabled={!id}
                              />
                            </td>
                            <td>
                              <span className={item._isAssignedNotInstalled ? "device-app-name pending" : "device-app-name"}>
                                {item._assignmentFailed && (
                                  <span
                                    className="device-app-failed-mark"
                                    title={failureTitleFull}
                                    aria-label={failureTitleFull}
                                  >
                                    ✖
                                  </span>
                                )}
                                <span>{item.profile_name || "-"}</span>
                              </span>
                            </td>
                            <td>{item.profile_identifier || "-"}</td>
                            <td>
                              <span className={stateClass}>{stateLabel}</span>
                              {item._assignmentFailed && (
                                <div className="device-app-state-detail">
                                  {t("deviceDetailsProfileInstallReason")}:{" "}
                                  {assignmentReason || t("deviceDetailsProfileInstallReasonUnknown")}
                                </div>
                              )}
                            </td>
                            <td>{formatTimestamp(item.updated_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {selectedDeviceProfiles.length === 0 && (
                    <p className="devices-empty">{t("deviceDetailsNoProfiles")}</p>
                  )}
                </div>
                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{lang === "ru" ? "Сертификаты устройства" : "Device certificates"}</h4>
                      <p>
                        {lang === "ru"
                          ? "Инвентарь сертификатов приходит из Apple MDM CertificateList."
                          : "Certificate inventory is populated from Apple MDM CertificateList."}
                      </p>
                    </div>
                    <button className="toolbar-action" type="button" onClick={requestDeviceCertificates}>
                      {lang === "ru" ? "Обновить сертификаты" : "Refresh certificates"}
                    </button>
                  </div>
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{lang === "ru" ? "Название" : "Name"}</th>
                          <th>{lang === "ru" ? "Издатель" : "Issuer"}</th>
                          <th>{lang === "ru" ? "Серийный номер" : "Serial"}</th>
                          <th>{lang === "ru" ? "Истекает" : "Expires"}</th>
                          <th>{lang === "ru" ? "Identity" : "Identity"}</th>
                          <th>{lang === "ru" ? "Managed" : "Managed"}</th>
                          <th>SHA-256</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deviceCertificates.map((item) => (
                          <tr key={item.certificate_id}>
                            <td>{item.common_name || item.subject || "-"}</td>
                            <td>{item.issuer || "-"}</td>
                            <td className="monospace-value">{item.serial_number || "-"}</td>
                            <td>{formatTimestamp(item.not_after)}</td>
                            <td>{formatBool(item.is_identity, t)}</td>
                            <td>{formatBool(item.is_managed, t)}</td>
                            <td className="monospace-value">{item.sha256_fingerprint || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {deviceCertificates.length === 0 && (
                      <p className="devices-empty">
                        {lang === "ru"
                          ? "Сертификаты пока не получены. Нажмите «Обновить сертификаты»."
                          : "No certificates received yet. Click Refresh certificates."}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            {deviceDetailsTab === "location" && (
              <>
                <div className="device-location-request-row">
                  <button
                    className="toolbar-action"
                    type="button"
                    disabled={Boolean(locationUnavailableReason)}
                    onClick={requestDeviceLocation}
                  >
                    {t("deviceDetailsLocationRequest")}
                  </button>
                  <span className="muted">
                    {locationUnavailableReason ||
                      (isDeviceLostModeEnabled
                        ? t("deviceDetailsLocationLostModeActiveHint")
                        : t("deviceDetailsLocationLostModeRequiredHint"))}
                  </span>
                </div>
                {selectedDeviceLocations.length > 0 ? (
                  (() => {
                    const latestPoint = selectedDeviceLocations[0];
                    const links = buildDeviceLocationMapLinks(latestPoint?.latitude, latestPoint?.longitude);
                    const accuracyParts = [
                      Number.isFinite(Number(latestPoint?.horizontal_accuracy))
                        ? `H ±${formatCoordinate(latestPoint?.horizontal_accuracy, 1)} m`
                        : "",
                      Number.isFinite(Number(latestPoint?.vertical_accuracy))
                        ? `V ±${formatCoordinate(latestPoint?.vertical_accuracy, 1)} m`
                        : ""
                    ].filter(Boolean);
                    return (
                      <div className="device-location-layout">
                        <DetailBlock title={t("deviceDetailsTabLocation")}>
                          <DetailRow label={t("deviceDetailsLocationUpdatedAt")} value={formatTimestamp(latestPoint?.observed_at)} />
                          <DetailRow label={t("deviceDetailsLocationLatitude")} value={formatCoordinate(latestPoint?.latitude)} />
                          <DetailRow label={t("deviceDetailsLocationLongitude")} value={formatCoordinate(latestPoint?.longitude)} />
                          <DetailRow
                            label={t("deviceDetailsLocationAltitude")}
                            value={
                              Number.isFinite(Number(latestPoint?.altitude))
                                ? `${formatCoordinate(latestPoint?.altitude, 1)} m`
                                : "-"
                            }
                          />
                          <DetailRow
                            label={t("deviceDetailsLocationAccuracy")}
                            value={accuracyParts.length > 0 ? accuracyParts.join(", ") : "-"}
                          />
                          <DetailRow
                            label={t("deviceDetailsLocationSpeed")}
                            value={
                              Number.isFinite(Number(latestPoint?.speed))
                                ? `${formatCoordinate(latestPoint?.speed, 1)} m/s`
                                : "-"
                            }
                          />
                          <DetailRow
                            label={t("deviceDetailsLocationSource")}
                            value={String(latestPoint?.raw?.source || "-")}
                          />
                        </DetailBlock>

                        <DetailBlock title={t("deviceDetailsLocationMap")}>
                          <div className="device-location-map-layout">
                            {links.embedURL ? (
                              <iframe
                                className="device-location-map"
                                src={links.embedURL}
                                title="device-location-map"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                              />
                            ) : (
                              <p className="devices-empty">{t("deviceDetailsNoLocation")}</p>
                            )}
                            <div className="device-location-map-links">
                              {links.osmURL && (
                                <a href={links.osmURL} target="_blank" rel="noreferrer">
                                  {t("deviceDetailsLocationOpenOSM")}
                                </a>
                              )}
                              {links.googleURL && (
                                <a href={links.googleURL} target="_blank" rel="noreferrer">
                                  {t("deviceDetailsLocationOpenGoogle")}
                                </a>
                              )}
                            </div>
                          </div>
                        </DetailBlock>

                        <DetailBlock title={t("deviceDetailsLocationHistory")}>
                          <div className="devices-table-wrap device-location-history-table-wrap">
                            <table>
                              <thead>
                                <tr>
                                  <th>{t("deviceDetailsLocationUpdatedAt")}</th>
                                  <th>{t("deviceDetailsLocationLatitude")}</th>
                                  <th>{t("deviceDetailsLocationLongitude")}</th>
                                  <th>{t("deviceDetailsLocationAccuracy")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedDeviceLocations.map((point, idx) => {
                                  const accuracy = [
                                    Number.isFinite(Number(point?.horizontal_accuracy))
                                      ? `H ±${formatCoordinate(point?.horizontal_accuracy, 1)} m`
                                      : "",
                                    Number.isFinite(Number(point?.vertical_accuracy))
                                      ? `V ±${formatCoordinate(point?.vertical_accuracy, 1)} m`
                                      : ""
                                  ]
                                    .filter(Boolean)
                                    .join(", ");
                                  return (
                                    <tr key={`${point?.observed_at || "point"}-${idx}`}>
                                      <td>{formatTimestamp(point?.observed_at)}</td>
                                      <td>{formatCoordinate(point?.latitude)}</td>
                                      <td>{formatCoordinate(point?.longitude)}</td>
                                      <td>{accuracy || "-"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </DetailBlock>
                      </div>
                    );
                  })()
                ) : (
                  <p className="devices-empty">{t("deviceDetailsNoLocation")}</p>
                )}
              </>
            )}

            {deviceDetailsTab === "sharedIPad" && (
              <div className="device-management-layout">
                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{t("deviceDetailsSharedIPadTitle")}</h4>
                      <p>{t("deviceDetailsSharedIPadHint")}</p>
                    </div>
                    <button
                      className="toolbar-action"
                      type="button"
                      onClick={() =>
                        queueSharedIPadCommand(
                          "/commands/shared-ipad/user-list",
                          { udid: routeDeviceUDID },
                          t("deviceDetailsSharedIPadRefreshUsers")
                        )
                      }
                    >
                      {t("deviceDetailsSharedIPadRefreshUsers")}
                    </button>
                  </div>
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("deviceDetailsSharedIPadUserName")}</th>
                          <th>{t("deviceDetailsSharedIPadFullName")}</th>
                          <th>{t("deviceDetailsSharedIPadLoggedIn")}</th>
                          <th>{t("deviceDetailsSharedIPadData")}</th>
                          <th>{t("profilesColUpdatedAt")}</th>
                          <th>{t("tableAction")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deviceSharedIPadUsers.map((item) => {
                          const userName = String(item.user_name || "").trim();
                          const dataBits = [
                            item.data_used_mb != null ? `${item.data_used_mb} MB` : "",
                            item.data_quota_mb != null ? `/ ${item.data_quota_mb} MB` : "",
                            item.has_data_to_sync === true ? "sync pending" : ""
                          ].filter(Boolean);
                          return (
                            <tr key={`${item.device_udid || routeDeviceUDID}-${userName}`}>
                              <td>{userName || "-"}</td>
                              <td>{item.full_name || "-"}</td>
                              <td>{formatBool(item.logged_in, t)}</td>
                              <td>{dataBits.length > 0 ? dataBits.join(" ") : "-"}</td>
                              <td>{formatTimestamp(item.updated_at || item.last_seen_at)}</td>
                              <td>
                                <div className="inline-actions">
                                  <button
                                    className="small-action"
                                    type="button"
                                    disabled={!userName}
                                    onClick={() =>
                                      queueSharedIPadCommand("/commands/shared-ipad/logout-user", {
                                        udid: routeDeviceUDID,
                                        user_name: userName
                                      })
                                    }
                                  >
                                    {t("deviceDetailsSharedIPadLogout")}
                                  </button>
                                  <button
                                    className="small-action danger"
                                    type="button"
                                    disabled={!userName}
                                    onClick={() =>
                                      queueSharedIPadCommand("/commands/shared-ipad/delete-user", {
                                        udid: routeDeviceUDID,
                                        user_name: userName
                                      })
                                    }
                                  >
                                    {t("deviceDetailsSharedIPadDelete")}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {deviceSharedIPadUsers.length === 0 && (
                      <p className="devices-empty">{t("deviceDetailsSharedIPadNoUsers")}</p>
                    )}
                  </div>
                </div>

                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{t("deviceDetailsSharedIPadSettings")}</h4>
                      <p>
                        {sharedIPadSettings?.updated_at
                          ? `${t("profilesColUpdatedAt")}: ${formatTimestamp(sharedIPadSettings.updated_at)}`
                          : ""}
                      </p>
                    </div>
                    <button className="toolbar-action" type="button" onClick={saveSharedIPadSettings}>
                      {t("save")}
                    </button>
                  </div>
                  <div className="modal-grid">
                    <label>{t("deviceDetailsSharedIPadManagedDomain")}</label>
                    <input
                      className="text-input"
                      value={sharedIPadSettingsDraft.managed_apple_account_domain}
                      onChange={(event) =>
                        setSharedIPadSettingsDraft((prev) => ({
                          ...prev,
                          managed_apple_account_domain: event.target.value
                        }))
                      }
                      placeholder="company.com"
                    />
                    <label>{t("deviceDetailsSharedIPadDefaultDomain")}</label>
                    <input
                      className="text-input"
                      value={sharedIPadSettingsDraft.default_shared_ipad_domain}
                      onChange={(event) =>
                        setSharedIPadSettingsDraft((prev) => ({
                          ...prev,
                          default_shared_ipad_domain: event.target.value
                        }))
                      }
                      placeholder="students.company.com"
                    />
                    <label>{t("deviceDetailsSharedIPadClassroomNotes")}</label>
                    <textarea
                      className="text-input"
                      value={sharedIPadSettingsDraft.classroom_notes}
                      onChange={(event) =>
                        setSharedIPadSettingsDraft((prev) => ({
                          ...prev,
                          classroom_notes: event.target.value
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {deviceDetailsTab === "activationLock" && (
              <div className="device-management-layout">
                {renderActivationLockPanel()}
              </div>
            )}

            {deviceDetailsTab === "macOSUEM" && (
              <div className="device-management-layout macos-uem-layout">
                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{lang === "ru" ? "Turuk macOS Agent" : "Turuk macOS Agent"}</h4>
                      <p>
                        {lang === "ru"
                          ? "После регистрации Mac через MDM сервер сам установит bootstrap profile, скачает pkg и активирует per-device token."
                          : "After Mac MDM enrollment, the server installs the bootstrap profile, downloads the pkg, and activates a per-device token automatically."}
                      </p>
                    </div>
                    <button className="toolbar-action secondary" type="button" onClick={() => loadMacOSUEM(routeDeviceUDID)}>
                      {t("refresh")}
                    </button>
                  </div>
                  <div className="stats-grid compact-stats">
                    <div className="stat-card">
                      <span>{t("tableStatus")}</span>
                      <strong>{macOSAgentStatusLabel(macOSAgentOnboardingStatus?.status)}</strong>
                    </div>
                    <div className="stat-card">
                      <span>{lang === "ru" ? "Последний check-in" : "Last check-in"}</span>
                      <strong>{formatTimestamp(macOSAgentOnboardingStatus?.last_checkin_at)}</strong>
                    </div>
                    <div className="stat-card">
                      <span>{lang === "ru" ? "Активные tokens" : "Active tokens"}</span>
                      <strong>{macOSAgentOnboardingStatus?.active_token_count ?? 0}</strong>
                    </div>
                    <div className="stat-card">
                      <span>{lang === "ru" ? "Jobs в очереди" : "Queued jobs"}</span>
                      <strong>{macOSAgentOnboardingStatus?.pending_job_count ?? 0}</strong>
                    </div>
                  </div>
                  <div className="stats-grid compact-stats">
                    <div className="stat-card">
                      <span>{lang === "ru" ? "Health" : "Health"}</span>
                      <strong>{macOSUEMSummary?.agent_health?.status || "-"}</strong>
                    </div>
                    <div className="stat-card">
                      <span>{lang === "ru" ? "Версия Agent" : "Agent version"}</span>
                      <strong>{macOSUEMSummary?.agent_health?.agent_version || "-"}</strong>
                    </div>
                    <div className="stat-card">
                      <span>{lang === "ru" ? "macOS" : "macOS"}</span>
                      <strong>{macOSUEMSummary?.agent_health?.os_version || "-"}</strong>
                    </div>
                    <div className="stat-card">
                      <span>{lang === "ru" ? "Hostname" : "Hostname"}</span>
                      <strong>{macOSUEMSummary?.agent_health?.hostname || "-"}</strong>
                    </div>
                  </div>
                  <div className="details-grid compact-details">
                    <DetailRow label="SHA-256" value={macOSUEMSummary?.agent_health?.agent_sha256 || "-"} />
                    <DetailRow label="Config SHA-256" value={macOSUEMSummary?.agent_health?.config_sha256 || "-"} />
                    <DetailRow label="LaunchDaemon SHA-256" value={macOSUEMSummary?.agent_health?.launchd_sha256 || "-"} />
                    <DetailRow
                      label={lang === "ru" ? "Подпись Agent" : "Agent signature"}
                      value={macOSUEMSummary?.agent_health?.code_signature_status || "-"}
                    />
                    <DetailRow
                      label={lang === "ru" ? "Config integrity" : "Config integrity"}
                      value={macOSUEMSummary?.agent_health?.config_integrity_status || "-"}
                    />
                    <DetailRow
                      label={lang === "ru" ? "Health check-in" : "Health check-in"}
                      value={formatTimestamp(macOSUEMSummary?.agent_health?.last_checkin_at)}
                    />
                    <DetailRow
                      label={lang === "ru" ? "Последняя ошибка Agent" : "Last Agent error"}
                      value={macOSUEMSummary?.agent_health?.last_error || "-"}
                    />
                  </div>
                  {macOSUEMSummary?.agent_health?.log_tail && (
                    <details className="notice-panel">
                      <summary>{lang === "ru" ? "Последние строки лога Agent" : "Agent log tail"}</summary>
                      <pre className="code-preview">{macOSUEMSummary.agent_health.log_tail}</pre>
                    </details>
                  )}
                  <div className="action-row">
                    <button className="toolbar-action" type="button" onClick={startMacOSAgentOnboarding}>
                      {lang === "ru" ? "Установить Agent через MDM" : "Install Agent through MDM"}
                    </button>
                    <button
                      className="toolbar-action secondary"
                      type="button"
                      onClick={() => queueMacOSAgentPackageJob("update")}
                      disabled={!macOSAgentOnboardingStatus?.package_install_url}
                    >
                      {lang === "ru" ? "Обновить Agent" : "Update Agent"}
                    </button>
                    <button
                      className="toolbar-action secondary"
                      type="button"
                      onClick={() => queueMacOSAgentPackageJob("reinstall")}
                      disabled={!macOSAgentOnboardingStatus?.package_install_url}
                    >
                      {lang === "ru" ? "Переустановить Agent" : "Reinstall Agent"}
                    </button>
                    <button className="toolbar-action danger" type="button" onClick={queueMacOSAgentUninstall}>
                      {lang === "ru" ? "Удалить Agent" : "Uninstall Agent"}
                    </button>
                    {macOSAgentOnboardingStatus?.package_install_url && (
                      <a className="toolbar-action secondary" href={macOSAgentOnboardingStatus.package_install_url}>
                        {lang === "ru" ? "Скачать pkg" : "Download pkg"}
                      </a>
                    )}
                  </div>
                </div>
                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{lang === "ru" ? "Agent tokens" : "Agent tokens"}</h4>
                      <p>
                        {lang === "ru"
                          ? "Отдельный per-device token для Turuk macOS Agent. Пользовательский токен консоли не используется."
                          : "Separate per-device token for Turuk macOS Agent. Console user tokens are not used."}
                      </p>
                    </div>
                    <button className="toolbar-action secondary" type="button" onClick={() => loadMacOSUEM(routeDeviceUDID)}>
                      {t("refresh")}
                    </button>
                  </div>
                  <div className="macos-uem-job-grid">
                    <label>
                      {lang === "ru" ? "Название token" : "Token name"}
                      <input
                        className="text-input"
                        value={macOSAgentTokenName}
                        onChange={(event) => setMacOSAgentTokenName(event.target.value)}
                        placeholder={lang === "ru" ? "Например: Anatolii MacBook daemon" : "For example: Anatolii MacBook daemon"}
                      />
                    </label>
                    <div className="action-row">
                    <button className="toolbar-action" type="button" onClick={createMacOSAgentToken}>
                      {lang === "ru" ? "Создать token" : "Create token"}
                    </button>
                    <button className="toolbar-action secondary" type="button" onClick={() => createMacOSAgentJob("token_rotate")}>
                      {lang === "ru" ? "Сменить token через Agent" : "Rotate through Agent"}
                    </button>
                  </div>
                  </div>
                  {issuedMacOSAgentToken && (
                    <div className="notice-panel">
                      <strong>{lang === "ru" ? "Скопируйте token сейчас" : "Copy this token now"}</strong>
                      <p>
                        {lang === "ru"
                          ? "После закрытия страницы plaintext token больше не будет доступен."
                          : "After leaving this page, the plaintext token will no longer be available."}
                      </p>
                      <code className="monospace-value">{issuedMacOSAgentToken}</code>
                    </div>
                  )}
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Prefix</th>
                          <th>{lang === "ru" ? "Название" : "Name"}</th>
                          <th>{t("tableStatus")}</th>
                          <th>{lang === "ru" ? "Создал" : "Created by"}</th>
                          <th>{lang === "ru" ? "Создано" : "Created"}</th>
                          <th>{lang === "ru" ? "Последнее использование" : "Last used"}</th>
                          <th>{t("tableAction")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {macOSAgentTokens.map((item) => (
                          <tr key={item.id}>
                            <td className="monospace-value">{item.token_prefix || "-"}</td>
                            <td>{item.name || "-"}</td>
                            <td>{item.status || "-"}</td>
                            <td>{item.created_by_name || item.created_by || "-"}</td>
                            <td>{formatTimestamp(item.created_at)}</td>
                            <td>{formatTimestamp(item.last_used_at)}</td>
                            <td>
                              <button
                                className="small-action danger"
                                type="button"
                                disabled={item.status !== "active"}
                                onClick={() => revokeMacOSAgentToken(item.id)}
                              >
                                {lang === "ru" ? "Отозвать" : "Revoke"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {macOSAgentTokens.length === 0 && (
                      <p className="devices-empty">
                        {lang === "ru" ? "Agent tokens для этого Mac пока не созданы." : "No agent tokens have been created for this Mac yet."}
                      </p>
                    )}
                  </div>
                </div>

                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>FileVault escrow</h4>
                      <p>
                        {lang === "ru"
                          ? "Инвентарь escrow recovery key. В консоли хранится fingerprint и статус, не plaintext ключ."
                          : "Escrow recovery key inventory. The console stores fingerprint and status, not the plaintext key."}
                      </p>
                    </div>
                    <div className="inline-actions">
                      <button className="toolbar-action" type="button" onClick={() => createMacOSAgentJob("filevault_rotate")}>
                        {lang === "ru" ? "Сменить ключ через Agent" : "Rotate via Agent"}
                      </button>
                      <button className="toolbar-action secondary" type="button" onClick={() => loadMacOSUEM(routeDeviceUDID)}>
                        {t("refresh")}
                      </button>
                    </div>
                  </div>
                  <div className="macos-uem-job-grid">
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={macOSFileVaultForm.enabled}
                        onChange={(event) => setMacOSFileVaultForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                      />
                      <span>{lang === "ru" ? "Включить FileVault через profile" : "Enable FileVault through profile"}</span>
                    </label>
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={macOSFileVaultForm.escrowRequired}
                        onChange={(event) => setMacOSFileVaultForm((prev) => ({ ...prev, escrowRequired: event.target.checked }))}
                      />
                      <span>{lang === "ru" ? "Требовать escrow recovery key" : "Require recovery key escrow"}</span>
                    </label>
                    <label>
                      {lang === "ru" ? "Key stale после дней" : "Key stale after days"}
                      <input
                        className="text-input"
                        type="number"
                        min="1"
                        value={macOSFileVaultForm.staleAfterDays}
                        onChange={(event) => setMacOSFileVaultForm((prev) => ({ ...prev, staleAfterDays: event.target.value }))}
                      />
                    </label>
                    <div className="action-row">
                      <button className="toolbar-action" type="button" onClick={saveMacOSFileVaultPolicy}>
                        {lang === "ru" ? "Установить FileVault profile" : "Install FileVault profile"}
                      </button>
                    </div>
                  </div>
                  {macOSUEMSummary?.filevault_policy && (
                    <div className="details-grid compact-details">
                      <DetailRow
                        label={lang === "ru" ? "Policy" : "Policy"}
                        value={macOSUEMSummary.filevault_policy.enabled ? (lang === "ru" ? "Включена" : "Enabled") : (lang === "ru" ? "Выключена" : "Disabled")}
                      />
                      <DetailRow
                        label="Identifier"
                        value={macOSUEMSummary.filevault_policy.profile_identifier || "-"}
                      />
                      <DetailRow
                        label={lang === "ru" ? "Последняя команда profile" : "Last profile command"}
                        value={macOSUEMSummary.filevault_policy.last_profile_command_uuid || "-"}
                      />
                    </div>
                  )}
                  {macOSUEMLoading && <p className="muted">{t("loading")}</p>}
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Fingerprint</th>
                          <th>{t("tableStatus")}</th>
                          <th>{lang === "ru" ? "Источник" : "Source"}</th>
                          <th>{lang === "ru" ? "Escrow" : "Escrow"}</th>
                          <th>{lang === "ru" ? "Ротация" : "Rotated"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(macOSUEMSummary?.filevault_keys || []).map((item) => (
                          <tr key={item.id}>
                            <td className="monospace-value">{item.key_fingerprint || "-"}</td>
                            <td>{item.key_status || "-"}</td>
                            <td>{item.source || "-"}</td>
                            <td>{formatTimestamp(item.escrowed_at)}</td>
                            <td>{formatTimestamp(item.rotated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(macOSUEMSummary?.filevault_keys || []).length === 0 && (
                      <p className="devices-empty">
                        {lang === "ru" ? "Escrow key пока не получен от macOS Agent." : "No escrow key has been reported by macOS Agent yet."}
                      </p>
                    )}
                  </div>
                </div>

                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{lang === "ru" ? "Библиотека scripts/packages" : "Scripts/packages library"}</h4>
                      <p>
                        {lang === "ru"
                          ? "Сохраненные рабочие задачи можно назначать на конкретный Mac или на группу Mac с расписанием и retry policy."
                          : "Saved operational items can be assigned to a Mac or Mac group with schedule and retry policy."}
                      </p>
                    </div>
                    <button className="toolbar-action secondary" type="button" onClick={() => loadMacOSUEM(routeDeviceUDID)}>
                      {t("refresh")}
                    </button>
                  </div>
                  <div className="macos-uem-job-grid">
                    <label>
                      {lang === "ru" ? "Тип" : "Type"}
                      <select
                        className="text-input"
                        value={macOSLibraryForm.itemType}
                        onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, itemType: event.target.value }))}
                      >
                        <option value="script">{lang === "ru" ? "Script" : "Script"}</option>
                        <option value="package">{lang === "ru" ? "Package" : "Package"}</option>
                      </select>
                    </label>
                    <label>
                      {lang === "ru" ? "Название" : "Name"}
                      <input
                        className="text-input"
                        value={macOSLibraryForm.name}
                        onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, name: event.target.value }))}
                      />
                    </label>
                    {macOSLibraryForm.itemType === "script" ? (
                      <>
                        <label>
                          Shell
                          <input
                            className="text-input"
                            value={macOSLibraryForm.shell}
                            onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, shell: event.target.value }))}
                          />
                        </label>
                        <label className="macos-uem-wide">
                          Script
                          <textarea
                            className="text-input code-input"
                            rows={7}
                            value={macOSLibraryForm.script}
                            onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, script: event.target.value }))}
                            placeholder="echo hello"
                          />
                        </label>
                      </>
                    ) : (
                      <>
                        <label className="macos-uem-wide">
                          Package URL
                          <input
                            className="text-input"
                            value={macOSLibraryForm.packageURL}
                            onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, packageURL: event.target.value }))}
                            placeholder="https://downloads.example.com/app.pkg"
                          />
                        </label>
                        <label>
                          SHA-256
                          <input
                            className="text-input"
                            value={macOSLibraryForm.sha256}
                            onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, sha256: event.target.value }))}
                          />
                        </label>
                        <label>
                          {lang === "ru" ? "Аргументы установки" : "Install args"}
                          <input
                            className="text-input"
                            value={macOSLibraryForm.installArgs}
                            onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, installArgs: event.target.value }))}
                          />
                        </label>
                      </>
                    )}
                    <label>
                      Timeout, sec
                      <input
                        className="text-input"
                        type="number"
                        min="30"
                        value={macOSLibraryForm.timeoutSeconds}
                        onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, timeoutSeconds: event.target.value }))}
                      />
                    </label>
                    <label>
                      Retry max
                      <input
                        className="text-input"
                        type="number"
                        min="1"
                        value={macOSLibraryForm.retryMaxAttempts}
                        onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, retryMaxAttempts: event.target.value }))}
                      />
                    </label>
                    <label>
                      {lang === "ru" ? "Куда назначить" : "Assign to"}
                      <select
                        className="text-input"
                        value={macOSLibraryForm.target}
                        onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, target: event.target.value }))}
                      >
                        <option value="device">{lang === "ru" ? "Текущий Mac" : "Current Mac"}</option>
                        <option value="group">{lang === "ru" ? "Группа Mac" : "Mac group"}</option>
                      </select>
                    </label>
                    {macOSLibraryForm.target === "group" && (
                      <label>
                        {lang === "ru" ? "Группа" : "Group"}
                        <select
                          className="text-input"
                          value={macOSLibraryForm.groupID}
                          onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, groupID: event.target.value }))}
                        >
                          <option value="">{lang === "ru" ? "Выберите группу" : "Select group"}</option>
                          {(groups || []).map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.group_name || group.name || group.id}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label>
                      {lang === "ru" ? "Запланировать" : "Schedule"}
                      <input
                        className="text-input"
                        type="datetime-local"
                        value={macOSLibraryForm.scheduledAt}
                        onChange={(event) => setMacOSLibraryForm((prev) => ({ ...prev, scheduledAt: event.target.value }))}
                      />
                    </label>
                  </div>
                  <div className="action-row">
                    <button className="toolbar-action" type="button" onClick={saveMacOSLibraryItem}>
                      {lang === "ru" ? "Сохранить в библиотеку" : "Save to library"}
                    </button>
                  </div>
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{lang === "ru" ? "Тип" : "Type"}</th>
                          <th>{lang === "ru" ? "Название" : "Name"}</th>
                          <th>{lang === "ru" ? "Retry" : "Retry"}</th>
                          <th>{lang === "ru" ? "Обновлено" : "Updated"}</th>
                          <th>{t("tableAction")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(macOSUEMSummary?.script_library || []), ...(macOSUEMSummary?.package_library || [])].map((item) => (
                          <tr key={`${item.item_type}-${item.id}`}>
                            <td>{item.item_type}</td>
                            <td>{item.name}</td>
                            <td>{item.retry_max_attempts ?? 3}</td>
                            <td>{formatTimestamp(item.updated_at)}</td>
                            <td>
                              <button className="small-action" type="button" onClick={() => assignMacOSLibraryItem(item)}>
                                {lang === "ru" ? "Назначить" : "Assign"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{lang === "ru" ? "Назначение" : "Assignment"}</th>
                          <th>{lang === "ru" ? "Цель" : "Target"}</th>
                          <th>{lang === "ru" ? "Расписание" : "Schedule"}</th>
                          <th>{lang === "ru" ? "Retry" : "Retry"}</th>
                          <th>{lang === "ru" ? "Создал" : "Created by"}</th>
                          <th>{lang === "ru" ? "Создано" : "Created"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(macOSUEMSummary?.assignments || []).map((item) => (
                          <tr key={item.id}>
                            <td>{item.item_name || item.item_id}</td>
                            <td>{item.group_name || item.device_udid || "-"}</td>
                            <td>{formatTimestamp(item.scheduled_at)}</td>
                            <td>{item.retry_max_attempts ?? 3}</td>
                            <td>{item.created_by_name || item.created_by || "-"}</td>
                            <td>{formatTimestamp(item.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{lang === "ru" ? "Turuk macOS Agent jobs" : "Turuk macOS Agent jobs"}</h4>
                      <p>
                        {lang === "ru"
                          ? "Очередь рабочих задач: scripts/packages, локальные администраторы, FileVault и сбор compliance."
                          : "Operational queue for scripts/packages, local admins, FileVault, and compliance collection."}
                      </p>
                    </div>
                    <button className="toolbar-action" type="button" onClick={() => createMacOSAgentJob("compliance_collect")}>
                      {lang === "ru" ? "Собрать compliance" : "Collect compliance"}
                    </button>
                  </div>
                  <div className="macos-uem-job-grid">
                    <label>
                      {lang === "ru" ? "Тип задачи" : "Job type"}
                      <select
                        className="text-input"
                        value={macOSJobForm.jobType}
                        onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, jobType: event.target.value }))}
                      >
                        {["script", "package", "agent_uninstall", "local_admin_create", "local_admin_rotate", "local_admin_revoke"].map((key) => (
                          <option key={key} value={key}>
                            {macOSAgentJobTypeLabel(key, lang)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {lang === "ru" ? "Название" : "Title"}
                      <input
                        className="text-input"
                        value={macOSJobForm.title}
                        onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, title: event.target.value }))}
                        placeholder={macOSAgentJobTypeLabel(macOSJobForm.jobType, lang)}
                      />
                    </label>
                    {macOSJobForm.jobType === "script" && (
                      <label className="macos-uem-wide">
                        Script
                        <textarea
                          className="text-input code-input"
                          rows={6}
                          value={macOSJobForm.script}
                          onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, script: event.target.value }))}
                          placeholder="echo hello"
                        />
                      </label>
                    )}
                    {macOSJobForm.jobType === "package" && (
                      <>
                        <label className="macos-uem-wide">
                          Package URL
                          <input
                            className="text-input"
                            value={macOSJobForm.packageURL}
                            onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, packageURL: event.target.value }))}
                            placeholder="https://downloads.example.com/app.pkg"
                          />
                        </label>
                        <label className="macos-uem-wide">
                          SHA-256
                          <input
                            className="text-input"
                            value={macOSJobForm.packageSHA256}
                            onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, packageSHA256: event.target.value }))}
                          />
                        </label>
                      </>
                    )}
                    {macOSJobForm.jobType.startsWith("local_admin_") && (
                      <>
                        <label>
                          {lang === "ru" ? "Логин" : "Username"}
                          <input
                            className="text-input"
                            value={macOSJobForm.adminUsername}
                            onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, adminUsername: event.target.value }))}
                            placeholder="turuk_admin"
                          />
                        </label>
                        {macOSJobForm.jobType === "local_admin_create" && (
                          <label>
                            {lang === "ru" ? "Полное имя" : "Full name"}
                            <input
                              className="text-input"
                              value={macOSJobForm.adminFullName}
                              onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, adminFullName: event.target.value }))}
                              placeholder="Turuk Admin"
                            />
                          </label>
                        )}
                        {macOSJobForm.jobType === "local_admin_create" && (
                          <label>
                            {lang === "ru" ? "Временный пароль" : "Temporary password"}
                            <input
                              className="text-input"
                              type="password"
                              value={macOSJobForm.adminPassword}
                              onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, adminPassword: event.target.value }))}
                            />
                          </label>
                        )}
                        {macOSJobForm.jobType === "local_admin_rotate" && (
                          <label>
                            {lang === "ru" ? "Новый пароль" : "New password"}
                            <input
                              className="text-input"
                              type="password"
                              value={macOSJobForm.rotatePassword}
                              onChange={(event) => setMacOSJobForm((prev) => ({ ...prev, rotatePassword: event.target.value }))}
                            />
                          </label>
                        )}
                      </>
                    )}
                  </div>
                  <div className="action-row">
                    <button className="toolbar-action" type="button" onClick={() => createMacOSAgentJob()}>
                      {lang === "ru" ? "Создать job" : "Create job"}
                    </button>
                  </div>
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{lang === "ru" ? "Тип" : "Type"}</th>
                          <th>{t("tableStatus")}</th>
                          <th>{lang === "ru" ? "Название" : "Title"}</th>
                          <th>{lang === "ru" ? "Создал" : "Requested by"}</th>
                          <th>{lang === "ru" ? "Попытки" : "Attempts"}</th>
                          <th>{lang === "ru" ? "Ошибка" : "Error"}</th>
                          <th>{lang === "ru" ? "Создано" : "Requested"}</th>
                          <th>{lang === "ru" ? "Завершено" : "Completed"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(macOSUEMSummary?.jobs || []).map((item) => (
                          <tr key={item.id}>
                            <td>{macOSAgentJobTypeLabel(item.job_type, lang)}</td>
                            <td>{item.status || "-"}</td>
                            <td>{item.title || "-"}</td>
                            <td>{item.requested_by_name || item.requested_by || "-"}</td>
                            <td>{`${item.attempt_count ?? 0} / ${item.max_attempts ?? 3}`}</td>
                            <td>{item.last_error || item.result?.error || "-"}</td>
                            <td>{formatTimestamp(item.requested_at)}</td>
                            <td>{formatTimestamp(item.completed_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>PPPC presets</h4>
                      <p>
                        {lang === "ru"
                          ? "Готовые пресеты для payload Privacy Preferences Policy Control."
                          : "Ready presets for Privacy Preferences Policy Control payloads."}
                      </p>
                    </div>
                  </div>
                  <div className="macos-uem-preset-grid">
                    {(macOSUEMSummary?.pppc_presets || []).map((item) => (
                      <div className="macos-uem-preset" key={item.key}>
                        <strong>{item.name}</strong>
                        <span>{item.description}</span>
                        <span className="monospace-value">{item.identifier}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{lang === "ru" ? "macOS compliance" : "macOS compliance"}</h4>
                      <p>
                        {lang === "ru"
                          ? "FileVault escrow, Agent activity и профильная готовность для firewall/PPPC."
                          : "FileVault escrow, Agent activity, and profile readiness for firewall/PPPC."}
                      </p>
                    </div>
                    <button className="toolbar-action" type="button" onClick={evaluateMacOSCompliance}>
                      {lang === "ru" ? "Проверить" : "Evaluate"}
                    </button>
                  </div>
                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{lang === "ru" ? "Правило" : "Rule"}</th>
                          <th>{t("tableStatus")}</th>
                          <th>{lang === "ru" ? "Важность" : "Severity"}</th>
                          <th>{lang === "ru" ? "Детали" : "Detail"}</th>
                          <th>{lang === "ru" ? "Обновлено" : "Observed"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(macOSUEMSummary?.compliance || []).map((item) => (
                          <tr key={item.id}>
                            <td>{item.rule_key}</td>
                            <td>{item.status}</td>
                            <td>{item.severity}</td>
                            <td>{item.detail}</td>
                            <td>{formatTimestamp(item.observed_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(macOSUEMSummary?.compliance || []).length === 0 && (
                      <p className="devices-empty">
                        {lang === "ru" ? "Compliance ещё не проверялся." : "Compliance has not been evaluated yet."}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {deviceDetailsTab === "commands" && (
              <>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>UUID</th>
                        <th>{t("commandsType")}</th>
                        <th>{t("tableState")}</th>
                        <th>{t("deviceDetailsCommandAttempts")}</th>
                        <th>{t("profilesColUpdatedAt")}</th>
                        <th>{t("tableAction")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDeviceCommands.map((item) => {
                        const commandDetail = String(item.result_detail || "").trim();
                        const commandResult = String(item.result_status || "").trim();
                        const rawCommandError = String(item.last_error || "").trim();
                        const commandSucceeded =
                          String(item.state || "").trim().toLowerCase() === "done" &&
                          commandResult.trim().toLowerCase() === "acknowledged";
                        const commandError =
                          commandSucceeded &&
                          commandDetail &&
                          (rawCommandError === commandDetail ||
                            (String(item.request_type || "") === "ActivationLockBypassCode" &&
                              rawCommandError.startsWith("Activation Lock bypass code:")))
                            ? ""
                            : rawCommandError;
                        const canRetry = Boolean(item.can_retry);
                        const canCancel = Boolean(item.can_cancel);
                        return (
                          <tr key={item.command_uuid}>
                            <td>{item.command_uuid}</td>
                            <td>{item.request_type || "-"}</td>
                            <td>
                              <div>{formatCommandState(item.state, item.result_status, t)}</div>
                              {commandResult && !commandError && !commandDetail && (
                                <div className="muted">
                                  {lang === "ru" ? "Ответ устройства" : "Device response"}: {commandResult}
                                </div>
                              )}
                              {commandDetail && (
                                <div className="muted">
                                  {lang === "ru" ? "Ответ устройства" : "Device response"}: {commandDetail}
                                </div>
                              )}
                              {commandError && (
                                <div className="muted">
                                  {t("deviceDetailsCommandLastError")}: {commandError}
                                </div>
                              )}
                            </td>
                            <td>
                              {Number(item.attempt_count || 0)} / {Number(item.max_attempts || 0)}
                            </td>
                            <td>
                              {formatTimestamp(
                                item.updated_at ||
                                  item.dead_letter_at ||
                                  item.canceled_at ||
                                  item.acknowledged_at ||
                                  item.sent_at ||
                                  item.queued_at
                              )}
                            </td>
                            <td>
                              <div className="inline-actions">
                                {canRetry && (
                                  <button
                                    className="small-action"
                                    type="button"
                                    onClick={() => retryCommand(item.command_uuid)}
                                  >
                                    {t("deviceDetailsCommandRetry")}
                                  </button>
                                )}
                                {canCancel && (
                                  <button
                                    className="small-action danger"
                                    type="button"
                                    onClick={() => cancelQueuedCommand(item.command_uuid)}
                                  >
                                    {t("deviceDetailsCommandCancel")}
                                  </button>
                                )}
                                {!canRetry && !canCancel && "-"}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {selectedDeviceCommands.length === 0 && <p className="devices-empty">{t("noCommands")}</p>}
                </div>
              </>
            )}

            {renderAllowedSIMModal()}

            {showDeviceActivationLockModal && (
              <div className="modal-backdrop" onClick={() => setShowDeviceActivationLockModal(false)}>
                <div className="modal-card activation-lock-modal" onClick={(event) => event.stopPropagation()}>
                  <div className="modal-heading">
                    <div>
                      <h3>
                        {t("deviceDetailsActivationLockTitle")} - {device?.device_name || device?.udid || routeDeviceUDID}
                      </h3>
                      <p className="muted">{t("deviceDetailsActivationLockHint")}</p>
                    </div>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => setShowDeviceActivationLockModal(false)}
                    >
                      ×
                    </button>
                  </div>
                  {renderActivationLockPanel()}
                </div>
              </div>
            )}

            {deviceDetailsTab === "remoteAccess" && canViewDeviceRemoteAccess && (
              <div className="device-remote-access-layout">
                <div className="panel compact-panel">
                  <div className="panel-heading">
                    <div>
                      <h4>{t("deviceDetailsTabRemoteAccess")}</h4>
                      <p>
                        {lang === "ru"
                          ? "Запрос удаленного просмотра экрана через Turuk Companion. Пользователь должен подтвердить трансляцию на устройстве."
                          : "Remote screen view request through Turuk Companion. The user must approve screen broadcast on the device."}
                      </p>
                    </div>
                    <button
                      className="secondary"
                      type="button"
                      onClick={() => {
                        reloadSupportSessions();
                        reloadRemoteScreenSessions();
                      }}
                    >
                      {t("refresh")}
                    </button>
                  </div>

                  {canEditDeviceRemoteAccess && (
                    <>
                      <div className="modal-grid">
                        <label>{lang === "ru" ? "Устройство" : "Device"}</label>
                        <span>{device?.device_name || device?.serial_number || routeDeviceUDID}</span>
                        <label>{lang === "ru" ? "Аккаунт" : "Account"}</label>
                        <span>{device?.account_name || device?.account_email || device?.account_id || "-"}</span>
                        <label>{lang === "ru" ? "Причина" : "Reason"}</label>
                        <textarea
                          className="text-input"
                          rows={3}
                          value={remoteScreenForm.reason}
                          onChange={(event) => setRemoteScreenForm((prev) => ({ ...prev, reason: event.target.value }))}
                        />
                        <label>TTL, min</label>
                        <input
                          className="text-input"
                          type="number"
                          min="15"
                          max="1440"
                          value={remoteScreenForm.ttlMinutes}
                          onChange={(event) => setRemoteScreenForm((prev) => ({ ...prev, ttlMinutes: event.target.value }))}
                        />
                      </div>
                      <div className="action-row">
                        <button
                          type="button"
                          onClick={() => createDeviceRemoteScreenSession(device)}
                          disabled={!remoteScreenForm.reason.trim()}
                        >
                          {lang === "ru" ? "Запросить экран" : "Request screen"}
                        </button>
                      </div>
                    </>
                  )}

                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("tableStatus")}</th>
                          <th>{lang === "ru" ? "Причина" : "Reason"}</th>
                          <th>{lang === "ru" ? "Запросил" : "Requested by"}</th>
                          <th>FPS</th>
                          <th>{lang === "ru" ? "Кадр" : "Frame"}</th>
                          <th>{lang === "ru" ? "Последний кадр" : "Last frame"}</th>
                          <th>{lang === "ru" ? "Viewer" : "Viewer"}</th>
                          <th>{lang === "ru" ? "Publisher" : "Publisher"}</th>
                          <th>{lang === "ru" ? "Истекает" : "Expires"}</th>
                          <th>{t("tableAction")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deviceRemoteScreenSessions.map((item) => {
                          const status = String(item.status || "").toLowerCase();
                          const canRevokeItem = canEditDeviceRemoteAccess && (status === "pending" || status === "active");
                          const canEndItem = canEditDeviceRemoteAccess && status === "active";
                          const frameURL = remoteScreenFrameURLs[item.id];
                          const metrics = remoteScreenMetrics[item.id] || {};
                          return (
                            <Fragment key={item.id}>
                              <tr>
                                <td>
                                  <span className={status === "active" ? "status-pill ok" : status === "revoked" || status === "rejected" ? "status-pill danger" : "status-pill muted"}>
                                    {remoteScreenStatusLabel(item, metrics, remoteScreenNow, lang)}
                                  </span>
                                </td>
                                <td>{item.reason || "-"}</td>
                                <td>{item.requested_by_name || item.requested_by_id || "-"}</td>
                                <td>{Number(metrics.fps || 0) > 0 ? Number(metrics.fps || 0).toFixed(1) : "-"}</td>
                                <td>{formatRemoteScreenBytes(metrics.last_frame_bytes)}</td>
                                <td>{remoteScreenLastFrameLabel(metrics, remoteScreenNow, lang)}</td>
                                <td>{metrics.viewer_connected ? (lang === "ru" ? "подключен" : "connected") : metrics.viewer_disconnected ? (lang === "ru" ? "отключен" : "disconnected") : "-"}</td>
                                <td>{metrics.publisher_connected ? (lang === "ru" ? "подключен" : "connected") : "-"}</td>
                                <td>{formatTimestamp(item.expires_at)}</td>
                                <td>
                                  <div className="inline-actions">
                                    {canEndItem && (
                                      <button className="small-action" onClick={() => endRemoteScreenSession(item.id)}>
                                        {lang === "ru" ? "Завершить" : "End"}
                                      </button>
                                    )}
                                    {canRevokeItem && (
                                      <button className="small-action danger" onClick={() => revokeRemoteScreenSession(item.id)}>
                                        {lang === "ru" ? "Отозвать" : "Revoke"}
                                      </button>
                                    )}
                                    {!canEndItem && !canRevokeItem && "-"}
                                  </div>
                                </td>
                              </tr>
                              {status === "active" && (
                                <tr className="remote-screen-frame-row">
                                  <td colSpan={10}>
                                    <div className="remote-screen-frame-panel">
                                      {frameURL ? (
                                        <img
                                          className="remote-screen-frame"
                                          src={frameURL}
                                          alt={lang === "ru" ? "Последний кадр экрана устройства" : "Latest device screen frame"}
                                        />
                                      ) : (
                                        <div className="remote-screen-frame-placeholder">
                                          {lang === "ru"
                                            ? "Ожидание первого кадра. На устройстве нужно нажать Начать трансляцию экрана."
                                            : "Waiting for the first frame. The device user must start screen broadcast."}
                                        </div>
                                      )}
                                    </div>
                                    {canEditDeviceRemoteAccess && (
                                      <div className="remote-assist-actions-panel">
                                        <div className="remote-assist-actions-header">
                                          <strong>{lang === "ru" ? "Открыть ссылку/приложение" : "Open link/app"}</strong>
                                          <span>
                                            {lang === "ru"
                                              ? "Пользователь должен подтвердить открытие в Companion."
                                              : "The user must approve opening in Companion."}
                                          </span>
                                        </div>
                                        <div className="remote-assist-actions-grid">
                                          <select
                                            className="text-input"
                                            value=""
                                            onChange={(event) => selectRemoteAssistService(item.id, event.target.value)}
                                          >
                                            <option value="">
                                              {lang === "ru" ? "Выбрать корпоративный сервис" : "Select corporate service"}
                                            </option>
                                            {agentServices
                                              .filter((service) => service.is_enabled !== false && String(service.url || "").trim())
                                              .map((service) => (
                                                <option key={service.id || service.url} value={service.id}>
                                                  {service.title || service.url}
                                                </option>
                                              ))}
                                          </select>
                                          <input
                                            className="text-input"
                                            placeholder={lang === "ru" ? "Название" : "Title"}
                                            value={remoteAssistDrafts[item.id]?.title || ""}
                                            onChange={(event) => updateRemoteAssistDraft(item.id, { title: event.target.value })}
                                          />
                                          <input
                                            className="text-input"
                                            placeholder="https://example.com или app://deep-link"
                                            value={remoteAssistDrafts[item.id]?.targetURL || ""}
                                            onChange={(event) => updateRemoteAssistDraft(item.id, { targetURL: event.target.value })}
                                          />
                                          <button
                                            type="button"
                                            className="small-action"
                                            disabled={!String(remoteAssistDrafts[item.id]?.targetURL || "").trim()}
                                            onClick={() => createRemoteAssistAction(item.id)}
                                          >
                                            {lang === "ru" ? "Отправить" : "Send"}
                                          </button>
                                        </div>
                                        {(remoteAssistActions[item.id] || []).length > 0 && (
                                          <div className="remote-assist-actions-history">
                                            {(remoteAssistActions[item.id] || []).slice(0, 5).map((assist) => (
                                              <div key={assist.id} className="remote-assist-action-item">
                                                <span className="status-pill muted">{assist.status || "-"}</span>
                                                <span>{assist.title || assist.target_url}</span>
                                                <span className="muted-text">{assist.target_url}</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    {deviceRemoteScreenSessions.length === 0 && (
                      <p className="devices-empty">
                        {lang === "ru" ? "Заявок на удаленный доступ для этого устройства пока нет." : "No remote access requests for this device yet."}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="page">
            <div className="panel devices-table-panel">
              <div className="devices-toolbar-line">
                <div className="devices-toolbar-actions">
                  <button className="toolbar-action secondary" onClick={() => downloadReport("devices", "pdf")}>
                    {t("reportsPDF")}
                  </button>
                  <button className="toolbar-action secondary" onClick={() => downloadReport("devices", "csv")}>
                    {t("reportsCSV")}
                  </button>
                  <button className="toolbar-action" onClick={loadAllData}>
                    {t("refresh")}
                  </button>
                  {canBulkRequestFromListPage && (
                    <button
                      className="toolbar-action"
                      onClick={() => requestDevicesRefreshBulk(shownDevices)}
                      disabled={shownDevices.length === 0}
                    >
                      {t("deviceDetailsRequestRefresh")}
                    </button>
                  )}
                  {selectedDeviceUDIDs.length > 0 && (
                    <div className="device-details-command-menu-wrap" ref={bulkDeviceCommandsMenuRef}>
                      <button
                        className="toolbar-action"
                        type="button"
                        onClick={() => setShowBulkDeviceCommandsMenu((prev) => !prev)}
                      >
                        {t("devicesBulkCommands")}
                      </button>
                      {showBulkDeviceCommandsMenu && (
                        <div className="device-details-command-menu">
                          <button
                            type="button"
                            onClick={() => openBulkScheduleOSUpdateModal("DownloadOnly")}
                          >
                            {t("devicesBulkOSUpdateDownload")}
                          </button>
                          <button
                            type="button"
                            onClick={() => openBulkScheduleOSUpdateModal("InstallASAP")}
                          >
                            {t("devicesBulkOSUpdateDownloadInstall")}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {selectedDeviceUDIDs.length > 0 && (
                    <button className="toolbar-action danger" onClick={deleteSelectedDevices}>
                      {t("devicesDeleteSelected")}
                    </button>
                  )}
                </div>
                <span className="muted">
                  {t("devicesCount")}: {shownDevices.length}
                </span>
              </div>

              <div className="devices-search-line">
                <input
                  className="text-input"
                  placeholder={t("searchDevices")}
                  value={devicesSearch}
                  onChange={(event) => setDevicesSearch(event.target.value)}
                />
                <div className="columns-menu-wrap">
                  <button className="toolbar-action" onClick={() => setShowDevicesColumnsMenu((prev) => !prev)}>
                    {t("devicesColumns")}
                  </button>
                  {showDevicesColumnsMenu && (
                    <div className="columns-menu">
                      {deviceColumns.map((column) => (
                        <label key={column.key} className="column-option">
                          <input
                            type="checkbox"
                            checked={visibleDeviceColumns.includes(column.key)}
                            onChange={(event) =>
                              setVisibleDeviceColumns((prev) => {
                                if (event.target.checked) return Array.from(new Set([...prev, column.key]));
                                return prev.filter((item) => item !== column.key);
                              })
                            }
                          />
                          {column.label}
                        </label>
                      ))}
                      <button
                        className="reset-columns-btn"
                        onClick={() => setVisibleDeviceColumns(DEVICE_DEFAULT_COLUMN_KEYS)}
                      >
                        {t("devicesResetColumns")}
                      </button>
                    </div>
                  )}
                </div>
                <button className="toolbar-action secondary" onClick={() => setShowDevicesFilters((prev) => !prev)}>
                  {t("addFilters")}
                </button>
              </div>

              {showDevicesFilters && (
                <div className="filters-row">
                  <select
                    className="text-input"
                    value={deviceFilterPlatform}
                    onChange={(event) => setDeviceFilterPlatform(event.target.value)}
                    disabled={pagePlatformFilter !== "all"}
                  >
                    <option value="all">{t("all")}</option>
                    <option value="windows">{t("platformWindows")}</option>
                    <option value="ios">{t("platformIOS")}</option>
                    <option value="macos">{t("platformMacOS")}</option>
                    <option value="android">{t("platformAndroid")}</option>
                  </select>
                  <select
                    className="text-input"
                    value={deviceFilterManaged}
                    onChange={(event) => setDeviceFilterManaged(event.target.value)}
                  >
                    <option value="all">{t("all")}</option>
                    <option value="yes">{t("yes")}</option>
                    <option value="no">{t("no")}</option>
                  </select>
                  <select
                    className="text-input"
                    value={deviceFilterEncrypted}
                    onChange={(event) => setDeviceFilterEncrypted(event.target.value)}
                  >
                    <option value="all">{t("all")}</option>
                    <option value="yes">{t("yes")}</option>
                    <option value="no">{t("no")}</option>
                  </select>
                  <select
                    className="text-input"
                    value={deviceFilterCompliance}
                    onChange={(event) => setDeviceFilterCompliance(event.target.value)}
                  >
                    <option value="all">{t("all")}</option>
                    <option value="yes">{t("yes")}</option>
                    <option value="no">{t("no")}</option>
                  </select>
                  <select
                    className="text-input"
                    value={deviceFilterLastSeen}
                    onChange={(event) => setDeviceFilterLastSeen(event.target.value)}
                  >
                    <option value="all">
                      {t("devicesFilterLastSeen")}: {t("all")}
                    </option>
                    <option value="recent7">{t("dashboardLastSeenRecent")}</option>
                    <option value="0to1">{t("dashboardLastSeen0to1")}</option>
                    <option value="2to7">{t("dashboardLastSeen2to7")}</option>
                    <option value="8to15">{t("dashboardLastSeen8to15")}</option>
                    <option value="16to30">{t("dashboardLastSeen16to30")}</option>
                    <option value="31plus">{t("dashboardLastSeen31Plus")}</option>
                  </select>
                </div>
              )}

              <div className="devices-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={() => toggleSelectAllVisibleDevices(visibleUDIDs, allVisibleSelected)}
                        />
                      </th>
                      {visibleColumns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shownDevices.map((item) => (
                      <tr
                        key={item.udid}
                        className={selectedDeviceUDIDs.includes(item.udid) ? "row-selected" : ""}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedDeviceUDIDs.includes(item.udid)}
                            onChange={() => toggleDeviceSelection(item.udid)}
                          />
                        </td>
                        {visibleColumns.map((column) => (
                          <td key={`${item.udid}-${column.key}`}>
                            {column.key === "device_name" ? (
                              <a className="table-link-anchor" href={deviceDetailsLinkHash(item.udid, activePage)}>
                                {column.value(item)}
                              </a>
                            ) : (
                              column.value(item)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {shownDevices.length === 0 && (
                  <div className="devices-empty">
                    <h3>{t("noDevices")}</h3>
                    <p>{t("noDevicesHint")}</p>
                  </div>
                )}
              </div>
            </div>
      </div>
    );
  }

  function renderProfiles(options = {}) {
    const forcedPlatform = normalizeForcedProfilePlatform(options.forcedPlatform);
    const isPoliciesPage = activePage === "policiesAll" || activePage === "policiesCompliance";
    const isProfilesAllPage = !isPoliciesPage;

    const profileColumnDefs = [
      { key: "name", label: t("profilesColName"), value: (item) => item.name || "-" },
      { key: "identifier", label: t("profilesColIdentifier"), value: (item) => item.identifier || "-" },
      {
        key: "groups",
        label: t("profilesColGroups"),
        value: (item) =>
          Array.isArray(item.group_names) && item.group_names.length > 0 ? item.group_names.join(", ") : "-"
      },
      {
        key: "devices",
        label: t("profilesColDevices"),
        value: (item) => `${Number(item.installed_device_count || 0)}/${Number(item.assigned_device_count || 0)}`,
        render: (item) => (
          <button className="table-link-button" onClick={() => openProfileDevicesModal(item)}>
            {Number(item.installed_device_count || 0)}/{Number(item.assigned_device_count || 0)}
          </button>
        )
      },
      { key: "status", label: t("profilesColStatus"), value: (item) => (item.is_active === false ? t("revoked") : t("active")) },
      { key: "updated_at", label: t("profilesColUpdatedAt"), value: (item) => formatTimestamp(item.updated_at) }
    ];
    const policyColumnDefs = [
      { key: "name", label: t("policiesColName"), value: (item) => item.name || "-" },
      { key: "platform", label: t("policiesColPlatform"), value: (item) => profilePlatformLabel(normalizeProfilePlatform(item.platform || item.target_platform), t) },
      { key: "policy_type", label: t("policiesColType"), value: (item) => item.policy_type || "-" },
      { key: "updated_at", label: t("policiesColUpdatedAt"), value: (item) => formatTimestamp(item.updated_at) },
      { key: "scope_tags", label: t("policiesColScopeTags"), value: (item) => item.scope_tags || "-" },
      { key: "compliance", label: t("policiesColCompliance"), value: (item) => item.compliance || "-" }
    ];

    const sourceProfiles = isPoliciesPage
      ? profiles.filter((item) => item.is_policy || item.policy_type)
      : profiles;

    const searchedProfiles = sourceProfiles.filter((item) => {
      const q = (isPoliciesPage ? policiesSearch : profilesSearch).trim().toLowerCase();
      if (!q) return true;
      const textValue = `${item.name || ""} ${item.identifier || ""} ${item.policy_type || ""}`.toLowerCase();
      return textValue.includes(q);
    });

    const platformFilteredProfiles = searchedProfiles.filter((item) => {
      const platform = normalizeProfilePlatform(
        item.platform || item.target_platform || item.payload?.TargetPlatform
      );
      if (forcedPlatform && forcedPlatform !== "all") return platform === forcedPlatform;
      if (isPoliciesPage && policiesPlatformFilter !== "all") return platform === policiesPlatformFilter;
      return true;
    });

    const statusFilteredProfiles = platformFilteredProfiles.filter((item) => {
      if (!isProfilesAllPage || profilesStatusFilter === "all") return true;
      const active = item.is_active !== false;
      return profilesStatusFilter === "active" ? active : !active;
    });

    const currentRows = [...statusFilteredProfiles].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), lang === "ru" ? "ru" : "en", {
        sensitivity: "base"
      })
    );
    const visibleColumns = isPoliciesPage
      ? policyColumnDefs.filter((column) => visiblePolicyColumns.includes(column.key))
      : profileColumnDefs.filter((column) => visibleProfileColumns.includes(column.key));
    const allVisibleIDs = currentRows.map((item) => item.id).filter(Boolean);
    const allVisibleSelected =
      allVisibleIDs.length > 0 && allVisibleIDs.every((id) => selectedProfileIDs.includes(id));

    return (
      <div className="page">
          <div className="panel devices-table-panel">
            <div className="devices-toolbar-line">
              <div className="devices-toolbar-actions">
                <button className="toolbar-action" onClick={() => handleProfilesAdd(forcedPlatform)}>
                  {t("create")}
                </button>
                <button className="toolbar-action" onClick={loadAllData}>
                  {t("refresh")}
                </button>
                <button className="toolbar-action" onClick={openAssignProfilesToGroupsModal}>
                  {t("profilesEditGroups")}
                </button>
                {selectedProfileIDs.length > 0 && (
                  <button className="toolbar-action danger" onClick={() => deleteSelectedProfiles()}>
                    {t("profilesDeleteSelected")}
                  </button>
                )}
              </div>
              <span className="muted">
                {isPoliciesPage ? t("policiesCount") : t("profilesCount")}: {currentRows.length}
              </span>
            </div>

            <div className="devices-search-line">
              <input
                className="text-input"
                placeholder={isPoliciesPage ? t("policiesSearch") : t("profilesSearch")}
                value={isPoliciesPage ? policiesSearch : profilesSearch}
                onChange={(event) =>
                  isPoliciesPage ? setPoliciesSearch(event.target.value) : setProfilesSearch(event.target.value)
                }
              />
              <div className="columns-menu-wrap">
                <button
                  className="toolbar-action"
                  onClick={() =>
                    isPoliciesPage
                      ? setShowPoliciesColumnsMenu((prev) => !prev)
                      : setShowProfilesColumnsMenu((prev) => !prev)
                  }
                >
                  {t("profilesColumns")}
                </button>
                {showProfilesColumnsMenu && !isPoliciesPage && (
                  <div className="columns-menu">
                    {profileColumnDefs.map((column) => (
                      <label key={column.key} className="column-option">
                        <input
                          type="checkbox"
                          checked={visibleProfileColumns.includes(column.key)}
                          onChange={(event) =>
                            setVisibleProfileColumns((prev) => {
                              if (event.target.checked) return Array.from(new Set([...prev, column.key]));
                              return prev.filter((item) => item !== column.key);
                            })
                          }
                        />
                        {column.label}
                      </label>
                    ))}
                    <button
                      className="reset-columns-btn"
                      onClick={() => setVisibleProfileColumns(PROFILE_DEFAULT_COLUMN_KEYS)}
                    >
                      {t("profilesResetColumns")}
                    </button>
                  </div>
                )}
                {showPoliciesColumnsMenu && isPoliciesPage && (
                  <div className="columns-menu">
                    {policyColumnDefs.map((column) => (
                      <label key={column.key} className="column-option">
                        <input
                          type="checkbox"
                          checked={visiblePolicyColumns.includes(column.key)}
                          onChange={(event) =>
                            setVisiblePolicyColumns((prev) => {
                              if (event.target.checked) return Array.from(new Set([...prev, column.key]));
                              return prev.filter((item) => item !== column.key);
                            })
                          }
                        />
                        {column.label}
                      </label>
                    ))}
                    <button
                      className="reset-columns-btn"
                      onClick={() => setVisiblePolicyColumns(POLICY_DEFAULT_COLUMN_KEYS)}
                    >
                      {t("profilesResetColumns")}
                    </button>
                  </div>
                )}
              </div>
              <button
                className="toolbar-action secondary"
                onClick={() =>
                  isPoliciesPage ? setShowPoliciesFilters((prev) => !prev) : setShowProfilesFilters((prev) => !prev)
                }
              >
                {t("addFilters")}
              </button>
            </div>

            {showProfilesFilters && !isPoliciesPage && (
              <div className="filters-row">
                <select
                  className="text-input"
                  value={profilesStatusFilter}
                  onChange={(event) => setProfilesStatusFilter(event.target.value)}
                >
                  <option value="all">{t("all")}</option>
                  <option value="active">{t("active")}</option>
                  <option value="revoked">{t("revoked")}</option>
                </select>
              </div>
            )}
            {showPoliciesFilters && isPoliciesPage && (
              <div className="filters-row">
                <select
                  className="text-input"
                  value={policiesPlatformFilter}
                  onChange={(event) => setPoliciesPlatformFilter(event.target.value)}
                >
                  <option value="all">{t("all")}</option>
                  <option value="windows">{t("platformWindows")}</option>
                  <option value="ios">{t("platformIOS")}</option>
                  <option value="macos">{t("platformMacOS")}</option>
                  <option value="android">{t("platformAndroid")}</option>
                </select>
              </div>
            )}

            {(forcedPlatform === "windows" || forcedPlatform === "android") && (
              <p className="muted">{t("profilesAppleOnlyBuilderHint")}</p>
            )}

            <div className="devices-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={() =>
                          setSelectedProfileIDs((prev) => {
                            if (allVisibleSelected) {
                              const hidden = new Set(allVisibleIDs);
                              return prev.filter((id) => !hidden.has(id));
                            }
                            return Array.from(new Set([...prev, ...allVisibleIDs]));
                          })
                        }
                      />
                    </th>
                    {visibleColumns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map((item) => (
                    <tr key={item.id} className={selectedProfileIDs.includes(item.id) ? "row-selected" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedProfileIDs.includes(item.id)}
                          onChange={() =>
                            setSelectedProfileIDs((prev) =>
                              prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
                            )
                          }
                        />
                      </td>
                      {visibleColumns.map((column) => (
                        <td key={`${item.id}-${column.key}`}>
                          {typeof column.render === "function" ? (
                            column.render(item)
                          ) : column.key === "name" ? (
                            <button className="table-link-button" onClick={() => openProfileView(item)}>
                              {column.value(item)}
                            </button>
                          ) : (
                            column.value(item)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {currentRows.length === 0 && <p className="devices-empty">{t("profilesNoItems")}</p>}
            </div>
          </div>
      </div>
    );
  }

  function renderProfileDevicesModal() {
    if (!profileDevicesModalItem) return null;
    const filteredItems = profileDevices.filter((item) => {
      if (profileDevicesFilter === "installed") return Boolean(item.installed);
      if (profileDevicesFilter === "missing") return !item.installed;
      return true;
    });
    const installedCount = profileDevices.filter((item) => Boolean(item.installed)).length;
    const assignedCount = profileDevices.length;

    return (
      <div className="modal-backdrop" onClick={closeProfileDevicesModal}>
        <div className="modal-card modal-card-wide document-assigned-devices-modal" onClick={(event) => event.stopPropagation()}>
          <div className="modal-heading">
            <div>
              <h3>{t("profileDevicesModalTitle")}</h3>
              <p className="muted">
                {profileDevicesModalItem.name || "-"} · {installedCount}/{assignedCount}
              </p>
            </div>
            <div className="devices-toolbar-actions">
              <button
                type="button"
                className="toolbar-action secondary"
                disabled={profileDevicesLoading || filteredItems.length === 0}
                onClick={() => exportProfileDevicesReport(filteredItems)}
              >
                {t("profileDevicesExportCSV")}
              </button>
              <button type="button" className="icon-button" onClick={closeProfileDevicesModal}>
                ×
              </button>
            </div>
          </div>

          <div className="filters-row">
            <select
              className="text-input"
              value={profileDevicesFilter}
              onChange={(event) => setProfileDevicesFilter(event.target.value)}
            >
              <option value="all">{t("profileDevicesFilterAll")}</option>
              <option value="installed">{t("profileDevicesFilterInstalled")}</option>
              <option value="missing">{t("profileDevicesFilterMissing")}</option>
            </select>
          </div>

          {profileDevicesLoading ? (
            <p className="muted">{t("loading")}</p>
          ) : (
            <div className="devices-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{lang === "ru" ? "Аккаунт" : "Account"}</th>
                    <th>{t("devicesColName")}</th>
                    <th>{t("devicesColSerial")}</th>
                    <th>{t("devicesColOSVersion")}</th>
                    <th>{t("profilesColGroups")}</th>
                    <th>{t("profilesColStatus")}</th>
                    <th>{t("devicesColLastSync")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.udid}>
                      <td>
                        {item.account_name || item.account_email || "-"}
                        {item.account_email && <div className="muted small-text">{item.account_email}</div>}
                      </td>
                      <td>
                        <a
                          href={deviceDetailsHash("devicesDetailAbout", item.udid)}
                          className="table-link-button"
                          onClick={(event) => {
                            if (
                              event.defaultPrevented ||
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.altKey ||
                              event.button !== 0
                            ) {
                              return;
                            }
                            closeProfileDevicesModal();
                          }}
                        >
                          {item.device_name || item.udid || "-"}
                        </a>
                      </td>
                      <td>{item.serial_number || "-"}</td>
                      <td>{item.os_version || "-"}</td>
                      <td>
                        {Array.isArray(item.group_names) && item.group_names.length > 0
                          ? item.group_names.join(", ")
                          : "-"}
                      </td>
                      <td>{item.installed ? t("profileDevicesInstalled") : t("profileDevicesNotInstalled")}</td>
                      <td>{item.installed_last_seen_at ? formatTimestamp(item.installed_last_seen_at) : formatTimestamp(item.last_checkin_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredItems.length === 0 && <p className="devices-empty">{t("profileDevicesEmpty")}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderAppsWorkspace() {
    const pagePlatformFilter =
      activePage === "appsPlatformWindows"
        ? "windows"
        : activePage === "appsPlatformIOS"
          ? "ios"
          : activePage === "appsPlatformMacOS"
            ? "macos"
            : activePage === "appsPlatformAndroid"
              ? "android"
              : "all";
    const effectivePlatformFilter = pagePlatformFilter === "all" ? appsPlatformFilter : pagePlatformFilter;
    const isConfigPage = activePage === "appsConfig";
    const isUploadPage = activePage === "appsUpload";

    const appColumns = [
      { key: "name", label: t("appsColName"), value: (item) => item.name || "-" },
      { key: "bundle_id", label: t("appsColBundleID"), value: (item) => item.bundle_id || "-" },
      { key: "platform", label: t("appsColPlatform"), value: (item) => profilePlatformLabel(normalizeAppPlatform(item), t) },
      { key: "type", label: t("appsColType"), value: (item) => item.type || item.source || "-" },
      { key: "version", label: t("appsColVersion"), value: (item) => item.version || "-" },
      { key: "vpp_token", label: t("appsColVPPToken"), value: (item) => item.vpp_token_name || "-" },
      { key: "assigned", label: t("appsColAssigned"), value: (item) => formatBool(item.is_assigned, t) },
      {
        key: "groups",
        label: t("appsColGroups"),
        value: (item) =>
          Array.isArray(item.group_names) && item.group_names.length > 0 ? item.group_names.join(", ") : "-"
      },
      { key: "developer", label: t("appsColDeveloper"), value: (item) => item.developer || "-" },
      { key: "status", label: t("appsColStatus"), value: (item) => (item.is_active === false ? t("revoked") : t("active")) },
      { key: "updated_at", label: t("appsColUpdatedAt"), value: (item) => formatTimestamp(item.updated_at) }
    ];
    const visibleColumns = appColumns.filter((column) => visibleAppColumns.includes(column.key));
    const filteredByPage = appsCatalog.filter((item) => {
      const platform = normalizeAppPlatform(item);
      if (effectivePlatformFilter !== "all" && platform !== effectivePlatformFilter) return false;
      if (appsTypeFilter !== "all") {
        const type = String(item.type || item.source || "").toLowerCase();
        if (type !== appsTypeFilter) return false;
      }
      const q = appsSearch.trim().toLowerCase();
      if (!q) return true;
      return `${item.name || ""} ${item.bundle_id || ""} ${item.developer || ""}`
        .toLowerCase()
        .includes(q);
    });
    const sortedRows = [...filteredByPage].sort((left, right) => {
      const leftValue = String(left[appsSortKey] ?? left.name ?? "").toLowerCase();
      const rightValue = String(right[appsSortKey] ?? right.name ?? "").toLowerCase();
      const base = leftValue.localeCompare(rightValue, lang === "ru" ? "ru" : "en", {
        sensitivity: "base"
      });
      return appsSortDirection === "asc" ? base : -base;
    });

    return (
      <div className="page">
          {!isConfigPage && !isUploadPage && (
            <div className="panel devices-table-panel">
              <div className="devices-toolbar-line">
                <div className="devices-toolbar-actions">
                  <button className="toolbar-action secondary" onClick={() => downloadReport("apps", "pdf")}>
                    {t("reportsPDF")}
                  </button>
                  <button className="toolbar-action secondary" onClick={() => downloadReport("apps", "csv")}>
                    {t("reportsCSV")}
                  </button>
                  <button className="toolbar-action" onClick={() => handleAppsAdd(pagePlatformFilter === "all" ? "" : pagePlatformFilter)}>
                    {t("create")}
                  </button>
                  <button className="toolbar-action" onClick={loadAllData}>
                    {t("refresh")}
                  </button>
                  <button className="toolbar-action" onClick={openAssignAppsToGroupsModal}>
                    {t("appsEditGroups")}
                  </button>
                  {selectedAppIDs.length > 0 && (
                    <button className="toolbar-action danger" onClick={() => deleteSelectedApps()}>
                      {t("appsDeleteSelected")}
                    </button>
                  )}
                </div>
                <span className="muted">
                  {t("appsCount")}: {sortedRows.length}
                </span>
              </div>

              <div className="devices-search-line">
                <input
                  className="text-input"
                  placeholder={t("appsSearchAll")}
                  value={appsSearch}
                  onChange={(event) => setAppsSearch(event.target.value)}
                />
                <div className="columns-menu-wrap">
                  <button className="toolbar-action" onClick={() => setShowAppsColumnsMenu((prev) => !prev)}>
                    {t("appsColumns")}
                  </button>
                  {showAppsColumnsMenu && (
                    <div className="columns-menu">
                      {appColumns.map((column) => (
                        <label key={column.key} className="column-option">
                          <input
                            type="checkbox"
                            checked={visibleAppColumns.includes(column.key)}
                            onChange={(event) =>
                              setVisibleAppColumns((prev) => {
                                if (event.target.checked) return Array.from(new Set([...prev, column.key]));
                                return prev.filter((item) => item !== column.key);
                              })
                            }
                          />
                          {column.label}
                        </label>
                      ))}
                      <button className="reset-columns-btn" onClick={() => setVisibleAppColumns(APP_DEFAULT_COLUMN_KEYS)}>
                        {t("appsResetColumns")}
                      </button>
                    </div>
                  )}
                </div>
                <button className="toolbar-action secondary" onClick={() => setShowAppsFilters((prev) => !prev)}>
                  {t("addFilters")}
                </button>
              </div>

              {showAppsFilters && (
                <div className="filters-row">
                  <select
                    className="text-input"
                    value={appsPlatformFilter}
                    onChange={(event) => setAppsPlatformFilter(event.target.value)}
                    disabled={pagePlatformFilter !== "all"}
                  >
                    <option value="all">{t("all")}</option>
                    <option value="windows">{t("platformWindows")}</option>
                    <option value="ios">{t("platformIOS")}</option>
                    <option value="macos">{t("platformMacOS")}</option>
                    <option value="android">{t("platformAndroid")}</option>
                  </select>
                  <select
                    className="text-input"
                    value={appsTypeFilter}
                    onChange={(event) => setAppsTypeFilter(event.target.value)}
                  >
                    <option value="all">{t("all")}</option>
                    <option value="store">Store</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              )}

              <div className="devices-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th />
                      {visibleColumns.map((column) => (
                        <th key={column.key}>
                          <button
                            className="sort-header-button"
                            onClick={() => {
                              if (appsSortKey === column.key) {
                                setAppsSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
                              } else {
                                setAppsSortKey(column.key);
                                setAppsSortDirection("asc");
                              }
                            }}
                          >
                            {column.label}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((item) => (
                      <tr key={item.id} className={selectedAppIDs.includes(item.id) ? "row-selected" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedAppIDs.includes(item.id)}
                            onChange={() => toggleAppSelection(item.id)}
                          />
                        </td>
                        {visibleColumns.map((column) => (
                          <td key={`${item.id}-${column.key}`}>
                            {column.key === "name" ? (
                              <button
                                className="table-link-button"
                                onClick={() => {
                                  setSelectedAppId(item.id);
                                  setActivePage("appsConfig");
                                }}
                              >
                                {column.value(item)}
                              </button>
                            ) : (
                              column.value(item)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sortedRows.length === 0 && <p className="devices-empty">{t("appsNoItems")}</p>}
              </div>
            </div>
          )}

          {isConfigPage && (
            <div className="panel">
              <div className="panel-heading">
                <h3>{t("appsConfig")}</h3>
                <div className="devices-toolbar-actions">
                  <button className="toolbar-action" onClick={createApp}>
                    {t("create")}
                  </button>
                  <button className="toolbar-action" onClick={updateApp}>
                    {t("save")}
                  </button>
                  <button className="toolbar-action danger" onClick={deleteApp}>
                    {t("delete")}
                  </button>
                </div>
              </div>
              <div className="modal-grid">
                <label>{t("appsColName")}</label>
                <input
                  className="text-input"
                  value={appForm.name}
                  onChange={(event) => setAppForm((prev) => ({ ...prev, name: event.target.value }))}
                />
                <label>{t("appsColBundleID")}</label>
                <input
                  className="text-input"
                  value={appForm.bundleId}
                  onChange={(event) => setAppForm((prev) => ({ ...prev, bundleId: event.target.value }))}
                />
                <label>{t("appsColPlatform")}</label>
                <select
                  className="text-input"
                  value={appForm.platform}
                  onChange={(event) => setAppForm((prev) => ({ ...prev, platform: event.target.value }))}
                >
                  <option value="windows">{t("platformWindows")}</option>
                  <option value="ios">{t("platformIOS")}</option>
                  <option value="macos">{t("platformMacOS")}</option>
                  <option value="android">{t("platformAndroid")}</option>
                </select>
                <label>{lang === "ru" ? "App Store ID" : "App Store ID"}</label>
                <input
                  className="text-input"
                  value={appForm.itunesStoreId}
                  onChange={(event) => setAppForm((prev) => ({ ...prev, itunesStoreId: event.target.value }))}
                />
                <label>{lang === "ru" ? "Назначение" : "Assignment"}</label>
                <select
                  className="text-input"
                  value={appForm.assignmentType}
                  onChange={(event) => setAppForm((prev) => ({ ...prev, assignmentType: event.target.value }))}
                >
                  <option value="optional">{lang === "ru" ? "Доступно пользователю" : "Optional"}</option>
                  <option value="required">{lang === "ru" ? "Обязательно установить" : "Required"}</option>
                </select>
                <label>{t("description")}</label>
                <textarea
                  className="text-input"
                  rows={3}
                  value={appForm.description}
                  onChange={(event) => setAppForm((prev) => ({ ...prev, description: event.target.value }))}
                />
                <details className="app-advanced-settings modal-grid-wide">
                  <summary>{lang === "ru" ? "Дополнительные настройки" : "Advanced settings"}</summary>
                  <div className="modal-grid app-advanced-settings-grid">
                    <label>{lang === "ru" ? "Manifest URL для enterprise-приложения" : "Enterprise Manifest URL"}</label>
                    <input
                      className="text-input"
                      value={appForm.manifestUrl}
                      onChange={(event) => setAppForm((prev) => ({ ...prev, manifestUrl: event.target.value }))}
                    />
                    <label>{lang === "ru" ? "Обновление" : "Update policy"}</label>
                    <select
                      className="text-input"
                      value={appForm.updatePolicy}
                      onChange={(event) => setAppForm((prev) => ({ ...prev, updatePolicy: event.target.value }))}
                    >
                      <option value="manual">{lang === "ru" ? "Вручную" : "Manual"}</option>
                      <option value="automatic">{lang === "ru" ? "Автоматически" : "Automatic"}</option>
                      <option value="managed">{lang === "ru" ? "Управляемо" : "Managed"}</option>
                    </select>
                    <label>{lang === "ru" ? "Приоритет установки" : "Install priority"}</label>
                    <select
                      className="text-input"
                      value={appForm.installPriority}
                      onChange={(event) => setAppForm((prev) => ({ ...prev, installPriority: event.target.value }))}
                    >
                      <option value="low">{lang === "ru" ? "Низкий" : "Low"}</option>
                      <option value="normal">{lang === "ru" ? "Обычный" : "Normal"}</option>
                      <option value="high">{lang === "ru" ? "Высокий" : "High"}</option>
                    </select>
                    <label>{lang === "ru" ? "Per-app VPN UUID" : "Per-app VPN UUID"}</label>
                    <input
                      className="text-input"
                      value={appForm.perAppVpnUuid}
                      onChange={(event) => setAppForm((prev) => ({ ...prev, perAppVpnUuid: event.target.value }))}
                    />
                    <label>{lang === "ru" ? "Конфигурация приложения JSON" : "Managed app config JSON"}</label>
                    <textarea
                      className="text-input"
                      rows={5}
                      value={appForm.managedConfigJSON}
                      onChange={(event) => setAppForm((prev) => ({ ...prev, managedConfigJSON: event.target.value }))}
                    />
                    <label>{lang === "ru" ? "In-house pipeline JSON" : "In-house pipeline JSON"}</label>
                    <textarea
                      className="text-input"
                      rows={4}
                      value={appForm.pipelineJSON}
                      onChange={(event) => setAppForm((prev) => ({ ...prev, pipelineJSON: event.target.value }))}
                    />
                    <label>{lang === "ru" ? "Параметры управления" : "Management options"}</label>
                    <div className="settings-checkbox-list">
                      <label>
                        <input
                          type="checkbox"
                          checked={appForm.uninstallProtected}
                          onChange={(event) => setAppForm((prev) => ({ ...prev, uninstallProtected: event.target.checked }))}
                        />
                        {lang === "ru" ? "Запретить удаление" : "Uninstall protection"}
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={appForm.requestManagedAttributes}
                          onChange={(event) => setAppForm((prev) => ({ ...prev, requestManagedAttributes: event.target.checked }))}
                        />
                        {lang === "ru" ? "Запрашивать managed attributes" : "Request managed attributes"}
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={appForm.requestManagedFeedback}
                          onChange={(event) => setAppForm((prev) => ({ ...prev, requestManagedFeedback: event.target.checked }))}
                        />
                        {lang === "ru" ? "Запрашивать managed feedback" : "Request managed feedback"}
                      </label>
                    </div>
                  </div>
                </details>
              </div>
              <div className="inline-form inline-form-wide">
                <input
                  className="text-input"
                  placeholder="UDID"
                  value={appDeployUdid}
                  onChange={(event) => setAppDeployUdid(event.target.value)}
                />
                <button onClick={deployApp}>{t("appsDeploy")}</button>
                <button
                  className="secondary"
                  onClick={() => requestManagedAppInfo("managed_application_attributes")}
                >
                  Attributes
                </button>
                <button
                  className="secondary"
                  onClick={() => requestManagedAppInfo("managed_application_feedback")}
                >
                  Feedback
                </button>
                <button
                  className="secondary"
                  onClick={() => requestManagedAppInfo("managed_application_configuration")}
                >
                  Config
                </button>
              </div>
            </div>
          )}

          {isUploadPage && (
            <div className="panel">
              <div className="panel-heading">
                <div>
                  <h3>{t("appsUpload")}</h3>
                  <p className="muted">{t("appsUploadHint")}</p>
                </div>
              </div>
              <form className="modal-grid two-col enterprise-app-form" onSubmit={createApp}>
                <label>
                  {t("appsColName")}
                  <input
                    className="text-input"
                    required
                    placeholder={lang === "ru" ? "Например, Turuk Companion" : "For example, Turuk Companion"}
                    value={appForm.name}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </label>
                <label>
                  {t("appsColPlatform")}
                  <select
                    className="text-input"
                    value={appForm.platform}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, platform: event.target.value }))}
                  >
                    <option value="ios">{t("platformIOS")}</option>
                    <option value="macos">{t("platformMacOS")}</option>
                    <option value="android">{t("platformAndroid")}</option>
                    <option value="windows">{t("platformWindows")}</option>
                  </select>
                </label>
                <label>
                  {t("appsColBundleID")}
                  <input
                    className="text-input"
                    required
                    placeholder="pro.turuk.agent"
                    value={appForm.bundleId}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, bundleId: event.target.value }))}
                  />
                </label>
                <label className="modal-grid-wide">
                  {t("appsEnterpriseManifestURL")}
                  <input
                    className="text-input"
                    required
                    placeholder="https://example.com/app/manifest.plist"
                    value={appForm.manifestUrl}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, manifestUrl: event.target.value }))}
                  />
                  <span className="field-help">{t("appsEnterpriseManifestHint")}</span>
                </label>
                <label className="modal-grid-wide">
                  {t("description")}
                  <textarea
                    className="text-input"
                    rows={3}
                    placeholder={t("appsEnterpriseDescriptionPlaceholder")}
                    value={appForm.description}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, description: event.target.value }))}
                  />
                </label>
                <label>
                  Assignment
                  <select
                    className="text-input"
                    value={appForm.assignmentType}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, assignmentType: event.target.value }))}
                  >
                    <option value="optional">Optional</option>
                    <option value="required">Required</option>
                  </select>
                </label>
                <label>
                  Update policy
                  <select
                    className="text-input"
                    value={appForm.updatePolicy}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, updatePolicy: event.target.value }))}
                  >
                    <option value="manual">Manual</option>
                    <option value="automatic">Automatic</option>
                    <option value="managed">Managed</option>
                  </select>
                </label>
                <label>
                  Per-app VPN UUID
                  <input
                    className="text-input"
                    value={appForm.perAppVpnUuid}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, perAppVpnUuid: event.target.value }))}
                  />
                </label>
                <label className="modal-grid-wide">
                  Managed app config JSON
                  <textarea
                    className="text-input"
                    rows={4}
                    value={appForm.managedConfigJSON}
                    onChange={(event) => setAppForm((prev) => ({ ...prev, managedConfigJSON: event.target.value }))}
                  />
                </label>
                <div className="action-row modal-grid-wide">
                  <button type="submit">{t("appsUploadCreate")}</button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() =>
                      setAppForm({
                        id: "",
                        name: "",
                        bundleId: "",
                        platform: "ios",
                        manifestUrl: "",
                        itunesStoreId: "",
                        description: "",
                        assignmentType: "optional",
                        updatePolicy: "manual",
                        installPriority: "normal",
                        uninstallProtected: false,
                        perAppVpnUuid: "",
                        managedConfigJSON: "{}",
                        requestManagedFeedback: false,
                        requestManagedAttributes: false,
                        pipelineJSON: "{}"
                      })
                    }
                  >
                    {t("appsUploadClear")}
                  </button>
                </div>
              </form>
            </div>
          )}
      </div>
    );
  }

  function renderDashboard() {
    return (
      <Suspense fallback={<div className="panel"><h3>{t("navDashboard")}</h3></div>}>
        <LazyDashboardPage
          t={t}
          devices={devices}
          accounts={accounts}
          groups={groups}
          profiles={profiles}
          appsCatalog={appsCatalog}
          agentSupportTickets={agentSupportTickets}
          platformCounts={platformCounts}
          dashboardSettings={dashboardSectionSettings}
          dashboardOrder={dashboardSectionOrder}
        />
      </Suspense>
    );
  }

  function renderCommandCenter() {
    const items = Array.isArray(commandCenter.items) ? commandCenter.items : [];
    const summary = commandCenter.summary || {};
    const selectedSet = new Set(selectedCommandCenterUUIDs);
    const allVisibleSelected = items.length > 0 && items.every((item) => selectedSet.has(item.command_uuid));
    const requestTypes = Array.from(new Set(items.map((item) => item.request_type).filter(Boolean))).sort();
    const labels = {
      title: lang === "ru" ? "Центр команд" : "Command Center",
      subtitle: lang === "ru" ? "Единая очередь команд по всем устройствам." : "Unified command queue across all devices.",
      search: lang === "ru" ? "Поиск команды, устройства, аккаунта" : "Search command, device, account",
      state: lang === "ru" ? "Состояние" : "State",
      type: lang === "ru" ? "Тип команды" : "Command type",
      retry: lang === "ru" ? "Повторить" : "Retry",
      cancel: lang === "ru" ? "Отменить" : "Cancel",
      refresh: lang === "ru" ? "Обновить" : "Refresh",
      device: lang === "ru" ? "Устройство" : "Device",
      sentBy: lang === "ru" ? "Кто отправил" : "Sent by",
      attempts: lang === "ru" ? "Попытки" : "Attempts",
      updated: lang === "ru" ? "Последнее изменение" : "Last update",
      error: lang === "ru" ? "Ошибка" : "Error",
      empty: lang === "ru" ? "Команд пока нет." : "No commands yet."
    };
    const summaryCards = [
      { key: "total", label: lang === "ru" ? "Всего" : "Total", value: summary.total || 0 },
      { key: "queued", label: lang === "ru" ? "В очереди" : "Queued", value: summary.queued || 0 },
      { key: "sent", label: lang === "ru" ? "Отправлено" : "Sent", value: summary.sent || 0 },
      { key: "failed", label: lang === "ru" ? "Ошибки" : "Errors", value: (summary.dead_letter || 0) + (summary.failed || 0) }
    ];
    return (
      <div className="page">
        <div className="panel">
          <div className="devices-toolbar-line">
            <div>
              <h2>{labels.title}</h2>
              <p className="muted">{labels.subtitle}</p>
            </div>
            <div className="devices-toolbar-actions">
              <button className="toolbar-action secondary" type="button" onClick={() => loadCommandCenter()}>
                {labels.refresh}
              </button>
              <button className="toolbar-action" type="button" disabled={selectedCommandCenterUUIDs.length === 0} onClick={() => bulkCommandCenter("retry")}>
                {labels.retry}
              </button>
              <button className="toolbar-action danger" type="button" disabled={selectedCommandCenterUUIDs.length === 0} onClick={() => bulkCommandCenter("cancel")}>
                {labels.cancel}
              </button>
            </div>
          </div>
          <div className="stats-grid compact-stats">
            {summaryCards.map((card) => (
              <div className="stat-card" key={card.key}>
                <div className="compact-stat-title">
                  <span>{card.label}</span>
                  <strong>{card.value}</strong>
                </div>
              </div>
            ))}
          </div>
          <div className="devices-toolbar-line" style={{ marginTop: 16 }}>
            <input
              className="text-input"
              value={commandCenterFilters.search}
              placeholder={labels.search}
              onChange={(event) => setCommandCenterFilters((prev) => ({ ...prev, search: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === "Enter") loadCommandCenter();
              }}
            />
            <select
              className="text-input"
              value={commandCenterFilters.state}
              onChange={(event) => {
                const next = { ...commandCenterFilters, state: event.target.value };
                setCommandCenterFilters(next);
                loadCommandCenter(next);
              }}
            >
              <option value="">{labels.state}: {lang === "ru" ? "все" : "all"}</option>
              {["queued", "sent", "done", "dead_letter", "canceled"].map((state) => (
                <option key={state} value={state}>{commandCenterStateLabel(state, lang)}</option>
              ))}
            </select>
            <select
              className="text-input"
              value={commandCenterFilters.requestType}
              onChange={(event) => {
                const next = { ...commandCenterFilters, requestType: event.target.value };
                setCommandCenterFilters(next);
                loadCommandCenter(next);
              }}
            >
              <option value="">{labels.type}: {lang === "ru" ? "все" : "all"}</option>
              {requestTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="devices-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={(event) =>
                        setSelectedCommandCenterUUIDs(event.target.checked ? items.map((item) => item.command_uuid) : [])
                      }
                    />
                  </th>
                  <th>UUID</th>
                  <th>{labels.device}</th>
                  <th>{labels.type}</th>
                  <th>{labels.state}</th>
                  <th>{labels.attempts}</th>
                  <th>{labels.sentBy}</th>
                  <th>{labels.updated}</th>
                  <th>{labels.error}</th>
                  <th>{lang === "ru" ? "Действие" : "Action"}</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={10} className="empty">{labels.empty}</td></tr>
                )}
                {items.map((item) => (
                  <tr key={item.command_uuid}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(item.command_uuid)}
                        onChange={(event) =>
                          setSelectedCommandCenterUUIDs((prev) =>
                            event.target.checked
                              ? Array.from(new Set([...prev, item.command_uuid]))
                              : prev.filter((uuid) => uuid !== item.command_uuid)
                          )
                        }
                      />
                    </td>
                    <td><span className="mono-text">{item.command_uuid}</span></td>
                    <td>
                      <strong>{item.device_name || item.device_udid || "-"}</strong>
                      <div className="muted">{item.serial_number || item.account_email || item.device_udid || ""}</div>
                    </td>
                    <td>{item.request_type || "-"}</td>
                    <td><span className={commandCenterStateClass(item.state)}>{commandCenterStateLabel(item.state, lang)}</span></td>
                    <td>{item.attempt_count || 0} / {item.max_attempts || 0}</td>
                    <td>{item.requested_by || item.canceled_by || "-"}</td>
                    <td>{formatTimestamp(item.updated_at || item.queued_at)}</td>
                    <td className="muted">{item.last_error || item.result_detail || "-"}</td>
                    <td>
                      {item.can_retry && (
                        <button className="toolbar-action secondary" type="button" onClick={() => commandCenterSingleAction("retry", item.command_uuid)}>
                          {labels.retry}
                        </button>
                      )}
                      {item.can_cancel && (
                        <button
                          className="toolbar-action danger"
                          type="button"
                          onClick={() => commandCenterSingleAction("cancel", item.command_uuid)}
                        >
                          {labels.cancel}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  function renderHealthStatus() {
    const allChecks = Array.isArray(systemHealth.checks) ? systemHealth.checks : [];
    const isPlatformAdmin = session?.user?.platform_admin === true;
    const visibleKeys = isPlatformAdmin
      ? null
      : new Set(["apns", "ade", "vpp", "backup", "ddm"]);
    const optionalUnknownKeys = new Set(["restore_drill", "webhooks", "macos_agent"]);
    const optionalTenantNotConfiguredKeys = new Set(["ade", "vpp"]);
    const tenantHasDDMState =
      hasActiveDDMSoftwareUpdatePolicy(ddmDeclarations) ||
      (Array.isArray(ddmProtocolCaptures) && ddmProtocolCaptures.length > 0) ||
      (Array.isArray(ddmStatuses) && ddmStatuses.length > 0);
    const checks = (visibleKeys ? allChecks.filter((check) => visibleKeys.has(check.key)) : allChecks).filter((check) => {
      if (!check) return false;
      const status = String(check.status || "").toLowerCase();
      const detail = String(check.detail || "").toLowerCase();
      if (!isPlatformAdmin && check.key === "ddm" && !tenantHasDDMState) return false;
      if (optionalUnknownKeys.has(check.key) && status === "unknown") return false;
      if (!isPlatformAdmin && optionalTenantNotConfiguredKeys.has(check.key) && status === "warning" && detail.includes("token is not configured")) {
        return false;
      }
      return true;
    });
	    const visibleOverall = summarizeVisibleHealth(checks);
	    return (
	      <div className="page">
	        <div className="panel">
	          <div className="devices-toolbar-line">
	            <div>
	              <h2>{lang === "ru" ? "Состояние системы" : "System Health"}</h2>
	              <p className="muted">
	                {isPlatformAdmin
	                  ? (lang === "ru" ? "Технический мониторинг сервисов, интеграций и очередей." : "Technical monitoring for services, integrations, and queues.")
	                  : (lang === "ru" ? "Основные сервисы, которые влияют на управление устройствами этой организации." : "Core services that affect device management for this organization.")}
	              </p>
	            </div>
	            <div className="devices-toolbar-actions">
	              <span className={healthStatusClass(visibleOverall)}>{healthStatusLabel(visibleOverall, lang)}</span>
	              <button className="toolbar-action secondary" type="button" onClick={loadSystemHealth}>
	                {lang === "ru" ? "Обновить" : "Refresh"}
              </button>
            </div>
          </div>
          <div className="stats-grid compact-stats">
	            {checks.map((check) => (
	              <div className="stat-card" key={check.key}>
	                <div className="health-check-title">
	                  <span>{healthCheckLabel(check, lang)}</span>
	                  <strong><span className={healthStatusClass(check.status)}>{healthStatusLabel(check.status, lang)}</span></strong>
	                </div>
                <p className="muted">{healthDetailLabel(check.detail, lang) || "-"}</p>
                <p className="muted">{formatTimestamp(check.updated_at)}</p>
              </div>
            ))}
            {checks.length === 0 && <p className="empty">{lang === "ru" ? "Нет данных health." : "No health data."}</p>}
          </div>
        </div>
      </div>
    );
  }

  function renderOrganizationAdmin() {
    const tenant = organizationSettings?.tenant || {};
    const canEditOrganization = hasPermission("settings.tenants", "edit");
    return (
      <div className="settings-stack">
        <section className="settings-card">
          <div className="devices-toolbar-line">
            <div>
              <h3>{lang === "ru" ? "Организация" : "Organization"}</h3>
              <p className="muted">
                {lang === "ru" ? "Домены, billing/trial, лимиты, контакты, timezone и retention для текущего tenant." : "Domains, billing/trial, limits, contacts, timezone and retention for the current tenant."}
              </p>
            </div>
            <div className="devices-toolbar-actions">
              <a className="toolbar-action secondary" href={`#${PAGE_TO_ROUTE.settingsConsoleBranding}`}>Branding</a>
              <a className="toolbar-action secondary" href={`#${PAGE_TO_ROUTE.settingsSSO}`}>SSO</a>
              <button className="toolbar-action secondary" type="button" onClick={loadOrganizationSettings}>
                {lang === "ru" ? "Обновить" : "Refresh"}
              </button>
            </div>
          </div>
          <form className="settings-form-grid" onSubmit={saveOrganizationSettings}>
            <label>
              {lang === "ru" ? "Название" : "Name"}
              <input className="text-input" value={organizationDraft.name} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, name: event.target.value }))} />
            </label>
            <label>
              {lang === "ru" ? "Основной домен" : "Primary domain"}
              <input className="text-input" value={organizationDraft.primary_domain} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, primary_domain: event.target.value }))} />
            </label>
            <label>
              {lang === "ru" ? "План" : "Plan"}
              <select className="text-input" value={organizationDraft.plan} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, plan: event.target.value }))}>
                <option value="trial">trial</option>
                <option value="business">business</option>
                <option value="enterprise">enterprise</option>
                <option value="internal">internal</option>
              </select>
            </label>
            <label>
              {lang === "ru" ? "Billing status" : "Billing status"}
              <select className="text-input" value={organizationDraft.billing_status} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, billing_status: event.target.value }))}>
                <option value="manual">manual</option>
                <option value="trialing">trialing</option>
                <option value="active">active</option>
                <option value="past_due">past_due</option>
                <option value="canceled">canceled</option>
              </select>
            </label>
            <label>
              Billing email
              <input className="text-input" value={organizationDraft.billing_email} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, billing_email: event.target.value }))} />
            </label>
            <label>
              Trial ends
              <input type="date" className="text-input" value={organizationDraft.trial_ends_at} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, trial_ends_at: event.target.value }))} />
            </label>
            <label>
              Subscription ends
              <input type="date" className="text-input" value={organizationDraft.subscription_ends_at} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, subscription_ends_at: event.target.value }))} />
            </label>
            <label>
              Timezone
              <input className="text-input" value={organizationDraft.timezone} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, timezone: event.target.value }))} />
            </label>
            <label>
              {lang === "ru" ? "Лимит устройств" : "Device limit"}
              <input className="text-input" type="number" min="1" value={organizationDraft.device_limit} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, device_limit: event.target.value }))} />
            </label>
            <label>
              {lang === "ru" ? "Лимит аккаунтов" : "Account limit"}
              <input className="text-input" type="number" min="1" value={organizationDraft.account_limit} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, account_limit: event.target.value }))} />
            </label>
            <label>
              {lang === "ru" ? "Лимит администраторов" : "Admin limit"}
              <input className="text-input" type="number" min="1" value={organizationDraft.admin_limit} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, admin_limit: event.target.value }))} />
            </label>
            <label>
              {lang === "ru" ? "Retention, дней" : "Retention, days"}
              <input className="text-input" type="number" min="1" value={organizationDraft.data_retention_days} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, data_retention_days: event.target.value }))} />
            </label>
            <label>
              Support email
              <input className="text-input" value={organizationDraft.support_email} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, support_email: event.target.value }))} />
            </label>
            <label>
              Security contact email
              <input className="text-input" value={organizationDraft.security_email} onChange={(event) => setOrganizationDraft((prev) => ({ ...prev, security_email: event.target.value }))} />
            </label>
            <div className="action-row modal-grid-wide">
              <button className="toolbar-action" type="submit" disabled={!canEditOrganization}>
                {lang === "ru" ? "Сохранить" : "Save"}
              </button>
              <span className="muted">ID: {tenant.id || "-"}</span>
            </div>
          </form>
        </section>
      </div>
    );
  }

  function renderSettings() {
    const isConsoleBrandingPage = activePage === "settingsConsoleBranding";
    const isConsoleDashboardPage = activePage === "settingsConsoleDashboard";
    const isOrganizationPage = activePage === "settingsOrganization";
    const isAgentPage = activePage === "settingsAgent";
    const isConsoleUsersPage = activePage === "settingsConsoleUsers";
    const isCertificatesPage = activePage === "settingsCertificates";
    const isADEPage = activePage === "settingsADE";
    const isVPPPage = activePage === "settingsVPP";
    const isDeclarativePage = activePage === "settingsDeclarative";
    const isSecurityPage = activePage === "settingsSecurity";
    const isNotificationsPage = activePage === "settingsNotifications";
    const isReportsPage = activePage === "settingsReports";
    const isSSOPage = activePage === "settingsSSO";
    const isTenantsPage = activePage === "settingsTenants";
    const isRoleSettingsPage = activePage === "settingsRoleSettings";
    const isPlatformAdmin = session?.user?.platform_admin === true;
    const canViewConsoleBranding = hasPermission("settings.console_branding", "view");
    const canEditConsoleBranding = hasPermission("settings.console_branding", "edit");
    const canViewConsoleDashboard = hasPermission("settings.console_branding", "view");
    const canEditConsoleDashboard = hasPermission("settings.console_branding", "edit");
    const canViewOrganization = hasPermission("settings.tenants", "view");
    const canEditOrganization = hasPermission("settings.tenants", "edit");
    const canViewAgent = hasPermission("settings.agent", "view");
    const canEditAgent = hasPermission("settings.agent", "edit");
    const canViewConsoleUsers = hasPermission("settings.console_users", "view");
    const canEditConsoleUsers = hasPermission("settings.console_users", "edit");
    const canViewCertificates = hasPermission("settings.certificates", "view");
    const canEditCertificates = hasPermission("settings.certificates", "edit");
    const canViewADE = hasPermission("settings.ade", "view");
    const canEditADE = hasPermission("settings.ade", "edit");
    const canViewVPP = hasPermission("settings.vpp", "view");
    const canEditVPP = hasPermission("settings.vpp", "edit");
    const canViewDeclarative = hasPermission("settings.declarative", "view");
    const canEditDeclarative = hasPermission("settings.declarative", "edit");
    const canViewSecurity = hasPermission("settings.security", "view");
    const canEditSecurity = hasPermission("settings.security", "edit");
    const securitySessionsPageSize = 5;
    const securitySessionsTotalPages = Math.max(1, Math.ceil(securitySessions.length / securitySessionsPageSize));
    const normalizedSecuritySessionsPage = Math.min(Math.max(1, securitySessionsPage), securitySessionsTotalPages);
    const visibleSecuritySessions = securitySessions.slice(
      (normalizedSecuritySessionsPage - 1) * securitySessionsPageSize,
      normalizedSecuritySessionsPage * securitySessionsPageSize
    );
    const renderSecuritySessionsPager = () => (
      <div className="table-pager">
        <button
          type="button"
          className="icon-button"
          disabled={normalizedSecuritySessionsPage <= 1}
          onClick={() => setSecuritySessionsPage((page) => Math.max(1, page - 1))}
          title={lang === "ru" ? "Предыдущая страница" : "Previous page"}
        >
          ‹
        </button>
        <span>
          {normalizedSecuritySessionsPage} / {securitySessionsTotalPages}
        </span>
        <button
          type="button"
          className="icon-button"
          disabled={normalizedSecuritySessionsPage >= securitySessionsTotalPages}
          onClick={() => setSecuritySessionsPage((page) => Math.min(securitySessionsTotalPages, page + 1))}
          title={lang === "ru" ? "Следующая страница" : "Next page"}
        >
          ›
        </button>
      </div>
    );
    const canViewAlertSettings =
      hasPermission("settings.notifications", "view") || hasPermission("settings", "view");
    const canEditAlertSettings =
      hasPermission("settings.notifications", "edit") || hasPermission("settings", "edit");
    const canViewReports =
      hasPermission("settings.reports", "view") || hasPermission("settings", "view");
    const canEditReports =
      hasPermission("settings.reports", "edit") || hasPermission("settings", "edit");
    const canViewSSO = hasPermission("settings.sso", "view");
    const canEditSSO = hasPermission("settings.sso", "edit");
    const canViewTenantIsolation =
      canViewSecurity &&
      String(session?.user?.role || "").toLowerCase() === "master-admin" &&
      session?.user?.platform_admin === true;
    const canViewTenants =
      String(session?.user?.role || "").toLowerCase() === "master-admin" &&
      session?.user?.platform_admin !== false &&
      hasPermission("settings.tenants", "view");
    const canEditTenants =
      String(session?.user?.role || "").toLowerCase() === "master-admin" &&
      session?.user?.platform_admin !== false &&
      hasPermission("settings.tenants", "edit");
    const canViewRoleSettings = hasPermission("settings.role_settings", "view");
    const canEditRoleSettings = hasPermission("settings.role_settings", "edit");
    const mdmAPNSStatus = mdmAPNSStatusMeta(mdmAPNSConfig, t);
    const mdmAPNSExpiry = mdmAPNSExpiryMeta(mdmAPNSConfig);
    const tenantIsolationMissingTables = Array.isArray(tenantIsolationReport?.missing_tenant_id_tables)
      ? tenantIsolationReport.missing_tenant_id_tables
      : [];
    const tenantIsolationRLSStatuses = Array.isArray(tenantIsolationReport?.rls_statuses)
      ? tenantIsolationReport.rls_statuses
      : [];
    const tenantIsolationRLSPolicyIssues = tenantIsolationRLSStatuses.filter(
      (item) => !item.rls_enabled || !item.has_tenant_policy
    );
    const tenantIsolationUnscopedUniqueIndexes = Array.isArray(tenantIsolationReport?.unscoped_unique_indexes)
      ? tenantIsolationReport.unscoped_unique_indexes
      : [];
    const tenantIsolationBlockingUniqueIndexes = tenantIsolationUnscopedUniqueIndexes.filter(
      (item) => !item.is_primary_key
    );
    const dashboardSectionItems = [
      {
        key: "total_deployment",
        title: t("consoleDashboardSectionTotalDeployment"),
        hint: t("consoleDashboardSectionTotalDeploymentHint"),
        children: [
          { key: "summary_accounts", label: t("consoleDashboardSummaryAccounts") },
          { key: "summary_profiles", label: t("consoleDashboardSummaryProfiles") },
          { key: "summary_apps", label: t("consoleDashboardSummaryApps") },
          { key: "summary_groups", label: t("consoleDashboardSummaryGroups") }
        ]
      },
      {
        key: "support_tickets",
        title: t("consoleDashboardSectionSupportTickets"),
        hint: t("consoleDashboardSectionSupportTicketsHint")
      },
      {
        key: "security",
        title: t("consoleDashboardSectionSecurity"),
        hint: t("consoleDashboardSectionSecurityHint"),
        children: [
          { key: "security_encrypted", label: t("consoleDashboardSecurityEncrypted") },
          { key: "security_passcode", label: t("consoleDashboardSecurityPasscode") },
          { key: "security_supervised", label: t("consoleDashboardSecuritySupervised") }
        ]
      },
      {
        key: "ownership",
        title: t("consoleDashboardSectionOwnership"),
        hint: t("consoleDashboardSectionOwnershipHint"),
        children: [
          { key: "ownership_corporate", label: t("consoleDashboardOwnershipCorporate") },
          { key: "ownership_personal", label: t("consoleDashboardOwnershipPersonal") },
          { key: "ownership_unassigned", label: t("consoleDashboardOwnershipUnassigned") }
        ]
      },
      {
        key: "last_seen_overview",
        title: t("consoleDashboardSectionLastSeenOverview"),
        hint: t("consoleDashboardSectionLastSeenOverviewHint")
      },
      {
        key: "last_seen_breakdown",
        title: t("consoleDashboardSectionLastSeenBreakdown"),
        hint: t("consoleDashboardSectionLastSeenBreakdownHint"),
        children: [
          { key: "last_seen_bucket_0to1", label: t("consoleDashboardLastSeenBucket0to1") },
          { key: "last_seen_bucket_2to7", label: t("consoleDashboardLastSeenBucket2to7") },
          { key: "last_seen_bucket_8to15", label: t("consoleDashboardLastSeenBucket8to15") },
          { key: "last_seen_bucket_16to30", label: t("consoleDashboardLastSeenBucket16to30") },
          { key: "last_seen_bucket_31plus", label: t("consoleDashboardLastSeenBucket31Plus") }
        ]
      },
      {
        key: "platforms",
        title: t("consoleDashboardSectionPlatforms"),
        hint: t("consoleDashboardSectionPlatformsHint"),
        children: [
          { key: "platform_ios", label: t("consoleDashboardPlatformIOS") },
          { key: "platform_macos", label: t("consoleDashboardPlatformMacOS") },
          { key: "platform_windows", label: t("consoleDashboardPlatformWindows") },
          { key: "platform_android", label: t("consoleDashboardPlatformAndroid") }
        ]
      }
    ];
    const dashboardSectionItemByKey = new Map(
      dashboardSectionItems.map((item) => [item.key, item])
    );
    const orderedDashboardSectionItems = normalizeDashboardSectionOrder(
      dashboardSectionDraftOrder
    )
      .map((key) => dashboardSectionItemByKey.get(key))
      .filter(Boolean);
    const dashboardSettingsDirty =
      Object.keys(DEFAULT_DASHBOARD_SECTION_SETTINGS).some(
        (key) =>
          Boolean(dashboardSectionDraftSettings[key]) !==
          Boolean(dashboardSectionSettings[key])
      ) ||
      JSON.stringify(normalizeDashboardSectionOrder(dashboardSectionDraftOrder)) !==
        JSON.stringify(normalizeDashboardSectionOrder(dashboardSectionOrder));
    const mainNavOrderSettingsItems = [
      { key: "dashboard", title: t("navDashboard"), hint: `#${PAGE_TO_ROUTE.dashboard}` },
      { key: "command_center", title: lang === "ru" ? "Центр команд" : "Command Center", hint: `#${PAGE_TO_ROUTE.commandCenter}` },
      { key: "health_status", title: lang === "ru" ? "Состояние системы" : "System Health", hint: `#${PAGE_TO_ROUTE.healthStatus}` },
      { key: "accounts", title: t("navAccounts"), hint: `#${PAGE_TO_ROUTE.accountsList}` },
      { key: "groups", title: t("navAccountsGroups"), hint: `#${PAGE_TO_ROUTE.groups}` },
      { key: "turuk_agent", title: t("navNotifications"), hint: `#${PAGE_TO_ROUTE.agentNotificationsCreate}` },
      { key: "documents", title: t("navDocuments"), hint: `#${PAGE_TO_ROUTE.documents}` },
      { key: "action_logs", title: t("navActionLogs"), hint: `#${PAGE_TO_ROUTE.actionLogs}` },
      { key: "devices", title: t("navDevices"), hint: `#${PAGE_TO_ROUTE.devicesList}` },
      { key: "profiles", title: t("navProfiles"), hint: `#${PAGE_TO_ROUTE.profilesAll}` },
      { key: "apps", title: t("navApps"), hint: `#${PAGE_TO_ROUTE.appsAll}` },
      { key: "settings", title: t("navSettings"), hint: `#${PAGE_TO_ROUTE.settingsConsoleBranding}` }
    ];
    const mainNavOrderSettingsItemByKey = new Map(
      mainNavOrderSettingsItems.map((item) => [item.key, item])
    );
    const orderedMainNavSettingsItems = normalizeMainNavOrder(mainNavDraftOrder)
      .map((key) => mainNavOrderSettingsItemByKey.get(key))
      .filter(Boolean);
    const mainNavOrderDirty =
      JSON.stringify(normalizeMainNavOrder(mainNavDraftOrder)) !==
      JSON.stringify(normalizeMainNavOrder(mainNavOrder));
    const selectedRoleItem =
      roleSettings.find(
        (item) => String(item.role_key || "").toLowerCase() === String(selectedRoleKey || "").toLowerCase()
      ) || null;
    const ssoRoleOptions = roleSettings.length > 0
      ? roleSettings
      : [
          { role_key: "helpdesk", display_name: "Helpdesk" },
          { role_key: "read-only", display_name: "Read Only" },
          { role_key: "admin", display_name: "Admin" },
          { role_key: "master-admin", display_name: "Master Admin" }
        ];
    const selectedSSOPreset = SSO_PROVIDER_PRESETS[ssoForm.sso_preset] || SSO_PROVIDER_PRESETS.custom_oidc;
    const visibleApplePolicyDeclarations = (Array.isArray(ddmDeclarations) ? ddmDeclarations : []).filter((item) => {
      if (item?.declaration_type !== "configuration") return false;
      let payload = {};
      try {
        payload = typeof item?.payload === "string" ? JSON.parse(item.payload || "{}") : item?.payload || {};
      } catch {
        payload = {};
      }
      const type = String(payload?.Type || "");
      return type.includes("softwareupdate") || type.includes("passcode") || type.includes("app");
    });

    const moveDashboardSectionDraft = (sourceKey, targetKey) => {
      setDashboardSectionDraftOrder((prev) => {
        const ordered = normalizeDashboardSectionOrder(prev);
        const sourceIndex = ordered.indexOf(sourceKey);
        const targetIndex = ordered.indexOf(targetKey);
        if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
          return ordered;
        }
        const next = [...ordered];
        const [moved] = next.splice(sourceIndex, 1);
        next.splice(sourceIndex < targetIndex ? targetIndex : targetIndex, 0, moved);
        return next;
      });
    };
    const moveMainNavDraft = (sourceKey, targetKey) => {
      setMainNavDraftOrder((prev) => {
        const ordered = normalizeMainNavOrder(prev);
        const sourceIndex = ordered.indexOf(sourceKey);
        const targetIndex = ordered.indexOf(targetKey);
        if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
          return ordered;
        }
        const next = [...ordered];
        const [moved] = next.splice(sourceIndex, 1);
        next.splice(targetIndex, 0, moved);
        return next;
      });
    };

    return (
      <div className="devices-shell">
        <aside className="panel devices-subnav">
          <div className="devices-subnav-scroll">
            {(canViewConsoleBranding || canViewConsoleDashboard || canViewOrganization) && (
              <details className="devices-nav-group" open>
                <summary>{t("settingsWorkspaceConsoleGroup")}</summary>
                {canViewConsoleBranding && (
                  <a
                    className={isConsoleBrandingPage ? "devices-nav-btn active" : "devices-nav-btn"}
                    href={`#${PAGE_TO_ROUTE.settingsConsoleBranding}`}
                  >
                    {t("settingsWorkspaceConsoleBranding")}
                  </a>
                )}
                {canViewConsoleDashboard && (
                  <a
                    className={isConsoleDashboardPage ? "devices-nav-btn active" : "devices-nav-btn"}
                    href={`#${PAGE_TO_ROUTE.settingsConsoleDashboard}`}
                  >
                    {t("settingsWorkspaceConsoleDashboard")}
                  </a>
                )}
                {canViewOrganization && (
                  <a
                    className={isOrganizationPage ? "devices-nav-btn active" : "devices-nav-btn"}
                    href={`#${PAGE_TO_ROUTE.settingsOrganization}`}
                  >
                    {lang === "ru" ? "Организация" : "Organization"}
                  </a>
                )}
              </details>
            )}
            {canViewAgent && (
              <a
                className={isAgentPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsAgent}`}
              >
                {t("settingsWorkspaceAgent")}
              </a>
            )}
            {canViewConsoleUsers && (
              <a
                className={isConsoleUsersPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsConsoleUsers}`}
              >
                {t("settingsWorkspaceConsoleUsers")}
              </a>
            )}
            {canViewCertificates && (
              <a
                className={isCertificatesPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsCertificates}`}
              >
                {t("settingsWorkspaceCertificates")}
              </a>
            )}
            {canViewADE && (
              <a
                className={isADEPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsADE}`}
              >
                {t("settingsWorkspaceADE")}
              </a>
            )}
            {canViewVPP && (
              <a
                className={isVPPPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsVPP}`}
              >
                {t("settingsWorkspaceVPP")}
              </a>
            )}
            {canViewDeclarative && (
              <a
                className={isDeclarativePage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsDeclarative}`}
              >
                {t("settingsWorkspaceDeclarative")}
              </a>
            )}
            {canViewSecurity && (
              <a
                className={isSecurityPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsSecurity}`}
              >
                {t("settingsWorkspaceSecurity")}
              </a>
            )}
            {canViewAlertSettings && (
              <a
                className={isNotificationsPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsNotifications}`}
              >
                {t("settingsWorkspaceNotifications")}
              </a>
            )}
            {canViewReports && (
              <a
                className={isReportsPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsReports}`}
              >
                {t("settingsWorkspaceReports")}
              </a>
            )}
            {canViewSSO && (
              <a
                className={isSSOPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsSSO}`}
              >
                {t("settingsWorkspaceSSO")}
              </a>
            )}
            {canViewTenants && (
              <a
                className={isTenantsPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsTenants}`}
              >
                {t("settingsWorkspaceTenants")}
              </a>
            )}
            {canViewRoleSettings && (
              <a
                className={isRoleSettingsPage ? "devices-nav-btn active" : "devices-nav-btn"}
                href={`#${PAGE_TO_ROUTE.settingsRoleSettings}`}
              >
                {t("settingsWorkspaceRoleSettings")}
              </a>
            )}
          </div>
        </aside>

        <div className="page">
          {isConsoleBrandingPage && canViewConsoleBranding && (
            <div className="panel">
              <div className="panel-heading">
                <h3>{t("consoleBrandingTitle")}</h3>
              </div>
              <p>{t("consoleBrandingHint")}</p>

              <div className="console-branding-layout">
                <div>
                  <h4>{t("consoleBrandingPreview")}</h4>
                  <div className="console-branding-preview">
                    <img
                      className="console-branding-logo"
                      src={consoleLogoDraftDataURL || defaultConsoleLogo}
                      alt={t("consoleBrandingLogoAlt")}
                    />
                  </div>
                  {!consoleLogoDraftDataURL && <p className="muted">{t("consoleBrandingDefaultPreview")}</p>}
                </div>

                <div>
                  <label>{t("consoleBrandingUploadLabel")}</label>
                  <input
                    ref={consoleLogoInputRef}
                    className="text-input"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                    disabled={!canEditConsoleBranding}
                    aria-label={t("consoleBrandingChooseFile")}
                    onChange={handleConsoleLogoFileChange}
                  />
                  <p className="muted">{t("consoleBrandingUploadHint")}</p>

                  <label>{t("consoleBrandingLogoClickLabel")}</label>
                  <input
                    className="text-input"
                    type="text"
                    placeholder={t("consoleBrandingLogoClickPlaceholder")}
                    value={consoleLogoDraftClickURL}
                    disabled={!canEditConsoleBranding}
                    onChange={(event) => setConsoleLogoDraftClickURL(event.target.value)}
                  />
                  <p className="muted">{t("consoleBrandingLogoClickHint")}</p>

                  <div className="action-row console-branding-actions">
                    <button
                      onClick={() => saveConsoleBranding()}
                      disabled={
                        !canEditConsoleBranding ||
                        (consoleLogoDraftDataURL === consoleLogoDataURL &&
                          normalizeConsoleLogoClickURL(consoleLogoDraftClickURL) ===
                            normalizeConsoleLogoClickURL(consoleLogoClickURL))
                      }
                    >
                      {t("consoleBrandingSave")}
                    </button>
                    <button
                      className="secondary"
                      onClick={resetConsoleBranding}
                      disabled={!canEditConsoleBranding || !consoleLogoDataURL}
                    >
                      {t("consoleBrandingReset")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="panel console-dashboard-section-panel console-main-nav-order-panel">
                <h4>{t("consoleMainNavOrderTitle")}</h4>
                <p className="muted">{t("consoleMainNavOrderHint")}</p>
                <div className="console-dashboard-order-list">
                  {orderedMainNavSettingsItems.map((item) => (
                    <div
                      key={item.key}
                      className={
                        mainNavDraggingKey === item.key
                          ? "console-dashboard-order-item dragging"
                          : "console-dashboard-order-item"
                      }
                      draggable={canEditConsoleBranding}
                      onDragStart={() => setMainNavDraggingKey(item.key)}
                      onDragEnd={() => setMainNavDraggingKey("")}
                      onDragOver={(event) => {
                        if (!canEditConsoleBranding || !mainNavDraggingKey) return;
                        event.preventDefault();
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        if (!canEditConsoleBranding || !mainNavDraggingKey) return;
                        moveMainNavDraft(mainNavDraggingKey, item.key);
                        setMainNavDraggingKey("");
                      }}
                    >
                      <span className="console-dashboard-order-handle" aria-hidden="true">
                        ⋮⋮
                      </span>
                      <div className="console-dashboard-order-copy">
                        <strong>{item.title}</strong>
                        <span className="muted">{item.hint}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="action-row console-branding-actions">
                  <button
                    onClick={() => saveMainNavOrder()}
                    disabled={!canEditConsoleBranding || !mainNavOrderDirty}
                  >
                    {t("consoleMainNavOrderSave")}
                  </button>
                  <button
                    className="secondary"
                    onClick={() => {
                      setMainNavDraftOrder([...DEFAULT_MAIN_NAV_ORDER]);
                      setMainNavDraggingKey("");
                    }}
                    disabled={!canEditConsoleBranding}
                  >
                    {t("consoleMainNavOrderReset")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {isConsoleDashboardPage && canViewConsoleDashboard && (
            <div className="panel">
              <div className="panel-heading">
                <h3>{t("consoleDashboardTitle")}</h3>
              </div>
              <p className="muted">{t("consoleDashboardPersonalHint")}</p>

              <div className="panel console-dashboard-section-panel console-dashboard-order-panel">
                <h4>{t("consoleDashboardOrderTitle")}</h4>
                <p className="muted">{t("consoleDashboardOrderHint")}</p>
                <div className="console-dashboard-order-list">
                  {orderedDashboardSectionItems.map((item) => (
                    <div
                      key={item.key}
                      className={
                        dashboardSectionDraggingKey === item.key
                          ? "console-dashboard-order-item dragging"
                          : "console-dashboard-order-item"
                      }
                      draggable={canEditConsoleDashboard}
                      onDragStart={() => setDashboardSectionDraggingKey(item.key)}
                      onDragEnd={() => setDashboardSectionDraggingKey("")}
                      onDragOver={(event) => {
                        if (!canEditConsoleDashboard || !dashboardSectionDraggingKey) return;
                        event.preventDefault();
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        if (!canEditConsoleDashboard || !dashboardSectionDraggingKey) return;
                        moveDashboardSectionDraft(dashboardSectionDraggingKey, item.key);
                        setDashboardSectionDraggingKey("");
                      }}
                    >
                      <span className="console-dashboard-order-handle" aria-hidden="true">
                        ⋮⋮
                      </span>
                      <div className="console-dashboard-order-copy">
                        <strong>{item.title}</strong>
                        <span className="muted">{item.hint}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="panel console-dashboard-section-panel console-dashboard-content-panel">
                <h4>{t("consoleDashboardContentTitle")}</h4>
                <p className="muted console-dashboard-selection-hint">
                  {t("consoleDashboardHint")}
                </p>
                <div className="console-dashboard-settings-list">
                  {orderedDashboardSectionItems.map((item) => (
                    <div className="console-dashboard-setting-group" key={item.key}>
                      <label className="console-dashboard-setting">
                        <input
                          type="checkbox"
                          checked={Boolean(dashboardSectionDraftSettings[item.key])}
                          disabled={!canEditConsoleDashboard}
                          onChange={(event) =>
                            setDashboardSectionDraftSettings((prev) => ({
                              ...prev,
                              [item.key]: event.target.checked
                            }))
                          }
                        />
                        <div className="console-dashboard-setting-copy">
                          <strong>{item.title}</strong>
                          <span className="muted">{item.hint}</span>
                        </div>
                      </label>
                      {Array.isArray(item.children) && item.children.length > 0 && (
                        <div className="console-dashboard-setting-children">
                          {item.children.map((child) => (
                            <label className="console-dashboard-setting-child" key={child.key}>
                              <input
                                type="checkbox"
                                checked={Boolean(dashboardSectionDraftSettings[child.key])}
                                disabled={!canEditConsoleDashboard}
                                onChange={(event) =>
                                  setDashboardSectionDraftSettings((prev) => ({
                                    ...prev,
                                    [child.key]: event.target.checked
                                  }))
                                }
                              />
                              <span>{child.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="action-row console-branding-actions">
                <button
                  onClick={() => saveConsoleDashboardSettings()}
                  disabled={!canEditConsoleDashboard || !dashboardSettingsDirty}
                >
                  {t("consoleDashboardSave")}
                </button>
                <button
                  className="secondary"
                  onClick={() => {
                    setDashboardSectionDraftSettings({
                      ...DEFAULT_DASHBOARD_SECTION_SETTINGS
                    });
                    setDashboardSectionDraftOrder([
                      ...DEFAULT_DASHBOARD_SECTION_ORDER
                    ]);
                    setDashboardSectionDraggingKey("");
                  }}
                  disabled={!canEditConsoleDashboard}
                >
                  {t("consoleDashboardReset")}
                </button>
              </div>
            </div>
          )}

          {isOrganizationPage && canViewOrganization && renderOrganizationAdmin()}

          {isAgentPage && canViewAgent && (
            <div className="panel">
              <div className="panel-heading">
                <h3>{t("agentSettingsTitle")}</h3>
                <button onClick={saveAgentSettings} disabled={!canEditAgent}>
                  {t("save")}
                </button>
              </div>
              <p>{t("agentSettingsHint")}</p>

              <div className="panel">
                <h4>{t("agentSettingsBrandingTitle")}</h4>
                <div className="modal-grid">
                  <label>{t("agentSettingsOrganizationName")}</label>
                  <input
                    className="text-input"
                    value={agentBrandingDraft.organization_name || ""}
                    disabled={!canEditAgent}
                    onChange={(event) =>
                      setAgentBrandingDraft((prev) => ({ ...prev, organization_name: event.target.value }))
                    }
                  />
                  <label>{t("agentSettingsPrimaryColor")}</label>
                  <input
                    className="text-input"
                    type="text"
                    value={agentBrandingDraft.primary_color || ""}
                    disabled={!canEditAgent}
                    onChange={(event) =>
                      setAgentBrandingDraft((prev) => ({ ...prev, primary_color: event.target.value }))
                    }
                  />
                  <label>{t("agentSettingsLogoDataURL")}</label>
                  <textarea
                    className="text-input"
                    rows={3}
                    value={agentBrandingDraft.logo_data_url || ""}
                    disabled={!canEditAgent}
                    onChange={(event) =>
                      setAgentBrandingDraft((prev) => ({ ...prev, logo_data_url: event.target.value }))
                    }
                  />
                  <label>{t("agentSettingsSupportEmail")}</label>
                  <input
                    className="text-input"
                    value={agentBrandingDraft.support_email || ""}
                    disabled={!canEditAgent}
                    onChange={(event) =>
                      setAgentBrandingDraft((prev) => ({ ...prev, support_email: event.target.value }))
                    }
                  />
                  <label>{t("agentSettingsSupportPhone")}</label>
                  <input
                    className="text-input"
                    value={agentBrandingDraft.support_phone || ""}
                    disabled={!canEditAgent}
                    onChange={(event) =>
                      setAgentBrandingDraft((prev) => ({ ...prev, support_phone: event.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="panel">
                <div className="panel-heading">
                  <h4>{t("agentSettingsServicesTitle")}</h4>
                  <button
                    className="secondary"
                    disabled={!canEditAgent}
                    onClick={() => setAgentServicesDraft((prev) => [...prev, createEmptyAgentService()])}
                  >
                    {t("agentSettingsAddService")}
                  </button>
                </div>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("agentSettingsEnabled")}</th>
                        <th>{t("agentSettingsServiceTitle")}</th>
                        <th>{t("description")}</th>
                        <th>URL</th>
                        <th>{t("agentSettingsIcon")}</th>
                        <th>{t("agentSettingsCategory")}</th>
                        <th>{t("tableAction")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentServicesDraft.map((item, index) => (
                        <tr key={`${item.id || "new"}-${index}`}>
                          <td>
                            <input
                              type="checkbox"
                              checked={item.is_enabled !== false}
                              disabled={!canEditAgent}
                              onChange={(event) => updateAgentServiceDraft(index, { is_enabled: event.target.checked })}
                            />
                          </td>
                          <td>
                            <input
                              className="text-input compact-input"
                              value={item.title || ""}
                              disabled={!canEditAgent}
                              onChange={(event) => updateAgentServiceDraft(index, { title: event.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              className="text-input compact-input"
                              value={item.description || ""}
                              disabled={!canEditAgent}
                              onChange={(event) => updateAgentServiceDraft(index, { description: event.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              className="text-input compact-input"
                              value={item.url || ""}
                              disabled={!canEditAgent}
                              onChange={(event) => updateAgentServiceDraft(index, { url: event.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              className="text-input compact-input"
                              value={item.icon || "link"}
                              disabled={!canEditAgent}
                              onChange={(event) => updateAgentServiceDraft(index, { icon: event.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              className="text-input compact-input"
                              value={item.category || "service"}
                              disabled={!canEditAgent}
                              onChange={(event) => updateAgentServiceDraft(index, { category: event.target.value })}
                            />
                          </td>
                          <td>
                            <button
                              className="danger"
                              disabled={!canEditAgent}
                              onClick={() =>
                                setAgentServicesDraft((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
                              }
                            >
                              {t("delete")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {agentServicesDraft.length === 0 && <p className="empty">{t("agentSettingsNoServices")}</p>}
              </div>

            </div>
          )}

          {isConsoleUsersPage && canViewConsoleUsers && (
            <>
              <div className="panel">
                <div className="panel-heading">
                  <h3>{t("consoleUsersTitle")}</h3>
                  {canEditConsoleUsers && (
                    <button onClick={() => setShowCreateUserModal(true)}>{t("consoleUsersAdd")}</button>
                  )}
                </div>
                <p>{t("consoleUsersHint")}</p>
                <div className="devices-table-wrap console-users-table-wrap">
                  <table className="console-users-table">
                    <colgroup>
                      <col className="console-users-col-name" />
                      <col className="console-users-col-login" />
                      <col className="console-users-col-email" />
                      <col className="console-users-col-role" />
                      <col className="console-users-col-password" />
                      <col className="console-users-col-mfa" />
                      <col className="console-users-col-status" />
                      <col className="console-users-col-actions" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>{t("consoleUsersName")}</th>
                        <th>{t("consoleUsersLogin")}</th>
                        <th>{t("consoleUsersEmail")}</th>
                        <th>{t("consoleUsersRole")}</th>
                        <th>{t("consoleUsersMustChange")}</th>
                        <th>{t("consoleUsersMFA")}</th>
                        <th>Status</th>
                        <th>{t("tenantsLifecycle")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consoleUsers.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <button className="console-user-link" onClick={() => openEditConsoleUserModal(item)}>
                              {item.display_name || [item.first_name, item.last_name].filter(Boolean).join(" ") || "-"}
                            </button>
                          </td>
                          <td>{item.login || "-"}</td>
                          <td>{item.email || "-"}</td>
                          <td>
                            {roleSettings.find(
                              (roleItem) =>
                                String(roleItem.role_key || "").toLowerCase() ===
                                String(item.role || "").toLowerCase()
                            )?.display_name || formatConsoleRole(item.role, t)}
                          </td>
                          <td>{item.must_change_password ? t("yes") : t("no")}</td>
                          <td>
                            {item.passkey_enabled
                              ? "Passkey"
                              : item.mfa_required
                              ? item.mfa_enabled
                                ? t("mfaEnabledRequired")
                                : t("mfaRequiredNotConfigured")
                              : item.mfa_enabled
                                ? t("mfaEnabled")
                                : t("no")}
                          </td>
                          <td>{item.is_active ? t("active") : t("revoked")}</td>
                          <td>
                            {canEditConsoleUsers ? (
                              <div className="action-row">
                                <button className="secondary" onClick={() => openEditConsoleUserModal(item)}>
                                  {t("consoleUsersEdit")}
                                </button>
                                {item.is_active ? (
                                  <button className="danger" onClick={() => revokeConsoleUser(item.id)}>
                                    {t("consoleUsersRevoke")}
                                  </button>
                                ) : (
                                  <span>-</span>
                                )}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {consoleUsers.length === 0 && <p className="empty">{t("consoleUsersNoItems")}</p>}
              </div>

              {canEditSecurity && (
                <>
                  <div className="panel">
                    <h4>{t("securityAdminPasswordTitle")}</h4>
                    <p>{t("securityAdminPasswordHint")}</p>
                    <form className="modal-grid" onSubmit={changeConsoleUserPassword}>
                      <label>{t("securitySelectUser")}</label>
                      <select
                        className="text-input"
                        value={securityAdminPasswordForm.consoleUserId}
                        onChange={(event) =>
                          setSecurityAdminPasswordForm((prev) => ({
                            ...prev,
                            consoleUserId: event.target.value
                          }))
                        }
                      >
                        <option value="">{t("securitySelectAnotherUser")}</option>
                        {consoleUsers
                          .filter(
                            (item) =>
                              item.is_active &&
                              String(item.id || "") !== String(session?.user?.id || "")
                          )
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.display_name || item.login} ({item.login})
                            </option>
                          ))}
                      </select>
                      <label>{t("newPassword")}</label>
                      <input
                        className="text-input"
                        type="password"
                        autoComplete="new-password"
                        value={securityAdminPasswordForm.newPassword}
                        onChange={(event) =>
                          setSecurityAdminPasswordForm((prev) => ({
                            ...prev,
                            newPassword: event.target.value
                          }))
                        }
                      />
                      <label>{t("confirmPassword")}</label>
                      <input
                        className="text-input"
                        type="password"
                        autoComplete="new-password"
                        value={securityAdminPasswordForm.confirmPassword}
                        onChange={(event) =>
                          setSecurityAdminPasswordForm((prev) => ({
                            ...prev,
                            confirmPassword: event.target.value
                          }))
                        }
                      />
                      <label className="check-inline">
                        <input
                          type="checkbox"
                          checked={securityAdminPasswordForm.mustChangePassword}
                          onChange={(event) =>
                            setSecurityAdminPasswordForm((prev) => ({
                              ...prev,
                              mustChangePassword: event.target.checked
                            }))
                          }
                        />
                        {t("securityRequirePasswordChange")}
                      </label>
                      <div className="action-row">
                        <button type="submit" disabled={loading}>
                          {t("changePassword")}
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="panel">
                    <h4>{t("securityAdminMFATitle")}</h4>
                    <p>{t("securityAdminMFAHint")}</p>
                    <form className="modal-grid" onSubmit={updateConsoleUserMFARequirement}>
                      <label>{t("securitySelectUser")}</label>
                      <select
                        className="text-input"
                        value={securityAdminMFAForm.consoleUserId}
                        onChange={(event) => {
                          const consoleUserId = event.target.value;
                          const selected = consoleUsers.find(
                            (item) => String(item.id || "") === consoleUserId
                          );
                          setSecurityAdminMFAForm({
                            consoleUserId,
                            required: Boolean(selected?.mfa_required)
                          });
                        }}
                      >
                        <option value="">{t("securitySelectUser")}</option>
                        {consoleUsers
                          .filter(
                            (item) =>
                              item.is_active &&
                              String(item.id || "") !== String(session?.user?.id || "")
                          )
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.display_name || item.login} ({item.login})
                            </option>
                          ))}
                      </select>
                      <label className="check-inline">
                        <input
                          type="checkbox"
                          checked={securityAdminMFAForm.required}
                          disabled={
                            String(
                              consoleUsers.find(
                                (item) =>
                                  String(item.id || "") ===
                                  securityAdminMFAForm.consoleUserId
                              )?.role || ""
                            ).toLowerCase() === "master-admin"
                          }
                          onChange={(event) =>
                            setSecurityAdminMFAForm((prev) => ({
                              ...prev,
                              required: event.target.checked
                            }))
                          }
                        />
                        {t("securityMFARequired")}
                      </label>
                      <div className="action-row">
                        <button type="submit" disabled={loading}>
                          {t("consoleUsersUpdate")}
                        </button>
                      </div>
                    </form>
                  </div>
                </>
              )}
            </>
          )}

          {isCertificatesPage && canViewCertificates && (
            <div className="panel">
              <h3>{t("certificatesTitle")}</h3>
              <p>{t("certificatesHint")}</p>
              <div className="panel">
                <h4>{t("certificatesConfiguratorTitle")}</h4>
                <p>{t("certificatesConfiguratorHint")}</p>
                <div className="configurator-service-grid">
                  <div>
                    <span>{t("certificatesConfiguratorServiceName")}</span>
                    <strong>{t("certificatesConfiguratorServiceNameValue")}</strong>
                  </div>
                  <div>
                    <span>{t("certificatesConfiguratorHostURL")}</span>
                    <strong>
                      {configuratorEnrollmentURL?.service_config_url ||
                        configuratorEnrollmentURL?.url ||
                        window.location.origin}
                    </strong>
                  </div>
                </div>
                <div className="configurator-url-box">
                  <span>{t("certificatesConfiguratorPublicURL")}</span>
                  <code>{configuratorEnrollmentURL?.url || t("certificatesConfiguratorURLMissing")}</code>
                  {configuratorEnrollmentURL?.token && (
                    <div className="configurator-url-meta">
                      <span>
                        {t("certificatesConfiguratorRotatedAt")}:{" "}
                        {formatTimestamp(configuratorEnrollmentURL.token.rotated_at)}
                      </span>
                      <span>
                        {t("certificatesConfiguratorLastUsed")}:{" "}
                        {configuratorEnrollmentURL.token.last_used_at
                          ? formatTimestamp(configuratorEnrollmentURL.token.last_used_at)
                          : "-"}
                      </span>
                    </div>
                  )}
                </div>
                <p className="muted-text">{t("certificatesConfiguratorURLHint")}</p>
                <div className="action-row">
                  <button
                    type="button"
                    onClick={copyConfiguratorEnrollmentURL}
                    disabled={!configuratorEnrollmentURL?.url}
                  >
                    {t("certificatesConfiguratorCopyURL")}
                  </button>
                  <button
                    type="button"
                    onClick={rotateConfiguratorEnrollmentURL}
                    disabled={!canEditCertificates}
                  >
                    {t("certificatesConfiguratorRotateURL")}
                  </button>
                  <button
                    type="button"
                    onClick={downloadConfiguratorEnrollmentProfile}
                    disabled={!canEditCertificates}
                  >
                    {t("certificatesDownloadConfiguratorProfile")}
                  </button>
                </div>
              </div>
              <div className="panel">
                <h4>{t("certificatesMacOSAgentTitle")}</h4>
                <p>{t("certificatesMacOSAgentHint")}</p>
                <div className="configurator-url-box">
                  <span>{t("certificatesMacOSAgentPackageURL")}</span>
                  <code>
                    {configuratorEnrollmentURL?.macos_agent_package_url ||
                      `${window.location.origin}/macos-agent/package/TurukMacOSAgent.pkg`}
                  </code>
                </div>
                {!configuratorEnrollmentURL?.macos_agent_available && (
                  <p className="warning-text">{t("certificatesMacOSAgentUnavailable")}</p>
                )}
                {configuratorEnrollmentURL?.macos_agent_available && (
                  <div className="configurator-service-grid">
                    <div>
                      <span>{t("certificatesMacOSAgentSigned")}</span>
                      <strong>{configuratorEnrollmentURL?.macos_agent_package?.signed ? (lang === "ru" ? "Да" : "Yes") : (lang === "ru" ? "Нет" : "No")}</strong>
                    </div>
                    <div>
                      <span>{t("certificatesMacOSAgentNotarized")}</span>
                      <strong>{configuratorEnrollmentURL?.macos_agent_package?.notarized ? (lang === "ru" ? "Да" : "Yes") : (lang === "ru" ? "Нет" : "No")}</strong>
                    </div>
                    <div>
                      <span>{t("certificatesMacOSAgentVersion")}</span>
                      <strong>{configuratorEnrollmentURL?.macos_agent_package?.version || "-"}</strong>
                    </div>
                    <div>
                      <span>{t("certificatesMacOSAgentSHA256")}</span>
                      <strong className="monospace-value">{configuratorEnrollmentURL?.macos_agent_package?.sha256 || "-"}</strong>
                    </div>
                  </div>
                )}
                <div className="action-row">
                  <button
                    type="button"
                    onClick={downloadMacOSAgentPackage}
                    disabled={!configuratorEnrollmentURL?.macos_agent_available}
                  >
                    {t("certificatesDownloadMacOSAgentPackage")}
                  </button>
                </div>
              </div>
              <div className="panel apns-panel">
                <h4>{t("certificatesAPNSTitle")}</h4>
                <p>{t("certificatesAPNSHint")}</p>
                <p className="warning-text">{t("certificatesAPNSRenewSafety")}</p>
                <div className="apns-status-grid">
                  <div className={`apns-status-card ${mdmAPNSStatus.className}`}>
                    <span className="apns-status-label">{t("certificatesAPNSStatus")}</span>
                    <strong>{mdmAPNSStatus.label}</strong>
                    <span className="apns-value">{mdmAPNSStatus.detail}</span>
                  </div>
                  <div className="apns-status-card">
                    <span className="apns-status-label">{t("certificatesAPNSSource")}</span>
                    <strong>{mdmAPNSSourceLabel(mdmAPNSConfig, t)}</strong>
                    <span className="apns-value">
                      {t("certificatesAPNSCSRState")}:{" "}
                      {mdmAPNSConfig?.csr_ready
                        ? t("certificatesAPNSCSRReady")
                        : mdmAPNSConfig?.source === "global" && mdmAPNSConfig?.configured
                          ? t("certificatesAPNSCSRRenewOnly")
                          : t("certificatesAPNSCSRNotReady")}
                    </span>
                  </div>
                  {mdmAPNSConfig?.topic && (
                    <div className="apns-status-card">
                      <span className="apns-status-label">{t("certificatesAPNSTopic")}</span>
                      <code className="apns-value">{mdmAPNSConfig.topic}</code>
                    </div>
                  )}
                  {mdmAPNSConfig?.cert_not_after && (
                    <div className="apns-status-card">
                      <span className="apns-status-label">{t("certificatesAPNSExpires")}</span>
                      <strong>{formatTimestamp(mdmAPNSConfig.cert_not_after)}</strong>
                      {mdmAPNSExpiry && (
                        <span className="apns-value">
                          {t("certificatesAPNSDaysLeft")}: {mdmAPNSExpiry.daysLeft}
                        </span>
                      )}
                    </div>
                  )}
                  {mdmAPNSConfig?.apple_id && (
                    <div className="apns-status-card">
                      <span className="apns-status-label">{t("certificatesAPNSAppleID")}</span>
                      <span className="apns-value">{mdmAPNSConfig.apple_id}</span>
                    </div>
                  )}
                  {mdmAPNSConfig?.cert_subject && (
                    <div className="apns-status-card apns-status-card-wide">
                      <span className="apns-status-label">{t("certificatesAPNSCertSubject")}</span>
                      <span className="apns-value">{mdmAPNSConfig.cert_subject}</span>
                    </div>
                  )}
                </div>
                <div className="apns-steps">
                  <div className="apns-step">
                    <strong>{t("certificatesAPNSStep1")}</strong>
                    <div className="button-row">
                      <button
                        type="button"
                        onClick={createAndDownloadMDMAPNSCSR}
                        disabled={!canEditCertificates}
                      >
                        {t("certificatesAPNSCreateCSR")}
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        onClick={downloadMDMAPNSCSR}
                        disabled={!canViewCertificates || !mdmAPNSConfig?.csr_ready}
                      >
                        {t("certificatesAPNSDownloadCSR")}
                      </button>
                    </div>
                  </div>
                  <div className="apns-step">
                    <strong>{t("certificatesAPNSStep2")}</strong>
                    <a
                      className="button-link secondary"
                      href="https://identity.apple.com/pushcert/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("certificatesAPNSApplePortal")}
                    </a>
                  </div>
                  <form className="modal-grid apns-step" onSubmit={saveMDMAPNSConfig}>
                    <strong className="modal-grid-wide">{t("certificatesAPNSStep3")}</strong>
                    <label>{t("certificatesAPNSAppleID")}</label>
                    <input
                      className="text-input"
                      value={mdmAPNSForm.appleID}
                      disabled={!canEditCertificates}
                      onChange={(event) =>
                        setMDMAPNSForm((prev) => ({ ...prev, appleID: event.target.value }))
                      }
                    />
                    <label>{t("certificatesAPNSCert")}</label>
                    <input
                      type="file"
                      accept=".pem,.cer,.crt"
                      disabled={!canEditCertificates}
                      onChange={handleMDMAPNSCertificateFile}
                    />
                    {mdmAPNSConfig?.topic ? (
                      <div className="apns-topic-guard modal-grid-wide">
                        <strong>{t("certificatesAPNSExpectedTopic")}</strong>
                        <code>{mdmAPNSConfig.topic}</code>
                        <span>{t("certificatesAPNSUploadGuard")}</span>
                      </div>
                    ) : null}
                    <div />
                    <textarea
                      className="text-input"
                      rows={6}
                      value={mdmAPNSForm.certPEM}
                      disabled={!canEditCertificates}
                      onChange={(event) =>
                        setMDMAPNSForm((prev) => ({ ...prev, certPEM: event.target.value }))
                      }
                    />
                    <div />
                    <button type="submit" disabled={!canEditCertificates || !mdmAPNSConfig?.csr_ready}>
                      {t("certificatesAPNSSave")}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {isADEPage && canViewADE && (
            <div className="panel">
              <div className="panel-heading">
                <div>
                  <h3>{t("adeTitle")}</h3>
                  <p>{t("adeHint")}</p>
                </div>
                <button type="button" onClick={syncADEDevices} disabled={!canEditADE || !adeStatus?.configured}>
                  {t("adeSyncNow")}
                </button>
              </div>

              <div className="apns-status-grid">
                <div className={`apns-status-card ${adeStatus?.configured ? "ready" : "warning"}`}>
                  <span className="apns-status-label">{t("certificatesAPNSStatus")}</span>
                  <strong>{adeStatus?.configured ? t("adeConfigured") : t("adeNotConfigured")}</strong>
                  <span className="apns-value">
                    {adeStatus?.configured ? t("adeTokenTitle") : t("adeUploadToken")}
                  </span>
                </div>
                {adeStatus?.organization_name && (
                  <div className="apns-status-card">
                    <span className="apns-status-label">{t("adeOrganization")}</span>
                    <strong>{adeStatus.organization_name}</strong>
                  </div>
                )}
                {adeStatus?.server_uuid && (
                  <div className="apns-status-card">
                    <span className="apns-status-label">{t("adeServerUUID")}</span>
                    <code className="apns-value">{adeStatus.server_uuid}</code>
                  </div>
                )}
                {adeStatus?.token_expires_at && (
                  <div className="apns-status-card">
                    <span className="apns-status-label">{t("adeTokenExpires")}</span>
                    <strong>{formatTimestamp(adeStatus.token_expires_at)}</strong>
                  </div>
                )}
                {adeStatus?.last_sync_at && (
                  <div className="apns-status-card">
                    <span className="apns-status-label">{t("adeLastSync")}</span>
                    <strong>{formatTimestamp(adeStatus.last_sync_at)}</strong>
                    <span className="apns-value">{adeStatus.last_sync_status || "-"}</span>
                  </div>
                )}
              </div>

              <div className="panel">
                <h4>{t("adeTokenTitle")}</h4>
                <p className="muted">{t("adeUploadHint")}</p>
                <form className="modal-grid" onSubmit={uploadADEServerToken}>
                  <label>{t("adeAppleID")}</label>
                  <input
                    className="text-input"
                    value={adeTokenForm.appleID}
                    disabled={!canEditADE}
                    onChange={(event) =>
                      setADETokenForm((prev) => ({ ...prev, appleID: event.target.value }))
                    }
                  />
                  <label>{t("adeTokenFile")}</label>
                  <input
                    type="file"
                    accept=".p7m,application/pkcs7-mime,application/x-pkcs7-mime"
                    disabled={!canEditADE}
                    onChange={handleADEServerTokenFile}
                  />
                  {adeStatus?.server_token_filename && (
                    <>
                      <label>{t("adeTokenFilename")}</label>
                      <span>{adeStatus.server_token_filename}</span>
                    </>
                  )}
                  {adeStatus?.server_token_uploaded_at && (
                    <>
                      <label>{t("adeUploadedAt")}</label>
                      <span>{formatTimestamp(adeStatus.server_token_uploaded_at)}</span>
                    </>
                  )}
                  <div />
                  <button type="submit" disabled={!canEditADE || !adeTokenForm.file}>
                    {t("adeUploadToken")}
                  </button>
                </form>
              </div>

              <div className="panel">
                <h4>{t("adeProfilesTitle")}</h4>
                <form className="modal-grid" onSubmit={createADEProfile}>
                  <label>{t("adeProfileName")}</label>
                  <input
                    className="text-input"
                    value={adeProfileForm.name}
                    disabled={!canEditADE}
                    onChange={(event) =>
                      setADEProfileForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                  />
                  <label>{t("adeProfileURL")}</label>
                  <input
                    className="text-input"
                    value={adeProfileForm.url}
                    disabled={!canEditADE}
                    placeholder="https://mdm.turuk.pro/enroll/mobileconfig?token=..."
                    onChange={(event) =>
                      setADEProfileForm((prev) => ({ ...prev, url: event.target.value }))
                    }
                  />
                  <label>{t("adeDefaultProfile")}</label>
                  <input
                    type="checkbox"
                    checked={adeProfileForm.isDefault}
                    disabled={!canEditADE}
                    onChange={(event) =>
                      setADEProfileForm((prev) => ({ ...prev, isDefault: event.target.checked }))
                    }
                  />
                  <div />
                  <button type="submit" disabled={!canEditADE || !adeProfileForm.name.trim()}>
                    {t("adeCreateProfile")}
                  </button>
                </form>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("adeProfileName")}</th>
                        <th>UUID</th>
                        <th>{t("adeProfileURL")}</th>
                        <th>{t("adeDefaultProfile")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adeProfiles.map((profile) => (
                        <tr key={profile.id || profile.profile_uuid}>
                          <td>{profile.name || "-"}</td>
                          <td><code>{profile.profile_uuid || "-"}</code></td>
                          <td className="wrap-cell">{profile.url || "-"}</td>
                          <td>{profile.is_default ? t("yes") : t("no")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="panel">
                <h4>{t("adeDevicesTitle")}</h4>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("adeSerialNumber")}</th>
                        <th>{t("adeModel")}</th>
                        <th>{t("description")}</th>
                        <th>{t("adeProfile")}</th>
                        <th>{t("adeProfileStatus")}</th>
                        <th>{t("adeLastSeen")}</th>
                        <th>{t("tableAction")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adeDevices.map((device) => (
                        <tr key={device.id || device.serial_number}>
                          <td>{device.serial_number || "-"}</td>
                          <td>{device.model || "-"}</td>
                          <td>{device.description || "-"}</td>
                          <td>{device.profile_uuid || "-"}</td>
                          <td>{device.profile_status || "-"}</td>
                          <td>{device.last_seen_at ? formatTimestamp(device.last_seen_at) : "-"}</td>
                          <td>
                            <select
                              className="text-input compact-input"
                              value={device.profile_uuid || ""}
                              disabled={!canEditADE || adeProfiles.length === 0}
                              onChange={(event) =>
                                assignADEProfile(device.serial_number, event.target.value)
                              }
                            >
                              <option value="">-</option>
                              {adeProfiles.map((profile) => (
                                <option key={profile.profile_uuid} value={profile.profile_uuid}>
                                  {profile.name || profile.profile_uuid}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {adeDevices.length === 0 && <p className="empty">{t("adeNoDevices")}</p>}
              </div>

              <div className="panel">
                <h4>{lang === "ru" ? "История синхронизации ADE" : "ADE sync history"}</h4>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("appsColStatus")}</th>
                        <th>{lang === "ru" ? "Сообщение" : "Message"}</th>
                        <th>{lang === "ru" ? "Администратор" : "Admin"}</th>
                        <th>{t("appsColUpdatedAt")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adeSyncEvents.map((item) => (
                        <tr key={item.id}>
                          <td>{item.status || "-"}</td>
                          <td>{item.message || "-"}</td>
                          <td>{item.created_by || "-"}</td>
                          <td>{formatTimestamp(item.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {adeSyncEvents.length === 0 && <p className="empty">{lang === "ru" ? "Истории пока нет." : "No history yet."}</p>}
              </div>
            </div>
          )}

          {isVPPPage && canViewVPP && (
            <div className="panel">
              <div className="panel-heading">
                <div>
                  <h3>{t("vppTitle")}</h3>
                  <p>{t("vppHint")}</p>
                </div>
                <button type="button" onClick={syncVPPAssets} disabled={!canEditVPP || !vppStatus?.configured}>
                  {t("vppSyncNow")}
                </button>
              </div>

              <div className="apns-status-grid">
                <div className={`apns-status-card ${vppStatus?.configured ? "ready" : "warning"}`}>
                  <span className="apns-status-label">{t("certificatesAPNSStatus")}</span>
                  <strong>{vppStatus?.configured ? t("vppConfigured") : t("vppNotConfigured")}</strong>
                  <span className="apns-value">
                    {vppStatus?.configured ? t("vppTokenTitle") : t("vppUploadToken")}
                  </span>
                </div>
                {(vppStatus?.organization_name || vppStatus?.location_name) && (
                  <div className="apns-status-card">
                    <span className="apns-status-label">{t("vppOrganization")}</span>
                    <strong>{vppStatus.location_name || vppStatus.organization_name}</strong>
                    {vppStatus.location_id && <span className="apns-value">{vppStatus.location_id}</span>}
                  </div>
                )}
                {vppStatus?.token_expires_at && (
                  <div className="apns-status-card">
                    <span className="apns-status-label">{t("vppTokenExpires")}</span>
                    <strong>{formatTimestamp(vppStatus.token_expires_at)}</strong>
                  </div>
                )}
                {vppStatus?.last_sync_at && (
                  <div className="apns-status-card">
                    <span className="apns-status-label">{t("vppLastSync")}</span>
                    <strong>{formatTimestamp(vppStatus.last_sync_at)}</strong>
                    <span className="apns-value">{vppStatus.last_sync_status || "-"}</span>
                  </div>
                )}
                <div className="apns-status-card">
                  <span className="apns-status-label">{t("vppAssetsTitle")}</span>
                  <strong>{vppAssets.length}</strong>
                  <span className="apns-value">{t("vppAssignmentsTitle")}: {vppAssignments.length}</span>
                </div>
              </div>

              <div className="panel">
                <div className="panel-heading">
                  <div>
                    <h4>{lang === "ru" ? "Books / media" : "Books / media"}</h4>
                    <p className="muted">
                      {lang === "ru"
                        ? "Книги и медиаконтент Apple Books лучше назначать централизованно через Apps and Books assets и группы. Разовые команды InstallMedia/RemoveMedia скрыты из устройства, чтобы не путать обычных администраторов."
                        : "Apple Books and media are best assigned centrally through Apps and Books assets and groups. One-off InstallMedia/RemoveMedia commands are hidden from the device page to keep routine admin workflows clear."}
                    </p>
                  </div>
                </div>
                <div className="callout neutral">
                  {lang === "ru"
                    ? "Синхронизируйте Apps and Books, затем в таблице assets ниже выберите группу и назначьте лицензию. Этот workflow масштабируется на группы устройств и подходит для SaaS-клиентов."
                    : "Sync Apps and Books, then use the assets table below to choose a group and assign the license. This workflow scales to device groups and fits SaaS tenants."}
                </div>
              </div>

              <div className="panel">
                <h4>{t("vppTokenTitle")}</h4>
                <p className="muted">{t("vppUploadHint")}</p>
                <form className="modal-grid" onSubmit={uploadVPPToken}>
                  <label>{t("vppAppleID")}</label>
                  <input
                    className="text-input"
                    value={vppTokenForm.appleID}
                    disabled={!canEditVPP}
                    onChange={(event) =>
                      setVPPTokenForm((prev) => ({ ...prev, appleID: event.target.value }))
                    }
                  />
                  <label>{t("vppTokenFile")}</label>
                  <input
                    type="file"
                    accept=".vpptoken,.txt,.json"
                    disabled={!canEditVPP}
                    onChange={handleVPPTokenFile}
                  />
                  {vppStatus?.token_filename && (
                    <>
                      <label>{t("vppTokenFilename")}</label>
                      <span>{vppStatus.token_filename}</span>
                    </>
                  )}
                  {vppStatus?.token_uploaded_at && (
                    <>
                      <label>{t("adeUploadedAt")}</label>
                      <span>{formatTimestamp(vppStatus.token_uploaded_at)}</span>
                    </>
                  )}
                  <div />
                  <button type="submit" disabled={!canEditVPP || !vppTokenForm.file}>
                    {t("vppUploadToken")}
                  </button>
                </form>
              </div>

              <div className="panel">
                <h4>{t("vppAssetsTitle")}</h4>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("appsColName")}</th>
                        <th>Adam ID</th>
                        <th>{t("appsColBundleID")}</th>
                        <th>{t("vppLicensesTotal")}</th>
                        <th>{t("vppLicensesAvailable")}</th>
                        <th>{t("vppLicensesAssigned")}</th>
                        <th>{t("groupsTitle")}</th>
                        <th>{t("tenantsLifecycle")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vppAssets.map((asset) => {
                        const adamID = String(asset.adam_id || "");
                        return (
                          <tr key={asset.id || adamID}>
                            <td>{asset.title || "-"}</td>
                            <td>{adamID || "-"}</td>
                            <td>{asset.bundle_id || "-"}</td>
                            <td>{asset.total_licenses ?? 0}</td>
                            <td>{asset.available_licenses ?? 0}</td>
                            <td>{asset.assigned_licenses ?? 0}</td>
                            <td>
                              <select
                                className="text-input compact-input"
                                value={vppAssignDrafts[adamID] || ""}
                                disabled={!canEditVPP || groups.length === 0}
                                onChange={(event) =>
                                  setVPPAssignDrafts((prev) => ({ ...prev, [adamID]: event.target.value }))
                                }
                              >
                                <option value="">-</option>
                                {groups.map((group) => (
                                  <option key={group.id} value={group.id}>
                                    {group.name || group.group_name || group.id}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              {canEditVPP ? (
                                <div className="action-row">
                                  <button
                                    type="button"
                                    className="secondary"
                                    onClick={() => assignVPPLicense(asset)}
                                    disabled={!vppAssignDrafts[adamID]}
                                  >
                                    {t("vppAssignLicense")}
                                  </button>
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={() => revokeVPPLicense(asset)}
                                    disabled={!vppAssignDrafts[adamID]}
                                  >
                                    {t("vppRevokeLicense")}
                                  </button>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {vppAssets.length === 0 && <p className="empty">{t("vppNoAssets")}</p>}
              </div>

              <div className="panel">
                <h4>{t("vppAssignmentsTitle")}</h4>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Adam ID</th>
                        <th>{t("groupsTitle")}</th>
                        <th>{t("appsColStatus")}</th>
                        <th>{t("appsColUpdatedAt")}</th>
                        <th>{t("vppLastError")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vppAssignments.map((item) => (
                        <tr key={item.id}>
                          <td>{item.adam_id || "-"}</td>
                          <td>{item.group_name || item.group_id || "-"}</td>
                          <td>{item.status || "-"}</td>
                          <td>{item.updated_at ? formatTimestamp(item.updated_at) : "-"}</td>
                          <td>{item.last_error || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {vppAssignments.length === 0 && <p className="empty">{t("vppNoAssignments")}</p>}
              </div>

              <div className="panel">
                <h4>{lang === "ru" ? "История синхронизации VPP" : "VPP sync history"}</h4>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("appsColStatus")}</th>
                        <th>{lang === "ru" ? "Сообщение" : "Message"}</th>
                        <th>{lang === "ru" ? "Администратор" : "Admin"}</th>
                        <th>{t("appsColUpdatedAt")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vppSyncEvents.map((item) => (
                        <tr key={item.id}>
                          <td>{item.status || "-"}</td>
                          <td>{item.message || "-"}</td>
                          <td>{item.created_by || "-"}</td>
                          <td>{formatTimestamp(item.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {vppSyncEvents.length === 0 && <p className="empty">{lang === "ru" ? "Истории пока нет." : "No history yet."}</p>}
              </div>
            </div>
          )}

          {isNotificationsPage && canViewAlertSettings && (
            <div className="panel">
              <div className="panel-heading">
                <div>
                  <h3>{t("alertSettingsTitle")}</h3>
                  <p>{t("alertSettingsHint")}</p>
                </div>
                <span className={alertNotificationSettings.enabled ? "status-pill ok" : "status-pill muted"}>
                  {alertNotificationSettings.enabled ? t("enabled") : t("disabled")}
                </span>
              </div>

              <div className="alert-settings-grid">
                <div className="alert-settings-block">
                  <h4>{t("alertSettingsChannels")}</h4>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(alertNotificationDraft.enabled)}
                      disabled={!canEditAlertSettings}
                      onChange={(event) =>
                        setAlertNotificationDraft((prev) => ({ ...prev, enabled: event.target.checked }))
                      }
                    />
                    {t("alertSettingsEnabled")}
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(alertNotificationDraft.in_app_enabled)}
                      disabled={!canEditAlertSettings}
                      onChange={(event) =>
                        setAlertNotificationDraft((prev) => ({ ...prev, in_app_enabled: event.target.checked }))
                      }
                    />
                    {t("alertSettingsInApp")}
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(alertNotificationDraft.email_enabled)}
                      disabled={!canEditAlertSettings}
                      onChange={(event) =>
                        setAlertNotificationDraft((prev) => ({ ...prev, email_enabled: event.target.checked }))
                      }
                    />
                    {t("alertSettingsEmail")}
                  </label>
                  <textarea
                    className="text-input"
                    rows={3}
                    value={(alertNotificationDraft.email_recipients || []).join(", ")}
                    disabled={!canEditAlertSettings}
                    placeholder="admin@example.com, security@example.com"
                    onChange={(event) =>
                      setAlertNotificationDraft((prev) => ({
                        ...prev,
                        email_recipients: event.target.value
                          .split(",")
                          .map((item) => item.trim())
                          .filter(Boolean)
                      }))
                    }
                  />
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(alertNotificationDraft.webhook_enabled)}
                      disabled={!canEditAlertSettings}
                      onChange={(event) =>
                        setAlertNotificationDraft((prev) => ({ ...prev, webhook_enabled: event.target.checked }))
                      }
                    />
                    {t("alertSettingsWebhook")}
                  </label>
                  <input
                    className="text-input"
                    value={alertNotificationDraft.webhook_url || ""}
                    disabled={!canEditAlertSettings}
                    placeholder="https://example.com/turuk-alerts"
                    onChange={(event) =>
                      setAlertNotificationDraft((prev) => ({ ...prev, webhook_url: event.target.value }))
                    }
                  />
                </div>

                <div className="alert-settings-block">
                  <h4>{t("alertSettingsTriggers")}</h4>
                  {[
                    "alert_apns_expiring",
                    "alert_ade_sync_failed",
                    "alert_vpp_sync_failed",
                    "alert_device_offline",
                    "alert_enrollment_spike",
                    "alert_suspicious_admin_login",
                    "alert_management_profile_removed"
                  ].map((key) => (
                    <label key={key} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={Boolean(alertNotificationDraft[key])}
                        disabled={!canEditAlertSettings}
                        onChange={(event) =>
                          setAlertNotificationDraft((prev) => ({ ...prev, [key]: event.target.checked }))
                        }
                      />
                      {alertTriggerLabel(key)}
                    </label>
                  ))}
                </div>

                <div className="alert-settings-block">
                  <h4>{t("alertSettingsThresholds")}</h4>
                  <label className="form-label">
                    {t("alertSettingsAPNSDays")}
                    <input
                      className="text-input"
                      type="number"
                      min="1"
                      max="365"
                      value={alertNotificationDraft.apns_expiry_days}
                      disabled={!canEditAlertSettings}
                      onChange={(event) =>
                        setAlertNotificationDraft((prev) => ({
                          ...prev,
                          apns_expiry_days: event.target.value
                        }))
                      }
                    />
                  </label>
                  <label className="form-label">
                    {t("alertSettingsDeviceOfflineDays")}
                    <input
                      className="text-input"
                      type="number"
                      min="1"
                      max="365"
                      value={alertNotificationDraft.device_offline_days}
                      disabled={!canEditAlertSettings}
                      onChange={(event) =>
                        setAlertNotificationDraft((prev) => ({
                          ...prev,
                          device_offline_days: event.target.value
                        }))
                      }
                    />
                  </label>
                  <label className="form-label">
                    {t("alertSettingsEnrollmentWindow")}
                    <input
                      className="text-input"
                      type="number"
                      min="5"
                      max="1440"
                      value={alertNotificationDraft.enrollment_spike_window_minutes}
                      disabled={!canEditAlertSettings}
                      onChange={(event) =>
                        setAlertNotificationDraft((prev) => ({
                          ...prev,
                          enrollment_spike_window_minutes: event.target.value
                        }))
                      }
                    />
                  </label>
                  <label className="form-label">
                    {t("alertSettingsEnrollmentThreshold")}
                    <input
                      className="text-input"
                      type="number"
                      min="1"
                      value={alertNotificationDraft.enrollment_spike_threshold}
                      disabled={!canEditAlertSettings}
                      onChange={(event) =>
                        setAlertNotificationDraft((prev) => ({
                          ...prev,
                          enrollment_spike_threshold: event.target.value
                        }))
                      }
                    />
                  </label>
                </div>
              </div>

              {canEditAlertSettings && (
                <div className="action-row ddm-heading-actions">
                  <button onClick={saveAlertNotificationSettings}>{t("save")}</button>
                  <button className="secondary" onClick={evaluateAlertNotifications}>
                    {t("alertSettingsEvaluate")}
                  </button>
                </div>
              )}

              <div className="panel compact-panel">
                <div className="panel-heading">
                  <h4>{t("alertSettingsOpenAlerts")}</h4>
                  <span className="status-pill muted">{alertNotifications.length}</span>
                </div>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("alertSettingsSeverity")}</th>
                        <th>{t("alertSettingsAlert")}</th>
                        <th>{t("alertSettingsSource")}</th>
                        <th>{t("appsColUpdatedAt")}</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alertNotifications.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <span className={alertSeverityLabelClass(item.severity)}>
                              {item.severity || "-"}
                            </span>
                          </td>
                          <td>
                            <strong>{item.title || item.event_type}</strong>
                            <p className="muted small-text">{item.message}</p>
                          </td>
                          <td>{item.source || item.event_type || "-"}</td>
                          <td>{formatTimestamp(item.last_seen_at)}</td>
                          <td>
                            {canEditAlertSettings ? (
                              <button className="secondary" onClick={() => acknowledgeAlertNotification(item.id)}>
                                {t("alertSettingsAcknowledge")}
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {alertNotifications.length === 0 && <p className="empty">{t("alertSettingsNoAlerts")}</p>}
              </div>
            </div>
          )}

          {isReportsPage && canViewReports && (
            <div className="panel">
              <div className="panel-heading">
                <div>
                  <h3>{t("reportsSettingsTitle")}</h3>
                  <p>{t("reportsSettingsHint")}</p>
                </div>
                <div className="devices-toolbar-actions">
                  <button className="toolbar-action secondary" onClick={() => downloadReport("executive", "pdf")}>
                    {t("reportsPDF")}
                  </button>
                  <button className="toolbar-action secondary" onClick={() => downloadReport("executive", "csv")}>
                    {t("reportsCSV")}
                  </button>
                </div>
              </div>

              <div className="alert-settings-grid">
                <div className="alert-settings-block">
                  <h4>{t("reportsScheduleTitle")}</h4>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(reportScheduleDraft.enabled)}
                      disabled={!canEditReports}
                      onChange={(event) =>
                        setReportScheduleDraft((prev) => ({ ...prev, enabled: event.target.checked }))
                      }
                    />
                    {t("reportsScheduleEnabled")}
                  </label>
                  <label className="form-label">
                    {t("reportsFrequency")}
                    <select
                      className="text-input"
                      value={reportScheduleDraft.frequency}
                      disabled={!canEditReports}
                      onChange={(event) =>
                        setReportScheduleDraft((prev) => ({ ...prev, frequency: event.target.value }))
                      }
                    >
                      <option value="daily">{t("reportsFrequencyDaily")}</option>
                      <option value="weekly">{t("reportsFrequencyWeekly")}</option>
                      <option value="monthly">{t("reportsFrequencyMonthly")}</option>
                    </select>
                  </label>
                </div>

                <div className="alert-settings-block">
                  <h4>{t("reportsContentTitle")}</h4>
                  <label className="form-label">
                    {t("reportsReportType")}
                    <select
                      className="text-input"
                      value={reportScheduleDraft.report_type}
                      disabled={!canEditReports}
                      onChange={(event) =>
                        setReportScheduleDraft((prev) => ({ ...prev, report_type: event.target.value }))
                      }
                    >
                      <option value="executive">{t("reportsExecutive")}</option>
                      <option value="devices">{t("reportsDevices")}</option>
                      <option value="accounts">{t("reportsAccounts")}</option>
                      <option value="groups">{t("reportsGroups")}</option>
                      <option value="apps">{t("reportsApps")}</option>
                    </select>
                  </label>
                  <label className="form-label">
                    {t("reportsFormat")}
                    <select
                      className="text-input"
                      value={reportScheduleDraft.format}
                      disabled={!canEditReports}
                      onChange={(event) =>
                        setReportScheduleDraft((prev) => ({ ...prev, format: event.target.value }))
                      }
                    >
                      <option value="pdf">PDF</option>
                      <option value="csv">CSV</option>
                    </select>
                  </label>
                </div>

                <div className="alert-settings-block">
                  <h4>{t("reportsDeliveryTitle")}</h4>
                  <label className="form-label">
                    {t("reportsRecipients")}
                    <textarea
                      className="text-input"
                      rows={5}
                      value={(reportScheduleDraft.recipients || []).join(", ")}
                      disabled={!canEditReports}
                      placeholder="admin@example.com, security@example.com"
                      onChange={(event) =>
                        setReportScheduleDraft((prev) => ({
                          ...prev,
                          recipients: event.target.value
                            .split(",")
                            .map((item) => item.trim())
                            .filter(Boolean)
                        }))
                      }
                    />
                  </label>
                  <p className="muted small-text">{t("reportsRecipientsHint")}</p>
                </div>
              </div>

              {canEditReports && (
                <div className="action-row">
                  <button onClick={saveReportSchedule}>{t("save")}</button>
                </div>
              )}
            </div>
          )}

          {isSSOPage && canViewSSO && (
            <div className="panel">
              <div className="panel-heading">
                <div>
                  <h3>{t("ssoSettingsTitle")}</h3>
                  <p>{t("ssoSettingsHint")}</p>
                </div>
                <span className={ssoConfig?.enabled ? "status-pill ok" : "status-pill muted"}>
                  {ssoConfig?.enabled ? t("ssoStatusEnabled") : t("ssoStatusDisabled")}
                </span>
              </div>

              <form className="modal-grid sso-settings-grid" onSubmit={saveSSOConfig}>
                <label>{t("ssoProviderPreset")}</label>
                <select
                  className="text-input"
                  value={ssoForm.sso_preset}
                  disabled={!canEditSSO}
                  onChange={(event) => {
                    const presetKey = event.target.value;
                    const preset = SSO_PROVIDER_PRESETS[presetKey] || SSO_PROVIDER_PRESETS.custom_oidc;
                    setSSOForm((prev) => ({
                      ...prev,
                      sso_preset: presetKey,
                      provider: preset.provider,
                      oidc_scopes: preset.provider === "oidc" ? preset.scopes : prev.oidc_scopes
                    }));
                  }}
                >
                  {Object.entries(SSO_PROVIDER_PRESETS).map(([key, preset]) => (
                    <option key={key} value={key}>
                      {lang === "ru" ? preset.labelRu : preset.labelEn}
                    </option>
                  ))}
                </select>

                <div className="sso-checkbox-stack">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(ssoForm.enabled)}
                      disabled={!canEditSSO}
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                    />
                    {t("ssoEnabled")}
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(ssoForm.enforce_sso)}
                      disabled={!canEditSSO}
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, enforce_sso: event.target.checked }))}
                    />
                    {t("ssoEnforce")}
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(ssoForm.auto_provision)}
                      disabled={!canEditSSO}
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, auto_provision: event.target.checked }))}
                    />
                    {t("ssoAutoProvision")}
                  </label>
                </div>

                {Boolean(ssoForm.enforce_sso) && (
                  <div className="warning-banner modal-grid-wide">
                    {t("ssoEnforceWarning")}
                  </div>
                )}

                {selectedSSOPreset.provider === "oidc" ? (
                  <>
                    <label>{t("ssoTenantIssuerURL")}</label>
                    <input
                      className="text-input"
                      value={ssoForm.oidc_issuer_url}
                      disabled={!canEditSSO}
                      placeholder={selectedSSOPreset.issuerPlaceholder}
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, oidc_issuer_url: event.target.value }))}
                    />

                    <label>{t("ssoOIDCClientID")}</label>
                    <input
                      className="text-input"
                      value={ssoForm.oidc_client_id}
                      disabled={!canEditSSO}
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, oidc_client_id: event.target.value }))}
                    />

                    <label>{t("ssoOIDCClientSecret")}</label>
                    <input
                      className="text-input"
                      type="password"
                      value={ssoForm.oidc_client_secret}
                      disabled={!canEditSSO}
                      placeholder={
                        ssoConfig?.oidc_client_secret_configured
                          ? t("ssoSecretConfiguredPlaceholder")
                          : t("ssoSecretEmptyPlaceholder")
                      }
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, oidc_client_secret: event.target.value }))}
                    />
                  </>
                ) : (
                  <>
                    <label>{t("ssoSAMLEntityID")}</label>
                    <input
                      className="text-input"
                      value={ssoForm.saml_entity_id}
                      disabled={!canEditSSO}
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, saml_entity_id: event.target.value }))}
                    />

                    <label>{t("ssoSAMLSSOURL")}</label>
                    <input
                      className="text-input"
                      value={ssoForm.saml_sso_url}
                      disabled={!canEditSSO}
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, saml_sso_url: event.target.value }))}
                    />

                    <label>{t("ssoSAMLX509Cert")}</label>
                    <textarea
                      className="text-input"
                      rows={4}
                      value={ssoForm.saml_x509_cert}
                      disabled={!canEditSSO}
                      onChange={(event) => setSSOForm((prev) => ({ ...prev, saml_x509_cert: event.target.value }))}
                    />
                  </>
                )}

                <label>{t("ssoAllowedDomains")}</label>
                <textarea
                  className="text-input"
                  rows={3}
                  value={ssoForm.allowed_domains_text}
                  disabled={!canEditSSO}
                  placeholder="example.com"
                  onChange={(event) =>
                    setSSOForm((prev) => ({ ...prev, allowed_domains_text: event.target.value }))
                  }
                />

                <label>{t("ssoDefaultRole")}</label>
                <select
                  className="text-input"
                  value={ssoForm.default_role}
                  disabled={!canEditSSO}
                  onChange={(event) => setSSOForm((prev) => ({ ...prev, default_role: event.target.value }))}
                >
                  {ssoRoleOptions.map((role) => (
                    <option key={role.role_key} value={role.role_key}>
                      {role.display_name || role.role_key}
                    </option>
                  ))}
                </select>

                <details className="sso-advanced-settings modal-grid-wide">
                  <summary>{t("ssoAdvancedSettings")}</summary>
                  <div className="sso-advanced-grid">
                    {selectedSSOPreset.provider === "oidc" && (
                      <>
                        <label>
                          {t("ssoOIDCScopes")}
                          <input
                            className="text-input"
                            value={ssoForm.oidc_scopes}
                            disabled={!canEditSSO}
                            onChange={(event) => setSSOForm((prev) => ({ ...prev, oidc_scopes: event.target.value }))}
                          />
                        </label>
                        <label>
                          {t("ssoOIDCAuthURL")}
                          <input
                            className="text-input"
                            value={ssoForm.oidc_auth_url}
                            disabled={!canEditSSO}
                            placeholder={t("ssoOptionalDiscoveryPlaceholder")}
                            onChange={(event) => setSSOForm((prev) => ({ ...prev, oidc_auth_url: event.target.value }))}
                          />
                        </label>
                        <label>
                          {t("ssoOIDCTokenURL")}
                          <input
                            className="text-input"
                            value={ssoForm.oidc_token_url}
                            disabled={!canEditSSO}
                            placeholder={t("ssoOptionalDiscoveryPlaceholder")}
                            onChange={(event) => setSSOForm((prev) => ({ ...prev, oidc_token_url: event.target.value }))}
                          />
                        </label>
                        <label>
                          {t("ssoOIDCUserinfoURL")}
                          <input
                            className="text-input"
                            value={ssoForm.oidc_userinfo_url}
                            disabled={!canEditSSO}
                            placeholder={t("ssoOptionalDiscoveryPlaceholder")}
                            onChange={(event) => setSSOForm((prev) => ({ ...prev, oidc_userinfo_url: event.target.value }))}
                          />
                        </label>
                      </>
                    )}
                    <label>
                      {t("ssoRedirectURIHint")}
                      <input className="text-input" value={`${window.location.origin}/api/v1/auth/sso/callback`} readOnly />
                    </label>
                  </div>
                </details>

                <div />
                <div className="action-row">
                  <button type="submit" disabled={!canEditSSO}>
                    {t("ssoSave")}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setSSOForm(ssoConfigToForm(ssoConfig))}
                    disabled={!canEditSSO}
                  >
                    {t("reset")}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={testSSOLogin}
                    disabled={!ssoConfig?.enabled}
                  >
                    {t("ssoTest")}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={downloadSSOMetadata}
                    disabled={selectedSSOPreset.provider !== "saml"}
                  >
                    {lang === "ru" ? "Скачать SAML metadata" : "Download SAML metadata"}
                  </button>
                </div>
              </form>

              <p className="muted">{t("ssoBreakGlassHint")}</p>
            </div>
          )}

          {isDeclarativePage && canViewDeclarative && (
            <div className="panel">
              <div className="panel-heading">
                <div>
                  <h3>{isPlatformAdmin ? (lang === "ru" ? "Техническая консоль DDM" : "DDM Debug Console") : t("settingsWorkspaceDeclarative")}</h3>
                  <p>
                    {isPlatformAdmin
                      ? (lang === "ru"
                          ? "Техническая консоль деклараций, активаций, ресурсов, условий, канала статусов и журнала протокола."
                          : "Technical console for declarations, activations, assets, predicates, status channel, and protocol captures.")
                      : (lang === "ru"
                          ? "Автоматические политики Apple для обновлений ОС, требований к паролю и управляемых сценариев."
                          : "Automatic Apple policies for OS updates, passcode requirements, and managed workflows.")}
                  </p>
                </div>
                <div className="action-row">
                  {isPlatformAdmin && (
                    <button type="button" className="secondary" onClick={importLegacyProfilesToDDM} disabled={!canEditDeclarative}>
                      {lang === "ru" ? "Импорт старых профилей" : "Import legacy profiles"}
                    </button>
                  )}
                  <button type="button" onClick={syncDDMDeclarations} disabled={!canEditDeclarative}>
                    {lang === "ru" ? "Синхронизировать" : "Sync"}
                  </button>
                </div>
              </div>

              {hasActiveDDMSoftwareUpdatePolicy(ddmDeclarations) && (
                <div className="warning-banner">
                  {lang === "ru"
                    ? "Активна автоматическая политика обновления ОС. Обычные команды ручного обновления будут заблокированы, чтобы не создавать конфликт."
                    : "An automatic OS update policy is active. Manual OS update commands are blocked to avoid conflicts."}
                </div>
              )}

              {!isPlatformAdmin && (
                <>
              <form className="modal-grid apple-policy-form" onSubmit={saveDDMDeclaration}>
                <label>{lang === "ru" ? "Сценарий" : "Scenario"}</label>
                <select
                  className="text-input"
                  value={["software_update", "passcode"].includes(ddmForm.template) ? ddmForm.template : "software_update"}
                  disabled={!canEditDeclarative}
                  onChange={(event) => applyDDMTemplate(event.target.value)}
                >
                  <option value="software_update">{lang === "ru" ? "Обновления iOS/iPadOS/macOS" : "iOS/iPadOS/macOS updates"}</option>
                  <option value="passcode">{lang === "ru" ? "Требования к паролю" : "Passcode requirements"}</option>
                  <option value="managed_apps" disabled>{lang === "ru" ? "Управляемые приложения через DDM, позже" : "Managed apps with DDM, later"}</option>
                </select>

                <label>{lang === "ru" ? "Название политики" : "Policy name"}</label>
                <input
                  className="text-input"
                  value={ddmForm.display_name}
                  disabled={!canEditDeclarative}
                  onChange={(event) => setDDMForm((prev) => ({ ...prev, display_name: event.target.value }))}
                />

                {ddmForm.template === "software_update" && (
                  <>
                    <label>{lang === "ru" ? "Минимальная версия ОС" : "Minimum OS version"}</label>
                    <input
                      className="text-input"
                      value={ddmForm.fields?.targetOSVersion || ""}
                      disabled={!canEditDeclarative}
                      placeholder="18.8"
                      onChange={(event) => updateDDMField("targetOSVersion", event.target.value)}
                    />
                    <label>{lang === "ru" ? "Установить до даты" : "Install by"}</label>
                    <input
                      className="text-input"
                      type="datetime-local"
                      value={ddmForm.fields?.targetLocalDateTime || ""}
                      disabled={!canEditDeclarative}
                      onChange={(event) => updateDDMField("targetLocalDateTime", event.target.value)}
                    />
                    <label>{lang === "ru" ? "Разрешить отложить, раз" : "Allowed deferrals"}</label>
                    <input
                      className="text-input"
                      type="number"
                      min="0"
                      max="99"
                      value={ddmForm.fields?.maximumUserDeferrals || ""}
                      disabled={!canEditDeclarative}
                      onChange={(event) => updateDDMField("maximumUserDeferrals", event.target.value)}
                    />
                    <label>{lang === "ru" ? "Установить как можно скорее" : "Install as soon as possible"}</label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={Boolean(ddmForm.fields?.installASAP)}
                        disabled={!canEditDeclarative}
                        onChange={(event) => updateDDMField("installASAP", event.target.checked)}
                      />
                      {t("enabled")}
                    </label>
                  </>
                )}

                {ddmForm.template === "passcode" && (
                  <>
                    <label>{lang === "ru" ? "Минимальная длина" : "Minimum length"}</label>
                    <input
                      className="text-input"
                      type="number"
                      min="4"
                      max="64"
                      value={ddmForm.fields?.minimumLength || ""}
                      disabled={!canEditDeclarative}
                      onChange={(event) => updateDDMField("minimumLength", event.target.value)}
                    />
                    <label>{lang === "ru" ? "Требовать буквы и цифры" : "Require alphanumeric passcode"}</label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={Boolean(ddmForm.fields?.requireAlphanumeric)}
                        disabled={!canEditDeclarative}
                        onChange={(event) => updateDDMField("requireAlphanumeric", event.target.checked)}
                      />
                      {t("enabled")}
                    </label>
                    <label>{lang === "ru" ? "Максимум неудачных попыток" : "Maximum failed attempts"}</label>
                    <input
                      className="text-input"
                      type="number"
                      min="2"
                      max="20"
                      value={ddmForm.fields?.maximumFailedAttempts || ""}
                      disabled={!canEditDeclarative}
                      onChange={(event) => updateDDMField("maximumFailedAttempts", event.target.value)}
                    />
                  </>
                )}

                <label>{lang === "ru" ? "Применить к" : "Apply to"}</label>
                <div className="assignment-panel">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(ddmForm.all_devices)}
                      disabled={!canEditDeclarative}
                      onChange={(event) => setDDMForm((prev) => ({ ...prev, all_devices: event.target.checked }))}
                    />
                    {lang === "ru" ? "Все устройства" : "All devices"}
                  </label>
                  <div className="checkbox-grid">
                    {groups.map((group) => (
                      <label key={group.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(ddmForm.group_ids || []).includes(group.id)}
                          disabled={!canEditDeclarative || ddmForm.all_devices}
                          onChange={(event) =>
                            setDDMForm((prev) => ({
                              ...prev,
                              group_ids: event.target.checked
                                ? [...(prev.group_ids || []), group.id]
                                : (prev.group_ids || []).filter((id) => id !== group.id)
                            }))
                          }
                        />
                        {group.name}
                      </label>
                    ))}
                  </div>
                </div>

                <label>{lang === "ru" ? "Политика активна" : "Policy active"}</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={Boolean(ddmForm.is_active)}
                    disabled={!canEditDeclarative}
                    onChange={(event) => setDDMForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                  />
                  {ddmForm.is_active ? t("enabled") : t("disabled")}
                </label>

                <div />
                <div className="action-row">
                  <button type="submit" disabled={!canEditDeclarative}>
                    {ddmForm.id ? t("save") : lang === "ru" ? "Создать политику" : "Create policy"}
                  </button>
                  <button type="button" className="secondary" onClick={() => setDDMForm(createEmptyDDMForm())}>
                    {t("reset")}
                  </button>
                </div>
              </form>

              <div className="panel compact-panel">
                <h4>{lang === "ru" ? "Статус применения политики на устройствах" : "Policy application status"}</h4>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{lang === "ru" ? "Политика" : "Policy"}</th>
                        <th>{lang === "ru" ? "Сценарий" : "Scenario"}</th>
                        <th>{lang === "ru" ? "Требование" : "Requirement"}</th>
                        <th>{lang === "ru" ? "Назначение" : "Assignment"}</th>
                        <th>{lang === "ru" ? "Статус" : "Status"}</th>
                        <th>{t("appsColUpdatedAt")}</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleApplePolicyDeclarations.map((item) => (
                        <tr key={item.id}>
                          <td><strong>{item.display_name || item.identifier}</strong></td>
                          <td>{ddmPolicyKindLabel(item, lang)}</td>
                          <td>{ddmPolicyTargetSummary(item, lang)}</td>
                          <td>{ddmAssignmentSummary(item.assignments, lang)}</td>
                          <td>{ddmPolicyApplicationStatus(item, ddmStatuses, lang)}</td>
                          <td>{formatTimestamp(item.updated_at)}</td>
                          <td>
                            <button type="button" className="secondary" onClick={() => editDDMDeclaration(item)}>
                              {lang === "ru" ? "Изменить" : "Edit"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {visibleApplePolicyDeclarations.length === 0 && (
                  <p className="empty">
                    {lang === "ru" ? "Автоматические политики Apple пока не созданы." : "No automatic Apple policies yet."}
                  </p>
                )}
              </div>
                </>
              )}

              {isPlatformAdmin && (
                <>
                  <div className="section-divider">
                    <span>{lang === "ru" ? "Расширенные / Диагностика" : "Advanced / Diagnostics"}</span>
                  </div>

              <form className="modal-grid" onSubmit={saveDDMDeclaration}>
                <label>{lang === "ru" ? "Шаблон" : "Template"}</label>
                <select
                  className="text-input"
                  value={ddmForm.template}
                  disabled={!canEditDeclarative}
                  onChange={(event) => applyDDMTemplate(event.target.value)}
                >
                  {DDM_TEMPLATE_ORDER.map((key) => (
                    <option key={key} value={key}>
                      {lang === "ru" ? DDM_PAYLOAD_TEMPLATES[key].labelRu : DDM_PAYLOAD_TEMPLATES[key].labelEn}
                    </option>
                  ))}
                  <option value="custom">{lang === "ru" ? "Свой JSON" : "Custom JSON"}</option>
                </select>

                <label>{lang === "ru" ? "Тип декларации" : "Declaration type"}</label>
                <select
                  className="text-input"
                  value={ddmForm.declaration_type}
                  disabled={!canEditDeclarative}
                  onChange={(event) => setDDMForm((prev) => ({ ...prev, declaration_type: event.target.value, template: "custom" }))}
                >
                  <option value="configuration">{lang === "ru" ? "Конфигурация" : "configuration"}</option>
                  <option value="asset">{lang === "ru" ? "Ресурс" : "asset"}</option>
                  <option value="activation">{lang === "ru" ? "Активация" : "activation"}</option>
                  <option value="management">{lang === "ru" ? "Управление" : "management"}</option>
                </select>

                <label>{lang === "ru" ? "Канал" : "Channel"}</label>
                <select
                  className="text-input"
                  value={ddmForm.channel || "device"}
                  disabled={!canEditDeclarative}
                  onChange={(event) => setDDMForm((prev) => ({ ...prev, channel: event.target.value }))}
                >
                  <option value="device">{lang === "ru" ? "Устройство" : "device"}</option>
                  <option value="user">{lang === "ru" ? "Пользователь" : "user"}</option>
                </select>

                <label>{lang === "ru" ? "Название" : "Name"}</label>
                <input
                  className="text-input"
                  value={ddmForm.display_name}
                  disabled={!canEditDeclarative}
                  onChange={(event) => setDDMForm((prev) => ({ ...prev, display_name: event.target.value }))}
                />

                <label>{lang === "ru" ? "Идентификатор" : "Identifier"}</label>
                <input
                  className="text-input"
                  value={ddmForm.identifier}
                  disabled={!canEditDeclarative}
                  onChange={(event) => setDDMForm((prev) => ({ ...prev, identifier: event.target.value }))}
                />

                {ddmForm.template !== "custom" && (
                  <>
                    <label>{lang === "ru" ? "Поля декларации" : "Declaration fields"}</label>
                    <div className="assignment-panel">
                      {["software_update"].includes(ddmForm.template) && (
                        <div className="modal-grid">
                          <label>{lang === "ru" ? "Версия ОС" : "OS version"}</label>
                          <input className="text-input" value={ddmForm.fields?.targetOSVersion || ""} onChange={(event) => updateDDMField("targetOSVersion", event.target.value)} disabled={!canEditDeclarative} />
                          <label>{lang === "ru" ? "Сборка ОС" : "Build"}</label>
                          <input className="text-input" value={ddmForm.fields?.targetBuildVersion || ""} onChange={(event) => updateDDMField("targetBuildVersion", event.target.value)} disabled={!canEditDeclarative} />
                          <label>{lang === "ru" ? "Установить как можно скорее" : "Install ASAP"}</label>
                          <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.installASAP)} onChange={(event) => updateDDMField("installASAP", event.target.checked)} disabled={!canEditDeclarative} />{t("enabled")}</label>
                        </div>
                      )}
                      {["software_update_settings"].includes(ddmForm.template) && (
                        <div className="modal-grid">
                          <label>{lang === "ru" ? "Автоматические действия" : "Automatic actions"}</label>
                          <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.automaticActions)} onChange={(event) => updateDDMField("automaticActions", event.target.checked)} disabled={!canEditDeclarative} />{t("enabled")}</label>
                          <label>{lang === "ru" ? "Программа beta-обновлений" : "Beta program"}</label>
                          <input className="text-input" value={ddmForm.fields?.betaProgram || ""} onChange={(event) => updateDDMField("betaProgram", event.target.value)} disabled={!canEditDeclarative} />
                          <label>{lang === "ru" ? "Макс. отложенных установок" : "Maximum deferrals"}</label>
                          <input className="text-input" type="number" value={ddmForm.fields?.maximumUserDeferrals || ""} onChange={(event) => updateDDMField("maximumUserDeferrals", event.target.value)} disabled={!canEditDeclarative} />
                        </div>
                      )}
                      {ddmForm.template === "passcode" && (
                        <div className="modal-grid">
                          <label>{lang === "ru" ? "Минимальная длина" : "Minimum length"}</label>
                          <input className="text-input" type="number" value={ddmForm.fields?.minimumLength || ""} onChange={(event) => updateDDMField("minimumLength", event.target.value)} disabled={!canEditDeclarative} />
                          <label>{lang === "ru" ? "Буквы и цифры" : "Alphanumeric"}</label>
                          <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.requireAlphanumeric)} onChange={(event) => updateDDMField("requireAlphanumeric", event.target.checked)} disabled={!canEditDeclarative} />{t("enabled")}</label>
                          <label>{lang === "ru" ? "Макс. неудачных попыток" : "Maximum failed attempts"}</label>
                          <input className="text-input" type="number" value={ddmForm.fields?.maximumFailedAttempts || ""} onChange={(event) => updateDDMField("maximumFailedAttempts", event.target.value)} disabled={!canEditDeclarative} />
                        </div>
                      )}
                      {ddmForm.template === "safari" && (
                        <div className="checkbox-grid">
                          <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.allowJavaScript)} onChange={(event) => updateDDMField("allowJavaScript", event.target.checked)} disabled={!canEditDeclarative} />JavaScript</label>
                          <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.allowPopups)} onChange={(event) => updateDDMField("allowPopups", event.target.checked)} disabled={!canEditDeclarative} />Popups</label>
                          <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.allowAutoFill)} onChange={(event) => updateDDMField("allowAutoFill", event.target.checked)} disabled={!canEditDeclarative} />AutoFill</label>
                        </div>
                      )}
                      {["certificate_asset", "identity_asset"].includes(ddmForm.template) && (
                        <div className="modal-grid">
                          <label>{lang === "ru" ? "Ссылка ресурса" : "Reference"}</label>
                          <input className="text-input" value={ddmForm.fields?.reference || ""} onChange={(event) => updateDDMField("reference", event.target.value)} disabled={!canEditDeclarative} />
                          <label>{ddmForm.template === "certificate_asset" ? (lang === "ru" ? "Сертификат PEM" : "Certificate PEM") : (lang === "ru" ? "Учетные данные P12" : "Identity P12")}</label>
                          <textarea className="text-input" rows={5} value={ddmForm.template === "certificate_asset" ? ddmForm.fields?.certificatePEM || "" : ddmForm.fields?.identityP12 || ""} onChange={(event) => updateDDMField(ddmForm.template === "certificate_asset" ? "certificatePEM" : "identityP12", event.target.value)} disabled={!canEditDeclarative} />
                          {ddmForm.template === "identity_asset" && (
                            <>
                              <label>{lang === "ru" ? "Пароль" : "Password"}</label>
                              <input className="text-input" type="password" value={ddmForm.fields?.password || ""} onChange={(event) => updateDDMField("password", event.target.value)} disabled={!canEditDeclarative} />
                            </>
                          )}
                        </div>
                      )}
                      {["mail", "exchange", "google", "ldap", "calendar", "contacts"].includes(ddmForm.template) && (
                        <div className="modal-grid">
                          <label>{lang === "ru" ? "Описание аккаунта" : "Account description"}</label>
                          <input className="text-input" value={ddmForm.fields?.accountDescription || ""} onChange={(event) => updateDDMField("accountDescription", event.target.value)} disabled={!canEditDeclarative} />
                          {["mail", "exchange", "google"].includes(ddmForm.template) && (
                            <>
                              <label>{lang === "ru" ? "E-mail" : "Email"}</label>
                              <input className="text-input" value={ddmForm.fields?.emailAddress || ""} onChange={(event) => updateDDMField("emailAddress", event.target.value)} disabled={!canEditDeclarative} />
                            </>
                          )}
                          {ddmForm.template !== "google" && (
                            <>
                              <label>{lang === "ru" ? "Сервер" : "Host"}</label>
                              <input className="text-input" value={ddmForm.fields?.hostName || ""} onChange={(event) => updateDDMField("hostName", event.target.value)} disabled={!canEditDeclarative} />
                            </>
                          )}
                          <label>{lang === "ru" ? "Пользователь" : "User name"}</label>
                          <input className="text-input" value={ddmForm.fields?.userName || ""} onChange={(event) => updateDDMField("userName", event.target.value)} disabled={!canEditDeclarative} />
                          {["mail", "exchange"].includes(ddmForm.template) && (
                            <>
                              <label>{lang === "ru" ? "Ресурс учетных данных" : "Identity asset"}</label>
                              <select className="text-input" value={ddmForm.fields?.identityAssetReference || ""} onChange={(event) => updateDDMField("identityAssetReference", event.target.value)} disabled={!canEditDeclarative}>
                                <option value="">-</option>
                                {ddmAssetDeclarations().map((asset) => <option key={asset.identifier} value={asset.asset_reference || asset.identifier}>{ddmAssetLabel(asset)}</option>)}
                              </select>
                            </>
                          )}
                          {["ldap", "calendar", "contacts"].includes(ddmForm.template) && (
                            <>
                              <label>{lang === "ru" ? "SSL" : "SSL"}</label>
                              <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.useSSL)} onChange={(event) => updateDDMField("useSSL", event.target.checked)} disabled={!canEditDeclarative} />{t("enabled")}</label>
                            </>
                          )}
                        </div>
                      )}
                      {ddmForm.template === "screen_sharing" && (
                        <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.enabled)} onChange={(event) => updateDDMField("enabled", event.target.checked)} disabled={!canEditDeclarative} />{lang === "ru" ? "Разрешить общий доступ к экрану" : "Enable screen sharing"}</label>
                      )}
                      {["legacy_profile", "legacy_interactive_profile"].includes(ddmForm.template) && (
                        <div className="modal-grid">
                          <label>{lang === "ru" ? "Идентификатор профиля" : "Profile identifier"}</label>
                          <input className="text-input" value={ddmForm.fields?.profileIdentifier || ""} onChange={(event) => updateDDMField("profileIdentifier", event.target.value)} disabled={!canEditDeclarative} />
                          <label>{lang === "ru" ? "URL профиля" : "Profile URL"}</label>
                          <input className="text-input" value={ddmForm.fields?.profileURL || ""} onChange={(event) => updateDDMField("profileURL", event.target.value)} disabled={!canEditDeclarative} />
                          {ddmForm.template === "legacy_interactive_profile" && (
                            <>
                              <label>{lang === "ru" ? "Требует участия пользователя" : "User interaction required"}</label>
                              <label className="checkbox-label"><input type="checkbox" checked={Boolean(ddmForm.fields?.userEnrollmentRequired)} onChange={(event) => updateDDMField("userEnrollmentRequired", event.target.checked)} disabled={!canEditDeclarative} />{t("enabled")}</label>
                            </>
                          )}
                        </div>
                      )}
                      {ddmForm.template === "management_properties" && (
                        <div className="modal-grid">
                          <label>{lang === "ru" ? "JSON свойств" : "Properties JSON"}</label>
                          <textarea className="text-input" rows={6} value={ddmForm.fields?.customPropertiesText || ""} onChange={(event) => updateDDMField("customPropertiesText", event.target.value)} disabled={!canEditDeclarative} />
                        </div>
                      )}
                      {ddmForm.template === "activation" && (
                        <div className="modal-grid">
                          <label>{lang === "ru" ? "Условие" : "Predicate"}</label>
                          <select className="text-input" value={ddmForm.fields?.predicateMode || "all"} onChange={(event) => updateDDMField("predicateMode", event.target.value)} disabled={!canEditDeclarative}>
                            <option value="all">{lang === "ru" ? "Всегда применять" : "TRUEPREDICATE"}</option>
                            <option value="os">{lang === "ru" ? "Версия ОС" : "OS version"}</option>
                            <option value="model">{lang === "ru" ? "Семейство модели" : "Model family"}</option>
                            <option value="shared_ipad">{lang === "ru" ? "Shared iPad" : "Shared iPad"}</option>
                            <option value="status">{lang === "ru" ? "Поле статуса" : "Status item"}</option>
                            <option value="management_property">{lang === "ru" ? "Свойство управления" : "Management property"}</option>
                            <option value="custom">{lang === "ru" ? "Свое условие" : "Custom predicate"}</option>
                          </select>
                          {ddmForm.fields?.predicateMode === "os" && <><label>{lang === "ru" ? "Версия ОС" : "OS version"}</label><input className="text-input" value={ddmForm.fields?.osVersion || ""} onChange={(event) => updateDDMField("osVersion", event.target.value)} disabled={!canEditDeclarative} /></>}
                          {ddmForm.fields?.predicateMode === "model" && <><label>{lang === "ru" ? "Семейство модели" : "Model family"}</label><input className="text-input" value={ddmForm.fields?.modelFamily || ""} onChange={(event) => updateDDMField("modelFamily", event.target.value)} disabled={!canEditDeclarative} /></>}
                          {ddmForm.fields?.predicateMode === "shared_ipad" && <><label>Shared iPad</label><select className="text-input" value={ddmForm.fields?.sharedIPad || "true"} onChange={(event) => updateDDMField("sharedIPad", event.target.value)} disabled={!canEditDeclarative}><option value="true">true</option><option value="false">false</option></select></>}
                          {ddmForm.fields?.predicateMode === "status" && <><label>{lang === "ru" ? "Поле статуса" : "Status item"}</label><input className="text-input" value={ddmForm.fields?.statusItem || ""} onChange={(event) => updateDDMField("statusItem", event.target.value)} disabled={!canEditDeclarative} /><label>{lang === "ru" ? "Значение" : "Value"}</label><input className="text-input" value={ddmForm.fields?.statusValue || ""} onChange={(event) => updateDDMField("statusValue", event.target.value)} disabled={!canEditDeclarative} /></>}
                          {ddmForm.fields?.predicateMode === "management_property" && <><label>{lang === "ru" ? "Свойство" : "Property"}</label><input className="text-input" value={ddmForm.fields?.managementProperty || ""} onChange={(event) => updateDDMField("managementProperty", event.target.value)} disabled={!canEditDeclarative} /><label>{lang === "ru" ? "Значение" : "Value"}</label><input className="text-input" value={ddmForm.fields?.managementPropertyValue || ""} onChange={(event) => updateDDMField("managementPropertyValue", event.target.value)} disabled={!canEditDeclarative} /></>}
                          {ddmForm.fields?.predicateMode === "custom" && <><label>{lang === "ru" ? "Строка условия" : "Predicate string"}</label><input className="text-input" value={ddmForm.fields?.predicate || ""} onChange={(event) => updateDDMField("predicate", event.target.value)} disabled={!canEditDeclarative} /></>}
                          <label>{lang === "ru" ? "Декларации для активации" : "Declarations to activate"}</label>
                          <textarea className="text-input" rows={5} value={ddmForm.fields?.declarationsText || ""} onChange={(event) => updateDDMField("declarationsText", event.target.value)} disabled={!canEditDeclarative} placeholder={lang === "ru" ? "Идентификаторы деклараций, по одному в строке" : "Declaration identifiers, one per line"} />
                        </div>
                      )}
                    </div>
                  </>
                )}

                <label>{lang === "ru" ? (ddmForm.template === "custom" ? "Payload JSON" : "Предпросмотр Payload JSON") : (ddmForm.template === "custom" ? "Payload JSON" : "Payload JSON preview")}</label>
                <textarea
                  className="text-input"
                  rows={12}
                  value={ddmForm.payload_text}
                  disabled={!canEditDeclarative || ddmForm.template !== "custom"}
                  onChange={(event) => setDDMForm((prev) => ({ ...prev, payload_text: event.target.value, template: "custom" }))}
                />

                <label>{lang === "ru" ? "Назначение" : "Assignment"}</label>
                <div className="assignment-panel">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(ddmForm.all_devices)}
                      disabled={!canEditDeclarative}
                      onChange={(event) => setDDMForm((prev) => ({ ...prev, all_devices: event.target.checked }))}
                    />
                    {lang === "ru" ? "Все устройства" : "All devices"}
                  </label>
                  <div className="checkbox-grid">
                    {groups.map((group) => (
                      <label key={group.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(ddmForm.group_ids || []).includes(group.id)}
                          disabled={!canEditDeclarative || ddmForm.all_devices}
                          onChange={(event) =>
                            setDDMForm((prev) => ({
                              ...prev,
                              group_ids: event.target.checked
                                ? [...(prev.group_ids || []), group.id]
                                : (prev.group_ids || []).filter((id) => id !== group.id)
                            }))
                          }
                        />
                        {group.name}
                      </label>
                    ))}
                  </div>
                  <textarea
                    className="text-input"
                    rows={4}
                    value={ddmForm.device_udids_text}
                    disabled={!canEditDeclarative || ddmForm.all_devices}
                    placeholder={lang === "ru" ? "UDID устройств, по одному в строке" : "Device UDIDs, one per line"}
                    onChange={(event) => setDDMForm((prev) => ({ ...prev, device_udids_text: event.target.value }))}
                  />
                </div>

                <label>{lang === "ru" ? "Активна" : "Active"}</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={Boolean(ddmForm.is_active)}
                    disabled={!canEditDeclarative}
                    onChange={(event) => setDDMForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                  />
                  {ddmForm.is_active ? t("enabled") : t("disabled")}
                </label>

                <div />
                <div className="action-row">
                  <button type="submit" disabled={!canEditDeclarative}>
                    {ddmForm.id ? t("save") : lang === "ru" ? "Создать декларацию" : "Create declaration"}
                  </button>
                  <button type="button" className="secondary" onClick={() => setDDMForm(createEmptyDDMForm())}>
                    {t("reset")}
                  </button>
                </div>
              </form>

              <div className="panel compact-panel">
                <h4>{lang === "ru" ? "Повторно используемые ресурсы" : "Reusable assets"}</h4>
                <p className="muted">
                  {lang === "ru"
                    ? "Сертификаты и учетные данные хранятся как отдельные ресурсы и могут обновляться независимо от конфигураций."
                    : "Certificates and identity credentials are stored as reusable objects and can be updated independently from configurations."}
                </p>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{lang === "ru" ? "Название" : "Name"}</th>
                        <th>{lang === "ru" ? "Тип" : "Kind"}</th>
                        <th>{lang === "ru" ? "Ссылка" : "Reference"}</th>
                        <th>{lang === "ru" ? "Канал" : "Channel"}</th>
                        <th>{t("appsColUpdatedAt")}</th>
                        <th>{t("tableAction")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ddmAssetDeclarations().map((asset) => (
                        <tr key={asset.id}>
                          <td>
                            <strong>{asset.display_name || asset.identifier}</strong>
                            <p className="muted small-text"><code>{asset.identifier}</code></p>
                          </td>
                          <td>{asset.asset_kind || "-"}</td>
                          <td><code>{asset.asset_reference || "-"}</code></td>
                          <td>{ddmChannelLabel(asset.channel || "device", lang)}</td>
                          <td>{formatTimestamp(asset.updated_at)}</td>
                          <td>
                            <button type="button" className="secondary" onClick={() => editDDMDeclaration(asset)}>
                              {lang === "ru" ? "Обновить ресурс" : "Update asset"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ddmAssetDeclarations().length === 0 && (
                  <p className="empty">{lang === "ru" ? "Повторно используемых ресурсов пока нет." : "No reusable assets yet."}</p>
                )}
              </div>

              <div className="panel compact-panel">
                <h4>{lang === "ru" ? "Декларации" : "Declarations"}</h4>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{lang === "ru" ? "Название" : "Name"}</th>
                        <th>{lang === "ru" ? "Тип" : "Type"}</th>
                        <th>{lang === "ru" ? "Канал" : "Channel"}</th>
                        <th>{lang === "ru" ? "Идентификатор" : "Identifier"}</th>
                        <th>{lang === "ru" ? "Ресурс" : "Asset"}</th>
                        <th>{lang === "ru" ? "Назначение" : "Assignment"}</th>
                        <th>{lang === "ru" ? "Токен" : "Token"}</th>
                        <th>{t("appsColUpdatedAt")}</th>
                        <th>{t("tableAction")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ddmDeclarations.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.display_name || item.identifier}</strong>
                            <p className="muted small-text">{item.is_active ? t("enabled") : t("disabled")}</p>
                          </td>
                          <td>{ddmDeclarationTypeLabel(item.declaration_type, lang)}</td>
                          <td>{ddmChannelLabel(item.channel || "device", lang)}</td>
                          <td><code>{item.identifier}</code></td>
                          <td>{item.declaration_type === "asset" ? `${item.asset_kind || "-"} ${item.asset_reference || ""}` : "-"}</td>
                          <td>{ddmAssignmentSummary(item.assignments, lang)}</td>
                          <td><code>{String(item.server_token || "").slice(0, 12)}</code></td>
                          <td>{formatTimestamp(item.updated_at)}</td>
                          <td>
                            <button type="button" className="secondary" onClick={() => editDDMDeclaration(item)}>
                              {lang === "ru" ? "Изменить" : "Edit"}
                            </button>
                            <button type="button" className="secondary" onClick={() => setDDMDeclarationActive(item, !item.is_active)} disabled={!canEditDeclarative}>
                              {item.is_active ? t("disabled") : t("enabled")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ddmDeclarations.length === 0 && (
                  <p className="empty">{lang === "ru" ? "Деклараций пока нет." : "No declarations yet."}</p>
                )}
              </div>

              <div className="panel compact-panel">
                <div className="panel-heading">
                  <div>
                    <h4>{lang === "ru" ? "Подписки на статусы" : "Status subscriptions"}</h4>
                    <p className="muted">
                      {lang === "ru"
                        ? "Какие данные устройство должно отправлять через канал статусов DDM."
                        : "Status objects the device should report through the DDM status channel."}
                    </p>
                  </div>
                  <button type="button" onClick={saveDDMStatusSubscriptions} disabled={!canEditDeclarative}>
                    {t("save")}
                  </button>
                </div>
                <div className="checkbox-grid">
                  {DDM_STATUS_SUBSCRIPTION_OPTIONS.map((item) => (
                    <label key={item.key} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={ddmStatusSubscriptions.includes(item.key)}
                        disabled={!canEditDeclarative}
                        onChange={(event) =>
                          setDDMStatusSubscriptions((prev) =>
                            event.target.checked
                              ? Array.from(new Set([...(prev || []), item.key]))
                              : (prev || []).filter((key) => key !== item.key)
                          )
                        }
                      />
                      {lang === "ru" ? item.labelRu : item.labelEn}
                    </label>
                  ))}
                </div>
              </div>

              <div className="panel compact-panel">
                <h4>{lang === "ru" ? "Канал статусов" : "Status channel"}</h4>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{lang === "ru" ? "Устройство" : "Device"}</th>
                        <th>UDID</th>
                        <th>{lang === "ru" ? "Токен статуса" : "Status token"}</th>
                        <th>{lang === "ru" ? "Отчет получен" : "Reported"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ddmStatuses.map((item) => (
                        <tr key={item.device_udid}>
                          <td>{item.device_name || item.serial_number || "-"}</td>
                          <td><code>{item.device_udid}</code></td>
                          <td><code>{String(item.status_token || "").slice(0, 16) || "-"}</code></td>
                          <td>{formatTimestamp(item.reported_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ddmStatuses.length === 0 && (
                  <p className="empty">{lang === "ru" ? "Отчетов о статусе пока нет." : "No status reports yet."}</p>
                )}
              </div>

              <div className="panel compact-panel">
                <div className="panel-heading">
                  <div>
                    <h4>{lang === "ru" ? "Журнал протокола DDM" : "DDM protocol capture"}</h4>
                    <p className="muted">
                      {lang === "ru"
                        ? "Последние реальные запросы устройств к /mdm/declarative/manifest, /declaration и /status."
                        : "Latest real device requests to /mdm/declarative/manifest, /declaration and /status."}
                    </p>
                  </div>
                  <button type="button" className="secondary" onClick={loadAllData}>
                    {t("refresh")}
                  </button>
                </div>
                <div className="devices-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{lang === "ru" ? "Время" : "Time"}</th>
                        <th>{lang === "ru" ? "Точка входа" : "Endpoint"}</th>
                        <th>{lang === "ru" ? "Метод" : "Method"}</th>
                        <th>UDID</th>
                        <th>{lang === "ru" ? "Канал" : "Channel"}</th>
                        <th>{lang === "ru" ? "Идентификатор" : "Identifier"}</th>
                        <th>{lang === "ru" ? "Запрос" : "Request"}</th>
                        <th>{lang === "ru" ? "Статус" : "Status"}</th>
                        <th>{lang === "ru" ? "Детали" : "Details"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ddmProtocolCaptures.map((item) => {
                        const expanded = expandedDDMProtocolCaptureID === item.id;
                        return (
                          <Fragment key={item.id}>
                            <tr>
                              <td>{formatTimestamp(item.created_at)}</td>
                              <td>{item.endpoint || "-"}</td>
                              <td>{item.method || "-"}</td>
                              <td><code>{item.device_udid || "-"}</code></td>
                              <td>{ddmChannelLabel(item.channel, lang)}</td>
                              <td><code>{item.identifier || "-"}</code></td>
                              <td>
                                <div className="ddm-capture-request-meta">
                                  <span>{item.request_content_type || "-"}</span>
                                  <code>{String(item.request_body_sha256 || "").slice(0, 12) || "-"}</code>
                                </div>
                              </td>
                              <td>{item.response_status || "-"}</td>
                              <td>
                                <button
                                  type="button"
                                  className="secondary"
                                  onClick={() => setExpandedDDMProtocolCaptureID(expanded ? "" : item.id)}
                                >
                                  {expanded ? t("close") : lang === "ru" ? "Открыть" : "Open"}
                                </button>
                              </td>
                            </tr>
                            {expanded && (
                              <tr>
                                <td colSpan={9}>
                                  <div className="ddm-capture-details">
                                    <div>
                                      <strong>{lang === "ru" ? "Заголовки запроса" : "Request headers"}</strong>
                                      <pre>{prettyDDMProtocolValue(item.request_headers)}</pre>
                                    </div>
                                    <div>
                                      <strong>{lang === "ru" ? "Тело запроса" : "Request body"}</strong>
                                      <pre>{prettyDDMProtocolValue(item.request_body || item.parsed_request)}</pre>
                                    </div>
                                    <div>
                                      <strong>{lang === "ru" ? "Ответ сервера" : "Response body"}</strong>
                                      <pre>{prettyDDMProtocolValue(item.response_body)}</pre>
                                    </div>
                                    {item.error_text && (
                                      <div>
                                        <strong>{lang === "ru" ? "Ошибка" : "Error"}</strong>
                                        <pre>{item.error_text}</pre>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {ddmProtocolCaptures.length === 0 && (
                  <p className="empty">
                    {lang === "ru"
                      ? "Запросов протокола DDM пока нет. После команды синхронизации устройство должно обратиться к точкам входа DDM."
                      : "No DDM protocol requests yet. After DeclarativeManagement is sent, the device should call the DDM endpoints."}
                  </p>
                )}
              </div>
                </>
              )}
            </div>
          )}

          {isSecurityPage && canViewSecurity && (
            <div className="panel">
              <h3>{t("securityTitle")}</h3>
              <p>{t("securityHint")}</p>

              <div className="panel">
                <h4>{lang === "ru" ? "Политика паролей и доверенные IP" : "Password policy and trusted IPs"}</h4>
                <form className="modal-grid" onSubmit={saveSecuritySettings}>
                  <label>{lang === "ru" ? "Минимальная длина" : "Minimum length"}</label>
                  <input
                    className="text-input"
                    type="number"
                    min="8"
                    max="128"
                    value={securitySettingsDraft?.password_policy?.min_length ?? 12}
                    disabled={!canEditSecurity}
                    onChange={(event) =>
                      setSecuritySettingsDraft((prev) => ({
                        ...prev,
                        password_policy: {
                          ...(prev?.password_policy || {}),
                          min_length: Number(event.target.value || 12)
                        }
                      }))
                    }
                  />
                  <label>{lang === "ru" ? "Срок действия пароля, дней" : "Password expires after, days"}</label>
                  <input
                    className="text-input"
                    type="number"
                    min="0"
                    max="3650"
                    value={securitySettingsDraft?.password_policy?.expires_days ?? 90}
                    disabled={!canEditSecurity}
                    onChange={(event) =>
                      setSecuritySettingsDraft((prev) => ({
                        ...prev,
                        password_policy: {
                          ...(prev?.password_policy || {}),
                          expires_days: Number(event.target.value || 0)
                        }
                      }))
                    }
                  />
                  <label>{lang === "ru" ? "Запрет повторного использования последних N паролей" : "Prevent reuse of last N passwords"}</label>
                  <input
                    className="text-input"
                    type="number"
                    min="0"
                    max="24"
                    value={securitySettingsDraft?.password_policy?.prevent_reuse ?? 5}
                    disabled={!canEditSecurity}
                    onChange={(event) =>
                      setSecuritySettingsDraft((prev) => ({
                        ...prev,
                        password_policy: {
                          ...(prev?.password_policy || {}),
                          prevent_reuse: Number(event.target.value || 0)
                        }
                      }))
                    }
                  />
                  {[
                    ["require_upper", lang === "ru" ? "Требовать uppercase" : "Require uppercase"],
                    ["require_lower", lang === "ru" ? "Требовать lowercase" : "Require lowercase"],
                    ["require_number", lang === "ru" ? "Требовать цифру" : "Require number"],
                    ["require_symbol", lang === "ru" ? "Требовать символ" : "Require symbol"]
                  ].map(([key, label]) => (
                    <label key={key} className="check-inline">
                      <input
                        type="checkbox"
                        checked={Boolean(securitySettingsDraft?.password_policy?.[key])}
                        disabled={!canEditSecurity}
                        onChange={(event) =>
                          setSecuritySettingsDraft((prev) => ({
                            ...prev,
                            password_policy: {
                              ...(prev?.password_policy || {}),
                              [key]: event.target.checked
                            }
                          }))
                        }
                      />
                      {label}
                    </label>
                  ))}
                  <h4>{lang === "ru" ? "Блокировка входа" : "Account lockout"}</h4>
                  <div />
                  <label>{lang === "ru" ? "Неудачных попыток до блокировки" : "Failed attempts before lockout"}</label>
                  <input
                    className="text-input"
                    type="number"
                    min="3"
                    max="50"
                    value={securitySettingsDraft?.account_lockout?.threshold ?? 8}
                    disabled={!canEditSecurity}
                    onChange={(event) =>
                      setSecuritySettingsDraft((prev) => ({
                        ...prev,
                        account_lockout: {
                          ...(prev?.account_lockout || {}),
                          threshold: Number(event.target.value || 8)
                        }
                      }))
                    }
                  />
                  <label>{lang === "ru" ? "Окно подсчета, минут" : "Counting window, minutes"}</label>
                  <input
                    className="text-input"
                    type="number"
                    min="1"
                    max="1440"
                    value={securitySettingsDraft?.account_lockout?.window_minutes ?? 15}
                    disabled={!canEditSecurity}
                    onChange={(event) =>
                      setSecuritySettingsDraft((prev) => ({
                        ...prev,
                        account_lockout: {
                          ...(prev?.account_lockout || {}),
                          window_minutes: Number(event.target.value || 15)
                        }
                      }))
                    }
                  />
                  <label>{lang === "ru" ? "Длительность блокировки, минут" : "Lockout duration, minutes"}</label>
                  <input
                    className="text-input"
                    type="number"
                    min="1"
                    max="10080"
                    value={securitySettingsDraft?.account_lockout?.duration_minutes ?? 15}
                    disabled={!canEditSecurity}
                    onChange={(event) =>
                      setSecuritySettingsDraft((prev) => ({
                        ...prev,
                        account_lockout: {
                          ...(prev?.account_lockout || {}),
                          duration_minutes: Number(event.target.value || 15)
                        }
                      }))
                    }
                  />
                  <label className="check-inline">
                    <input
                      type="checkbox"
                      checked={Boolean(securitySettingsDraft?.trusted_ips?.enabled)}
                      disabled={!canEditSecurity}
                      onChange={(event) =>
                        setSecuritySettingsDraft((prev) => ({
                          ...prev,
                          trusted_ips: {
                            ...(prev?.trusted_ips || {}),
                            enabled: event.target.checked
                          }
                        }))
                      }
                    />
                    {lang === "ru" ? "Ограничить admin API доверенными IP" : "Restrict admin API to trusted IPs"}
                  </label>
                  <label>{lang === "ru" ? "Доверенные IP/CIDR, по одному на строку" : "Trusted IP/CIDR, one per line"}</label>
                  <textarea
                    className="text-input"
                    rows={4}
                    value={(securitySettingsDraft?.trusted_ips?.cidrs || []).join("\n")}
                    disabled={!canEditSecurity}
                    onChange={(event) =>
                      setSecuritySettingsDraft((prev) => ({
                        ...prev,
                        trusted_ips: {
                          ...(prev?.trusted_ips || {}),
                          cidrs: event.target.value.split(/\n|,/).map((item) => item.trim()).filter(Boolean)
                        }
                      }))
                    }
                  />
                  <div />
                  <div className="action-row">
                    <button type="submit" disabled={!canEditSecurity || loading}>{t("save")}</button>
                    <button
                      type="button"
                      className="secondary"
                      disabled={!canEditSecurity}
                      onClick={() => setSecuritySettingsDraft(securitySettings)}
                    >
                      {t("reset")}
                    </button>
                  </div>
                </form>
              </div>

              <div className="panel">
                <h4>{lang === "ru" ? "Админские сессии" : "Admin sessions"}</h4>
                {renderSecuritySessionsPager()}
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{lang === "ru" ? "Пользователь" : "User"}</th>
                        <th>IP</th>
                        <th>{lang === "ru" ? "Последняя активность" : "Last seen"}</th>
                        <th>{lang === "ru" ? "Истекает" : "Expires"}</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSecuritySessions.map((item) => (
                        <tr key={item.id}>
                          <td>{item.display_name || item.login}</td>
                          <td>{item.last_seen_ip || item.created_ip || "-"}</td>
                          <td>{formatTimestamp(item.last_seen_at)}</td>
                          <td>{formatTimestamp(item.expires_at)}</td>
                          <td>
                            {!item.revoked_at && item.id !== session?.user?.session_id && canEditSecurity ? (
                              <button type="button" className="secondary" onClick={() => revokeSecuritySession(item.id)}>
                                {lang === "ru" ? "Отозвать" : "Revoke"}
                              </button>
                            ) : (
                              <span className="muted">{item.revoked_at ? (lang === "ru" ? "Отозвана" : "Revoked") : "-"}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderSecuritySessionsPager()}
              </div>

              <div className="panel">
                <h4>{lang === "ru" ? "Администраторы и блокировки" : "Administrators and lockouts"}</h4>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{lang === "ru" ? "Администратор" : "Administrator"}</th>
                        <th>{lang === "ru" ? "Роль" : "Role"}</th>
                        <th>{lang === "ru" ? "Смена пароля" : "Password changed"}</th>
                        <th>{lang === "ru" ? "Неудачные входы" : "Failed logins"}</th>
                        <th>{lang === "ru" ? "Блокировка" : "Lockout"}</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consoleUsers.map((item) => {
                        const locked = item.locked_until && new Date(item.locked_until).getTime() > Date.now();
                        return (
                          <tr key={`security-user-${item.id}`} className={!item.is_active ? "muted" : ""}>
                            <td>
                              <strong>{item.display_name || item.login}</strong>
                              <div className="muted">{item.email}</div>
                            </td>
                            <td>{formatConsoleRole(item.role, t)}</td>
                            <td>{formatTimestamp(item.password_updated_at)}</td>
                            <td>{item.failed_login_count || 0}</td>
                            <td>
                              {locked ? (
                                <>
                                  <strong>{lang === "ru" ? "Заблокирован" : "Locked"}</strong>
                                  <div className="muted">
                                    {lang === "ru" ? "Причина: слишком много неудачных входов" : "Reason: too many failed login attempts"}
                                  </div>
                                  <div className="muted">{formatTimestamp(item.locked_until)}</div>
                                </>
                              ) : (
                                <span className="muted">{lang === "ru" ? "Нет" : "No"}</span>
                              )}
                            </td>
                            <td>
                              <div className="action-row">
                                {String(item.id || "") !== String(session?.user?.id || "") && canEditSecurity && (
                                  <button type="button" className="secondary" onClick={() => forceConsoleUserPasswordReset(item.id)} disabled={loading}>
                                    {lang === "ru" ? "Сброс пароля" : "Force reset"}
                                  </button>
                                )}
                                {locked && String(session?.user?.role || "").toLowerCase() === "master-admin" && canEditSecurity && (
                                  <button type="button" className="secondary" onClick={() => unlockConsoleUser(item.id)} disabled={loading}>
                                    {lang === "ru" ? "Разблокировать" : "Unlock"}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {hasPermission("settings.api_keys", "view") && (
                <div className="panel">
                  <div className="section-title-with-help">
                    <h4>Webhooks</h4>
                    <span className="help-tooltip" tabIndex={0} aria-label={lang === "ru" ? "Что такое Webhooks" : "What are Webhooks"}>
                      ?
                      <span className="help-tooltip-card" role="tooltip">
                        {lang === "ru"
                          ? "Webhooks автоматически отправляют события Turuk MDM во внешние системы: helpdesk, SIEM, Slack, Teams или внутренние автоматизации клиента."
                          : "Webhooks automatically send Turuk MDM events to external systems such as helpdesk, SIEM, Slack, Teams, or customer automations."}
                      </span>
                    </span>
                  </div>
                  <form className="modal-grid" onSubmit={saveWebhook}>
                    <label>{lang === "ru" ? "Название" : "Name"}</label>
                    <input
                      className="text-input"
                      value={webhookForm.name}
                      disabled={!hasPermission("settings.api_keys", "edit")}
                      onChange={(event) => setWebhookForm((prev) => ({ ...prev, name: event.target.value }))}
                    />
                    <label>URL</label>
                    <input
                      className="text-input"
                      value={webhookForm.url}
                      placeholder="https://example.com/turuk-webhook"
                      disabled={!hasPermission("settings.api_keys", "edit")}
                      onChange={(event) => setWebhookForm((prev) => ({ ...prev, url: event.target.value }))}
                    />
                    <label>{lang === "ru" ? "Secret" : "Secret"}</label>
                    <input
                      className="text-input"
                      type="password"
                      value={webhookForm.secret}
                      disabled={!hasPermission("settings.api_keys", "edit")}
                      placeholder={webhookForm.id ? (lang === "ru" ? "Оставьте пустым, чтобы не менять" : "Leave blank to keep") : ""}
                      onChange={(event) => setWebhookForm((prev) => ({ ...prev, secret: event.target.value }))}
                    />
                    <label>{lang === "ru" ? "События" : "Events"}</label>
                    <div className="checkbox-list">
                      {(webhookEventTypes.length ? webhookEventTypes : [
                        "device.enrolled",
                        "command.failed",
                        "compliance.violation",
                        "support.ticket.created"
                      ]).map((eventType) => (
                        <label key={eventType} className="check-inline">
                          <input
                            type="checkbox"
                            checked={webhookForm.eventTypes.includes(eventType)}
                            disabled={!hasPermission("settings.api_keys", "edit")}
                            onChange={(event) =>
                              setWebhookForm((prev) => ({
                                ...prev,
                                eventTypes: event.target.checked
                                  ? Array.from(new Set([...(prev.eventTypes || []), eventType]))
                                  : (prev.eventTypes || []).filter((item) => item !== eventType)
                              }))
                            }
                          />
                          {eventType}
                        </label>
                      ))}
                    </div>
                    <div />
                    <div className="action-row">
                      <button type="submit" disabled={!hasPermission("settings.api_keys", "edit") || loading}>
                        {t("save")}
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => setWebhookForm({ id: "", name: "", url: "", secret: "", eventTypes: [] })}
                      >
                        {t("reset")}
                      </button>
                    </div>
                  </form>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{lang === "ru" ? "Название" : "Name"}</th>
                          <th>URL</th>
                          <th>{lang === "ru" ? "События" : "Events"}</th>
                          <th>{lang === "ru" ? "Последняя доставка" : "Last delivery"}</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {webhooks.map((item) => (
                          <tr key={item.id} className={item.is_active ? "" : "muted"}>
                            <td>{item.name}</td>
                            <td>{item.url}</td>
                            <td>{(item.event_types || []).join(", ")}</td>
                            <td>{item.last_delivery_error || formatTimestamp(item.last_delivered_at)}</td>
                            <td>
                              <button
                                type="button"
                                className="secondary"
                                onClick={() =>
                                  setWebhookForm({
                                    id: item.id,
                                    name: item.name || "",
                                    url: item.url || "",
                                    secret: "",
                                    eventTypes: item.event_types || []
                                  })
                                }
                              >
                                {lang === "ru" ? "Изменить" : "Edit"}
                              </button>
                              {item.is_active && hasPermission("settings.api_keys", "edit") && (
                                <button type="button" className="secondary danger" onClick={() => deactivateWebhook(item.id)}>
                                  {lang === "ru" ? "Отключить" : "Deactivate"}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="panel">
                <h4>{t("securityOwnPasswordTitle")}</h4>
                <p>{t("securityOwnPasswordHint")}</p>
                <form className="modal-grid" onSubmit={handleChangePassword}>
                  <label>{t("currentPassword")}</label>
                  <input
                    className="text-input"
                    type="password"
                    autoComplete="current-password"
                    value={changePasswordForm.currentPassword}
                    onChange={(event) =>
                      setChangePasswordForm((prev) => ({
                        ...prev,
                        currentPassword: event.target.value
                      }))
                    }
                  />
                  <label>{t("newPassword")}</label>
                  <input
                    className="text-input"
                    type="password"
                    autoComplete="new-password"
                    value={changePasswordForm.newPassword}
                    onChange={(event) =>
                      setChangePasswordForm((prev) => ({
                        ...prev,
                        newPassword: event.target.value
                      }))
                    }
                  />
                  <label>{t("confirmPassword")}</label>
                  <input
                    className="text-input"
                    type="password"
                    autoComplete="new-password"
                    value={changePasswordForm.confirmPassword}
                    onChange={(event) =>
                      setChangePasswordForm((prev) => ({
                        ...prev,
                        confirmPassword: event.target.value
                      }))
                    }
                  />
                  <div className="action-row">
                    <button type="submit" disabled={loading}>
                      {t("changePassword")}
                    </button>
                  </div>
                </form>
              </div>

              <div className="panel">
                <h4>{t("securityOwnMFATitle")}</h4>
                <p>{t("securityOwnMFAHint")}</p>
                {session?.user?.mfa_enabled ? (
                  <p>{t("securityMFAAlreadyEnabled")}</p>
                ) : (
                  <form
                    className="modal-grid"
                    onSubmit={mfaSetupForm.secret ? enableMFA : startMFASetup}
                  >
                    {!mfaSetupForm.secret && (
                      <>
                        <label>{t("currentPassword")}</label>
                        <input
                          className="text-input"
                          type="password"
                          autoComplete="current-password"
                          value={mfaSetupForm.currentPassword}
                          onChange={(event) =>
                            setMFASetupForm((prev) => ({
                              ...prev,
                              currentPassword: event.target.value
                            }))
                          }
                        />
                      </>
                    )}
                    {mfaSetupForm.secret && (
                      <>
                        {mfaSetupForm.qrSVG && (
                          <div
                            className="mfa-qr"
                            dangerouslySetInnerHTML={{ __html: mfaSetupForm.qrSVG }}
                          />
                        )}
                        <label>{t("mfaSecret")}</label>
                        <input className="text-input" value={mfaSetupForm.secret} readOnly />
                        <p className="muted">{t("mfaSetupScanHint")}</p>
                        <label>{t("mfaCode")}</label>
                        <input
                          className="text-input"
                          value={mfaSetupForm.code}
                          inputMode="numeric"
                          placeholder="123456"
                          onChange={(event) =>
                            setMFASetupForm((prev) => ({
                              ...prev,
                              code: event.target.value
                            }))
                          }
                        />
                      </>
                    )}
                    <div className="action-row">
                      <button type="submit" disabled={loading}>
                        {mfaSetupForm.secret ? t("mfaEnable") : t("mfaStartSetup")}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              <div className="panel">
                <h4>{t("passkeySetupTitle")}</h4>
                <p>{t("passkeySetupHint")}</p>
                {session?.user?.passkey_enabled ? (
                  <p>{t("passkeyAlreadyEnabled")}</p>
                ) : (
                  <form className="modal-grid" onSubmit={registerPasskey}>
                    <label>{t("currentPassword")}</label>
                    <input
                      className="text-input"
                      type="password"
                      autoComplete="current-password"
                      value={passkeySetupForm.currentPassword}
                      onChange={(event) =>
                        setPasskeySetupForm((prev) => ({
                          ...prev,
                          currentPassword: event.target.value
                        }))
                      }
                    />
                    <label>{t("passkeyName")}</label>
                    <input
                      className="text-input"
                      value={passkeySetupForm.name}
                      placeholder={t("passkeyNamePlaceholder")}
                      onChange={(event) =>
                        setPasskeySetupForm((prev) => ({
                          ...prev,
                          name: event.target.value
                        }))
                      }
                    />
                    <div className="action-row">
                      <button type="submit" disabled={loading}>
                        {t("passkeyRegister")}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {canViewTenantIsolation && (
                <div className="panel">
                  <h4>{t("securityTenantIsolationTitle")}</h4>
                  <p>{t("securityTenantIsolationHint")}</p>
                  {!tenantIsolationReport?.available ? (
                    <div className="alert warning">
                      {t("securityTenantIsolationUnavailable")}
                    </div>
                  ) : (
                    <>
                      <div className="tenant-isolation-grid">
                        <div className="tenant-isolation-card">
                          <span>{t("securityTenantIsolationAppRole")}</span>
                          <strong className={tenantIsolationReport?.ready_for_app_role ? "ok-text" : "danger-text"}>
                            {tenantIsolationReport?.ready_for_app_role ? t("securityTenantIsolationReady") : t("securityTenantIsolationNeedsAction")}
                          </strong>
                        </div>
                        <div className="tenant-isolation-card">
                          <span>{t("securityTenantIsolationForceRLS")}</span>
                          <strong className={tenantIsolationReport?.force_rls_ready ? "ok-text" : "warning-text"}>
                            {tenantIsolationReport?.force_rls_ready ? t("securityTenantIsolationReady") : t("securityTenantIsolationPending")}
                          </strong>
                        </div>
                        <div className="tenant-isolation-card">
                          <span>{t("securityTenantIsolationMissingTables")}</span>
                          <strong>{tenantIsolationMissingTables.length}</strong>
                        </div>
                        <div className="tenant-isolation-card">
                          <span>{t("securityTenantIsolationRLSIssues")}</span>
                          <strong>{tenantIsolationReport?.rls_policy_issue_count ?? tenantIsolationRLSPolicyIssues.length}</strong>
                        </div>
                        <div className="tenant-isolation-card">
                          <span>{t("securityTenantIsolationUniqueIssues")}</span>
                          <strong>{tenantIsolationReport?.blocking_unique_index_count ?? tenantIsolationBlockingUniqueIndexes.length}</strong>
                        </div>
                      </div>

                      {(tenantIsolationMissingTables.length > 0 || tenantIsolationRLSPolicyIssues.length > 0 || tenantIsolationBlockingUniqueIndexes.length > 0) && (
                        <div className="tenant-isolation-issues">
                          {tenantIsolationMissingTables.length > 0 && (
                            <div>
                              <h5>{t("securityTenantIsolationMissingTables")}</h5>
                              <p>{tenantIsolationMissingTables.join(", ")}</p>
                            </div>
                          )}
                          {tenantIsolationRLSPolicyIssues.length > 0 && (
                            <div>
                              <h5>{t("securityTenantIsolationRLSIssues")}</h5>
                              <p>{tenantIsolationRLSPolicyIssues.map((item) => item.table_name).join(", ")}</p>
                            </div>
                          )}
                          {tenantIsolationBlockingUniqueIndexes.length > 0 && (
                            <div>
                              <h5>{t("securityTenantIsolationUniqueIssues")}</h5>
                              <p>{tenantIsolationBlockingUniqueIndexes.map((item) => `${item.table_name}.${item.index_name}`).join(", ")}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {canEditSecurity && (
                <>
                  <div className="panel">
                    <h4>{t("securityTenantExportTitle")}</h4>
                    <p>{t("securityTenantExportHint")}</p>
                    <div className="action-row">
                      <button type="button" onClick={downloadTenantDataExport} disabled={loading}>
                        {t("securityTenantExportButton")}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {isTenantsPage && canViewTenants && (
            <div className="panel">
              <div className="panel-heading">
                <h3>{t("tenantsTitle")}</h3>
              </div>
              <p>{t("tenantsHint")}</p>

              <form className="modal-grid" onSubmit={saveTenant}>
                <h4>{tenantForm.id ? t("tenantsEditTitle") : t("tenantsCreateTitle")}</h4>
                <label>{t("tenantsName")}</label>
                <input
                  className="text-input"
                  value={tenantForm.name}
                  disabled={!canEditTenants}
                  onChange={(event) =>
                    setTenantForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
                <label>{t("tenantsSlug")}</label>
                <input
                  className="text-input"
                  placeholder="companyname"
                  value={tenantForm.slug}
                  disabled={!canEditTenants}
                  onChange={(event) =>
                    setTenantForm((prev) => ({ ...prev, slug: event.target.value }))
                  }
                />
                <label>{t("tenantsPrimaryDomain")}</label>
                <input
                  className="text-input"
                  placeholder={
                    buildTenantDomainPreview(tenantForm, meta?.tenant_base_domain) ||
                    "companyname.turuk.pro"
                  }
                  value={tenantForm.primaryDomain}
                  disabled={!canEditTenants}
                  onChange={(event) =>
                    setTenantForm((prev) => ({ ...prev, primaryDomain: event.target.value }))
                  }
                />
                <div className="modal-grid two-col">
                  <div>
                    <label>{t("tenantsStatus")}</label>
                    <select
                      className="text-input"
                      value={tenantForm.status}
                      disabled={!canEditTenants || !tenantForm.id}
                      onChange={(event) =>
                        setTenantForm((prev) => ({ ...prev, status: event.target.value }))
                      }
                    >
                      <option value="active">{t("tenantsStatusActive")}</option>
                      <option value="trial">{t("tenantsStatusTrial")}</option>
                      <option value="suspended">{t("tenantsStatusSuspended")}</option>
                      <option value="deleted" disabled>
                        {t("tenantsStatusDeleted")}
                      </option>
                    </select>
                  </div>
                  <div>
                    <label>{t("tenantsPlan")}</label>
                    <select
                      className="text-input"
                      value={tenantForm.plan}
                      disabled={!canEditTenants}
                      onChange={(event) =>
                        setTenantForm((prev) => ({ ...prev, plan: event.target.value }))
                      }
                    >
                      <option value="internal">{t("tenantsPlanInternal")}</option>
                      <option value="trial">{t("tenantsPlanTrial")}</option>
                      <option value="business">{t("tenantsPlanBusiness")}</option>
                      <option value="enterprise">{t("tenantsPlanEnterprise")}</option>
                    </select>
                  </div>
                </div>
                <div className="settings-card">
                  <h4>{t("tenantsBilling")}</h4>
                  <div className="modal-grid two-col">
                    <div>
                      <label>{t("tenantsBillingStatus")}</label>
                      <select
                        className="text-input"
                        value={tenantForm.billingStatus}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({ ...prev, billingStatus: event.target.value }))
                        }
                      >
                        <option value="internal">{t("tenantsBillingStatusInternal")}</option>
                        <option value="trialing">{t("tenantsBillingStatusTrialing")}</option>
                        <option value="manual">{t("tenantsBillingStatusManual")}</option>
                        <option value="active">{t("tenantsBillingStatusActive")}</option>
                        <option value="past_due">{t("tenantsBillingStatusPastDue")}</option>
                        <option value="canceled">{t("tenantsBillingStatusCanceled")}</option>
                      </select>
                    </div>
                    <div>
                      <label>{t("tenantsBillingEmail")}</label>
                      <input
                        className="text-input"
                        type="email"
                        value={tenantForm.billingEmail}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({ ...prev, billingEmail: event.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div className="modal-grid two-col">
                    <div>
                      <label>{t("tenantsTrialEndsAt")}</label>
                      <input
                        className="text-input"
                        type="date"
                        value={tenantForm.trialEndsAt}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({ ...prev, trialEndsAt: event.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label>{t("tenantsSubscriptionEndsAt")}</label>
                      <input
                        className="text-input"
                        type="date"
                        value={tenantForm.subscriptionEndsAt}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({
                            ...prev,
                            subscriptionEndsAt: event.target.value
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="modal-grid two-col">
                    <div>
                      <label>{t("tenantsStripeCustomerID")}</label>
                      <input
                        className="text-input"
                        value={tenantForm.stripeCustomerID}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({
                            ...prev,
                            stripeCustomerID: event.target.value
                          }))
                        }
                      />
                    </div>
                    <div>
                      <label>{t("tenantsStripeSubscriptionID")}</label>
                      <input
                        className="text-input"
                        value={tenantForm.stripeSubscriptionID}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({
                            ...prev,
                            stripeSubscriptionID: event.target.value
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="settings-card">
                  <h4>{t("tenantsManualLimits")}</h4>
                  <p>{t("tenantsManualLimitsHint")}</p>
                  <div className="modal-grid three-col">
                    <div>
                      <label>{t("tenantsDeviceLimit")}</label>
                      <input
                        className="text-input"
                        type="number"
                        min="0"
                        value={tenantForm.deviceLimit}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({ ...prev, deviceLimit: event.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label>{t("tenantsAccountLimit")}</label>
                      <input
                        className="text-input"
                        type="number"
                        min="0"
                        value={tenantForm.accountLimit}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({ ...prev, accountLimit: event.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label>{t("tenantsAdminLimit")}</label>
                      <input
                        className="text-input"
                        type="number"
                        min="0"
                        value={tenantForm.adminLimit}
                        disabled={!canEditTenants}
                        onChange={(event) =>
                          setTenantForm((prev) => ({ ...prev, adminLimit: event.target.value }))
                        }
                      />
                    </div>
                  </div>
                </div>
                {!tenantForm.id && (
                  <div className="settings-card">
                    <h4>{t("tenantsInitialAdminTitle")}</h4>
                    <p>{t("tenantsInitialAdminHint")}</p>
                    <div className="modal-grid two-col">
                      <div>
                        <label>{t("tenantsAdminFirstName")}</label>
                        <input
                          className="text-input"
                          value={tenantForm.adminFirstName}
                          disabled={!canEditTenants}
                          onChange={(event) =>
                            setTenantForm((prev) => ({
                              ...prev,
                              adminFirstName: event.target.value
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label>{t("tenantsAdminLastName")}</label>
                        <input
                          className="text-input"
                          value={tenantForm.adminLastName}
                          disabled={!canEditTenants}
                          onChange={(event) =>
                            setTenantForm((prev) => ({
                              ...prev,
                              adminLastName: event.target.value
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="modal-grid two-col">
                      <div>
                        <label>{t("tenantsAdminEmail")}</label>
                        <input
                          className="text-input"
                          type="email"
                          value={tenantForm.adminEmail}
                          disabled={!canEditTenants}
                          onChange={(event) =>
                            setTenantForm((prev) => ({
                              ...prev,
                              adminEmail: event.target.value
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label>{t("tenantsAdminLogin")}</label>
                        <input
                          className="text-input"
                          value={tenantForm.adminLogin}
                          disabled={!canEditTenants}
                          onChange={(event) =>
                            setTenantForm((prev) => ({
                              ...prev,
                              adminLogin: event.target.value
                            }))
                          }
                        />
                      </div>
                    </div>
                    <label>{t("tenantsAdminTemporaryPassword")}</label>
                    <input
                      className="text-input"
                      type="password"
                      value={tenantForm.adminTemporaryPassword}
                      disabled={!canEditTenants}
                      onChange={(event) =>
                        setTenantForm((prev) => ({
                          ...prev,
                          adminTemporaryPassword: event.target.value
                        }))
                      }
                    />
                  </div>
                )}
                <div className="action-row">
                  <button type="submit" disabled={!canEditTenants || loading}>
                    {tenantForm.id ? t("documentsSave") : t("documentsAdd")}
                  </button>
                  {tenantForm.id && (
                    <button type="button" className="secondary" onClick={resetTenantForm}>
                      {t("consoleUsersCancel")}
                    </button>
                  )}
                </div>
              </form>

              <div className="devices-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("tenantsName")}</th>
                      <th>{t("tenantsSlug")}</th>
                      <th>{t("tenantsPrimaryDomain")}</th>
                      <th>{t("tenantsPortalURL")}</th>
                      <th>{t("tenantsDomainStatus")}</th>
                      <th>{t("tenantsReadiness")}</th>
                      <th>{t("tenantsUsage")}</th>
                      <th>{t("tenantsAccountsCount")}</th>
                      <th>{t("tenantsDevicesCount")}</th>
                      <th>{t("tenantsAdminsCount")}</th>
                      <th>{t("tenantsStatus")}</th>
                      <th>{t("tenantsPlan")}</th>
                      <th>{t("tenantsBillingStatus")}</th>
                      <th>{t("tenantsBillingEmail")}</th>
                      <th>{t("profilesColUpdatedAt")}</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((item) => {
                      const portalURL = buildTenantPortalURL(item, meta?.tenant_base_domain);
                      const domainCheck = tenantDomainChecks[item.id] || null;
                      const domainReady = Boolean(domainCheck?.allowed && domainCheck?.dns_resolved);
                      const readiness = item.readiness || {};
                      const readinessItems = Array.isArray(readiness.items) ? readiness.items : [];
                      const readinessReady = Boolean(readiness.ready);
                      const readinessPercent = Number.isFinite(Number(readiness.percent))
                        ? Number(readiness.percent)
                        : 0;
                      const missingReadinessItems = readinessItems.filter(
                        (check) => !check.ready && tenantReadinessMessage(check, t)
                      );
                      const usage = item.usage || {};
                      const usageItems = Array.isArray(usage.items) ? usage.items : [];
                      const usageOverLimit = Boolean(usage.over_limit);
                      const isDeletedTenant = String(item.status || "").toLowerCase() === "deleted";
                      const isSuspendedTenant = String(item.status || "").toLowerCase() === "suspended";
                      return (
                        <tr key={item.id}>
                          <td>{item.name || "-"}</td>
                          <td>{item.slug || "-"}</td>
                          <td>{item.primary_domain || "-"}</td>
                          <td>
                            {portalURL ? (
                              <a href={portalURL} target="_blank" rel="noreferrer">
                                {portalURL}
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            <div className="tenant-domain-check">
                              {domainCheck ? (
                                <span className={`badge ${domainReady ? "tenant-badge" : "danger-badge"}`}>
                                  {domainReady ? t("tenantsDomainReady") : t("tenantsDomainNotReady")}
                                </span>
                              ) : null}
                              {domainCheck ? (
                                <span className="muted">
                                  {domainCheck.allowed ? t("tenantsDomainAllowed") : domainCheck.message || "-"}
                                  {" / "}
                                  {domainCheck.dns_resolved ? t("tenantsDomainDNSResolved") : "DNS -"}
                                </span>
                              ) : null}
                              <button
                                type="button"
                                className="secondary"
                                disabled={tenantDomainCheckingID === item.id}
                                onClick={() => checkTenantDomain(item)}
                              >
                                {tenantDomainCheckingID === item.id
                                  ? t("tenantsDomainChecking")
                                  : t("tenantsDomainCheck")}
                              </button>
                            </div>
                          </td>
                          <td>
                            <div className="tenant-readiness">
                              <div className="tenant-readiness-head">
                                <span className={`badge ${readinessReady ? "tenant-badge" : "warning-badge"}`}>
                                  {readinessReady ? t("tenantsReadinessReady") : t("tenantsReadinessNotReady")}
                                </span>
                                <strong>{readinessPercent}%</strong>
                              </div>
                              <div className="tenant-readiness-bar">
                                <span style={{ width: `${Math.max(0, Math.min(100, readinessPercent))}%` }} />
                              </div>
                              <div className="tenant-readiness-list">
                                {readinessItems.map((check) => (
                                  <span
                                    key={check.key}
                                    className={check.ready ? "tenant-check-ok" : "tenant-check-missing"}
                                    title={check.ready ? "" : tenantReadinessMessage(check, t)}
                                  >
                                    {check.ready ? "✓" : "×"} {tenantReadinessLabel(check.key, t)}
                                  </span>
                                ))}
                              </div>
                              {missingReadinessItems.length > 0 && (
                                <div className="tenant-readiness-reasons">
                                  {missingReadinessItems.map((check) => (
                                    <div key={`${check.key}-reason`}>
                                      <strong>{tenantReadinessLabel(check.key, t)}:</strong>{" "}
                                      {tenantReadinessMessage(check, t)}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="tenant-usage">
                              {usageOverLimit && <span className="badge danger-badge">{t("tenantsUsageOverLimit")}</span>}
                              {usageItems.map((usageItem) => {
                                const limit = Number(usageItem.limit || 0);
                                const used = Number(usageItem.used || 0);
                                const percent = Number.isFinite(Number(usageItem.percent)) ? Number(usageItem.percent) : 0;
                                const unlimited = limit <= 0;
                                return (
                                  <div className="tenant-usage-item" key={usageItem.key}>
                                    <div className="tenant-usage-line">
                                      <span>{tenantUsageLabel(usageItem.key, t)}</span>
                                      <strong>
                                        {used}
                                        {" / "}
                                        {unlimited ? t("tenantsUsageUnlimited") : limit}
                                      </strong>
                                    </div>
                                    {!unlimited && (
                                      <div className={`tenant-usage-bar ${usageItem.over_limit ? "is-over-limit" : ""}`}>
                                        <span style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                          <td>{Number(item.accounts_count || 0)}</td>
                          <td>{Number(item.devices_count || 0)}</td>
                          <td>{Number(item.console_users_count || 0)}</td>
                          <td>
                            <div className="tenant-lifecycle-cell">
                              <strong>{t(`tenantsStatus${capitalizeTenantToken(item.status)}`)}</strong>
                              {item.suspended_at && (
                                <span>{`${t("tenantsSuspendedAt")}: ${formatTimestamp(item.suspended_at)}`}</span>
                              )}
                              {item.suspended_reason && (
                                <span>{`${t("tenantsSuspendedReason")}: ${item.suspended_reason}`}</span>
                              )}
                              {item.scheduled_deletion_at && (
                                <span>{`${t("tenantsScheduledDeletionAt")}: ${formatTimestamp(
                                  item.scheduled_deletion_at
                                )}`}</span>
                              )}
                              {item.deleted_at && (
                                <span>{`${t("tenantsDeletedAt")}: ${formatTimestamp(item.deleted_at)}`}</span>
                              )}
                              {item.deletion_reason && (
                                <span>{`${t("tenantsDeletionReason")}: ${item.deletion_reason}`}</span>
                              )}
                            </div>
                          </td>
                          <td>{t(`tenantsPlan${capitalizeTenantToken(item.plan)}`)}</td>
                          <td>{tenantBillingStatusLabel(item.billing_status, t)}</td>
                          <td>{item.billing_email || "-"}</td>
                          <td>{formatTimestamp(item.updated_at || item.created_at)}</td>
                          <td>
                            {canEditTenants ? (
                              <div className="tenant-actions">
                                <button className="secondary" onClick={() => editTenant(item)}>
                                  {t("edit")}
                                </button>
                                <button className="secondary" onClick={() => downloadPlatformTenantDataExport(item)}>
                                  {t("securityTenantExportButton")}
                                </button>
                                <button className="secondary" onClick={() => downloadPlatformTenantConfiguratorProfile(item)}>
                                  {t("tenantsConfiguratorProfileDownload")}
                                </button>
                                {isSuspendedTenant || isDeletedTenant ? (
                                  <button
                                    className="secondary"
                                    onClick={() => updateTenantLifecycle(item, "reactivate")}
                                  >
                                    {t("tenantsLifecycleReactivate")}
                                  </button>
                                ) : (
                                  <button
                                    className="secondary"
                                    onClick={() => updateTenantLifecycle(item, "suspend")}
                                  >
                                    {t("tenantsLifecycleSuspend")}
                                  </button>
                                )}
                                {item.scheduled_deletion_at && !isDeletedTenant ? (
                                  <button
                                    className="secondary"
                                    onClick={() => updateTenantLifecycle(item, "cancel_delete")}
                                  >
                                    {t("tenantsLifecycleCancelDelete")}
                                  </button>
                                ) : null}
                                {!item.scheduled_deletion_at && !isDeletedTenant ? (
                                  <button
                                    className="secondary"
                                    onClick={() => updateTenantLifecycle(item, "schedule_delete")}
                                  >
                                    {t("tenantsLifecycleScheduleDelete")}
                                  </button>
                                ) : null}
                                {!isDeletedTenant ? (
                                  <button
                                    className="danger"
                                    onClick={() => updateTenantLifecycle(item, "delete_now")}
                                  >
                                    {t("tenantsLifecycleDeleteNow")}
                                  </button>
                                ) : null}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {tenants.length === 0 && <p className="empty">{t("tenantsNoItems")}</p>}
            </div>
          )}

          {isRoleSettingsPage && canViewRoleSettings && (
            <div className="panel">
              <div className="panel-heading">
                <h3>{t("roleSettingsTitle")}</h3>
                {canEditRoleSettings && (
                  <button onClick={() => setShowCreateRoleModal(true)}>{t("roleSettingsCreate")}</button>
                )}
              </div>
              <p>{t("roleSettingsHint")}</p>

              <div className="devices-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("roleSettingsDisplayName")}</th>
                      <th>{t("roleSettingsRoleKey")}</th>
                      <th>{t("roleSettingsType")}</th>
                      <th>{t("roleSettingsUsersCount")}</th>
                      <th>{t("profilesColUpdatedAt")}</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roleSettings.map((item) => {
                      const roleKey = String(item.role_key || "").toLowerCase();
                      const isSelected = roleKey === String(selectedRoleKey || "").toLowerCase();
                      return (
                        <tr key={roleKey} className={isSelected ? "row-selected" : ""}>
                          <td>
                            <button className="console-user-link" onClick={() => openRoleEditor(item)}>
                              {item.display_name || roleKey}
                            </button>
                          </td>
                          <td>{roleKey}</td>
                          <td>{item.is_system ? t("roleSettingsSystem") : t("roleSettingsCustom")}</td>
                          <td>{Number(item.users_count || 0)}</td>
                          <td>{formatTimestamp(item.updated_at)}</td>
                          <td>
                            {canEditRoleSettings ? (
                              item.is_system ? (
                                "-"
                              ) : (
                                <button className="danger" onClick={() => deleteRoleSetting(roleKey)}>
                                  {t("roleSettingsDelete")}
                                </button>
                              )
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {roleSettings.length === 0 && <p className="empty">{t("roleSettingsNoItems")}</p>}

              {selectedRoleItem ? (
                <div className="role-editor-panel">
                  <div className="role-editor-header">
                    <div>
                      <h3>{t("roleSettingsPermissions")}</h3>
                      <p>{selectedRoleItem.display_name || selectedRoleItem.role_key}</p>
                    </div>
                    <div className="role-template-actions">
                      {ENTERPRISE_ROLE_TEMPLATES.map((template) => (
                        <button
                          key={template.key}
                          className="secondary"
                          disabled={!canEditRoleSettings}
                          onClick={() => applyRoleTemplateToEditor(template)}
                        >
                          {roleTemplateLabel(template)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="role-scope-panel">
                    <div>
                      <h4>{t("roleSettingsAccessScope")}</h4>
                      <p>{t("roleSettingsScopeHint")}</p>
                    </div>
                    <div className="role-scope-grid">
                      <div className="role-scope-block">
                        <strong>{t("roleSettingsScopeGroups")}</strong>
                        {groups.length > 0 ? (
                          <div className="role-scope-options">
                            {groups.map((group) => {
                              const groupID = String(group.id || "").trim();
                              if (!groupID) return null;
                              return (
                                <label key={groupID} className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={(editRoleForm.accessScope?.group_ids || []).includes(groupID)}
                                    disabled={!canEditRoleSettings}
                                    onChange={(event) =>
                                      setRoleScopeValue("group_ids", groupID, event.target.checked)
                                    }
                                  />
                                  {group.name || group.group_name || groupID}
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="muted-text">{t("roleSettingsScopeAllGroups")}</span>
                        )}
                      </div>

                      <div className="role-scope-block">
                        <strong>{t("roleSettingsScopePlatforms")}</strong>
                        <div className="role-scope-options">
                          {ROLE_SCOPE_PLATFORMS.map((platform) => (
                            <label key={platform.key} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={(editRoleForm.accessScope?.platforms || []).includes(platform.key)}
                                disabled={!canEditRoleSettings}
                                onChange={(event) =>
                                  setRoleScopeValue("platforms", platform.key, event.target.checked)
                                }
                              />
                              {platform.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      <label className="role-scope-block">
                        <strong>{t("roleSettingsScopeLocations")}</strong>
                        <input
                          className="text-input"
                          value={(editRoleForm.accessScope?.location_ids || []).join(", ")}
                          placeholder={t("roleSettingsScopeLocationsPlaceholder")}
                          disabled={!canEditRoleSettings}
                          onChange={(event) => setRoleScopeLocations(event.target.value)}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="devices-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th aria-label={t("roleSettingsPermissions")}></th>
                          <th>{t("roleSettingsView")}</th>
                          <th>{t("roleSettingsEditAction")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ROLE_PERMISSION_GROUPS.flatMap((group) => {
                          const rows = [
                            <tr key={`group-${group.titleKey}`}>
                              <th colSpan={3}>{t(group.titleKey)}</th>
                            </tr>
                          ];
                          (group.items || []).forEach((item) => {
                            const permission = editRoleForm.permissions?.[item.key] || {
                              view: false,
                              edit: false
                            };
                            rows.push(
                              <tr key={item.key}>
                                <td>{t(item.labelKey)}</td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(permission.view || permission.edit)}
                                    disabled={!canEditRoleSettings}
                                    onChange={(event) =>
                                      setRolePermissionValue(item.key, "view", event.target.checked)
                                    }
                                  />
                                </td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(permission.edit)}
                                    disabled={!canEditRoleSettings}
                                    onChange={(event) =>
                                      setRolePermissionValue(item.key, "edit", event.target.checked)
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          });
                          return rows;
                        })}
                      </tbody>
                    </table>
                  </div>
                  {canEditRoleSettings && (
                    <div className="action-row" style={{ marginTop: 16 }}>
                      <button onClick={saveRoleSetting}>
                        {t("roleSettingsSavePermissions")}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="empty">{t("roleSettingsSelectHint")}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderSectionWithGuard(sectionKey, renderFn) {
    try {
      if (typeof renderFn !== "function") {
        throw new Error(`missing renderer: ${sectionKey}`);
      }
      return renderFn();
    } catch (err) {
      const message =
        err && typeof err === "object" && "message" in err
          ? String(err.message || "")
          : String(err || "");
      console.error(`Render failed for section "${sectionKey}"`, err);
      const errorTitle = lang === "ru" ? "Ошибка отображения раздела" : "Section render error";
      const errorPrefix = lang === "ru" ? "Не удалось отрисовать раздел" : "Render failed for";
      return (
        <div className="page">
          <div className="panel">
            <h3>{errorTitle}</h3>
            <p className="error">
              {`${errorPrefix} "${sectionKey}"`}
              {message ? `: ${message}` : ""}
            </p>
          </div>
        </div>
      );
    }
  }

  function renderContent() {
    switch (activePage) {
      case "dashboard":
        return renderSectionWithGuard("dashboard", () => renderDashboard());
      case "commandCenter":
        return renderSectionWithGuard("command-center", () => renderCommandCenter());
      case "healthStatus":
        return renderSectionWithGuard("health-status", () => renderHealthStatus());
      case "devices":
      case "devicesOverview":
      case "devicesList":
      case "devicesRequest":
      case "devicesMonitor":
      case "devicesPlatformWindows":
      case "devicesPlatformIOS":
      case "devicesPlatformMacOS":
      case "devicesPlatformAndroid":
      case "devicesConfig":
      case "devicesDetailAbout":
      case "devicesDetailApps":
      case "devicesDetailProfiles":
      case "devicesDetailLocation":
      case "devicesDetailSharedIPad":
      case "devicesDetailActivationLock":
      case "devicesDetailMacOSUEM":
      case "devicesDetailCommands":
      case "devicesDetailRemoteAccess":
        return renderSectionWithGuard("devices", () => renderDevicesWorkspace());
      case "devicesCompliance":
        return renderSectionWithGuard("devices", () => renderComplianceCenter());
      case "profiles":
      case "profilesAll":
        return renderSectionWithGuard("profiles", () => renderProfiles());
      case "profilesPlatformWindows":
        return renderSectionWithGuard("profiles-windows", () =>
          renderProfiles({ forcedPlatform: "windows" })
        );
      case "profilesPlatformIOS":
        return renderSectionWithGuard("profiles-ios", () =>
          renderProfiles({ forcedPlatform: "ios" })
        );
      case "profilesPlatformMacOS":
        return renderSectionWithGuard("profiles-macos", () =>
          renderProfiles({ forcedPlatform: "macos" })
        );
      case "profilesPlatformAndroid":
        return renderSectionWithGuard("profiles-android", () =>
          renderProfiles({ forcedPlatform: "android" })
        );
      case "policiesAll":
      case "devicesProfiles":
        return renderSectionWithGuard("policies", () => renderProfiles());
      case "policiesCompliance":
        return renderSectionWithGuard("policies-compliance", () => renderComplianceCenter());
      case "accounts":
      case "accountsList":
      case "accountsCreate":
      case "accountsConnect":
        return renderSectionWithGuard("accounts", () => renderAccountsWorkspace());
      case "accountsGroups":
      case "groups":
        return renderSectionWithGuard("groups", () => renderAccountsGroups());
      case "notifications":
      case "agentNotificationsCreate":
      case "agentNotificationsSent":
      case "agentSupportTickets":
        return renderSectionWithGuard("turuk-agent", () => renderTurukAgentWorkspace());
      case "documents":
        return renderSectionWithGuard("documents", () => renderDocumentsPage());
      case "actionLogs":
        if (session?.user?.platform_admin !== true) {
          return renderSectionWithGuard("dashboard", () => renderDashboard());
        }
        return renderSectionWithGuard("action-logs", () => renderActionLogsPage());
      case "apps":
      case "appsOverview":
      case "appsAll":
      case "appsPlatformWindows":
      case "appsPlatformIOS":
      case "appsPlatformMacOS":
      case "appsPlatformAndroid":
      case "appsConfig":
      case "appsUpload":
        return renderSectionWithGuard("apps", () => renderAppsWorkspace());
      case "settingsConsoleBranding":
      case "settingsConsoleDashboard":
      case "settingsOrganization":
      case "settingsAgent":
      case "settingsConsoleUsers":
      case "settingsCertificates":
      case "settingsADE":
      case "settingsVPP":
      case "settingsDeclarative":
      case "settingsSecurity":
      case "settingsNotifications":
      case "settingsSSO":
      case "settingsReports":
      case "settingsTenants":
      case "settingsRoleSettings":
        return renderSectionWithGuard("settings", () => renderSettings());
      default:
        return renderSectionWithGuard("dashboard-default", () => renderDashboard());
    }
  }

  if (!session) {
    const publicTenantRawName = String(publicTenantBranding?.tenant?.name || "").trim();
    const publicTenantSlug = String(publicTenantBranding?.tenant?.slug || "").trim().toLowerCase();
    const isDefaultTenantLogin = publicTenantSlug === "default" || publicTenantRawName.toLowerCase() === "default";
    const publicTenantName = isDefaultTenantLogin ? "" : publicTenantRawName;
    const publicTenantLogo = normalizeConsoleLogoDataURL(publicTenantBranding?.logo_data_url);

    return (
      <div className="auth-layout">
        <form className="auth-card" onSubmit={handleSignIn}>
          {publicTenantLogo && (
            <div className="auth-logo-wrap">
              <img
                className="auth-logo"
                src={publicTenantLogo}
                alt={publicTenantName || t("consoleBrandingLogoAlt")}
              />
            </div>
          )}
          <h1>{publicTenantName || t("loginTitle")}</h1>
          <p>{publicTenantName ? t("loginTenantSubtitle") : t("loginSubtitle")}</p>

          <label>{t("login")}</label>
          <input
            className="text-input"
            value={signInForm.login}
            onChange={(event) => setSignInForm((prev) => ({ ...prev, login: event.target.value }))}
          />

          <label>{t("password")}</label>
          <input
            className="text-input"
            value={signInForm.password}
            onChange={(event) => setSignInForm((prev) => ({ ...prev, password: event.target.value }))}
            placeholder="••••••••"
            type="password"
          />

          {signInMFARequired && (
            <>
              <label>{t("mfaCode")}</label>
              <input
                className="text-input"
                value={signInForm.mfaCode}
                onChange={(event) => setSignInForm((prev) => ({ ...prev, mfaCode: event.target.value }))}
                placeholder="123456"
                inputMode="numeric"
              />
            </>
          )}

          <div className="action-row">
            <button type="submit" disabled={loading}>
              {loading ? t("signingIn") : t("signIn")}
            </button>
            {publicSSOConfig?.enabled && (
              <button type="button" className="secondary" onClick={startSSOLogin}>
                {t("ssoSignIn")}
              </button>
            )}
            <select className="text-input" value={lang} onChange={(event) => setLang(event.target.value)}>
              <option value="ru">RU</option>
              <option value="en">EN</option>
            </select>
          </div>
          {infoMessage && <p className="muted">{infoMessage}</p>}
          {errorMessage && <p className="error">{errorMessage}</p>}
        </form>
      </div>
    );
  }

  if (mustChangePassword) {
    return (
      <div className="auth-layout">
        <form className="auth-card" onSubmit={handleChangePassword}>
          <h1>{t("forcePasswordTitle")}</h1>
          <p>{t("forcePasswordHint")}</p>

          <label>{t("currentPassword")}</label>
          <input
            className="text-input"
            type="password"
            value={changePasswordForm.currentPassword}
            onChange={(event) =>
              setChangePasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))
            }
          />

          <label>{t("newPassword")}</label>
          <input
            className="text-input"
            type="password"
            value={changePasswordForm.newPassword}
            onChange={(event) =>
              setChangePasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))
            }
          />

          <label>{t("confirmPassword")}</label>
          <input
            className="text-input"
            type="password"
            value={changePasswordForm.confirmPassword}
            onChange={(event) =>
              setChangePasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
            }
          />

          <div className="action-row">
            <button type="submit" disabled={loading}>
              {loading ? t("signingIn") : t("changePassword")}
            </button>
            <button type="button" className="danger" onClick={handleSignOut}>
              {t("signOut")}
            </button>
          </div>
          {infoMessage && <p className="muted">{infoMessage}</p>}
          {errorMessage && <p className="error">{errorMessage}</p>}
        </form>
      </div>
    );
  }

  if (mfaSetupRequired) {
    return (
      <div className="auth-layout">
        <form className="auth-card" onSubmit={mfaSetupForm.secret ? enableMFA : startMFASetup}>
          <h1>{t("mfaSetupTitle")}</h1>
          <p>{t("mfaSetupRequiredHint")}</p>

          {!mfaSetupForm.secret && (
            <>
              <label>{t("currentPassword")}</label>
              <input
                className="text-input"
                type="password"
                value={mfaSetupForm.currentPassword}
                onChange={(event) =>
                  setMFASetupForm((prev) => ({ ...prev, currentPassword: event.target.value }))
                }
              />
            </>
          )}

          {mfaSetupForm.secret && (
            <>
              {mfaSetupForm.qrSVG && (
                <div className="mfa-qr" dangerouslySetInnerHTML={{ __html: mfaSetupForm.qrSVG }} />
              )}
              <label>{t("mfaSecret")}</label>
              <input className="text-input" value={mfaSetupForm.secret} readOnly />
              <p className="muted">{t("mfaSetupScanHint")}</p>
              <label>{t("mfaCode")}</label>
              <input
                className="text-input"
                value={mfaSetupForm.code}
                onChange={(event) => setMFASetupForm((prev) => ({ ...prev, code: event.target.value }))}
                placeholder="123456"
                inputMode="numeric"
              />
            </>
          )}

          <div className="action-row">
            <button type="submit" disabled={loading}>
              {mfaSetupForm.secret ? t("mfaEnable") : t("mfaStartSetup")}
            </button>
            <button type="button" className="danger" onClick={handleSignOut}>
              {t("signOut")}
            </button>
          </div>
          {infoMessage && <p className="muted">{infoMessage}</p>}
          {errorMessage && <p className="error">{errorMessage}</p>}
        </form>
      </div>
    );
  }

  const accountsNavActive = ["accounts", "accountsList", "accountsCreate", "accountsConnect"].includes(activePage);
  const groupsNavActive = ["accountsGroups", "groups"].includes(activePage);
  const notificationsNavActive = [
    "notifications",
    "agentNotificationsCreate",
    "agentNotificationsSent",
    "agentSupportTickets"
  ].includes(activePage);
  const documentsNavActive = activePage === "documents";
  const actionLogsNavActive = activePage === "actionLogs";
  const devicesNavActive = [
    "devices",
    "devicesList",
    "devicesPlatformWindows",
    "devicesPlatformIOS",
    "devicesPlatformMacOS",
    "devicesPlatformAndroid",
    "devicesConfig",
    "devicesDetailAbout",
    "devicesDetailApps",
    "devicesDetailProfiles",
    "devicesDetailLocation",
    "devicesDetailMacOSUEM",
    "devicesDetailCommands",
    "devicesDetailRemoteAccess"
  ].includes(activePage);
  const profilesNavActive = [
    "profiles",
    "profilesAll",
    "profilesPlatformWindows",
    "profilesPlatformIOS",
    "profilesPlatformMacOS",
    "profilesPlatformAndroid",
    "policiesAll",
    "policiesCompliance",
    "devicesProfiles"
  ].includes(activePage);
  const appsNavActive = [
    "apps",
    "appsAll",
    "appsPlatformWindows",
    "appsPlatformIOS",
    "appsPlatformMacOS",
    "appsPlatformAndroid",
    "appsConfig",
    "appsUpload"
  ].includes(activePage);
  const settingsNavActive = [
    "settingsConsoleBranding",
    "settingsConsoleDashboard",
    "settingsOrganization",
    "settingsAgent",
    "settingsConsoleUsers",
    "settingsCertificates",
    "settingsADE",
    "settingsVPP",
    "settingsDeclarative",
    "settingsSecurity",
    "settingsNotifications",
    "settingsReports",
    "settingsSSO",
    "settingsTenants",
    "settingsRoleSettings"
  ].includes(activePage);
  const pickFirstAccessiblePage = (...pages) => pages.find((page) => canViewPage(page)) || pages[0];
  const accountsNavRoute = PAGE_TO_ROUTE[pickFirstAccessiblePage("accountsList", "accountsCreate", "accountsConnect")];
  const devicesNavRoute = PAGE_TO_ROUTE[
    pickFirstAccessiblePage(
      "devicesList",
      "devicesPlatformIOS",
      "devicesPlatformMacOS",
      "devicesPlatformWindows",
      "devicesPlatformAndroid"
    )
  ];
  const profilesNavRoute = PAGE_TO_ROUTE[
    pickFirstAccessiblePage(
      "profilesAll",
      "profilesPlatformIOS",
      "profilesPlatformMacOS",
      "profilesPlatformWindows",
      "profilesPlatformAndroid",
      "policiesAll",
      "policiesCompliance"
    )
  ];
  const appsNavRoute = PAGE_TO_ROUTE[
    pickFirstAccessiblePage(
      "appsAll",
      "appsPlatformIOS",
      "appsPlatformMacOS",
      "appsPlatformWindows",
      "appsPlatformAndroid",
      "appsUpload"
    )
  ];
  const settingsNavRoute = PAGE_TO_ROUTE[
    pickFirstAccessiblePage(
      "settingsConsoleBranding",
      "settingsConsoleDashboard",
      "settingsOrganization",
      "settingsAgent",
      "settingsConsoleUsers",
      "settingsCertificates",
      "settingsADE",
      "settingsVPP",
      "settingsDeclarative",
      "settingsSecurity",
      "settingsNotifications",
      "settingsReports",
      "settingsSSO",
      "settingsRoleSettings"
    )
  ];
  const turukAgentNavRoute = hasPermission("notifications", "edit")
    ? PAGE_TO_ROUTE.agentNotificationsCreate
    : hasPermission("notifications", "view")
      ? PAGE_TO_ROUTE.agentNotificationsSent
      : PAGE_TO_ROUTE.agentSupportTickets;
  const mainNavRenderItems = [
    {
      key: "dashboard",
      canView: hasPermission("dashboard", "view"),
      active: activePage === "dashboard",
      route: PAGE_TO_ROUTE.dashboard,
      label: t("navDashboard")
    },
    {
      key: "command_center",
      canView: hasPermission("devices.commands", "view"),
      active: activePage === "commandCenter",
      route: PAGE_TO_ROUTE.commandCenter,
      label: lang === "ru" ? "Центр команд" : "Command Center"
    },
    {
      key: "health_status",
      canView: hasPermission("dashboard", "view"),
      active: activePage === "healthStatus",
      route: PAGE_TO_ROUTE.healthStatus,
      label: lang === "ru" ? "Состояние системы" : "System Health"
    },
    {
      key: "accounts",
      canView: hasPermission("accounts", "view"),
      active: accountsNavActive,
      route: accountsNavRoute,
      label: t("navAccounts")
    },
    {
      key: "groups",
      canView: hasPermission("groups", "view"),
      active: groupsNavActive,
      route: PAGE_TO_ROUTE.groups,
      label: t("navAccountsGroups")
    },
    {
      key: "turuk_agent",
      canView: hasPermission("notifications", "view") || hasPermission("settings.agent", "view"),
      active: notificationsNavActive,
      route: turukAgentNavRoute,
      label: t("navNotifications")
    },
    {
      key: "documents",
      canView: hasPermission("documents", "view"),
      active: documentsNavActive,
      route: PAGE_TO_ROUTE.documents,
      label: t("navDocuments")
    },
    {
      key: "action_logs",
      canView: session?.user?.platform_admin === true && hasPermission("settings.security", "view"),
      active: actionLogsNavActive,
      route: PAGE_TO_ROUTE.actionLogs,
      label: t("navActionLogs")
    },
    {
      key: "devices",
      canView: hasPermission("devices", "view"),
      active: devicesNavActive,
      route: devicesNavRoute,
      label: t("navDevices")
    },
    {
      key: "profiles",
      canView: hasPermission("profiles", "view"),
      active: profilesNavActive,
      route: profilesNavRoute,
      label: t("navProfiles")
    },
    {
      key: "apps",
      canView: hasPermission("apps", "view"),
      active: appsNavActive,
      route: appsNavRoute,
      label: t("navApps")
    },
    {
      key: "settings",
      canView: hasPermission("settings", "view"),
      active: settingsNavActive,
      route: settingsNavRoute,
      label: t("navSettings")
    }
  ];
  const mainNavRenderItemByKey = new Map(mainNavRenderItems.map((item) => [item.key, item]));
  const orderedMainNavRenderItems = normalizeMainNavOrder(mainNavOrder)
    .map((key) => mainNavRenderItemByKey.get(key))
    .filter(Boolean);
  const logoClickHref = normalizeConsoleLogoClickURL(consoleLogoClickURL) || DEFAULT_CONSOLE_LOGO_CLICK_URL;
  const unreadSupportTicketCount = agentSupportTickets.reduce((count, rawItem) => {
    const item = rawItem?.ticket || rawItem;
    if (!item || item.status === "closed" || item.viewed_at) {
      return count;
    }
    return count + 1;
  }, 0);
  const unreadSupportTicketLabel = t("supportBellUnread").replace(
    "{count}",
    String(unreadSupportTicketCount)
  );
  const mdmAPNSReminderActive =
    hasPermission("settings.certificates", "view") && mdmAPNSReminders?.active
      ? mdmAPNSReminders.active
      : null;
  const mdmAPNSReminderText = mdmAPNSReminderActive
    ? t("certificatesAPNSRenewBannerText")
        .replace("{days}", String(mdmAPNSReminderActive.days_left ?? "-"))
        .replace("{date}", formatTimestamp(mdmAPNSReminderActive.cert_not_after))
    : "";
  const activeSupportSession = supportSessions.find((item) => {
    const status = String(item.status || "").toLowerCase();
    if (status !== "approved") return false;
    if (!item.last_accessed_at) return false;
    const expiresAt = new Date(item.expires_at || 0).getTime();
    return Number.isFinite(expiresAt) && expiresAt > Date.now();
  });
  const supportAccessBannerText = activeSupportSession
    ? t("supportSessionsClientBanner")
        .replace("{reason}", activeSupportSession.reason || "-")
        .replace("{expires}", formatTimestamp(activeSupportSession.expires_at))
    : "";
  const navParentClassName = (item) =>
    [
      item.active ? "nav-parent active" : "nav-parent",
      closedNavDropdownKey === item.key ? "nav-parent-closed" : ""
    ]
      .filter(Boolean)
      .join(" ");
  const handleNavDropdownMouseEnter = (key) => {
    setClosedNavDropdownKey((current) => (current === key ? "" : current));
  };
  const closeNavDropdown = (event, key) => {
    event.currentTarget.blur();
    setClosedNavDropdownKey(key);
  };

  return (
    <div className="portal-layout">
      <aside className="sidebar">
        <a className="brand brand-has-logo brand-link" href={logoClickHref}>
          <img
            className="brand-logo-image"
            src={consoleLogoDataURL || defaultConsoleLogo}
            alt={t("consoleBrandingLogoAlt")}
          />
        </a>
        <nav>
          {orderedMainNavRenderItems.filter((item) => item.canView).map((item) => {
            if (item.key === "devices") {
              return (
                <div key={item.key} className={navParentClassName(item)} onMouseEnter={() => handleNavDropdownMouseEnter(item.key)}>
                  <a
                    className={item.active ? "nav nav-dropdown-trigger active" : "nav nav-dropdown-trigger"}
                    href={`#${PAGE_TO_ROUTE.devicesList}`}
                  >
                    <span>{item.label}</span>
                    <span className="nav-caret">v</span>
                  </a>
                  <div className="nav-submenu nav-submenu-card">
                    <a className={(activePage === "devices" || activePage === "devicesList") ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.devicesList}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("devicesSectionAll")}
                    </a>
                    <div className="nav-submenu-heading">{t("devicesGroupPlatforms")}</div>
                    <a className={activePage === "devicesPlatformWindows" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.devicesPlatformWindows}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformWindows")}
                    </a>
                    <a className={activePage === "devicesPlatformIOS" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.devicesPlatformIOS}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformIOS")}
                    </a>
                    <a className={activePage === "devicesPlatformMacOS" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.devicesPlatformMacOS}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformMacOS")}
                    </a>
                    <a className={activePage === "devicesPlatformAndroid" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.devicesPlatformAndroid}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformAndroid")}
                    </a>
                  </div>
                </div>
              );
            }
            if (item.key === "profiles") {
              return (
                <div key={item.key} className={navParentClassName(item)} onMouseEnter={() => handleNavDropdownMouseEnter(item.key)}>
                  <a
                    className={item.active ? "nav nav-dropdown-trigger active" : "nav nav-dropdown-trigger"}
                    href={`#${PAGE_TO_ROUTE.profilesAll}`}
                  >
                    <span>{item.label}</span>
                    <span className="nav-caret">v</span>
                  </a>
	                  <div className="nav-submenu nav-submenu-card">
	                    <a className={(activePage === "profiles" || activePage === "profilesAll") ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.profilesAll}`} onClick={(event) => closeNavDropdown(event, item.key)}>
	                      {t("profilesAll")}
	                    </a>
	                    <div className="nav-submenu-heading">{t("devicesGroupPlatforms")}</div>
	                    <a className={activePage === "profilesPlatformWindows" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.profilesPlatformWindows}`} onClick={(event) => closeNavDropdown(event, item.key)}>
	                      {t("platformWindows")}
                    </a>
                    <a className={activePage === "profilesPlatformIOS" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.profilesPlatformIOS}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformIOS")}
                    </a>
                    <a className={activePage === "profilesPlatformMacOS" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.profilesPlatformMacOS}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformMacOS")}
                    </a>
	                    <a className={activePage === "profilesPlatformAndroid" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.profilesPlatformAndroid}`} onClick={(event) => closeNavDropdown(event, item.key)}>
	                      {t("platformAndroid")}
	                    </a>
	                    <a className={activePage === "policiesCompliance" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.policiesCompliance}`} onClick={(event) => closeNavDropdown(event, item.key)}>
	                      {t("policiesCompliance")}
	                    </a>
	                  </div>
                </div>
              );
            }
            if (item.key === "apps") {
              return (
                <div key={item.key} className={navParentClassName(item)} onMouseEnter={() => handleNavDropdownMouseEnter(item.key)}>
                  <a
                    className={item.active ? "nav nav-dropdown-trigger active" : "nav nav-dropdown-trigger"}
                    href={`#${PAGE_TO_ROUTE.appsAll}`}
                  >
                    <span>{item.label}</span>
                    <span className="nav-caret">v</span>
                  </a>
                  <div className="nav-submenu nav-submenu-card">
                    <a className={(activePage === "apps" || activePage === "appsAll") ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.appsAll}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("appsAll")}
                    </a>
                    <div className="nav-submenu-heading">{t("devicesGroupPlatforms")}</div>
                    <a className={activePage === "appsPlatformWindows" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.appsPlatformWindows}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformWindows")}
                    </a>
                    <a className={activePage === "appsPlatformIOS" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.appsPlatformIOS}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformIOS")}
                    </a>
                    <a className={activePage === "appsPlatformMacOS" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.appsPlatformMacOS}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformMacOS")}
                    </a>
                    <a className={activePage === "appsPlatformAndroid" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.appsPlatformAndroid}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("platformAndroid")}
                    </a>
                    <div className="nav-submenu-heading">{t("navApps")}</div>
                    <a className={activePage === "appsUpload" ? "nav nav-sub active" : "nav nav-sub"} href={`#${PAGE_TO_ROUTE.appsUpload}`} onClick={(event) => closeNavDropdown(event, item.key)}>
                      {t("appsUpload")}
                    </a>
                  </div>
                </div>
              );
            }
            return (
              <a
                key={item.key}
                className={item.active ? "nav active" : "nav"}
                href={`#${item.route}`}
              >
                {item.label}
              </a>
            );
          })}
        </nav>
      </aside>

      <main className="main">
        <header className="topbar">
          <h1>{t("appTitle")}</h1>
          <div className="topbar-actions">
            {buildTenantDisplayName(session?.user) && (
              <span className="badge tenant-badge">{buildTenantDisplayName(session?.user)}</span>
            )}
            {hasPermission("settings.agent", "view") && (
              <a
                className={unreadSupportTicketCount > 0 ? "topbar-bell has-unread" : "topbar-bell"}
                href={`#${PAGE_TO_ROUTE.agentSupportTickets}`}
                title={unreadSupportTicketCount > 0 ? unreadSupportTicketLabel : t("supportBellLabel")}
                aria-label={unreadSupportTicketCount > 0 ? unreadSupportTicketLabel : t("supportBellLabel")}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M12 22a2.5 2.5 0 0 0 2.35-1.65h-4.7A2.5 2.5 0 0 0 12 22Zm7-5-1.35-1.55V10a5.67 5.67 0 0 0-4.4-5.62V3a1.25 1.25 0 0 0-2.5 0v1.38A5.67 5.67 0 0 0 6.35 10v5.45L5 17v1.4h14V17Z" />
                </svg>
                {unreadSupportTicketCount > 0 && (
                  <span className="topbar-bell-count">
                    {unreadSupportTicketCount > 99 ? "99+" : unreadSupportTicketCount}
                  </span>
                )}
              </a>
            )}
            <span className="badge muted-badge">{buildDisplayName(session?.user)}</span>
            <select className="text-input compact" value={lang} onChange={(event) => setLang(event.target.value)}>
              <option value="ru">RU</option>
              <option value="en">EN</option>
            </select>
            <button className="danger" onClick={handleSignOut}>
              {t("signOut")}
            </button>
          </div>
        </header>

        {infoMessage && <div className="banner info">{infoMessage}</div>}
        {errorMessage && <div className="banner error">{errorMessage}</div>}
        {mdmAPNSReminderActive && (
          <div className={`banner warning apns-renew-banner ${mdmAPNSReminderActive.severity === "critical" ? "critical" : ""}`}>
            <div>
              <strong>{t("certificatesAPNSRenewBannerTitle")}</strong>
              <span>{mdmAPNSReminderText}</span>
            </div>
            <a className="banner-action" href={`#${PAGE_TO_ROUTE.settingsCertificates}`}>
              {t("certificatesAPNSRenewBannerAction")}
            </a>
          </div>
        )}
        {activeSupportSession && (
          <div className="banner warning">
            <strong>{t("supportSessionsClientBannerTitle")}</strong>
            <span>{supportAccessBannerText}</span>
          </div>
        )}

        {renderContent()}
      </main>

      {showDocumentModal && (
        <div className="modal-backdrop" onClick={closeDocumentModal}>
          <form className="modal-card" onSubmit={saveDocument} onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>{documentModalMode === "edit" ? t("documentsEdit") : (lang === "ru" ? "Добавить файл" : "Add file")}</h3>
              <button type="button" className="icon-button" onClick={closeDocumentModal}>
                ×
              </button>
            </div>

            <div className="modal-grid">
              {documentModalMode === "edit" && documentForm.documentType === "file" && (
                <>
                  <label>{t("documentsFileName")}</label>
                  <input
                    className="text-input"
                    required
                    value={documentForm.fileName}
                    onChange={(event) => setDocumentForm((prev) => ({ ...prev, fileName: event.target.value }))}
                  />
                </>
              )}

              {documentModalMode === "edit" && documentForm.documentType === "link" && (
                <>
                  <label>{t("documentsURL")}</label>
                  <input
                    className="text-input"
                    required
                    placeholder="https://..."
                    value={documentForm.url}
                    onChange={(event) => setDocumentForm((prev) => ({ ...prev, url: event.target.value }))}
                  />
                </>
              )}

              <label>{lang === "ru" ? "Папка" : "Folder"}</label>
              <div className="document-folder-select-row">
                <select
                  className="text-input"
                  value={documentForm.folderId}
                  onChange={(event) => setDocumentForm((prev) => ({ ...prev, folderId: event.target.value }))}
                >
                  <option value="">{lang === "ru" ? "Без папки" : "No folder"}</option>
                  {buildDocumentFolderOptions().map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.label}
                    </option>
                  ))}
                </select>
                {hasPermission("documents", "edit") && (
                  <button type="button" className="secondary compact-button" onClick={createDocumentFolderForDocumentForm}>
                    +
                  </button>
                )}
              </div>

              {documentModalMode !== "edit" && (
                <>
                  <div className="documents-upload-target">
                    {lang === "ru" ? "Куда загрузить" : "Upload destination"}:{" "}
                    <strong>
                      {documentForm.folderId
                        ? buildDocumentFolderOptions().find((folder) => folder.id === documentForm.folderId)?.label ||
                          (lang === "ru" ? "Папка" : "Folder")
                        : lang === "ru"
                          ? "Без папки"
                          : "No folder"}
                    </strong>
                  </div>
                  <label className="documents-file-picker">
                    <input
                      type="file"
                      multiple
                      accept={ALLOWED_DOCUMENT_UPLOAD_EXTENSIONS.join(",")}
                      onChange={handleDocumentFileChange}
                    />
                    <span>{lang === "ru" ? "Загрузить с компьютера" : "Upload from computer"}</span>
                  </label>
                  <p className="documents-upload-hint">
                    {t("documentsAllowedFileTypes").replace("{types}", ALLOWED_DOCUMENT_UPLOAD_EXTENSIONS.join(", "))}
                  </p>
                  <div className="documents-upload-list">
                    {(documentForm.documentFiles || []).map((file) => (
                      <div className="documents-upload-item" key={file.id}>
                        <div>
                          <strong>{file.name}</strong>
                          <span>
                            {formatDocumentFileSize(file.size)}
                            {file.contentType ? ` · ${file.contentType}` : ""}
                          </span>
                        </div>
                        <button type="button" className="toolbar-action danger" onClick={() => removeDocumentUploadFile(file.id)}>
                          {lang === "ru" ? "Убрать" : "Remove"}
                        </button>
                      </div>
                    ))}
                    {(documentForm.documentFiles || []).length === 0 && (
                      <p className="muted">{lang === "ru" ? "Файлы еще не выбраны" : "No files selected"}</p>
                    )}
                  </div>
                </>
              )}

              {documentModalMode === "edit" && documentForm.documentType === "file" ? (
                <>
                  <label>{t("documentsFile")}</label>
                  <input
                    className="text-input"
                    type="file"
                    accept={ALLOWED_DOCUMENT_UPLOAD_EXTENSIONS.join(",")}
                    onChange={handleDocumentFileChange}
                  />
                  <p className="documents-upload-hint">
                    {t("documentsAllowedFileTypes").replace("{types}", ALLOWED_DOCUMENT_UPLOAD_EXTENSIONS.join(", "))}
                  </p>
                </>
              ) : null}

              {documentModalMode === "edit" && (
                <>
                  <label>{t("documentsActive")}</label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={documentForm.isActive}
                      onChange={(event) => setDocumentForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                    />
                    {documentForm.isActive ? t("documentsStatusActive") : t("documentsStatusInactive")}
                  </label>
                </>
              )}
            </div>

            <div className="document-acknowledgement-box">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={documentForm.requireAck}
                  onChange={(event) => setDocumentForm((prev) => ({ ...prev, requireAck: event.target.checked }))}
                />
                {lang === "ru" ? "К ознакомлению" : "Requires acknowledgement"}
              </label>
              <label>{lang === "ru" ? "Срок ознакомления" : "Acknowledgement due date"}</label>
              <input
                className="text-input"
                type="date"
                value={documentForm.ackDueDate}
                disabled={!documentForm.requireAck}
                onChange={(event) => setDocumentForm((prev) => ({ ...prev, ackDueDate: event.target.value }))}
              />
            </div>

            <div className="panel compact-panel">
              <h4>{t("documentsGroups")}</h4>
              <div className="assignment-grid">
                {groups.map((group) => (
                  <label key={group.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={(documentForm.groupIds || []).includes(group.id)}
                      onChange={() => toggleDocumentGroupSelection(group.id)}
                    />
                    {group.name || group.id}
                  </label>
                ))}
                {groups.length === 0 && <p className="muted">{t("groupsNoItems")}</p>}
              </div>
            </div>

            <div className="action-row">
              <button type="submit">
                {documentModalMode === "edit" ? t("documentsSave") : (lang === "ru" ? "Добавить" : "Add")}
              </button>
              {documentModalMode === "edit" && (
                <button type="button" className="danger" onClick={deleteDocumentFromModal}>
                  {t("documentsDelete")}
                </button>
              )}
              <button type="button" className="secondary" onClick={closeDocumentModal}>
                {t("cancel")}
              </button>
            </div>
          </form>
        </div>
      )}

      {documentGroupsItem && (
        <div className="modal-backdrop" onClick={closeDocumentGroupsModal}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <div>
                <h3>{lang === "ru" ? "Назначенные группы" : "Assigned groups"}</h3>
                <p className="muted">{documentGroupsItem.title || documentGroupsItem.file_name || "-"}</p>
              </div>
              <button type="button" className="icon-button" onClick={closeDocumentGroupsModal}>
                ×
              </button>
            </div>
            <div className="devices-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{lang === "ru" ? "Группа" : "Group"}</th>
                  </tr>
                </thead>
                <tbody>
                  {(documentGroupsItem.group_names || []).map((name, index) => (
                    <tr key={`${name || "group"}-${documentGroupsItem.group_ids?.[index] || index}`}>
                      <td>{name || documentGroupsItem.group_ids?.[index] || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!Array.isArray(documentGroupsItem.group_names) || documentGroupsItem.group_names.length === 0) && (
                <p className="devices-empty">{lang === "ru" ? "Группы не назначены." : "No groups assigned."}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {documentAssignedDevicesItem && (
        <div className="modal-backdrop" onClick={closeDocumentAssignedDevicesModal}>
          <div className="modal-card modal-card-wide document-assigned-devices-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <div>
                <h3>{lang === "ru" ? "Назначенные устройства" : "Assigned devices"}</h3>
                <p className="muted">{documentAssignedDevicesItem.title || documentAssignedDevicesItem.file_name || "-"}</p>
              </div>
              <button type="button" className="icon-button" onClick={closeDocumentAssignedDevicesModal}>
                ×
              </button>
            </div>
            <div className="devices-toolbar-actions">
              <button className="toolbar-action secondary" onClick={exportDocumentAssignedDevicesReport}>
                CSV
              </button>
            </div>
            {documentAssignedDevicesLoading ? (
              <p className="muted">{t("loading")}</p>
            ) : (
              <div className="devices-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{lang === "ru" ? "Аккаунт" : "Account"}</th>
                      <th>{t("devicesColName")}</th>
                      <th>{t("devicesColSerial")}</th>
                      <th>{t("devicesColOSVersion")}</th>
                      <th>{lang === "ru" ? "Группы" : "Groups"}</th>
                      <th>{lang === "ru" ? "Открывал" : "Opened"}</th>
                      <th>{t("devicesColLastSync")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentAssignedDevices.map((item) => (
                      <tr key={item.udid}>
                        <td>
                          {item.account_name || item.account_email || "-"}
                          {item.account_email && <div className="muted small-text">{item.account_email}</div>}
                        </td>
                        <td>{item.device_name || "-"}</td>
                        <td>{item.serial_number || "-"}</td>
                        <td>{item.os_version || "-"}</td>
                        <td>
                          {Array.isArray(item.group_names) && item.group_names.length > 0
                            ? item.group_names.join(", ")
                            : "-"}
                        </td>
                        <td>{item.opened_at ? formatTimestamp(item.opened_at) : "-"}</td>
                        <td>{formatTimestamp(item.last_checkin_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {documentAssignedDevices.length === 0 && (
                  <p className="devices-empty">
                    {lang === "ru" ? "Назначенных устройств пока нет." : "No assigned devices yet."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showCreateProfileModal && renderProfileBuilderModal("create")}
      {showProfileViewModal && renderProfileBuilderModal("view")}
      {renderProfileDevicesModal()}
      {showEnableLostModeModal && (
        <div className="modal-backdrop" onClick={closeEnableLostModeModal}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <h3>
              {enableLostModeRequestLocation
                ? t("deviceDetailsLocationLostModeModalTitle")
                : t("deviceDetailsLostModeModalTitle")}
            </h3>
            {enableLostModeRequestLocation && (
              <p className="muted">{t("deviceDetailsLocationLostModeModalHint")}</p>
            )}
            <div className="modal-grid">
              <label>{t("deviceDetailsLostModeModalMessage")} *</label>
              <input
                className="text-input"
                maxLength={120}
                value={enableLostModeForm.message}
                onChange={(event) =>
                  setEnableLostModeForm((prev) => ({
                    ...prev,
                    message: event.target.value
                  }))
                }
              />
              <label>{t("deviceDetailsLostModeModalPhone")} *</label>
              <input
                className="text-input"
                maxLength={32}
                value={enableLostModeForm.phoneNumber}
                onChange={(event) =>
                  setEnableLostModeForm((prev) => ({
                    ...prev,
                    phoneNumber: event.target.value
                  }))
                }
              />
            </div>
            <div className="action-row">
              <button onClick={submitEnableLostMode}>
                {enableLostModeRequestLocation
                  ? t("deviceDetailsLocationLostModeModalSubmit")
                  : t("deviceDetailsLostModeModalSubmit")}
              </button>
              <button className="secondary" onClick={closeEnableLostModeModal}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showScheduleOSUpdateModal && (
        <div className="modal-backdrop" onClick={closeScheduleOSUpdateModal}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <h3>
              {scheduleOSUpdateForm.installAction === "InstallASAP"
                ? t("deviceDetailsOSUpdateModalTitleInstall")
                : t("deviceDetailsOSUpdateModalTitleDownload")}
            </h3>
            <div className="muted">{t("deviceDetailsOSUpdateModalHint")}</div>
            <div className="muted">
              {t("deviceDetailsOSUpdateModalTargets")}: {scheduleOSUpdateTargetUDIDs.length || 1}
            </div>
            <div className="modal-grid">
              <label>{t("deviceDetailsOSUpdateModalProductKey")}</label>
              <input
                className="text-input"
                value={scheduleOSUpdateForm.productKey}
                onChange={(event) =>
                  setScheduleOSUpdateForm((prev) => ({
                    ...prev,
                    productKey: event.target.value
                  }))
                }
              />
              <label>{t("deviceDetailsOSUpdateModalProductVersion")}</label>
              <input
                className="text-input"
                value={scheduleOSUpdateForm.productVersion}
                onChange={(event) =>
                  setScheduleOSUpdateForm((prev) => ({
                    ...prev,
                    productVersion: event.target.value
                  }))
                }
              />
            </div>
            <div className="action-row">
              <button onClick={submitScheduleOSUpdate}>
                {scheduleOSUpdateForm.installAction === "InstallASAP"
                  ? t("deviceDetailsOSUpdateModalSubmitInstall")
                  : t("deviceDetailsOSUpdateModalSubmitDownload")}
              </button>
              <button className="secondary" onClick={closeScheduleOSUpdateModal}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showAppleCommandModal && appleCommandForm.command && (
        <div className="modal-backdrop" onClick={closeAppleCommandModal}>
          <div className="modal-card modal-card-medium" onClick={(event) => event.stopPropagation()}>
            <h3>{t(appleCommandForm.command.labelKey)}</h3>
            <p className="muted">{t("deviceDetailsAppleCommandModalHint")}</p>
            {appleCommandForm.command.destructive && (
              <div className="danger-note">{t("deviceDetailsAppleCommandDestructiveWarning")}</div>
            )}
            <div className="modal-grid">
              <label>{t("deviceDetailsAppleCommandParameters")}</label>
              <textarea
                className="text-input apple-command-json"
                spellCheck={false}
                value={appleCommandForm.parametersJSON}
                onChange={(event) =>
                  setAppleCommandForm((prev) => ({
                    ...prev,
                    parametersJSON: event.target.value
                  }))
                }
              />
            </div>
            <div className="action-row">
              <button
                className={appleCommandForm.command.destructive ? "danger" : ""}
                onClick={submitAppleCommand}
              >
                {t("deviceDetailsAppleCommandSubmit")}
              </button>
              <button className="secondary" onClick={closeAppleCommandModal}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeviceSettingModal && (
        <div className="modal-backdrop" onClick={closeDeviceSettingModal}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <h3>{lang === "ru" ? "Настройка устройства" : "Configure setting"}</h3>
            <p className="muted">
              {lang === "ru"
                ? "Одноразовая управляемая настройка Apple MDM. Пользователь может изменить часть настроек позже, если это не запрещено профилем."
                : "One-time Apple MDM managed setting. Users may change some settings later unless a profile restricts them."}
            </p>
            <div className="modal-grid">
              <label>{lang === "ru" ? "Настройка" : "Setting"}</label>
              <select
                value={deviceSettingForm.settingType}
                onChange={(event) =>
                  setDeviceSettingForm((prev) => ({ ...prev, settingType: event.target.value }))
                }
              >
                <option value="device_name">{lang === "ru" ? "Имя устройства" : "Device name"}</option>
                <option value="time_zone">{lang === "ru" ? "Часовой пояс" : "Time zone"}</option>
                <option value="bluetooth">Bluetooth</option>
                <option value="data_roaming">{lang === "ru" ? "Роуминг данных" : "Data roaming"}</option>
                <option value="personal_hotspot">{lang === "ru" ? "Режим модема" : "Personal Hotspot"}</option>
                <option value="app_analytics">App Analytics</option>
              </select>
              {["device_name", "time_zone"].includes(deviceSettingForm.settingType) ? (
                <>
                  <label>{lang === "ru" ? "Значение" : "Value"}</label>
                  <input
                    className="text-input"
                    value={deviceSettingForm.value}
                    onChange={(event) =>
                      setDeviceSettingForm((prev) => ({ ...prev, value: event.target.value }))
                    }
                    placeholder={deviceSettingForm.settingType === "time_zone" ? "America/Vancouver" : ""}
                  />
                </>
              ) : (
                <>
                  <label>{lang === "ru" ? "Состояние" : "State"}</label>
                  <select
                    value={deviceSettingForm.enabled ? "true" : "false"}
                    onChange={(event) =>
                      setDeviceSettingForm((prev) => ({ ...prev, enabled: event.target.value === "true" }))
                    }
                  >
                    <option value="true">{lang === "ru" ? "Включить" : "Enable"}</option>
                    <option value="false">{lang === "ru" ? "Отключить" : "Disable"}</option>
                  </select>
                </>
              )}
            </div>
            <div className="action-row">
              <button onClick={configureManagedSetting}>
                {lang === "ru" ? "Отправить настройку" : "Send setting"}
              </button>
              <button className="secondary" onClick={closeDeviceSettingModal}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateAccountModal && (
        <div className="modal-backdrop" onClick={closeAccountModal}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <h3>{accountModalMode === "edit" ? t("accountsEditModalTitle") : t("accountsCreateModalTitle")}</h3>
            <div className="modal-grid">
              <input
                type="text"
                name="modal_fake_username"
                autoComplete="username"
                tabIndex={-1}
                aria-hidden="true"
                style={{ display: "none" }}
              />
              <input
                type="password"
                name="modal_fake_password"
                autoComplete="current-password"
                tabIndex={-1}
                aria-hidden="true"
                style={{ display: "none" }}
              />
              <label>{t("accountsName")}</label>
              <input
                className="text-input"
                name="account_name"
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                value={createAccountForm.accountName}
                onChange={(event) => setCreateAccountForm((prev) => ({ ...prev, accountName: event.target.value }))}
              />
              <div className="muted">{t("accountsLoginHint")}</div>

              <label>{t("accountsFirstName")}</label>
              <input
                className="text-input"
                name="given_name"
                autoComplete="given-name"
                value={createAccountForm.firstName}
                onChange={(event) => setCreateAccountForm((prev) => ({ ...prev, firstName: event.target.value }))}
              />

              <label>{t("accountsSecondName")}</label>
              <input
                className="text-input"
                name="family_name"
                autoComplete="family-name"
                value={createAccountForm.secondName}
                onChange={(event) => setCreateAccountForm((prev) => ({ ...prev, secondName: event.target.value }))}
              />

              <label>{t("accountsEmail")} *</label>
              <input
                className="text-input"
                type="email"
                name="account_email"
                autoComplete="email"
                value={createAccountForm.email}
                onChange={(event) => setCreateAccountForm((prev) => ({ ...prev, email: event.target.value }))}
              />

              <label>{t("accountsDepartment")}</label>
              <input
                className="text-input"
                name="account_department"
                autoComplete="organization"
                value={createAccountForm.department}
                onChange={(event) => setCreateAccountForm((prev) => ({ ...prev, department: event.target.value }))}
              />

              <label>
                {t("accountsPassword")}
                {accountModalMode === "create" ? " *" : ""}
              </label>
              <div>
                <input
                  className="text-input"
                  type="password"
                  name="account_password"
                  autoComplete="new-password"
                  value={createAccountForm.password}
                  onChange={(event) => setCreateAccountForm((prev) => ({ ...prev, password: event.target.value }))}
                />
                {accountModalMode === "edit" && <div className="muted">{t("accountsPasswordKeepHint")}</div>}
              </div>

              {accountModalMode === "edit" && (
                <>
                  <label>{t("tableStatus")}</label>
                  <label className="check-inline">
                    <input
                      type="checkbox"
                      checked={!!createAccountForm.isActive}
                      onChange={(event) =>
                        setCreateAccountForm((prev) => ({ ...prev, isActive: event.target.checked }))
                      }
                    />
                    {t("accountsActiveLabel")}
                  </label>
                </>
              )}
            </div>
            <div className="action-row">
              <button onClick={submitAccountModal}>
                {accountModalMode === "edit" ? t("accountsUpdate") : t("accountsCreateAccount")}
              </button>
              <button className="secondary" onClick={closeAccountModal}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAccountImportModal && (
        <div className="modal-backdrop" onClick={closeAccountImportModal}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <div className="section-title-with-help">
                <h3>{t("accountsImportTitle")}</h3>
                <span className="help-tooltip account-import-help-tooltip" tabIndex={0}>
                  ?
                  <span className="help-tooltip-card account-import-help-card">{t("accountsImportHelp")}</span>
                </span>
              </div>
              <button type="button" className="icon-button" onClick={closeAccountImportModal} disabled={accountImportInFlight}>
                ×
              </button>
            </div>

            <div className="modal-grid">
              <label className="documents-file-picker">
                <input type="file" accept=".csv,.txt,.xlsx,.xls" onChange={handleAccountImportFile} disabled={accountImportInFlight} />
                <span>{t("accountsImportUpload")}</span>
              </label>
              <p className="documents-upload-hint">{t("accountsImportFormat")}</p>

              {accountImportFileName && (
                <div className="documents-upload-target">
                  {t("accountsImportFile")}: <strong>{accountImportFileName}</strong>
                </div>
              )}

              {accountImportRows.length > 0 && (
                <div className="documents-upload-list account-import-preview">
                  {accountImportRows.slice(0, 8).map((row) => (
                    <div className="documents-upload-item" key={`account-import-${row.rowNumber}`}>
                      <div>
                        <strong>{row.accountName || row.email || `${t("row")} ${row.rowNumber}`}</strong>
                        <span>
                          {row.email || "-"}
                          {row.firstName || row.secondName ? ` · ${[row.firstName, row.secondName].filter(Boolean).join(" ")}` : ""}
                          {row.department ? ` · ${row.department}` : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                  {accountImportRows.length > 8 && (
                    <p className="muted">{t("accountsImportMoreRows").replace("{count}", String(accountImportRows.length - 8))}</p>
                  )}
                </div>
              )}

              {accountImportErrors.length > 0 && (
                <div className="danger-note account-import-errors">
                  {accountImportErrors.map((item, index) => (
                    <div key={`account-import-error-${index}`}>{item}</div>
                  ))}
                </div>
              )}
            </div>

            <div className="action-row">
              <button onClick={submitAccountImport} disabled={accountImportInFlight || accountImportRows.length === 0 || accountImportErrors.length > 0}>
                {accountImportInFlight ? t("loading") : t("accountsImportSubmit")}
              </button>
              <button className="secondary" onClick={closeAccountImportModal} disabled={accountImportInFlight}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateGroupModal && (
        <div className="modal-backdrop" onClick={() => setShowCreateGroupModal(false)}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>{t("groupsCreateModalTitle")}</h3>
              <button className="icon-button" onClick={() => setShowCreateGroupModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-grid">
              <label>{t("groupsName")} *</label>
              <input
                className="text-input"
                value={createGroupForm.name}
                onChange={(event) => setCreateGroupForm({ name: event.target.value })}
              />
            </div>
            <div className="action-row">
              <button onClick={createGroup}>{t("create")}</button>
              <button className="secondary" onClick={() => setShowCreateGroupModal(false)}>
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignAccountsGroupsModal && (
        <div className="modal-backdrop" onClick={() => setShowAssignAccountsGroupsModal(false)}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>{t("accountsAddToGroupModalTitle")}</h3>
              <button className="icon-button" onClick={() => setShowAssignAccountsGroupsModal(false)}>
                ×
              </button>
            </div>
            <div className="muted">
              {t("accountsSelectedCount")}: {selectedAccountIDs.length}
            </div>
            <div className="modal-grid">
              <label>{t("profilesGroups")}</label>
              <div className="check-list">
                {groups.map((group) => (
                  <label key={group.id} className="check-inline">
                    <input
                      type="checkbox"
                      checked={assignGroupsForm.groupIds.includes(group.id)}
                      onChange={() => toggleAssignGroupSelection(group.id)}
                    />
                    {group.name}
                  </label>
                ))}
                {groups.length === 0 && <p className="empty">{t("groupsNoItems")}</p>}
              </div>
            </div>
            <div className="action-row">
              <button onClick={assignSelectedAccountsToGroups}>{t("accountsAssignToGroups")}</button>
              <button className="secondary" onClick={() => setShowAssignAccountsGroupsModal(false)}>
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignAppsGroupsModal && (
        <div className="modal-backdrop" onClick={() => setShowAssignAppsGroupsModal(false)}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>{t("appsEditGroupsModalTitle")}</h3>
              <button className="icon-button" onClick={() => setShowAssignAppsGroupsModal(false)}>
                ×
              </button>
            </div>
            <div className="muted">
              {t("appsSelectedCount")}: {selectedAppIDs.length}
            </div>
            <div className="modal-grid">
              <label>{t("profilesGroups")}</label>
              <div className="check-list">
                {groups.map((group) => (
                  <label key={group.id} className="check-inline">
                    <input
                      type="checkbox"
                      checked={assignAppGroupsForm.groupIds.includes(group.id)}
                      onChange={() => toggleAssignAppGroupSelection(group.id)}
                    />
                    {group.name}
                  </label>
                ))}
                {groups.length === 0 && <p className="empty">{t("groupsNoItems")}</p>}
              </div>
            </div>
            <div className="action-row">
              <button onClick={assignSelectedAppsToGroups}>{t("appsAssignToGroups")}</button>
              <button className="secondary" onClick={() => setShowAssignAppsGroupsModal(false)}>
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignProfilesGroupsModal && (
        <div className="modal-backdrop" onClick={() => setShowAssignProfilesGroupsModal(false)}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>{t("profilesEditGroupsModalTitle")}</h3>
              <button className="icon-button" onClick={() => setShowAssignProfilesGroupsModal(false)}>
                ×
              </button>
            </div>
            <div className="muted">
              {t("profilesSelectedCount")}: {selectedProfileIDs.length}
            </div>
            <div className="modal-grid">
              <label>{t("profilesGroups")}</label>
              <div className="check-list">
                {groups.map((group) => (
                  <label key={group.id} className="check-inline">
                    <input
                      type="checkbox"
                      checked={assignProfileGroupsForm.groupIds.includes(group.id)}
                      onChange={() => toggleAssignProfileGroupSelection(group.id)}
                    />
                    {group.name}
                  </label>
                ))}
                {groups.length === 0 && <p className="empty">{t("groupsNoItems")}</p>}
              </div>
            </div>
            <div className="action-row">
              <button onClick={assignSelectedProfilesToGroups}>{t("profilesAssignToGroups")}</button>
              <button className="secondary" onClick={() => setShowAssignProfilesGroupsModal(false)}>
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPlatformPickerModal && (
        <div className="modal-backdrop" onClick={closePlatformPicker}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>
                {platformPickerContext === "profiles"
                  ? t("platformPickerTitleProfiles")
                  : t("platformPickerTitleApps")}
              </h3>
              <button className="icon-button" onClick={closePlatformPicker}>
                ×
              </button>
            </div>
            <p className="muted">
              {platformPickerContext === "profiles"
                ? t("platformPickerHintProfiles")
                : t("platformPickerHintApps")}
            </p>
            <div className="platform-picker-grid">
              {getPlatformPickerOptions().map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="platform-picker-option"
                  disabled={Boolean(item.disabled)}
                  onClick={() => handlePlatformPickerSelection(item.key)}
                >
                  {item.label}
                  {item.disabled && platformPickerContext === "profiles" && (
                    <span>{lang === "ru" ? "Пока недоступно" : "Not available yet"}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAppStoreModal && (
        <div className="modal-backdrop" onClick={() => setShowAppStoreModal(false)}>
          <div className="modal-card modal-card-store" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>
                {appsStorePlatform === "android"
                  ? t("appsStoreTitleAndroid")
                  : appsStorePlatform === "macos"
                    ? t("appsStoreTitleMac")
                    : t("appsStoreTitleIOS")}
              </h3>
              <button className="icon-button" onClick={() => setShowAppStoreModal(false)}>
                ×
              </button>
            </div>
            <p className="muted">
              {appsStorePlatform === "android"
                ? t("appsStoreHintAndroid")
                : appsStorePlatform === "macos"
                  ? t("appsStoreHintMac")
                  : t("appsStoreHintIOS")}
            </p>

            {appsStorePlatform === "android" ? (
              <div className="app-store-managed-wrap">
                {appsStoreLoading && <p className="muted">{t("appsManagedPlayLoading")}</p>}
                {!appsStoreLoading && appsManagedPlayURL && <p className="muted">{t("appsManagedPlaySelectHint")}</p>}
                {!appsStoreLoading && appsManagedPlayURL && (
                  <div className="app-store-managed-iframe" ref={managedPlayContainerRef} aria-label={t("appsManagedPlayFrameTitle")} />
                )}
                {!appsStoreLoading && appsManagedPlayURL && !appsManagedPlayReady && !appsManagedPlayError && (
                  <p className="muted">{t("appsManagedPlayLoading")}</p>
                )}
                {!appsStoreLoading && appsManagedPlayError && <p className="error">{appsManagedPlayError}</p>}
                {!appsStoreLoading && !appsManagedPlayURL && !appsManagedPlayError && (
                  <p className="empty">{t("appsManagedPlayInitFailed")}</p>
                )}
                {appsManagedPlayURL && (
                  <a
                    className="link-button app-store-managed-fallback-link"
                    href={appsManagedPlayURL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("appsManagedPlayOpenFallback")}
                  </a>
                )}
              </div>
            ) : (
              <>
                <div className="inline-form inline-form-wide">
                  <input
                    className="text-input"
                    value={appsStoreQuery}
                    placeholder={t("appsSearchAll")}
                    onChange={(event) => setAppsStoreQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        searchIOSAppStore();
                      }
                    }}
                  />
                  <select
                    className="text-input"
                    value={appsStoreCountry}
                    onChange={(event) => setAppsStoreCountry(event.target.value)}
                  >
                    <option value="US">{t("appsStoreCountry")}: US</option>
                    <option value="CA">{t("appsStoreCountry")}: CA</option>
                    <option value="GB">{t("appsStoreCountry")}: GB</option>
                    <option value="DE">{t("appsStoreCountry")}: DE</option>
                    <option value="FR">{t("appsStoreCountry")}: FR</option>
                    <option value="RU">{t("appsStoreCountry")}: RU</option>
                  </select>
                  <button onClick={searchIOSAppStore} disabled={appsStoreLoading}>
                    {appsStoreLoading ? t("signingIn") : t("appsStoreSearchButton")}
                  </button>
                </div>

                <div className="app-store-results">
                  {appsStoreResults.map((item) => (
                    <div key={`${item.itunes_store_id}-${item.bundle_id}`} className="app-store-item">
                      <div className="app-store-artwork-wrap">
                        {item.artwork_url ? (
                          <img src={item.artwork_url} alt={item.name} className="app-store-artwork" />
                        ) : (
                          <div className="app-store-artwork app-store-artwork-fallback">APP</div>
                        )}
                      </div>
                      <div className="app-store-meta">
                        <div className="app-store-name">{item.name}</div>
                        <div className="muted">{item.bundle_id}</div>
                        <div className="muted">
                          {item.developer || "-"}
                          {item.version ? ` · v${item.version}` : ""}
                        </div>
                      </div>
                      <div className="action-row">
                        {item.track_view_url && (
                          <a
                            className="link-button"
                            href={item.track_view_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t("appsStoreOpen")}
                          </a>
                        )}
                        <button onClick={() => addAppFromStore(item)}>{t("appsStoreAddToCatalog")}</button>
                      </div>
                    </div>
                  ))}
                  {!appsStoreLoading && appsStoreResults.length === 0 && (
                    <p className="empty">{t("appsStoreNoItems")}</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showRestrictionsAppPickerModal && (
        <div className="modal-backdrop" onClick={closeRestrictionsAppPicker}>
          <div
            className="modal-card modal-card-small restrictions-app-picker-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-heading">
              <h3>{t("profileRestrictionsAppPickerTitle")}</h3>
              <button className="icon-button" onClick={closeRestrictionsAppPicker}>
                ×
              </button>
            </div>
            <p className="muted">{t("profileRestrictionsAppPickerHint")}</p>
            <div className="inline-form inline-form-wide">
              <input
                className="text-input"
                value={restrictionsAppPickerQuery}
                placeholder={t("profileRestrictionsAppPickerSearchPlaceholder")}
                onChange={(event) => setRestrictionsAppPickerQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    searchRestrictionsApps();
                  }
                }}
              />
              <button onClick={searchRestrictionsApps} disabled={restrictionsAppPickerLoading}>
                {restrictionsAppPickerLoading ? t("signingIn") : t("appsStoreSearchButton")}
              </button>
            </div>
            <div className="restrictions-app-picker-results">
              {restrictionsAppPickerResults.map((item) => (
                <div
                  key={`${item.bundle_id}-${item.itunes_store_id || "app"}`}
                  className="app-store-item restrictions-app-picker-item"
                >
                  <div className="app-store-meta">
                    <div className="app-store-name">{item.name}</div>
                    <div className="muted">{item.bundle_id}</div>
                  </div>
                  <button onClick={() => chooseRestrictionsApp(item)}>
                    {t("profileRestrictionsAppPickerChoose")}
                  </button>
                </div>
              ))}
              {!restrictionsAppPickerLoading && restrictionsAppPickerResults.length === 0 && (
                <p className="empty">{t("profileRestrictionsAppPickerNoItems")}</p>
              )}
            </div>
            <div className="action-row">
              <button className="secondary" onClick={closeRestrictionsAppPicker}>
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAirPlayDestinationModal && (
        <div className="modal-backdrop" onClick={closeAirPlayDestinationModal}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <h3>{t("profileAirPlayDestinationDialogTitle")}</h3>
            <div className="modal-grid">
              <label>{t("profileAirPlayDestinationName")}</label>
              <input
                className="text-input"
                value={airPlayDestinationForm.deviceName}
                onChange={(event) =>
                  setAirPlayDestinationForm((prev) => ({
                    ...prev,
                    deviceName: event.target.value
                  }))
                }
                placeholder={t("profileAirPlayDeviceName")}
              />

              <label>{t("profileAirPlayDestinationPassword")}</label>
              <input
                className="text-input"
                type="password"
                value={airPlayDestinationForm.password}
                onChange={(event) =>
                  setAirPlayDestinationForm((prev) => ({
                    ...prev,
                    password: event.target.value
                  }))
                }
                placeholder={t("profileAirPlayPassword")}
              />
            </div>
            <div className="action-row">
              <button className="secondary" onClick={closeAirPlayDestinationModal}>
                {t("cancel")}
              </button>
              <button onClick={saveAirPlayDestination}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {showAirPrintPrinterModal && (
        <div className="modal-backdrop" onClick={closeAirPrintPrinterModal}>
          <div className="modal-card modal-card-airprint" onClick={(event) => event.stopPropagation()}>
            <div className="modal-grid modal-grid-airprint">
              <label>{lang === "ru" ? "Настроить принтер:" : "Configure printer:"}</label>
              <select
                className="text-input"
                value={airPrintPrinterForm.configureMode}
                onChange={(event) =>
                  setAirPrintPrinterForm((prev) => ({
                    ...prev,
                    configureMode: event.target.value
                  }))
                }
              >
                <option value="manual">{lang === "ru" ? "Вручную" : "Manually"}</option>
              </select>

              <span aria-hidden="true" />
              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={Boolean(airPrintPrinterForm.forceTLS)}
                  onChange={(event) =>
                    setAirPrintPrinterForm((prev) => ({
                      ...prev,
                      forceTLS: event.target.checked
                    }))
                  }
                />
                {lang === "ru" ? "Использовать TLS" : "Use TLS"}
              </label>

              <label>{lang === "ru" ? "Имя хоста или IP-адрес:" : "Host Name or IP Address:"}</label>
              <input
                className="text-input"
                value={airPrintPrinterForm.hostName}
                onChange={(event) =>
                  setAirPrintPrinterForm((prev) => ({
                    ...prev,
                    hostName: event.target.value
                  }))
                }
                placeholder="[required]"
                autoFocus
              />

              <label>{lang === "ru" ? "Порт:" : "Port:"}</label>
              <input
                className="text-input"
                type="number"
                min={1}
                max={65535}
                value={airPrintPrinterForm.port}
                onChange={(event) =>
                  setAirPrintPrinterForm((prev) => ({
                    ...prev,
                    port: event.target.value
                  }))
                }
                placeholder="[optional]"
              />

              <label>{lang === "ru" ? "Путь ресурса:" : "Resource path:"}</label>
              <input
                className="text-input"
                value={airPrintPrinterForm.resourcePath}
                onChange={(event) =>
                  setAirPrintPrinterForm((prev) => ({
                    ...prev,
                    resourcePath: event.target.value
                  }))
                }
                placeholder="[optional]"
              />
            </div>
            <div className="action-row">
              <button type="button" className="secondary" onClick={closeAirPrintPrinterModal}>
                {t("cancel")}
              </button>
              <button type="button" onClick={saveAirPrintPrinter} disabled={!String(airPrintPrinterForm.hostName || "").trim()}>
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {reauthModal.open && (
        <div
          className="modal-backdrop reauth-modal-backdrop"
          onClick={closeReauthModalWithCancel}
        >
          <form className="modal-card modal-card-small" onSubmit={submitReauthPassword} onClick={(event) => event.stopPropagation()}>
            <h3>{t("reauthTitle")}</h3>
            <p>{t("reauthHint")}</p>
            <div className="modal-grid">
              <label>{t("password")}</label>
              <input
                className="text-input"
                type="password"
                value={reauthModal.password}
                onChange={(event) =>
                  setReauthModal((prev) => ({ ...prev, password: event.target.value, error: "" }))
                }
                autoFocus
              />
            </div>
            {reauthModal.error && <p className="error">{reauthModal.error}</p>}
            <div className="action-row">
              <button type="submit">{t("confirm")}</button>
              <button type="button" className="secondary" onClick={closeReauthModalWithCancel}>
                {t("cancel")}
              </button>
            </div>
          </form>
        </div>
      )}

      {showCreateUserModal && (
        <div className="modal-backdrop" onClick={() => setShowCreateUserModal(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <h3>{t("modalTitleAddUser")}</h3>
            <div className="modal-grid">
              <label>{t("consoleUsersFirstName")}</label>
              <input
                className="text-input"
                value={createConsoleUserForm.firstName}
                onChange={(event) =>
                  setCreateConsoleUserForm((prev) => ({ ...prev, firstName: event.target.value }))
                }
              />

              <label>{t("consoleUsersLastName")}</label>
              <input
                className="text-input"
                value={createConsoleUserForm.lastName}
                onChange={(event) =>
                  setCreateConsoleUserForm((prev) => ({ ...prev, lastName: event.target.value }))
                }
              />

              <label>{t("consoleUsersEmail")}</label>
              <input
                className="text-input"
                value={createConsoleUserForm.email}
                onChange={(event) =>
                  setCreateConsoleUserForm((prev) => ({ ...prev, email: event.target.value }))
                }
              />

              <label>{t("consoleUsersLogin")}</label>
              <input
                className="text-input"
                value={createConsoleUserForm.login}
                onChange={(event) =>
                  setCreateConsoleUserForm((prev) => ({ ...prev, login: event.target.value }))
                }
              />

              <label>{t("consoleUsersTempPassword")}</label>
              <input
                className="text-input"
                type="password"
                value={createConsoleUserForm.temporaryPassword}
                onChange={(event) =>
                  setCreateConsoleUserForm((prev) => ({ ...prev, temporaryPassword: event.target.value }))
                }
              />

              <label>{t("consoleUsersRole")}</label>
              <select
                className="text-input"
                value={createConsoleUserForm.role}
                onChange={(event) =>
                  setCreateConsoleUserForm((prev) => ({ ...prev, role: event.target.value }))
                }
              >
                {roleOptions.map((item) => (
                  <option key={item.role_key} value={item.role_key}>
                    {item.display_name || item.role_key}
                  </option>
                ))}
              </select>

            </div>

            <div className="action-row">
              <button onClick={createConsoleUser}>{t("consoleUsersCreate")}</button>
              <button className="secondary" onClick={() => setShowCreateUserModal(false)}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditUserModal && (
        <div className="modal-backdrop" onClick={() => setShowEditUserModal(false)}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>{t("consoleUsersEditModalTitle")}</h3>
              <button className="icon-button" onClick={() => setShowEditUserModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-grid">
              <label>{t("consoleUsersFirstName")}</label>
              <input
                className="text-input"
                value={editConsoleUserForm.firstName}
                onChange={(event) =>
                  setEditConsoleUserForm((prev) => ({
                    ...prev,
                    firstName: event.target.value
                  }))
                }
              />

              <label>{t("consoleUsersLastName")}</label>
              <input
                className="text-input"
                value={editConsoleUserForm.lastName}
                onChange={(event) =>
                  setEditConsoleUserForm((prev) => ({
                    ...prev,
                    lastName: event.target.value
                  }))
                }
              />

              <label>{t("consoleUsersLogin")}</label>
              <input
                className="text-input"
                value={editConsoleUserForm.login}
                onChange={(event) =>
                  setEditConsoleUserForm((prev) => ({
                    ...prev,
                    login: event.target.value
                  }))
                }
              />

              <label>{t("consoleUsersEmail")}</label>
              <input
                className="text-input"
                value={editConsoleUserForm.email}
                onChange={(event) =>
                  setEditConsoleUserForm((prev) => ({
                    ...prev,
                    email: event.target.value
                  }))
                }
              />

              <label>{t("consoleUsersRole")}</label>
              <select
                className="text-input"
                value={editConsoleUserForm.role}
                onChange={(event) =>
                  setEditConsoleUserForm((prev) => ({
                    ...prev,
                    role: event.target.value
                  }))
                }
              >
                {roleOptions.map((item) => (
                  <option key={item.role_key} value={item.role_key}>
                    {item.display_name || item.role_key}
                  </option>
                ))}
              </select>

            </div>

            <div className="action-row">
              <button onClick={updateConsoleUser} disabled={!hasPermission("settings.console_users", "edit")}>
                {t("consoleUsersUpdate")}
              </button>
              <button className="secondary" onClick={() => setShowEditUserModal(false)}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateRoleModal && (
        <div className="modal-backdrop" onClick={() => setShowCreateRoleModal(false)}>
          <div className="modal-card modal-card-small" onClick={(event) => event.stopPropagation()}>
            <div className="modal-heading">
              <h3>{t("roleSettingsCreate")}</h3>
              <button className="icon-button" onClick={() => setShowCreateRoleModal(false)}>
                ×
              </button>
            </div>
            <div className="role-template-picker">
              <strong>{t("roleSettingsTemplates")}</strong>
              {ENTERPRISE_ROLE_TEMPLATES.map((template) => (
                <button
                  key={template.key}
                  className="role-template-button"
                  onClick={() => applyRoleTemplateToCreateForm(template)}
                >
                  <span>{roleTemplateLabel(template)}</span>
                  <small>{roleTemplateHint(template)}</small>
                </button>
              ))}
            </div>
            <div className="modal-grid">
              <label>{t("roleSettingsRoleKey")}</label>
              <input
                className="text-input"
                value={createRoleForm.roleKey}
                placeholder="ios-admin"
                onChange={(event) =>
                  setCreateRoleForm((prev) => ({ ...prev, roleKey: event.target.value.toLowerCase() }))
                }
              />

              <label>{t("roleSettingsDisplayName")}</label>
              <input
                className="text-input"
                value={createRoleForm.displayName}
                placeholder="iOS Admin"
                onChange={(event) =>
                  setCreateRoleForm((prev) => ({ ...prev, displayName: event.target.value }))
                }
              />
            </div>
            <div className="action-row">
              <button onClick={createRoleSetting}>{t("roleSettingsCreate")}</button>
              <button className="secondary" onClick={() => setShowCreateRoleModal(false)}>
                {t("consoleUsersCancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailBlock({ title, children }) {
  return (
    <section className="detail-block">
      <h4>{title}</h4>
      <div className="detail-grid">{children}</div>
    </section>
  );
}

function DetailRow({ label, value, className = "" }) {
  return (
    <div className={`detail-row-pair ${className}`.trim()}>
      <div className="detail-label">{label}</div>
      <div className="detail-value">{value || "-"}</div>
    </div>
  );
}

export default App;
